import { Meteor }       from "meteor/meteor";
import { Template }     from "meteor/templating";
import { Blaze }        from "meteor/blaze";
import { ReactiveVar }  from "meteor/reactive-var";
import { ReactiveDict } from "meteor/reactive-dict";
import { Tracker }      from "meteor/tracker";
import { Session }      from "meteor/session";
import { FlowRouter }   from "meteor/ostrio:flow-router-extra";
import slugify          from "slugify";

import { Scenes, DiffusionSceneAccess, Cartes, HubFiles, VoiceProfiles, Seeds, Instructions, Projets, HypeWidgets, url_asset_diffusion_protegee } from "../../imports/collections.js";
import {
  prepare_svg_pour_scene,
} from "../imports/svg_scene.js";
import {
  rasterise_svg_en_jpeg,
  rasterise_svg_en_canvas,
  rasterise_svg_en_png,
  televerse_hubfile,
} from "../imports/svg_scene.js";
import { buildSharingPickerData, collectCheckedProjectIds } from "../imports/voice_sharing.js";
import {
  annule_animations_entree_scene,
  joue_animations_entree_scene,
  reprend_animations_entree_scene,
} from "../imports/animation_entree_lecteur.js";
import { audio_entry_gate_en_attente } from "../imports/audio_entry_gate/lecture/audio_entry_gate_lecture.js";
import { est_runtime_embarque, scene_verrouillee_embarque } from "../imports/runtime_embarque.js";
import { ouvre_gate_parentale_embarque, ouvre_url_externe_embarque } from "../imports/achat_embarque/achat_embarque.js";
import { ouvre_document_embarque, url_est_document_embarque } from "../imports/document_embarque/document_embarque.js";
import {
  cree_fenetre_cogibulle,
  cree_stockage_local_fenetre_cogibulle,
} from "../imports/fenetre_cogibulle.js";
import {
  create_lecture_audio_player,
} from "../imports/lecture_audio/lecture_audio.js";
import {
  REGLAGES_IMPORT_AUDIO_PROJET_DEFAUTS,
  normalise_reglages_import_audio_projet,
} from "../../imports/projet_audio_import_settings.js";
import {
  REGLAGES_BULLES_PROJET_DEFAUTS,
  normalise_reglages_bulles_projet,
} from "../../imports/projet_bulles_settings.js";
import {
  REGLAGES_MODE_LECTURE_PROJET_DEFAUTS,
  REGLAGES_INTERFACE_CARTE_ANIMATION_CLES,
  REGLAGES_INTERFACE_CARTE_CLES,
  extrait_reglages_interface_carte,
  normalise_reglages_mode_lecture_projet,
} from "../../imports/projet_mode_lecture_settings.js";
import {
  normalise_reglages_survol_contour_objet,
} from "../../imports/scene_hover_settings.js";
import {
  couleur_fond_ambiance_rgba,
  normalise_ambiance_lecture_scene,
} from "../../imports/scene_ambiance_settings.js";
import {
  mode_affichage_diffusion_est_visible,
  normalise_mode_affichage_diffusion,
} from "../../imports/diffusion_display_mode.js";
import {
  dimensions_scene_vignette_pour_rapport,
  format_css_rapport_lh_scene_projet,
  format_rapport_lh_scene_projet,
  normalise_rapport_lh_scene_projet,
  parse_rapport_lh_scene_projet,
  ratio_rapport_lh_scene_projet,
} from "../../imports/projet_scene_ratio_settings.js";
import {
  auto_hide_success_message_reactive,
} from "../imports/auto_hide_success_message.js";
import {
  normalise_banana_progress_gauge_config,
} from "../imports/banana_progress_gauge/lecture/banana_progress_gauge_lecture.js";

import "./scene.html";
import "./scene_runtime.css";

const DIFFUSION_RUNTIME_LECTURE_SEULE = true;
const SVG_RUNTIME_COMPILATION_VERSION = 3;
function edition_hub_active ()
  {
    return !DIFFUSION_RUNTIME_LECTURE_SEULE;
  }

let Sortable = null;
let gSortableImportPromise = null;
function charge_sortable_edition ()
  {
    if (Sortable) return Promise.resolve (Sortable);
    if (!gSortableImportPromise)
      gSortableImportPromise = import ("sortablejs")
        .then ((pModule) =>
          {
            Sortable = pModule.default || pModule;
            return Sortable;
          });
    return gSortableImportPromise;
  }

let WaveSurfer = null;
let RegionsPlugin = null;
let EnvelopePlugin = null;
let gWaveSurferImportPromise = null;
function charge_wavesurfer_edition ()
  {
    if (WaveSurfer && RegionsPlugin && EnvelopePlugin)
      return Promise.resolve ({WaveSurfer, RegionsPlugin, EnvelopePlugin});
    if (!gWaveSurferImportPromise)
      gWaveSurferImportPromise = Promise.all (
        [
          import ("wavesurfer.js"),
          import ("wavesurfer.js/dist/plugins/regions.esm.js"),
          import ("wavesurfer.js/dist/plugins/envelope.esm.js"),
        ])
        .then (([pWaveSurfer, pRegionsPlugin, pEnvelopePlugin]) =>
          {
            WaveSurfer = pWaveSurfer.default || pWaveSurfer;
            RegionsPlugin = pRegionsPlugin.default || pRegionsPlugin;
            EnvelopePlugin = pEnvelopePlugin.default || pEnvelopePlugin;
            return {WaveSurfer, RegionsPlugin, EnvelopePlugin};
          });
    return gWaveSurferImportPromise;
  }

// ---------------------------------------------------------------------------
// Inspecteur (édition titre/slug de la scène courante)
// ---------------------------------------------------------------------------

function fabrique_slug (pTexte)
  {
    return slugify (pTexte || "",
      {
        lower       : true,
        strict      : true,
        locale      : "fr",
        replacement : "-",
        trim        : true,
      });
  }

// Valeurs en cours d'édition dans l'inspecteur. null = pas modifié, on
// se rabat alors sur la valeur en base.
const gInspecteurTitre       = new ReactiveVar (null);
const gInspecteurSlug        = new ReactiveVar (null);
const gInspecteurSlugManuel  = new ReactiveVar (false);  // true si l'utilisateur a édité le slug à la main
const gInspecteurMessage     = new ReactiveVar (null);   // { texte, type }
const gInspecteurProno       = new ReactiveVar (null);   // prononciation en cours d'édition
const gInspecteurStyleMessage = new ReactiveVar (null);  // { texte, type }
const gInspecteurStyleDraft = new ReactiveVar (null);
const gInspecteurStyleApplyFuture = new ReactiveVar (false);
const gInspecteurStyleSaving = new ReactiveVar (null);   // null | "scene" | "project"
const gInspecteurSceneModeLectureMasqueDraft = new ReactiveVar (null);
const gInspecteurSceneModeLectureMasqueSaving = new ReactiveVar (null);
const gInspecteurSceneModeLectureMasqueMessage = new ReactiveVar (null);
const gInspecteurSceneGeometrieDraft = new ReactiveVar (null);
const gInspecteurSceneGeometrieSaving = new ReactiveVar (null);
const gInspecteurSceneGeometrieMessage = new ReactiveVar (null);
const gInspecteurProjetNom = new ReactiveVar (null);
const gInspecteurProjetSlug = new ReactiveVar (null);
const gInspecteurProjetSlugManuel = new ReactiveVar (false);
const gInspecteurProjetRapportLhScene = new ReactiveVar (null);
const gInspecteurProjetReglagesBulles = new ReactiveVar (null);
const gInspecteurProjetReglagesImportAudio = new ReactiveVar (null);
const gInspecteurProjetReglagesModeLecture = new ReactiveVar (null);
const gInspecteurProjetReglagesInterfaceCarte = new ReactiveVar (null);
const gInspecteurProjetSavingSection = new ReactiveVar (null);
const gInspecteurProjetMessage = new ReactiveVar (null);
const gInspecteurProjetBullesMessage = new ReactiveVar (null);
const gInspecteurProjetImportAudioMessage = new ReactiveVar (null);
const gInspecteurProjetModeLectureMessage = new ReactiveVar (null);
const gInspecteurProjetBullesUpload = new ReactiveVar (null);
const gInspecteurProjetZoomAltUpload = new ReactiveVar (null);
const gInspecteurProjetJaugeVisuelleOpen = new ReactiveVar (false);
const gInspecteurProjetJaugeVisuelleSaving = new ReactiveVar (false);
const gInspecteurProjetJaugeVisuelleMessage = new ReactiveVar (null);
const gInspecteurProjetJaugeVisuelleDerniereConfig = new ReactiveVar (null);
const gInspecteurProjetBullesBulkDelete = new ReactiveVar (false);
const gInspecteurProjetBullesColorPicker = new ReactiveVar (null);
const gInspecteurProjetBullesContextMenu = new ReactiveVar (null);
const gInspecteurProjetAudioActif = new ReactiveVar (null);
const gInspecteurProjetAudioUpload = new ReactiveDict ();
const gInspecteurProjetAudioSeedCustom = new ReactiveDict ();
const gInspecteurProjetAudioMessages = new ReactiveDict ();
const gInspecteurProjetAudioLecture = new ReactiveVar (null);
const gInspecteurProjetAudioLectureApresGeneration = new ReactiveDict ();
const gInspecteurProjetHypeUpload = new ReactiveDict ();
const gInspecteurProjetHypeMessages = new ReactiveDict ();
const gInspecteurSceneAudioActif = new ReactiveVar (null);
const gInspecteurSceneAudioUpload = new ReactiveDict ();
const gInspecteurSceneAudioSeedCustom = new ReactiveDict ();
const gInspecteurSceneAudioMessages = new ReactiveDict ();
const gInspecteurSceneAudioLecture = new ReactiveVar (null);
const gInspecteurSceneAudioLectureApresGeneration = new ReactiveDict ();
const gInspecteurCartesGlobalMessage = new ReactiveVar (null);
const gInspecteurCartesGlobalAction = new ReactiveVar (null);
const gInspecteurCartesInterfaceDraft = new ReactiveVar (null);
const gInspecteurCartesInterfaceAction = new ReactiveVar (null);
const gInspecteurProjetSousOnglet = new ReactiveVar ("reglages");
const gInspecteurSectionsRepliables = new ReactiveVar ({});
const gInspecteurProjetDialogOpen = new ReactiveVar (false);
const gInspecteurProjetDialogSaving = new ReactiveVar (false);
const gInspecteurProjetDialogSelection = new ReactiveDict ();
const gInspecteurVideoPreviewUrl = new ReactiveVar (null);
const gLectureSceneActive = new ReactiveVar (false);
const gLectureSceneFocusCarte = new ReactiveVar (null);
const gLectureSceneTransition = new ReactiveVar (false);
const gLectureSceneTransitionSortie = new ReactiveVar (false);
const gLectureSceneTransitionDureeMs = new ReactiveVar (null);
const gLectureCarteProgress = new ReactiveVar (0);
const gInspecteurSurvolMessage = new ReactiveVar (null);
const gInspecteurContoursGlobalMessage = new ReactiveVar (null);
const gInspecteurContoursGlobalAction = new ReactiveVar (null);
const gInspecteurObjetsContourDraft = new ReactiveVar (null);
const gInspecteurBoutonsContourDraft = new ReactiveVar (null);
let gInspecteurApercuContourCleanup = null;

if (edition_hub_active ())
  {
    auto_hide_success_message_reactive (gInspecteurMessage);
    auto_hide_success_message_reactive (gInspecteurStyleMessage);
    auto_hide_success_message_reactive (gInspecteurSceneModeLectureMasqueMessage);
    auto_hide_success_message_reactive (gInspecteurSceneGeometrieMessage);
    auto_hide_success_message_reactive (gInspecteurProjetMessage);
    auto_hide_success_message_reactive (gInspecteurProjetBullesMessage);
    auto_hide_success_message_reactive (gInspecteurProjetImportAudioMessage);
    auto_hide_success_message_reactive (gInspecteurProjetModeLectureMessage);
    auto_hide_success_message_reactive (gInspecteurProjetJaugeVisuelleMessage);
    auto_hide_success_message_reactive (gInspecteurCartesGlobalMessage);
    auto_hide_success_message_reactive (gInspecteurContoursGlobalMessage);
    auto_hide_success_message_reactive (gInspecteurSurvolMessage);
  }

let gSceneTemplateActif = null;
let gLectureSceneAudio = null;
let gLectureSceneAudioFin = null;
let gLectureSceneAudioInstant = null;
let gLectureSceneRetourMenuInstant = null;
let gLectureSceneSequenceAudioId = 0;
let gLectureMorphingSequenceId = 0;
let gLectureMorphingAutoSceneId = null;
let gLectureSceneAmbianceAudio = null;
let gLectureSceneAmbianceFileId = null;
// Ambiance lancée depuis la barre de contrôles en mode édition (hub) : elle
// survit aux autoruns tant que la scène et son fichier ne changent pas.
let gLectureSceneAmbianceEdition = false;
// État de l'ambiance pour la barre de contrôles (lecture, position, durée en s).
const LECTURE_AMBIANCE_ETAT_VIDE = Object.freeze ({lecture : false, position : 0, duree : 0});
const gLectureSceneAmbianceEtat = new ReactiveVar (LECTURE_AMBIANCE_ETAT_VIDE);
// Aperçu local des réglages d'ambiance (utilisé par l'inspecteur du hub ;
// jamais alimenté dans diffusion, gardé pour un code de lecture identique).
const gAmbianceLectureLocal = new ReactiveVar (null);
let gLectureSceneRetourMenuAudio = null;
let gLectureSceneTransitionTimer = null;
let gLectureSceneTransitionComputation = null;
let gLectureSceneTransitionSequence = 0;
let gLectureSceneTransitionOverlay = null;
let gLectureSceneTransitionOverlayAnimation = null;
let gLectureSceneTransitionOverlayTimer = null;
let gLectureCarteProgressRaf = null;
const LECTURE_PEDAGO_SESSION_KEY = "lecture_pedago_scene_scores";
const LECTURE_PEDAGO_LOCAL_STORAGE_PREFIX = "cogibulle_lecture_scores_v1:";
const DECORATION_SCENE_REUSSIE_CLASS = "cogi-scene-reussie-decor";
const DECORATION_SCENE_REUSSIE_CALQUE_CLASS = "cogi-scene-reussie-calque-decor";
const gLectureRejouerSceneDialog = new ReactiveVar (null);
const gLectureCheatScoresAvant = new Map ();
const gBullesProjetSvgCacheRevision = new ReactiveVar (0);
const gBullesProjetSvgModeles = new Map ();

const DUREE_APPUI_LONG_INSPECTEUR_PROJET_BULLES = 460;
const DISTANCE_APPUI_LONG_INSPECTEUR_PROJET_BULLES = 10;

const ZOOM_ALTERNATIF_SVG_ROLES =
  [
    {cle : "carte_jauge", libelle : "carte_jauge"},
    {cle : "carte_fermeture", libelle : "carte_fermeture"},
    {cle : "carte_legende", libelle : "carte_légende"},
    {cle : "carte_non", libelle : "carte_non"},
    {cle : "carte_oui", libelle : "carte_oui"},
  ];
const ZOOM_ALTERNATIF_SVG_ROLE_KEYS = new Set (ZOOM_ALTERNATIF_SVG_ROLES.map ((pRole) => pRole.cle));
const REGLAGES_INTERFACE_CARTE_CLES_SET = new Set (REGLAGES_INTERFACE_CARTE_CLES);
const REGLAGES_INTERFACE_CARTE_ANIMATION_CLES_SET = new Set (REGLAGES_INTERFACE_CARTE_ANIMATION_CLES);
const gZoomAlternatifSvgCacheRevision = new ReactiveVar (0);
const gZoomAlternatifSvgModeles = new Map ();
const gPrechauffageAssetsLectureScene = new Map ();

const gInspecteurProjetBullesContextMenuLongPress =
  {
    timer : null,
    pointerId : null,
    fileId : null,
    nom : "",
    startX : 0,
    startY : 0,
    clientX : 0,
    clientY : 0,
    moveHandler : null,
    endHandler : null,
  };

export function lecture_scene_est_active ()
  {
    return gLectureSceneActive.get ();
  }

function mode_edition_lecture_prefs_defaut (pEditionVisible = true, pLectureVisible = false)
  {
    return {
      edition : !!pEditionVisible,
      lecture : !!pLectureVisible,
    };
  }

const INSPECTEUR_PROJET_SOUS_ONGLETS =
  [
    {cle : "reglages", libelle : "Réglages"},
    {cle : "outils", libelle : "Outils"},
    {cle : "participants", libelle : "Participants"},
    {cle : "utilisateurs", libelle : "Utilisateurs"},
  ];

const INSPECTEUR_PROJET_SECTION_GENERAL = "general";
const INSPECTEUR_PROJET_SECTION_BULLES = "bulles";
const INSPECTEUR_PROJET_SECTION_AUDIO_IMPORT = "audio_import";
const INSPECTEUR_PROJET_SECTION_MODE_LECTURE = "mode_lecture";

const AUDIO_SCENE_ITEMS = Object.freeze (
  [
    {cle : "phoneme", libelle : "Phonème de la scène", synthese : true},
    {cle : "ambiance", libelle : "Ambiance", synthese : false},
  ]
);
const AUDIO_SCENE_ITEMS_PAR_CLE = new Map (AUDIO_SCENE_ITEMS.map ((pItem) => [pItem.cle, pItem]));

const LANGUES_VOIX_LABELS =
  {
    Auto : "Auto",
    German : "Allemand",
    English : "Anglais",
    Spanish : "Espagnol",
    French : "Français",
    Italian : "Italien",
    Portuguese : "Portugais",
    Polish : "Polonais",
    Dutch : "Néerlandais",
    Czech : "Tchèque",
  };

function label_langue_voix_scene (pValeur)
  {
    return LANGUES_VOIX_LABELS[pValeur] || pValeur || "Auto";
  }

function profil_voix_materialisable_client (pProfil)
  {
    const tSamples = Array.isArray (pProfil && pProfil.samples) ? pProfil.samples : [];
    return tSamples.some ((pSample) =>
      !!(
        pSample
        && (
          pSample.hub_file_id
          || (pSample.audio_file && pSample.audio_file.hub_file_id)
        )
      )
    );
  }

function profil_voix_defaut_client (pListe)
  {
    return (pListe || []).find (profil_voix_materialisable_client)
      || (pListe || [])[0]
      || null;
  }

const AUDIO_PROJET_ITEMS = Object.freeze (
  [
    {cle : "entends_tu_le_son_1", libelle : "Entends-tu le son (1)", synthese : true},
    {cle : "entends_tu_le_son_2", libelle : "Entends-tu le son (2)", synthese : true},
    {cle : "pour_reecouter_le_mot", libelle : "Pour réécouter le mot", synthese : true},
    {cle : "musique_generique", libelle : "Musique générique", synthese : false},
    {cle : "musique_animation_liane", libelle : "Musique animation liane", synthese : false},
    {cle : "musique_pluie_de_bananes", libelle : "Musique pluie de bananes", synthese : false},
    {cle : "son_reussite", libelle : "Son réussite", synthese : false},
    {cle : "son_echec", libelle : "Son échec", synthese : false},
    {cle : "son_appareil_photo", libelle : "Son appareil photo", synthese : false},
    {cle : "son_clic", libelle : "Son clic", synthese : false},
    {cle : "son_retour_menu", libelle : "Son retour menu", synthese : false},
  ]
);
const AUDIO_PROJET_AMBIANCE_ITEMS = Object.freeze (
  Array.from ({length : 10}, (_, i) => (
    {index : i, cle : "musique_ambiance_" + (i + 1), libelle : "Musique ambiance " + (i + 1), synthese : false}
  ))
);
const AUDIO_PROJET_ITEMS_PAR_CLE = new Map (
  [...AUDIO_PROJET_ITEMS, ...AUDIO_PROJET_AMBIANCE_ITEMS].map ((pItem) => [pItem.cle, pItem])
);

const INTERACTIVITE_ACTION_AUCUNE = "";
const INTERACTIVITE_ACTION_SCENE = "scene";
const INTERACTIVITE_ACTION_URL = "url";
const INTERACTIVITE_ACTION_SON = "sound";
const INTERACTIVITE_ACTION_VIDEO = "video";
const INTERACTIVITE_ACTION_DIAPORAMA = "slideshow";
const INTERACTIVITE_ACTION_COMPTE = "account";
const INTERACTIVITE_ACTION_INIT_AUDIO_MOBILE = "init-mobile-audio";

const INTERACTIVITE_ACTIONS =
  [
    {value : INTERACTIVITE_ACTION_AUCUNE, label : "Aucune action"},
    {value : INTERACTIVITE_ACTION_SCENE,  label : "Ouvrir une scène"},
    {value : INTERACTIVITE_ACTION_URL,    label : "Ouvrir une url"},
    {value : INTERACTIVITE_ACTION_SON,    label : "Lire un son"},
    {value : INTERACTIVITE_ACTION_VIDEO,  label : "Lire une vidéo"},
    {value : INTERACTIVITE_ACTION_DIAPORAMA, label : "Lire un diaporama"},
    {value : INTERACTIVITE_ACTION_COMPTE, label : "Ouvrir la page \"Mon compte\""},
    {value : INTERACTIVITE_ACTION_INIT_AUDIO_MOBILE, label : "Initialiser le son sur mobile"},
  ];

const gInspecteurProjetDialog =
  {
    box : null,
    drag : null,
    resize : null,
    interactionOverlay : null,
  };

const STYLE_INTERFACE_TRAIT_OPTIONS =
  [
    {value : "solid",     label : "Continu"},
    {value : "dashed",    label : "Pointillé"},
    {value : "dotted",    label : "Points"},
    {value : "dash_dot",  label : "Tiret-point"},
    {value : "long_dash", label : "Longs tirets"},
  ];

const STYLE_INTERFACE_DASHARRAYS =
  {
    solid     : "",
    dashed    : "6 4",
    dotted    : "2 4",
    dash_dot  : "10 4 2 4",
    long_dash : "12 5",
  };

const STYLE_INTERFACE_RAINBOW_COLORS =
  [
    "#ff6b6b",
    "#ff9f43",
    "#ffd93d",
    "#6bcB77",
    "#4d96ff",
    "#845ef7",
    "#f06595",
    "#2ec4b6",
  ];

const STYLE_INTERFACE_DEFAUT =
  {
    selection_color  : "#ff5722",
    hover_color      : "#1185c8",
    stroke_width     : 3.5,
    stroke_style     : "long_dash",
    rainbow_category : false,
  };

const MODE_LECTURE_MASQUE_SCENE_DEFAUT = Object.freeze (
  {
    couleur : "#ffffff",
    opacite : 1,
    fond : "#ffffff",
    masque_identique_fond : false,
  }
);

const GEOMETRIE_SCENE_DEFAUT = Object.freeze (
  {
    offset_x : 0,
    offset_y : 0,
    offset_z : 0,
  }
);

const OMBRE_OBJET_DEFAUT = Object.freeze (
  {
    active : false,
    opacite : 0.4,
    blur : 1,
    x : 3,
    y : 3,
    couleur : "#000000",
    zones_dessinees_seules : true,
  }
);

const INSPECTEUR_PROJET_DIALOG_MIN_W = 360;
const INSPECTEUR_PROJET_DIALOG_MIN_H = 260;
const INSPECTEUR_PROJET_DIALOG_MARGIN = 8;
const INSPECTEUR_PROJET_DIALOG_BOX_KEY = "cogivoice_hub_inspecteur_projet_dialog_box";
const INSPECTEUR_PROJET_DIALOG_OPEN_KEY = "cogivoice_hub_inspecteur_projet_dialog_open";
const INSPECTEUR_PROJET_JAUGE_VISUELLE_MIN_W = 360;
const INSPECTEUR_PROJET_JAUGE_VISUELLE_MIN_H = 360;
const INSPECTEUR_PROJET_JAUGE_VISUELLE_BOX_KEY = "cogivoice_hub_inspecteur_jauge_visuelle_box_v2";
const INSPECTEUR_PROJET_JAUGE_VISUELLE_OPEN_KEY = "cogivoice_hub_inspecteur_jauge_visuelle_open_v2";
const INSPECTEUR_PROJET_JAUGE_VISUELLE_DEFAULT_BOX = Object.freeze ({x : 280, y : 120, w : 410, h : 710});
const JAUGE_VISUELLE_ROLE = "carte_jauge";
const JAUGE_VISUELLE_PROGRESS_TEST = 0.35;
const JAUGE_VISUELLE_VIEWBOX = Object.freeze ({x : 0, y : 0, w : 137.14, h : 75.17});

function normalise_couleur_style_interface (pVal, pFallback)
  {
    const tVal = typeof pVal === "string" ? pVal.trim ().toLowerCase () : "";
    return /^#[0-9a-f]{6}$/.test (tVal) ? tVal : pFallback;
  }

function absolue_url_client (pUrl)
  {
    const tUrl = typeof pUrl === "string" ? pUrl.trim () : "";
    if (!tUrl) return "";
    try { return new URL (tUrl, window.location.origin).href; }
    catch (e) { return tUrl; }
  }

function rend_urls_svg_absolues_pour_serialisation (pRoot)
  {
    if (!pRoot || !pRoot.querySelectorAll) return;
    pRoot.querySelectorAll ("image").forEach ((pImage) =>
      {
        const tHref = pImage.getAttribute ("href")
          || pImage.getAttributeNS ("http://www.w3.org/1999/xlink", "href");
        const tUrl = absolue_url_client (tHref);
        if (!tUrl) return;
        pImage.setAttribute ("href", tUrl);
        pImage.setAttributeNS ("http://www.w3.org/1999/xlink", "href", tUrl);
      });
  }

function blob_vers_data_url_client (pBlob)
  {
    return new Promise ((resolve, reject) =>
      {
        const tLecteur = new FileReader ();
        tLecteur.onload = () => resolve (String (tLecteur.result || ""));
        tLecteur.onerror = () => reject (tLecteur.error || new Error ("Lecture image impossible"));
        tLecteur.readAsDataURL (pBlob);
      });
  }

async function embarque_images_svg_pour_serialisation (pRoot)
  {
    if (!pRoot || !pRoot.querySelectorAll || typeof fetch !== "function") return;
    const tImages = Array.from (pRoot.querySelectorAll ("image"));
    const tCache = new Map ();
    await Promise.all (tImages.map (async (pImage) =>
      {
        const tHref = pImage.getAttribute ("href")
          || pImage.getAttributeNS ("http://www.w3.org/1999/xlink", "href");
        const tUrl = absolue_url_client (tHref);
        if (!tUrl || /^data:/i.test (tUrl)) return;
        try
          {
            if (!tCache.has (tUrl))
              tCache.set (
                tUrl,
                fetch (tUrl, {credentials : "include"})
                  .then ((pReponse) =>
                    {
                      if (!pReponse || !pReponse.ok) throw new Error ("Image non disponible");
                      return pReponse.blob ();
                    })
                  .then ((pBlob) => blob_vers_data_url_client (pBlob))
              );
            const tDataUrl = await tCache.get (tUrl);
            if (!tDataUrl) return;
            pImage.setAttribute ("href", tDataUrl);
            pImage.setAttributeNS ("http://www.w3.org/1999/xlink", "href", tDataUrl);
          }
        catch (e)
          {
            pImage.setAttribute ("href", tUrl);
            pImage.setAttributeNS ("http://www.w3.org/1999/xlink", "href", tUrl);
          }
      }));
  }

function normalise_mode_lecture_masque_scene_client (pValeur, pFallback = MODE_LECTURE_MASQUE_SCENE_DEFAUT)
  {
    const tSource = (pValeur && typeof pValeur === "object") ? pValeur : {};
    const tOpacite = Number (tSource.opacite);
    const tFond = normalise_couleur_style_interface (tSource.fond, pFallback.fond || "#ffffff");
    const tIdentiqueFond = typeof tSource.masque_identique_fond === "boolean"
      ? tSource.masque_identique_fond
      : !!pFallback.masque_identique_fond;
    return {
      couleur : normalise_couleur_style_interface (tSource.couleur, pFallback.couleur),
      opacite : Number.isFinite (tOpacite)
        ? Math.max (0, Math.min (1, Math.round (tOpacite * 100) / 100))
        : pFallback.opacite,
      fond : tFond,
      masque_identique_fond : tIdentiqueFond,
    };
  }

function couleur_effective_mode_lecture_masque_scene_client (pMasque)
  {
    const tMasque = normalise_mode_lecture_masque_scene_client (pMasque);
    return tMasque.masque_identique_fond
      ? tMasque.fond
      : tMasque.couleur;
  }

function normalise_nombre_geometrie_scene_client (pValeur, pFallback = 0)
  {
    const tNombre = Number (pValeur);
    if (!Number.isFinite (tNombre)) return pFallback;
    return Math.round (Math.max (-500, Math.min (500, tNombre)) * 1000) / 1000;
  }

function normalise_geometrie_scene_client (pValeur, pFallback = GEOMETRIE_SCENE_DEFAUT)
  {
    const tSource = (pValeur && typeof pValeur === "object") ? pValeur : {};
    return {
      offset_x : normalise_nombre_geometrie_scene_client (tSource.offset_x, pFallback.offset_x),
      offset_y : normalise_nombre_geometrie_scene_client (tSource.offset_y, pFallback.offset_y),
      offset_z : normalise_nombre_geometrie_scene_client (tSource.offset_z, pFallback.offset_z),
    };
  }

function normalise_style_interface_scene_client (pStyle)
  {
    const tStyle = (pStyle && typeof pStyle === "object") ? pStyle : {};
    const tStrokeWidth = Number (tStyle.stroke_width);
    return {
      selection_color  : normalise_couleur_style_interface (
        tStyle.selection_color,
        STYLE_INTERFACE_DEFAUT.selection_color
      ),
      hover_color      : normalise_couleur_style_interface (
        tStyle.hover_color,
        STYLE_INTERFACE_DEFAUT.hover_color
      ),
      stroke_width     : isFinite (tStrokeWidth)
        ? Math.max (1, Math.min (12, Math.round (tStrokeWidth * 2) / 2))
        : STYLE_INTERFACE_DEFAUT.stroke_width,
      stroke_style     : STYLE_INTERFACE_TRAIT_OPTIONS.some ((pOpt) => pOpt.value === tStyle.stroke_style)
        ? tStyle.stroke_style
        : STYLE_INTERFACE_DEFAUT.stroke_style,
      rainbow_category : !!tStyle.rainbow_category,
    };
  }

function pointille_style_interface_scene (pStyleTrait)
  {
    return STYLE_INTERFACE_DASHARRAYS[pStyleTrait] || "";
  }

function style_interface_scene_base ()
  {
    const tSceneId = Session.get ("scene_id");
    const tProjetId = Session.get ("projet_id");
    const tScene = tSceneId ? Scenes.findOne (tSceneId) : null;
    const tProjet = tProjetId ? Projets.findOne (tProjetId) : null;
    return normalise_style_interface_scene_client (
      {
        ...STYLE_INTERFACE_DEFAUT,
        ...((tProjet && tProjet.scene_interface_style_default) || {}),
        ...((tScene && tScene.interface_style) || {}),
      }
    );
  }

function style_interface_scene_previsualise ()
  {
    return normalise_style_interface_scene_client (
      gInspecteurStyleDraft.get () || style_interface_scene_base ()
    );
  }

function mode_lecture_masque_scene_courant (pScene = null)
  {
    const tScene = pScene || (() =>
      {
        const tSceneId = Session.get ("scene_id");
        return tSceneId ? Scenes.findOne (tSceneId) : null;
      }) ();
    return normalise_mode_lecture_masque_scene_client (
      tScene && tScene.mode_lecture_masque
    );
  }

function geometrie_scene_courante (pScene = null)
  {
    const tScene = pScene || (() =>
      {
        const tSceneId = Session.get ("scene_id");
        return tSceneId ? Scenes.findOne (tSceneId) : null;
      }) ();
    return normalise_geometrie_scene_client (
      tScene && tScene.geometrie
    );
  }

function brouillon_geometrie_scene ()
  {
    const tBrouillon = gInspecteurSceneGeometrieDraft.get ();
    if (tBrouillon)
      return normalise_geometrie_scene_client (tBrouillon);
    return geometrie_scene_courante ();
  }

function modifie_brouillon_geometrie_scene (pPatch)
  {
    const tCourant = brouillon_geometrie_scene ();
    gInspecteurSceneGeometrieDraft.set (
      normalise_geometrie_scene_client ({...tCourant, ...(pPatch || {})})
    );
    gInspecteurSceneGeometrieMessage.set (null);
  }

function cle_offset_geometrie_scene_client (pCle)
  {
    return pCle === "offset_x" || pCle === "offset_y" || pCle === "offset_z";
  }

function valeur_affichage_geometrie_scene_client (pCle, pValeur)
  {
    const tValeur = Number (pValeur) || 0;
    return cle_offset_geometrie_scene_client (pCle) ? -tValeur : tValeur;
  }

function valeur_stockage_geometrie_scene_client (pCle, pValeur)
  {
    const tValeur = Number (pValeur) || 0;
    return cle_offset_geometrie_scene_client (pCle) ? -tValeur : tValeur;
  }

function brouillon_mode_lecture_masque_scene ()
  {
    const tBrouillon = gInspecteurSceneModeLectureMasqueDraft.get ();
    if (tBrouillon)
      return normalise_mode_lecture_masque_scene_client (tBrouillon);
    return mode_lecture_masque_scene_courant ();
  }

function modifie_brouillon_mode_lecture_masque_scene (pPatch)
  {
    const tCourant = brouillon_mode_lecture_masque_scene ();
    gInspecteurSceneModeLectureMasqueDraft.set (
      normalise_mode_lecture_masque_scene_client ({...tCourant, ...(pPatch || {})})
    );
    gInspecteurSceneModeLectureMasqueMessage.set (null);
  }

function signature_style_interface_scene_previsualise ()
  {
    return JSON.stringify (style_interface_scene_previsualise ());
  }

function signature_geometrie_scene_previsualisee ()
  {
    return JSON.stringify (brouillon_geometrie_scene ());
  }

function maj_style_interface_brouillon (pPatch)
  {
    const tCourant = style_interface_scene_previsualise ();
    gInspecteurStyleDraft.set (
      normalise_style_interface_scene_client ({...tCourant, ...pPatch})
    );
    gInspecteurStyleMessage.set (null);
  }

function palette_arc_en_ciel_style_interface (pIds)
  {
    const tMap = new Map ();
    const tListe = Array.isArray (pIds) ? pIds : [];
    for (let i = 0; i < tListe.length; i++)
      tMap.set (
        tListe[i],
        STYLE_INTERFACE_RAINBOW_COLORS[i % STYLE_INTERFACE_RAINBOW_COLORS.length]
      );
    return tMap;
  }

function projet_courant_scene ()
  {
    const tProjetId = Session.get ("projet_id");
    return tProjetId ? Projets.findOne (tProjetId) : null;
  }

function cle_audio_scene_valide (pCle)
  {
    return AUDIO_SCENE_ITEMS_PAR_CLE.has (pCle || "");
  }

function entree_audio_scene (pScene, pCle)
  {
    if (!pScene || !cle_audio_scene_valide (pCle)) return null;
    const tAssets = pScene.audio_assets && typeof pScene.audio_assets === "object"
      ? pScene.audio_assets
      : {};
    return tAssets[pCle] || null;
  }

function fichier_audio_scene (pScene, pCle)
  {
    const tEntree = entree_audio_scene (pScene, pCle);
    return tEntree && tEntree.file_id
      ? HubFiles.findOne ({_id : tEntree.file_id})
      : null;
  }

function cle_audio_projet_valide (pCle)
  {
    return AUDIO_PROJET_ITEMS_PAR_CLE.has (pCle || "");
  }

function entree_audio_projet (pProjet, pCle)
  {
    if (!pProjet || !cle_audio_projet_valide (pCle)) return null;
    const tItem = AUDIO_PROJET_ITEMS_PAR_CLE.get (pCle);
    if (tItem && typeof tItem.index === "number")
      {
        const tListe = Array.isArray (pProjet.musique_ambiance)
          ? pProjet.musique_ambiance
          : [];
        return tListe[tItem.index] || null;
      }
    const tAssets = pProjet.audio_assets && typeof pProjet.audio_assets === "object"
      ? pProjet.audio_assets
      : {};
    return tAssets[pCle] || null;
  }

function fichier_audio_projet (pProjet, pCle)
  {
    const tEntree = entree_audio_projet (pProjet, pCle);
    return tEntree && tEntree.file_id
      ? HubFiles.findOne ({_id : tEntree.file_id})
      : null;
  }

function widget_hype_projet (pType = "recompense_carte", pProjet = projet_courant_scene ())
  {
    if (!pProjet || !pProjet._id) return null;
    return HypeWidgets.findOne ({projet_id : pProjet._id, type : type_widget_hype_normalise (pType)}, {sort : {createdAt : -1}});
  }

function type_widget_hype_normalise (pType)
  {
    return pType === "recompense_scene" ? "recompense_scene" : "recompense_carte";
  }

function cle_reglage_widget_hype (pType, pSuffixe)
  {
    return "hype_" + type_widget_hype_normalise (pType) + "_" + pSuffixe;
  }

function url_widget_hype_client (pWidget)
  {
    if (!pWidget || !pWidget.url) return null;
    const tUrl = url_asset_diffusion_protegee (pWidget.url, pWidget._diffusion_version_id);
    return String (tUrl).startsWith ("http")
      ? tUrl
      : window.location.origin + tUrl;
  }

function normalise_volume_audio_pct_client (pValeur)
  {
    const tValeur = Number (pValeur);
    if (!Number.isFinite (tValeur)) return 100;
    return Math.max (0, Math.min (100, Math.round (tValeur)));
  }

function normalise_delai_sequence_entends_tu_ms_client (pValeur)
  {
    const tValeur = Number (pValeur);
    if (!Number.isFinite (tValeur)) return 1000;
    return Math.max (0, Math.min (10000, Math.round (tValeur)));
  }

function volume_audio_entree_pct (pEntree)
  {
    return normalise_volume_audio_pct_client (
      pEntree && pEntree.volume_pct !== undefined ? pEntree.volume_pct : 100
    );
  }

function delai_sequence_entends_tu_entree_ms (pEntree)
  {
    return normalise_delai_sequence_entends_tu_ms_client (
      pEntree && pEntree.delai_sequence_ms !== undefined ? pEntree.delai_sequence_ms : 1000
    );
  }

function url_hubfile_client (pFichier)
  {
    return url_client_hubfile (pFichier);
  }

function confirme_action_inspecteur (pMessage, pOptions = {})
  {
    if (typeof document === "undefined" || !document.body)
      return Promise.resolve (typeof confirm === "function" ? confirm (pMessage) : false);

    return new Promise ((pResolve) =>
      {
        let tTermine = false;
        const tBackdrop = document.createElement ("div");
        tBackdrop.className = "modal-backdrop cogibulle_backdrop";

        const tCarte = document.createElement ("div");
        tCarte.className = "modal-card card";
        tCarte.setAttribute ("role", "dialog");
        tCarte.setAttribute ("aria-modal", "true");

        const tTitre = document.createElement ("h3");
        tTitre.textContent = pOptions.titre || "Confirmation";

        const tTexte = document.createElement ("p");
        tTexte.textContent = pMessage;

        const tActions = document.createElement ("div");
        tActions.className = "modal-actions";

        const tAnnuler = document.createElement ("button");
        tAnnuler.type = "button";
        tAnnuler.className = "btn btn-secondary";
        tAnnuler.textContent = pOptions.annulerLabel || "Annuler";

        const tConfirmer = document.createElement ("button");
        tConfirmer.type = "button";
        tConfirmer.className = "btn btn-danger";
        tConfirmer.textContent = pOptions.confirmerLabel || "Supprimer";

        function termine (pValeur)
          {
            if (tTermine) return;
            tTermine = true;
            document.removeEventListener ("keydown", tKeydown, true);
            if (tBackdrop.parentNode) tBackdrop.parentNode.removeChild (tBackdrop);
            pResolve (!!pValeur);
          }

        function tKeydown (pEvent)
          {
            if (pEvent.key === "Escape")
              {
                pEvent.preventDefault ();
                termine (false);
              }
          }

        tAnnuler.addEventListener ("click", () => termine (false));
        tConfirmer.addEventListener ("click", () => termine (true));
        tBackdrop.addEventListener ("click", (pEvent) =>
          {
            if (pEvent.target === tBackdrop) termine (false);
          });

        tActions.appendChild (tAnnuler);
        tActions.appendChild (tConfirmer);
        tCarte.appendChild (tTitre);
        tCarte.appendChild (tTexte);
        tCarte.appendChild (tActions);
        tBackdrop.appendChild (tCarte);
        document.body.appendChild (tBackdrop);
        document.addEventListener ("keydown", tKeydown, true);
        try { tConfirmer.focus (); } catch (e) {}
      });
  }

function extension_hubfile_client (pFichier)
  {
    if (!pFichier) return ".wav";
    const tE = pFichier.extension || pFichier.ext;
    if (tE) return "." + String (tE).replace (/^\./, "");
    const tNom = String (pFichier.name || pFichier.fileName || "");
    const tMatch = tNom.match (/\.([a-z0-9]+)$/i);
    if (tMatch && tMatch[1]) return "." + tMatch[1].toLowerCase ();
    const tType = String (pFichier.type || "").toLowerCase ();
    if (tType === "audio/mpeg" || tType === "audio/mp3") return ".mp3";
    if (tType === "audio/wav" || tType === "audio/x-wav" || tType === "audio/wave") return ".wav";
    return ".wav";
  }

function utilisateur_affiche_scene (pUserId)
  {
    if (!pUserId) return {displayName : "Utilisateur inconnu", slug : "", rolesLabel : "aucun rôle"};
    const tUser = Meteor.users.findOne (pUserId);
    if (!tUser)
      return {displayName : "Utilisateur inconnu", slug : "", rolesLabel : "aucun rôle"};
    const tRoles = Array.isArray (tUser.roles) ? tUser.roles.filter (Boolean) : [];
    return {
      ...tUser,
      displayName : (tUser.profile && tUser.profile.name) || tUser.username || tUser.slug || "Sans nom",
      slug : tUser.slug || "",
      rolesLabel : tRoles.length ? tRoles.join (", ") : "aucun rôle",
    };
  }

function utilisateur_est_proprietaire_projet_inspecteur ()
  {
    const tProjet = projet_courant_scene ();
    return !!(tProjet && Meteor.userId () && tProjet.user_id === Meteor.userId ());
  }

function utilisateur_est_admin_projet (pProjet, pUserId = Meteor.userId ())
  {
    if (!pProjet || !pUserId || pProjet.user_id === pUserId) return false;
    return Array.isArray (pProjet.admins) && pProjet.admins.includes (pUserId);
  }

function utilisateur_est_admin_projet_inspecteur ()
  {
    return utilisateur_est_admin_projet (projet_courant_scene ());
  }

function utilisateur_peut_modifier_projet_inspecteur ()
  {
    return utilisateur_est_proprietaire_projet_inspecteur ()
      || utilisateur_est_admin_projet_inspecteur ();
  }

function utilisateur_peut_modifier_scene_inspecteur ()
  {
    return utilisateur_peut_modifier_projet_inspecteur ();
  }

function utilisateur_peut_gerer_participants_projet_inspecteur ()
  {
    return utilisateur_peut_modifier_projet_inspecteur ();
  }

function utilisateur_peut_gerer_admins_projet_inspecteur ()
  {
    return utilisateur_est_proprietaire_projet_inspecteur ();
  }

function utilisateur_peut_retirer_membre_projet_inspecteur (pProjet, pMembreId)
  {
    if (!pProjet || !pMembreId || pMembreId === pProjet.user_id) return false;
    if (!utilisateur_peut_gerer_participants_projet_inspecteur ()) return false;
    if (!utilisateur_est_proprietaire_projet_inspecteur ()
        && utilisateur_est_admin_projet (pProjet, pMembreId)) return false;
    return true;
  }

function reinitialise_inspecteur_projet ()
  {
    gInspecteurProjetNom.set (null);
    gInspecteurProjetSlug.set (null);
    gInspecteurProjetSlugManuel.set (false);
    gInspecteurProjetRapportLhScene.set (null);
    gInspecteurProjetReglagesBulles.set (null);
    gInspecteurProjetReglagesImportAudio.set (null);
    gInspecteurProjetReglagesModeLecture.set (null);
    gInspecteurProjetReglagesInterfaceCarte.set (null);
    applique_preview_reglages_interface_carte_tpl (null);
    gInspecteurProjetSavingSection.set (null);
    gInspecteurProjetMessage.set (null);
    gInspecteurProjetBullesMessage.set (null);
    gInspecteurProjetImportAudioMessage.set (null);
    gInspecteurProjetModeLectureMessage.set (null);
    gInspecteurProjetJaugeVisuelleSaving.set (false);
    gInspecteurProjetJaugeVisuelleMessage.set (null);
    gInspecteurProjetJaugeVisuelleDerniereConfig.set (null);
    gInspecteurProjetBullesUpload.set (null);
    gInspecteurProjetBullesBulkDelete.set (false);
    gInspecteurProjetBullesColorPicker.set (null);
    gInspecteurProjetBullesContextMenu.set (null);
  }

function rapport_lh_scene_projet_courant (pProjet = projet_courant_scene ())
  {
    return normalise_rapport_lh_scene_projet (
      pProjet && pProjet.rapport_lh_scene
    );
  }

function brouillon_rapport_lh_scene_projet ()
  {
    const tBrouillon = gInspecteurProjetRapportLhScene.get ();
    if (tBrouillon)
      return normalise_rapport_lh_scene_projet (tBrouillon);
    return rapport_lh_scene_projet_courant ();
  }

function modifie_brouillon_rapport_lh_scene_projet (pValeur)
  {
    gInspecteurProjetRapportLhScene.set (
      normalise_rapport_lh_scene_projet (pValeur)
    );
    gInspecteurProjetMessage.set (null);
  }

function reglages_bulles_projet_courant (pProjet = projet_courant_scene ())
  {
    return normalise_reglages_bulles_projet (
      pProjet && pProjet.reglages_bulles
    );
  }

function brouillon_reglages_bulles_projet ()
  {
    const tBrouillon = gInspecteurProjetReglagesBulles.get ();
    if (tBrouillon)
      return normalise_reglages_bulles_projet (tBrouillon);
    return reglages_bulles_projet_courant ();
  }

function modifie_brouillon_reglages_bulles_projet (pPatch)
  {
    const tCourant = brouillon_reglages_bulles_projet ();
    gInspecteurProjetReglagesBulles.set (
      normalise_reglages_bulles_projet ({...tCourant, ...(pPatch || {})})
    );
    gInspecteurProjetBullesMessage.set (null);
  }

function couleur_picker_bulles_projet ()
  {
    const tCouleur = gInspecteurProjetBullesColorPicker.get ();
    if (typeof tCouleur === "string" && /^#[0-9a-f]{6}$/i.test (tCouleur.trim ()))
      return tCouleur.trim ().toLowerCase ();
    const tCouleurs = brouillon_reglages_bulles_projet ().couleurs_auto;
    return tCouleurs[0] || "#285880";
  }

function ajoute_couleur_bulles_projet (pCouleur)
  {
    const tCouleur = typeof pCouleur === "string" ? pCouleur.trim ().toLowerCase () : "";
    if (!/^#[0-9a-f]{6}$/.test (tCouleur)) return false;
    const tReglages = brouillon_reglages_bulles_projet ();
    if (tReglages.couleurs_auto.includes (tCouleur)) return false;
    modifie_brouillon_reglages_bulles_projet (
      {couleurs_auto : [...tReglages.couleurs_auto, tCouleur]}
    );
    return true;
  }

function retire_couleur_bulles_projet (pCouleur)
  {
    const tCouleur = typeof pCouleur === "string" ? pCouleur.trim ().toLowerCase () : "";
    const tReglages = brouillon_reglages_bulles_projet ();
    const tCouleurs = tReglages.couleurs_auto.filter ((pItem) => pItem !== tCouleur);
    if (tCouleurs.length === tReglages.couleurs_auto.length) return false;
    modifie_brouillon_reglages_bulles_projet ({couleurs_auto : tCouleurs});
    return true;
  }

function style_attr_aspect_ratio_rapport_lh_scene (pValeur)
  {
    return `aspect-ratio: ${format_css_rapport_lh_scene_projet (pValeur)};`;
  }

function adapte_vue_au_ratio_cible (pVue, pRatioCible)
  {
    const tVue = normalise_vue_carte_client (pVue);
    const tRatio = Number (pRatioCible);
    if (!tVue || !Number.isFinite (tRatio) || tRatio <= 0) return tVue;
    const tRatioVue = tVue.w / tVue.h;
    if (!Number.isFinite (tRatioVue) || tRatioVue <= 0) return tVue;
    if (Math.abs (tRatioVue - tRatio) < 0.0005) return {...tVue};

    let tLargeur = tVue.w;
    let tHauteur = tVue.h;
    if (tRatioVue > tRatio) tHauteur = tLargeur / tRatio;
    else                    tLargeur = tHauteur * tRatio;

    return normalise_vue_carte_client (
      {
        x : tVue.x - ((tLargeur - tVue.w) / 2),
        y : tVue.y - ((tHauteur - tVue.h) / 2),
        w : tLargeur,
        h : tHauteur,
      },
      tVue
    );
  }

function copie_vue_sur_place (pDestination, pSource)
  {
    pDestination.x = pSource.x;
    pDestination.y = pSource.y;
    pDestination.w = pSource.w;
    pDestination.h = pSource.h;
  }

function applique_geometrie_scene_sur_vue (pVue, pGeometrie)
  {
    const tVue = normalise_vue_carte_client (pVue);
    if (!tVue) return null;
    const tGeom = normalise_geometrie_scene_client (pGeometrie);
    const tScale = Math.max (0.01, 1 + (tGeom.offset_z / 100));
    const tLargeur = tVue.w * tScale;
    const tHauteur = tVue.h * tScale;
    return normalise_vue_carte_client (
      {
        x : tVue.x + tGeom.offset_x - ((tLargeur - tVue.w) / 2),
        y : tVue.y + tGeom.offset_y - ((tHauteur - tVue.h) / 2),
        w : tLargeur,
        h : tHauteur,
      },
      tVue
    );
  }

function retire_geometrie_scene_sur_vue (pVue, pGeometrie)
  {
    const tVue = normalise_vue_carte_client (pVue);
    if (!tVue) return null;
    const tGeom = normalise_geometrie_scene_client (pGeometrie);
    const tScale = Math.max (0.01, 1 + (tGeom.offset_z / 100));
    const tLargeur = tVue.w / tScale;
    const tHauteur = tVue.h / tScale;
    return normalise_vue_carte_client (
      {
        x : tVue.x - tGeom.offset_x + ((tVue.w - tLargeur) / 2),
        y : tVue.y - tGeom.offset_y + ((tVue.h - tHauteur) / 2),
        w : tLargeur,
        h : tHauteur,
      },
      tVue
    );
  }

function convertit_point_entre_vues_scene (pPoint, pVueSource, pVueDestination)
  {
    const tSource = normalise_vue_carte_client (pVueSource);
    const tDestination = normalise_vue_carte_client (pVueDestination);
    if (!pPoint || !tSource || !tDestination || !tSource.w || !tSource.h) return null;
    return {
      x : tDestination.x + ((pPoint.x - tSource.x) / tSource.w) * tDestination.w,
      y : tDestination.y + ((pPoint.y - tSource.y) / tSource.h) * tDestination.h,
    };
  }

function geometrie_scene_active_tpl (pTpl)
  {
    return normalise_geometrie_scene_client (
      (pTpl && pTpl._sceneGeometrieActive) || brouillon_geometrie_scene ()
    );
  }

function vue_camera_svg_courante_scene (pTpl)
  {
    return normalise_vue_carte_client (pTpl && pTpl._svgVB);
  }

function vue_camera_brute_depuis_vue_svg_scene (pTpl, pVueSvg)
  {
    const tVueSvg = normalise_vue_carte_client (pVueSvg);
    if (!tVueSvg) return null;
    return retire_geometrie_scene_sur_vue (tVueSvg, geometrie_scene_active_tpl (pTpl)) || tVueSvg;
  }

function vue_camera_svg_depuis_vue_brute_scene (pTpl, pVueBrute)
  {
    const tVueBrute = normalise_vue_carte_client (pVueBrute);
    if (!tVueBrute) return null;
    return applique_geometrie_scene_sur_vue (tVueBrute, geometrie_scene_active_tpl (pTpl)) || tVueBrute;
  }

function vue_camera_brute_courante_scene (pTpl)
  {
    return normalise_vue_carte_client (pTpl && pTpl._svgVBBrut)
        || vue_camera_brute_depuis_vue_svg_scene (pTpl, pTpl && pTpl._svgVB);
  }

function transform_calque_cartes_scene (pVueBrute, pVueSvg)
  {
    const tBrute = normalise_vue_carte_client (pVueBrute);
    const tSvg = normalise_vue_carte_client (pVueSvg);
    if (!tBrute || !tSvg || !tBrute.w || !tBrute.h) return "";

    const tScaleX = tSvg.w / tBrute.w;
    const tScaleY = tSvg.h / tBrute.h;
    const tTranslateX = tSvg.x - (tBrute.x * tScaleX);
    const tTranslateY = tSvg.y - (tBrute.y * tScaleY);
    if (Math.abs (tScaleX - 1) < 0.000001
     && Math.abs (tScaleY - 1) < 0.000001
     && Math.abs (tTranslateX) < 0.000001
     && Math.abs (tTranslateY) < 0.000001)
      return "";

    const tNombre = (pValeur) => Number (pValeur || 0).toFixed (6).replace (/\.0+$/, "").replace (/(\.\d*?)0+$/, "$1");
    return `matrix(${tNombre (tScaleX)} 0 0 ${tNombre (tScaleY)} ${tNombre (tTranslateX)} ${tNombre (tTranslateY)})`;
  }

function transform_calque_cartes_tpl (pTpl)
  {
    return transform_calque_cartes_scene (
      vue_camera_brute_courante_scene (pTpl),
      vue_camera_svg_courante_scene (pTpl)
    );
  }

function transform_calque_cartes_doc (pDoc)
  {
    const tTpl = (gSceneTemplateActif && gSceneTemplateActif._svgDoc === pDoc)
      ? gSceneTemplateActif
      : null;
    return tTpl ? transform_calque_cartes_tpl (tTpl) : "";
  }

function union_vues_cartes_client (...pVues)
  {
    const tVues = pVues.map ((pVue) => normalise_vue_carte_client (pVue)).filter (Boolean);
    if (!tVues.length) return null;
    const tMinX = Math.min (...tVues.map ((pVue) => pVue.x));
    const tMinY = Math.min (...tVues.map ((pVue) => pVue.y));
    const tMaxX = Math.max (...tVues.map ((pVue) => pVue.x + pVue.w));
    const tMaxY = Math.max (...tVues.map ((pVue) => pVue.y + pVue.h));
    return {
      x : tMinX,
      y : tMinY,
      w : Math.max (0, tMaxX - tMinX),
      h : Math.max (0, tMaxY - tMinY),
    };
  }

function vue_scene_decorations_cartes_tpl (pTpl)
  {
    const tVueCourante = vue_camera_brute_courante_scene (pTpl);
    const tVueTransitionDepart = normalise_vue_carte_client (pTpl && pTpl._lectureDecorTransitionVueDepartBrute);
    const tVueTransitionCible = normalise_vue_carte_client (pTpl && pTpl._lectureDecorTransitionVueCibleBrute);
    return tVueTransitionDepart
      ? union_vues_cartes_client (tVueCourante, tVueTransitionDepart, tVueTransitionCible)
      : tVueCourante;
  }

function applique_transform_calque_cartes_noeud (pNoeud, pTransform)
  {
    if (!pNoeud || !pNoeud.setAttribute) return;
    if (pTransform) pNoeud.setAttribute ("transform", pTransform);
    else            pNoeud.removeAttribute ("transform");
  }

function applique_transform_calques_cartes_racine (pRoot, pTransform, pOptions = {})
  {
    if (!pRoot || typeof pRoot.querySelectorAll !== "function") return;
    const tSkipZoomAlt = !!(pOptions && pOptions.skipZoomAlt);
    const tNoeuds = pRoot.querySelectorAll (
      `.${CARTE_DECOR_FOND_CLASS}, .${CARTE_DECOR_MASQUE_CLASS}, .${CARTE_DECOR_BULLES_LAYER_CLASS}, .${CARTE_ZOOM_ALTERNATIF_GROUP_CLASS}, .${HOVER_BULLE_GROUP_CLASS}, .${SELECTION_BULLE_GROUP_CLASS}, .${CATEGORIE_BULLES_GROUP_CLASS}`
    );
    tNoeuds.forEach ((pNoeud) =>
      {
        if (tSkipZoomAlt && pNoeud.classList && pNoeud.classList.contains (CARTE_ZOOM_ALTERNATIF_GROUP_CLASS)) return;
        applique_transform_calque_cartes_noeud (pNoeud, pTransform);
      });
  }

function actualise_transform_calques_cartes_tpl (pTpl)
  {
    const tRoot = pTpl && pTpl._svgDoc && pTpl._svgDoc.documentElement;
    if (!tRoot) return;
    if (pTpl._lectureZoomAltCameraAnimation) return;
    actualise_geometrie_masque_fond_cartes_racine (
      tRoot,
      vue_scene_decorations_cartes_tpl (pTpl)
    );
    applique_transform_calques_cartes_racine (tRoot, transform_calque_cartes_tpl (pTpl));
  }

function point_cartes_brut_depuis_point_svg (pTpl, pPoint)
  {
    const tPoint = pPoint && Number.isFinite (pPoint.x) && Number.isFinite (pPoint.y)
      ? pPoint
      : null;
    if (!tPoint) return null;
    return convertit_point_entre_vues_scene (
      tPoint,
      vue_camera_svg_courante_scene (pTpl),
      vue_camera_brute_courante_scene (pTpl)
    ) || tPoint;
  }

function actualise_rapport_lh_scene_tpl (pTpl, pOptions = {})
  {
    if (!pTpl) return;

    const tRapport = rapport_lh_scene_projet_courant ();
    const tRatioNum = ratio_rapport_lh_scene_projet (tRapport);
    const tRatioCss = format_css_rapport_lh_scene_projet (tRapport);
    const tSceneId = Session.get ("scene_id");
    const tScene = tSceneId ? Scenes.findOne (tSceneId) : null;
    const tGeometrieBase = geometrie_scene_courante (tScene);
    const tGeometrie = brouillon_geometrie_scene ();
    const tGeometrieSignature = JSON.stringify (tGeometrie);
    const tForce = !!(pOptions && pOptions.force);
    const tRecentrer = !pOptions || pOptions.recentrer !== false;

    if (!tForce
     && pTpl._sceneRatioKey === tRatioCss
     && pTpl._sceneGeometrieKey === tGeometrieSignature)
      {
        programme_maj_cadre_scene_dimensions_tpl (pTpl);
        return;
      }

    const tRoot = pTpl._svgDoc && pTpl._svgDoc.documentElement;
    if (!tRoot || !pTpl._svgVB || typeof pTpl._svgApplique !== "function")
      {
        pTpl._sceneRatioKey = tRatioCss;
        programme_maj_cadre_scene_dimensions_tpl (pTpl);
        return;
      }

    const tGeometriePrecedente = normalise_geometrie_scene_client (
      pTpl._sceneGeometrieActive || tGeometrieBase
    );
    const tVueSourceBrute = normalise_vue_carte_client (
      pTpl._svgVBSourceBrut
      || pTpl._svgVBNatifBrut
      || retire_geometrie_scene_sur_vue (vue_svg_racine_client (tRoot), tGeometrieBase)
      || retire_geometrie_scene_sur_vue (pTpl._svgVBNatif, tGeometriePrecedente)
      || retire_geometrie_scene_sur_vue (pTpl._svgVB, tGeometriePrecedente)
      || vue_svg_racine_client (tRoot)
      || pTpl._svgVB
    );
    const tVueSourceRatio = adapte_vue_au_ratio_cible (tVueSourceBrute, tRatioNum) || tVueSourceBrute;
    if (tVueSourceBrute) pTpl._svgVBSourceBrut = {...tVueSourceBrute};
    if (tVueSourceRatio) pTpl._svgVBNatifBrut = {...tVueSourceRatio};

    const tTplGeometrie = {_sceneGeometrieActive : tGeometrie};
    const tVueNatif = vue_camera_svg_depuis_vue_brute_scene (tTplGeometrie, tVueSourceRatio)
      || tVueSourceRatio;
    if (tVueNatif) pTpl._svgVBNatif = {...tVueNatif};

    const tVueDefautBrute = adapte_vue_au_ratio_cible (
      normalise_vue_carte_client (tScene && tScene.vue_defaut, tVueSourceRatio),
      tRatioNum
    ) || normalise_vue_carte_client (tVueSourceRatio);
    if (tVueDefautBrute) pTpl._svgVBInitBrut = {...tVueDefautBrute};
    const tVueInitiale = vue_camera_svg_depuis_vue_brute_scene (tTplGeometrie, tVueDefautBrute)
      || tVueDefautBrute;
    if (tVueInitiale) pTpl._svgVBInit = {...tVueInitiale};

    const tVueCouranteBrute = tRecentrer
      ? tVueDefautBrute
      : adapte_vue_au_ratio_cible (
        normalise_vue_carte_client (
          pTpl._svgVBBrut
          || retire_geometrie_scene_sur_vue (pTpl._svgVB, tGeometriePrecedente),
          tVueDefautBrute
        ),
        tRatioNum
      );
    const tVueCourante = vue_camera_svg_depuis_vue_brute_scene (tTplGeometrie, tVueCouranteBrute)
      || tVueCouranteBrute;
    if (tVueCourante)
      {
        if (tVueCouranteBrute) pTpl._svgVBBrut = {...tVueCouranteBrute};
        pTpl._sceneGeometrieActive = {...tGeometrie};
        copie_vue_sur_place (pTpl._svgVB, tVueCourante);
        pTpl._svgApplique ();
      }

    pTpl._sceneRatioKey = tRatioCss;
    pTpl._sceneGeometrieKey = tGeometrieSignature;
    pTpl._sceneGeometrieActive = {...tGeometrie};
    programme_maj_cadre_scene_dimensions_tpl (pTpl);
    if (mode_lecture_scene_actif ()) rafraichit_scene_lecture_tpl (pTpl, false);
  }

function reglages_import_audio_projet_courant (pProjet = projet_courant_scene ())
  {
    return normalise_reglages_import_audio_projet (
      pProjet && pProjet.reglages_import_audio
    );
  }

function brouillon_reglages_import_audio_projet ()
  {
    const tBrouillon = gInspecteurProjetReglagesImportAudio.get ();
    if (tBrouillon)
      return normalise_reglages_import_audio_projet (tBrouillon);
    return reglages_import_audio_projet_courant ();
  }

function modifie_brouillon_reglages_import_audio_projet (pPatch)
  {
    const tCourant = brouillon_reglages_import_audio_projet ();
    gInspecteurProjetReglagesImportAudio.set (
      normalise_reglages_import_audio_projet ({...tCourant, ...(pPatch || {})})
    );
    gInspecteurProjetImportAudioMessage.set (null);
  }

function reglages_mode_lecture_projet_courant (pProjet = projet_courant_scene ())
  {
    return normalise_reglages_mode_lecture_projet (
      pProjet && pProjet.reglages_mode_lecture
    );
  }

function brouillon_reglages_mode_lecture_projet ()
  {
    const tBrouillon = gInspecteurProjetReglagesModeLecture.get ();
    const tReglages = tBrouillon
      ? normalise_reglages_mode_lecture_projet (tBrouillon)
      : reglages_mode_lecture_projet_courant ();
    const tBrouillonInterface = gInspecteurProjetReglagesInterfaceCarte.get ();
    if (!tBrouillonInterface) return tReglages;
    return normalise_reglages_mode_lecture_projet (
      {
        ...tReglages,
        ...reglages_interface_carte_pour_stockage (tBrouillonInterface),
      }
    );
  }

function modifie_brouillon_reglages_mode_lecture_projet (pPatch)
  {
    const tCourant = brouillon_reglages_mode_lecture_projet ();
    gInspecteurProjetReglagesModeLecture.set (
      normalise_reglages_mode_lecture_projet ({...tCourant, ...(pPatch || {})})
    );
    gInspecteurProjetModeLectureMessage.set (null);
    programme_actualisation_brouillon_zoom_carte_tpl (gSceneTemplateActif);
  }

function reglages_interface_carte_projet_courants (pProjet = projet_courant_scene ())
  {
    return reglages_interface_carte_pour_stockage (
      reglages_mode_lecture_projet_courant (pProjet)
    );
  }

function brouillon_reglages_interface_carte_projet ()
  {
    const tBrouillon = gInspecteurProjetReglagesInterfaceCarte.get ();
    if (tBrouillon)
      return reglages_interface_carte_pour_stockage (tBrouillon);
    return reglages_interface_carte_projet_courants ();
  }

function modifie_brouillon_reglages_interface_carte_projet (pPatch)
  {
    const tCourant = brouillon_reglages_interface_carte_projet ();
    const tReglages = reglages_interface_carte_pour_stockage ({...tCourant, ...(pPatch || {})});
    gInspecteurProjetReglagesInterfaceCarte.set (tReglages);
    gInspecteurProjetModeLectureMessage.set (null);
    applique_preview_reglages_interface_carte_tpl (tReglages);
  }

function inspecteur_projet_general_est_modifie (pProjet = projet_courant_scene ())
  {
    if (!pProjet) return false;
    const tNom = (gInspecteurProjetNom.get () === null ? (pProjet.nom || "") : gInspecteurProjetNom.get ()).trim ();
    const tSlug = fabrique_slug (
      gInspecteurProjetSlug.get () === null
        ? (pProjet.slug || "")
        : gInspecteurProjetSlug.get ()
    );
    return tNom !== (pProjet.nom || "")
      || tSlug !== (pProjet.slug || "")
      || JSON.stringify (brouillon_rapport_lh_scene_projet ())
         !== JSON.stringify (rapport_lh_scene_projet_courant (pProjet))
      || JSON.stringify (brouillon_reglages_mode_lecture_projet ())
         !== JSON.stringify (reglages_mode_lecture_projet_courant (pProjet));
  }

function inspecteur_projet_bulles_est_modifie (pProjet = projet_courant_scene ())
  {
    if (!pProjet) return false;
    return JSON.stringify (brouillon_reglages_bulles_projet ())
      !== JSON.stringify (reglages_bulles_projet_courant (pProjet));
  }

function inspecteur_projet_audio_import_est_modifie (pProjet = projet_courant_scene ())
  {
    if (!pProjet) return false;
    return JSON.stringify (brouillon_reglages_import_audio_projet ())
      !== JSON.stringify (reglages_import_audio_projet_courant (pProjet));
  }

function inspecteur_projet_mode_lecture_est_modifie (pProjet = projet_courant_scene ())
  {
    if (!pProjet || pProjet.type !== "chasse_aux_sons") return false;
    return JSON.stringify (brouillon_reglages_mode_lecture_projet ())
      !== JSON.stringify (reglages_mode_lecture_projet_courant (pProjet));
  }

function inspecteur_projet_section_est_modifie (pSection, pProjet = projet_courant_scene ())
  {
    switch (pSection)
      {
        case INSPECTEUR_PROJET_SECTION_GENERAL:
          return inspecteur_projet_general_est_modifie (pProjet);
        case INSPECTEUR_PROJET_SECTION_BULLES:
          return inspecteur_projet_bulles_est_modifie (pProjet);
        case INSPECTEUR_PROJET_SECTION_AUDIO_IMPORT:
          return inspecteur_projet_audio_import_est_modifie (pProjet);
        case INSPECTEUR_PROJET_SECTION_MODE_LECTURE:
          return inspecteur_projet_mode_lecture_est_modifie (pProjet);
        default:
          return false;
      }
  }

function inspecteur_projet_message_reactive_pour_section (pSection)
  {
    switch (pSection)
      {
        case INSPECTEUR_PROJET_SECTION_GENERAL:
          return gInspecteurProjetMessage;
        case INSPECTEUR_PROJET_SECTION_BULLES:
          return gInspecteurProjetBullesMessage;
        case INSPECTEUR_PROJET_SECTION_AUDIO_IMPORT:
          return gInspecteurProjetImportAudioMessage;
        case INSPECTEUR_PROJET_SECTION_MODE_LECTURE:
          return gInspecteurProjetModeLectureMessage;
        default:
          return null;
      }
  }

function inspecteur_projet_section_sauvegarde_disabled_attr_client (pSection)
  {
    if (!utilisateur_peut_modifier_projet_inspecteur ()) return "disabled";
    if (gInspecteurProjetSavingSection.get ()) return "disabled";
    return inspecteur_projet_section_est_modifie (pSection) ? null : "disabled";
  }

function inspecteur_projet_section_sauvegarde_label_client (pSection)
  {
    return gInspecteurProjetSavingSection.get () === pSection
      ? "Enregistrement…"
      : "Enregistrer";
  }

function sauvegarde_inspecteur_projet_section_client (pSection)
  {
    applique_inspecteur_projet_depuis_champ (champ_actif_document ());

    const tProjet = projet_courant_scene ();
    if (!tProjet || gInspecteurProjetSavingSection.get ()) return;
    if (!inspecteur_projet_section_est_modifie (pSection, tProjet)) return;

    const tMessageVar = inspecteur_projet_message_reactive_pour_section (pSection);
    if (tMessageVar) tMessageVar.set (null);

    const tOp = {
      projet_id : tProjet._id,
      nom : tProjet.nom || "",
      slug : tProjet.slug || "",
    };
    let tSucces = "";
    let tAncSlug = tProjet.slug || "";

    switch (pSection)
      {
        case INSPECTEUR_PROJET_SECTION_GENERAL:
          {
            const tNomInput = typeof document !== "undefined"
              ? document.getElementById ("inspecteur_projet_nom")
              : null;
            const tSlugInput = typeof document !== "undefined"
              ? document.getElementById ("inspecteur_projet_slug")
              : null;
            const tRapportInput = typeof document !== "undefined"
              ? document.getElementById ("inspecteur_projet_rapport_lh_scene")
              : null;
            const tNom = ((tNomInput && tNomInput.value) || (gInspecteurProjetNom.get () === null ? (tProjet.nom || "") : gInspecteurProjetNom.get ()) || "").trim ();
            const tSlug = fabrique_slug (((tSlugInput && tSlugInput.value) || (gInspecteurProjetSlug.get () === null ? (tProjet.slug || "") : gInspecteurProjetSlug.get ()) || ""));
            const tRapportTexte = ((tRapportInput && tRapportInput.value) || format_rapport_lh_scene_projet (brouillon_rapport_lh_scene_projet ()) || "").trim ();
            if (!tNom)
              {
                gInspecteurProjetMessage.set ({texte : "Le nom du projet est requis.", type : "erreur"});
                return;
              }
            if (!tSlug)
              {
                gInspecteurProjetMessage.set ({texte : "Le slug du projet est requis.", type : "erreur"});
                return;
              }
            if (tRapportTexte)
              {
                const tRapport = parse_rapport_lh_scene_projet (tRapportTexte);
                if (!tRapport)
                  {
                    gInspecteurProjetMessage.set ({texte : "Rapport L/H invalide. Exemple : 16/9", type : "erreur"});
                    return;
                  }
                tOp.rapport_lh_scene = tRapport;
              }
            else
              {
                tOp.rapport_lh_scene = null;
              }
            tOp.nom = tNom;
            tOp.slug = tSlug;
            tOp.reglages_mode_lecture = brouillon_reglages_mode_lecture_projet ();
            tSucces = "Informations du projet mises à jour.";
          }
          break;

        case INSPECTEUR_PROJET_SECTION_BULLES:
          tOp.reglages_bulles = brouillon_reglages_bulles_projet ();
          tSucces = "Réglages des bulles du projet mis à jour.";
          break;

        case INSPECTEUR_PROJET_SECTION_AUDIO_IMPORT:
          tOp.reglages_import_audio = brouillon_reglages_import_audio_projet ();
          tSucces = "Réglages d'import audio mis à jour.";
          break;

        case INSPECTEUR_PROJET_SECTION_MODE_LECTURE:
          tOp.reglages_mode_lecture = brouillon_reglages_mode_lecture_projet ();
          tSucces = "Réglages du mode lecture mis à jour.";
          break;

        default:
          return;
      }

    gInspecteurProjetSavingSection.set (pSection);
    Meteor.call (
      "maj_projet_nom_slug",
      tOp,
      (pErr, pRes) =>
        {
          gInspecteurProjetSavingSection.set (null);
          if (pErr)
            {
              if (tMessageVar)
                tMessageVar.set ({texte : "Erreur : " + (pErr.reason || pErr.message), type : "erreur"});
              return;
            }
          if (pRes === "slug existant")
            {
              if (tMessageVar)
                tMessageVar.set ({texte : "Ce slug est déjà utilisé par un autre de vos projets.", type : "erreur"});
              return;
            }

          switch (pSection)
            {
              case INSPECTEUR_PROJET_SECTION_GENERAL:
                gInspecteurProjetNom.set (null);
                gInspecteurProjetSlug.set (null);
                gInspecteurProjetSlugManuel.set (false);
                gInspecteurProjetRapportLhScene.set (null);
                break;

              case INSPECTEUR_PROJET_SECTION_BULLES:
                gInspecteurProjetReglagesBulles.set (null);
                break;

              case INSPECTEUR_PROJET_SECTION_AUDIO_IMPORT:
                gInspecteurProjetReglagesImportAudio.set (null);
                break;

              case INSPECTEUR_PROJET_SECTION_MODE_LECTURE:
                gInspecteurProjetReglagesModeLecture.set (null);
                gInspecteurProjetReglagesInterfaceCarte.set (null);
                applique_preview_reglages_interface_carte_tpl (null);
                break;

              default:
                break;
            }

          if (tMessageVar)
            tMessageVar.set ({texte : tSucces, type : "succes"});

          if (pSection === INSPECTEUR_PROJET_SECTION_GENERAL && tOp.slug && tOp.slug !== tAncSlug)
            {
              const tSlugs = Session.get ("projet_slugs") || {};
              const tScene = tSlugs.scene ? `/${tSlugs.scene}` : "";
              if (tSlugs.user)
                FlowRouter.go (`/projets/${tSlugs.user}/${tOp.slug}${tScene}`);
            }
        }
    );
  }

function reglages_mode_lecture_scene ()
  {
    return brouillon_reglages_mode_lecture_projet ();
  }

function clone_reglages_interface_carte_client (pValeur)
  {
    return extrait_reglages_interface_carte (pValeur || {});
  }

function reglages_interface_carte_scene_sauves (pScene = Scenes.findOne (Session.get ("scene_id")))
  {
    return pScene && pScene.reglages_interface_carte
      ? clone_reglages_interface_carte_client (pScene.reglages_interface_carte)
      : null;
  }

function brouillon_reglages_interface_carte_scene ()
  {
    const tBrouillon = gInspecteurCartesInterfaceDraft.get ();
    const tSceneId = Session.get ("scene_id");
    if (tBrouillon && tBrouillon.scene_id === tSceneId)
      return clone_reglages_interface_carte_client (tBrouillon.reglages);
    return reglages_interface_carte_scene_sauves ();
  }

function base_reglages_interface_carte ()
  {
    return brouillon_reglages_mode_lecture_projet ();
  }

function reglages_interface_carte_effectifs (pCarte = null, pOverride = null)
  {
    const tBase = base_reglages_interface_carte ();
    const tSceneOverride = brouillon_reglages_interface_carte_scene ();
    const tBrouillonBulle = brouillon_bulle_courant ();
    const tCarteId = pCarte && pCarte._id;
    const tCarteOverride = tBrouillonBulle && (!tCarteId || tBrouillonBulle.carte_id === tCarteId)
      ? tBrouillonBulle.reglages_interface_carte
      : (pCarte && pCarte.reglages_interface_carte);
    const tReglages = normalise_reglages_mode_lecture_projet (
      {
        ...tBase,
        ...(tSceneOverride || {}),
        ...(tCarteOverride || {}),
        ...(pOverride || {}),
      }
    );
    REGLAGES_INTERFACE_CARTE_ANIMATION_CLES.forEach ((pCle) =>
      {
        tReglages[pCle] = tBase[pCle];
      });
    return tReglages;
  }

function reglages_interface_carte_pour_stockage (pReglages)
  {
    return clone_reglages_interface_carte_client (pReglages || {});
  }

function reglages_interface_carte_signature (pReglages)
  {
    return JSON.stringify (reglages_interface_carte_pour_stockage (pReglages || {}));
  }

function modifie_brouillon_reglages_interface_carte_scene (pPatch)
  {
    const tCourant = brouillon_reglages_interface_carte_scene ()
      || reglages_interface_carte_pour_stockage (base_reglages_interface_carte ());
    const tReglages = reglages_interface_carte_pour_stockage ({...tCourant, ...(pPatch || {})});
    gInspecteurCartesInterfaceDraft.set (
      {
        scene_id : Session.get ("scene_id"),
        reglages : tReglages,
      }
    );
    gInspecteurCartesGlobalMessage.set (null);
    applique_preview_reglages_interface_carte_tpl (tReglages);
  }

function modifie_brouillon_reglages_interface_carte_bulle (pPatch)
  {
    const tBrouillon = brouillon_bulle_courant ();
    if (!tBrouillon) return false;
    const tCourant = tBrouillon.reglages_interface_carte
      || reglages_interface_carte_pour_stockage (reglages_interface_carte_effectifs (carte_bulle_selectionnee_scene ()));
    const tReglages = reglages_interface_carte_pour_stockage ({...tCourant, ...(pPatch || {})});
    const tOk = maj_brouillon_bulle_calque ({reglages_interface_carte : tReglages});
    if (tOk) applique_preview_reglages_interface_carte_tpl (tReglages);
    return tOk;
  }

function patch_reglage_interface_carte_depuis_champ (pChamp)
  {
    if (!pChamp || !pChamp.getAttribute) return null;
    const tCle = pChamp.getAttribute ("data-cle");
    if (!REGLAGES_INTERFACE_CARTE_CLES_SET.has (tCle)) return null;
    const tType = (pChamp.getAttribute ("type") || "").toLowerCase ();
    const tValeur = tType === "color" || pChamp.tagName === "SELECT"
      ? pChamp.value
      : valeur_numerique_champ_ou_defaut (
          pChamp,
          REGLAGES_MODE_LECTURE_PROJET_DEFAUTS[tCle]
        );
    return {[tCle] : tValeur};
  }

function items_interface_carte_inspecteur (pReglages, pPrefix, pOpt = {})
  {
    const tReglages = pReglages || reglages_interface_carte_effectifs ();
    const tFichiers = fichiers_zoom_alternatif_projet_courant ();
    return ZOOM_ALTERNATIF_SVG_ROLES.map ((pRole) =>
      {
        const tFichier = tFichiers.get (pRole.cle) || null;
        const tTailleCle = cle_taille_zoom_alternatif_role (pRole.cle);
        const tOffsetXCle = cle_offset_zoom_alternatif_role (pRole.cle, "x");
        const tOffsetYCle = cle_offset_zoom_alternatif_role (pRole.cle, "y");
        const tAncrageCle = cle_ancrage_zoom_alternatif_role (pRole.cle);
        return {
          ...pRole,
          fichierNom : tFichier ? tFichier.nom : "Placeholder ?",
          fichierUrl : tFichier ? tFichier.url : null,
          tailleCle : tTailleCle,
          tailleInputId : `${pPrefix}_taille_${pRole.cle}`,
          tailleValeur : tReglages[tTailleCle],
          offsetXCle : tOffsetXCle,
          offsetYCle : tOffsetYCle,
          offsetXInputId : `${pPrefix}_offset_x_${pRole.cle}`,
          offsetYInputId : `${pPrefix}_offset_y_${pRole.cle}`,
          offsetXValeur : tReglages[tOffsetXCle],
          offsetYValeur : tReglages[tOffsetYCle],
          ancrageCle : tAncrageCle,
          ancrageInputId : `${pPrefix}_ancrage_${pRole.cle}`,
          ancrageGaucheSelected : tAncrageCle && tReglages[tAncrageCle] === "gauche" ? "selected" : null,
          ancrageDroiteSelected : tAncrageCle && tReglages[tAncrageCle] !== "gauche" ? "selected" : null,
          ancrageVisible : tAncrageCle ? true : false,
          uploadLabel : pOpt.uploadLabel || "SVG…",
          uploadVisible : pOpt.uploadVisible ? true : false,
          jaugeEditVisible : pRole.cle === JAUGE_VISUELLE_ROLE && pOpt.jaugeEditVisible ? true : false,
          disabledAttr : pOpt.disabledAttr || null,
        };
      });
  }

function fps_animation_lecture_scene ()
  {
    return mode_lecture_scene_actif () && reglages_mode_lecture_scene ().animation_30fps_actif ? 30 : 0;
  }

function delai_frame_animation_lecture_scene ()
  {
    const tFps = fps_animation_lecture_scene ();
    return tFps > 0 ? 1000 / tFps : 0;
  }

function reinitialise_dialogue_participants_projet ()
  {
    gInspecteurProjetDialogSaving.set (false);
    gInspecteurProjetDialogSelection.clear ();
  }

function inspecteur_projet_contacts_ids ()
  {
    const tUser = Meteor.user ();
    return Array.isArray (tUser && tUser.contacts) ? tUser.contacts.filter (Boolean) : [];
  }

function inspecteur_projet_contacts_disponibles ()
  {
    const tProjet = projet_courant_scene ();
    if (!tProjet) return [];
    const tMembres = new Set ([
      tProjet.user_id,
      ...((tProjet.membres || []).filter (Boolean)),
      ...((tProjet.admins || []).filter (Boolean)),
    ]);
    return Meteor.users.find (
      {_id : {$in : inspecteur_projet_contacts_ids ().filter ((pId) => !tMembres.has (pId))}},
      {sort : {'profile.name' : 1, slug : 1}}
    ).map ((pUser) =>
      ({
        ...utilisateur_affiche_scene (pUser._id),
        _id : pUser._id,
        checkedAttr : gInspecteurProjetDialogSelection.get (pUser._id) ? "checked" : null,
      }));
  }

function inspecteur_projet_membres_liste ()
  {
    const tProjet = projet_courant_scene ();
    if (!tProjet) return [];
    const tAdminIds = new Set ((Array.isArray (tProjet.admins) ? tProjet.admins : []).filter (Boolean));
    const tOwner = utilisateur_affiche_scene (tProjet.user_id);
    const tAutresIds = [...new Set ([
      ...((tProjet.membres || []).filter (Boolean)),
      ...((tProjet.admins || []).filter (Boolean)),
    ])]
      .filter ((pId) => pId && pId !== tProjet.user_id)
      .slice ()
      .sort ((a, b) =>
        utilisateur_affiche_scene (a).displayName.localeCompare (
          utilisateur_affiche_scene (b).displayName,
          "fr",
          {sensitivity : "base"}
        ));
    return [
      {
        ...tOwner,
        _id : tProjet.user_id,
        isOwner : true,
        isAdmin : false,
        suffixe : "(propriétaire)",
        adminDisabledAttr : "disabled",
        adminAction : "",
        adminButtonLabel : "",
      },
      ...tAutresIds.map ((pId) =>
        {
          const tIsAdmin = tAdminIds.has (pId);
          return {
            ...utilisateur_affiche_scene (pId),
            _id : pId,
            isOwner : false,
            isAdmin : tIsAdmin,
            suffixe : tIsAdmin ? "(admin)" : "",
            removeDisabledAttr : utilisateur_peut_retirer_membre_projet_inspecteur (tProjet, pId) ? null : "disabled",
            adminDisabledAttr : utilisateur_peut_gerer_admins_projet_inspecteur () ? null : "disabled",
            adminAction : tIsAdmin ? "remove" : "add",
            adminButtonLabel : tIsAdmin ? "Retirer admin" : "Attribuer admin",
          };
        }),
    ];
  }

function inspecteur_projet_dialog_header_safe_top ()
  {
    if (typeof document === "undefined") return INSPECTEUR_PROJET_DIALOG_MARGIN;
    const tHeader = document.querySelector (".app-header");
    if (!tHeader) return INSPECTEUR_PROJET_DIALOG_MARGIN;
    const tRect = tHeader.getBoundingClientRect ();
    return Math.max (INSPECTEUR_PROJET_DIALOG_MARGIN, Math.ceil (tRect.bottom) + INSPECTEUR_PROJET_DIALOG_MARGIN);
  }

const gInspecteurProjetDialogCogibulle = cree_fenetre_cogibulle (
  {
    selector            : ".js_inspecteur_project_dialog_card",
    storage             : cree_stockage_local_fenetre_cogibulle (
      {
        boxKey  : INSPECTEUR_PROJET_DIALOG_BOX_KEY,
        openKey : INSPECTEUR_PROJET_DIALOG_OPEN_KEY,
      }),
    isOpen              : () => gInspecteurProjetDialogOpen.get (),
    setOpen             : (pOpen) => gInspecteurProjetDialogOpen.set (!!pOpen),
    minWidth            : INSPECTEUR_PROJET_DIALOG_MIN_W,
    minHeight           : INSPECTEUR_PROJET_DIALOG_MIN_H,
    preferredWidth      : 520,
    preferredHeight     : 430,
    widthViewportRatio  : 0.92,
    heightViewportRatio : 0.85,
    margin              : INSPECTEUR_PROJET_DIALOG_MARGIN,
    safeTop             : inspecteur_projet_dialog_header_safe_top,
    onEscape            : () => { inspecteur_projet_dialog_close (); },
  });

function inspecteur_projet_dialog_default_box ()
  {
    const tSafeTop = inspecteur_projet_dialog_header_safe_top ();
    const tW = Math.max (INSPECTEUR_PROJET_DIALOG_MIN_W,
      Math.min (520, Math.floor (window.innerWidth * 0.92)));
    const tH = Math.max (INSPECTEUR_PROJET_DIALOG_MIN_H,
      Math.min (430, Math.floor ((window.innerHeight - tSafeTop) * 0.85)));
    return {
      x : Math.max (INSPECTEUR_PROJET_DIALOG_MARGIN, Math.floor ((window.innerWidth - tW) / 2)),
      y : Math.max (tSafeTop, Math.floor ((window.innerHeight - tH + tSafeTop) / 2)),
      w : tW,
      h : tH,
    };
  }

function inspecteur_projet_dialog_normalise_box (pBox)
  {
    return gInspecteurProjetDialogCogibulle.normaliseBox (pBox);
  }

function inspecteur_projet_dialog_apply_box ()
  {
    gInspecteurProjetDialogCogibulle.applyBox ();
  }

function inspecteur_projet_dialog_remove_interaction_overlay ()
  {
    gInspecteurProjetDialogCogibulle.removeInteractionOverlay ();
  }

function inspecteur_projet_dialog_create_interaction_overlay (pCursor)
  {
    inspecteur_projet_dialog_remove_interaction_overlay ();
    if (typeof document === "undefined") return;
    const tOverlay = document.createElement ("div");
    tOverlay.style.cssText =
        "position:fixed;inset:0;z-index:1450;background:transparent;touch-action:none;"
      + "cursor:" + pCursor + ";";
    document.body.appendChild (tOverlay);
    document.body.style.userSelect = "none";
    document.body.style.cursor = pCursor;
    gInspecteurProjetDialog.interactionOverlay = tOverlay;
  }

function inspecteur_projet_dialog_open (pOptions = {})
  {
    gInspecteurProjetDialogSelection.clear ();
    return gInspecteurProjetDialogCogibulle.open (pOptions);
  }

function inspecteur_projet_dialog_close ()
  {
    gInspecteurProjetDialogSelection.clear ();
    gInspecteurProjetDialogSaving.set (false);
    return gInspecteurProjetDialogCogibulle.close ();
  }

const gInspecteurProjetJaugeVisuelleCogibulle = cree_fenetre_cogibulle (
  {
    selector            : ".js_inspecteur_jauge_visuelle_card",
    storage             : cree_stockage_local_fenetre_cogibulle (
      {
        boxKey  : INSPECTEUR_PROJET_JAUGE_VISUELLE_BOX_KEY,
        openKey : INSPECTEUR_PROJET_JAUGE_VISUELLE_OPEN_KEY,
      }),
    isOpen              : () => gInspecteurProjetJaugeVisuelleOpen.get (),
    setOpen             : (pOpen) => gInspecteurProjetJaugeVisuelleOpen.set (!!pOpen),
    minWidth            : INSPECTEUR_PROJET_JAUGE_VISUELLE_MIN_W,
    minHeight           : INSPECTEUR_PROJET_JAUGE_VISUELLE_MIN_H,
    preferredWidth      : INSPECTEUR_PROJET_JAUGE_VISUELLE_DEFAULT_BOX.w,
    preferredHeight     : INSPECTEUR_PROJET_JAUGE_VISUELLE_DEFAULT_BOX.h,
    defaultBox          : INSPECTEUR_PROJET_JAUGE_VISUELLE_DEFAULT_BOX,
    widthViewportRatio  : 0.96,
    heightViewportRatio : 0.9,
    margin              : INSPECTEUR_PROJET_DIALOG_MARGIN,
    safeTop             : inspecteur_projet_dialog_header_safe_top,
    onEscape            : () => { inspecteur_jauge_visuelle_close (); },
  });

function config_jauge_visuelle_brouillon ()
  {
    const tConfig = gInspecteurProjetJaugeVisuelleDerniereConfig.get ()
      || brouillon_reglages_mode_lecture_projet ().jauge_visuelle
      || null;
    return normalise_banana_progress_gauge_config (tConfig);
  }

function inspecteur_jauge_visuelle_open (pOptions = {})
  {
    gInspecteurProjetJaugeVisuelleDerniereConfig.set (config_jauge_visuelle_brouillon ());
    gInspecteurProjetJaugeVisuelleMessage.set (null);
    return gInspecteurProjetJaugeVisuelleCogibulle.open (pOptions);
  }

function inspecteur_jauge_visuelle_close ()
  {
    gInspecteurProjetJaugeVisuelleSaving.set (false);
    return gInspecteurProjetJaugeVisuelleCogibulle.close ();
  }

function config_jauge_visuelle_depuis_dom ()
  {
    if (typeof document === "undefined") return config_jauge_visuelle_brouillon ();
    const tTuner = document.querySelector (".js_inspecteur_jauge_visuelle_card .banana-progress-tuner");
    return normalise_banana_progress_gauge_config (
      tTuner && tTuner.__bananaProgressGaugeConfig
        ? tTuner.__bananaProgressGaugeConfig
        : config_jauge_visuelle_brouillon ()
    );
  }

function fichier_jauge_visuelle_projet ()
  {
    return fichier_zoom_alternatif_projet_role (JAUGE_VISUELLE_ROLE);
  }

function libelle_fichier_jauge_visuelle ()
  {
    const tFichier = fichier_jauge_visuelle_projet ();
    return tFichier && tFichier.nom
      ? tFichier.nom
      : "Banane de référence";
  }

function source_jauge_visuelle_projet ()
  {
    const tFichier = fichier_jauge_visuelle_projet ();
    const tModele = modele_svg_zoom_alternatif_client (JAUGE_VISUELLE_ROLE);
    if (!tFichier || !tFichier.url) return {url : "", svgText : "", viewBox : null};
    return {
      url : tFichier.url,
      svgText : (tModele && tModele.status === "ready" && tModele.svg_text) || "",
      viewBox : (tModele && tModele.viewBox) || null,
    };
  }

function sauvegarde_jauge_visuelle_projet_client ()
  {
    const tProjet = projet_courant_scene ();
    if (!tProjet || !utilisateur_peut_modifier_projet_inspecteur ()) return;
    if (gInspecteurProjetJaugeVisuelleSaving.get ()) return;

    const tConfig = config_jauge_visuelle_depuis_dom ();
    gInspecteurProjetJaugeVisuelleDerniereConfig.set (tConfig);
    modifie_brouillon_reglages_mode_lecture_projet ({jauge_visuelle : tConfig});

    const tReglages = brouillon_reglages_mode_lecture_projet ();
    gInspecteurProjetJaugeVisuelleSaving.set (true);
    gInspecteurProjetJaugeVisuelleMessage.set (null);
    Meteor.call (
      "maj_projet_nom_slug",
      {
        projet_id : tProjet._id,
        nom : tProjet.nom || "",
        slug : tProjet.slug || "",
        reglages_mode_lecture : tReglages,
      },
      (pErr) =>
        {
          gInspecteurProjetJaugeVisuelleSaving.set (false);
          if (pErr)
            {
              gInspecteurProjetJaugeVisuelleMessage.set ({
                texte : "Erreur : " + (pErr.reason || pErr.message),
                type : "erreur",
              });
              return;
            }
          gInspecteurProjetReglagesModeLecture.set (null);
          gInspecteurProjetJaugeVisuelleDerniereConfig.set (tConfig);
          gInspecteurProjetJaugeVisuelleMessage.set ({
            texte : "Paramètres de la jauge enregistrés.",
            type : "succes",
          });
          applique_preview_reglages_interface_carte_tpl (null);
        }
    );
  }

function inspecteur_projet_dialog_drag_start (pEvent)
  {
    gInspecteurProjetDialogCogibulle.startDrag (pEvent);
  }

function inspecteur_projet_dialog_drag_move (pEvent)
  {
    if (!gInspecteurProjetDialog.drag || !gInspecteurProjetDialog.box) return;
    if (gInspecteurProjetDialog.drag.pointerId !== undefined
        && pEvent.pointerId !== gInspecteurProjetDialog.drag.pointerId)
      return;
    const tSafeTop = inspecteur_projet_dialog_header_safe_top ();
    const tMaxX = window.innerWidth - gInspecteurProjetDialog.box.w - INSPECTEUR_PROJET_DIALOG_MARGIN;
    const tMaxY = window.innerHeight - gInspecteurProjetDialog.box.h - INSPECTEUR_PROJET_DIALOG_MARGIN;
    gInspecteurProjetDialog.box.x = Math.max (
      INSPECTEUR_PROJET_DIALOG_MARGIN,
      Math.min (tMaxX, pEvent.clientX - gInspecteurProjetDialog.drag.offsetX)
    );
    gInspecteurProjetDialog.box.y = Math.max (
      tSafeTop,
      Math.min (tMaxY, pEvent.clientY - gInspecteurProjetDialog.drag.offsetY)
    );
    inspecteur_projet_dialog_apply_box ();
  }

function inspecteur_projet_dialog_drag_end (pEvent)
  {
    if (!gInspecteurProjetDialog.drag) return;
    if (pEvent && gInspecteurProjetDialog.drag.pointerId !== undefined
        && pEvent.pointerId !== gInspecteurProjetDialog.drag.pointerId)
      return;
    gInspecteurProjetDialog.drag = null;
    document.removeEventListener ("pointermove", inspecteur_projet_dialog_drag_move);
    document.removeEventListener ("pointerup", inspecteur_projet_dialog_drag_end);
    document.removeEventListener ("pointercancel", inspecteur_projet_dialog_drag_end);
    inspecteur_projet_dialog_remove_interaction_overlay ();
  }

function inspecteur_projet_dialog_resize_start (pEvent)
  {
    gInspecteurProjetDialogCogibulle.startResize (pEvent);
  }

function inspecteur_projet_dialog_resize_move (pEvent)
  {
    if (!gInspecteurProjetDialog.resize || !gInspecteurProjetDialog.box) return;
    if (gInspecteurProjetDialog.resize.pointerId !== undefined
        && pEvent.pointerId !== gInspecteurProjetDialog.resize.pointerId)
      return;
    const tSafeTop = inspecteur_projet_dialog_header_safe_top ();
    const tMaxW = window.innerWidth - gInspecteurProjetDialog.box.x - INSPECTEUR_PROJET_DIALOG_MARGIN;
    const tMaxH = window.innerHeight - gInspecteurProjetDialog.box.y - INSPECTEUR_PROJET_DIALOG_MARGIN;
    gInspecteurProjetDialog.box.w = Math.max (
      INSPECTEUR_PROJET_DIALOG_MIN_W,
      Math.min (tMaxW, gInspecteurProjetDialog.resize.startW + (pEvent.clientX - gInspecteurProjetDialog.resize.startX))
    );
    gInspecteurProjetDialog.box.h = Math.max (
      INSPECTEUR_PROJET_DIALOG_MIN_H,
      Math.min (Math.max (INSPECTEUR_PROJET_DIALOG_MIN_H, window.innerHeight - tSafeTop - INSPECTEUR_PROJET_DIALOG_MARGIN),
        Math.min (tMaxH, gInspecteurProjetDialog.resize.startH + (pEvent.clientY - gInspecteurProjetDialog.resize.startY)))
    );
    inspecteur_projet_dialog_apply_box ();
  }

function inspecteur_projet_dialog_resize_end (pEvent)
  {
    if (!gInspecteurProjetDialog.resize) return;
    if (pEvent && gInspecteurProjetDialog.resize.pointerId !== undefined
        && pEvent.pointerId !== gInspecteurProjetDialog.resize.pointerId)
      return;
    gInspecteurProjetDialog.resize = null;
    document.removeEventListener ("pointermove", inspecteur_projet_dialog_resize_move);
    document.removeEventListener ("pointerup", inspecteur_projet_dialog_resize_end);
    document.removeEventListener ("pointercancel", inspecteur_projet_dialog_resize_end);
    inspecteur_projet_dialog_remove_interaction_overlay ();
  }

// Upload audio en cours pour le calque courant (bouton "Ajouter…")
const gInspecteurUploadEnCours = new ReactiveVar (false);
const gInspecteurUploadProgression = new ReactiveVar (0);
const gInspecteurUploadPhase = new ReactiveVar ("");
const gInspecteurMorphingUpload = new ReactiveVar (null);

function reinitialise_inspecteur_upload_audio ()
  {
    gInspecteurUploadEnCours.set (false);
    gInspecteurUploadProgression.set (0);
    gInspecteurUploadPhase.set ("");
  }

function phase_upload_inspecteur_label ()
  {
    return gInspecteurUploadPhase.get () === "traitement"
      ? "Traitement…"
      : "Téléversement…";
  }

// Lecture automatique des fichiers audio (à la sélection d'un calque
// avec audio, et après chaque génération). Persisté en localStorage.
const CLE_LS_INSPECTEUR_AUTOPLAY = "cogivoice_hub_inspecteur_autoplay";

function lit_inspecteur_autoplay ()
  {
    try
      {
        const v = localStorage.getItem (CLE_LS_INSPECTEUR_AUTOPLAY);
        if (v === null) return false;
        return v === "1" || v === "true";
      }
    catch (e) { return false; }
  }

const gInspecteurAutoplay = new ReactiveVar (lit_inspecteur_autoplay ());

const gInspecteurAutoplayTransition =
  {
    selectionKeyObservee : null,
    fileIdObserve        : null,
    selectionKeyJeton    : null,
    fileIdJeton          : null,
    modeJeton            : null,
    selectionInhibeeKey  : null,
    majFichierAutoriseSelectionKey : null,
  };

function fabrique_selection_key_inspecteur (pSceneId, pCalqueId)
  {
    return (pSceneId && pCalqueId) ? (pSceneId + ":" + pCalqueId) : null;
  }

function inhibe_autoplay_selection_inspecteur (pSelectionKey)
  {
    gInspecteurAutoplayTransition.selectionInhibeeKey = pSelectionKey || null;
  }

function autorise_autoplay_prochaine_maj_fichier_inspecteur (pSelectionKey)
  {
    gInspecteurAutoplayTransition.majFichierAutoriseSelectionKey = pSelectionKey || null;
  }

function annule_autoplay_prochaine_maj_fichier_inspecteur (pSelectionKey)
  {
    if (!pSelectionKey
        || gInspecteurAutoplayTransition.majFichierAutoriseSelectionKey === pSelectionKey)
      gInspecteurAutoplayTransition.majFichierAutoriseSelectionKey = null;
  }

function aligne_autoplay_maj_fichier_sur_selection_inspecteur (pSelectionKey)
  {
    const tSelectionKey = pSelectionKey || null;
    if (gInspecteurAutoplayTransition.majFichierAutoriseSelectionKey
        && gInspecteurAutoplayTransition.majFichierAutoriseSelectionKey !== tSelectionKey)
      gInspecteurAutoplayTransition.majFichierAutoriseSelectionKey = null;
  }

function note_transition_autoplay_inspecteur (pSelectionKey, pFileId)
  {
    const tSelectionKey = pSelectionKey || null;
    const tFileId = pFileId || null;
    if (tSelectionKey !== gInspecteurAutoplayTransition.selectionKeyObservee)
      {
        gInspecteurAutoplayTransition.selectionKeyJeton = tSelectionKey;
        gInspecteurAutoplayTransition.fileIdJeton = tFileId;
        gInspecteurAutoplayTransition.modeJeton =
          (tSelectionKey
           && gInspecteurAutoplayTransition.selectionInhibeeKey === tSelectionKey)
            ? "selection-inhibee"
            : "selection";
        if (gInspecteurAutoplayTransition.selectionInhibeeKey === tSelectionKey)
          gInspecteurAutoplayTransition.selectionInhibeeKey = null;
      }
    else if (tFileId !== gInspecteurAutoplayTransition.fileIdObserve)
      {
        if (tSelectionKey
            && gInspecteurAutoplayTransition.majFichierAutoriseSelectionKey === tSelectionKey)
          {
            gInspecteurAutoplayTransition.selectionKeyJeton = tSelectionKey;
            gInspecteurAutoplayTransition.fileIdJeton = tFileId;
            gInspecteurAutoplayTransition.modeJeton = "maj-fichier";
          }
        else
          {
            gInspecteurAutoplayTransition.selectionKeyJeton = null;
            gInspecteurAutoplayTransition.fileIdJeton = null;
            gInspecteurAutoplayTransition.modeJeton = null;
          }
      }
    gInspecteurAutoplayTransition.selectionKeyObservee = tSelectionKey;
    gInspecteurAutoplayTransition.fileIdObserve = tFileId;
  }

function autoplay_transition_inspecteur_autorisee (pSelectionKey, pFileId, pFile)
  {
    const tSelectionKey = pSelectionKey || null;
    const tFileId = pFileId || null;
    if (gInspecteurAutoplayTransition.selectionKeyJeton !== tSelectionKey)
      return false;
    if (gInspecteurAutoplayTransition.fileIdJeton !== tFileId)
      return false;
    if (gInspecteurAutoplayTransition.modeJeton === "selection")
      return true;
    return gInspecteurAutoplayTransition.modeJeton === "maj-fichier";
  }

function autoplay_transition_inspecteur_forcee (pSelectionKey, pFileId)
  {
    const tSelectionKey = pSelectionKey || null;
    const tFileId = pFileId || null;
    return gInspecteurAutoplayTransition.selectionKeyJeton === tSelectionKey
      && gInspecteurAutoplayTransition.fileIdJeton === tFileId
      && gInspecteurAutoplayTransition.modeJeton === "maj-fichier";
  }

function consomme_transition_autoplay_inspecteur (pSelectionKey, pFileId)
  {
    const tSelectionKey = pSelectionKey || null;
    const tFileId = pFileId || null;
    if (gInspecteurAutoplayTransition.selectionKeyJeton !== tSelectionKey)
      return;
    if (gInspecteurAutoplayTransition.fileIdJeton !== tFileId)
      return;
    if (gInspecteurAutoplayTransition.modeJeton === "maj-fichier"
        && gInspecteurAutoplayTransition.majFichierAutoriseSelectionKey === tSelectionKey)
      gInspecteurAutoplayTransition.majFichierAutoriseSelectionKey = null;
    gInspecteurAutoplayTransition.selectionKeyJeton = null;
    gInspecteurAutoplayTransition.fileIdJeton = null;
    gInspecteurAutoplayTransition.modeJeton = null;
  }

function ecrit_inspecteur_autoplay (pVal)
  {
    const v = !!pVal;
    try { localStorage.setItem (CLE_LS_INSPECTEUR_AUTOPLAY, v ? "1" : "0"); }
    catch (e) {}
    gInspecteurAutoplay.set (v);
  }

function inspecteur_autoplay_autorise ()
  {
    return gInspecteurAutoplay.get ();
  }

async function relance_autoplay_calque_courant ()
  {
    if (!inspecteur_autoplay_autorise () || !gWS.instance) return;
    ws_force_pause ();
    await audio_resume_context (gWS.audioCtx);
    const tSel = ws_selection_bounds ();
    const tStart = tSel ? tSel.start : 0;
    const tEnd   = tSel ? tSel.end   : null;
    gWS.play_end = tEnd;
    const p = tEnd === null ? gWS.instance.play (tStart)
                            : gWS.instance.play (tStart, tEnd);
    if (p && typeof p.catch === "function") p.catch (() => {});
  }

// Saisie locale du seed (champ numérique) — par calque. Map id -> string.
const gInspecteurSeedCustom = new ReactiveDict ();

// Dialogue de sauvegarde de variante (analogue à tabGenerate).
const gInspecteurSeedDialogOpen  = new ReactiveVar (false);
const gInspecteurSeedDialogValue = new ReactiveVar (null);
const gInspecteurSeedDialogSharedProjectIds = new ReactiveVar (null);

function inspecteur_seed_dialog_projet_id ()
  {
    const tProjetId = Session.get ("projet_id");
    if (typeof tProjetId === "string" && tProjetId) return tProjetId;
    const tSceneId = Session.get ("scene_id");
    const tScene = tSceneId ? Scenes.findOne (tSceneId, {fields : {projet_id : 1}}) : null;
    return (tScene && typeof tScene.projet_id === "string" && tScene.projet_id) ? tScene.projet_id : null;
  }

// Renvoie la seed effective pour le calque courant : prend en compte la
// saisie manuelle (gInspecteurSeedCustom) si elle est valide, sinon retombe
// sur la valeur sauvegardée dans objets_config. Renvoie null si aléatoire.
function inspecteur_seed_courant ()
  {
    const tId = gCalqueSelectionne.get ();
    if (!tId) return null;
    const tBrut = gInspecteurSeedCustom.get (tId);
    if (tBrut !== undefined && tBrut !== null)
      {
        const tStr = String (tBrut).trim ();
        if (tStr === "") return null;
        const n = parseInt (tStr, 10);
        if (!isNaN (n) && n >= 0) return n;
        return null;
      }
    const tSceneId = Session.get ("scene_id");
    const tScene   = Scenes.findOne (tSceneId);
    const tConfig  = get_objet_config (tScene, tId);
    return (tConfig && tConfig.seed !== null && tConfig.seed !== undefined)
      ? tConfig.seed : null;
  }

function seed_enregistree_existe (pSeed)
  {
    const tSeed = Number (pSeed);
    if (!Number.isFinite (tSeed)) return false;
    return !!Seeds.findOne ({value : tSeed});
  }

function seed_placeholder_selected_attr (pSeed)
  {
    return pSeed !== null && pSeed !== undefined && !seed_enregistree_existe (pSeed)
      ? "selected"
      : null;
  }

function reinitialise_inspecteur ()
  {
    gInspecteurTitre.set      (null);
    gInspecteurSlug.set       (null);
    gInspecteurSlugManuel.set (false);
    gInspecteurMessage.set    (null);
    gInspecteurProno.set      (null);
    gInspecteurVideoPreviewUrl.set (null);
    gInspecteurSceneModeLectureMasqueDraft.set (null);
    gInspecteurSceneModeLectureMasqueSaving.set (null);
    gInspecteurSceneModeLectureMasqueMessage.set (null);
    gInspecteurSceneGeometrieDraft.set (null);
    gInspecteurSceneGeometrieSaving.set (null);
    gInspecteurSceneGeometrieMessage.set (null);
  }

// ---------------------------------------------------------------------------
// État de l'upload SVG en cours
// ---------------------------------------------------------------------------

const gUploadSvgEnCours    = new ReactiveVar (false);
const gUploadSvgProgression = new ReactiveVar (0);
const gUploadSvgPhase      = new ReactiveVar ("");

// Catégories de calques pertinentes (ordre d'affichage)
const CALQUES_CATEGORIE_PART = "part";
const CALQUES_CATEGORIE_BOUTONS = "boutons";
const CALQUES_CATEGORIE_OBJ = "obj";
const CALQUES_CATEGORIE_ZOOG = "zoog";
const CATEGORIES_PERTINENTES = ["obj", CALQUES_CATEGORIE_PART, CALQUES_CATEGORIE_BOUTONS, "zoog", "prop", "div"];
const CATEGORIES_CALQUE_SVG_REGLABLES = new Set ([CALQUES_CATEGORIE_ZOOG, CALQUES_CATEGORIE_OBJ, CALQUES_CATEGORIE_PART, "prop", "div"]);
const CALQUES_CATEGORIE_BULLES = "bulles";
const gOmbresRasterSvgSceneCache = new Map ();
const gOmbresRasterSvgSceneFile = [];
let gOmbresRasterSvgSceneActives = 0;
const OMBRES_RASTER_SVG_SCENE_CACHE_MAX = 48;
const OMBRES_RASTER_SVG_SCENE_PENDING_MAX = 32;
const OMBRES_RASTER_SVG_SCENE_PIXELS_MAX = 900000;
const OMBRES_RASTER_SVG_SCENE_COTE_MAX = 1400;
const OMBRES_RASTER_SVG_SCENE_CONCURRENCE_MAX = 2;
const OMBRES_RASTER_SVG_SCENE_GENERATION_ACTIVE = false;
const CALQUES_CATEGORIE_INTERFACE = "interface";
const SVG_INTERFACE_LAYER_ID = "cogibulle-interface-items-layer";
const INSPECTEURS_CATEGORIES_SCENE = Object.freeze (
  {
    [CALQUES_CATEGORIE_BULLES] : {
      titre : "Cartes de la scène",
      description : "Paramètres globaux de la catégorie sélectionnée.",
      countLabel : "Nombre de cartes",
    },
    [CALQUES_CATEGORIE_OBJ] : {
      titre : "Objets de la scène",
      description : "Actions globales pour les objets de la catégorie obj.",
      countLabel : "Nombre d'objets",
    },
    [CALQUES_CATEGORIE_BOUTONS] : {
      titre : "Boutons de la scène",
      description : "Actions globales pour les objets de la catégorie boutons.",
      countLabel : "Nombre de boutons",
    },
    [CALQUES_CATEGORIE_INTERFACE] : {
      titre : "Interface",
      description : "Items d'interface de la scène.",
      countLabel : "Nombre d'items",
    },
  }
);
const CLE_LS_BULLES_VISIBLES_PAR_SCENE = "cogivoice_hub_bubbles_visible_by_scene";
const CLE_LS_CATEGORIES_OUVERTES_PAR_SCENE = "cogivoice_hub_layer_categories_open_by_scene";
const CLE_LS_CATEGORIES_VISIBLES_PAR_SCENE = "cogivoice_hub_layer_categories_visible_by_scene";
const CLE_LS_CATEGORIES_VERROUILLEES_PAR_SCENE = "cogivoice_hub_layer_categories_locked_by_scene";
const CLE_LS_CATEGORIES_ORDRE_PAR_SCENE = "cogivoice_hub_layer_categories_order_by_scene";
const HOVER_BULLE_CARTE_PREFIX = "bubble:";
const SVG_STYLE_CATEGORIES_MASQUEES_ID = "cogi-category-hidden-style";
const SVG_STYLE_CATEGORIES_VERROUILLEES_ID = "cogi-category-locked-style";
const HOVER_BULLE_GROUP_CLASS = "surbrillance_hover_bulle_hub";
const SELECTION_BULLE_GROUP_CLASS = "surbrillance_selection_bulle_hub";
const CATEGORIE_BULLES_GROUP_CLASS = "surbrillance_categorie_bulles_hub";
const SELECTION_BULLE_HANDLE_CLASS = `${SELECTION_BULLE_GROUP_CLASS}__handle`;
const CARTE_VUE_MIN_TAILLE = 24;

// État d'ouverture/fermeture des branches de premier niveau (par scène)
// Clé : nom de catégorie ; valeur : booléen.
const gCategoriesOuvertes = new ReactiveDict ();
const gBullesVisibles = new ReactiveDict ();
const gCategoriesVisibles = new ReactiveDict ();
const gCategoriesVerrouillees = new ReactiveDict ();
const gCategoriesOrdre = new ReactiveDict ();

// Identifiant du calque actuellement sélectionné dans l'arbre / le SVG.
const gCalqueSelectionne = new ReactiveVar (null);
const gBulleSelectionnee = new ReactiveVar (null);
const gBulleBrouillon = new ReactiveVar (null);
const gInspecteurBulleSvgPickerOuvert = new ReactiveVar (false);
const gInspecteurBulleCouleurPickerOuvert = new ReactiveVar (false);

// Catégorie de premier niveau actuellement sélectionnée dans l'arbre
// (mutuellement exclusif avec gCalqueSelectionne).
const gCategorieSelectionnee = new ReactiveVar (null);
const gInspecteurCalquesMode = new ReactiveVar (null);

const CLE_LS_DERNIER_CALQUE_PAR_SCENE = "cogivoice_hub_last_layer_by_scene";
const DERNIERE_SELECTION_BULLE_PREFIX = "bubble:";

function lit_map_localstorage_scene (pCle)
  {
    try
      {
        const tVal = JSON.parse (localStorage.getItem (pCle) || "{}");
        return (tVal && typeof tVal === "object") ? tVal : {};
      }
    catch (e) { return {}; }
  }

function ecrit_map_localstorage_scene (pCle, pPatch)
  {
    const tCourant = lit_map_localstorage_scene (pCle);
    try { localStorage.setItem (pCle, JSON.stringify ({...tCourant, ...pPatch})); }
    catch (e) {}
  }

function lit_dernier_calque_scene (pSceneId)
  {
    if (!pSceneId) return null;
    const tMap = lit_map_localstorage_scene (CLE_LS_DERNIER_CALQUE_PAR_SCENE);
    const tVal = tMap[pSceneId];
    return (typeof tVal === "string" && tVal) ? tVal : null;
  }

function ecrit_dernier_calque_scene (pSceneId, pCalqueId)
  {
    if (!pSceneId || !pCalqueId) return;
    ecrit_map_localstorage_scene (CLE_LS_DERNIER_CALQUE_PAR_SCENE,
      {[pSceneId] : pCalqueId});
  }

function decode_derniere_selection_calques_scene (pSceneId)
  {
    const tVal = lit_dernier_calque_scene (pSceneId);
    if (!tVal) return null;
    if (tVal.startsWith (DERNIERE_SELECTION_BULLE_PREFIX))
      {
        const tCarteId = tVal.slice (DERNIERE_SELECTION_BULLE_PREFIX.length);
        return tCarteId ? {type : "bulle", id : tCarteId} : null;
      }
    return {type : "calque", id : tVal};
  }

function ecrit_derniere_selection_calques_scene (pSceneId, pType, pId)
  {
    if (!pSceneId || !pId) return;
    const tVal = pType === "bulle"
      ? `${DERNIERE_SELECTION_BULLE_PREFIX}${pId}`
      : pId;
    ecrit_dernier_calque_scene (pSceneId, tVal);
  }

function lit_bulles_scene_visibles_localstorage (pSceneId)
  {
    if (!pSceneId) return true;
    const tMap = lit_map_localstorage_scene (CLE_LS_BULLES_VISIBLES_PAR_SCENE);
    const tVal = tMap[pSceneId];
    return typeof tVal === "boolean" ? tVal : true;
  }

function initialise_visibilite_bulles_scene (pSceneId)
  {
    if (!pSceneId) return;
    if (typeof gBullesVisibles.get (pSceneId) === "boolean") return;
    gBullesVisibles.set (pSceneId, lit_bulles_scene_visibles_localstorage (pSceneId));
  }

function bulles_scene_visibles (pSceneId = Session.get ("scene_id"))
  {
    if (!pSceneId) return true;
    initialise_visibilite_bulles_scene (pSceneId);
    return gBullesVisibles.get (pSceneId) !== false;
  }

function ecrit_bulles_scene_visibles (pSceneId, pVisible)
  {
    if (!pSceneId) return;
    const tVisible = !!pVisible;
    gBullesVisibles.set (pSceneId, tVisible);
    ecrit_map_localstorage_scene (CLE_LS_BULLES_VISIBLES_PAR_SCENE,
      {[pSceneId] : tVisible});
  }

function cle_etat_categorie_scene (pSceneId, pNom)
  {
    return (pSceneId && pNom) ? `${pSceneId}::${pNom}` : null;
  }

function lit_etats_categories_scene_localstorage (pCle, pSceneId)
  {
    if (!pSceneId) return {};
    const tMap = lit_map_localstorage_scene (pCle);
    const tVal = tMap[pSceneId];
    return (tVal && typeof tVal === "object") ? tVal : {};
  }

function initialise_etat_categorie_scene (pDict, pCle, pSceneId, pNom, pDefaut)
  {
    const tKey = cle_etat_categorie_scene (pSceneId, pNom);
    if (!tKey) return pDefaut;
    const tCourant = pDict.get (tKey);
    if (typeof tCourant === "boolean") return tCourant;
    const tEtats = lit_etats_categories_scene_localstorage (pCle, pSceneId);
    const tVal = tEtats[pNom];
    const tNormalise = typeof tVal === "boolean" ? tVal : pDefaut;
    pDict.set (tKey, tNormalise);
    return tNormalise;
  }

function lit_etat_categorie_scene (pDict, pCle, pSceneId, pNom, pDefaut)
  {
    if (!pSceneId || !pNom) return pDefaut;
    return initialise_etat_categorie_scene (pDict, pCle, pSceneId, pNom, pDefaut);
  }

function ecrit_etat_categorie_scene (pDict, pCle, pSceneId, pNom, pValeur)
  {
    const tKey = cle_etat_categorie_scene (pSceneId, pNom);
    if (!tKey) return;
    const tNormalise = !!pValeur;
    pDict.set (tKey, tNormalise);
    const tEtats = lit_etats_categories_scene_localstorage (pCle, pSceneId);
    ecrit_map_localstorage_scene (pCle,
      {[pSceneId] : {...tEtats, [pNom] : tNormalise}});
  }

function categorie_svg_depuis_data_name_scene (pDataName, pFallback = null)
  {
    const tTexte = typeof pDataName === "string" ? pDataName.trim () : "";
    if (!tTexte)
      return (typeof pFallback === "string" && pFallback) ? pFallback : null;
    const tPremier = ((tTexte.split (",")[0]) || "").trim ().toLowerCase ();
    if (!tPremier)
      return (typeof pFallback === "string" && pFallback) ? pFallback : null;
    if (tPremier.includes ("bouton")) return CALQUES_CATEGORIE_BOUTONS;
    if (tPremier.startsWith ("obj"))  return "obj";
    if (tPremier === CALQUES_CATEGORIE_PART) return CALQUES_CATEGORIE_PART;
    if (tPremier.startsWith ("zoog")) return "zoog";
    if (tPremier.startsWith ("prop")) return "prop";
    return "div";
  }

function svg_objets_scene_normalises (pScene)
  {
    const tSource = pScene && pScene.svg_objets;
    if (!tSource || typeof tSource !== "object") return null;

    // Recalcule la catégorie depuis data-name pour que les scènes déjà en base
    // profitent aussi de la nouvelle catégorie `part`.
    const tResultat = {};
    const tIdsVus = new Set ();
    for (const [tCategorieBrute, tListeBrute] of Object.entries (tSource))
      {
        if (!Array.isArray (tListeBrute)) continue;
        const tFallback = CATEGORIES_PERTINENTES.includes (tCategorieBrute)
          ? tCategorieBrute
          : "div";
        for (const tCalque of tListeBrute)
          {
            if (!tCalque || !tCalque.id || tIdsVus.has (tCalque.id)) continue;
            const tCategorie = categorie_svg_depuis_data_name_scene (tCalque["data-name"], tFallback);
            if (!tCategorie) continue;
            if (!tResultat[tCategorie]) tResultat[tCategorie] = [];
            tResultat[tCategorie].push (tCalque);
            tIdsVus.add (tCalque.id);
          }
      }
    return tResultat;
  }

function normalise_item_interface_scene (pItem, pIndex = 0)
  {
    if (!pItem || pItem.type !== "item" || !pItem.id) return null;
    const tNom = (typeof pItem.item_nom === "string" && pItem.item_nom.trim ())
      || (typeof pItem.nom === "string" && pItem.nom.trim ())
      || "item";
    const tNum = (pValeur, pDefaut, pMin = null, pMax = null) =>
      {
        let tValeur = Number (pValeur);
        if (!Number.isFinite (tValeur)) tValeur = pDefaut;
        if (pMin !== null) tValeur = Math.max (pMin, tValeur);
        if (pMax !== null) tValeur = Math.min (pMax, tValeur);
        return tValeur;
      };
    const tRotation = (pValeur) =>
      {
        let tValeur = Number (pValeur);
        if (!Number.isFinite (tValeur)) return 0;
        tValeur = ((tValeur % 360) + 360) % 360;
        if (tValeur > 180) tValeur -= 360;
        return Math.round (tValeur * 1000) / 1000;
      };
    return {
      ...pItem,
      type : "item",
      item_nom : tNom,
      nom : tNom,
      item_x : tNum (pItem.item_x, 40),
      item_y : tNum (pItem.item_y, 40),
      item_w : tNum (pItem.item_w, 96, 8),
      item_h : tNum (pItem.item_h, 96, 8),
      item_rotation_deg : tRotation (pItem.item_rotation_deg),
      item_opacite : tNum (pItem.item_opacite, 1, 0, 1),
      item_radius_pct : tNum (pItem.item_radius_pct, 24, 0, 100),
      item_couleur : (typeof pItem.item_couleur === "string" && pItem.item_couleur) || "#ffffff",
      item_fond_actif : typeof pItem.item_fond_actif === "boolean" ? pItem.item_fond_actif : true,
      item_fond_opacite : tNum (pItem.item_fond_opacite, 1, 0, 1),
      item_padding_top : tNum (pItem.item_padding_top, 0, 0),
      item_padding_right : tNum (pItem.item_padding_right, 0, 0),
      item_padding_bottom : tNum (pItem.item_padding_bottom, 0, 0),
      item_padding_left : tNum (pItem.item_padding_left, 0, 0),
      item_masque : !!pItem.item_masque,
      mode_affichage_diffusion : normalise_mode_affichage_diffusion (pItem.mode_affichage_diffusion),
      item_image_file_id : (typeof pItem.item_image_file_id === "string" && pItem.item_image_file_id) || null,
      item_image_url : (typeof pItem.item_image_url === "string" && pItem.item_image_url) || null,
      item_svg_inline : typeof pItem.item_svg_inline === "boolean" ? pItem.item_svg_inline : false,
      item_ombre_asset_file_id : (typeof pItem.item_ombre_asset_file_id === "string" && pItem.item_ombre_asset_file_id) || null,
      item_ombre_asset_file_name : (typeof pItem.item_ombre_asset_file_name === "string" && pItem.item_ombre_asset_file_name) || null,
      item_ombre_asset_signature : (typeof pItem.item_ombre_asset_signature === "string" && pItem.item_ombre_asset_signature) || null,
      item_ombre_asset_url : (typeof pItem.item_ombre_asset_url === "string" && pItem.item_ombre_asset_url) || null,
      item_hype_widget_id : (typeof pItem.item_hype_widget_id === "string" && pItem.item_hype_widget_id) || null,
      item_hype_autoplay : typeof pItem.item_hype_autoplay === "boolean" ? pItem.item_hype_autoplay : true,
      item_hype_loop : typeof pItem.item_hype_loop === "boolean" ? pItem.item_hype_loop : false,
      item_hype_traverse_clics : typeof pItem.item_hype_traverse_clics === "boolean" ? pItem.item_hype_traverse_clics : false,
      item_ombre_opacite : tNum (pItem.item_ombre_opacite, OMBRE_OBJET_DEFAUT.opacite, 0, 1),
      item_ombre_blur : tNum (pItem.item_ombre_blur, OMBRE_OBJET_DEFAUT.blur, 0, 80),
      item_ombre_x : tNum (pItem.item_ombre_x, OMBRE_OBJET_DEFAUT.x, -200, 200),
      item_ombre_y : tNum (pItem.item_ombre_y, OMBRE_OBJET_DEFAUT.y, -200, 200),
      item_ombre_couleur : normalise_couleur_style_interface (pItem.item_ombre_couleur, OMBRE_OBJET_DEFAUT.couleur),
      item_ombre_active : typeof pItem.item_ombre_active === "boolean" ? pItem.item_ombre_active : OMBRE_OBJET_DEFAUT.active,
      item_ombre_zones_dessinees_seules : typeof pItem.item_ombre_zones_dessinees_seules === "boolean" ? pItem.item_ombre_zones_dessinees_seules : true,
      item_animation_bulles : typeof pItem.item_animation_bulles === "boolean" ? pItem.item_animation_bulles : false,
      item_ordre : Number.isFinite (Number (pItem.item_ordre)) ? Number (pItem.item_ordre) : pIndex,
    };
  }

function items_interface_scene (pScene = Scenes.findOne (Session.get ("scene_id")))
  {
    return (Array.isArray (pScene && pScene.objets_config) ? pScene.objets_config : [])
      .map (normalise_item_interface_scene)
      .filter (Boolean)
      .sort ((a, b) => (Number (a.item_ordre) || 0) - (Number (b.item_ordre) || 0));
  }

function ids_rendu_items_interface_svg (pItems)
  {
    return (Array.isArray (pItems) ? pItems : [])
      .map ((pItem) => pItem && pItem.id)
      .filter (Boolean)
      .reverse ();
  }

function item_interface_scene_par_id (pId, pScene = Scenes.findOne (Session.get ("scene_id")))
  {
    if (!pId) return null;
    return items_interface_scene (pScene).find ((pItem) => pItem.id === pId) || null;
  }

function item_interface_est_source_obj (pItem)
  {
    return !!(pItem && pItem.item_source_obj === true);
  }

function item_interface_a_action_lecture_scene (pItemOuId, pScene = Scenes.findOne (Session.get ("scene_id")))
  {
    const tItem = typeof pItemOuId === "string"
      ? item_interface_scene_par_id (pItemOuId, pScene)
      : pItemOuId;
    return !!(tItem && (
      normalise_action_interactivite_objet (tItem.interactivite_action)
      || (tItem.item_hype_widget_id && tItem.item_hype_autoplay === false)
      || (item_interface_est_svg_inline (tItem) && tItem.item_hype_autoplay === false)
    ));
  }

function action_interactivite_objet_scene_courante (pId)
  {
    const tId = typeof pId === "string" ? pId : "";
    if (!tId) return INTERACTIVITE_ACTION_AUCUNE;
    const tConfig = config_objet_scene_courante (tId);
    const tActionConfig = normalise_action_interactivite_objet (tConfig && tConfig.interactivite_action);
    if (tActionConfig) return tActionConfig;
    const tItem = item_interface_scene_par_id (tId);
    return normalise_action_interactivite_objet (tItem && tItem.interactivite_action);
  }

function interaction_lecture_autorisee_diffusion (pContexte)
  {
    if (typeof window === "undefined" || typeof window.cogibulleDiffusionAccessGate !== "function")
      return true;
    return !!window.cogibulleDiffusionAccessGate (pContexte || {});
  }

function utilisateur_connecte_diffusion_scene ()
  {
    // Mode embarqué : l'appli n'a ni compte ni connexion. Elle montre
    // l'habillage « connecté » (le jeu complet, sans invitation à s'identifier) ;
    // l'accès aux scènes payantes reste réglé par le verrou d'achat in-app.
    if (est_runtime_embarque ()) return true;
    return !!Session.get ("diffusion_connecte");
  }

function objet_visible_mode_affichage_diffusion_client (pConfig)
  {
    // Mode embarqué : pas de page « Mon compte », ses boutons disparaissent.
    if (est_runtime_embarque ()
        && normalise_action_interactivite_objet (pConfig && pConfig.interactivite_action) === INTERACTIVITE_ACTION_COMPTE)
      return false;
    return mode_affichage_diffusion_est_visible (
      pConfig && pConfig.mode_affichage_diffusion,
      utilisateur_connecte_diffusion_scene ()
    );
  }

function scene_access_diffusion_par_id_ou_slug (pId)
  {
    const tId = typeof pId === "string" ? pId.trim () : "";
    if (!tId) return null;
    return DiffusionSceneAccess.findOne (
      {
        $or : [
          {scene_id : tId},
          {slug : tId},
        ],
      }
    );
  }

function action_scene_verrouillee_diffusion (pSceneIdOuSlug)
  {
    const tId = typeof pSceneIdOuSlug === "string" ? pSceneIdOuSlug.trim () : "";
    if (!tId) return false;
    // Mode embarqué (appli native) : toutes les scènes sont hydratées en
    // Minimongo ; le verrou dépend d'« Accès libre » et de l'achat in-app.
    if (est_runtime_embarque ()) return scene_verrouillee_embarque (tId);
    if (Scenes.findOne (tId) || Scenes.findOne ({slug : tId})) return false;
    const tAccess = scene_access_diffusion_par_id_ou_slug (tId);
    return !!(tAccess && tAccess.acces_libre !== true);
  }

// ---------------------------------------------------------------------------
// Mode embarqué — scènes « commerciales ». Les stores exigent que tout contenu
// commercial d'une appli pour enfants (produits, liens vers une boutique) vive
// dans une zone réservée derrière la gate parentale. Est commerciale une scène
// qui porte au moins un lien sortant de l'appli (action URL qui n'est pas un
// document embarqué) : la gate se place à son ENTRÉE ; une fois dedans, ses
// liens s'ouvrent sans nouvelle question.
// ---------------------------------------------------------------------------
let gSceneCommercialeDeverrouilleeId = null;

function noeud_porte_lien_sortant_embarque (pNoeud)
  {
    if (Array.isArray (pNoeud)) return pNoeud.some (noeud_porte_lien_sortant_embarque);
    if (!pNoeud || typeof pNoeud !== "object") return false;
    if (normalise_action_interactivite_objet (pNoeud.interactivite_action) === INTERACTIVITE_ACTION_URL
        && typeof pNoeud.interactivite_url === "string" && pNoeud.interactivite_url.trim ()
        && !url_est_document_embarque (pNoeud.interactivite_url))
      return true;
    return Object.values (pNoeud).some (noeud_porte_lien_sortant_embarque);
  }

function scene_commerciale_embarque (pSceneIdOuSlug)
  {
    const tId = typeof pSceneIdOuSlug === "string" ? pSceneIdOuSlug.trim () : "";
    if (!tId || !est_runtime_embarque ()) return null;
    const tScene = Scenes.findOne ({$or : [{_id : tId}, {slug : tId}]});
    return tScene && noeud_porte_lien_sortant_embarque (tScene.objets_config) ? tScene : null;
  }

function intercepte_action_scene_commerciale_embarque (pTpl, pSceneIdOuSlug)
  {
    const tScene = scene_commerciale_embarque (pSceneIdOuSlug);
    if (!tScene) return false;
    gSceneCommercialeDeverrouilleeId = null;
    return ouvre_gate_parentale_embarque (() =>
      {
        gSceneCommercialeDeverrouilleeId = tScene._id;
        ouvre_scene_lecture_avec_fondu (pTpl, pSceneIdOuSlug);
      });
  }

function intercepte_action_scene_verrouillee_diffusion (pSceneIdOuSlug)
  {
    if (!action_scene_verrouillee_diffusion (pSceneIdOuSlug)) return false;
    interaction_lecture_autorisee_diffusion (
      {
        action : INTERACTIVITE_ACTION_SCENE,
        scene_id : pSceneIdOuSlug,
      }
    );
    return true;
  }

function applique_etat_visuel_scene_verrouillee_diffusion (pNoeud, pVerrouille)
  {
    if (!pNoeud || !pNoeud.style || !pNoeud.setAttribute) return;
    const tAttr = "data-cogi-diffusion-scene-locked";
    const tPrevOpacityAttr = "data-cogi-diffusion-scene-locked-prev-opacity";
    const tPrevCursorAttr = "data-cogi-diffusion-scene-locked-prev-cursor";
    const tPrevPointerAttr = "data-cogi-diffusion-scene-locked-prev-pointer-events";
    if (pVerrouille)
      {
        if (pNoeud.getAttribute (tAttr) !== "1")
          {
            pNoeud.setAttribute (tPrevOpacityAttr, pNoeud.style.opacity || "");
            pNoeud.setAttribute (tPrevCursorAttr, pNoeud.style.cursor || "");
            pNoeud.setAttribute (tPrevPointerAttr, pNoeud.style.pointerEvents || "");
          }
        pNoeud.setAttribute (tAttr, "1");
        pNoeud.style.opacity = "0.42";
        pNoeud.style.cursor = "pointer";
        pNoeud.style.pointerEvents = "all";
        return;
      }
    if (pNoeud.getAttribute (tAttr) === "1")
      {
        pNoeud.removeAttribute (tAttr);
        pNoeud.style.opacity = pNoeud.getAttribute (tPrevOpacityAttr) || "";
        pNoeud.style.cursor = pNoeud.getAttribute (tPrevCursorAttr) || "";
        pNoeud.style.pointerEvents = pNoeud.getAttribute (tPrevPointerAttr) || "";
        pNoeud.removeAttribute (tPrevOpacityAttr);
        pNoeud.removeAttribute (tPrevCursorAttr);
        pNoeud.removeAttribute (tPrevPointerAttr);
      }
  }

function scene_cible_config_verrouillee_diffusion (pConfig)
  {
    if (!pConfig) return false;
    if (normalise_action_interactivite_objet (pConfig.interactivite_action) !== INTERACTIVITE_ACTION_SCENE) return false;
    return action_scene_verrouillee_diffusion (pConfig.interactivite_scene_id);
  }

function signature_actions_scene_verrouillees_diffusion_client (pConfigs)
  {
    const tConfigs = Array.isArray (pConfigs) ? pConfigs : [];
    return tConfigs.map ((pConfig) =>
      {
        if (!pConfig) return null;
        if (normalise_action_interactivite_objet (pConfig.interactivite_action) !== INTERACTIVITE_ACTION_SCENE)
          return null;
        const tSceneId = typeof pConfig.interactivite_scene_id === "string"
          ? pConfig.interactivite_scene_id.trim ()
          : "";
        if (!tSceneId) return null;
        const tScene = Scenes.findOne (tSceneId) || Scenes.findOne ({slug : tSceneId});
        const tAccess = scene_access_diffusion_par_id_ou_slug (tSceneId);
        return [
          pConfig.id || "",
          tSceneId,
          tScene && tScene._id || "",
          tAccess ? (tAccess.acces_libre === true ? "libre" : "protege") : "",
        ].join (":");
      })
      .filter (Boolean)
      .join ("|");
  }

function applique_actions_scene_verrouillees_calques_svg_tpl (pTpl)
  {
    const tDoc = pTpl && pTpl._svgDoc;
    const tSceneId = Session.get ("scene_id");
    const tScene = tSceneId ? Scenes.findOne (tSceneId) : null;
    if (!tDoc || !tScene || !Array.isArray (tScene.objets_config)) return;
    for (const tConfig of tScene.objets_config)
      {
        if (!tConfig || !tConfig.id) continue;
        const tNoeud = tDoc.getElementById (tConfig.id);
        if (!tNoeud) continue;
        applique_etat_visuel_scene_verrouillee_diffusion (
          tNoeud,
          scene_cible_config_verrouillee_diffusion (tConfig)
        );
      }
  }

function normalise_rotation_degres_client (pValeur)
  {
    let tValeur = Number (pValeur);
    if (!Number.isFinite (tValeur)) return 0;
    tValeur = ((tValeur % 360) + 360) % 360;
    if (tValeur > 180) tValeur -= 360;
    return Math.round (tValeur * 1000) / 1000;
  }

function transform_item_interface_svg (pItem, pW, pH)
  {
    const tX = Number (pItem && pItem.item_x) || 0;
    const tY = Number (pItem && pItem.item_y) || 0;
    const tRotation = normalise_rotation_degres_client (pItem && pItem.item_rotation_deg);
    const tTransform = [`translate(${tX} ${tY})`];
    if (tRotation)
      tTransform.push (`rotate(${tRotation} ${Math.max (8, Number (pW) || 96) / 2} ${Math.max (8, Number (pH) || 96) / 2})`);
    return tTransform.join (" ");
  }

function signature_items_interface_svg_client (pItems, pEtat)
  {
    return JSON.stringify ({
      etat : pEtat || {},
      items : (Array.isArray (pItems) ? pItems : []).map ((pItem) => [
        pItem.id,
        pItem.item_ordre,
        pItem.item_masque,
        pItem.mode_affichage_diffusion,
        pItem.item_x,
        pItem.item_y,
        pItem.item_w,
        pItem.item_h,
        pItem.item_rotation_deg,
        pItem.item_opacite,
        pItem.item_radius_pct,
        pItem.item_couleur,
        pItem.item_fond_actif,
        pItem.item_fond_opacite,
        pItem.item_padding_top,
        pItem.item_padding_right,
        pItem.item_padding_bottom,
        pItem.item_padding_left,
        pItem.item_image_file_id,
        pItem.item_image_url,
        pItem.item_svg_inline,
        pItem.item_hype_widget_id,
        pItem.item_hype_autoplay,
        pItem.item_hype_loop,
        pItem.item_hype_traverse_clics,
        pItem.item_ombre_asset_file_id,
        pItem.item_ombre_asset_file_name,
        pItem.item_ombre_asset_signature,
        pItem.item_ombre_asset_url,
        pItem.item_ombre_opacite,
        pItem.item_ombre_blur,
        pItem.item_ombre_x,
        pItem.item_ombre_y,
        pItem.item_ombre_couleur,
        pItem.item_ombre_active,
        pItem.item_ombre_zones_dessinees_seules,
        pItem.item_animation_bulles,
        pItem.interactivite_action,
        pItem.interactivite_scene_id,
        scene_cible_config_verrouillee_diffusion (pItem),
        scene_cible_config_terminee_lecture (pItem),
      ]),
    });
  }

function categories_calques_scene_defaut (pInclureBulles = true)
  {
    const tListe = pInclureBulles
      ? [CALQUES_CATEGORIE_BULLES, CALQUES_CATEGORIE_INTERFACE, ...CATEGORIES_PERTINENTES]
      : [CALQUES_CATEGORIE_INTERFACE, ...CATEGORIES_PERTINENTES];
    return tListe;
  }

function normalise_ordre_categories_calques_scene (pOrdre, pDisponibles = null)
  {
    const tConnues = new Set (categories_calques_scene_defaut ());
    const tSourceDisponibles = Array.isArray (pDisponibles) && pDisponibles.length
      ? pDisponibles
      : categories_calques_scene_defaut ();
    const tFiltreDisponibles = Array.isArray (pDisponibles) && pDisponibles.length
      ? new Set (tSourceDisponibles)
      : null;
    const tDisponibles = [];
    for (const tNom of tSourceDisponibles)
      {
        if (typeof tNom !== "string" || !tConnues.has (tNom) || tDisponibles.includes (tNom)) continue;
        tDisponibles.push (tNom);
      }

    const tOrdre = [];
    if (Array.isArray (pOrdre))
      {
        for (const tNom of pOrdre)
          {
            if (typeof tNom !== "string" || !tConnues.has (tNom) || tOrdre.includes (tNom)) continue;
            if (tFiltreDisponibles && !tFiltreDisponibles.has (tNom)) continue;
            tOrdre.push (tNom);
          }
      }

    for (const tNom of tDisponibles)
      {
        if (tOrdre.includes (tNom)) continue;
        const tIndexDisponibles = tDisponibles.indexOf (tNom);
        let tIndexInsertion = -1;

        for (let i = tIndexDisponibles - 1; i >= 0; i--)
          {
            const tIndexPrecedent = tOrdre.indexOf (tDisponibles[i]);
            if (tIndexPrecedent >= 0)
              {
                tIndexInsertion = tIndexPrecedent + 1;
                break;
              }
          }

        if (tIndexInsertion < 0)
          {
            for (let i = tIndexDisponibles + 1; i < tDisponibles.length; i++)
              {
                const tIndexSuivant = tOrdre.indexOf (tDisponibles[i]);
                if (tIndexSuivant >= 0)
                  {
                    tIndexInsertion = tIndexSuivant;
                    break;
                  }
              }
          }

        if (tIndexInsertion < 0) tOrdre.push (tNom);
        else                     tOrdre.splice (tIndexInsertion, 0, tNom);
      }
    return tOrdre;
  }

function lit_ordre_categories_calques_scene_localstorage (pSceneId)
  {
    if (!pSceneId) return categories_calques_scene_defaut ();
    const tMap = lit_map_localstorage_scene (CLE_LS_CATEGORIES_ORDRE_PAR_SCENE);
    return normalise_ordre_categories_calques_scene (tMap[pSceneId]);
  }

function initialise_ordre_categories_calques_scene (pSceneId)
  {
    if (!pSceneId) return categories_calques_scene_defaut ();
    const tCourant = gCategoriesOrdre.get (pSceneId);
    if (Array.isArray (tCourant))
      return normalise_ordre_categories_calques_scene (tCourant);
    const tOrdre = lit_ordre_categories_calques_scene_localstorage (pSceneId);
    gCategoriesOrdre.set (pSceneId, tOrdre);
    return tOrdre;
  }

function ordre_categories_calques_scene (pSceneId = Session.get ("scene_id"), pDisponibles = null)
  {
    const tOrdre = pSceneId
      ? initialise_ordre_categories_calques_scene (pSceneId)
      : categories_calques_scene_defaut ();
    return normalise_ordre_categories_calques_scene (tOrdre, pDisponibles);
  }

function ecrit_ordre_categories_calques_scene (pSceneId, pOrdre)
  {
    if (!pSceneId) return;
    const tOrdre = normalise_ordre_categories_calques_scene (pOrdre);
    gCategoriesOrdre.set (pSceneId, tOrdre);
    ecrit_map_localstorage_scene (CLE_LS_CATEGORIES_ORDRE_PAR_SCENE,
      {[pSceneId] : tOrdre});
  }

function noms_categories_svg_scene (pScene, pSceneId = Session.get ("scene_id"))
  {
    const tObjets = svg_objets_scene_normalises (pScene);
    if (!tObjets) return [];
    const tDisponibles = [];
    if (items_interface_scene (pScene).length)
      tDisponibles.push (CALQUES_CATEGORIE_INTERFACE);
    for (const tNom of CATEGORIES_PERTINENTES)
      {
        const tBrut = tObjets[tNom];
        if (!Array.isArray (tBrut) || !tBrut.length) continue;
        tDisponibles.push (tNom);
      }
    const tDisponiblesSet = new Set (tDisponibles);
    return ordre_categories_calques_scene (pSceneId, tDisponibles)
      .filter ((pNom) => tDisponiblesSet.has (pNom));
  }

function noms_categories_calques_scene (pScene, pSceneId = Session.get ("scene_id"))
  {
    const tDisponibles = noms_categories_svg_scene (pScene, pSceneId);
    if (cartes_scene_liste ().length) tDisponibles.push (CALQUES_CATEGORIE_BULLES);
    const tDisponiblesSet = new Set (tDisponibles);
    return ordre_categories_calques_scene (pSceneId, tDisponibles)
      .filter ((pNom) => tDisponiblesSet.has (pNom));
  }

function categorie_scene_ouverte (pNom, pSceneId = Session.get ("scene_id"))
  {
    return lit_etat_categorie_scene (
      gCategoriesOuvertes,
      CLE_LS_CATEGORIES_OUVERTES_PAR_SCENE,
      pSceneId,
      pNom,
      true
    ) !== false;
  }

function ecrit_ouverture_categorie_scene (pNom, pOuverte, pSceneId = Session.get ("scene_id"))
  {
    ecrit_etat_categorie_scene (
      gCategoriesOuvertes,
      CLE_LS_CATEGORIES_OUVERTES_PAR_SCENE,
      pSceneId,
      pNom,
      !!pOuverte
    );
  }

function categorie_scene_visible (pNom, pSceneId = Session.get ("scene_id"))
  {
    if (!pNom) return true;
    if (pNom === CALQUES_CATEGORIE_BULLES) return bulles_scene_visibles (pSceneId);
    return lit_etat_categorie_scene (
      gCategoriesVisibles,
      CLE_LS_CATEGORIES_VISIBLES_PAR_SCENE,
      pSceneId,
      pNom,
      true
    ) !== false;
  }

function ecrit_visibilite_categorie_scene (pSceneId, pNom, pVisible)
  {
    if (pNom === CALQUES_CATEGORIE_BULLES)
      {
        ecrit_bulles_scene_visibles (pSceneId, pVisible);
        return;
      }
    ecrit_etat_categorie_scene (
      gCategoriesVisibles,
      CLE_LS_CATEGORIES_VISIBLES_PAR_SCENE,
      pSceneId,
      pNom,
      pVisible
    );
  }

function categorie_scene_verrouillee (pNom, pSceneId = Session.get ("scene_id"))
  {
    if (!pNom) return false;
    return !!lit_etat_categorie_scene (
      gCategoriesVerrouillees,
      CLE_LS_CATEGORIES_VERROUILLEES_PAR_SCENE,
      pSceneId,
      pNom,
      false
    );
  }

function ecrit_verrouillage_categorie_scene (pSceneId, pNom, pVerrouillee)
  {
    if (!pSceneId || !pNom) return;
    ecrit_etat_categorie_scene (
      gCategoriesVerrouillees,
      CLE_LS_CATEGORIES_VERROUILLEES_PAR_SCENE,
      pSceneId,
      pNom,
      pVerrouillee
    );
  }

function categorie_scene_interactive (pNom, pSceneId = Session.get ("scene_id"))
  {
    return categorie_scene_visible (pNom, pSceneId)
      && !categorie_scene_verrouillee (pNom, pSceneId);
  }

function echappe_selecteur_css_svg (pValeur)
  {
    if (typeof pValeur !== "string") return "";
    if (typeof CSS !== "undefined" && CSS && typeof CSS.escape === "function")
      return CSS.escape (pValeur);
    return pValeur.replace (/[ !\"#$%&'()*+,./:;<=>?@[\\\]^`{|}~]/g, "\\$&");
  }

function ids_categorie_scene_depuis_tpl (pTpl, pNom)
  {
    if (!pNom || pNom === CALQUES_CATEGORIE_BULLES) return [];
    if (pNom === CALQUES_CATEGORIE_INTERFACE) return items_interface_scene ().map ((pItem) => pItem.id);
    const tIds = pTpl && pTpl._idsParCategorie && pTpl._idsParCategorie.get (pNom);
    if (Array.isArray (tIds)) return tIds;
    const tSceneId = Session.get ("scene_id");
    const tScene = tSceneId ? Scenes.findOne (tSceneId) : null;
    const tObjets = svg_objets_scene_normalises (tScene);
    const tBrut = tObjets && tObjets[pNom];
    return Array.isArray (tBrut)
      ? tBrut.filter ((pCalque) => pCalque && pCalque.id).map ((pCalque) => pCalque.id)
      : [];
  }

function assure_calque_items_interface_svg (pDoc)
  {
    const tRoot = pDoc && pDoc.documentElement;
    if (!tRoot) return null;
    let tLayer = pDoc.getElementById (SVG_INTERFACE_LAYER_ID);
    if (!tLayer)
      {
        tLayer = pDoc.createElementNS ("http://www.w3.org/2000/svg", "g");
        tLayer.setAttribute ("id", SVG_INTERFACE_LAYER_ID);
        tLayer.setAttribute ("data-cogibulle-layer", "interface");
      }
    if (tLayer.parentNode !== tRoot || tRoot.lastChild !== tLayer)
      tRoot.appendChild (tLayer);
    return tLayer;
  }

function items_interface_suspendus_par_focus_carte ()
  {
    return !!gBulleSelectionnee.get () || !!gLectureSceneFocusCarte.get ();
  }

function items_interface_masques_lecture_tpl (pTpl)
  {
    if (pTpl && pTpl._lectureZoomAltReaffichageMasquesEnCours) return false;
    return !!(mode_lecture_scene_actif ()
      && (
        gLectureSceneFocusCarte.get ()
        || (pTpl && (
          pTpl._lectureZoomAltMasquagesCarteId
          || pTpl._lectureBullesSuspendues
          || pTpl._lectureZoomAltSortieEnCours
          || pTpl._lectureZoomAltPreparation
          || pTpl._lectureFocusCarteEntreeEnCours
        ))
      ));
  }

function applique_visibilite_calque_items_interface_tpl (pTpl, pLayer, pOptions = {})
  {
    if (!pLayer) return;
    const tMasqueCategorie = !!(pOptions && pOptions.masqueCategorie);
    const tVerrouilleCategorie = !!(pOptions && pOptions.verrouilleCategorie);
    const tLectureActive = mode_lecture_scene_actif ();
    const tMasqueLecture = items_interface_masques_lecture_tpl (pTpl);
    const tMasqueEdition = !tLectureActive && items_interface_suspendus_par_focus_carte ();
    const tMasque = tMasqueCategorie || tMasqueEdition || tMasqueLecture;

    if (tMasque)
      {
        pLayer.style.display = "none";
        pLayer.style.opacity = "";
        pLayer.style.transition = "";
        pLayer.style.pointerEvents = "none";
        if (pTpl) pTpl._itemsInterfaceLectureVisible = null;
        return;
      }

    pLayer.style.display = "";
    const tVisibleLecture = !tMasqueLecture;
    pLayer.style.pointerEvents = (tMasqueLecture || tVerrouilleCategorie) ? "none" : "all";
    if (!tLectureActive)
      {
        pLayer.style.opacity = "";
        pLayer.style.transition = "";
        if (pTpl) pTpl._itemsInterfaceLectureVisible = null;
        return;
      }

    const tAncien = pTpl ? pTpl._itemsInterfaceLectureVisible : null;
    const tOpaciteCourante = (() =>
      {
        const tInline = Number (pLayer.style.opacity);
        if (Number.isFinite (tInline)) return tInline;
        if (typeof window !== "undefined" && window.getComputedStyle)
          {
            const tComputed = Number (window.getComputedStyle (pLayer).opacity);
            if (Number.isFinite (tComputed)) return tComputed;
          }
        return 1;
      }) ();
    const tVisibleDepart = (tAncien === null || tAncien === undefined)
      ? tOpaciteCourante > 0.01
      : !!tAncien;
    const tDuree = tVisibleLecture ? duree_fade_masque_in_ms_lecture () : duree_fade_masque_out_ms_lecture ();
    const tAnime = tVisibleDepart !== tVisibleLecture && tDuree > 0;
    pLayer.style.transition = tAnime ? `opacity ${tDuree}ms ease` : "";
    if (tVisibleLecture && tAnime)
      {
        pLayer.style.opacity = "0";
        requestAnimationFrame (() =>
          {
            if (pTpl && pTpl._itemsInterfaceSvgLayer !== pLayer) return;
            pLayer.style.opacity = "1";
          });
      }
    else if (!tVisibleLecture && tAnime)
      {
        pLayer.style.opacity = String (Math.max (0, Math.min (1, tOpaciteCourante || 1)));
        try { pLayer.getBoundingClientRect (); } catch (e) {}
        requestAnimationFrame (() =>
          {
            if (pTpl && pTpl._itemsInterfaceSvgLayer !== pLayer) return;
            pLayer.style.opacity = "0";
          });
      }
    else
      pLayer.style.opacity = tVisibleLecture ? "1" : "0";
    if (pTpl) pTpl._itemsInterfaceLectureVisible = tVisibleLecture;
  }

function applique_geometrie_item_interface_svg (pG, pItem, pW = null, pH = null, pSceneId = null)
  {
    if (!pG || !pItem) return;
    const tW = Math.max (8, Number (pW !== null ? pW : pItem.item_w) || 96);
    const tH = Math.max (8, Number (pH !== null ? pH : pItem.item_h) || 96);
    const tRadius = Math.max (0, Math.min (100, Number (pItem.item_radius_pct) || 0)) / 100 * Math.min (tW, tH) / 2;
    const tPadTop = Math.max (0, Number (pItem.item_padding_top) || 0);
    const tPadRight = Math.max (0, Number (pItem.item_padding_right) || 0);
    const tPadBottom = Math.max (0, Number (pItem.item_padding_bottom) || 0);
    const tPadLeft = Math.max (0, Number (pItem.item_padding_left) || 0);
    const tInnerW = Math.max (1, tW - tPadLeft - tPadRight);
    const tInnerH = Math.max (1, tH - tPadTop - tPadBottom);
    const tOmbre = pG.querySelector && pG.querySelector ('g[data-role="ombre"]');
    const tOmbreFiltre = pG.querySelector && pG.querySelector ('filter[data-role="ombre_filtre"]');
    const tOmbreRect = pG.querySelector && pG.querySelector ('rect[data-role="ombre_rect"]');
    const tOmbreImage = pG.querySelector && pG.querySelector ('image[data-role="ombre_image"]');
    const tFond = pG.querySelector && pG.querySelector ('rect[data-role="fond"]');
    const tClip = pG.querySelector && pG.querySelector ('rect[data-role="clip"]');
    const tImage = pG.querySelector && pG.querySelector ('image[data-role="image"]');
    const tSvgInline = pG.querySelector && pG.querySelector ('svg[data-role="inline_svg"]');
    const tHype = pG.querySelector && pG.querySelector ('foreignObject[data-role="hype"]');
    for (const tRect of [tOmbreRect, tFond, tClip])
      if (tRect)
        {
          tRect.setAttribute ("x", "0");
          tRect.setAttribute ("y", "0");
          tRect.setAttribute ("width", String (tW));
          tRect.setAttribute ("height", String (tH));
          tRect.setAttribute ("rx", String (tRadius));
          tRect.setAttribute ("ry", String (tRadius));
        }
    for (const tImg of [tImage, tOmbreImage])
      if (tImg)
        {
          tImg.setAttribute ("x", String (tPadLeft));
          tImg.setAttribute ("y", String (tPadTop));
          tImg.setAttribute ("width", String (tInnerW));
          tImg.setAttribute ("height", String (tInnerH));
        }
    if (tSvgInline)
      {
        tSvgInline.setAttribute ("x", String (tPadLeft));
        tSvgInline.setAttribute ("y", String (tPadTop));
        tSvgInline.setAttribute ("width", String (tInnerW));
        tSvgInline.setAttribute ("height", String (tInnerH));
      }
    if (tHype)
      {
        tHype.setAttribute ("x", "0");
        tHype.setAttribute ("y", "0");
        tHype.setAttribute ("width", String (tW));
        tHype.setAttribute ("height", String (tH));
      }
    if (tOmbre)
      {
        const tShadowOpacity = Math.max (0, Math.min (1, Number (pItem.item_ombre_opacite) || 0));
        const tShadowBlur = Math.max (0, Number (pItem.item_ombre_blur) || 0);
        const tShadowX = Number (pItem.item_ombre_x) || 0;
        const tShadowY = Number (pItem.item_ombre_y) || 0;
        const tShadowColor = normalise_couleur_style_interface (pItem.item_ombre_couleur, "#000000");
        const tMarge = Math.ceil (tShadowBlur * 4 + Math.max (Math.abs (tShadowX), Math.abs (tShadowY), 8));
        const tRasterOmbre = ombres_svg_rasterisees_scene_client ();
        if (tOmbreFiltre)
          {
            tOmbreFiltre.setAttribute ("x", String (-tMarge));
            tOmbreFiltre.setAttribute ("y", String (-tMarge));
            tOmbreFiltre.setAttribute ("width", String (tW + tMarge * 2));
            tOmbreFiltre.setAttribute ("height", String (tH + tMarge * 2));
            const tBlur = tOmbreFiltre.querySelector ("feGaussianBlur");
            const tOffset = tOmbreFiltre.querySelector ("feOffset");
            const tFlood = tOmbreFiltre.querySelector ("feFlood");
            if (tBlur) tBlur.setAttribute ("stdDeviation", String (tShadowBlur));
            if (tOffset)
              {
                tOffset.setAttribute ("dx", String (tShadowX));
                tOffset.setAttribute ("dy", String (tShadowY));
              }
            if (tFlood)
              {
                tFlood.setAttribute ("flood-color", tShadowColor);
                tFlood.setAttribute ("flood-opacity", String (tShadowOpacity));
              }
          }
        const tSupportOmbre = !!tOmbreImage || pItem.item_fond_actif !== false;
        const tOmbreVisible = pItem.item_ombre_active === true && tShadowOpacity > 0 && tSupportOmbre;
        if (tOmbreVisible && tRasterOmbre && tOmbreFiltre)
          {
            const tDefsFiltre = tOmbreFiltre.parentNode;
            const tSignatureRaster = JSON.stringify ([
              "item",
              pSceneId || Session.get ("scene_id") || "",
              pItem.id || "",
              pItem.item_image_file_id || "",
              tW, tH, tRadius,
              tShadowColor, tShadowOpacity, tShadowBlur, tShadowX, tShadowY,
              pItem.item_ombre_zones_dessinees_seules !== false,
              pItem.item_fond_actif !== false,
            ]);
            const tAssetExporte = url_asset_diffusion_scene_client (pItem.item_ombre_asset_url)
              || url_ombre_asset_scene_client (pItem.item_ombre_asset_file_id, pItem.item_ombre_asset_file_name);
            const tAssetPersistant = tAssetExporte;
            const tImageRaster = tAssetPersistant || image_raster_ombre_svg_scene_client (tSignatureRaster);
            if (tImageRaster)
              {
                while (tOmbre.firstChild) tOmbre.removeChild (tOmbre.firstChild);
                tOmbre.removeAttribute ("filter");
                const tImageRasterNode = pG.ownerDocument.createElementNS ("http://www.w3.org/2000/svg", "image");
                tImageRasterNode.setAttribute ("x", String (-tMarge));
                tImageRasterNode.setAttribute ("y", String (-tMarge));
                tImageRasterNode.setAttribute ("width", String (tW + tMarge * 2));
                tImageRasterNode.setAttribute ("height", String (tH + tMarge * 2));
                tImageRasterNode.setAttribute ("preserveAspectRatio", "none");
                tImageRasterNode.setAttribute ("href", tImageRaster);
                tImageRasterNode.setAttributeNS ("http://www.w3.org/1999/xlink", "href", tImageRaster);
                tImageRasterNode.style.pointerEvents = "none";
                tOmbre.appendChild (tImageRasterNode);
                tOmbre.style.display = "";
              }
            else if (OMBRES_RASTER_SVG_SCENE_GENERATION_ACTIVE)
              {
                const tDocRaster = document.implementation.createDocument ("http://www.w3.org/2000/svg", "svg", null);
                const tSvg = tDocRaster.documentElement;
                const tFiltre = tOmbreFiltre.cloneNode (true);
                tFiltre.setAttribute ("id", "ombre-item-raster-" + hachage_texte_simple_scene_client (tSignatureRaster));
                tSvg.setAttribute ("xmlns", "http://www.w3.org/2000/svg");
                tSvg.setAttribute ("width", String (Math.ceil (tW + tMarge * 2)));
                tSvg.setAttribute ("height", String (Math.ceil (tH + tMarge * 2)));
                tSvg.setAttribute ("viewBox", `${-tMarge} ${-tMarge} ${tW + tMarge * 2} ${tH + tMarge * 2}`);
                const tDefs = tDocRaster.createElementNS ("http://www.w3.org/2000/svg", "defs");
                tDefs.appendChild (tFiltre);
                tSvg.appendChild (tDefs);
                const tGroupe = tDocRaster.createElementNS ("http://www.w3.org/2000/svg", "g");
                tGroupe.setAttribute ("filter", `url(#${tFiltre.getAttribute ("id")})`);
                if (tOmbreImage && pItem.item_ombre_zones_dessinees_seules !== false)
                  tGroupe.appendChild (tDocRaster.importNode (tOmbreImage.cloneNode (true), true));
                else
                  {
                    const tRect = tDocRaster.createElementNS ("http://www.w3.org/2000/svg", "rect");
                    tRect.setAttribute ("x", "0");
                    tRect.setAttribute ("y", "0");
                    tRect.setAttribute ("width", String (tW));
                    tRect.setAttribute ("height", String (tH));
                    tRect.setAttribute ("rx", String (tRadius));
                    tRect.setAttribute ("ry", String (tRadius));
                    tRect.setAttribute ("fill", "#000000");
                    tGroupe.appendChild (tRect);
                  }
                tSvg.appendChild (tGroupe);
                tOmbre.style.display = "";
                programme_raster_ombre_svg_scene_client (
                  tSignatureRaster,
                  new XMLSerializer ().serializeToString (tSvg),
                  tW + tMarge * 2,
                  tH + tMarge * 2,
                  () =>
                    {
                      if (gSceneTemplateActif
                          && gSceneTemplateActif._svgDoc
                          && gSceneTemplateActif._svgDoc.documentElement
                          && gSceneTemplateActif._svgDoc.documentElement.contains (pG))
                        {
                          gSceneTemplateActif._itemsInterfaceSvgSignature = null;
                          synchronise_items_interface_svg_tpl (gSceneTemplateActif);
                        }
                    }
                );
              }
            if (!tImageRaster) tOmbre.style.display = "none";
            if (tDefsFiltre && tDefsFiltre.parentNode) tDefsFiltre.parentNode.removeChild (tDefsFiltre);
          }
        else
          tOmbre.style.display = "none";
      }
  }

function synchronise_items_interface_svg_tpl (pTpl)
  {
    const tDoc = pTpl && pTpl._svgDoc;
    const tLayer = assure_calque_items_interface_svg (tDoc);
    if (!tLayer) return;
    const tMasqueCategorie = !categorie_scene_visible (CALQUES_CATEGORIE_INTERFACE);
    const tVerrouilleCategorie = categorie_scene_verrouillee (CALQUES_CATEGORIE_INTERFACE);
    const tMasqueLecture = items_interface_masques_lecture_tpl (pTpl);
    const tSuspenduFocusCarte = !mode_lecture_scene_actif () && items_interface_suspendus_par_focus_carte ();
    applique_visibilite_calque_items_interface_tpl (
      pTpl,
      tLayer,
      {
        masqueCategorie : tMasqueCategorie,
        verrouilleCategorie : tVerrouilleCategorie,
      }
    );
    const tSceneId = Session.get ("scene_id");
    const tScene = tSceneId ? Scenes.findOne (tSceneId) : null;
    const tItems = items_interface_scene (tScene);
    const tSignature = signature_items_interface_svg_client (
      tItems,
      {
        masqueCategorie : tMasqueCategorie,
        verrouilleCategorie : tVerrouilleCategorie,
        suspenduFocusCarte : tSuspenduFocusCarte,
        masqueLecture : tMasqueLecture,
        modeAffichageConnecte : utilisateur_connecte_diffusion_scene (),
      }
    );
    if (pTpl._itemsInterfaceSvgLayer === tLayer
        && pTpl._itemsInterfaceSvgSignature === tSignature
        && tLayer.children.length === tItems.length)
      {
        relance_items_svg_inline_lecture_tpl (pTpl);
        programme_synchronise_items_hype_tpl (pTpl);
        return;
      }
    pTpl._itemsInterfaceSvgLayer = tLayer;
    pTpl._itemsInterfaceSvgSignature = tSignature;
    const tIds = new Set (tItems.map ((pItem) => pItem.id));
    Array.from (tLayer.children).forEach ((pNode) =>
      {
        const tId = pNode && pNode.getAttribute && pNode.getAttribute ("id");
        if (!tIds.has (tId)) pNode.remove ();
      });
    for (const tItem of tItems)
      {
        let tG = tDoc.getElementById (tItem.id);
        if (!tG || tG.parentNode !== tLayer)
          {
            tG = tDoc.createElementNS ("http://www.w3.org/2000/svg", "g");
            tG.setAttribute ("id", tItem.id);
            tG.setAttribute ("data-cogibulle-interface-item", "1");
            tLayer.appendChild (tG);
          }
        while (tG.firstChild) tG.removeChild (tG.firstChild);
        const tW = Math.max (8, Number (tItem.item_w) || 96);
        const tH = Math.max (8, Number (tItem.item_h) || 96);
        const tRadius = Math.max (0, Math.min (100, Number (tItem.item_radius_pct) || 0)) / 100 * Math.min (tW, tH) / 2;
        const tItemActionLecture = item_interface_a_action_lecture_scene (tItem);
        const tSvgInlinePresent = item_interface_est_svg_inline (tItem);
        const tHypeTraverseClics = (!!tItem.item_hype_widget_id || tSvgInlinePresent) && tItem.item_hype_traverse_clics === true;
        const tMasqueModeAffichage = !objet_visible_mode_affichage_diffusion_client (tItem);
        tG.setAttribute ("transform", transform_item_interface_svg (tItem, tW, tH));
        const tOpaciteItem = Math.max (0, Math.min (1, Number (tItem.item_opacite) || 0));
        const tItemSceneVerrouillee = scene_cible_config_verrouillee_diffusion (tItem);
        tG.style.opacity = String (tItemSceneVerrouillee ? Math.min (tOpaciteItem, 0.42) : tOpaciteItem);
        tG.style.display = (tMasqueLecture || tItem.item_masque || tMasqueModeAffichage) ? "none" : "";
        tG.style.cursor = tItemActionLecture ? "pointer" : "default";
        tG.style.pointerEvents = (tMasqueLecture || tMasqueModeAffichage || tHypeTraverseClics) ? "none" : "all";
        tG.style.filter = "";
        if (tItemSceneVerrouillee) tG.setAttribute ("data-cogi-diffusion-scene-locked", "1");
        else tG.removeAttribute ("data-cogi-diffusion-scene-locked");

        const tItemIdSvg = tItem.id.replace (/[^a-zA-Z0-9_-]/g, "_");
        const tClipId = "clip-" + tItemIdSvg;
        const tOmbreFiltreId = "ombre-" + tItemIdSvg;
        const tOmbreInitialeActive = tItem.item_ombre_active === true;
        const tDefs = tDoc.createElementNS ("http://www.w3.org/2000/svg", "defs");
        tDefs.setAttribute ("data-role", "defs");
        if (tOmbreInitialeActive)
          {
            const tOmbreFiltre = tDoc.createElementNS ("http://www.w3.org/2000/svg", "filter");
            tOmbreFiltre.setAttribute ("id", tOmbreFiltreId);
            tOmbreFiltre.setAttribute ("data-role", "ombre_filtre");
            tOmbreFiltre.setAttribute ("filterUnits", "userSpaceOnUse");
            const tOmbreBlur = tDoc.createElementNS ("http://www.w3.org/2000/svg", "feGaussianBlur");
            tOmbreBlur.setAttribute ("in", "SourceAlpha");
            tOmbreBlur.setAttribute ("result", "ombre_blur");
            const tOmbreOffset = tDoc.createElementNS ("http://www.w3.org/2000/svg", "feOffset");
            tOmbreOffset.setAttribute ("in", "ombre_blur");
            tOmbreOffset.setAttribute ("result", "ombre_offset");
            const tOmbreFlood = tDoc.createElementNS ("http://www.w3.org/2000/svg", "feFlood");
            tOmbreFlood.setAttribute ("flood-color", "#000000");
            tOmbreFlood.setAttribute ("result", "ombre_couleur");
            const tOmbreComposite = tDoc.createElementNS ("http://www.w3.org/2000/svg", "feComposite");
            tOmbreComposite.setAttribute ("in", "ombre_couleur");
            tOmbreComposite.setAttribute ("in2", "ombre_offset");
            tOmbreComposite.setAttribute ("operator", "in");
            tOmbreFiltre.appendChild (tOmbreBlur);
            tOmbreFiltre.appendChild (tOmbreOffset);
            tOmbreFiltre.appendChild (tOmbreFlood);
            tOmbreFiltre.appendChild (tOmbreComposite);
            tDefs.appendChild (tOmbreFiltre);
          }
        tG.appendChild (tDefs);

        const tClip = tDoc.createElementNS ("http://www.w3.org/2000/svg", "clipPath");
        tClip.setAttribute ("id", tClipId);
        const tClipRect = tDoc.createElementNS ("http://www.w3.org/2000/svg", "rect");
        tClipRect.setAttribute ("data-role", "clip");
        tClip.appendChild (tClipRect);
        tG.appendChild (tClip);

        const tOmbre = tDoc.createElementNS ("http://www.w3.org/2000/svg", "g");
        tOmbre.setAttribute ("data-role", "ombre");
        if (tOmbreInitialeActive)
          tOmbre.setAttribute ("filter", "url(#" + tOmbreFiltreId + ")");
        tOmbre.style.pointerEvents = "none";

        const tUrl = url_asset_diffusion_scene_client (tItem.item_image_url) || (tItem.item_image_file_id ? url_image_morphing_client (tItem.item_image_file_id) : null);
        const tOmbreZonesDessinees = tItem.item_ombre_zones_dessinees_seules !== false;
        if (tUrl && tOmbreZonesDessinees && !tSvgInlinePresent)
          {
            const tOmbreImage = tDoc.createElementNS ("http://www.w3.org/2000/svg", "image");
            tOmbreImage.setAttribute ("data-role", "ombre_image");
            tOmbreImage.setAttribute ("preserveAspectRatio", "xMidYMid meet");
            tOmbreImage.setAttributeNS ("http://www.w3.org/1999/xlink", "href", tUrl);
            tOmbreImage.setAttribute ("href", tUrl);
            tOmbre.appendChild (tOmbreImage);
          }
        else
          {
            const tOmbreRect = tDoc.createElementNS ("http://www.w3.org/2000/svg", "rect");
            tOmbreRect.setAttribute ("data-role", "ombre_rect");
            tOmbreRect.setAttribute ("x", "0");
            tOmbreRect.setAttribute ("y", "0");
            tOmbreRect.setAttribute ("width", String (tW));
            tOmbreRect.setAttribute ("height", String (tH));
            tOmbreRect.setAttribute ("rx", String (tRadius));
            tOmbreRect.setAttribute ("ry", String (tRadius));
            tOmbreRect.setAttribute ("fill", "#000000");
            tOmbre.appendChild (tOmbreRect);
          }
        tG.appendChild (tOmbre);

        const tHypePresent = !!tItem.item_hype_widget_id;
        const tRect = tDoc.createElementNS ("http://www.w3.org/2000/svg", "rect");
        tRect.setAttribute ("data-role", "fond");
        tRect.setAttribute ("x", "0");
        tRect.setAttribute ("y", "0");
        tRect.setAttribute ("width", String (tW));
        tRect.setAttribute ("height", String (tH));
        tRect.setAttribute ("rx", String (tRadius));
        tRect.setAttribute ("ry", String (tRadius));
        tRect.setAttribute ("fill", (tHypePresent || tItem.item_fond_actif === false) ? "none" : (tItem.item_couleur || "#ffffff"));
        tRect.setAttribute ("fill-opacity", tHypePresent ? "0" : String (Math.max (0, Math.min (1, Number (tItem.item_fond_opacite) || 0))));
        tRect.style.pointerEvents = tHypeTraverseClics ? "none" : "all";
        tG.appendChild (tRect);
        const tContenu = tDoc.createElementNS ("http://www.w3.org/2000/svg", "g");
        tContenu.setAttribute ("clip-path", "url(#" + tClipId + ")");
        tG.appendChild (tContenu);
        if (tUrl && !tHypePresent && !tSvgInlinePresent)
          {
            const tImg = tDoc.createElementNS ("http://www.w3.org/2000/svg", "image");
            tImg.setAttribute ("data-role", "image");
            tImg.setAttribute ("preserveAspectRatio", "xMidYMid meet");
            tImg.setAttributeNS ("http://www.w3.org/1999/xlink", "href", tUrl);
            tImg.setAttribute ("href", tUrl);
            tContenu.appendChild (tImg);
          }
        if (tUrl && tSvgInlinePresent)
          {
            const tSvgDoc = charge_svg_inline_item_client (tUrl, pTpl, tItem.id);
            const tSvgInline = tSvgDoc
              ? nettoie_svg_inline_item_importe (tSvgDoc, tDoc, "item-" + tItem.id)
              : null;
            if (tSvgInline)
              {
                tContenu.appendChild (tSvgInline);
                applique_autoplay_svg_inline_item_noeud (tSvgInline, tItem);
              }
          }
        applique_geometrie_item_interface_svg (tG, tItem, tW, tH, tScene && tScene._id);
        if (mode_lecture_scene_actif () && scene_cible_config_terminee_lecture (tItem))
          ajoute_decoration_scene_reussie_item_svg (tDoc.documentElement, tG, tW, tH);
      }
    relance_items_svg_inline_lecture_tpl (pTpl);
    programme_synchronise_items_hype_tpl (pTpl);
    rattache_items_flottement_lecture_tpl (pTpl);
  }

function nettoie_items_hype_tpl (pTpl)
  {
    const tLayer = pTpl && typeof pTpl.find === "function" ? pTpl.find (".js_items_hype_layer") : null;
    if (tLayer)
      {
        Array.from (tLayer.children).forEach ((pHost) =>
          {
            nettoie_widget_hype_direct (pHost, {apresNettoyage : () => pHost.remove ()});
          });
      }
    hosts_hype_items_tpl (pTpl).forEach ((pHost) =>
      {
        nettoie_widget_hype_direct (pHost);
      });
  }

function vue_courante_items_hype_tpl (pTpl)
  {
    const tRoot = pTpl && pTpl._svgDoc && pTpl._svgDoc.documentElement;
    const tVB = (pTpl && pTpl._svgVB) || normalise_vue_carte_client (pTpl && pTpl._svgVBInit);
    if (tVB && tVB.w > 0 && tVB.h > 0) return tVB;
    if (!tRoot) return null;
    const tAttr = tRoot.getAttribute ("viewBox") || "";
    const tParts = tAttr.split (/[\s,]+/).map (Number);
    if (tParts.length >= 4 && tParts.every (Number.isFinite) && tParts[2] > 0 && tParts[3] > 0)
      return {x : tParts[0], y : tParts[1], w : tParts[2], h : tParts[3]};
    return null;
  }

const gSvgInlineItemsCache = new Map ();
const SVG_INLINE_ITEM_ANIMATION_GUARD_ID = "cogi-inline-item-animation-guard";
const SVG_INLINE_ITEM_ANIMATION_STATE_ATTR = "data-cogi-animation-state";
const SVG_INLINE_ITEM_ANIMATION_LOOP_ATTR = "data-cogi-animation-loop";
const SVG_INLINE_ITEM_ANIMATION_RESET_ATTR = "data-cogi-animation-reset";
const SVG_INLINE_ITEM_ANIMATION_CSS_ATTR = "data-cogi-animation-css";
const SVG_INLINE_ITEM_SMIL_SELECTOR = "animate, animateTransform, animateMotion, animateColor, set";
const SVG_INLINE_ITEM_CSS_ANIMATION_RE = /\banimation\s*:|\banimation-(?:name|duration)\s*:/i;

function item_interface_est_svg_inline (pItem)
  {
    return !!(pItem
      && pItem.item_svg_inline === true
      && (pItem.item_image_url || pItem.item_image_file_id)
      && !pItem.item_hype_widget_id);
  }

function svg_inline_item_detecte_animation_css (pSvg)
  {
    if (!pSvg || !pSvg.getAttribute) return false;
    const tAttribut = pSvg.getAttribute (SVG_INLINE_ITEM_ANIMATION_CSS_ATTR);
    if (tAttribut === "true") return true;
    if (tAttribut === "false") return false;
    if (SVG_INLINE_ITEM_CSS_ANIMATION_RE.test (pSvg.getAttribute ("style") || "")) return true;
    if (!pSvg.querySelectorAll) return false;
    const tStyles = Array.from (pSvg.querySelectorAll ("style"))
      .filter ((pNoeud) => !pNoeud || !pNoeud.getAttribute || pNoeud.getAttribute ("id") !== SVG_INLINE_ITEM_ANIMATION_GUARD_ID)
      .map ((pNoeud) => pNoeud.textContent || "")
      .join ("\n");
    if (SVG_INLINE_ITEM_CSS_ANIMATION_RE.test (tStyles)) return true;
    return Array.from (pSvg.querySelectorAll ("[style]")).some ((pNoeud) =>
      SVG_INLINE_ITEM_CSS_ANIMATION_RE.test (pNoeud.getAttribute ("style") || "")
    );
  }

function nettoie_svg_inline_item_noeuds_interdits (pSvg)
  {
    if (!pSvg || !pSvg.querySelectorAll) return;
    pSvg.querySelectorAll ("script, foreignObject, iframe, object, embed").forEach ((pNoeud) => pNoeud.remove ());
    pSvg.querySelectorAll (SVG_INLINE_ITEM_SMIL_SELECTOR).forEach ((pNoeud) => pNoeud.remove ());
    const tNoeuds = [pSvg, ...Array.from (pSvg.querySelectorAll ("*"))];
    tNoeuds.forEach ((pNoeud) =>
      {
        Array.from (pNoeud.attributes || []).forEach ((pAttr) =>
          {
            const tNom = pAttr.name || "";
            const tValeur = pAttr.value || "";
            if (/^on/i.test (tNom)) pNoeud.removeAttribute (tNom);
            if ((tNom === "href" || tNom === "xlink:href") && /^\s*(javascript:|https?:)/i.test (tValeur))
              pNoeud.removeAttribute (tNom);
          });
      });
  }

function applique_garde_animation_svg_inline_item (pSvg)
  {
    if (!pSvg || !pSvg.setAttribute) return pSvg;
    const tAnimationCss = svg_inline_item_detecte_animation_css (pSvg);
    pSvg.setAttribute (SVG_INLINE_ITEM_ANIMATION_STATE_ATTR, "paused");
    pSvg.setAttribute (SVG_INLINE_ITEM_ANIMATION_LOOP_ATTR, "false");
    pSvg.setAttribute (SVG_INLINE_ITEM_ANIMATION_CSS_ATTR, tAnimationCss ? "true" : "false");
    pSvg.removeAttribute (SVG_INLINE_ITEM_ANIMATION_RESET_ATTR);
    if (pSvg.querySelectorAll)
      pSvg.querySelectorAll ("#" + SVG_INLINE_ITEM_ANIMATION_GUARD_ID).forEach ((pNoeud) => pNoeud.remove ());
    const tStyle = pSvg.ownerDocument.createElementNS ("http://www.w3.org/2000/svg", "style");
    tStyle.setAttribute ("id", SVG_INLINE_ITEM_ANIMATION_GUARD_ID);
    tStyle.textContent = `
svg[${SVG_INLINE_ITEM_ANIMATION_STATE_ATTR}="paused"],
svg[${SVG_INLINE_ITEM_ANIMATION_STATE_ATTR}="paused"] * {
  animation-play-state: paused !important;
}
svg[${SVG_INLINE_ITEM_ANIMATION_STATE_ATTR}="running"],
svg[${SVG_INLINE_ITEM_ANIMATION_STATE_ATTR}="running"] * {
  animation-play-state: running !important;
}
svg[${SVG_INLINE_ITEM_ANIMATION_RESET_ATTR}="true"],
svg[${SVG_INLINE_ITEM_ANIMATION_RESET_ATTR}="true"] * {
  animation: none !important;
  animation-play-state: paused !important;
}
svg[${SVG_INLINE_ITEM_ANIMATION_STATE_ATTR}="running"][${SVG_INLINE_ITEM_ANIMATION_LOOP_ATTR}="true"],
svg[${SVG_INLINE_ITEM_ANIMATION_STATE_ATTR}="running"][${SVG_INLINE_ITEM_ANIMATION_LOOP_ATTR}="true"] * {
  animation-iteration-count: infinite !important;
}
`;
    pSvg.appendChild (tStyle);
    return pSvg;
  }

function animation_css_svg_inline_item_disponible_noeud (pNoeud)
  {
    if (!pNoeud || !pNoeud.setAttribute) return false;
    const tDisponible = svg_inline_item_detecte_animation_css (pNoeud);
    pNoeud.setAttribute (SVG_INLINE_ITEM_ANIMATION_CSS_ATTR, tDisponible ? "true" : "false");
    return tDisponible;
  }

function redemarre_animation_css_svg_inline_item (pNoeud)
  {
    if (!pNoeud || !pNoeud.setAttribute) return false;
    pNoeud.setAttribute (SVG_INLINE_ITEM_ANIMATION_STATE_ATTR, "paused");
    pNoeud.setAttribute (SVG_INLINE_ITEM_ANIMATION_RESET_ATTR, "true");
    try { pNoeud.getBoundingClientRect (); }
    catch (e) {}
    pNoeud.removeAttribute (SVG_INLINE_ITEM_ANIMATION_RESET_ATTR);
    pNoeud.setAttribute (SVG_INLINE_ITEM_ANIMATION_STATE_ATTR, "running");
    return true;
  }

function commande_svg_inline_item_noeud (pNoeud, pAction, pOptions = {})
  {
    if (pNoeud && pNoeud.setAttribute && Object.prototype.hasOwnProperty.call (pOptions || {}, "loop"))
      pNoeud.setAttribute (SVG_INLINE_ITEM_ANIMATION_LOOP_ATTR, pOptions.loop ? "true" : "false");
    const tDisponible = animation_css_svg_inline_item_disponible_noeud (pNoeud);
    if (!pNoeud || !pNoeud.setAttribute) return false;
    if (pAction === "stop")
      {
        pNoeud.setAttribute (SVG_INLINE_ITEM_ANIMATION_STATE_ATTR, "paused");
        pNoeud.setAttribute (SVG_INLINE_ITEM_ANIMATION_RESET_ATTR, "true");
        return tDisponible;
      }
    if (pAction === "restart")
      return tDisponible ? redemarre_animation_css_svg_inline_item (pNoeud) : false;
    pNoeud.removeAttribute (SVG_INLINE_ITEM_ANIMATION_RESET_ATTR);
    pNoeud.setAttribute (SVG_INLINE_ITEM_ANIMATION_STATE_ATTR, pAction === "play" ? "running" : "paused");
    return tDisponible;
  }

function noeud_svg_inline_item_tpl (pTpl, pItemId)
  {
    const tDoc = pTpl && pTpl._svgDoc;
    const tItemNode = tDoc && pItemId ? tDoc.getElementById (pItemId) : null;
    return tItemNode && tItemNode.querySelector
      ? tItemNode.querySelector ('svg[data-role="inline_svg"]')
      : null;
  }

function applique_autoplay_svg_inline_item_noeud (pNoeud, pItem)
  {
    if (!pNoeud || !pItem) return false;
    const tLecture = mode_lecture_scene_actif ();
    const tAutoplay = pItem.item_hype_autoplay !== false;
    const tLoop = tLecture && pItem.item_hype_loop === true;
    if (!tLecture)
      {
        pNoeud._itemSvgInlineAutoplayApplique = "edition-pause";
        commande_svg_inline_item_noeud (pNoeud, "pause");
        return true;
      }
    if (pNoeud._itemSvgInlineLectureDemandee && !tAutoplay)
      {
        pNoeud._itemSvgInlineLectureDemandee = false;
        pNoeud._itemSvgInlineAutoplayApplique = "lecture-demande";
        commande_svg_inline_item_noeud (pNoeud, "restart", {loop : tLoop});
        return true;
      }
    const tCle = "lecture:" + (tAutoplay ? "auto" : "manuel") + ":" + (tLoop ? "loop" : "once");
    if (pNoeud._itemSvgInlineAutoplayApplique === tCle) return true;
    pNoeud._itemSvgInlineAutoplayApplique = tCle;
    commande_svg_inline_item_noeud (pNoeud, tAutoplay ? "restart" : "stop", {loop : tLoop});
    return true;
  }

function lance_svg_inline_item_tpl (pTpl, pItemId)
  {
    const tNoeud = noeud_svg_inline_item_tpl (pTpl, pItemId);
    const tItem = item_interface_scene_par_id (pItemId);
    if (!tNoeud || !tItem) return false;
    tNoeud._itemSvgInlineLectureDemandee = true;
    return applique_autoplay_svg_inline_item_noeud (tNoeud, tItem)
      || commande_svg_inline_item_noeud (tNoeud, "restart", {loop : tItem.item_hype_loop === true});
  }

function commande_items_svg_inline_tpl (pTpl, pAction)
  {
    if (!pTpl) return;
    items_interface_scene ().forEach ((pItem) =>
      {
        if (!item_interface_est_svg_inline (pItem)) return;
        const tNoeud = noeud_svg_inline_item_tpl (pTpl, pItem.id);
        if (!tNoeud) return;
        if (pAction === "reset-lecture")
          {
            tNoeud._itemSvgInlineAutoplayApplique = null;
            tNoeud._itemSvgInlineLectureDemandee = false;
            commande_svg_inline_item_noeud (tNoeud, "stop");
            return;
          }
        if (pAction === "pause-edition")
          {
            tNoeud._itemSvgInlineAutoplayApplique = "edition-pause";
            tNoeud._itemSvgInlineLectureDemandee = false;
            commande_svg_inline_item_noeud (tNoeud, "pause");
            return;
          }
        if (pAction === "pause")
          {
            commande_svg_inline_item_noeud (tNoeud, "pause");
            return;
          }
        if (pAction === "play")
          {
            tNoeud._itemSvgInlineAutoplayApplique = null;
            applique_autoplay_svg_inline_item_noeud (tNoeud, pItem);
          }
      });
  }

function relance_items_svg_inline_lecture_tpl (pTpl)
  {
    if (!pTpl || !mode_lecture_scene_actif ()) return;
    items_interface_scene ().forEach ((pItem) =>
      {
        if (!item_interface_est_svg_inline (pItem)) return;
        const tNoeud = noeud_svg_inline_item_tpl (pTpl, pItem.id);
        if (!tNoeud) return;
        applique_autoplay_svg_inline_item_noeud (tNoeud, pItem);
      });
  }

function nettoie_svg_inline_item_importe (pSvgSource, pDocCible, pPrefixe)
  {
    if (!pSvgSource || !pDocCible) return null;
    const tSvg = pSvgSource.documentElement;
    if (!tSvg || String (tSvg.nodeName).toLowerCase () !== "svg") return null;
    const tClone = pDocCible.importNode (tSvg, true);
    nettoie_svg_inline_item_noeuds_interdits (tClone);
    tClone.setAttribute ("data-role", "inline_svg");
    tClone.setAttribute ("overflow", "visible");
    tClone.style.overflow = "visible";
    tClone.style.pointerEvents = "none";
    const tViewBox = tClone.getAttribute ("viewBox");
    if (!tViewBox)
      {
        const tW = Number (String (tClone.getAttribute ("width") || "").replace (/[^0-9.-]/g, ""));
        const tH = Number (String (tClone.getAttribute ("height") || "").replace (/[^0-9.-]/g, ""));
        if (Number.isFinite (tW) && Number.isFinite (tH) && tW > 0 && tH > 0)
          tClone.setAttribute ("viewBox", `0 0 ${tW} ${tH}`);
      }
    isole_svg_inline_zoom_alternatif (tClone, pPrefixe || "cogi_item_svg");
    applique_garde_animation_svg_inline_item (tClone);
    return tClone;
  }

function document_svg_inline_item_depuis_texte (pTexte)
  {
    const tDoc = new DOMParser ().parseFromString (String (pTexte || ""), "image/svg+xml");
    const tErreur = tDoc.querySelector && tDoc.querySelector ("parsererror");
    const tRoot = tDoc && tDoc.documentElement;
    return tErreur || !tRoot || String (tRoot.nodeName).toLowerCase () !== "svg" ? null : tDoc;
  }

function charge_svg_inline_item_client (pUrl, pTpl, pItemId)
  {
    if (!pUrl || !pTpl || !pItemId) return null;
    let tEntree = gSvgInlineItemsCache.get (pUrl);
    if (tEntree && tEntree.svgDoc) return tEntree.svgDoc;
    if (tEntree && tEntree.svgText)
      {
        tEntree.svgDoc = document_svg_inline_item_depuis_texte (tEntree.svgText);
        if (tEntree.svgDoc) return tEntree.svgDoc;
      }
    if (tEntree && tEntree.loading) return null;
    tEntree = {loading : true, svgText : null, svgDoc : null, erreur : null};
    gSvgInlineItemsCache.set (pUrl, tEntree);
    if (gSvgInlineItemsCache.size > 32)
      {
        const tPremiereCle = gSvgInlineItemsCache.keys ().next ().value;
        if (tPremiereCle && tPremiereCle !== pUrl) gSvgInlineItemsCache.delete (tPremiereCle);
      }
    fetch (pUrl, {credentials : "same-origin"})
      .then ((pRes) =>
        {
          if (!pRes.ok) throw new Error ("SVG inline introuvable");
          return pRes.text ();
        })
      .then ((pTexte) =>
        {
          const tDoc = document_svg_inline_item_depuis_texte (pTexte);
          if (!tDoc) throw new Error ("SVG inline invalide");
          tEntree.svgText = pTexte;
          tEntree.svgDoc = tDoc;
          tEntree.loading = false;
          if (pTpl && pTpl._itemsInterfaceSvgSignature)
            pTpl._itemsInterfaceSvgSignature = null;
          synchronise_items_interface_svg_tpl (pTpl || gSceneTemplateActif);
        })
      .catch ((pErr) =>
        {
          tEntree.loading = false;
          tEntree.erreur = pErr;
          console.warn ("[diffusion item] SVG inline:", pErr && (pErr.message || pErr));
        });
    return null;
  }

function selecteur_item_hype_host (pItemId)
  {
    const tId = String (pItemId || "");
    const tEsc = window.CSS && typeof window.CSS.escape === "function"
      ? window.CSS.escape (tId)
      : tId.replace (/["\\]/g, "\\$&");
    return '.item_hype_widget[data-item-id="' + tEsc + '"]';
  }

function hosts_hype_items_tpl (pTpl)
  {
    const tRoot = pTpl && pTpl._svgDoc && pTpl._svgDoc.documentElement;
    // Nettoyage transitoire d'anciens essais de widgets Hype dans foreignObject SVG.
    return tRoot ? Array.from (tRoot.querySelectorAll (".item_hype_widget_svg")) : [];
  }

function positionne_widget_hype_item_tpl (pTpl, pHost, pItem, pProfondeur = 1)
  {
    const tScene = pTpl && typeof pTpl.find === "function" ? pTpl.find (".scene") : null;
    const tSvgHost = (pTpl && pTpl._svgObjectNode) || (pTpl && typeof pTpl.find === "function" ? pTpl.find (".contenu_svg_scene") : null);
    const tVue = vue_courante_items_hype_tpl (pTpl);
    if (!tScene || !tSvgHost || !tVue || !pHost || !pItem) return false;
    const tSceneRect = tScene.getBoundingClientRect ();
    const tSvgRect = tSvgHost.getBoundingClientRect ();
    const tW = Math.max (8, Number (pItem.item_w) || 96);
    const tH = Math.max (8, Number (pItem.item_h) || 96);
    pHost.style.left = ((tSvgRect.left - tSceneRect.left) + ((Number (pItem.item_x) - tVue.x) / tVue.w) * tSvgRect.width) + "px";
    pHost.style.top = ((tSvgRect.top - tSceneRect.top) + ((Number (pItem.item_y) - tVue.y) / tVue.h) * tSvgRect.height) + "px";
    pHost.style.width = Math.max (1, (tW / tVue.w) * tSvgRect.width) + "px";
    pHost.style.height = Math.max (1, (tH / tVue.h) * tSvgRect.height) + "px";
    pHost.style.opacity = String (Math.max (0, Math.min (1, Number (pItem.item_opacite) || 0)));
    pHost.style.transform = "rotate(" + normalise_rotation_degres_client (pItem.item_rotation_deg) + "deg)";
    pHost.style.zIndex = String (Math.max (1, Number (pProfondeur) || 1));
    return true;
  }

function widget_hype_item_client (pItem)
  {
    const tWidgetId = pItem && typeof pItem.item_hype_widget_id === "string" ? pItem.item_hype_widget_id : "";
    return tWidgetId ? HypeWidgets.findOne ({_id : tWidgetId}) : null;
  }

function applique_autoplay_hype_item_host (pHost, pItem)
  {
    if (!pHost || !pHost._hubHypeDocument || !pItem) return;
    const tLecture = mode_lecture_scene_actif ();
    const tAutoplay = pItem.item_hype_autoplay !== false;
    pHost._hubHypeLoop = tLecture && pItem.item_hype_loop === true;
    if (!tLecture)
      {
        if (pHost._itemHypeAutoplayApplique === null || typeof pHost._itemHypeAutoplayApplique === "undefined")
          {
            pHost._itemHypeAutoplayApplique = "edition-pause";
            commande_timeline_hype (pHost._hubHypeDocument, "pause");
          }
        return;
      }
    if (pHost._itemHypeLectureResetPending && tAutoplay)
      {
        pHost._itemHypeLectureResetPending = false;
        pHost._itemHypeLectureDemandee = false;
        pHost._itemHypeAutoplayApplique = "lecture:restart";
        commande_timeline_hype (pHost._hubHypeDocument, "restart");
        return;
      }
    pHost._itemHypeLectureResetPending = false;
    if (!tAutoplay && pHost._itemHypeLectureDemandee)
      {
        pHost._itemHypeLectureDemandee = false;
        commande_timeline_hype (pHost._hubHypeDocument, "restart");
        pHost._itemHypeAutoplayApplique = "lecture-demande";
        return;
      }
    const tCleAutoplay = "lecture:" + (tAutoplay ? "auto" : "manuel");
    if (pHost._itemHypeAutoplayApplique === tCleAutoplay) return;
    pHost._itemHypeAutoplayApplique = tCleAutoplay;
    commande_timeline_hype (pHost._hubHypeDocument, tAutoplay ? "play" : "stop");
  }

function applique_interactions_hype_item_host (pHost, pItem)
  {
    if (!pHost || !pItem) return;
    pHost.classList.toggle ("traverse_clics", pItem.item_hype_traverse_clics === true);
  }

function synchronise_items_hype_tpl (pTpl)
  {
    const tLayer = pTpl && typeof pTpl.find === "function" ? pTpl.find (".js_items_hype_layer") : null;
    if (!tLayer) return;
    if (pTpl._lectureZoomAltPreparation
     || pTpl._lectureFocusCarteEntreeEnCours
     || pTpl._lectureZoomAltSortieEnCours
     || pTpl._lectureZoomAltReaffichageMasquesEnCours)
      return;
    const tItems = items_interface_scene ();
    const tProfondeurs = new Map (
      ids_rendu_items_interface_svg (tItems).map ((pId, pIndex) => [pId, pIndex + 1])
    );
    const tMasqueLecture = items_interface_masques_lecture_tpl (pTpl);
    const tIdsVoulus = new Set ();
    for (const tItem of tItems)
      {
        if (!tItem || !tItem.item_hype_widget_id) continue;
        if (tMasqueLecture || tItem.item_masque || !objet_visible_mode_affichage_diffusion_client (tItem)) continue;
        const tWidget = widget_hype_item_client (tItem);
        if (!tWidget) continue;
        tIdsVoulus.add (tItem.id);
        let tHost = tLayer.querySelector (selecteur_item_hype_host (tItem.id));
        if (!tHost)
          {
            tHost = document.createElement ("div");
            tHost.className = "item_hype_widget";
            tHost.setAttribute ("data-item-id", tItem.id);
            tLayer.appendChild (tHost);
          }
        applique_interactions_hype_item_host (tHost, tItem);
        positionne_widget_hype_item_tpl (pTpl, tHost, tItem, tProfondeurs.get (tItem.id));
        const tHypeLoadingSignature = tWidget._id + ":item_" + tItem.id;
        const tHypeChargementIdentique = tHost._hubHypeLoadingSignature === tHypeLoadingSignature;
        if (tHost._itemHypeWidgetId !== tWidget._id || (!tHost._hubHypeDocument && !tHypeChargementIdentique))
          {
            tHost._itemHypeWidgetId = tWidget._id;
            tHost._hubHypeLoadingSignature = tHypeLoadingSignature;
            tHost._itemHypeAutoplayApplique = null;
            monte_widget_hype_direct (tHost, tWidget, {instanceKey : "item_" + tItem.id})
              .then ((pHypeDocument) =>
                {
                  if (!pHypeDocument) return;
                  if (tHost._hubHypeLoadingSignature === tHypeLoadingSignature)
                    tHost._hubHypeLoadingSignature = null;
                  tHost._itemHypeWidgetId = tWidget._id;
                  if (mode_lecture_scene_actif ())
                    {
                      commande_timeline_hype (tHost._hubHypeDocument, "stop");
                      tHost._itemHypeLectureResetPending = true;
                    }
                  applique_autoplay_hype_item_host (tHost, tItem);
                })
              .catch ((pErr) =>
                {
                  if (tHost._hubHypeLoadingSignature === tHypeLoadingSignature)
                    tHost._hubHypeLoadingSignature = null;
                  tHost._itemHypeWidgetId = null;
                  affiche_erreur_hype_direct (tHost, pErr);
                });
          }
        else if (tHost._hubHypeDocument)
          applique_autoplay_hype_item_host (tHost, tItem);
      }
    Array.from (tLayer.children).forEach ((pHost) =>
      {
        const tId = pHost.getAttribute ("data-item-id");
        if (tIdsVoulus.has (tId)) return;
        nettoie_widget_hype_direct (pHost, {apresNettoyage : () => pHost.remove ()});
      });
  }

function programme_synchronise_items_hype_tpl (pTpl)
  {
    if (!pTpl) return;
    if (pTpl._itemsHypeSyncRaf) cancelAnimationFrame (pTpl._itemsHypeSyncRaf);
    pTpl._itemsHypeSyncRaf = requestAnimationFrame (() =>
      {
        pTpl._itemsHypeSyncRaf = null;
        synchronise_items_hype_tpl (pTpl);
      });
  }

function lance_widget_hype_item_tpl (pTpl, pItemId)
  {
    const tLayer = pTpl && typeof pTpl.find === "function" ? pTpl.find (".js_items_hype_layer") : null;
    const tHost = tLayer && pItemId ? tLayer.querySelector (selecteur_item_hype_host (pItemId)) : null;
    if (!tHost) return false;
    if (tHost._hubHypeDocument)
      return commande_timeline_hype (tHost._hubHypeDocument, "restart") || commande_timeline_hype (tHost._hubHypeDocument, "play");
    tHost._itemHypeLectureDemandee = true;
    return true;
  }

function commande_items_hype_tpl (pTpl, pAction, pOptions = {})
  {
    const tLayer = pTpl && typeof pTpl.find === "function" ? pTpl.find (".js_items_hype_layer") : null;
    const tHosts = tLayer ? Array.from (tLayer.children) : [];
    tHosts.forEach ((pHost) =>
      {
        if (!pHost || !pHost._hubHypeDocument) return;
        pHost._hubHypeManualLoop = false;
        if (pAction === "reset-lecture")
          {
            pHost._itemHypeLectureResetPending = true;
            pHost._itemHypeAutoplayApplique = null;
            pHost._itemHypeLectureDemandee = false;
            commande_timeline_hype (pHost._hubHypeDocument, "stop");
            return;
          }
        if (pAction === "pause-edition")
          {
            pHost._itemHypeLectureResetPending = false;
            pHost._itemHypeAutoplayApplique = "edition-pause";
            commande_timeline_hype (pHost._hubHypeDocument, "pause");
            return;
          }
        if (pOptions && pOptions.resetEtat)
          pHost._itemHypeAutoplayApplique = null;
        commande_timeline_hype (pHost._hubHypeDocument, pAction);
      });
  }

function assure_style_svg_scene (pDoc, pId)
  {
    const tRoot = pDoc && pDoc.documentElement;
    if (!tRoot) return null;
    let tStyle = pDoc.getElementById (pId);
    if (!tStyle)
      {
        tStyle = pDoc.createElementNS ("http://www.w3.org/2000/svg", "style");
        tStyle.setAttribute ("id", pId);
        tStyle.setAttribute ("type", "text/css");
        tRoot.insertBefore (tStyle, tRoot.firstChild || null);
      }
    return tStyle;
  }

function applique_etats_categories_svg_tpl (pTpl)
  {
    const tDoc = pTpl && pTpl._svgDoc;
    const tSceneId = Session.get ("scene_id");
    if (!tDoc || !tDoc.documentElement || !tSceneId) return;

    const tReglesMasquees = [];
    const tReglesVerrouillees = [];
    for (const tNom of CATEGORIES_PERTINENTES)
      {
        const tIds = ids_categorie_scene_depuis_tpl (pTpl, tNom);
        if (!tIds.length) continue;
        const tSelecteurs = tIds
          .map ((pId) => echappe_selecteur_css_svg (pId))
          .filter (Boolean)
          .map ((pId) => `#${pId}`);
        if (!tSelecteurs.length) continue;

        if (!categorie_scene_visible (tNom, tSceneId))
          tReglesMasquees.push (`${tSelecteurs.join (", ")} { display: none !important; }`);

        if (categorie_scene_verrouillee (tNom, tSceneId))
          {
            const tSelecteursEtendus = tSelecteurs.flatMap ((pSel) => [pSel, `${pSel} *`]);
            tReglesVerrouillees.push (
              `${tSelecteursEtendus.join (", ")} { pointer-events: none !important; cursor: default !important; }`
            );
          }
      }

    const tStyleMasquees = assure_style_svg_scene (tDoc, SVG_STYLE_CATEGORIES_MASQUEES_ID);
    const tStyleVerrouillees = assure_style_svg_scene (tDoc, SVG_STYLE_CATEGORIES_VERROUILLEES_ID);
    if (tStyleMasquees) tStyleMasquees.textContent = tReglesMasquees.join ("\n");
    if (tStyleVerrouillees) tStyleVerrouillees.textContent = tReglesVerrouillees.join ("\n");
  }

function force_rafraichissement_categories_svg_tpl (pTpl)
  {
    if (!pTpl) return;
    Tracker.afterFlush (() =>
      {
        synchronise_items_interface_svg_tpl (pTpl);
        applique_etats_categories_svg_tpl (pTpl);
        if (pTpl._svgDoc)
          applique_hover_svg (pTpl._svgDoc, pTpl._svgHoverId || null);
      });
  }

function applique_selection_arbre_calques_tpl (pTpl)
  {
    const tArbre = pTpl && pTpl.find && pTpl.find (".analyse_svg_scene > .arbre_calques");
    if (!tArbre) return;
    tArbre.querySelectorAll (".arbre_calque.est_selectionne, .arbre_categorie.est_selectionnee")
      .forEach ((pElt) => pElt.classList.remove ("est_selectionne", "est_selectionnee"));

    const tCategorie = gCategorieSelectionnee.get ();
    const tBulle = gBulleSelectionnee.get ();
    const tCalque = gCalqueSelectionne.get ();
    let tCible = null;
    if (tBulle)
      tCible = tArbre.querySelector (`.arbre_calque[data-bulle-carte-id="${echappe_selecteur_css_svg (tBulle)}"]`);
    else if (tCalque)
      tCible = tArbre.querySelector (`.arbre_calque[data-id="${echappe_selecteur_css_svg (tCalque)}"]`);
    else if (tCategorie)
      tCible = tArbre.querySelector (`.arbre_categorie[data-categorie="${echappe_selecteur_css_svg (tCategorie)}"]`);

    if (!tCible) return;
    tCible.classList.add (tCible.classList.contains ("arbre_categorie") ? "est_selectionnee" : "est_selectionne");
  }

function synchronise_selection_scene_avec_categories ()
  {
    const tCalqueId = gCalqueSelectionne.get ();
    if (tCalqueId)
      {
        const tCategorie = trouve_categorie_pour_id (tCalqueId);
        if (tCategorie && !categorie_scene_interactive (tCategorie))
          selectionne_calque (null, {autoplay : false});
      }

    const tCategorie = gCategorieSelectionnee.get ();
    if (tCategorie && !categorie_scene_interactive (tCategorie))
      {
        gCategorieSelectionnee.set (null);
        if (gInspecteurCalquesMode.get () === "categorie")
          gInspecteurCalquesMode.set (null);
      }

    const tBulleId = gBulleSelectionnee.get ();
    if (tBulleId && !categorie_scene_interactive (CALQUES_CATEGORIE_BULLES))
      {
        reinitialise_selection_bulle_calque ();
        if (gInspecteurCalquesMode.get () === "bulle")
          gInspecteurCalquesMode.set (null);
      }
  }

function hover_info_bulle_carte (pCarteId)
  {
    return pCarteId ? (HOVER_BULLE_CARTE_PREFIX + pCarteId) : null;
  }

function carte_id_depuis_hover_info_bulle (pHoverId)
  {
    return (typeof pHoverId === "string" && pHoverId.startsWith (HOVER_BULLE_CARTE_PREFIX))
      ? (pHoverId.slice (HOVER_BULLE_CARTE_PREFIX.length) || null)
      : null;
  }

function normalise_hover_svg_interactif (pId)
  {
    if (mode_lecture_scene_actif ()) return null;
    const tCarteBulleId = carte_id_depuis_hover_info_bulle (pId);
    if (tCarteBulleId)
      {
        if (!bulles_scene_visibles ()
         || categorie_scene_verrouillee (CALQUES_CATEGORIE_BULLES))
          return null;
        return pId;
      }
    return pId;
  }

function ids_calques_selectionnables_scene (pScene)
  {
    const tObjets = svg_objets_scene_normalises (pScene);
    if (!tObjets) return [];
    const tIds = [];
    for (const tNom of noms_categories_svg_scene (pScene))
      {
        const tBrut = tObjets[tNom];
        if (!Array.isArray (tBrut) || !tBrut.length) continue;
        const tTries = tBrut
          .filter ((pCalque) => pCalque && pCalque.id)
          .slice ()
          .sort ((a, b) =>
            cle_tri_calque (a["data-name"], tNom).localeCompare (
              cle_tri_calque (b["data-name"], tNom),
              "fr",
              {sensitivity : "base"}
            ));
        for (const tCalque of tTries)
          tIds.push (tCalque.id);
      }
    return tIds;
  }

function bulles_arbre_calques_scene ()
  {
    return cartes_scene_liste ().map ((pCarte, pIndex) =>
      ({
        carte_id : pCarte._id,
        nom : nom_affiche_carte (carte_source_pour_bulle_scene (pCarte._id) || pCarte, pIndex + 1),
        est_bulle : true,
        sortable_class : "js_calques_tree_carte_item est_reordonnable",
      }));
  }

function categorie_bulles_arbre_calques ()
  {
    const tVisible = bulles_scene_visibles ();
    const tBulles = bulles_arbre_calques_scene ();
    const tVerrouillee = categorie_scene_verrouillee (CALQUES_CATEGORIE_BULLES);
    return {
      nom : CALQUES_CATEGORIE_BULLES,
      libelle : "cartes",
      ouverte : categorie_scene_ouverte (CALQUES_CATEGORIE_BULLES),
      calques : tBulles,
      est_bulles : true,
      sortable_class : "js_calques_tree_cartes_sortable",
      visible : tVisible,
      toggle_label : tVisible ? "Masquer" : "Montrer",
      toggle_class : tVisible ? "" : "est_actif",
      verrouillee : tVerrouillee,
      lock_label : tVerrouillee ? "Déverrouiller" : "Verrouiller",
      lock_class : tVerrouillee ? "est_actif" : "",
    };
  }

function selectionne_calque (pId, pOpt)
  {
    const tOpt = pOpt || {};
    if (!abandon_brouillon_bulle_si_necessaire (null, tOpt)) return false;
    const tAutoplay = tOpt.autoplay !== false;
    const tSceneId = Session.get ("scene_id");
    const tSelectionKey = fabrique_selection_key_inspecteur (tSceneId, pId || null);
    if (!tAutoplay && tSelectionKey)
      inhibe_autoplay_selection_inspecteur (tSelectionKey);
    gInspecteurCartesGlobalMessage.set (null);
    aligne_autoplay_maj_fichier_sur_selection_inspecteur (tSelectionKey);
    if (gWS.instance && gWS.instance.isPlaying ()) gWS.instance.pause ();
    arrete_lecture_audio_projet_inspecteur ();
    arrete_lecture_audio_scene_inspecteur ();
    const tMeme = !!pId && gCalqueSelectionne.get () === pId;
    reinitialise_selection_bulle_calque ();
    gInspecteurSceneAudioActif.set (null);
    gCategorieSelectionnee.set (null);
    gCalqueSelectionne.set (pId || null);
    gInspecteurCalquesMode.set (pId ? "calque" : null);
    if (pId && tSceneId) ecrit_derniere_selection_calques_scene (tSceneId, "calque", pId);
    if (tMeme && tAutoplay)
      Tracker.afterFlush (() => { relance_autoplay_calque_courant (); });
    return true;
  }

function selectionne_categorie (pNom)
  {
    if (!abandon_brouillon_bulle_si_necessaire ()) return false;
    annule_autoplay_prochaine_maj_fichier_inspecteur ();
    gInspecteurCartesGlobalMessage.set (null);
    reinitialise_selection_bulle_calque ();
    gCalqueSelectionne.set (null);
    gCategorieSelectionnee.set (pNom || null);
    gInspecteurCalquesMode.set (pNom ? "categorie" : null);
    return true;
  }

// Sensibilité du zoom à la molette (persistée en localStorage).
// 1.0 → facteur 1.15 par cran (valeur par défaut historique).
const CLE_LS_SENSIBILITE_ZOOM = "cogivoice_hub_sensibilite_zoom";

function lit_sensibilite_zoom ()
  {
    try
      {
        const v = parseFloat (localStorage.getItem (CLE_LS_SENSIBILITE_ZOOM));
        if (isFinite (v) && v >= 0.01 && v <= 2) return v;
      }
    catch (e) {}
    return 0.1;
  }

const gSensibiliteZoom = new ReactiveVar (lit_sensibilite_zoom ());

function ecrit_sensibilite_zoom (pVal)
  {
    let v = parseFloat (pVal);
    if (!isFinite (v)) v = 0.1;
    v = Math.max (0.01, Math.min (2, v));
    try { localStorage.setItem (CLE_LS_SENSIBILITE_ZOOM, String (v)); } catch (e) {}
    gSensibiliteZoom.set (v);
  }

// Switch « zoom sur le curseur » : si vrai, la molette zoome vers le
// pointeur ; sinon vers le centre de la scène. Persiste en localStorage.
const CLE_LS_ZOOM_SUR_CURSEUR = "cogivoice_hub_zoom_sur_curseur";

function lit_zoom_sur_curseur ()
  {
    try
      {
        const v = localStorage.getItem (CLE_LS_ZOOM_SUR_CURSEUR);
        if (v === null) return true;          // valeur par défaut : actif
        return v === "1" || v === "true";
      }
    catch (e) { return true; }
  }

const gZoomSurCurseur = new ReactiveVar (lit_zoom_sur_curseur ());

function ecrit_zoom_sur_curseur (pVal)
  {
    const v = !!pVal;
    try { localStorage.setItem (CLE_LS_ZOOM_SUR_CURSEUR, v ? "1" : "0"); } catch (e) {}
    gZoomSurCurseur.set (v);
  }

// État courant du pan/zoom exposé aux helpers (mis à jour par
// applique_vb dans attache_interactions_svg).
const gPanX  = new ReactiveVar (0);
const gPanY  = new ReactiveVar (0);
const gZoom  = new ReactiveVar (1);

// Message de feedback du bouton « Adopter ces coordonnées ».
const gVueMessage = new ReactiveVar (null);  // {texte, type}
if (edition_hub_active ())
  auto_hide_success_message_reactive (gVueMessage);

// ---------------------------------------------------------------------------
// Colonne droite : largeur + onglet actif (persistés en localStorage)
// ---------------------------------------------------------------------------

const ONGLETS_DROITE = [
  {cle : "calques", libelle : "Calques"},
  {cle : "voix",    libelle : "Voix"},
  {cle : "scene",   libelle : "Scène"},
  {cle : "projet",  libelle : "Projet"},
];

const CLE_LS_LARGEUR_COL_DROITE = "cogivoice_hub_largeur_col_droite";
const CLE_LS_LARGEUR_COL_DROITE_PAR_PROJET = "cogivoice_hub_largeur_col_droite_by_project";
const CLE_LS_ONGLET_DROITE      = "cogivoice_hub_onglet_droite";
const CLE_LS_INSPECTEUR_SECTIONS_REPLIABLES_PAR_PROJET = "cogivoice_hub_inspecteur_sections_repliables_by_project";
const CLE_LS_VISIBILITE_INSPECTEUR_PAR_PROJET = "cogivoice_hub_inspecteur_visibility_by_project";
const LARGEUR_MIN_COL_DROITE    = 180;
const LARGEUR_MAX_COL_DROITE    = 1400;
const LARGEUR_DEF_COL_DROITE    = 320;
const CLE_LS_AIDE_SCENE_OPEN_PAR_PROJET = "cogivoice_hub_scene_help_open_by_project";
const CLE_LS_AIDE_SCENE_BOX_PAR_PROJET  = "cogivoice_hub_scene_help_box_by_project";
const AIDE_SCENE_BOX_MARGIN = 12;
const AIDE_SCENE_BOX_MIN_W = 360;
const AIDE_SCENE_BOX_MIN_H = 170;
const AIDE_SCENE_BOX_PREF_W = 520;
const AIDE_SCENE_BOX_PREF_H = 210;

function projet_id_pour_preferences_scene ()
  {
    const tProjetId = Session.get ("projet_id");
    if (typeof tProjetId === "string" && tProjetId) return tProjetId;
    const tSceneId = Session.get ("scene_id");
    const tScene = tSceneId ? Scenes.findOne (tSceneId, {fields : {projet_id : 1}}) : null;
    return (tScene && typeof tScene.projet_id === "string" && tScene.projet_id) ? tScene.projet_id : null;
  }

function normalise_largeur_col_droite (pVal)
  {
    const tVal = parseInt (pVal, 10);
    if (!isFinite (tVal)) return null;
    if (tVal < LARGEUR_MIN_COL_DROITE || tVal > LARGEUR_MAX_COL_DROITE) return null;
    return tVal;
  }

function lit_largeur_col_droite (pProjetId = projet_id_pour_preferences_scene ())
  {
    if (pProjetId)
      {
        const tMap = lit_map_localstorage_scene (CLE_LS_LARGEUR_COL_DROITE_PAR_PROJET);
        const tValProjet = normalise_largeur_col_droite (tMap[pProjetId]);
        if (tValProjet !== null) return tValProjet;
      }

    try
      {
        const tValLegacy = normalise_largeur_col_droite (localStorage.getItem (CLE_LS_LARGEUR_COL_DROITE));
        if (tValLegacy !== null) return tValLegacy;
      }
    catch (e) {}
    return LARGEUR_DEF_COL_DROITE;
  }

function lit_onglet_droite ()
  {
    try
      {
        const v = localStorage.getItem (CLE_LS_ONGLET_DROITE);
        if (v && ONGLETS_DROITE.some ((o) => o.cle === v)) return v;
      }
    catch (e) {}
    return "calques";
  }

const gLargeurColDroite = new ReactiveVar (lit_largeur_col_droite ());
const gOngletDroite     = new ReactiveVar (lit_onglet_droite ());
const gVisibiliteInspecteurProjet = new ReactiveVar (mode_edition_lecture_prefs_defaut (true, false));
const gSceneHelpOpen = new ReactiveVar (false);

function normalise_visibilite_mode_edition_lecture (pValeur, pEditionDefaut = true, pLectureDefaut = false)
  {
    const tDefaut = mode_edition_lecture_prefs_defaut (pEditionDefaut, pLectureDefaut);
    const tSource = (pValeur && typeof pValeur === "object" && !Array.isArray (pValeur))
      ? pValeur
      : {};

    return {
      edition : typeof tSource.edition === "boolean" ? tSource.edition : tDefaut.edition,
      lecture : typeof tSource.lecture === "boolean" ? tSource.lecture : tDefaut.lecture,
    };
  }

function lit_visibilite_inspecteur_projet (pProjetId = projet_id_pour_preferences_scene ())
  {
    if (!pProjetId) return mode_edition_lecture_prefs_defaut (true, false);
    const tMap = lit_map_localstorage_scene (CLE_LS_VISIBILITE_INSPECTEUR_PAR_PROJET);
    return normalise_visibilite_mode_edition_lecture (tMap[pProjetId], true, false);
  }

function charge_visibilite_inspecteur_projet (pProjetId = projet_id_pour_preferences_scene ())
  {
    const tPrefs = lit_visibilite_inspecteur_projet (pProjetId);
    gVisibiliteInspecteurProjet.set (tPrefs);
    return tPrefs;
  }

function ecrit_visibilite_inspecteur_projet (pPatch, pProjetId = projet_id_pour_preferences_scene ())
  {
    const tPrefs = normalise_visibilite_mode_edition_lecture ({
      ...gVisibiliteInspecteurProjet.get (),
      ...pPatch,
    }, true, false);

    gVisibiliteInspecteurProjet.set (tPrefs);
    if (pProjetId)
      ecrit_map_localstorage_scene (CLE_LS_VISIBILITE_INSPECTEUR_PAR_PROJET, {[pProjetId] : tPrefs});
    return tPrefs;
  }

function lit_scene_help_open_projet (pProjetId = projet_id_pour_preferences_scene ())
  {
    if (!pProjetId) return false;
    const tMap = lit_map_localstorage_scene (CLE_LS_AIDE_SCENE_OPEN_PAR_PROJET);
    return !!tMap[pProjetId];
  }

function charge_scene_help_open_projet (pProjetId = projet_id_pour_preferences_scene ())
  {
    const tOpen = lit_scene_help_open_projet (pProjetId);
    gSceneHelpOpen.set (tOpen);
    return tOpen;
  }

function ecrit_scene_help_open_projet (pOpen, pProjetId = projet_id_pour_preferences_scene ())
  {
    const tOpen = !!pOpen;
    gSceneHelpOpen.set (tOpen);
    if (pProjetId)
      ecrit_map_localstorage_scene (CLE_LS_AIDE_SCENE_OPEN_PAR_PROJET, {[pProjetId] : tOpen});
    return tOpen;
  }

function lit_scene_help_box_projet (pProjetId = projet_id_pour_preferences_scene ())
  {
    if (!pProjetId) return null;
    const tMap = lit_map_localstorage_scene (CLE_LS_AIDE_SCENE_BOX_PAR_PROJET);
    const tBox = tMap[pProjetId];
    return (tBox && typeof tBox === "object") ? tBox : null;
  }

function ecrit_scene_help_box_projet (pBox, pProjetId = projet_id_pour_preferences_scene ())
  {
    if (!pProjetId || !pBox) return;
    ecrit_map_localstorage_scene (CLE_LS_AIDE_SCENE_BOX_PAR_PROJET, {[pProjetId] : pBox});
  }

function scene_help_header_safe_top ()
  {
    if (typeof document === "undefined") return AIDE_SCENE_BOX_MARGIN;
    const tHeader = document.querySelector (".app-header");
    if (!tHeader) return AIDE_SCENE_BOX_MARGIN;
    const tRect = tHeader.getBoundingClientRect ();
    return Math.max (AIDE_SCENE_BOX_MARGIN, Math.ceil (tRect.bottom) + AIDE_SCENE_BOX_MARGIN);
  }

const gSceneHelpCogibulle = cree_fenetre_cogibulle (
  {
    selector            : ".js_scene_help_card",
    storage             : {
      stackId   : "cogivoice_hub_scene_help_pad",
      readBox   : () => lit_scene_help_box_projet (),
      writeBox  : (pBox) => ecrit_scene_help_box_projet (pBox),
      readOpen  : () => lit_scene_help_open_projet (),
      writeOpen : (pOpen) => ecrit_scene_help_open_projet (pOpen),
    },
    isOpen              : () => gSceneHelpOpen.get (),
    setOpen             : (pOpen) => gSceneHelpOpen.set (!!pOpen),
    minWidth            : AIDE_SCENE_BOX_MIN_W,
    minHeight           : AIDE_SCENE_BOX_MIN_H,
    preferredWidth      : AIDE_SCENE_BOX_PREF_W,
    preferredHeight     : AIDE_SCENE_BOX_PREF_H,
    widthViewportRatio  : 0.45,
    heightViewportRatio : 0.33,
    margin              : AIDE_SCENE_BOX_MARGIN,
    safeTop             : scene_help_header_safe_top,
  });

function scene_help_open_dialog (pOptions = {})
  {
    return gSceneHelpCogibulle.open (pOptions);
  }

function scene_help_close_dialog ()
  {
    return gSceneHelpCogibulle.close ();
  }

function scene_help_toggle_dialog ()
  {
    if (gSceneHelpOpen.get ()) return scene_help_close_dialog ();
    return scene_help_open_dialog ();
  }

export function inspecteur_scene_est_visible ()
  {
    const tPrefs = normalise_visibilite_mode_edition_lecture (gVisibiliteInspecteurProjet.get (), true, false);
    return mode_lecture_scene_actif () ? (tPrefs.edition && tPrefs.lecture) : tPrefs.edition;
  }

export function bascule_visibilite_inspecteur_scene ()
  {
    if (!projet_id_pour_preferences_scene ()) return false;
    const tVisible = inspecteur_scene_est_visible ();
    if (mode_lecture_scene_actif ())
      ecrit_visibilite_inspecteur_projet (!tVisible
        ? {edition : true, lecture : true}
        : {lecture : false});
    else
      ecrit_visibilite_inspecteur_projet ({edition : !tVisible});
    return true;
  }

function normalise_cle_section_repliable_inspecteur (pCle)
  {
    const tCle = typeof pCle === "string" ? pCle.trim ().toLowerCase () : "";
    return /^[a-z0-9_]+$/.test (tCle) ? tCle : "";
  }

function normalise_etats_sections_repliables_inspecteur (pValeur)
  {
    const tSource = (pValeur && typeof pValeur === "object" && !Array.isArray (pValeur))
      ? pValeur
      : {};
    const tEtats = {};

    Object.entries (tSource).forEach (([pCle, pOuverte]) =>
      {
        const tCle = normalise_cle_section_repliable_inspecteur (pCle);
        if (!tCle || typeof pOuverte !== "boolean") return;
        tEtats[tCle] = pOuverte;
      });

    return tEtats;
  }

function lit_etats_sections_repliables_inspecteur (pProjetId = projet_id_pour_preferences_scene ())
  {
    if (!pProjetId) return {};
    const tMap = lit_map_localstorage_scene (CLE_LS_INSPECTEUR_SECTIONS_REPLIABLES_PAR_PROJET);
    return normalise_etats_sections_repliables_inspecteur (tMap[pProjetId]);
  }

function charge_etats_sections_repliables_inspecteur (pProjetId = projet_id_pour_preferences_scene ())
  {
    const tEtats = lit_etats_sections_repliables_inspecteur (pProjetId);
    gInspecteurSectionsRepliables.set (tEtats);
    return tEtats;
  }

function etat_par_defaut_section_repliable_inspecteur (pEtatParDefaut)
  {
    if (typeof pEtatParDefaut === "boolean") return pEtatParDefaut;
    return pEtatParDefaut !== "fermee";
  }

function inspecteur_section_repliable_est_ouverte (pCle, pEtatParDefaut = "ouverte")
  {
    const tCle = normalise_cle_section_repliable_inspecteur (pCle);
    if (!tCle) return etat_par_defaut_section_repliable_inspecteur (pEtatParDefaut);

    const tEtats = gInspecteurSectionsRepliables.get () || {};
    if (Object.prototype.hasOwnProperty.call (tEtats, tCle))
      return !!tEtats[tCle];

    return etat_par_defaut_section_repliable_inspecteur (pEtatParDefaut);
  }

function set_etat_section_repliable_inspecteur (pCle, pOuverte, pProjetId = projet_id_pour_preferences_scene ())
  {
    const tCle = normalise_cle_section_repliable_inspecteur (pCle);
    if (!tCle) return;

    const tEtatsSuivants = {
      ...normalise_etats_sections_repliables_inspecteur (gInspecteurSectionsRepliables.get ()),
      [tCle] : !!pOuverte,
    };

    gInspecteurSectionsRepliables.set (tEtatsSuivants);

    if (!pProjetId) return;

    ecrit_map_localstorage_scene (
      CLE_LS_INSPECTEUR_SECTIONS_REPLIABLES_PAR_PROJET,
      {
        [pProjetId] : tEtatsSuivants,
      }
    );
  }

function bascule_section_repliable_inspecteur (pCle, pEtatParDefaut = "ouverte", pProjetId = projet_id_pour_preferences_scene ())
  {
    const tOuverte = inspecteur_section_repliable_est_ouverte (pCle, pEtatParDefaut);
    set_etat_section_repliable_inspecteur (pCle, !tOuverte, pProjetId);
  }

function bouton_inspecteur_est_actionnable (pElement)
  {
    if (!pElement) return false;
    if (pElement.disabled) return false;
    if (pElement.getAttribute && pElement.getAttribute ("aria-disabled") === "true") return false;
    if (pElement.classList && pElement.classList.contains ("disabled")) return false;
    return true;
  }

function marque_bouton_inspecteur_presse (pElement)
  {
    if (!bouton_inspecteur_est_actionnable (pElement)) return;
    const tMaintenant = Date.now ();
    if (pElement._inspecteurBoutonPresseDernier && tMaintenant - pElement._inspecteurBoutonPresseDernier < 80) return;
    pElement._inspecteurBoutonPresseDernier = tMaintenant;
    if (pElement._inspecteurBoutonPresseTimer)
      clearTimeout (pElement._inspecteurBoutonPresseTimer);
    pElement.classList.add ("inspecteur_bouton_presse");
    pElement._inspecteurBoutonPresseTimer = setTimeout (() =>
      {
        pElement.classList.remove ("inspecteur_bouton_presse");
        pElement._inspecteurBoutonPresseTimer = null;
      }, 260);
    const tInline = {
      transform : pElement.style.getPropertyValue ("transform"),
      transformPriority : pElement.style.getPropertyPriority ("transform"),
      filter : pElement.style.getPropertyValue ("filter"),
      filterPriority : pElement.style.getPropertyPriority ("filter"),
      boxShadow : pElement.style.getPropertyValue ("box-shadow"),
      boxShadowPriority : pElement.style.getPropertyPriority ("box-shadow"),
      outline : pElement.style.getPropertyValue ("outline"),
      outlinePriority : pElement.style.getPropertyPriority ("outline"),
      backgroundColor : pElement.style.getPropertyValue ("background-color"),
      backgroundColorPriority : pElement.style.getPropertyPriority ("background-color"),
    };
    pElement.style.setProperty ("transform", "translateY(2px) scale(0.92)", "important");
    pElement.style.setProperty ("filter", "brightness(0.68) saturate(1.2)", "important");
    pElement.style.setProperty ("box-shadow", "inset 0 2px 8px rgba(0, 0, 0, 0.42), 0 0 0 2px rgba(88, 166, 255, 0.72)", "important");
    pElement.style.setProperty ("outline", "1px solid rgba(88, 166, 255, 0.92)", "important");
    pElement.style.setProperty ("background-color", "rgba(17, 133, 200, 0.35)", "important");
    setTimeout (() =>
      {
        pElement.style.setProperty ("transform", tInline.transform, tInline.transformPriority);
        pElement.style.setProperty ("filter", tInline.filter, tInline.filterPriority);
        pElement.style.setProperty ("box-shadow", tInline.boxShadow, tInline.boxShadowPriority);
        pElement.style.setProperty ("outline", tInline.outline, tInline.outlinePriority);
        pElement.style.setProperty ("background-color", tInline.backgroundColor, tInline.backgroundColorPriority);
      }, 240);
  }

function bouton_inspecteur_depuis_evenement (pEvent)
  {
    const tCible = pEvent && pEvent.target;
    if (!tCible || typeof tCible.closest !== "function") return null;
    const tBouton = tCible.closest (".btn");
    if (!tBouton || !tBouton.closest || !tBouton.closest (".colonne_droite_scene")) return null;
    return tBouton;
  }

function infos_section_repliable_inspecteur (pCle, pEtatParDefaut = "ouverte")
  {
    const tCle = normalise_cle_section_repliable_inspecteur (pCle);
    if (!tCle) return null;

    const tOuverteParDefaut = etat_par_defaut_section_repliable_inspecteur (pEtatParDefaut);
    const tOuverte = inspecteur_section_repliable_est_ouverte (tCle, tOuverteParDefaut);

    return {
      cle : tCle,
      ouverte : tOuverte,
      ariaExpanded : tOuverte ? "true" : "false",
      replieeClass : tOuverte ? "" : "est_repliee",
      contentId : `inspecteur_section_${tCle}`,
      defaultOpenAttr : tOuverteParDefaut ? "1" : "0",
    };
  }

function ecrit_largeur_col_droite (pVal, pProjetId = projet_id_pour_preferences_scene ())
  {
    let v = parseInt (pVal, 10);
    if (!isFinite (v)) v = LARGEUR_DEF_COL_DROITE;
    v = Math.max (LARGEUR_MIN_COL_DROITE, Math.min (LARGEUR_MAX_COL_DROITE, v));
    if (pProjetId)
      ecrit_map_localstorage_scene (CLE_LS_LARGEUR_COL_DROITE_PAR_PROJET, {[pProjetId] : v});
    else
      {
        try { localStorage.setItem (CLE_LS_LARGEUR_COL_DROITE, String (v)); } catch (e) {}
      }
    gLargeurColDroite.set (v);
  }

function ecrit_onglet_droite (pCle)
  {
    if (!ONGLETS_DROITE.some ((o) => o.cle === pCle)) return;
    try { localStorage.setItem (CLE_LS_ONGLET_DROITE, pCle); } catch (e) {}
    gOngletDroite.set (pCle);
  }

const CARTE_MODE_LISTE = "liste";
const CARTE_MODE_EDITEUR = "editeur";
const CARTE_MASK_ATTR = "data-cogi-carte-masked";
const CARTE_MASK_PREV_DISPLAY_ATTR = "data-cogi-carte-prev-display";
const CARTE_MASK_PREV_OPACITY_ATTR = "data-cogi-carte-prev-opacity";
const CARTE_MASK_PREV_VISIBLE_OPACITY_ATTR = "data-cogi-carte-prev-visible-opacity";
const CARTE_MASK_PREV_TRANSITION_ATTR = "data-cogi-carte-prev-transition";
const CARTE_MASK_PREV_POINTER_EVENTS_ATTR = "data-cogi-carte-prev-pointer-events";
const CARTE_MASK_STATE_ATTR = "data-cogi-carte-mask-state";
const CARTE_COULEUR_FOND_DEFAUT = "#ffffff";
const CARTE_OPACITE_BULLE_DEFAUT = 0.6;
const CARTE_TAILLE_BULLE_DEFAUT = 72;
const CARTE_OPACITE_BULLE_CUSTOM_DEFAUT = 1;
const CARTE_TAILLE_BULLE_CUSTOM_DEFAUT = REGLAGES_BULLES_PROJET_DEFAUTS.taille_bulle_custom_defaut;
const CARTE_TAILLE_BULLE_MIN = REGLAGES_BULLES_PROJET_DEFAUTS.taille_bulle_min;
const CARTE_TAILLE_BULLE_MAX = REGLAGES_BULLES_PROJET_DEFAUTS.taille_bulle_max;
const CARTE_ROTATION_BULLE_DEFAUT = 0;
const CARTE_ROTATION_BULLE_MIN = -180;
const CARTE_ROTATION_BULLE_MAX = 180;
const CARTE_CREATION_LARGEUR_DEFAUT = 400;
const CARTE_CREATION_HAUTEUR_DEFAUT = 300;
const CARTE_DECOR_FOND_CLASS = "cogi-carte-decor-fond";
const CARTE_DECOR_MASQUE_CLASS = "cogi-carte-focus-mask";
const CARTE_DECOR_MASQUE_SELECTION_VIEW_ATTR = "data-cogi-selection-view";
const CARTE_DECOR_BULLES_LAYER_CLASS = "cogi-cartes-bulles-layer";
const CARTE_DECOR_BULLE_CLASS = "cogi-carte-bulle";
const CARTE_DECOR_BULLE_VISUAL_CLASS = "cogi-carte-bulle-visual";
const CARTE_DECOR_BULLE_HIT_CLASS = "cogi-carte-bulle-hit";
const CARTE_ZOOM_ALTERNATIF_GROUP_CLASS = "cogi-carte-zoom-alt-group";
const CARTE_ZOOM_ALTERNATIF_MASK_CLASS = "cogi-carte-zoom-alt-mask";
const CARTE_ZOOM_ALTERNATIF_FRAME_CLASS = "cogi-carte-zoom-alt-frame";
const CARTE_ZOOM_ALTERNATIF_FRAME_RELIEF_CLASS = "cogi-carte-zoom-alt-frame-relief";
const CARTE_ZOOM_ALTERNATIF_ASSETS_CLASS = "cogi-carte-zoom-alt-assets";
const CARTE_ZOOM_ALTERNATIF_ROLE_ATTR = "data-cogi-zoom-alt-role";
const CARTE_ZOOM_ALTERNATIF_RELECTURE_ROLE = "carte_relecture_audio";
const CARTE_ZOOM_ALTERNATIF_ASSET_SIZE_PX = 56;
const CARTE_ZOOM_ALTERNATIF_ASSET_GAP_PX = 12;
const CARTE_ZOOM_ALTERNATIF_ASSET_OFFSET_PX = 18;
const CARTE_ZOOM_ALTERNATIF_REFERENCE_PX = 1000;
let gZoomAlternatifReliefMaskSeq = 0;
const LECTURE_MASK_FADE_CLASS = "cogi-lecture-focus-mask-fade";
const LECTURE_BULLES_FADE_CLASS = "cogi-lecture-bulles-fade";
const LECTURE_CAMERA_TRANSITION_CLASS = "transition_camera_lecture";
const LECTURE_CAMERA_TRANSITION_ACTIVE_CLASS = "transition_camera_lecture_active";
const LECTURE_CAMERA_TRANSITION_MASK_CLASS = "masque_transition_camera_lecture";
const LECTURE_CAMERA_TRANSITION_MASK_PADDING_PX = 4;
const LECTURE_CAMERA_TRANSITION_EASING_CSS = "cubic-bezier(0.65, 0, 0.35, 1)";
const CARTE_DECOR_MASQUE_OVERSCAN_FACTOR = 4;
const LECTURE_BULLES_FLOTTEMENT_FREQ_MIN = 0.09;
const LECTURE_BULLES_FLOTTEMENT_FREQ_MAX = 0.32;

// Durée de remise en vitesse de l'horloge du flottement après une pause (reprise sans à-coup),
// et pas de temps maximal pris en compte (onglet resté masqué, frame très longue).
const LECTURE_BULLES_FLOTTEMENT_REPRISE_MS = 400;
const LECTURE_BULLES_FLOTTEMENT_PAS_MAX_MS = 100;

function fabrique_easing_cubic_bezier_lecture_scene (pX1, pY1, pX2, pY2)
  {
    const tCx = 3 * pX1;
    const tBx = 3 * (pX2 - pX1) - tCx;
    const tAx = 1 - tCx - tBx;
    const tCy = 3 * pY1;
    const tBy = 3 * (pY2 - pY1) - tCy;
    const tAy = 1 - tCy - tBy;

    const sampleX = (pT) => ((tAx * pT + tBx) * pT + tCx) * pT;
    const sampleY = (pT) => ((tAy * pT + tBy) * pT + tCy) * pT;
    const sampleDerivativeX = (pT) => (3 * tAx * pT + 2 * tBx) * pT + tCx;

    return (pX) =>
      {
        const tX = Math.max (0, Math.min (1, Number (pX) || 0));
        if (tX <= 0 || tX >= 1) return tX;

        let tGuess = tX;
        for (let i = 0; i < 6; i += 1)
          {
            const tErreur = sampleX (tGuess) - tX;
            const tDerivee = sampleDerivativeX (tGuess);
            if (Math.abs (tErreur) < 0.000001 || Math.abs (tDerivee) < 0.000001) break;
            tGuess -= tErreur / tDerivee;
            tGuess = Math.max (0, Math.min (1, tGuess));
          }

        return sampleY (tGuess);
      };
  }

const lecture_ease_camera_transition =
  fabrique_easing_cubic_bezier_lecture_scene (0.65, 0, 0.35, 1);

function ratio_flottement_seed_bulle (pSeed, pCle)
  {
    const tHash = hash_chaine_carte_client (`${pSeed || "bulle"}|${pCle || "k"}`);
    return (tHash % 10007) / 10007;
  }

function fabrique_etat_flottement_bulle_lecture_client (pBulle, pReglages)
  {
    const tReglages = pReglages || REGLAGES_BULLES_PROJET_DEFAUTS;
    const tCarteId = (pBulle && pBulle.carte_id) || `bulle-${Date.now ()}-${Math.random ()}`;
    const tFreq = (pCle) =>
      LECTURE_BULLES_FLOTTEMENT_FREQ_MIN
      + (ratio_flottement_seed_bulle (tCarteId, pCle) * (LECTURE_BULLES_FLOTTEMENT_FREQ_MAX - LECTURE_BULLES_FLOTTEMENT_FREQ_MIN));
    const tPhase = (pCle) => ratio_flottement_seed_bulle (tCarteId, pCle) * Math.PI * 2;
    const tVitDep = Math.max (0, Number (tReglages.lecture_flottement_vitesse_deplacement) || 0);
    const tVitTaille = Math.max (0, Number (tReglages.lecture_flottement_vitesse_taille) || 0);
    const tVitRotation = Math.max (0, Number (tReglages.lecture_flottement_vitesse_rotation) || 0);
    const tAmpX = Math.max (0, Number (tReglages.lecture_flottement_deplacement_max_x) || 0);
    const tAmpY = Math.max (0, Number (tReglages.lecture_flottement_deplacement_max_y) || 0);
    const tScaleMinPct = Number (tReglages.lecture_flottement_taille_min_pct);
    const tScaleMaxPct = Number (tReglages.lecture_flottement_taille_max_pct);
    const tScaleMin = Math.max (0.1, Math.min (3, (Number.isFinite (tScaleMinPct) ? tScaleMinPct : 100) / 100));
    const tScaleMax = Math.max (tScaleMin, Math.min (3, (Number.isFinite (tScaleMaxPct) ? tScaleMaxPct : 100) / 100));
    const tRotMinPct = Number (tReglages.lecture_flottement_rotation_min_pct);
    const tRotMaxPct = Number (tReglages.lecture_flottement_rotation_max_pct);
    const tRotMinFactor = (Number.isFinite (tRotMinPct) ? tRotMinPct : 100) / 100;
    const tRotMaxFactor = Math.max (tRotMinFactor, (Number.isFinite (tRotMaxPct) ? tRotMaxPct : 100) / 100);

    return {
      carte_id : tCarteId,
      base_x : Number (pBulle && pBulle.x) || 0,
      base_y : Number (pBulle && pBulle.y) || 0,
      base_rotation : Number (pBulle && pBulle.rotation) || 0,
      amp_x : tAmpX * (0.72 + (ratio_flottement_seed_bulle (tCarteId, "ax") * 0.65)),
      amp_y : tAmpY * (0.72 + (ratio_flottement_seed_bulle (tCarteId, "ay") * 0.65)),
      freq_x : tFreq ("fx") * tVitDep,
      freq_y : tFreq ("fy") * tVitDep,
      phase_x : tPhase ("px"),
      phase_y : tPhase ("py"),
      freq_rot : tFreq ("fr") * 0.9 * tVitRotation,
      phase_rot : tPhase ("pr"),
      freq_scale : tFreq ("fs") * 1.1 * tVitTaille,
      phase_scale : tPhase ("ps"),
      freq_scale_2 : tFreq ("fs2") * 1.8 * tVitTaille,
      phase_scale_2 : tPhase ("ps2"),
      scale_min : tScaleMin,
      scale_max : tScaleMax,
      rot_factor_min : tRotMinFactor,
      rot_factor_max : tRotMaxFactor,
    };
  }

const gCarteSelectionnee = new ReactiveVar (null);
const gCarteMode = new ReactiveVar (CARTE_MODE_LISTE);
const gCarteBrouillon = new ReactiveVar (null);
const gCarteAdoptCurrentSuggested = new ReactiveVar (false);
const gCartePreviewNoticeArmed = new ReactiveVar (false);
const gCarteArbreSelectionne = new ReactiveVar (null);
const gCarteArbreCategorieSelectionnee = new ReactiveVar (null);
const gCarteCategoriesOuvertes = new ReactiveDict ();

const gCarteObjReorder =
  {
    container : null,
    tpl : null,
    ignoreClickUntil : 0,
    active : false,
    sourceIds : null,
    dragId : null,
    pointerId : null,
    item : null,
    placeholder : null,
    helper : null,
    startX : 0,
    startY : 0,
    offsetX : 0,
    offsetY : 0,
    moveHandler : null,
    endHandler : null,
  };

const gCalquesCartesReorder =
  {
    sortable : null,
    container : null,
    tpl : null,
    ignoreClickUntil : 0,
  };

const gCategoriesCalqueReorder =
  {
    container : null,
    tpl : null,
    sceneId : null,
    ignoreClickUntil : 0,
    active : false,
    ordreSource : null,
    dragCategorie : null,
    pointerId : null,
    item : null,
    placeholder : null,
    helper : null,
    startX : 0,
    startY : 0,
    offsetX : 0,
    offsetY : 0,
    moveHandler : null,
    endHandler : null,
  };

function cartes_scene_liste ()
  {
    const tSceneId = Session.get ("scene_id");
    if (!tSceneId) return [];
    return Cartes.find ({scene_id : tSceneId}, {sort : {ordre : 1}}).fetch ();
  }

function normalise_vue_carte_client (pVue, pFallback = null)
  {
    const tFallback = pFallback && typeof pFallback === "object" ? pFallback : null;
    const tVue = pVue && typeof pVue === "object" ? pVue : null;
    if (!tVue) return tFallback ? {...tFallback} : null;
    const tX = Number (tVue.x);
    const tY = Number (tVue.y);
    const tW = Number (tVue.w);
    const tH = Number (tVue.h);
    if (![tX, tY, tW, tH].every (Number.isFinite) || tW <= 0 || tH <= 0)
      return tFallback ? {...tFallback} : null;
    return {
      x : Math.round (tX * 1000) / 1000,
      y : Math.round (tY * 1000) / 1000,
      w : Math.round (tW * 1000) / 1000,
      h : Math.round (tH * 1000) / 1000,
    };
  }

function normalise_ids_masques_carte_client (pIds)
  {
    if (!Array.isArray (pIds)) return [];
    return [...new Set (
      pIds
        .filter ((pId) => typeof pId === "string")
        .map ((pId) => pId.trim ())
        .filter (Boolean)
    )];
  }

function signature_ids_masques_carte_client (pIds)
  {
    return normalise_ids_masques_carte_client (pIds).slice ().sort ().join ("\n");
  }

function normalise_ordre_objets_carte_client (pIds)
  {
    return normalise_ids_masques_carte_client (pIds);
  }

function signature_ordre_objets_carte_client (pIds)
  {
    return normalise_ordre_objets_carte_client (pIds).join ("\n");
  }

function normalise_couleur_carte_client (pVal, pFallback = CARTE_COULEUR_FOND_DEFAUT)
  {
    return normalise_couleur_style_interface (pVal, pFallback);
  }

function normalise_opacite_bulle_carte_client (pVal, pFallback = CARTE_OPACITE_BULLE_DEFAUT)
  {
    const tVal = Number (pVal);
    if (!Number.isFinite (tVal)) return pFallback;
    return Math.max (0, Math.min (1, Math.round (tVal * 1000) / 1000));
  }

function bornes_taille_bulle_projet_client (pProjet = null)
  {
    const tReglages = reglages_bulles_projet_courant (pProjet || projet_courant_scene ());
    const tMinBrut = Number (tReglages && tReglages.taille_bulle_min);
    const tMaxBrut = Number (tReglages && tReglages.taille_bulle_max);
    const tMin = Number.isFinite (tMinBrut) ? tMinBrut : CARTE_TAILLE_BULLE_MIN;
    const tMax = Number.isFinite (tMaxBrut) ? tMaxBrut : CARTE_TAILLE_BULLE_MAX;
    return {
      min : Math.min (tMin, tMax),
      max : Math.max (tMin, tMax),
    };
  }

function normalise_taille_bulle_carte_client (pVal, pFallback = CARTE_TAILLE_BULLE_DEFAUT, pOpt = {})
  {
    const tBornes = bornes_taille_bulle_projet_client (pOpt && pOpt.projet);
    const tFallback = Number (pFallback);
    const tFallbackNormalise = Number.isFinite (tFallback)
      ? Math.max (tBornes.min, Math.min (tBornes.max, Math.round (tFallback * 1000) / 1000))
      : CARTE_TAILLE_BULLE_DEFAUT;
    const tVal = Number (pVal);
    if (!Number.isFinite (tVal)) return tFallbackNormalise;
    return Math.max (
      tBornes.min,
      Math.min (tBornes.max, Math.round (tVal * 1000) / 1000)
    );
  }

function normalise_rotation_bulle_carte_client (pVal, pFallback = CARTE_ROTATION_BULLE_DEFAUT)
  {
    const tFallback = Number (pFallback);
    const tFallbackNormalise = Number.isFinite (tFallback)
      ? Math.max (CARTE_ROTATION_BULLE_MIN, Math.min (CARTE_ROTATION_BULLE_MAX, Math.round (tFallback * 1000) / 1000))
      : CARTE_ROTATION_BULLE_DEFAUT;
    const tVal = Number (pVal);
    if (!Number.isFinite (tVal)) return tFallbackNormalise;
    return Math.max (
      CARTE_ROTATION_BULLE_MIN,
      Math.min (CARTE_ROTATION_BULLE_MAX, Math.round (tVal * 1000) / 1000)
    );
  }

function normalise_position_bulle_carte_client (pPosition, pFallback = null)
  {
    const tFallback = pFallback && typeof pFallback === "object"
      ? normalise_position_bulle_carte_client (pFallback, null)
      : null;
    const tPosition = pPosition && typeof pPosition === "object" ? pPosition : null;
    if (!tPosition) return tFallback;

    const tX = Number (tPosition.x);
    const tY = Number (tPosition.y);
    if (![tX, tY].every (Number.isFinite)) return tFallback;

    return {
      x : Math.round (tX * 1000) / 1000,
      y : Math.round (tY * 1000) / 1000,
    };
  }

function signature_position_bulle_carte_client (pPosition)
  {
    const tPosition = normalise_position_bulle_carte_client (pPosition);
    return tPosition ? `${tPosition.x}:${tPosition.y}` : "";
  }

function hash_chaine_carte_client (pTexte)
  {
    const tTexte = typeof pTexte === "string" ? pTexte : String (pTexte || "");
    let tHash = 0;
    for (let tI = 0; tI < tTexte.length; tI += 1)
      {
        tHash = ((tHash << 5) - tHash) + tTexte.charCodeAt (tI);
        tHash |= 0;
      }
    return Math.abs (tHash);
  }

function hsl_vers_hex_carte_client (pHue, pSat, pLight)
  {
    const tHue = ((Number (pHue) % 360) + 360) % 360;
    const tSat = Math.max (0, Math.min (100, Number (pSat))) / 100;
    const tLight = Math.max (0, Math.min (100, Number (pLight))) / 100;
    const tA = tSat * Math.min (tLight, 1 - tLight);
    const tCanal = (pN) =>
      {
        const tK = (pN + tHue / 30) % 12;
        const tColor = tLight - tA * Math.max (Math.min (tK - 3, 9 - tK, 1), -1);
        return Math.round (255 * tColor).toString (16).padStart (2, "0");
      };
    return `#${tCanal (0)}${tCanal (8)}${tCanal (4)}`;
  }

function couleur_bulle_depuis_seed_carte_client (pSeed)
  {
    const tHue = hash_chaine_carte_client (pSeed || "carte") % 360;
    return hsl_vers_hex_carte_client (tHue, 72, 74);
  }

function genere_couleur_bulle_aleatoire_client ()
  {
    return couleur_bulle_depuis_seed_carte_client (`${Date.now ()}-${Math.random ()}`);
  }

function couleur_fond_carte_effective_client (pCarteLike)
  {
    return normalise_couleur_carte_client (
      pCarteLike && pCarteLike.couleur_fond,
      CARTE_COULEUR_FOND_DEFAUT
    );
  }

function compare_cartes_bulles_scene_client (pA, pB)
  {
    const tOrdreA = Number (pA && pA.ordre);
    const tOrdreB = Number (pB && pB.ordre);
    const tHasOrdreA = Number.isFinite (tOrdreA);
    const tHasOrdreB = Number.isFinite (tOrdreB);
    if (tHasOrdreA && tHasOrdreB && tOrdreA !== tOrdreB) return tOrdreA - tOrdreB;
    if (tHasOrdreA !== tHasOrdreB) return tHasOrdreA ? -1 : 1;
    const tIdA = String ((pA && (pA._id || pA.carte_id || pA.nom)) || "");
    const tIdB = String ((pB && (pB._id || pB.carte_id || pB.nom)) || "");
    return tIdA.localeCompare (tIdB, "fr", {numeric : true, sensitivity : "base"});
  }

function incremente_compteur_bulles_scene_client (pMap, pCle)
  {
    if (!pMap || !pCle) return;
    pMap.set (pCle, (pMap.get (pCle) || 0) + 1);
  }

function choisit_option_bulles_scene_client (pCandidats, pCompteurs, pSeed)
  {
    if (!Array.isArray (pCandidats) || !pCandidats.length) return null;
    const tOptions = pCandidats.filter (Boolean);
    if (!tOptions.length) return null;

    return tOptions.slice ().sort ((pA, pB) =>
      {
        const tCountA = pCompteurs && typeof pCompteurs.get === "function"
          ? (pCompteurs.get (pA) || 0)
          : 0;
        const tCountB = pCompteurs && typeof pCompteurs.get === "function"
          ? (pCompteurs.get (pB) || 0)
          : 0;
        if (tCountA !== tCountB) return tCountA - tCountB;
        const tHashA = hash_chaine_carte_client (`${pSeed || "bulle"}|${pA}`);
        const tHashB = hash_chaine_carte_client (`${pSeed || "bulle"}|${pB}`);
        if (tHashA !== tHashB) return tHashA - tHashB;
        return String (pA).localeCompare (String (pB), "fr", {numeric : true, sensitivity : "base"});
      })[0] || null;
  }

function planifie_affectations_bulles_projet_scene_client (pCartes, pOpt = {})
  {
    const tFichiers = documents_svg_bulles_projet_courant (pOpt.projet);
    const tFichiersParId = new Map (tFichiers.map ((pFichier) => [pFichier._id, pFichier]));
    const tIdsDisponibles = tFichiers.map ((pFichier) => pFichier && pFichier._id).filter (Boolean);
    const tCouleursDisponibles = reglages_bulles_projet_courant (pOpt.projet).couleurs_auto.slice ();
    const tAssignations = new Map ();

    if (!tIdsDisponibles.length)
      {
        return {
          hasCustomFiles : false,
          fichiers : tFichiers,
          fichiersParId : tFichiersParId,
          couleurs : tCouleursDisponibles,
          assignations : tAssignations,
        };
      }

    const tCompteursFichiers = new Map (tIdsDisponibles.map ((pId) => [pId, 0]));
    const tCompteursCombos = new Map ();
    const tSeedSalt = pOpt.seedSalt ? `|${pOpt.seedSalt}` : "";
    const tCartesTriees = (Array.isArray (pCartes) ? pCartes.slice () : [])
      .filter (Boolean)
      .sort (compare_cartes_bulles_scene_client);

    tCartesTriees.forEach ((pCarte) =>
      {
        const tCarteId = pCarte && (pCarte._id || pCarte.carte_id);
        const tSvgBulleId = normalise_svg_bulle_id_carte_client (
          pCarte && pCarte.svg_bulle_id,
          tIdsDisponibles
        );
        if (!tCarteId || !tSvgBulleId) return;

        const tCouleur = tCouleursDisponibles.length
          ? normalise_couleur_auto_bulle_projet_client (pCarte && pCarte.couleur_bulle, tCouleursDisponibles)
          : null;
        const tAssignation = {
          mode : "custom",
          svg_bulle_id : tSvgBulleId,
          couleur_bulle : tCouleur,
          explicite : true,
          auto_generee : false,
        };
        tAssignations.set (tCarteId, tAssignation);
        incremente_compteur_bulles_scene_client (tCompteursFichiers, tSvgBulleId);
        if (tCouleur)
          incremente_compteur_bulles_scene_client (tCompteursCombos, `${tSvgBulleId}|${tCouleur}`);
      });

    tCartesTriees.forEach ((pCarte, pIndex) =>
      {
        const tCarteId = pCarte && (pCarte._id || pCarte.carte_id);
        if (!tCarteId) return;

        const tExistante = tAssignations.get (tCarteId);
        if (tExistante)
          {
            if (tCouleursDisponibles.length && !tExistante.couleur_bulle)
              {
                const tCombo = choisit_option_bulles_scene_client (
                  tCouleursDisponibles.map ((pCouleur) => `${tExistante.svg_bulle_id}|${pCouleur}`),
                  tCompteursCombos,
                  `${tCarteId}|combo|${pIndex}${tSeedSalt}`
                );
                if (tCombo)
                  {
                    incremente_compteur_bulles_scene_client (tCompteursCombos, tCombo);
                    tExistante.couleur_bulle = tCombo.split ("|")[1] || null;
                  }
              }
            return;
          }

        if (!pOpt.autogenLegacy) return;

        const tSvgBulleId = choisit_option_bulles_scene_client (
          tIdsDisponibles,
          tCompteursFichiers,
          `${tCarteId}|file|${pIndex}${tSeedSalt}`
        );
        if (!tSvgBulleId) return;
        incremente_compteur_bulles_scene_client (tCompteursFichiers, tSvgBulleId);

        let tCouleur = null;
        if (tCouleursDisponibles.length)
          {
            const tCombo = choisit_option_bulles_scene_client (
              tCouleursDisponibles.map ((pItem) => `${tSvgBulleId}|${pItem}`),
              tCompteursCombos,
              `${tCarteId}|combo|${pIndex}${tSeedSalt}`
            );
            if (tCombo)
              {
                incremente_compteur_bulles_scene_client (tCompteursCombos, tCombo);
                tCouleur = tCombo.split ("|")[1] || null;
              }
          }

        tAssignations.set (tCarteId,
          {
            mode : "custom",
            svg_bulle_id : tSvgBulleId,
            couleur_bulle : tCouleur,
            explicite : false,
            auto_generee : true,
          });
      });

    return {
      hasCustomFiles : true,
      fichiers : tFichiers,
      fichiersParId : tFichiersParId,
      couleurs : tCouleursDisponibles,
      assignations : tAssignations,
    };
  }

function configuration_bulle_carte_effective_client (pCarteLike, pOpt = {})
  {
    const tCarteId = pCarteLike && (pCarteLike._id || pCarteLike.carte_id) || null;
    const tPlan = pOpt.planBulles || null;
    const tAssignation = tPlan && tCarteId ? (tPlan.assignations.get (tCarteId) || null) : null;
    if (tAssignation && tAssignation.mode === "custom")
      {
        return {
          mode : "custom",
          svg_bulle_id : tAssignation.svg_bulle_id,
          couleur_bulle : tAssignation.couleur_bulle || null,
          auto_generee : !!tAssignation.auto_generee,
        };
      }

    const tIdsDisponibles = tPlan
      ? Array.from (tPlan.fichiersParId.keys ())
      : ids_svg_bulles_projet_courant (pOpt.projet);
    const tSvgBulleId = normalise_svg_bulle_id_carte_client (
      pCarteLike && pCarteLike.svg_bulle_id,
      tIdsDisponibles
    );
    if (tSvgBulleId)
      {
        const tCouleursDisponibles = tPlan
          ? tPlan.couleurs
          : reglages_bulles_projet_courant (pOpt.projet).couleurs_auto;
        return {
          mode : "custom",
          svg_bulle_id : tSvgBulleId,
          couleur_bulle : tCouleursDisponibles.length
            ? normalise_couleur_auto_bulle_projet_client (
              pCarteLike && pCarteLike.couleur_bulle,
              tCouleursDisponibles
            )
            : null,
          auto_generee : false,
        };
      }

    const tSeed = pOpt.seed
      || (pCarteLike && (pCarteLike._id || pCarteLike.carte_id || pCarteLike.nom))
      || "carte";
    return {
      mode : "default",
      svg_bulle_id : null,
      couleur_bulle : normalise_couleur_style_interface (
        pCarteLike && pCarteLike.couleur_bulle,
        couleur_bulle_depuis_seed_carte_client (tSeed)
      ),
      auto_generee : false,
    };
  }

function couleur_bulle_carte_effective_client (pCarteLike, pSeed = null)
  {
    return configuration_bulle_carte_effective_client (pCarteLike, {seed : pSeed}).couleur_bulle;
  }

function couleur_fond_bulle_carte_effective_client (pCarteLike)
  {
    const tCouleur = pCarteLike && pCarteLike.couleur_fond;
    if (tCouleur && typeof tCouleur === "string" && tCouleur.match (/^#[0-9a-f]{6}$/i))
      return tCouleur;
    return null;
  }

function opacite_bulle_carte_fallback_client (pCarteLike, pOpt = {})
  {
    const tConfig = pOpt.configuration || configuration_bulle_carte_effective_client (pCarteLike, pOpt);
    return tConfig && tConfig.mode === "custom"
      ? CARTE_OPACITE_BULLE_CUSTOM_DEFAUT
      : CARTE_OPACITE_BULLE_DEFAUT;
  }

function taille_bulle_carte_fallback_client (pCarteLike, pOpt = {})
  {
    const tConfig = pOpt.configuration || configuration_bulle_carte_effective_client (pCarteLike, pOpt);
    const tReglages = reglages_bulles_projet_courant (pOpt && pOpt.projet);
    return tConfig && tConfig.mode === "custom"
      ? Number (tReglages && tReglages.taille_bulle_custom_defaut) || CARTE_TAILLE_BULLE_CUSTOM_DEFAUT
      : CARTE_TAILLE_BULLE_DEFAUT;
  }

function plage_rotation_bulle_creation_client (pProjet = null)
  {
    const tReglages = reglages_bulles_projet_courant (pProjet || projet_courant_scene ());
    const tMinBrut = Number (tReglages && tReglages.rotation_bulle_min);
    const tMaxBrut = Number (tReglages && tReglages.rotation_bulle_max);
    const tMin = Number.isFinite (tMinBrut) ? tMinBrut : REGLAGES_BULLES_PROJET_DEFAUTS.rotation_bulle_min;
    const tMax = Number.isFinite (tMaxBrut) ? tMaxBrut : REGLAGES_BULLES_PROJET_DEFAUTS.rotation_bulle_max;
    return {
      min : normalise_rotation_bulle_carte_client (Math.min (tMin, tMax)),
      max : normalise_rotation_bulle_carte_client (Math.max (tMin, tMax)),
    };
  }

function rotation_bulle_depuis_seed_client (pSeed, pProjet = null)
  {
    const tPlage = plage_rotation_bulle_creation_client (pProjet);
    if (Math.abs (tPlage.max - tPlage.min) < 0.0001) return tPlage.min;
    const tHash = hash_chaine_carte_client (`${pSeed || "bulle"}|rotation`);
    const tRatio = (tHash % 1000000) / 1000000;
    return normalise_rotation_bulle_carte_client (
      tPlage.min + (tRatio * (tPlage.max - tPlage.min)),
      tPlage.min
    );
  }

function rotation_bulle_carte_effective_client (pCarteLike, pOpt = {})
  {
    const tConfig = pOpt.configuration || configuration_bulle_carte_effective_client (pCarteLike, pOpt);
    if (tConfig && tConfig.auto_generee)
      {
        const tSeedBase = pOpt.seed
          || (pCarteLike && (pCarteLike._id || pCarteLike.carte_id || pCarteLike.nom))
          || "bulle";
        return rotation_bulle_depuis_seed_client (
          `${tSeedBase}|${tConfig.svg_bulle_id || ""}|${tConfig.couleur_bulle || ""}`,
          pOpt && pOpt.projet
        );
      }

    return normalise_rotation_bulle_carte_client (
      pCarteLike && pCarteLike.rotation_bulle,
      CARTE_ROTATION_BULLE_DEFAUT
    );
  }

function rotation_bulle_aleatoire_creation_client (pProjet = null)
  {
    const tPlage = plage_rotation_bulle_creation_client (pProjet);
    if (Math.abs (tPlage.max - tPlage.min) < 0.0001) return tPlage.min;
    return normalise_rotation_bulle_carte_client (
      tPlage.min + (Math.random () * (tPlage.max - tPlage.min)),
      tPlage.min
    );
  }

async function recree_bulles_scene_courante_depuis_reglages_projet_client ()
  {
    const tSceneId = Session.get ("scene_id");
    const tProjet = projet_courant_scene ();
    const tCartes = cartes_scene_liste ().filter ((pCarte) => pCarte && pCarte._id);
    if (!tSceneId || !tCartes.length) return {updatedCount : 0};
    const tReglages = reglages_bulles_projet_courant (tProjet);
    const tTailleBulle = normalise_taille_bulle_carte_client (
      Number (tReglages && tReglages.taille_bulle_custom_defaut),
      CARTE_TAILLE_BULLE_CUSTOM_DEFAUT,
      {projet : tProjet}
    );

    const tSeedSalt = `regen-${Date.now ()}-${Math.random ()}`;
    const tCartesReroll = tCartes.map ((pCarte) =>
      ({
        ...pCarte,
        svg_bulle_id : null,
        couleur_bulle : null,
      }));
    const tPlanBulles = planifie_affectations_bulles_projet_scene_client (
      tCartesReroll,
      {
        autogenLegacy : true,
        seedSalt : tSeedSalt,
        projet : tProjet,
      }
    );

    let tUpdatedCount = 0;
    for (const tCarte of tCartes)
      {
        const tConfig = configuration_bulle_carte_effective_client (
          tCarte,
          {
            planBulles : tPlanBulles,
            seed : `${tCarte._id}|${tSeedSalt}`,
            projet : tProjet,
          }
        );
        const tOpacite = opacite_bulle_carte_fallback_client (
          tCarte,
          {configuration : tConfig, planBulles : tPlanBulles, projet : tProjet}
        );
        const tRotation = rotation_bulle_aleatoire_creation_client (tProjet);
        const tModeCustom = tConfig && tConfig.mode === "custom";
        const tCouleurBulle = tModeCustom
          ? (tConfig.couleur_bulle || null)
          : genere_couleur_bulle_aleatoire_client ();
        const tSvgBulleId = tModeCustom
          ? (tConfig.svg_bulle_id || null)
          : null;

        const tPayload = {
          carte_id : tCarte._id,
          couleur_bulle : tCouleurBulle,
          svg_bulle_id : tSvgBulleId,
          opacite_bulle : tOpacite,
          taille_bulle : tTailleBulle,
          rotation_bulle : tRotation,
        };

        await meteor_call_scene_promise ("maj_carte_scene", tPayload);
        tUpdatedCount += 1;
      }

    return {updatedCount : tUpdatedCount};
  }

async function recree_contours_scene_courante_depuis_reglages_categorie_client (pCategorie)
  {
    if (!contour_categorie_supportee (pCategorie)) return {updatedCount : 0};
    const tSceneId = Session.get ("scene_id");
    const tScene = tSceneId ? Scenes.findOne (tSceneId) : null;
    const tObjets = svg_objets_scene_normalises (tScene);
    const tObjetsCategorie = Array.isArray (tObjets && tObjets[pCategorie])
      ? tObjets[pCategorie].filter ((pObjet) => pObjet && pObjet.id)
      : [];
    if (!tSceneId || !tObjetsCategorie.length) return {updatedCount : 0};

    const tContourCategorie = reglages_survol_contour_categorie_brouillon (pCategorie);
    let tUpdatedCount = 0;

    for (const tObjet of tObjetsCategorie)
      {
        await meteor_call_scene_promise (
          "maj_objet_scene",
          {
            scene_id : tSceneId,
            objet_id : tObjet.id,
            survol_contour : tContourCategorie,
          }
        );
        tUpdatedCount += 1;
      }

    return {updatedCount : tUpdatedCount};
  }

function opacite_bulle_carte_effective_client (pCarteLike, pOpt = {})
  {
    const tConfig = pOpt.configuration || configuration_bulle_carte_effective_client (pCarteLike, pOpt);
    if (tConfig && tConfig.auto_generee)
      return CARTE_OPACITE_BULLE_CUSTOM_DEFAUT;

    return normalise_opacite_bulle_carte_client (
      pCarteLike && pCarteLike.opacite_bulle,
      opacite_bulle_carte_fallback_client (pCarteLike, {...pOpt, configuration : tConfig})
    );
  }

function taille_bulle_carte_effective_client (pCarteLike, pOpt = {})
  {
    const tConfig = pOpt.configuration || configuration_bulle_carte_effective_client (pCarteLike, pOpt);
    if (tConfig && tConfig.auto_generee)
      {
        const tReglages = reglages_bulles_projet_courant (pOpt && pOpt.projet);
        return Number (tReglages && tReglages.taille_bulle_custom_defaut) || CARTE_TAILLE_BULLE_CUSTOM_DEFAUT;
      }

    return normalise_taille_bulle_carte_client (
      pCarteLike && pCarteLike.taille_bulle,
      taille_bulle_carte_fallback_client (pCarteLike, {...pOpt, configuration : tConfig}),
      pOpt
    );
  }

function vue_svg_racine_client (pRoot)
  {
    if (!pRoot || typeof pRoot.getAttribute !== "function") return null;
    const tViewBox = pRoot.getAttribute ("viewBox");
    if (tViewBox)
      {
        const tParties = tViewBox.split (/[\s,]+/).map (parseFloat);
        return normalise_vue_carte_client (
          {x : tParties[0], y : tParties[1], w : tParties[2], h : tParties[3]}
        );
      }
    return normalise_vue_carte_client (
      {
        x : 0,
        y : 0,
        w : parseFloat (pRoot.getAttribute ("width")) || 0,
        h : parseFloat (pRoot.getAttribute ("height")) || 0,
      }
    );
  }

function rayon_bulle_carte_effectif_client (pCarteLike, pVue = null, pOpt = {})
  {
    const tTaille = taille_bulle_carte_effective_client (pCarteLike, pOpt);
    const tRayon = tTaille / 2;
    const tVue = normalise_vue_carte_client (pVue || (pCarteLike && pCarteLike.vue));
    if (!tVue) return tRayon;
    return Math.max (
      1,
      Math.min (tRayon, tVue.w / 2, tVue.h / 2)
    );
  }

function position_bulle_carte_effective_client (pCarteLike, pVue = null, pOpt = {})
  {
    const tVue = normalise_vue_carte_client (pVue || (pCarteLike && pCarteLike.vue));
    if (!tVue) return null;

    const tRayon = rayon_bulle_carte_effectif_client (pCarteLike, tVue, pOpt);
    const tPosition = normalise_position_bulle_carte_client (pCarteLike && pCarteLike.position_bulle);
    const tMinX = tVue.x + Math.min (tRayon, tVue.w / 2);
    const tMaxX = tVue.x + Math.max (tVue.w - tRayon, tVue.w / 2);
    const tMinY = tVue.y + Math.min (tRayon, tVue.h / 2);
    const tMaxY = tVue.y + Math.max (tVue.h - tRayon, tVue.h / 2);
    const tCentre = {
      x : tVue.x + (tVue.w / 2),
      y : tVue.y + (tVue.h / 2),
    };
    const tSource = tPosition || tCentre;

    return {
      x : Math.round (Math.max (tMinX, Math.min (tMaxX, tSource.x)) * 1000) / 1000,
      y : Math.round (Math.max (tMinY, Math.min (tMaxY, tSource.y)) * 1000) / 1000,
    };
  }

function normalise_position_bulle_carte_pour_vue_client (pPosition, pVue)
  {
    const tPosition = normalise_position_bulle_carte_client (pPosition);
    if (!tPosition) return null;
    return position_bulle_carte_effective_client ({position_bulle : tPosition, vue : pVue}, pVue);
  }

function position_bulle_carte_initiale_client (pVue, pTaille = CARTE_TAILLE_BULLE_DEFAUT)
  {
    const tVue = normalise_vue_carte_client (pVue);
    if (!tVue) return null;
    return position_bulle_carte_effective_client (
      {
        vue : tVue,
        taille_bulle : normalise_taille_bulle_carte_client (pTaille, CARTE_TAILLE_BULLE_DEFAUT),
        position_bulle : null,
      },
      tVue
    );
  }

function fusionne_carte_avec_brouillon_client (pCarte, pBrouillon = brouillon_carte_courant ())
  {
    if (!pCarte) return null;
    if (!pBrouillon || pBrouillon.carte_id !== pCarte._id) return pCarte;
    return {
      ...pCarte,
      nom : pBrouillon.nom,
      vue : pBrouillon.vue,
      ids_masques : pBrouillon.ids_masques,
      ordre_objets : pBrouillon.ordre_objets,
      couleur_fond : pBrouillon.couleur_fond,
      couleur_bulle : pBrouillon.couleur_bulle,
      svg_bulle_id : pBrouillon.svg_bulle_id,
      opacite_bulle : pBrouillon.opacite_bulle,
      taille_bulle : pBrouillon.taille_bulle,
      rotation_bulle : pBrouillon.rotation_bulle,
      position_bulle : pBrouillon.position_bulle,
    };
  }

function fusionne_carte_avec_brouillon_bulle_client (pCarte, pBrouillonBulle = brouillon_bulle_courant ())
  {
    if (!pCarte) return null;
    if (!pBrouillonBulle || pBrouillonBulle.carte_id !== pCarte._id) return pCarte;
    return {
      ...pCarte,
      nom : pBrouillonBulle.nom,
      vue : pBrouillonBulle.vue,
      ids_masques : Object.prototype.hasOwnProperty.call (pBrouillonBulle, "ids_masques")
        ? pBrouillonBulle.ids_masques
        : pCarte.ids_masques,
      ordre_objets : Object.prototype.hasOwnProperty.call (pBrouillonBulle, "ordre_objets")
        ? pBrouillonBulle.ordre_objets
        : pCarte.ordre_objets,
      couleur_fond : Object.prototype.hasOwnProperty.call (pBrouillonBulle, "couleur_fond")
        ? pBrouillonBulle.couleur_fond
        : pCarte.couleur_fond,
      couleur_bulle : pBrouillonBulle.couleur_bulle,
      svg_bulle_id : Object.prototype.hasOwnProperty.call (pBrouillonBulle, "svg_bulle_id")
        ? pBrouillonBulle.svg_bulle_id
        : pCarte.svg_bulle_id,
      opacite_bulle : pBrouillonBulle.opacite_bulle,
      taille_bulle : pBrouillonBulle.taille_bulle,
      rotation_bulle : pBrouillonBulle.rotation_bulle,
      position_bulle : pBrouillonBulle.position_bulle,
    };
  }

function signature_decoration_carte_client (pCarteLike, pOpt = {})
  {
    const tVue = normalise_vue_carte_client (pCarteLike && pCarteLike.vue);
    const tConfigBulle = configuration_bulle_carte_effective_client (pCarteLike, pOpt);
    return [
      (pCarteLike && (pCarteLike._id || pCarteLike.carte_id)) || "",
      tVue ? `${tVue.x}:${tVue.y}:${tVue.w}:${tVue.h}` : "",
      couleur_fond_carte_effective_client (pCarteLike),
      tConfigBulle.mode,
      tConfigBulle.svg_bulle_id || "",
      tConfigBulle.couleur_bulle || "",
      opacite_bulle_carte_effective_client (pCarteLike, {...pOpt, configuration : tConfigBulle}),
      taille_bulle_carte_effective_client (pCarteLike, {...pOpt, configuration : tConfigBulle}),
      rotation_bulle_carte_effective_client (pCarteLike, {...pOpt, configuration : tConfigBulle}),
      signature_position_bulle_carte_client (pCarteLike && pCarteLike.position_bulle),
      String (gBullesProjetSvgCacheRevision.get ()),
    ].join ("|");
  }

function carte_courante_scene ()
  {
    const tCarteId = gCarteSelectionnee.get ();
    if (!tCarteId) return null;
    return Cartes.findOne (tCarteId) || null;
  }

function nom_affiche_carte (pCarte, pIndex)
  {
    const tNom = typeof pCarte?.nom === "string" ? pCarte.nom.trim () : "";
    if (tNom) return tNom;
    return `Carte ${pIndex}`;
  }

function url_vignette_scene_client (pScene)
  {
    if (!pScene || !pScene.vignette_id) return null;
    const tFichier = HubFiles.findOne ({_id : pScene.vignette_id});
    if (!tFichier) return null;
    try { return tFichier.link (undefined, window.location.origin); }
    catch (e) { return tFichier.link (); }
  }

function libelle_carte_scene (pCarteId)
  {
    if (!pCarteId) return "";
    const tCartes = cartes_scene_liste ();
    const tIndex = tCartes.findIndex ((pCarte) => pCarte && pCarte._id === pCarteId);
    if (tIndex < 0) return "";
    return nom_affiche_carte (carte_source_pour_bulle_scene (pCarteId) || tCartes[tIndex], tIndex + 1);
  }

function titre_carte_pour_actions (pCarteId, pFallback = "cette carte")
  {
    const tBrouillon = brouillon_bulle_courant ();
    if (tBrouillon && tBrouillon.carte_id === pCarteId)
      {
        const tNom = typeof tBrouillon.nom === "string" ? tBrouillon.nom.trim () : "";
        if (tNom) return tNom;
      }
    return libelle_carte_scene (pCarteId) || pFallback;
  }

function demande_suppression_carte_scene (pCarteId, pTitre = "")
  {
    const tId = typeof pCarteId === "string" ? pCarteId.trim () : "";
    if (!tId) return false;
    const tTitre = (typeof pTitre === "string" ? pTitre.trim () : "") || "cette carte";
    if (!confirm (`Supprimer ${tTitre} ?\n\nCette action supprimera aussi sa vignette.`)) return false;
    Meteor.call ("supprime_carte_scene", tId,
      (pErr) =>
        {
          if (pErr) alert ("Erreur : " + (pErr.reason || pErr.message));
        });
    return true;
  }

function carte_source_pour_bulle_scene (pCarteId)
  {
    const tCarte = pCarteId ? Cartes.findOne (pCarteId) : null;
    if (!tCarte) return null;
    return fusionne_carte_avec_brouillon_bulle_client (
      fusionne_carte_avec_brouillon_client (tCarte),
      brouillon_bulle_courant ()
    );
  }

function carte_bulle_selectionnee_scene ()
  {
    const tCarteId = gBulleSelectionnee.get ();
    return tCarteId ? carte_source_pour_bulle_scene (tCarteId) : null;
  }

function cartes_scene_liste_pour_plan_bulles_client (pOpt = {})
  {
    const tBrouillonCarte = Object.prototype.hasOwnProperty.call (pOpt, "brouillonCarte")
      ? pOpt.brouillonCarte
      : brouillon_carte_courant ();
    const tBrouillonBulle = Object.prototype.hasOwnProperty.call (pOpt, "brouillonBulle")
      ? pOpt.brouillonBulle
      : brouillon_bulle_courant ();
    const tOverrides = new Map ();

    if (Array.isArray (pOpt.cartesOverride))
      {
        pOpt.cartesOverride.filter (Boolean).forEach ((pCarte) =>
          {
            const tId = pCarte && (pCarte._id || pCarte.carte_id);
            if (tId) tOverrides.set (tId, pCarte);
          });
      }

    const tCartes = cartes_scene_liste ().map ((pCarte) =>
      {
        const tOverride = tOverrides.get (pCarte._id) || null;
        return fusionne_carte_avec_brouillon_bulle_client (
          fusionne_carte_avec_brouillon_client (tOverride || pCarte, tBrouillonCarte),
          tBrouillonBulle
        );
      });

    if (pOpt.carteSupplementaire)
      tCartes.push (pOpt.carteSupplementaire);

    return tCartes;
  }

function plan_bulles_projet_scene_editee_client (pOpt = {})
  {
    return planifie_affectations_bulles_projet_scene_client (
      cartes_scene_liste_pour_plan_bulles_client (pOpt),
      {
        autogenLegacy : pOpt.autogenLegacy !== false,
        projet : pOpt.projet,
      }
    );
  }

function configuration_bulle_inspecteur_courant_client ()
  {
    const tCarteId = gBulleSelectionnee.get ();
    if (!tCarteId) return null;

    const tCartes = cartes_scene_liste_pour_plan_bulles_client ();
    const tSource = tCartes.find ((pCarte) => pCarte && pCarte._id === tCarteId)
      || carte_source_pour_bulle_scene (tCarteId)
      || {_id : tCarteId, carte_id : tCarteId};
    const tPlanBulles = planifie_affectations_bulles_projet_scene_client (tCartes, {autogenLegacy : true});

    return {
      source : tSource,
      planBulles : tPlanBulles,
      config : configuration_bulle_carte_effective_client (
        tSource,
        {planBulles : tPlanBulles, seed : tCarteId}
      ),
    };
  }

function couleur_auto_bulle_inspecteur_par_defaut_client (pCouleurs, pCouleurCourante = null)
  {
    const tCouleurs = Array.isArray (pCouleurs)
      ? pCouleurs
        .map ((pCouleur) => normalise_couleur_auto_bulle_projet_client (pCouleur))
        .filter (Boolean)
      : [];
    if (tCouleurs.length === 0) return null;

    const tCourante = normalise_couleur_auto_bulle_projet_client (pCouleurCourante);
    return tCourante && tCouleurs.includes (tCourante) ? tCourante : tCouleurs[0];
  }

function valeur_couleur_bulle_inspecteur_client ()
  {
    const tInfo = configuration_bulle_inspecteur_courant_client ();
    return couleur_auto_bulle_inspecteur_par_defaut_client (
      reglages_bulles_projet_courant ().couleurs_auto,
      tInfo && tInfo.config && tInfo.config.couleur_bulle
    );
  }

function menus_locaux_inspecteur_bulle_ouverts_client ()
  {
    return gInspecteurBulleSvgPickerOuvert.get () || gInspecteurBulleCouleurPickerOuvert.get ();
  }

function ferme_menus_locaux_inspecteur_bulle_client ()
  {
    gInspecteurBulleSvgPickerOuvert.set (false);
    gInspecteurBulleCouleurPickerOuvert.set (false);
  }

function mode_lecture_scene_actif ()
  {
    return gLectureSceneActive.get ();
  }

function carte_focus_lecture_scene ()
  {
    const tCarteId = gLectureSceneFocusCarte.get ();
    return tCarteId ? carte_source_pour_bulle_scene (tCarteId) : null;
  }

function arrete_audio_lecture_scene (pOpt = {})
  {
    if (!pOpt.interne) arrete_morphing_lecture_scene (gSceneTemplateActif, {garderAudio : true});
    if (!pOpt.garderInstant) arrete_audio_instant_lecture_scene ();
    const tAudio = gLectureSceneAudio;
    const tFin = gLectureSceneAudioFin;
    if (!pOpt.interne) gLectureSceneSequenceAudioId += 1;
    gLectureSceneAudio = null;
    gLectureSceneAudioFin = null;
    if (tAudio)
      {
        try { tAudio.pause (); } catch (e) {}
        try { tAudio.currentTime = 0; } catch (e) {}
      }
    if (typeof tFin === "function")
      { try { tFin (false); } catch (e) {} }
  }

function arrete_ambiance_lecture_scene ()
  {
    const tAudio = gLectureSceneAmbianceAudio;
    gLectureSceneAmbianceAudio = null;
    gLectureSceneAmbianceFileId = null;
    gLectureSceneAmbianceEdition = false;
    gLectureSceneAmbianceEtat.set (LECTURE_AMBIANCE_ETAT_VIDE);
    if (!tAudio) return;
    try { tAudio.pause (); } catch (e) {}
    try { tAudio.currentTime = 0; } catch (e) {}
  }

function url_audio_lecture_scene_pour_file_id (pFileId)
  {
    const tFileId = typeof pFileId === "string" ? pFileId.trim () : "";
    if (!tFileId) return null;
    const tFichier = HubFiles.findOne ({_id : tFileId});
    return url_client_hubfile (tFichier);
  }

function lecteur_audio_instant_lecture_scene ()
  {
    if (!gLectureSceneAudioInstant) gLectureSceneAudioInstant = create_lecture_audio_player ();
    return gLectureSceneAudioInstant;
  }

function lecteur_retour_menu_instant_lecture_scene ()
  {
    if (!gLectureSceneRetourMenuInstant) gLectureSceneRetourMenuInstant = create_lecture_audio_player ();
    return gLectureSceneRetourMenuInstant;
  }

function prepare_audio_instant_file_id_lecture_scene (pFileId)
  {
    const tUrl = url_audio_lecture_scene_pour_file_id (pFileId);
    if (tUrl) lecteur_audio_instant_lecture_scene ().prepare (tUrl);
  }

function prepare_audios_interface_lecture_scene ()
  {
    [
      "son_clic",
      "son_appareil_photo",
      "son_retour_menu",
      "son_reussite",
      "son_echec",
    ].forEach ((pCle) =>
      {
        const tFileId = file_id_audio_projet_lecture_scene (pCle);
        prepare_audio_instant_file_id_lecture_scene (tFileId);
        if (pCle === "son_retour_menu")
          {
            const tUrl = url_audio_lecture_scene_pour_file_id (tFileId);
            if (tUrl) lecteur_retour_menu_instant_lecture_scene ().prepare (tUrl);
          }
      });
  }

function signature_audios_interface_lecture_scene ()
  {
    return [
      "son_clic",
      "son_appareil_photo",
      "son_retour_menu",
      "son_reussite",
      "son_echec",
    ].map ((pCle) =>
      {
        const tFileId = file_id_audio_projet_lecture_scene (pCle);
        const tFichier = tFileId ? HubFiles.findOne ({_id : tFileId}) : null;
        return `${pCle}:${tFileId || ""}:${(tFichier && (tFichier.updatedAt || tFichier.url || tFichier.path || tFichier._id)) || ""}`;
      }).join ("|");
  }

export function deverrouille_audio_instant_lecture_scene ()
  {
    lecteur_audio_instant_lecture_scene ().unlock ();
    lecteur_retour_menu_instant_lecture_scene ().unlock ();
  }

function joue_audio_instant_file_id_lecture_scene (pFileId, pVolumePct = 100, pOptions = {})
  {
    const tUrl = url_audio_lecture_scene_pour_file_id (pFileId);
    if (!tUrl) return false;
    const tPlayer = pOptions && pOptions.retourMenu
      ? lecteur_retour_menu_instant_lecture_scene ()
      : lecteur_audio_instant_lecture_scene ();
    tPlayer.play (
      tUrl,
      {volume : volume_audio_webaudio_lecture_scene_pour_pct (pVolumePct) / 100}
    ).catch (() => {});
    return true;
  }

function arrete_audio_instant_lecture_scene ()
  {
    if (gLectureSceneAudioInstant) gLectureSceneAudioInstant.stop ();
    if (gLectureSceneRetourMenuInstant) gLectureSceneRetourMenuInstant.stop ();
  }

function dispose_audio_instant_lecture_scene ()
  {
    if (gLectureSceneAudioInstant) gLectureSceneAudioInstant.dispose ();
    if (gLectureSceneRetourMenuInstant) gLectureSceneRetourMenuInstant.dispose ();
    gLectureSceneAudioInstant = null;
    gLectureSceneRetourMenuInstant = null;
  }

function applique_volume_audio_element_lecture_scene (pAudio, pVolumePct)
  {
    if (!pAudio) return;
    pAudio.volume = volume_audio_html_lecture_scene_pour_pct (pVolumePct) / 100;
  }

function volume_audio_general_lecture_scene_pct ()
  {
    return normalise_volume_audio_pct_client (
      reglages_mode_lecture_scene ().audio_general_volume_pct
    );
  }

function volume_audio_html_lecture_scene_pct ()
  {
    return volume_audio_general_lecture_scene_pct ();
  }

function volume_audio_webaudio_lecture_scene_pct ()
  {
    return volume_audio_general_lecture_scene_pct ();
  }

function volume_audio_html_lecture_scene_pour_pct (pVolumePct = 100)
  {
    return Math.round (
      normalise_volume_audio_pct_client (pVolumePct)
      * volume_audio_html_lecture_scene_pct ()
      / 100
    );
  }

function volume_audio_webaudio_lecture_scene_pour_pct (pVolumePct = 100)
  {
    return Math.round (
      normalise_volume_audio_pct_client (pVolumePct)
      * volume_audio_webaudio_lecture_scene_pct ()
      / 100
    );
  }

function volume_voix_lecture_scene_pct ()
  {
    return normalise_volume_audio_pct_client (
      reglages_mode_lecture_scene ().voix_volume_pct
    );
  }

function volume_voix_lecture_scene_pour_pct (pVolumePct = 100)
  {
    return Math.round (
      normalise_volume_audio_pct_client (pVolumePct)
      * volume_voix_lecture_scene_pct ()
      / 100
    );
  }

function marque_audio_voix_lecture_scene (pAudio, pVolumePct = 100)
  {
    if (!pAudio) return null;
    pAudio._cogibulleVoixVolumeBasePct = normalise_volume_audio_pct_client (pVolumePct);
    applique_volume_audio_element_lecture_scene (
      pAudio,
      volume_voix_lecture_scene_pour_pct (pAudio._cogibulleVoixVolumeBasePct)
    );
    return pAudio;
  }

function cree_audio_voix_lecture_scene_depuis_file_id (pFileId, pVolumePct = 100)
  {
    return marque_audio_voix_lecture_scene (
      cree_audio_lecture_scene_depuis_file_id (
        pFileId,
        volume_voix_lecture_scene_pour_pct (pVolumePct)
      ),
      pVolumePct
    );
  }

function joue_audio_voix_lecture_scene (pFileId, pVolumePct = 100)
  {
    const tAudio = cree_audio_voix_lecture_scene_depuis_file_id (pFileId, pVolumePct);
    if (!tAudio) return false;
    joue_audio_lecture_scene_prepare_promesse (tAudio);
    return true;
  }

function joue_audio_lecture_scene_promesse (pFileId, pVolumePct = 100)
  {
    const tUrl = url_audio_lecture_scene_pour_file_id (pFileId);
    if (!tUrl) return Promise.resolve (false);

    arrete_audio_lecture_scene ({interne : true});
    return new Promise ((resolve) =>
      {
        let tTermine = false;
        const tAudio = new Audio (tUrl);
        const termine = (pOk) =>
          {
            if (tTermine) return;
            tTermine = true;
            if (gLectureSceneAudio === tAudio) gLectureSceneAudio = null;
            if (gLectureSceneAudioFin === termine) gLectureSceneAudioFin = null;
            resolve (!!pOk);
          };

        tAudio.preload = "auto";
        applique_volume_audio_element_lecture_scene (tAudio, pVolumePct);
        gLectureSceneAudio = tAudio;
        gLectureSceneAudioFin = termine;
        tAudio.addEventListener ("ended", () => termine (true), {once : true});
        tAudio.addEventListener ("error", () => termine (false), {once : true});
        const tPlay = tAudio.play ();
        if (tPlay && typeof tPlay.catch === "function")
          tPlay.catch (() => termine (false));
      });
  }

function joue_audio_lecture_scene (pFileId, pVolumePct = 100)
  {
    const tUrl = url_audio_lecture_scene_pour_file_id (pFileId);
    if (!tUrl) return false;

    arrete_audio_lecture_scene ();
    const tAudio = new Audio (tUrl);
    tAudio.preload = "auto";
    applique_volume_audio_element_lecture_scene (tAudio, pVolumePct);
    gLectureSceneAudio = tAudio;
    tAudio.addEventListener ("ended", () =>
      {
        if (gLectureSceneAudio === tAudio) gLectureSceneAudio = null;
      }, {once : true});
    tAudio.addEventListener ("error", () =>
      {
        if (gLectureSceneAudio === tAudio) gLectureSceneAudio = null;
      }, {once : true});
    const tPlay = tAudio.play ();
    if (tPlay && typeof tPlay.catch === "function")
      tPlay.catch (() =>
        {
          if (gLectureSceneAudio === tAudio) gLectureSceneAudio = null;
        });
    return true;
  }

function arrete_morphing_lecture_scene (pTpl = gSceneTemplateActif, pOpt = {})
  {
    gLectureMorphingSequenceId += 1;
    const tEtat = pTpl && pTpl._lectureMorphing;
    if (!tEtat) return;
    pTpl._lectureMorphing = null;
    (tEtat.timers || []).forEach ((pTimer) => clearTimeout (pTimer));
    if (tEtat.interval) clearInterval (tEtat.interval);
    if (tEtat.audio && !pOpt.garderAudio)
      {
        try { tEtat.audio.pause (); } catch (e) {}
        try { tEtat.audio.currentTime = 0; } catch (e) {}
        if (gLectureSceneAudio === tEtat.audio) gLectureSceneAudio = null;
      }
    const tOverlay = tEtat.overlay;
    if (tOverlay && tOverlay.classList)
      {
        tOverlay.style.pointerEvents = "none";
        tOverlay.classList.remove ("est_visible");
        const tFadeMs = Math.max (0, Number (tEtat.fade_ms) || 0);
        const tTimer = setTimeout (fin_fondu_opacite_lecture (tOverlay, () =>
          {
            clearTimeout (tTimer);
            if (tOverlay.parentNode) tOverlay.parentNode.removeChild (tOverlay);
            marque_overlay_lourd_flottement_lecture_tpl (pTpl, tOverlay, false);
          }), tFadeMs + 40);
        if (pTpl) pTpl._lectureMorphingCleanupTimer = tTimer;
      }
  }

function images_morphing_objet_lecture_scene (pConfig)
  {
    return normalise_images_morphing_client (pConfig && pConfig.morphing_images)
      .map ((pImage) => ({...pImage, url : url_image_morphing_client (pImage.file_id)}))
      .filter ((pImage) => !!pImage.url);
  }

function ratio_diaporama_depuis_config (pConfig = null, pReglages = reglages_mode_lecture_scene (), pCategorie = null)
  {
    const tConfig = pConfig && typeof pConfig === "object" ? pConfig : {};
    const tReglages = pReglages || {};
    const tCategorie = pCategorie
      || (normalise_action_interactivite_objet (tConfig.interactivite_action) === INTERACTIVITE_ACTION_DIAPORAMA
        ? CALQUES_CATEGORIE_BOUTONS
        : CALQUES_CATEGORIE_ZOOG);
    const tProjetRatioL = tCategorie === CALQUES_CATEGORIE_BOUTONS
      ? tReglages.morphing_bouton_ratio_l
      : tReglages.morphing_ratio_l;
    const tProjetRatioH = tCategorie === CALQUES_CATEGORIE_BOUTONS
      ? tReglages.morphing_bouton_ratio_h
      : tReglages.morphing_ratio_h;
    const tL = Number.isFinite (Number (tConfig.morphing_ratio_l))
      ? Number (tConfig.morphing_ratio_l)
      : Number (tProjetRatioL);
    const tH = Number.isFinite (Number (tConfig.morphing_ratio_h))
      ? Number (tConfig.morphing_ratio_h)
      : Number (tProjetRatioH);
    return {
      l : Math.max (0.1, Math.min (20, Number.isFinite (tL) ? tL : 1)),
      h : Math.max (0.1, Math.min (20, Number.isFinite (tH) ? tH : 1)),
    };
  }

function applique_dimensions_stage_diaporama (pStage, pRatio, pTaillePct)
  {
    if (!pStage) return;
    const tRatio = pRatio || {l : 1, h : 1};
    const tTaille = Math.max (5, Math.min (100, Number (pTaillePct) || 60));
    pStage.style.aspectRatio = tRatio.l + " / " + tRatio.h;
    pStage.style.maxWidth = tTaille + "%";
    pStage.style.maxHeight = tTaille + "%";
    if (tRatio.l >= tRatio.h)
      {
        pStage.style.width = tTaille + "%";
        pStage.style.height = "auto";
      }
    else
      {
        pStage.style.width = "auto";
        pStage.style.height = tTaille + "%";
      }
  }

function applique_reglages_diaporama_lecture_scene (pEtat, pReglages = reglages_mode_lecture_scene ())
  {
    if (!pEtat) return;
    const tFonduMs = Math.max (0, Number (pReglages.morphing_fondu_ms) || 0);
    if (pEtat.overlay)
      pEtat.overlay.style.transition = "opacity " + tFonduMs + "ms ease";
    if (pEtat.stage)
      {
        const tTailleStagePct = Math.max (5, Math.min (100, Number (pReglages.morphing_stage_taille_pct) || 60));
        const tPaddingStageRem = Math.max (0, Number (pReglages.morphing_stage_padding_rem) || 0);
        pEtat.stage.style.background = pReglages.morphing_fond_couleur || "#ffffff";
        pEtat.stage.style.padding = tPaddingStageRem + "rem";
        pEtat.stage.style.setProperty ("--lecture-morphing-stage-padding", tPaddingStageRem + "rem");
        pEtat.stage.style.borderRadius = (Math.max (0, Number (pReglages.morphing_stage_radius_rem) || 0)) + "rem";
        const tRatio = ratio_diaporama_depuis_config (pEtat.config || null, pReglages, pEtat.categorie || null);
        applique_dimensions_stage_diaporama (pEtat.stage, tRatio, tTailleStagePct);
      }
    (pEtat.imageNodes || []).forEach ((pNode) =>
      {
        if (pNode && pNode.style)
          pNode.style.transition = "opacity " + tFonduMs + "ms ease";
      });
    pEtat.fade_ms = tFonduMs;
  }

function affiche_overlay_morphing_lecture_scene (pTpl, pImages, pAudio, pDureeMs, pConfig = null)
  {
    const tSceneNode = pTpl && pTpl.find && pTpl.find (".scene");
    if (!tSceneNode || !Array.isArray (pImages) || !pImages.length) return null;
    const tOverlay = document.createElement ("div");
    tOverlay.className = "lecture_morphing_overlay";
    const tReglagesMorphing = reglages_mode_lecture_scene ();
    const tImageMs = Math.max (100, (Number (tReglagesMorphing.morphing_image_duree_s) || 1) * 1000);
    const tFonduMs = Math.max (0, Number (tReglagesMorphing.morphing_fondu_ms) || 0);
    tOverlay.style.transition = "opacity " + tFonduMs + "ms ease";
    const tSceneSvgNode = pTpl && pTpl.find && pTpl.find (".contenu_svg_scene");
    if (tSceneSvgNode && typeof tSceneSvgNode.getBoundingClientRect === "function")
      {
        const tSceneRect = tSceneNode.getBoundingClientRect ();
        const tSvgRect = tSceneSvgNode.getBoundingClientRect ();
        if (tSvgRect.width > 0 && tSvgRect.height > 0)
          {
            tOverlay.style.inset = "auto";
            tOverlay.style.left = (tSvgRect.left - tSceneRect.left) + "px";
            tOverlay.style.top = (tSvgRect.top - tSceneRect.top) + "px";
            tOverlay.style.width = tSvgRect.width + "px";
            tOverlay.style.height = tSvgRect.height + "px";
          }
      }
    const tStage = document.createElement ("div");
    tStage.className = "lecture_morphing_stage";
    tStage.style.background = tReglagesMorphing.morphing_fond_couleur || "#ffffff";
    const tTailleStagePct = Math.max (5, Math.min (100, Number (tReglagesMorphing.morphing_stage_taille_pct) || 60));
    const tPaddingStageRem = Math.max (0, Number (tReglagesMorphing.morphing_stage_padding_rem) || 0);
    tStage.style.padding = tPaddingStageRem + "rem";
    tStage.style.setProperty ("--lecture-morphing-stage-padding", tPaddingStageRem + "rem");
    tStage.style.borderRadius = (Math.max (0, Number (tReglagesMorphing.morphing_stage_radius_rem) || 0)) + "rem";
    const tRatio = ratio_diaporama_depuis_config (pConfig, tReglagesMorphing, pConfig && pConfig._diaporama_categorie);
    applique_dimensions_stage_diaporama (tStage, tRatio, tTailleStagePct);
    tOverlay.appendChild (tStage);
    const tImageNodes = pImages.map ((pImage, pIndex) =>
      {
        const tImageFrame = document.createElement ("div");
        tImageFrame.className = "lecture_morphing_image" + (pIndex === 0 ? " est_active" : "");
        tImageFrame.style.opacity = pIndex === 0 ? "1" : "0";
        tImageFrame.style.zIndex = pIndex === 0 ? "2" : "1";
        tImageFrame.style.transform = "translate(" +
          (Number.isFinite (Number (pImage.offset_x_pct)) ? Number (pImage.offset_x_pct) : 0) + "%, " +
          (Number.isFinite (Number (pImage.offset_y_pct)) ? Number (pImage.offset_y_pct) : 0) + "%) scale(" +
          ((Number.isFinite (Number (pImage.taille_pct)) ? Math.max (1, Number (pImage.taille_pct)) : 100) / 100) + ")";
        const tImg = document.createElement ("img");
        tImg.src = pImage.url;
        tImg.alt = "";
        tImageFrame.appendChild (tImg);
        tStage.appendChild (tImageFrame);
        return tImageFrame;
      });
    tSceneNode.appendChild (tOverlay);
    marque_overlay_lourd_flottement_lecture_tpl (pTpl, tOverlay, true);
    const tTimers = [];
    const tSequenceId = gLectureMorphingSequenceId;
    const ferme = () => arrete_morphing_lecture_scene (pTpl);
    tOverlay.addEventListener ("click", ferme, {once : true});
    tTimers.push (setTimeout (() => tOverlay.classList.add ("est_visible"), 20));
    tImageNodes.forEach ((pNode) =>
      {
        pNode.style.transition = "opacity " + tFonduMs + "ms ease";
      });
    let tIndex = 0;
    const activeIndex = (pIndex, pIndexPrecedent = null) =>
      {
        tImageNodes.forEach ((pNode, pNodeIndex) =>
          {
            const tActif = pNodeIndex === pIndex;
            pNode.classList.toggle ("est_active", tActif);
            pNode.style.opacity = tActif ? "1" : "0";
            pNode.style.zIndex = tActif ? "2" : (pNodeIndex === pIndexPrecedent ? "1" : "0");
          });
      };
    const planifieImageSuivante = () =>
      {
        const tTimer = setTimeout (() =>
          {
            if (gLectureMorphingSequenceId !== tSequenceId) return;
            const tIndexPrecedent = tIndex;
            tIndex = (tIndex + 1) % tImageNodes.length;
            const tImagePrecedente = tImageNodes[tIndexPrecedent];
            const tImageSuivante = tImageNodes[tIndex];
            if (!tImagePrecedente || !tImageSuivante) return;
            tImagePrecedente.style.zIndex = "1";
            tImageSuivante.style.zIndex = "2";
            tImageSuivante.style.opacity = "0";
            tImageSuivante.classList.add ("est_active");
            requestAnimationFrame (() =>
              {
                if (gLectureMorphingSequenceId !== tSequenceId) return;
                tImagePrecedente.style.opacity = "0";
                tImageSuivante.style.opacity = "1";
              });
            const tFinFonduTimer = setTimeout (() =>
              {
                if (gLectureMorphingSequenceId !== tSequenceId) return;
                activeIndex (tIndex, tIndexPrecedent);
                planifieImageSuivante ();
              }, tFonduMs);
            tTimers.push (tFinFonduTimer);
          }, tImageMs);
        tTimers.push (tTimer);
      };
    if (tImageNodes.length > 1) planifieImageSuivante ();
    const tFinTimer = setTimeout (() =>
      {
        if (gLectureMorphingSequenceId === tSequenceId)
          arrete_morphing_lecture_scene (pTpl, {garderAudio : true});
      }, Math.max (300, Number (pDureeMs) || 0));
    tTimers.push (tFinTimer);
    pTpl._lectureMorphing = {
      overlay : tOverlay,
      stage : tStage,
      imageNodes : tImageNodes,
      timers : tTimers,
      interval : null,
      audio : pAudio || null,
      fade_ms : tFonduMs,
      config : pConfig || null,
      categorie : pConfig && pConfig._diaporama_categorie || null,
    };
    return tOverlay;
  }

function duree_diaporama_sans_audio_ms (pImages)
  {
    const tImages = Array.isArray (pImages) ? pImages : [];
    const tImageMs = Math.max (100, (Number (reglages_mode_lecture_scene ().morphing_image_duree_s) || 1) * 1000);
    return Math.max (300, Math.max (1, tImages.length) * tImageMs);
  }

function joue_diaporama_objet_lecture_scene (pObjId, pOpt = {})
  {
    if (!mode_lecture_scene_actif ()) return Promise.resolve (false);
    const tSceneId = Session.get ("scene_id");
    const tScene = tSceneId ? Scenes.findOne (tSceneId) : null;
    const tCategorie = (pOpt && pOpt.categorie) || trouve_categorie_pour_id (pObjId);
    const tConfigBase = get_objet_config (tScene, pObjId);
    if (!tConfigBase) return Promise.resolve (false);
    const tAudio = tConfigBase.audio_file_id
      ? cree_audio_voix_lecture_scene_depuis_file_id (tConfigBase.audio_file_id)
      : null;
    const tConfig = {
      ...tConfigBase,
      _diaporama_categorie : tCategorie,
    };
    const tImages = images_morphing_objet_lecture_scene (tConfig);
    if (!tAudio && !tImages.length) return Promise.resolve (false);
    const tTpl = (pOpt && pOpt.tpl) || gSceneTemplateActif;
    arrete_morphing_lecture_scene (tTpl);
    arrete_audio_lecture_scene ({interne : true});
    const tSequenceId = ++gLectureMorphingSequenceId;
    return new Promise ((resolve) =>
      {
        let tTermine = false;
        let tOverlayDemarre = false;
        const termine = (pOk) =>
          {
            if (tTermine) return;
            tTermine = true;
            if (gLectureSceneAudio === tAudio) gLectureSceneAudio = null;
            if (gLectureSceneAudioFin === termine) gLectureSceneAudioFin = null;
            if (gLectureMorphingSequenceId === tSequenceId)
              arrete_morphing_lecture_scene (tTpl, {garderAudio : true});
            resolve (!!pOk);
          };
        const demarreOverlay = () =>
          {
            if (tOverlayDemarre) return;
            tOverlayDemarre = true;
            if (gLectureMorphingSequenceId !== tSequenceId) return;
            const tDureeMs = tAudio
              ? Math.max (300, (Number (tAudio.duration) || 0) * 1000)
              : duree_diaporama_sans_audio_ms (tImages);
            if (tImages.length)
              affiche_overlay_morphing_lecture_scene (tTpl, tImages, tAudio, tDureeMs, tConfig);
            if (!tAudio)
              setTimeout (() => termine (true), tDureeMs);
          };
        if (!tAudio)
          {
            demarreOverlay ();
            return;
          }
        tAudio.addEventListener ("loadedmetadata", demarreOverlay, {once : true});
        tAudio.addEventListener ("ended", () => termine (true), {once : true});
        tAudio.addEventListener ("error", () => termine (false), {once : true});
        gLectureSceneAudio = tAudio;
        gLectureSceneAudioFin = termine;
        const tPlay = tAudio.play ();
        if (tPlay && typeof tPlay.catch === "function")
          tPlay.catch (() => termine (false));
        if (Number.isFinite (tAudio.duration) && tAudio.duration > 0) demarreOverlay ();
      });
  }

function joue_morphing_zoog_lecture_scene (pObjId, pOpt = {})
  {
    return joue_diaporama_objet_lecture_scene (pObjId, {...(pOpt || {}), categorie : CALQUES_CATEGORIE_ZOOG});
  }

function zoogs_scene_lecture_ordonnes (pScene = null)
  {
    const tScene = pScene || Scenes.findOne (Session.get ("scene_id"));
    const tObjets = tScene && tScene.svg_objets && Array.isArray (tScene.svg_objets[CALQUES_CATEGORIE_ZOOG])
      ? tScene.svg_objets[CALQUES_CATEGORIE_ZOOG]
      : [];
    return [...tObjets].sort ((a, b) =>
      cle_tri_calque (a && a["data-name"], CALQUES_CATEGORIE_ZOOG).localeCompare (
        cle_tri_calque (b && b["data-name"], CALQUES_CATEGORIE_ZOOG),
        "fr",
        {sensitivity : "base"}
      ));
  }

async function enchaine_morphings_zoog_scene_lecture (pSceneId)
  {
    if (!mode_lecture_scene_actif ()) return false;
    const tSceneId = pSceneId || Session.get ("scene_id");
    const tScene = tSceneId ? Scenes.findOne (tSceneId) : null;
    if (!tScene || tScene._id !== Session.get ("scene_id")) return false;
    const tZoogs = zoogs_scene_lecture_ordonnes (tScene);
    if (!tZoogs.length) return false;
    for (const tZoog of tZoogs)
      {
        if (!mode_lecture_scene_actif () || Session.get ("scene_id") !== tSceneId) return false;
        await joue_morphing_zoog_lecture_scene (tZoog.id, {tpl : gSceneTemplateActif});
      }
    arrete_morphing_lecture_scene (gSceneTemplateActif, {garderAudio : true});
    return true;
  }

function consomme_navigation_interne_morphing_si_prete (pTpl)
  {
    const tSceneId = Session.get ("scene_id");
    if (!tSceneId || gLectureMorphingAutoSceneId !== tSceneId) return false;
    if (!mode_lecture_scene_actif ()) return false;
    if (!svg_scene_document_pret_tpl (pTpl, tSceneId)) return false;
    gLectureMorphingAutoSceneId = null;
    Tracker.afterFlush (() => { enchaine_morphings_zoog_scene_lecture (tSceneId); });
    return true;
  }

function joue_audio_projet_lecture_scene (pCle)
  {
    if (["son_clic", "son_appareil_photo", "son_reussite", "son_echec"].includes (pCle))
      {
        const tOk = joue_audio_instant_file_id_lecture_scene (
          file_id_audio_projet_lecture_scene (pCle),
          volume_audio_projet_lecture_scene (pCle)
        );
        arrete_audio_lecture_scene ({garderInstant : true});
        return tOk;
      }
    return joue_audio_lecture_scene (
      file_id_audio_projet_lecture_scene (pCle),
      volume_audio_projet_lecture_scene (pCle)
    );
  }

function joue_audio_projet_lecture_scene_promesse (pCle)
  {
    return joue_audio_lecture_scene_promesse (
      file_id_audio_projet_lecture_scene (pCle),
      volume_audio_projet_lecture_scene (pCle)
    );
  }

function cree_audio_lecture_scene_depuis_file_id (pFileId, pVolumePct = 100)
  {
    const tUrl = url_audio_lecture_scene_pour_file_id (pFileId);
    if (!tUrl) return null;
    const tAudio = new Audio (tUrl);
    tAudio.preload = "auto";
    applique_volume_audio_element_lecture_scene (tAudio, pVolumePct);
    try { tAudio.load (); } catch (e) {}
    return tAudio;
  }

function joue_audio_lecture_scene_prepare_promesse (pAudio, pOptions = {})
  {
    if (!pAudio) return Promise.resolve (false);

    arrete_audio_lecture_scene ({interne : true});
    return new Promise ((resolve) =>
      {
        let tTermine = false;
        let tDebutSignale = false;
        const signale_debut = () =>
          {
            if (tDebutSignale) return;
            tDebutSignale = true;
            if (pOptions && typeof pOptions.onStart === "function")
              {
                try { pOptions.onStart (); } catch (e) {}
              }
          };
        const termine = (pOk) =>
          {
            if (tTermine) return;
            tTermine = true;
            if (gLectureSceneAudio === pAudio) gLectureSceneAudio = null;
            if (gLectureSceneAudioFin === termine) gLectureSceneAudioFin = null;
            resolve (!!pOk);
          };

        gLectureSceneAudio = pAudio;
        gLectureSceneAudioFin = termine;
        pAudio.addEventListener ("playing", signale_debut, {once : true});
        pAudio.addEventListener ("ended", () => termine (true), {once : true});
        pAudio.addEventListener ("error", () => termine (false), {once : true});
        const tPlay = pAudio.play ();
        if (tPlay && typeof tPlay.catch === "function")
          {
            if (typeof tPlay.then === "function")
              tPlay.then (signale_debut).catch (() => termine (false));
            else
              tPlay.catch (() => termine (false));
          }
        else
          signale_debut ();
      });
  }

async function joue_sequence_audio_lecture_scene (pFileIds, pOptions = {})
  {
    if (!mode_lecture_scene_actif ()) return false;
    const tItems = Array.isArray (pFileIds) ? pFileIds.filter (Boolean) : [];
    if (!tItems.length) return false;
    const tSequenceId = gLectureSceneSequenceAudioId + 1;
    gLectureSceneSequenceAudioId = tSequenceId;
    const tAudios = tItems
      .map ((pItem) =>
        {
          const tFileId = typeof pItem === "string" ? pItem : (pItem && pItem.file_id);
          const tVolume = typeof pItem === "string" ? 100 : (pItem && pItem.volume_pct);
          const tVoix = !!(pItem && pItem.voix);
          const tVolumeVoixBase = pItem && pItem.volume_base_pct !== undefined
            ? pItem.volume_base_pct
            : tVolume;
          return {
            item : pItem,
            audio : tVoix
              ? cree_audio_voix_lecture_scene_depuis_file_id (tFileId, tVolumeVoixBase)
              : cree_audio_lecture_scene_depuis_file_id (tFileId, tVolume),
          };
        })
      .filter ((pEntree) => !!(pEntree && pEntree.audio));
    if (!tAudios.length) return false;
    for (let i = 0; i < tAudios.length; i++)
      {
        if (!mode_lecture_scene_actif () || gLectureSceneSequenceAudioId !== tSequenceId)
          return false;
        const tEntree = tAudios[i];
        const tOk = await joue_audio_lecture_scene_prepare_promesse (
          tEntree.audio,
          {
            onStart : () =>
              {
                if (!mode_lecture_scene_actif () || gLectureSceneSequenceAudioId !== tSequenceId)
                  return;
                if (pOptions && typeof pOptions.onItemStart === "function")
                  pOptions.onItemStart (tEntree.item, i);
              },
          }
        );
        if (!tOk) return false;
      }
    return mode_lecture_scene_actif () && gLectureSceneSequenceAudioId === tSequenceId;
  }

function joue_audio_retour_menu_lecture_scene ()
  {
    const tFileId = file_id_audio_projet_lecture_scene ("son_retour_menu");
    if (joue_audio_instant_file_id_lecture_scene (
      tFileId,
      volume_audio_projet_lecture_scene ("son_retour_menu"),
      {retourMenu : true}
    ))
      return true;

    const tUrl = url_audio_lecture_scene_pour_file_id (
      tFileId
    );
    if (!tUrl) return false;

    const tAncien = gLectureSceneRetourMenuAudio;
    if (tAncien)
      {
        try { tAncien.pause (); } catch (e) {}
        try { tAncien.currentTime = 0; } catch (e) {}
      }

    const tAudio = new Audio (tUrl);
    const nettoie = () =>
      {
        if (gLectureSceneRetourMenuAudio === tAudio)
          gLectureSceneRetourMenuAudio = null;
      };
    tAudio.preload = "auto";
    applique_volume_audio_element_lecture_scene (tAudio, volume_audio_projet_lecture_scene ("son_retour_menu"));
    gLectureSceneRetourMenuAudio = tAudio;
    tAudio.addEventListener ("ended", nettoie, {once : true});
    tAudio.addEventListener ("error", nettoie, {once : true});
    const tPlay = tAudio.play ();
    if (tPlay && typeof tPlay.catch === "function")
      tPlay.catch (nettoie);
    return true;
  }

function data_name_svg_depuis_noeud_lecture (pNoeud)
  {
    let tNoeud = pNoeud || null;
    while (tNoeud && tNoeud.nodeType === 1)
      {
        if (tNoeud.getAttribute)
          {
            const tDataName = tNoeud.getAttribute ("data-name")
              || tNoeud.getAttribute ("name")
              || tNoeud.getAttribute ("inkscape:label")
              || tNoeud.getAttribute ("label");
            if (tDataName) return tDataName;
          }
        tNoeud = tNoeud.parentNode;
      }
    return "";
  }

function nom_objet_inspecteur_depuis_data_name (pDataName, pCategorie)
  {
    if (!pDataName) return "";
    if (pCategorie === CALQUES_CATEGORIE_PART)
      return nom_affiche_calque_arbre (pDataName, pCategorie) || "";
    const tParts = String (pDataName).split (",").map ((s) => s.trim ());
    return tParts[1] || nom_affiche_calque_arbre (pDataName, pCategorie) || "";
  }

function bouton_scene_lecture_est_retour_menu (pId, pNoeud = null, pDataName = "")
  {
    const tDataName = pDataName
      || data_name_svg_depuis_noeud_lecture (pNoeud)
      || get_data_name_pour_id (Session.get ("scene_id"), pId);
    const tParts = typeof tDataName === "string"
      ? tDataName.split (",").map ((s) => s.trim ()).filter (Boolean)
      : [];
    if (String (tParts[1] || "").trim ().toLowerCase ().includes ("retour"))
      return true;
    const tNom = nom_objet_inspecteur_depuis_id (pId);
    return tNom.toLowerCase ().includes ("retour");
  }

function bouton_retour_menu_scene_lecture_courante ()
  {
    const tSceneId = Session.get ("scene_id");
    const tScene = tSceneId ? Scenes.findOne (tSceneId) : null;
    if (!tScene) return null;
    const tCandidats = [];
    if (Array.isArray (tScene.objets_config))
      tScene.objets_config.forEach ((pConfig) =>
        {
          if (pConfig && pConfig.id) tCandidats.push ({id : pConfig.id, config : pConfig});
        });
    items_interface_scene (tScene).forEach ((pItem) =>
      {
        if (pItem && pItem.id && !tCandidats.some ((pCandidat) => pCandidat.id === pItem.id))
          tCandidats.push ({id : pItem.id, config : pItem});
      });
    return tCandidats.find ((pCandidat) =>
      pCandidat
      && pCandidat.id
      && bouton_scene_lecture_est_retour_menu (pCandidat.id)
      && normalise_action_interactivite_objet (pCandidat.config && pCandidat.config.interactivite_action)
    ) || null;
  }

function joue_son_appareil_photo_bulle_lecture (pTpl)
  {
    const tTpl = pTpl || gSceneTemplateActif;
    if (tTpl) tTpl._lectureSonAppareilPhotoBulleJusqua = Date.now () + 1400;
    return joue_audio_projet_lecture_scene ("son_appareil_photo");
  }

function reglages_ambiance_lecture_scene (pScene)
  {
    const tLocal = gAmbianceLectureLocal.get ();
    const tPatch = tLocal && pScene && tLocal.scene_id === pScene._id ? tLocal.patch : null;
    return normalise_ambiance_lecture_scene ({...((pScene && pScene.ambiance_lecture) || {}), ...(tPatch || {})});
  }

function reglages_ambiance_lecture_scene_courante ()
  {
    const tSceneId = Session.get ("scene_id");
    return reglages_ambiance_lecture_scene (tSceneId ? Scenes.findOne (tSceneId) : null);
  }

// Recopie l'état de l'élément audio dans gLectureSceneAmbianceEtat (barre de
// contrôles). Les événements d'un élément remplacé entre-temps sont ignorés.
function ecoute_etat_ambiance_lecture_scene (pAudio)
  {
    const tMaj = () =>
      {
        if (gLectureSceneAmbianceAudio !== pAudio) return;
        gLectureSceneAmbianceEtat.set (
          {
            lecture : !pAudio.paused && !pAudio.ended,
            position : Number.isFinite (pAudio.currentTime) ? pAudio.currentTime : 0,
            duree : Number.isFinite (pAudio.duration) ? pAudio.duration : 0,
          });
      };
    for (const tType of ["play", "pause", "ended", "timeupdate", "loadedmetadata", "durationchange", "seeked"])
      pAudio.addEventListener (tType, tMaj);
    tMaj ();
  }

function lance_ambiance_lecture_scene ()
  {
    // En édition, l'élément audio n'existe pas encore : la barre le crée.
    if (!gLectureSceneAmbianceAudio && !mode_lecture_scene_actif ())
      {
        const tFileId = file_id_audio_scene_lecture_scene ("ambiance");
        const tUrl = url_audio_lecture_scene_pour_file_id (tFileId);
        if (!tFileId || !tUrl) return;
        cree_audio_ambiance_lecture_scene (tUrl, tFileId, reglages_ambiance_lecture_scene_courante ());
        gLectureSceneAmbianceEdition = true;
      }
    const tAudio = gLectureSceneAmbianceAudio;
    if (!tAudio) return;
    if (tAudio.ended) { try { tAudio.currentTime = 0; } catch (e) {} }
    const tPlay = tAudio.play ();
    if (tPlay && typeof tPlay.catch === "function") tPlay.catch (() => {});
  }

function pause_ambiance_lecture_scene ()
  {
    const tAudio = gLectureSceneAmbianceAudio;
    if (tAudio) { try { tAudio.pause (); } catch (e) {} }
  }

function stoppe_ambiance_lecture_scene ()
  {
    const tAudio = gLectureSceneAmbianceAudio;
    if (!tAudio) return;
    try { tAudio.pause (); } catch (e) {}
    try { tAudio.currentTime = 0; } catch (e) {}
  }

function positionne_ambiance_lecture_scene (pFraction)
  {
    const tAudio = gLectureSceneAmbianceAudio;
    if (!tAudio || !Number.isFinite (tAudio.duration) || tAudio.duration <= 0) return;
    const tFraction = Math.max (0, Math.min (1, Number (pFraction) || 0));
    try { tAudio.currentTime = tFraction * tAudio.duration; } catch (e) {}
  }

function formate_temps_ambiance_lecture (pSecondes)
  {
    const tTotal = Math.max (0, Math.floor (Number (pSecondes) || 0));
    return Math.floor (tTotal / 60) + ":" + String (tTotal % 60).padStart (2, "0");
  }

// Glisser sur la timeline de la barre d'ambiance : positionne la lecture en
// continu tant que le pointeur est capturé.
function suit_timeline_ambiance_lecture (pEvent)
  {
    const tPiste = pEvent.currentTarget;
    if (!tPiste || typeof tPiste.getBoundingClientRect !== "function") return;
    const tPositionne = (pEv) =>
      {
        const tRect = tPiste.getBoundingClientRect ();
        if (!tRect.width) return;
        positionne_ambiance_lecture_scene ((pEv.clientX - tRect.left) / tRect.width);
      };
    tPositionne (pEvent);
    try { tPiste.setPointerCapture (pEvent.pointerId); } catch (e) {}
    // Garde le curseur visible pendant le glissé (aussi au toucher).
    tPiste.classList.add ("est_glisse");
    const tFin = () =>
      {
        tPiste.classList.remove ("est_glisse");
        tPiste.removeEventListener ("pointermove", tPositionne);
        tPiste.removeEventListener ("pointerup", tFin);
        tPiste.removeEventListener ("pointercancel", tFin);
      };
    tPiste.addEventListener ("pointermove", tPositionne);
    tPiste.addEventListener ("pointerup", tFin);
    tPiste.addEventListener ("pointercancel", tFin);
  }

function cree_audio_ambiance_lecture_scene (pUrl, pFileId, pReglages)
  {
    arrete_ambiance_lecture_scene ();
    const tVolumePct = volume_audio_scene_lecture_scene ("ambiance");
    const tAudio = new Audio (pUrl);
    tAudio.loop = pReglages.boucle;
    tAudio.preload = "auto";
    applique_volume_audio_element_lecture_scene (tAudio, tVolumePct);
    gLectureSceneAmbianceAudio = tAudio;
    gLectureSceneAmbianceFileId = pFileId;
    ecoute_etat_ambiance_lecture_scene (tAudio);
    tAudio.addEventListener ("error", () =>
      {
        if (gLectureSceneAmbianceAudio === tAudio) arrete_ambiance_lecture_scene ();
      }, {once : true});
    return tAudio;
  }

function synchronise_ambiance_lecture_scene ()
  {
    const tLecture = mode_lecture_scene_actif ();
    const tFileId = file_id_audio_scene_lecture_scene ("ambiance");
    const tUrl = url_audio_lecture_scene_pour_file_id (tFileId);
    const tReglages = reglages_ambiance_lecture_scene_courante ();
    const tMemeFichier = !!gLectureSceneAmbianceAudio && gLectureSceneAmbianceFileId === tFileId;
    // Hors lecture, seule l'ambiance lancée depuis la barre (édition) continue.
    const tEditionConservee = !tLecture && gLectureSceneAmbianceEdition && tMemeFichier && tReglages.controles;
    // Sans lecture automatique ni contrôles, l'ambiance ne pourrait jamais
    // démarrer : inutile de la télécharger.
    if ((!tLecture && !tEditionConservee)
     || !tFileId || !tUrl || (!tReglages.lecture_auto && !tReglages.controles))
      {
        arrete_ambiance_lecture_scene ();
        return false;
      }
    if (tLecture) gLectureSceneAmbianceEdition = false;
    const tVolumePct = volume_audio_scene_lecture_scene ("ambiance");
    if (tMemeFichier)
      {
        gLectureSceneAmbianceAudio.loop = tReglages.boucle;
        applique_volume_audio_element_lecture_scene (gLectureSceneAmbianceAudio, tVolumePct);
        return true;
      }

    const tAudio = cree_audio_ambiance_lecture_scene (tUrl, tFileId, tReglages);
    if (!tReglages.lecture_auto) return true;
    const tPlay = tAudio.play ();
    if (tPlay && typeof tPlay.catch === "function")
      tPlay.catch (() =>
        {
          // Lecture automatique refusée par le navigateur : avec la barre de
          // contrôles, l'élément reste prêt pour le bouton lecture.
          if (gLectureSceneAmbianceAudio !== tAudio) return;
          if (!Tracker.nonreactive (() => reglages_ambiance_lecture_scene_courante ().controles))
            arrete_ambiance_lecture_scene ();
        });
    return true;
  }

function config_objet_scene_courante (pId)
  {
    const tSceneId = Session.get ("scene_id");
    const tScene = tSceneId ? Scenes.findOne (tSceneId) : null;
    return get_objet_config (tScene, pId);
  }

function joue_audio_objet_lecture_scene (pId, pOptions = {})
  {
    const tFileId = file_id_audio_objet_lecture_scene (pId);
    if (!tFileId)
      {
        arrete_audio_lecture_scene ();
        return false;
      }
    if (pOptions && typeof pOptions.onStart === "function")
      {
        const tAudio = cree_audio_voix_lecture_scene_depuis_file_id (tFileId, 100);
        if (!tAudio) return false;
        joue_audio_lecture_scene_prepare_promesse (
          tAudio,
          {
            onStart : pOptions.onStart,
          }
        );
        return true;
      }
    return joue_audio_voix_lecture_scene (tFileId);
  }

function joue_audio_objet_lecture_scene_promesse (pId, pOptions = {})
  {
    const tFileId = file_id_audio_objet_lecture_scene (pId);
    if (!tFileId)
      {
        arrete_audio_lecture_scene ();
        return Promise.resolve (false);
      }
    const tAudio = cree_audio_voix_lecture_scene_depuis_file_id (tFileId, 100);
    if (!tAudio) return Promise.resolve (false);
    return joue_audio_lecture_scene_prepare_promesse (
      tAudio,
      {
        onStart : pOptions && typeof pOptions.onStart === "function"
          ? pOptions.onStart
          : null,
      }
    );
  }

function file_id_audio_objet_lecture_scene (pId)
  {
    let tConfig = config_objet_scene_courante (pId);
    if ((!tConfig || !tConfig.audio_file_id) && trouve_categorie_pour_id (pId) === CALQUES_CATEGORIE_PART)
      {
        const tSceneId = Session.get ("scene_id");
        const tScene = tSceneId ? Scenes.findOne (tSceneId) : null;
        const tParentId = id_obj_parent_pour_part_scene (tScene, pId);
        if (tParentId) tConfig = config_objet_scene_courante (tParentId);
      }
    return tConfig && tConfig.audio_file_id ? tConfig.audio_file_id : null;
  }

function entree_audio_projet_lecture_scene (pCle)
  {
    return entree_audio_projet (projet_courant_scene (), pCle);
  }

function volume_audio_projet_lecture_scene (pCle)
  {
    return volume_audio_entree_pct (entree_audio_projet_lecture_scene (pCle));
  }

function volume_audio_scene_lecture_scene (pCle)
  {
    const tSceneId = Session.get ("scene_id");
    const tScene = tSceneId ? Scenes.findOne (tSceneId) : null;
    return volume_audio_entree_pct (entree_audio_scene (tScene, pCle));
  }

function delai_sequence_entends_tu_lecture_ms ()
  {
    return delai_sequence_entends_tu_entree_ms (
      entree_audio_projet_lecture_scene ("entends_tu_le_son_1")
    );
  }

function file_id_audio_projet_lecture_scene (pCle)
  {
    const tProjet = projet_courant_scene ();
    const tEntree = entree_audio_projet (tProjet, pCle);
    return tEntree && tEntree.file_id ? tEntree.file_id : null;
  }

function file_id_audio_scene_lecture_scene (pCle)
  {
    const tSceneId = Session.get ("scene_id");
    const tScene = tSceneId ? Scenes.findOne (tSceneId) : null;
    const tEntree = entree_audio_scene (tScene, pCle);
    return tEntree && tEntree.file_id ? tEntree.file_id : null;
  }

function fichiers_sequence_entends_tu_lecture_scene ()
  {
    return [
      {
        cle : "entends_tu_le_son_1",
        file_id : file_id_audio_projet_lecture_scene ("entends_tu_le_son_1"),
        volume_pct : volume_audio_projet_lecture_scene ("entends_tu_le_son_1"),
      },
      {
        cle : "phoneme",
        file_id : file_id_audio_scene_lecture_scene ("phoneme"),
        volume_pct : volume_audio_scene_lecture_scene ("phoneme"),
      },
      {
        cle : "entends_tu_le_son_2",
        file_id : file_id_audio_projet_lecture_scene ("entends_tu_le_son_2"),
        volume_pct : volume_audio_projet_lecture_scene ("entends_tu_le_son_2"),
      },
    ].filter ((pItem) => !!(pItem && pItem.file_id));
  }

function fichiers_sequence_entends_tu_objet_lecture_scene (pId)
  {
    return fichiers_sequence_entends_tu_lecture_scene ()
      .concat ([{cle : "objet", file_id : file_id_audio_objet_lecture_scene (pId), volume_base_pct : 100, voix : true}])
      .filter ((pItem) => !!(pItem && pItem.file_id));
  }

async function joue_sequence_entends_tu_lecture_scene (pOptions = {})
  {
    return joue_sequence_audio_lecture_scene (fichiers_sequence_entends_tu_lecture_scene (), pOptions);
  }

async function joue_sequence_entends_tu_objet_lecture_scene (pId, pOptions = {})
  {
    return joue_sequence_audio_lecture_scene (fichiers_sequence_entends_tu_objet_lecture_scene (pId), pOptions);
  }

function lecture_scores_session ()
  {
    const tScores = Session.get (LECTURE_PEDAGO_SESSION_KEY);
    return tScores && typeof tScores === "object" ? tScores : {};
  }

function cle_stockage_local_scores_lecture ()
  {
    const tProjetId = Session.get ("projet_id") || "global";
    return LECTURE_PEDAGO_LOCAL_STORAGE_PREFIX + tProjetId;
  }

function clone_scores_lecture (pScores)
  {
    if (!pScores || typeof pScores !== "object") return {};
    try { return JSON.parse (JSON.stringify (pScores)); }
    catch (e) { return {}; }
  }

function lit_scores_lecture_stockes_localement ()
  {
    if (typeof localStorage === "undefined") return {};
    try
      {
        const tScores = JSON.parse (localStorage.getItem (cle_stockage_local_scores_lecture ()) || "{}");
        return tScores && typeof tScores === "object" ? tScores : {};
      }
    catch (e) { return {}; }
  }

function sauvegarde_scores_lecture_localement ()
  {
    if (typeof localStorage === "undefined") return;
    try
      {
        localStorage.setItem (
          cle_stockage_local_scores_lecture (),
          JSON.stringify (clone_scores_lecture (lecture_scores_session ()))
        );
      }
    catch (e) {}
  }

function recharge_scores_lecture_stockes_tpl (pTpl)
  {
    const tCle = cle_stockage_local_scores_lecture ();
    if (pTpl && pTpl._lectureScoresLocalStorageKey === tCle) return false;
    if (pTpl) pTpl._lectureScoresLocalStorageKey = tCle;
    Session.set (LECTURE_PEDAGO_SESSION_KEY, lit_scores_lecture_stockes_localement ());
    return true;
  }

function actualise_parcours_lecture_apres_score ()
  {
    sauvegarde_scores_lecture_localement ();
    synchronise_items_interface_svg_tpl (gSceneTemplateActif);
    applique_decorations_scene_reussies_tpl (gSceneTemplateActif);
  }

function lecture_score_scene_session (pSceneId = Session.get ("scene_id"))
  {
    const tSceneId = typeof pSceneId === "string" ? pSceneId : "";
    const tScores = lecture_scores_session ();
    const tScore = tSceneId ? tScores[tSceneId] : null;
    return tScore && typeof tScore === "object" ? tScore : {cartes : {}, terminee : false};
  }

function ecrit_lecture_score_scene_session (pSceneId, pScore)
  {
    const tSceneId = typeof pSceneId === "string" ? pSceneId : "";
    if (!tSceneId) return;
    Session.set (
      LECTURE_PEDAGO_SESSION_KEY,
      {
        ...lecture_scores_session (),
        [tSceneId] : {
          cartes : {},
          terminee : false,
          ...(pScore || {}),
        },
      }
    );
    actualise_parcours_lecture_apres_score ();
  }

function efface_lecture_score_scene_session (pSceneId = Session.get ("scene_id"))
  {
    const tSceneId = typeof pSceneId === "string" ? pSceneId : "";
    if (!tSceneId) return;
    const tScores = {...lecture_scores_session ()};
    delete tScores[tSceneId];
    Session.set (LECTURE_PEDAGO_SESSION_KEY, tScores);
    actualise_parcours_lecture_apres_score ();
  }

function lecture_score_carte_session (pSceneId, pCarteId)
  {
    const tScore = lecture_score_scene_session (pSceneId);
    const tCartes = tScore.cartes && typeof tScore.cartes === "object" ? tScore.cartes : {};
    const tCarteScore = pCarteId ? tCartes[pCarteId] : null;
    return tCarteScore && typeof tCarteScore === "object"
      ? tCarteScore
      : {reponses : {}, terminee : false};
  }

function ecrit_lecture_score_carte_session (pSceneId, pCarteId, pCarteScore)
  {
    const tSceneId = typeof pSceneId === "string" ? pSceneId : "";
    const tCarteId = typeof pCarteId === "string" ? pCarteId : "";
    if (!tSceneId || !tCarteId) return;
    const tSceneScore = lecture_score_scene_session (tSceneId);
    const tCartes = tSceneScore.cartes && typeof tSceneScore.cartes === "object"
      ? tSceneScore.cartes
      : {};
    ecrit_lecture_score_scene_session (
      tSceneId,
      {
        ...tSceneScore,
        cartes : {
          ...tCartes,
          [tCarteId] : {
            reponses : {},
            terminee : false,
            ...(pCarteScore || {}),
          },
        },
      }
    );
  }

function score_carte_terminee_lecture (pSceneId, pCarteId)
  {
    return !!lecture_score_carte_session (pSceneId, pCarteId).terminee;
  }

function reponses_reussies_carte_lecture (pTpl, pCarte, pScene)
  {
    return objets_autorises_carte_lecture (pTpl, pCarte, pScene)
      .reduce ((pAcc, pObj) =>
        {
          if (pObj && pObj.id) pAcc[pObj.id] = true;
          return pAcc;
        }, {});
  }

function clone_score_scene_lecture (pScore)
  {
    if (!pScore || typeof pScore !== "object") return null;
    try { return JSON.parse (JSON.stringify (pScore)); }
    catch (e) { return {cartes : {}, terminee : false}; }
  }

function score_scene_session_existe_lecture (pSceneId)
  {
    const tSceneId = typeof pSceneId === "string" ? pSceneId : "";
    const tScores = lecture_scores_session ();
    return !!(tSceneId && Object.prototype.hasOwnProperty.call (tScores, tSceneId));
  }

function carte_restante_cheat_lecture (pCartes, pSceneId)
  {
    const tCartes = Array.isArray (pCartes) ? pCartes.filter ((pCarte) => pCarte && pCarte._id) : [];
    if (!tCartes.length) return null;
    const tFocusCarteId = gLectureSceneFocusCarte.get ();
    const tCarteFocus = tFocusCarteId
      ? tCartes.find ((pCarte) => pCarte._id === tFocusCarteId)
      : null;
    if (tCarteFocus) return tCarteFocus;
    return tCartes.find ((pCarte) => !score_carte_terminee_lecture (pSceneId, pCarte._id))
      || tCartes[tCartes.length - 1];
  }

function restaure_cheat_cartes_lecture_scene (pTpl, pSceneId)
  {
    const tSceneId = typeof pSceneId === "string" ? pSceneId : "";
    const tSnapshot = tSceneId ? gLectureCheatScoresAvant.get (tSceneId) : null;
    if (!tSnapshot) return false;
    if (tSnapshot.existe)
      ecrit_lecture_score_scene_session (tSceneId, clone_score_scene_lecture (tSnapshot.score));
    else
      efface_lecture_score_scene_session (tSceneId);
    gLectureCheatScoresAvant.delete (tSceneId);
    if (!gLectureSceneFocusCarte.get ())
      rafraichit_scene_lecture_tpl (pTpl || gSceneTemplateActif, false);
    return true;
  }

function applique_cheat_cartes_lecture_scene (pTpl)
  {
    if (!mode_lecture_scene_actif ()) return false;
    const tSceneId = Session.get ("scene_id");
    if (restaure_cheat_cartes_lecture_scene (pTpl, tSceneId)) return true;
    const tScene = tSceneId ? Scenes.findOne (tSceneId) : null;
    const tCartes = cartes_scene_liste ().filter ((pCarte) => pCarte && pCarte._id);
    const tCarteRestante = carte_restante_cheat_lecture (tCartes, tSceneId);
    if (!tScene || !tCarteRestante) return false;
    const tSceneScore = lecture_score_scene_session (tSceneId);
    gLectureCheatScoresAvant.set (
      tSceneId,
      {
        existe : score_scene_session_existe_lecture (tSceneId),
        score : clone_score_scene_lecture (tSceneScore),
      }
    );
    const tCartesScore = tSceneScore.cartes && typeof tSceneScore.cartes === "object"
      ? tSceneScore.cartes
      : {};
    const tNouveauxScoresCartes = {...tCartesScore};
    tCartes.forEach ((pCarte) =>
      {
        const tScoreCarte = lecture_score_carte_session (tSceneId, pCarte._id);
        if (pCarte._id === tCarteRestante._id)
          {
            const tFocusCarteId = gLectureSceneFocusCarte.get ();
            tNouveauxScoresCartes[pCarte._id] = tFocusCarteId === pCarte._id
              ? {...tScoreCarte, terminee : false, interrompue : false}
              : {reponses : {}, terminee : false, interrompue : false};
            return;
          }
        tNouveauxScoresCartes[pCarte._id] = {
          ...tScoreCarte,
          reponses : {
            ...(tScoreCarte.reponses && typeof tScoreCarte.reponses === "object" ? tScoreCarte.reponses : {}),
            ...reponses_reussies_carte_lecture (pTpl, pCarte, tScene),
          },
          terminee : true,
          interrompue : false,
        };
      });
    ecrit_lecture_score_scene_session (
      tSceneId,
      {
        ...tSceneScore,
        cartes : tNouveauxScoresCartes,
        terminee : false,
      }
    );
    if (!gLectureSceneFocusCarte.get ())
      rafraichit_scene_lecture_tpl (pTpl || gSceneTemplateActif, false);
    return true;
  }

function data_name_contient_son_oui (pDataName)
  {
    const tParts = typeof pDataName === "string"
      ? pDataName.split (",").map ((pItem) => pItem.trim ().toLowerCase ())
      : [];
    return tParts.includes ("son-oui");
  }

function objet_contient_son_lecture (pScene, pObjId)
  {
    const tConfig = get_objet_config (pScene, pObjId);
    if (tConfig && typeof tConfig.son_override === "boolean")
      return tConfig.son_override;
    const tEntree = entree_svg_scene_depuis_id (pScene, pObjId);
    return data_name_contient_son_oui (tEntree && tEntree.entree && tEntree.entree["data-name"]);
  }

function objets_autorises_carte_lecture (pTpl, pCarte, pScene = null)
  {
    const tSceneId = Session.get ("scene_id");
    const tScene = pScene || (tSceneId ? Scenes.findOne (tSceneId) : null);
    const tVue = normalise_vue_carte_client (pCarte && pCarte.vue);
    const tObjets = svg_objets_scene_normalises (tScene);
    const tDoc = pTpl && pTpl._svgDoc;
    const tRoot = tDoc && tDoc.documentElement;
    if (!tScene || !tVue || !tObjets || !tRoot) return [];

    const tMasques = new Set (ids_masques_carte_intersectant_vue (pTpl, pCarte && pCarte.ids_masques, tVue));
    const tOrdre = normalise_ordre_objets_carte_client (pCarte && pCarte.ordre_objets);
    const tOrdreParId = new Map (tOrdre.map ((pId, pIndex) => [pId, pIndex]));
    const tObjs = Array.isArray (tObjets[CALQUES_CATEGORIE_OBJ]) ? tObjets[CALQUES_CATEGORIE_OBJ] : [];

    return tObjs
      .filter ((pObj) =>
        {
          if (!pObj || !pObj.id || tMasques.has (pObj.id)) return false;
          const tConfig = get_objet_config (tScene, pObj.id);
          if (tConfig && (tConfig.calque_masque === true || !objet_visible_mode_affichage_diffusion_client (tConfig)))
            return false;
          return vue_carte_intersecte_bbox (
            tVue,
            bbox_racine_cachee_pour_id (pTpl, tRoot, pObj.id)
          );
        })
      .sort ((pA, pB) =>
        {
          const tOrdA = tOrdreParId.has (pA.id) ? tOrdreParId.get (pA.id) : Number.POSITIVE_INFINITY;
          const tOrdB = tOrdreParId.has (pB.id) ? tOrdreParId.get (pB.id) : Number.POSITIVE_INFINITY;
          if (tOrdA !== tOrdB) return tOrdA - tOrdB;
          return cle_tri_calque (pA["data-name"], CALQUES_CATEGORIE_OBJ)
            .localeCompare (cle_tri_calque (pB["data-name"], CALQUES_CATEGORIE_OBJ), "fr", {sensitivity : "base"});
        });
  }

function ids_elements_associes_obj_lecture (pScene, pObjId)
  {
    return [pObjId, ...ids_parts_associes_a_objet_scene (pScene, pObjId)]
      .filter (Boolean);
  }

function ids_masques_carte_avec_associes_scene (pIds, pScene = Scenes.findOne (Session.get ("scene_id")))
  {
    const tIds = normalise_ids_masques_carte_client (pIds);
    if (!tIds.length || !pScene) return tIds;
    const tResultat = new Set ();
    tIds.forEach ((pId) =>
      {
        if (!pId) return;
        tResultat.add (pId);
        const tEntree = entree_svg_scene_depuis_id (pScene, pId);
        if (!tEntree) return;
        if (tEntree.categorie === CALQUES_CATEGORIE_OBJ)
          ids_parts_associes_a_objet_scene (pScene, pId).forEach ((pPartId) => tResultat.add (pPartId));
      });
    return Array.from (tResultat);
  }

function noeuds_decor_calque_svg_pour_id (pDoc, pId)
  {
    const tDoc = pDoc && (pDoc.ownerDocument || pDoc);
    const tId = typeof pId === "string" ? pId : "";
    if (!tDoc || !tId) return [];
    const tMap = tDoc._cogiCalqueSvgDecorsById;
    if (tMap && typeof tMap.get === "function")
      return (tMap.get (tId) || []).filter ((pNoeud) => pNoeud && pNoeud.parentNode);
    return [];
  }

function indexe_decors_calques_svg_doc (pDoc)
  {
    const tDoc = pDoc && (pDoc.ownerDocument || pDoc);
    if (!tDoc || typeof tDoc.querySelectorAll !== "function") return;
    const tMap = new Map ();
    tDoc.querySelectorAll ("[data-cogi-calque-svg-decor-for]").forEach ((pNoeud) =>
      {
        const tId = pNoeud && pNoeud.getAttribute
          ? pNoeud.getAttribute ("data-cogi-calque-svg-decor-for")
          : "";
        if (!tId) return;
        tMap.set (tId, [...(tMap.get (tId) || []), pNoeud]);
      });
    tDoc._cogiCalqueSvgDecorsById = tMap;
  }

function id_source_decor_calque_svg (pNoeud)
  {
    let tNoeud = pNoeud;
    while (tNoeud && tNoeud.getAttribute)
      {
        const tId = tNoeud.getAttribute ("data-cogi-calque-svg-decor-for");
        if (tId) return tId;
        tNoeud = tNoeud.parentNode;
      }
    return null;
  }

function noeuds_calque_svg_et_decors_pour_id (pDoc, pId)
  {
    const tDoc = pDoc && (pDoc.ownerDocument || pDoc);
    const tId = typeof pId === "string" ? pId : "";
    if (!tDoc || !tId) return [];
    const tNoeuds = [];
    if (typeof tDoc.getElementById === "function")
      {
        const tNoeud = tDoc.getElementById (tId);
        if (tNoeud) tNoeuds.push (tNoeud);
      }
    tNoeuds.push (...noeuds_decor_calque_svg_pour_id (tDoc, tId));
    return tNoeuds.filter ((pNoeud, pIndex, pListe) => pNoeud && pListe.indexOf (pNoeud) === pIndex);
  }

function applique_aux_noeuds_calque_svg_et_decors (pDoc, pId, pCallback)
  {
    if (typeof pCallback !== "function") return;
    noeuds_calque_svg_et_decors_pour_id (pDoc, pId).forEach (pCallback);
  }

function objets_a_rejouer_carte_lecture (pTousObjets, pCarteScore)
  {
    const tReponses = (pCarteScore && pCarteScore.reponses && typeof pCarteScore.reponses === "object")
      ? pCarteScore.reponses
      : {};
    const tObjets = Array.isArray (pTousObjets) ? pTousObjets : [];
    const tPremierIndexErreur = tObjets.findIndex ((pObj) => pObj && tReponses[pObj.id] === false);
    if (tPremierIndexErreur >= 0)
      return tObjets.slice (tPremierIndexErreur).filter ((pObj) => pObj && tReponses[pObj.id] !== true);
    return tObjets.filter ((pObj) => pObj && tReponses[pObj.id] !== true);
  }

function carte_score_contient_erreur_lecture (pCarteScore)
  {
    const tReponses = (pCarteScore && pCarteScore.reponses && typeof pCarteScore.reponses === "object")
      ? pCarteScore.reponses
      : {};
    return Object.values (tReponses).some ((pReponse) => pReponse === false);
  }

function objets_deja_reussis_carte_lecture (pTousObjets, pCarteScore)
  {
    const tReponses = (pCarteScore && pCarteScore.reponses && typeof pCarteScore.reponses === "object")
      ? pCarteScore.reponses
      : {};
    return (Array.isArray (pTousObjets) ? pTousObjets : [])
      .filter ((pObj) => pObj && tReponses[pObj.id] === true);
  }

function objets_anterieurs_objet_courant_carte_lecture (pTousObjets, pObjetsARejouer)
  {
    const tTousObjets = Array.isArray (pTousObjets) ? pTousObjets : [];
    const tObjetsARejouer = Array.isArray (pObjetsARejouer) ? pObjetsARejouer : [];
    const tPremier = tObjetsARejouer.find ((pObj) => pObj && pObj.id);
    if (!tPremier) return tTousObjets.slice ();
    const tIndex = tTousObjets.findIndex ((pObj) => pObj && pObj.id === tPremier.id);
    return tIndex > 0 ? tTousObjets.slice (0, tIndex) : [];
  }

function objets_a_afficher_avec_objet_reprise_interrompue_lecture (pEtat, pObjCourant)
  {
    if (!pEtat || !pObjCourant || !pObjCourant.id) return [];
    const tTousObjets = Array.isArray (pEtat.tousObjets) ? pEtat.tousObjets : [];
    const tObjetsARejouer = Array.isArray (pEtat.objets) ? pEtat.objets : [];
    const tIndexCourantRejoue = Number (pEtat.index) || 0;
    if (tIndexCourantRejoue <= 0) return [pObjCourant];
    const tObjPrecedentRejoue = tObjetsARejouer[tIndexCourantRejoue - 1] || null;
    const tIndexPrecedent = tTousObjets.findIndex ((pObj) => pObj && tObjPrecedentRejoue && pObj.id === tObjPrecedentRejoue.id);
    const tIndexCourant = tTousObjets.findIndex ((pObj) => pObj && pObj.id === pObjCourant.id);
    if (tIndexPrecedent < 0 || tIndexCourant < 0 || tIndexCourant <= tIndexPrecedent)
      return [pObjCourant];
    return tTousObjets.slice (tIndexPrecedent + 1, tIndexCourant + 1);
  }

function nombre_bonnes_reponses_carte_lecture (pObjets, pCarteScore)
  {
    const tReponses = (pCarteScore && pCarteScore.reponses && typeof pCarteScore.reponses === "object")
      ? pCarteScore.reponses
      : {};
    return (Array.isArray (pObjets) ? pObjets : [])
      .filter ((pObj) => pObj && tReponses[pObj.id] === true)
      .length;
  }

let gLectureCarteProgressAnim = null;

function anime_progression_carte_lecture (pProgress)
  {
    const tCible = Math.max (0, Math.min (1, Number (pProgress) || 0));
    const tDepart = Math.max (0, Math.min (1, Number (gLectureCarteProgress.get ()) || 0));
    if (gLectureCarteProgressRaf)
      {
        cancelAnimationFrame (gLectureCarteProgressRaf);
        gLectureCarteProgressRaf = null;
      }
    if (Math.abs (tCible - tDepart) < 0.001)
      {
        gLectureCarteProgress.set (tCible);
        return;
      }
    const tAnim = {
      debut : (typeof performance !== "undefined" && performance.now) ? performance.now () : Date.now (),
      duree : 420,
    };
    if (gLectureCarteProgressAnim) gLectureAnimationsJsAccelerables.delete (gLectureCarteProgressAnim);
    gLectureCarteProgressAnim = tAnim;
    gLectureAnimationsJsAccelerables.add (tAnim);
    const step = (pNow) =>
      {
        if (gLectureCarteProgressAnim !== tAnim) return;
        const t = Math.max (0, Math.min (1, ((Number (pNow) || Date.now ()) - tAnim.debut) / tAnim.duree));
        const tEase = 1 - Math.pow (1 - t, 3);
        gLectureCarteProgress.set (tDepart + ((tCible - tDepart) * tEase));
        if (t < 1)
          {
            gLectureCarteProgressRaf = requestAnimationFrame (step);
            return;
          }
        gLectureCarteProgressRaf = null;
        gLectureAnimationsJsAccelerables.delete (tAnim);
        gLectureCarteProgressAnim = null;
        gLectureCarteProgress.set (tCible);
      };
    gLectureCarteProgressRaf = requestAnimationFrame (step);
  }

function actualise_progression_carte_lecture (pObjets, pCarteScore, pOptions = {})
  {
    const tTotal = Math.max (0, (Array.isArray (pObjets) ? pObjets.length : 0));
    const setProgress = (pProgress) =>
      {
        if (pOptions && pOptions.anime) anime_progression_carte_lecture (pProgress);
        else
          {
            if (gLectureCarteProgressRaf)
              {
                cancelAnimationFrame (gLectureCarteProgressRaf);
                gLectureCarteProgressRaf = null;
              }
            gLectureCarteProgress.set (pProgress);
          }
      };
    if (tTotal <= 0)
      {
        setProgress (0);
        return;
      }
    setProgress (
      Math.max (0, Math.min (1, nombre_bonnes_reponses_carte_lecture (pObjets, pCarteScore) / tTotal))
    );
  }

function nettoie_timers_pedago_lecture_tpl (pTpl)
  {
    if (!pTpl || !Array.isArray (pTpl._lecturePedagoTimers)) return;
    pTpl._lecturePedagoTimers.forEach ((pTimer) => clearTimeout (pTimer));
    pTpl._lecturePedagoTimers = [];
  }

function timer_pedago_lecture_tpl (pTpl, pCallback, pDelaiMs)
  {
    if (!pTpl) return null;
    if (!Array.isArray (pTpl._lecturePedagoTimers)) pTpl._lecturePedagoTimers = [];
    const tTimer = setTimeout (() =>
      {
        pTpl._lecturePedagoTimers = (pTpl._lecturePedagoTimers || []).filter ((pItem) => pItem !== tTimer);
        pCallback ();
      }, Math.max (0, Number (pDelaiMs) || 0));
    pTpl._lecturePedagoTimers.push (tTimer);
    return tTimer;
  }

function enregistre_nettoyage_contour_lecture_tpl (pTpl, pCleanup)
  {
    if (!pTpl || typeof pCleanup !== "function") return pCleanup;
    if (!Array.isArray (pTpl._lectureContourCleanups)) pTpl._lectureContourCleanups = [];
    let tActif = true;
    const tCleanup = (pImmediat = false) =>
      {
        if (!tActif) return;
        tActif = false;
        pTpl._lectureContourCleanups = (pTpl._lectureContourCleanups || [])
          .filter ((pItem) => pItem !== tCleanup);
        pCleanup (pImmediat);
      };
    pTpl._lectureContourCleanups.push (tCleanup);
    return tCleanup;
  }

function enregistre_noeud_contour_lecture_tpl (pTpl, pNoeud)
  {
    if (!pTpl || !pNoeud) return;
    if (!Array.isArray (pTpl._lectureContourOverlayNodes)) pTpl._lectureContourOverlayNodes = [];
    pTpl._lectureContourOverlayNodes.push (pNoeud);
  }

function nettoie_contours_lecture_tpl (pTpl, pImmediat = true)
  {
    if (!pTpl) return;
    if (pImmediat) pTpl._lectureContourGeneration = (Number (pTpl._lectureContourGeneration) || 0) + 1;
    if (Array.isArray (pTpl._lectureContourCleanups))
      {
        const tCleanups = pTpl._lectureContourCleanups.slice ();
        if (pImmediat) pTpl._lectureContourCleanups = [];
        tCleanups.forEach ((pCleanup) =>
          {
            if (typeof pCleanup === "function") pCleanup (!!pImmediat);
          });
      }
    if (pImmediat && Array.isArray (pTpl._lectureContourOverlayNodes))
      {
        const tNoeuds = pTpl._lectureContourOverlayNodes.slice ();
        pTpl._lectureContourOverlayNodes = [];
        tNoeuds.forEach ((pNoeud) =>
          {
            if (pNoeud && pNoeud.parentNode) pNoeud.parentNode.removeChild (pNoeud);
          });
      }
    const tDoc = pTpl._svgDoc || null;
    if (pImmediat && tDoc && typeof tDoc.querySelectorAll === "function")
      {
        Array.from (tDoc.querySelectorAll ("[data-cogi-lecture-contour-overlay='1']")).forEach ((pNoeud) =>
          {
            if (pNoeud && pNoeud.parentNode) pNoeud.parentNode.removeChild (pNoeud);
          });
      }
  }

function applique_visibilite_element_lecture (pNoeud, pVisible, pDureeMs = 0)
  {
    if (!pNoeud || !pNoeud.style) return Promise.resolve ();
    pNoeud._cogiLecturePedagoVisibiliteToken = (Number (pNoeud._cogiLecturePedagoVisibiliteToken) || 0) + 1;
    const tToken = pNoeud._cogiLecturePedagoVisibiliteToken;
    if (!pNoeud._cogiLecturePedagoInitial)
      {
        pNoeud._cogiLecturePedagoInitial = {
          visibility : pNoeud.style.visibility || "",
          opacity : pNoeud.style.opacity || "",
          transition : pNoeud.style.transition || "",
          pointerEvents : pNoeud.style.pointerEvents || "",
        };
      }
    const tDuree = Math.max (0, Number (pDureeMs) || 0);
    pNoeud.style.transition = tDuree > 0 ? `opacity ${tDuree}ms ease` : "";
    pNoeud.style.pointerEvents = pVisible ? "auto" : "none";
    if (pVisible)
      {
        pNoeud.style.visibility = "visible";
        pNoeud.style.opacity = "0";
        return new Promise ((pResolve) =>
          {
            requestAnimationFrame (() =>
              {
                if (!pNoeud.parentNode) { pResolve (); return; }
                if (pNoeud._cogiLecturePedagoVisibiliteToken !== tToken) { pResolve (); return; }
                pNoeud.style.opacity = "1";
                setTimeout (() =>
                  {
                    if (pNoeud._cogiLecturePedagoVisibiliteToken === tToken) pResolve ();
                    else pResolve ();
                  }, tDuree + 50);
              });
          });
      }
    pNoeud.style.opacity = "0";
    if (tDuree <= 0) pNoeud.style.visibility = "hidden";
    return new Promise ((pResolve) =>
      {
        setTimeout (() =>
          {
            if (pNoeud.parentNode && pNoeud._cogiLecturePedagoVisibiliteToken === tToken)
              pNoeud.style.visibility = "hidden";
            pResolve ();
          }, tDuree + 50);
      });
  }

function restaure_elements_pedago_lecture_tpl (pTpl)
  {
    const tIds = pTpl && pTpl._lecturePedagoHiddenIds;
    const tDoc = pTpl && pTpl._svgDoc;
    if (!(tIds instanceof Set) || !tDoc) return;
    tIds.forEach ((pId) =>
      {
        applique_aux_noeuds_calque_svg_et_decors (tDoc, pId, (tNoeud) =>
          {
            const tInitial = tNoeud && tNoeud._cogiLecturePedagoInitial;
            if (!tNoeud || !tNoeud.style || !tInitial) return;
            tNoeud.style.visibility = tInitial.visibility;
            tNoeud.style.opacity = tInitial.opacity;
            tNoeud.style.transition = tInitial.transition;
            tNoeud.style.pointerEvents = tInitial.pointerEvents;
            delete tNoeud._cogiLecturePedagoInitial;
            delete tNoeud._cogiLecturePedagoVisibiliteToken;
          });
      });
    pTpl._lecturePedagoHiddenIds = new Set ();
  }

function masque_elements_pedago_carte_lecture (pTpl, pScene, pObjets)
  {
    const tDoc = pTpl && pTpl._svgDoc;
    if (!tDoc) return;
    restaure_elements_pedago_lecture_tpl (pTpl);
    pTpl._lecturePedagoHiddenIds = new Set ();
    (Array.isArray (pObjets) ? pObjets : []).forEach ((pObj) =>
      {
        ids_elements_associes_obj_lecture (pScene, pObj && pObj.id).forEach ((pId) =>
          {
            pTpl._lecturePedagoHiddenIds.add (pId);
            applique_aux_noeuds_calque_svg_et_decors (tDoc, pId, (tNoeud) =>
              applique_visibilite_element_lecture (tNoeud, false, 0));
          });
      });
  }

function affiche_elements_pedago_carte_lecture (pTpl, pScene, pObjets, pDureeMs = 0)
  {
    const tDoc = pTpl && pTpl._svgDoc;
    if (!tDoc) return;
    const tDuree = Math.max (0, Number (pDureeMs) || 0);
    (Array.isArray (pObjets) ? pObjets : []).forEach ((pObj) =>
      {
        ids_elements_associes_obj_lecture (pScene, pObj && pObj.id).forEach ((pId) =>
          {
            if (pTpl._lecturePedagoHiddenIds instanceof Set)
              pTpl._lecturePedagoHiddenIds.add (pId);
            applique_aux_noeuds_calque_svg_et_decors (tDoc, pId, (tNoeud) =>
              applique_visibilite_element_lecture (tNoeud, true, tDuree));
          });
      });
  }

function affiche_objet_pedago_lecture (pTpl, pScene, pObjId)
  {
    const tDoc = pTpl && pTpl._svgDoc;
    if (!tDoc || !pObjId) return Promise.resolve ();
    const tFadeMs = 450;
    return Promise.all (
      ids_elements_associes_obj_lecture (pScene, pObjId)
        .flatMap ((pId) => noeuds_calque_svg_et_decors_pour_id (tDoc, pId))
        .map ((pNoeud) => applique_visibilite_element_lecture (pNoeud, true, tFadeMs))
    );
  }

function reapplique_etat_pedago_lecture_tpl (pTpl)
  {
    const tEtat = pTpl && pTpl._lecturePedago;
    const tDoc = pTpl && pTpl._svgDoc;
    if (!tEtat || !tEtat.actif || !tDoc) return false;
    if (tEtat.affichageIntegralNouvelEssai) return true;

    const tScene = tEtat.sceneId ? Scenes.findOne (tEtat.sceneId) : null;
    masque_elements_pedago_carte_lecture (pTpl, tScene, tEtat.tousObjets);

    const tVisibles = [];
    if (Array.isArray (tEtat.objetsContexte))
      tVisibles.push (...tEtat.objetsContexte);

    const tIndex = Number.isFinite (Number (tEtat.index)) ? Number (tEtat.index) : -1;
    const tMaxIndexVisible = (tIndex === 0 && tEtat.attente && !tEtat.objetPremierAffiche)
      ? -1
      : tIndex;
    if (tMaxIndexVisible >= 0 && Array.isArray (tEtat.objets))
      tVisibles.push (...tEtat.objets.slice (0, tMaxIndexVisible + 1));

    affiche_elements_pedago_carte_lecture (pTpl, tScene, tVisibles, 0);
    return true;
  }

function overlay_message_scene_lecture (pTpl, pMessage)
  {
    const tScene = element_cadre_scene_tpl (pTpl || gSceneTemplateActif);
    if (!tScene || !pMessage) return Promise.resolve ();
    const tDoc = tScene.ownerDocument || document;
    const tOverlay = tDoc.createElement ("div");
    tOverlay.className = "lecture_pedago_overlay";
    tOverlay.textContent = pMessage;
    tScene.appendChild (tOverlay);
    return new Promise ((pResolve) =>
      {
        requestAnimationFrame (() =>
          {
            tOverlay.classList.add ("est_visible");
            setTimeout (() =>
              {
                tOverlay.classList.remove ("est_visible");
                setTimeout (() =>
                  {
                    if (tOverlay.parentNode) tOverlay.parentNode.removeChild (tOverlay);
                    pResolve ();
                  }, 450);
              }, 2000);
          });
      });
  }

function valeur_reglage_widget_hype (pReglages, pType, pSuffixe)
  {
    const tReglages = pReglages || {};
    return tReglages[cle_reglage_widget_hype (pType, pSuffixe)];
  }

function position_css_widget_hype (pReglages, pType = "recompense_carte")
  {
    const tAlignX = valeur_reglage_widget_hype (pReglages, pType, "align_x");
    const tAlignY = valeur_reglage_widget_hype (pReglages, pType, "align_y");
    const xBase = tAlignX === "gauche"
      ? "0%"
      : tAlignX === "droite" ? "100%" : "50%";
    const yBase = tAlignY === "haut"
      ? "0%"
      : tAlignY === "bas" ? "100%" : "50%";
    const xTranslate = tAlignX === "gauche"
      ? "0%"
      : tAlignX === "droite" ? "-100%" : "-50%";
    const yTranslate = tAlignY === "haut"
      ? "0%"
      : tAlignY === "bas" ? "-100%" : "-50%";
    return {
      left : `calc(${xBase} + ${Number (valeur_reglage_widget_hype (pReglages, pType, "offset_x_pct")) || 0}%)`,
      top : `calc(${yBase} + ${Number (valeur_reglage_widget_hype (pReglages, pType, "offset_y_pct")) || 0}%)`,
      transform : `translate(${xTranslate}, ${yTranslate})`,
    };
  }

function fond_css_widget_hype (pReglages = reglages_mode_lecture_scene (), pType = "recompense_carte")
  {
    return pReglages && valeur_reglage_widget_hype (pReglages, pType, "fond_transparent")
      ? "transparent"
      : (valeur_reglage_widget_hype (pReglages, pType, "couleur_fond") || "#ffffff");
  }

function style_apercu_widget_hype (pType = "recompense_carte", pReglages = brouillon_reglages_mode_lecture_projet ())
  {
    const tFond = fond_css_widget_hype (pReglages, pType);
    return "position:relative;width:100%;border:1px solid rgba(230, 230, 230, 0.44);margin-block:1rem;border-radius:0.4rem;aspect-ratio:16/9;overflow:hidden;background:" + tFond + ";background-image:none;";
  }

const gHypeDirectIndexCache = new Map ();

function host_apercu_hype_inspecteur (pType = "recompense_carte", pSource = null)
  {
    const tType = pType === "recompense_scene" ? "recompense_scene" : "recompense_carte";
    const tBloc = pSource && typeof pSource.closest === "function"
      ? pSource.closest (".inspecteur_hype_widget")
      : null;
    const tLocal = tBloc && tBloc.querySelector ('.js_inspecteur_hype_preview_host[data-type="' + tType + '"]');
    if (tLocal) return tLocal;
    return document.querySelector ('.js_inspecteur_hype_preview_host[data-type="' + tType + '"]');
  }

function nombre_px_style_hype (pValeur, pFallback)
  {
    const tMatch = String (pValeur || "").match (/(-?\d+(?:\.\d+)?)px/i);
    const tValeur = tMatch ? Number (tMatch[1]) : NaN;
    return Number.isFinite (tValeur) && tValeur > 0 ? tValeur : pFallback;
  }

function echappe_regex_hype_direct (pValeur)
  {
    return String (pValeur || "").replace (/[.*+?^${}()|[\]\\]/g, "\\$&");
  }

function chaine_js_hype_direct (pValeur)
  {
    return JSON.stringify (String (pValeur || ""));
  }

function script_widget_hype_instance (pInfo, pDocumentName, pContainerId)
  {
    const tSource = String (pInfo && pInfo.scriptSource || "");
    const tResourceBase = String (pInfo && pInfo.resourceBase || "").replace (/\/+$/, "");
    const tOriginalDocument = String (pInfo && pInfo.documentName || "");
    const tOriginalContainer = String (pInfo && pInfo.containerId || "");
    if (!tSource || !tOriginalDocument || !tOriginalContainer || !tResourceBase)
      throw new Error ("Script Hype incomplet");
    const tVarPattern = new RegExp (
      'var\\s+l="(?:\\\\.|[^"])*",f='
      + echappe_regex_hype_direct (chaine_js_hype_direct (tOriginalDocument))
      + ',g='
      + echappe_regex_hype_direct (chaine_js_hype_direct (tOriginalContainer))
    );
    const tVarReplacement = "var l=" + chaine_js_hype_direct (tResourceBase)
      + ",f=" + chaine_js_hype_direct (pDocumentName)
      + ",g=" + chaine_js_hype_direct (pContainerId);
    const tReecrit = tSource.replace (tVarPattern, tVarReplacement);
    if (tReecrit === tSource)
      throw new Error ("Script Hype non réécrit");
    return "(function(){\n"
      + "try{\n"
      + "if(!document.getElementById(" + chaine_js_hype_direct (pContainerId) + ")) return;\n"
      + tReecrit
      + "\n}catch(e){\n"
      + "try{window.dispatchEvent(new CustomEvent('cogibulle:hype-instance-error',{detail:{containerId:"
      + chaine_js_hype_direct (pContainerId)
      + ",message:(e&&(e.message||e.reason))||String(e)}}));}catch(_e){console.warn('[lecture] Hype instance error',e);}\n"
      + "}\n})();";
  }

async function infos_widget_hype_direct (pWidget, pForcer = false)
  {
    const tUrl = url_widget_hype_client (pWidget);
    if (!tUrl) throw new Error ("URL Hype absente");
    if (!pForcer && gHypeDirectIndexCache.has (tUrl)) return gHypeDirectIndexCache.get (tUrl);

    const tIndexRes = await fetch (tUrl + (tUrl.includes ("?") ? "&" : "?") + "hub_index=" + Date.now (), {cache : "no-store"});
    if (!tIndexRes.ok) throw new Error ("Index Hype introuvable");
    const tHtml = await tIndexRes.text ();
    const tDoc = new DOMParser ().parseFromString (tHtml, "text/html");
    const tScript = tDoc.querySelector ('script[src*="hype_generated_script"]');
    const tContainer = tScript && (tScript.closest (".HYPE_document") || tScript.parentElement);
    if (!tScript || !tContainer || !tContainer.id)
      throw new Error ("Structure Hype exportée non reconnue");

    const tScriptUrl = new URL (tScript.getAttribute ("src"), tUrl).href;
    const tScriptRes = await fetch (tScriptUrl + (tScriptUrl.includes ("?") ? "&" : "?") + "hub_script=" + Date.now (), {cache : "no-store"});
    if (!tScriptRes.ok) throw new Error ("Script Hype introuvable");
    const tScriptSource = await tScriptRes.text ();
    const tStyle = tContainer.getAttribute ("style") || "";
    const tDocumentNameMatch = tScriptSource.match (/HYPE\.documents\["([^"]+)"\]/)
      || tScriptSource.match (/var\s+l="(?:\\.|[^"])*",f="((?:\\.|[^"])*)",g="/);
    const tInfo =
      {
        indexUrl : tUrl,
        scriptUrl : tScriptUrl,
        scriptSource : tScriptSource,
        documentName : tDocumentNameMatch ? tDocumentNameMatch[1] : (pWidget.nom || "Widget Hype"),
        resourceBase : tScriptUrl.replace (/\/[^/]*$/, ""),
        containerId : tContainer.id,
        containerClass : tContainer.getAttribute ("class") || "HYPE_document",
        containerStyle : tStyle,
        width : nombre_px_style_hype (tStyle.match (/width\s*:\s*[^;]+/i), 1920),
        height : nombre_px_style_hype (tStyle.match (/height\s*:\s*[^;]+/i), 1080),
      };
    gHypeDirectIndexCache.set (tUrl, tInfo);
    return tInfo;
  }

function affiche_erreur_hype_direct (pHost, pErreur)
  {
    if (!pHost) return;
    if (!nettoie_widget_hype_direct (pHost)) return;
    const tMsg = document.createElement ("div");
    tMsg.className = "inspecteur_message inspecteur_message_erreur hub_hype_embed_error";
    tMsg.textContent = "Chargement Hype impossible : " + (pErreur && (pErreur.reason || pErreur.message) || pErreur || "erreur inconnue");
    pHost.appendChild (tMsg);
  }

function nouveau_token_hype_direct (pPrefixe = "hype")
  {
    return pPrefixe + "_" + Date.now ().toString (36) + "_" + Math.random ().toString (36).slice (2);
  }

function appelle_apres_nettoyage_hype_direct (pCallback)
  {
    if (typeof pCallback !== "function") return;
    try { pCallback (); } catch (e) { console.warn ("Nettoyage Hype incomplet", e); }
  }

function microtache_hype_direct (pCallback)
  {
    if (typeof queueMicrotask === "function") queueMicrotask (pCallback);
    else Promise.resolve ().then (pCallback);
  }

function retire_listener_hype_direct (pListener)
  {
    if (!pListener || !Array.isArray (window.HYPE_eventListeners)) return;
    const tIndex = window.HYPE_eventListeners.indexOf (pListener);
    if (tIndex >= 0) window.HYPE_eventListeners.splice (tIndex, 1);
  }

function element_hype_direct_correspond_conteneur (pElement, pContainer)
  {
    return !!(pElement && pContainer
      && (pElement === pContainer || (typeof pContainer.contains === "function" && pContainer.contains (pElement))));
  }

function document_hype_direct_utilisable (pDocument)
  {
    const tHost = pDocument && pDocument._cogibulleHypeHost;
    if (!tHost) return true;
    return tHost._hubHypeDocument === pDocument
      && tHost._hubHypeDisposing !== true
      && tHost.isConnected !== false;
  }

function nettoie_widget_hype_direct (pHost, pOptions = {})
  {
    if (!pHost) return true;
    const tApresNettoyage = pOptions && typeof pOptions.apresNettoyage === "function"
      ? pOptions.apresNettoyage
      : null;
    if (typeof pHost._hubHypeCleanup === "function")
      return pHost._hubHypeCleanup (tApresNettoyage) !== false;
    pHost._hubHypeToken = nouveau_token_hype_direct ("hype_cancelled");
    pHost._hubHypeDocument = null;
    pHost._hubHypeDisposing = false;
    pHost._hubHypeLoadingSignature = null;
    pHost._itemHypeWidgetId = null;
    pHost._itemHypeAutoplayApplique = null;
    pHost.style.removeProperty ("visibility");
    pHost.style.removeProperty ("pointer-events");
    pHost.innerHTML = "";
    appelle_apres_nettoyage_hype_direct (tApresNettoyage);
    return true;
  }

function attend_nettoyage_widget_hype_direct (pHost)
  {
    if (!pHost) return Promise.resolve (false);
    if (typeof pHost._hubHypeCleanup !== "function")
      {
        nettoie_widget_hype_direct (pHost);
        return Promise.resolve (true);
      }
    let tResolve;
    const tAttente = new Promise ((pResolve) => { tResolve = pResolve; });
    const tNettoye = nettoie_widget_hype_direct (
      pHost,
      {apresNettoyage : () => tResolve (true)}
    );
    return tNettoye ? Promise.resolve (true) : tAttente;
  }

async function monte_widget_hype_direct_dom (pHost, pWidget, pOptions = {})
  {
    if (!pHost || !pWidget) return null;
    const tSignatureChargementInitiale = pHost._hubHypeLoadingSignature || null;
    const tWidgetItemInitial = pHost._itemHypeWidgetId || null;
    await attend_nettoyage_widget_hype_direct (pHost);
    if (pHost.isConnected === false) return null;
    if (tSignatureChargementInitiale && !pHost._hubHypeLoadingSignature)
      pHost._hubHypeLoadingSignature = tSignatureChargementInitiale;
    if (tWidgetItemInitial && !pHost._itemHypeWidgetId)
      pHost._itemHypeWidgetId = tWidgetItemInitial;
    const tToken = nouveau_token_hype_direct ();
    pHost._hubHypeToken = tToken;
    const tInfo = await infos_widget_hype_direct (pWidget, !!pOptions.forcer);
    if (pHost._hubHypeToken !== tToken) return null;

    pHost._hubHypeToken = tToken;
    pHost._hubHypeDisposing = false;
    pHost.innerHTML = "";
    pHost.style.removeProperty ("visibility");
    pHost.style.removeProperty ("pointer-events");
    pHost.classList.add ("hub_hype_embed_host");

    const tDoc = pHost.ownerDocument || document;
    const tStage = tDoc.createElement ("div");
    const tContainer = tDoc.createElement ("div");
    const tScript = tDoc.createElement ("script");
    const tInstance = ((pOptions.instanceKey || "preview") + "_" + tToken).replace (/[^a-zA-Z0-9_-]/g, "_");
    const tContainerId = tInfo.containerId + "_" + tInstance;
    const tDocumentName = (tInfo.documentName || (pWidget.nom || "Widget Hype")) + "_" + tInstance;
    let tResizeObserver = null;
    let tHypeListener = null;
    let tHypeTimelineListener = null;
    let tHypeSceneLoadListener = null;
    let tScriptObjectUrl = null;
    let tDocumentHype = null;
    let tChargeTerminee = false;
    let resolveChargement = null;
    let rejectChargement = null;
    let tTimeoutChargement = null;
    const tEtatCycle =
      {
        documentLoadRecu : false,
        echecChargement : false,
        nettoyageDemande : false,
        finalise : false,
        callbacks : [],
      };
    const tChargement = new Promise ((pResolve, pReject) =>
      {
        resolveChargement = pResolve;
        rejectChargement = pReject;
      });
    const nettoie_ecouteurs_chargement = () =>
      {
        if (tTimeoutChargement)
          {
            clearTimeout (tTimeoutChargement);
            tTimeoutChargement = null;
          }
        window.removeEventListener ("cogibulle:hype-instance-error", on_erreur_instance_hype);
      };
    const on_erreur_instance_hype = (pEvent) =>
      {
        const tDetail = pEvent && pEvent.detail;
        if (!tDetail || tDetail.containerId !== tContainerId) return;
        echoue_chargement (new Error (tDetail.message || "script Hype interrompu"));
        if (tEtatCycle.nettoyageDemande) finalise_nettoyage ();
      };

    const resize = () =>
      {
        const tW = pHost.clientWidth || tInfo.width;
        const tH = pHost.clientHeight || (tW * tInfo.height / tInfo.width);
        const tScale = Math.min (tW / tInfo.width, tH / tInfo.height);
        tContainer.style.width = tInfo.width + "px";
        tContainer.style.height = tInfo.height + "px";
        tContainer.style.transform = "scale(" + tScale + ")";
        tContainer.style.transformOrigin = "0 0";
        tContainer.style.left = Math.max (0, (tW - tInfo.width * tScale) / 2) + "px";
        tContainer.style.top = Math.max (0, (tH - tInfo.height * tScale) / 2) + "px";
      };
    const termine_chargement = (pDocument) =>
      {
        if (tChargeTerminee) return;
        tChargeTerminee = true;
        nettoie_ecouteurs_chargement ();
        resolveChargement (pDocument || null);
      };
    const echoue_chargement = (pErreur) =>
      {
        tEtatCycle.echecChargement = true;
        if (!tChargeTerminee)
          {
            tChargeTerminee = true;
            nettoie_ecouteurs_chargement ();
            rejectChargement (pErreur);
          }
      };
    const ajoute_callback_nettoyage = (pCallback) =>
      {
        if (typeof pCallback === "function") tEtatCycle.callbacks.push (pCallback);
      };
    const vide_callbacks_nettoyage = () =>
      {
        const tCallbacks = tEtatCycle.callbacks.splice (0);
        tCallbacks.forEach (appelle_apres_nettoyage_hype_direct);
      };
    const finalise_nettoyage = () =>
      {
        if (tEtatCycle.finalise)
          {
            vide_callbacks_nettoyage ();
            return true;
          }
        tEtatCycle.finalise = true;
        if (tResizeObserver) tResizeObserver.disconnect ();
        else window.removeEventListener ("resize", resize);
        retire_listener_hype_direct (tHypeListener);
        retire_listener_hype_direct (tHypeTimelineListener);
        retire_listener_hype_direct (tHypeSceneLoadListener);
        try
          {
            if (tDocumentHype && typeof tDocumentHype.pauseTimelineNamed === "function")
              tDocumentHype.pauseTimelineNamed ("Main Timeline");
          }
        catch (e) {}
        if (tScriptObjectUrl)
          {
            URL.revokeObjectURL (tScriptObjectUrl);
            tScriptObjectUrl = null;
	          }
        nettoie_ecouteurs_chargement ();
        if (window.HYPE && window.HYPE.documents && window.HYPE.documents[tDocumentName] === tDocumentHype)
          delete window.HYPE.documents[tDocumentName];
        if (tDocumentHype && tDocumentHype._cogibulleHypeHost === pHost)
          {
            try { delete tDocumentHype._cogibulleHypeHost; } catch (e) { tDocumentHype._cogibulleHypeHost = null; }
          }
        pHost._hubHypeCleanup = null;
        pHost._hubHypeDocument = null;
        pHost._hubHypeDisposing = false;
        pHost._hubHypeLoadingSignature = null;
        pHost._itemHypeWidgetId = null;
        pHost._itemHypeAutoplayApplique = null;
        pHost.style.removeProperty ("visibility");
        pHost.style.removeProperty ("pointer-events");
        pHost.innerHTML = "";
        vide_callbacks_nettoyage ();
        return true;
      };

    tStage.className = "hub_hype_embed_stage";
    tContainer.className = tInfo.containerClass + " hub_hype_embed_document";
    tContainer.id = tContainerId;
    tContainer.setAttribute ("style", tInfo.containerStyle);
    tContainer.style.margin = "0";
    tContainer.style.position = "absolute";
    tContainer.style.overflow = "hidden";

    tHypeListener =
      {
        type : "HypeDocumentLoad",
        callback : (pDocument, pElement) =>
          {
            if (!pDocument || !element_hype_direct_correspond_conteneur (pElement, tContainer)) return;
            tEtatCycle.documentLoadRecu = true;
            tDocumentHype = pDocument;
            pDocument._cogibulleHypeHost = pHost;
            pHost._hubHypeDocument = pDocument;
            if (tEtatCycle.nettoyageDemande)
              {
                termine_chargement (null);
                microtache_hype_direct (finalise_nettoyage);
                return false;
              }
            termine_chargement (pDocument);
          },
      };
    window.HYPE_eventListeners = window.HYPE_eventListeners || [];
    window.HYPE_eventListeners.push (tHypeListener);
    tHypeSceneLoadListener =
      {
        type : "HypeSceneLoad",
        callback : (pDocument) =>
          {
            if (pDocument === tDocumentHype && tEtatCycle.nettoyageDemande)
              microtache_hype_direct (finalise_nettoyage);
          },
      };
    window.HYPE_eventListeners.push (tHypeSceneLoadListener);
    tHypeTimelineListener =
      {
        type : "HypeTimelineComplete",
        callback : (pDocument, pElement, pEvent) =>
          {
            if (!(pHost._hubHypeLoop || pHost._hubHypeManualLoop) || !document_hype_direct_utilisable (pDocument) || pDocument !== pHost._hubHypeDocument) return;
            const tTimeline = pEvent && pEvent.timelineName ? pEvent.timelineName : "Main Timeline";
            if (tTimeline !== "Main Timeline") return;
            commande_timeline_hype (pHost._hubHypeDocument, "restart");
          },
      };
    window.HYPE_eventListeners.push (tHypeTimelineListener);
    window.addEventListener ("cogibulle:hype-instance-error", on_erreur_instance_hype);

    tScript.type = "text/javascript";
    tScript.charset = "utf-8";
    tScriptObjectUrl = URL.createObjectURL (
      new Blob (
        [
          script_widget_hype_instance (tInfo, tDocumentName, tContainerId),
          "\n//# sourceURL=" + tInfo.scriptUrl + "?hub_direct_instance=" + encodeURIComponent (tInstance),
        ],
        {type : "text/javascript"}
      )
    );
    tScript.src = tScriptObjectUrl;
    tScript.onerror = () =>
      {
        if (tChargeTerminee) return;
        echoue_chargement (new Error ("script Hype introuvable ou bloqué"));
        if (tEtatCycle.nettoyageDemande) finalise_nettoyage ();
      };
    tTimeoutChargement = setTimeout (() =>
      {
        if (tChargeTerminee) return;
        if (pHost._hubHypeToken !== tToken || tEtatCycle.nettoyageDemande)
          {
            termine_chargement (null);
            return;
          }
        echoue_chargement (new Error ("script Hype sans événement de chargement"));
      }, 3000);

    tStage.appendChild (tContainer);
    pHost.appendChild (tStage);
    tContainer.appendChild (tScript);
    resize ();
    if (typeof ResizeObserver !== "undefined")
      {
        tResizeObserver = new ResizeObserver (resize);
        tResizeObserver.observe (pHost);
      }
    else
      window.addEventListener ("resize", resize);

    pHost._hubHypeCleanup = (pApresNettoyage = null) =>
      {
        ajoute_callback_nettoyage (pApresNettoyage);
        tEtatCycle.nettoyageDemande = true;
        pHost._hubHypeDisposing = true;
        pHost.style.visibility = "hidden";
        pHost.style.pointerEvents = "none";
        if (!tEtatCycle.documentLoadRecu && !tEtatCycle.echecChargement)
          return false;
        return finalise_nettoyage ();
      };
    return await tChargement;
  }

async function monte_widget_hype_direct (pHost, pWidget, pOptions = {})
  {
    return await monte_widget_hype_direct_dom (pHost, pWidget, pOptions);
  }

function direction_avant_hype (pDocument)
  {
    if (pDocument && pDocument.kDirectionForward !== undefined) return pDocument.kDirectionForward;
    return 1;
  }

function commande_timeline_hype (pDocument, pAction)
  {
    const tTimeline = "Main Timeline";
    if (!pDocument || !document_hype_direct_utilisable (pDocument)) return false;
    try
      {
        if (pAction === "play")
          {
            if (typeof pDocument.continueTimelineNamed === "function")
              {
                pDocument.continueTimelineNamed (tTimeline, direction_avant_hype (pDocument));
                return true;
              }
            if (typeof pDocument.startTimelineNamed === "function")
              {
                pDocument.startTimelineNamed (tTimeline, direction_avant_hype (pDocument));
                return true;
              }
          }
        if (pAction === "restart")
          {
            if (typeof pDocument.pauseTimelineNamed === "function") pDocument.pauseTimelineNamed (tTimeline);
            if (typeof pDocument.goToTimeInTimelineNamed === "function") pDocument.goToTimeInTimelineNamed (0, tTimeline);
            if (typeof pDocument.continueTimelineNamed === "function")
              {
                pDocument.continueTimelineNamed (tTimeline, direction_avant_hype (pDocument));
                return true;
              }
            if (typeof pDocument.startTimelineNamed === "function")
              {
                pDocument.startTimelineNamed (tTimeline, direction_avant_hype (pDocument));
                return true;
              }
          }
        if (pAction === "pause" && typeof pDocument.pauseTimelineNamed === "function")
          {
            pDocument.pauseTimelineNamed (tTimeline);
            return true;
          }
        if (pAction === "stop")
          {
            if (typeof pDocument.pauseTimelineNamed === "function") pDocument.pauseTimelineNamed (tTimeline);
            if (typeof pDocument.goToTimeInTimelineNamed === "function")
              {
                pDocument.goToTimeInTimelineNamed (0, tTimeline);
                return true;
              }
          }
      }
    catch (e)
      {
        console.warn ("Commande Hype impossible", pAction, e);
      }
    return false;
  }

function overlay_widget_hype_recompense_scene_lecture (pTpl, pType = "recompense_carte", pPlaceholder = "Bravo ! Animation à venir...")
  {
    const tType = type_widget_hype_normalise (pType);
    const tWidget = widget_hype_projet (tType);
    const tUrl = url_widget_hype_client (tWidget);
    if (!tUrl) return overlay_message_scene_lecture (pTpl, pPlaceholder);

    const tScene = element_cadre_scene_tpl (pTpl || gSceneTemplateActif);
    if (!tScene) return Promise.resolve ();
    const tDoc = tScene.ownerDocument || document;
    const tReglages = reglages_mode_lecture_scene ();
    const tOverlay = tDoc.createElement ("div");
    const tFrameWrap = tDoc.createElement ("div");
    const tPosition = position_css_widget_hype (tReglages, tType);
    const tFond = fond_css_widget_hype (tReglages, tType);
    const tTaillePct = Math.max (5, Math.min (100, Number (valeur_reglage_widget_hype (tReglages, tType, "taille_pct")) || 60));
    const tFadeMs = Math.max (0, Number (valeur_reglage_widget_hype (tReglages, tType, "fadeout_ms")) || 0);
    const tDelaiMs = Math.max (0, Number (valeur_reglage_widget_hype (tReglages, tType, "disparition_ms")) || 0);

    tOverlay.className = "lecture_hype_overlay";
    tFrameWrap.className = "lecture_hype_widget hub_hype_embed_host";
    tFrameWrap.style.left = tPosition.left;
    tFrameWrap.style.top = tPosition.top;
    tFrameWrap.style.transform = tPosition.transform;
    tFrameWrap.style.width = `${tTaillePct}%`;
    tFrameWrap.style.height = `${tTaillePct}%`;
    tFrameWrap.style.background = tFond;
    tOverlay.style.transitionDuration = `${tFadeMs}ms`;

    tOverlay.appendChild (tFrameWrap);
    tScene.appendChild (tOverlay);
    marque_overlay_lourd_flottement_lecture_tpl (pTpl, tOverlay, true);
    monte_widget_hype_direct (tFrameWrap, tWidget, {instanceKey : "lecture_" + tType, forcer : true})
      .catch ((pErr) => console.warn ("Chargement Hype impossible", pErr));

    return new Promise ((pResolve) =>
      {
        const tEntree =
          {
            timers : [],
            done : false,
            cleanup : (pTermineNaturellement = false) =>
              {
                if (tEntree.done) return;
                tEntree.done = true;
                tEntree.timers.forEach ((pTimer) => clearTimeout (pTimer));
                tEntree.timers = [];
                tOverlay.style.visibility = "hidden";
                tOverlay.style.pointerEvents = "none";
                nettoie_widget_hype_direct (
                  tFrameWrap,
                  {apresNettoyage : () =>
                    {
                      if (tOverlay.parentNode) tOverlay.parentNode.removeChild (tOverlay);
                      marque_overlay_lourd_flottement_lecture_tpl (pTpl, tOverlay, false);
                    }}
                );
                if (pTpl && pTpl._lectureHypeOverlays)
                  pTpl._lectureHypeOverlays.delete (tEntree);
                pResolve (!!pTermineNaturellement);
              },
          };
        if (pTpl)
          {
            if (!pTpl._lectureHypeOverlays) pTpl._lectureHypeOverlays = new Set ();
            pTpl._lectureHypeOverlays.add (tEntree);
          }
        requestAnimationFrame (() =>
          {
            if (tEntree.done) return;
            tOverlay.classList.add ("est_visible");
            tEntree.timers.push (setTimeout (() =>
              {
                if (tEntree.done) return;
                tOverlay.classList.remove ("est_visible");
                tEntree.timers.push (setTimeout (fin_fondu_opacite_lecture (tOverlay, () =>
                  {
                    tEntree.cleanup (true);
                  }), tFadeMs));
              }, tDelaiMs));
          });
      });
  }

function annule_overlays_hype_lecture_tpl (pTpl)
  {
    if (!pTpl || !pTpl._lectureHypeOverlays) return false;
    const tOverlays = Array.from (pTpl._lectureHypeOverlays);
    tOverlays.forEach ((pEntree) =>
      {
        if (pEntree && typeof pEntree.cleanup === "function")
          pEntree.cleanup (false);
      });
    pTpl._lectureHypeOverlays.clear ();
    return tOverlays.length > 0;
  }

function toutes_cartes_scene_terminees_lecture (pSceneId = Session.get ("scene_id"))
  {
    const tCartes = cartes_scene_liste ();
    if (!tCartes.length) return false;
    return tCartes.every ((pCarte) => pCarte && score_carte_terminee_lecture (pSceneId, pCarte._id));
  }

function marque_scene_terminee_si_complete_lecture (pSceneId = Session.get ("scene_id"))
  {
    if (!toutes_cartes_scene_terminees_lecture (pSceneId)) return false;
    const tScore = lecture_score_scene_session (pSceneId);
    ecrit_lecture_score_scene_session (pSceneId, {...tScore, terminee : true});
    return true;
  }

function scene_terminee_lecture_session (pSceneId = Session.get ("scene_id"))
  {
    return !!lecture_score_scene_session (pSceneId).terminee;
  }

function scene_id_depuis_cible_lecture (pSceneIdOuSlug)
  {
    const tId = typeof pSceneIdOuSlug === "string" ? pSceneIdOuSlug.trim () : "";
    if (!tId) return "";
    const tScene = Scenes.findOne (tId) || Scenes.findOne ({slug : tId});
    return (tScene && tScene._id) || tId;
  }

function scene_cible_config_terminee_lecture (pConfig)
  {
    if (!pConfig) return false;
    if (normalise_action_interactivite_objet (pConfig.interactivite_action) !== INTERACTIVITE_ACTION_SCENE)
      return false;
    const tSceneId = scene_id_depuis_cible_lecture (pConfig.interactivite_scene_id);
    return !!(tSceneId && scene_terminee_lecture_session (tSceneId));
  }

function reinitialise_scene_si_deja_terminee_lecture (pSceneId = Session.get ("scene_id"))
  {
    if (scene_terminee_lecture_session (pSceneId)) efface_lecture_score_scene_session (pSceneId);
  }

function demande_rejouer_scene_terminee_lecture (pSceneId = Session.get ("scene_id"))
  {
    const tSceneId = scene_id_depuis_cible_lecture (pSceneId);
    if (!tSceneId || !scene_terminee_lecture_session (tSceneId))
      return Promise.resolve (true);
    const tDialogExistant = gLectureRejouerSceneDialog.get ();
    if (tDialogExistant && typeof tDialogExistant.resolve === "function")
      tDialogExistant.resolve (false);
    return new Promise ((resolve) =>
      {
        gLectureRejouerSceneDialog.set ({sceneId : tSceneId, resolve});
      });
  }

function repond_dialog_rejouer_scene_lecture (pRejouer)
  {
    const tDialog = gLectureRejouerSceneDialog.get ();
    gLectureRejouerSceneDialog.set (null);
    if (tDialog && typeof tDialog.resolve === "function")
      tDialog.resolve (!!pRejouer);
  }

function ajoute_decoration_scene_reussie_item_svg (pRoot, pGroupe, pW, pH)
  {
    if (!pRoot || !pGroupe) return false;
    const tImporte = importe_svg_zoom_alternatif_noeud (pRoot, JAUGE_VISUELLE_ROLE);
    const tNode = tImporte && tImporte.node;
    if (!tNode) return false;
    const tVue = normalise_vue_carte_client (tImporte.viewBox, {x : 0, y : 0, w : 100, h : 100});
    const tReglages = reglages_mode_lecture_scene ();
    const tTaillePct = Math.max (25, Math.min (400, Number (tReglages.boutons_scene_taille_carte_jauge_pct) || 100)) / 100;
    const tOffsetX = Number (tReglages.boutons_scene_offset_x_carte_jauge) || 0;
    const tOffsetY = Number (tReglages.boutons_scene_offset_y_carte_jauge) || 0;
    const tBaseAvantReglage = Math.max (10, Math.min (26, Math.min (Number (pW) || 96, Number (pH) || 96) * 0.34));
    const tBase = tBaseAvantReglage * 4 * 0.8 * tTaillePct;
    const tScale = tBase / Math.max (1, Math.max (tVue.w, tVue.h));
    const tX = (Number (pW) || 96) - (tVue.w * tScale * 0.50) + tOffsetX;
    const tY = -(tVue.h * tScale * 0.34) + tOffsetY;
    tNode.setAttribute ("class", ((tNode.getAttribute ("class") || "") + " " + DECORATION_SCENE_REUSSIE_CLASS).trim ());
    tNode.setAttribute (
      "transform",
      `translate(${tX} ${tY}) scale(${tScale}) translate(${-tVue.x} ${-tVue.y})`
    );
    tNode.setAttribute ("aria-hidden", "true");
    tNode.style.pointerEvents = "none";
    tNode.style.opacity = "0.86";
    pGroupe.appendChild (tNode);
    return true;
  }

function applique_decorations_scene_reussies_tpl (pTpl)
  {
    const tDoc = pTpl && pTpl._svgDoc;
    const tRoot = tDoc && tDoc.documentElement;
    if (!tRoot) return false;
    tRoot.querySelectorAll (`.${DECORATION_SCENE_REUSSIE_CALQUE_CLASS}`).forEach ((pNoeud) => pNoeud.remove ());
    if (!mode_lecture_scene_actif ()) return false;
    const tScene = Scenes.findOne (Session.get ("scene_id"));
    if (!tScene || !Array.isArray (tScene.objets_config)) return false;
    let tAjouts = 0;
    tScene.objets_config.forEach ((pConfig) =>
      {
        if (!pConfig || !pConfig.id || !scene_cible_config_terminee_lecture (pConfig)) return;
        const tNoeud = tDoc.getElementById (pConfig.id);
        if (!tNoeud || tNoeud.getAttribute ("data-cogibulle-interface-item") === "1") return;
        let tBBox = null;
        try { tBBox = typeof tNoeud.getBBox === "function" ? tNoeud.getBBox () : null; }
        catch (e) { tBBox = null; }
        if (!tBBox || !(tBBox.width > 0) || !(tBBox.height > 0)) return;
        const tG = cree_element_svg_decor (tRoot, "g");
        tG.setAttribute ("class", `${DECORATION_SCENE_REUSSIE_CALQUE_CLASS} ${DECORATION_SCENE_REUSSIE_CLASS}`);
        tG.setAttribute ("transform", `translate(${tBBox.x} ${tBBox.y})`);
        tG.style.pointerEvents = "none";
        if (ajoute_decoration_scene_reussie_item_svg (tRoot, tG, tBBox.width, tBBox.height))
          {
            tRoot.appendChild (tG);
            tAjouts += 1;
          }
      });
    return tAjouts > 0;
  }

function point_dans_vue_carte (pPoint, pVue)
  {
    const tVue = normalise_vue_carte_client (pVue);
    return !!(pPoint && tVue
      && pPoint.x >= tVue.x
      && pPoint.x <= tVue.x + tVue.w
      && pPoint.y >= tVue.y
      && pPoint.y <= tVue.y + tVue.h);
  }

function lecture_ease_out_cubic (pT)
  {
    const t = Math.max (0, Math.min (1, Number (pT) || 0));
    return 1 - Math.pow (1 - t, 3);
  }

function lecture_ease_in_out_cubic (pT)
  {
    const t = Math.max (0, Math.min (1, Number (pT) || 0));
    return t < 0.5
      ? 4 * t * t * t
      : 1 - Math.pow (-2 * t + 2, 3) / 2;
  }

function applique_fond_masque_lecture_scene_tpl (pTpl)
  {
    if (!pTpl) return;
    const tScene = element_cadre_scene_tpl (pTpl);
    const tElement = element_svg_scene_tpl (pTpl);
    const tCouleur = mode_lecture_masque_scene_courant ().fond || "#ffffff";
    if (tScene)
      {
        if (!Object.prototype.hasOwnProperty.call (pTpl, "_lectureSceneBackgroundInitial"))
          pTpl._lectureSceneBackgroundInitial = tScene.style.backgroundColor || "";
        tScene.style.backgroundColor = tCouleur;
      }
    if (tElement)
      {
        if (!Object.prototype.hasOwnProperty.call (pTpl, "_lectureSceneElementBackgroundInitial"))
          pTpl._lectureSceneElementBackgroundInitial = tElement.style.backgroundColor || "";
        tElement.style.backgroundColor = tCouleur;
      }
  }

function restaure_fond_masque_lecture_scene_tpl (pTpl)
  {
    if (!pTpl) return;
    const tScene = element_cadre_scene_tpl (pTpl);
    const tElement = element_svg_scene_tpl (pTpl);
    if (tScene && Object.prototype.hasOwnProperty.call (pTpl, "_lectureSceneBackgroundInitial"))
      {
        const tFondInitial = pTpl._lectureSceneBackgroundInitial;
        if (typeof tFondInitial === "string" && tFondInitial)
          tScene.style.backgroundColor = tFondInitial;
        else
          tScene.style.removeProperty ("background-color");
        delete pTpl._lectureSceneBackgroundInitial;
      }
    if (tElement && Object.prototype.hasOwnProperty.call (pTpl, "_lectureSceneElementBackgroundInitial"))
      {
        const tFondInitial = pTpl._lectureSceneElementBackgroundInitial;
        if (typeof tFondInitial === "string" && tFondInitial)
          tElement.style.backgroundColor = tFondInitial;
        else
          tElement.style.removeProperty ("background-color");
        delete pTpl._lectureSceneElementBackgroundInitial;
      }
  }

function rafraichit_scene_lecture_tpl (pTpl, pRecentrer = false)
  {
    if (!pTpl) return;
    if (mode_lecture_scene_actif ()) applique_fond_masque_lecture_scene_tpl (pTpl);
    else                            restaure_fond_masque_lecture_scene_tpl (pTpl);
    if (typeof pTpl._effaceHoverLectureSvg === "function")
      pTpl._effaceHoverLectureSvg ();
    efface_hover_svg (pTpl);
    if (pTpl._svgDoc)
      {
        applique_surbrillance_svg (pTpl._svgDoc, []);
        dessine_selection_bulle_carte (pTpl._svgDoc, null);
      }
    assure_zoom_alternatif_focus_carte_tpl (pTpl);
    applique_masquages_carte_tpl (pTpl);
    applique_decorations_cartes_tpl (pTpl);
    applique_actions_scene_verrouillees_calques_svg_tpl (pTpl);
    if (pRecentrer)
      {
        const tCarteFocus = carte_focus_lecture_scene ();
        const tVueFocus = tCarteFocus && pTpl._lectureZoomAltActif
          ? vue_zoom_alternatif_carte_brute_tpl (pTpl, tCarteFocus, pTpl._lectureZoomAltReglagesPreview)
          : null;
        if (tVueFocus) applique_vue_carte_dans_scene (pTpl, tVueFocus, false);
        else           reinitialise_vue_scene (pTpl);
      }
  }

function programme_rafraichissement_viewport_lecture_tpl (pTpl)
  {
    if (!pTpl || !mode_lecture_scene_actif ()) return;
    if (pTpl._lectureViewportRefreshRaf)
      cancelAnimationFrame (pTpl._lectureViewportRefreshRaf);
    pTpl._lectureViewportRefreshRaf = requestAnimationFrame (() =>
      {
        pTpl._lectureViewportRefreshRaf = requestAnimationFrame (() =>
          {
            pTpl._lectureViewportRefreshRaf = null;
            if (!mode_lecture_scene_actif ()) return;
            const tCarteFocus = carte_focus_lecture_scene ();
            if (tCarteFocus && pTpl._lectureZoomAltActif)
              {
                assure_zoom_alternatif_focus_carte_tpl (pTpl);
                const tVueBrute = vue_zoom_alternatif_carte_brute_tpl (pTpl, tCarteFocus, pTpl._lectureZoomAltReglagesPreview);
                if (tVueBrute) applique_vue_carte_dans_scene (pTpl, tVueBrute, false);
                actualise_transform_calques_cartes_tpl (pTpl);
                return;
              }
            if (pTpl._lectureZoomAltMasquagesCarteId || pTpl._lectureZoomAltSortieEnCours)
              return;
            rafraichit_scene_lecture_tpl (pTpl, false);
          });
      });
  }

function reattache_document_svg_charge_tpl (pTpl)
  {
    if (!pTpl || !pTpl.find) return false;
    const tObject = pTpl.find (".contenu_svg_scene");
    const tDoc = document_svg_scene_depuis_conteneur (tObject);
    if (!tObject || !tDoc) return false;
    if (pTpl._svgDoc === tDoc) return true;
    if (typeof pTpl._svgObjectLoadHandler === "function")
      {
        pTpl._svgObjectLoadHandler ();
        return pTpl._svgDoc === tDoc;
      }
    return false;
  }

function preserve_bulles_lecture_refresh_tpl (pTpl, pPasses = 3)
  {
    if (!pTpl) return;
    const tPasses = Math.max (1, Math.floor (Number (pPasses) || 1));
    const tPassesActuelles = Math.max (0, Math.floor (Number (pTpl._lecturePreserveBullesRefreshPasses) || 0));
    pTpl._lecturePreserveBullesRefresh = true;
    pTpl._lecturePreserveBullesRefreshPasses = Math.max (tPassesActuelles, tPasses);
  }

function restaure_lecture_sans_focus_carte_tpl (pTpl, pOptions = {})
  {
    if (!pTpl || !mode_lecture_scene_actif ()) return false;
    const tFocusCarteId = gLectureSceneFocusCarte.get ();
    const tFocusCarte = tFocusCarteId ? carte_source_pour_bulle_scene (tFocusCarteId) : null;
    if (tFocusCarte) return false;

    if (pTpl._lectureZoomAltTimer)
      {
        clearTimeout (pTpl._lectureZoomAltTimer);
        pTpl._lectureZoomAltTimer = null;
      }
    if (pTpl._lectureZoomAltBrouillonRaf)
      {
        cancelAnimationFrame (pTpl._lectureZoomAltBrouillonRaf);
        pTpl._lectureZoomAltBrouillonRaf = null;
      }
    if (pTpl._lectureZoomAltFocusPreviewRaf)
      {
        cancelAnimationFrame (pTpl._lectureZoomAltFocusPreviewRaf);
        pTpl._lectureZoomAltFocusPreviewRaf = null;
      }
    nettoie_photos_focus_carte_tpl (pTpl);
    restaure_bulle_focus_carte_lecture_tpl (pTpl);
    gLectureSceneFocusCarte.set (null);
    pTpl._lectureZoomAltReglagesPreview = null;
    pTpl._lectureZoomAltPreparation = false;
    pTpl._lectureZoomAltActif = false;
    pTpl._lectureZoomAltCadreSortie = false;
    pTpl._lectureZoomAltMasquagesCarteId = null;
    pTpl._lectureZoomAltSortieEnCours = false;
    pTpl._lectureZoomAltSortieCarteId = null;
    pTpl._lectureZoomAltReaffichageMasquesEnCours = false;
    oublie_canevas_photo_focus_carte_tpl (pTpl);
    pTpl._lectureFocusInteractionsActives = false;
    pTpl._lectureBullesSuspendues = false;
    pTpl._lectureBullesAnimateIn = false;
    termine_animation_camera_zoom_alternatif_tpl (pTpl);
    retire_zoom_alternatif_scene_tpl (pTpl);
    restaure_elements_pedago_lecture_tpl (pTpl);
    pTpl._lecturePedago = null;
    preserve_bulles_lecture_refresh_tpl (pTpl, 4);
    synchronise_items_interface_svg_tpl (pTpl);
    if (pTpl._svgDoc)
      applique_masquages_carte_svg (pTpl._svgDoc, [], {animate : false});
    applique_decorations_cartes_tpl (pTpl);
    if (pOptions && pOptions.animateBullesRetour === false)
      {
        annule_timer_transition_decor_focus_carte_tpl (pTpl);
        pTpl._lectureDecorTransitionDelaiMs = 0;
        pTpl._lectureDecorTransitionVueDepartBrute = null;
        pTpl._lectureDecorTransitionVueCibleBrute = null;
        pTpl._lectureBullesSuspendues = false;
        pTpl._lectureBullesAnimateIn = false;
      }
    else
      termine_transition_decor_focus_carte_tpl (pTpl);
    if (pOptions && pOptions.recentrer) reinitialise_vue_scene (pTpl);
    return true;
  }

export function stabilise_lecture_scene_apres_changement_viewport (pTpl = null, pOptions = {})
  {
    const tTpl = pTpl || gSceneTemplateActif;
    if (!tTpl || !mode_lecture_scene_actif ()) return false;
    const tForcerEtatIntermediaire = !!(pOptions && pOptions.forcerEtatIntermediaire);
    const tDelais = Array.isArray (pOptions && pOptions.delais_ms)
      ? pOptions.delais_ms
      : [0, 80, 260];

    const tPasse = () =>
      {
        if (!mode_lecture_scene_actif ()) return;
        if (!reattache_document_svg_charge_tpl (tTpl))
          return;
        const tFocusCarte = carte_focus_lecture_scene ();
        if (tFocusCarte && tTpl._lectureZoomAltActif)
          {
            assure_zoom_alternatif_focus_carte_tpl (tTpl);
            const tVueBrute = vue_zoom_alternatif_carte_brute_tpl (
              tTpl,
              tFocusCarte,
              tTpl._lectureZoomAltReglagesPreview
            );
            if (tVueBrute) applique_vue_carte_dans_scene (tTpl, tVueBrute, false);
            actualise_transform_calques_cartes_tpl (tTpl);
            applique_masquages_carte_tpl (tTpl, {duree_in_ms : 0, duree_out_ms : 0});
            applique_decorations_cartes_tpl (tTpl);
            return;
          }
        if (!tFocusCarte)
          {
            const tEtatIntermediaire = !!(
              tTpl._lectureZoomAltMasquagesCarteId
              || tTpl._lectureZoomAltSortieEnCours
              || tTpl._lectureZoomAltSortieCarteId
              || tTpl._lectureZoomAltCadreSortie
            );
            if (!tEtatIntermediaire || tForcerEtatIntermediaire)
              restaure_lecture_sans_focus_carte_tpl (tTpl, {recentrer : false});
          }
        rafraichit_scene_lecture_tpl (tTpl, false);
      };

    tDelais.forEach ((pDelai) =>
      {
        const tDelai = Math.max (0, Number (pDelai) || 0);
        setTimeout (() =>
          {
            requestAnimationFrame (() => requestAnimationFrame (tPasse));
          }, tDelai);
      });
    return true;
  }

function ratio_scene_plein_ecran_runtime (pRuntime)
  {
    const tScene = pRuntime && pRuntime.querySelector && pRuntime.querySelector (".scene");
    if (!tScene) return 16 / 9;

    const tStyle = window.getComputedStyle (tScene);
    const tAspect = (tStyle && tStyle.aspectRatio || "").trim ();
    const tRatioMatch = tAspect.match (/^([0-9.]+)\s*\/\s*([0-9.]+)$/);
    if (tRatioMatch)
      {
        const tLargeur = Number (tRatioMatch[1]);
        const tHauteur = Number (tRatioMatch[2]);
        if (tLargeur > 0 && tHauteur > 0) return tLargeur / tHauteur;
      }
    const tRatioSimple = Number (tAspect);
    if (tRatioSimple > 0) return tRatioSimple;

    const tRect = tScene.getBoundingClientRect ();
    if (tRect && tRect.width > 0 && tRect.height > 0) return tRect.width / tRect.height;
    return 16 / 9;
  }

function applique_dimensions_scene_plein_ecran_runtime (pRuntime)
  {
    if (!pRuntime || !pRuntime.style) return;
    const tRatio = ratio_scene_plein_ecran_runtime (pRuntime);
    const tViewportW = window.innerWidth || document.documentElement.clientWidth || 0;
    const tViewportH = window.innerHeight || document.documentElement.clientHeight || 0;
    if (!tRatio || !tViewportW || !tViewportH) return;

    let tLargeur = tViewportW;
    let tHauteur = tLargeur / tRatio;
    if (tHauteur > tViewportH)
      {
        tHauteur = tViewportH;
        tLargeur = tHauteur * tRatio;
      }

    pRuntime.style.setProperty ("--scene-native-fullscreen-width", Math.round (tLargeur) + "px");
    pRuntime.style.setProperty ("--scene-native-fullscreen-height", Math.round (tHauteur) + "px");
  }

function nettoie_dimensions_scene_plein_ecran_runtime (pRuntime)
  {
    if (!pRuntime || !pRuntime.style) return;
    pRuntime.style.removeProperty ("--scene-native-fullscreen-width");
    pRuntime.style.removeProperty ("--scene-native-fullscreen-height");
  }

const EVENEMENTS_CHANGEMENT_PLEIN_ECRAN_SCENE = [
  "fullscreenchange",
  "webkitfullscreenchange",
  "mozfullscreenchange",
  "MSFullscreenChange",
];

const SEUIL_DIMENSIONS_FENETRE_PLEIN_ECRAN_PX = 8;

function element_document_plein_ecran_scene ()
  {
    return document.fullscreenElement
      || document.webkitFullscreenElement
      || document.webkitCurrentFullScreenElement
      || document.mozFullScreenElement
      || document.msFullscreenElement
      || null;
  }

function document_plein_ecran_actif_scene ()
  {
    return !!(
      element_document_plein_ecran_scene ()
      || document.fullscreen
      || document.webkitIsFullScreen
      || document.mozFullScreen
      || document.msFullscreenElement
    );
  }

function diffusion_est_webapp_plein_ecran ()
  {
    if (typeof window === "undefined") return false;
    const tNavigator = window.navigator || {};
    if (tNavigator.standalone === true) return true;
    if (typeof window.matchMedia !== "function") return false;
    return [
      "(display-mode: fullscreen)",
      "(display-mode: standalone)",
      "(display-mode: minimal-ui)",
    ].some ((pQuery) =>
      {
        try { return window.matchMedia (pQuery).matches; }
        catch (e) { return false; }
      });
  }

function dimensions_fenetre_navigateur_plein_ecran_scene ()
  {
    if (typeof window === "undefined") return false;
    if (window.fullScreen === true) return true;

    const tScreen = window.screen || (typeof screen !== "undefined" ? screen : null);
    if (!tScreen) return false;

    const tLargeurEcran = Math.max (
      Number (tScreen.width) || 0,
      Number (tScreen.availWidth) || 0
    );
    const tHauteurEcran = Math.max (
      Number (tScreen.height) || 0,
      Number (tScreen.availHeight) || 0
    );
    const tLargeurFenetre = Number (window.outerWidth) || Number (window.innerWidth) || 0;
    const tHauteurFenetre = Number (window.outerHeight) || Number (window.innerHeight) || 0;

    if (!tLargeurEcran || !tHauteurEcran || !tLargeurFenetre || !tHauteurFenetre) return false;
    return (
      Math.abs (tLargeurFenetre - tLargeurEcran) <= SEUIL_DIMENSIONS_FENETRE_PLEIN_ECRAN_PX
      && Math.abs (tHauteurFenetre - tHauteurEcran) <= SEUIL_DIMENSIONS_FENETRE_PLEIN_ECRAN_PX
    );
  }

function scene_est_element_plein_ecran_tpl (pTpl, pRuntime)
  {
    if (!pRuntime) return false;
    const tCiblePleinEcran = cible_plein_ecran_scene_tpl (pTpl, pRuntime);
    const tElementPleinEcran = element_document_plein_ecran_scene ();
    if (tElementPleinEcran)
      return tElementPleinEcran === pRuntime || tElementPleinEcran === tCiblePleinEcran;
    if (!document_plein_ecran_actif_scene ()) return false;
    return !!(
      pTpl
      && (
        pTpl._sceneFullscreenElement === pRuntime
        || pTpl._sceneFullscreenElement === tCiblePleinEcran
        || pTpl._sceneFullscreenPending
      )
    );
  }

function cible_plein_ecran_scene_tpl (pTpl, pRuntime)
  {
    if (pRuntime && typeof pRuntime.closest === "function")
      {
        const tShell = pRuntime.closest (".js_scene_fullscreen_shell");
        if (tShell) return tShell;
      }
    return (pTpl && pTpl.find && pTpl.find (".js_scene_fullscreen_shell")) || pRuntime;
  }

function scene_plein_ecran_externe_actif_tpl (pTpl, pRuntime)
  {
    if (scene_est_element_plein_ecran_tpl (pTpl, pRuntime)) return false;
    if (element_document_plein_ecran_scene ()) return true;
    if (document_plein_ecran_actif_scene ()) return true;
    return diffusion_est_webapp_plein_ecran ()
      || dimensions_fenetre_navigateur_plein_ecran_scene ();
  }

function synchronise_plein_ecran_externe_scene_tpl (pTpl, pRuntime)
  {
    if (!pTpl || !pTpl._sceneFullscreenExterneActive) return false;
    const tActive = scene_plein_ecran_externe_actif_tpl (pTpl, pRuntime);
    if (pTpl._sceneFullscreenExterneActive.get () !== tActive)
      pTpl._sceneFullscreenExterneActive.set (tActive);
    return tActive;
  }

function demande_plein_ecran_scene_element (pElement)
  {
    if (!pElement) return Promise.reject ();
    const tFn = pElement.requestFullscreen
      || pElement.webkitRequestFullscreen
      || pElement.mozRequestFullScreen
      || pElement.msRequestFullscreen;
    if (typeof tFn !== "function") return Promise.reject ();
    const tResultat = tFn.call (pElement);
    return tResultat && typeof tResultat.then === "function"
      ? tResultat
      : Promise.resolve ();
  }

function quitte_plein_ecran_scene_document ()
  {
    const tFn = document.exitFullscreen
      || document.webkitExitFullscreen
      || document.mozCancelFullScreen
      || document.msExitFullscreen;
    if (typeof tFn !== "function") return Promise.reject ();
    const tResultat = tFn.call (document);
    return tResultat && typeof tResultat.then === "function"
      ? tResultat
      : Promise.resolve ();
  }

function active_classe_scene_plein_ecran (pRuntime, pOptions = {})
  {
    if (!(pOptions && pOptions.preserverTransitionScene))
      annule_transition_lecture_scene ();
    if (pRuntime && pRuntime.classList) pRuntime.classList.remove ("scene_fullscreen_transitioning");
    if (pRuntime && pRuntime.classList) pRuntime.classList.add ("scene_native_fullscreen_active");
    applique_dimensions_scene_plein_ecran_runtime (pRuntime);
  }

function desactive_classe_scene_plein_ecran (pRuntime, pOptions = {})
  {
    if (!(pOptions && pOptions.preserverTransitionScene))
      annule_transition_lecture_scene ();
    if (pRuntime && pRuntime.classList) pRuntime.classList.remove ("scene_fullscreen_transitioning");
    if (pRuntime && pRuntime.classList) pRuntime.classList.remove ("scene_native_fullscreen_active");
    nettoie_dimensions_scene_plein_ecran_runtime (pRuntime);
  }

function assure_svg_scene_charge_apres_viewport_tpl (pTpl)
  {
    if (!pTpl || !pTpl.find) return false;
    const tFrame = pTpl.find (".contenu_svg_scene");
    if (!tFrame) return false;
    return !!document_svg_scene_depuis_conteneur (tFrame);
  }

function scene_lecture_a_focus_carte_en_cours_tpl (pTpl)
  {
    const tPhase = pTpl && pTpl._lectureZoomAltEtat
      ? pTpl._lectureZoomAltEtat.phase
      : LECTURE_ZOOM_ALT_PHASE.IDLE;
    return !!(
      pTpl
      && mode_lecture_scene_actif ()
      && (
        carte_focus_lecture_scene ()
        || tPhase !== LECTURE_ZOOM_ALT_PHASE.IDLE
        || pTpl._lectureZoomAltPreparation
        || pTpl._lectureZoomAltTimer
        || pTpl._lectureZoomAltActif
        || pTpl._lectureZoomAltMasquagesCarteId
        || pTpl._lectureZoomAltSortieEnCours
        || pTpl._lectureZoomAltSortieCarteId
        || pTpl._lectureFocusCarteEntreeEnCours
      )
    );
  }

function restaure_scene_lecture_apres_sortie_plein_ecran_tpl (pTpl)
  {
    if (!scene_lecture_a_focus_carte_en_cours_tpl (pTpl)) return false;
    stabilise_lecture_scene_apres_changement_viewport (
      pTpl,
      {forcerEtatIntermediaire : true}
    );
    return true;
  }

function synchronise_etat_plein_ecran_scene_tpl (pTpl)
  {
    const tRuntime = pTpl && pTpl.find && pTpl.find (".js_scene_fullscreen_runtime");
    const tCiblePleinEcran = cible_plein_ecran_scene_tpl (pTpl, tRuntime);
    if (!tRuntime)
      {
        if (tCiblePleinEcran && element_document_plein_ecran_scene () === tCiblePleinEcran)
          {
            pTpl._sceneFullscreenPending = false;
            pTpl._sceneFullscreenElement = tCiblePleinEcran;
            if (pTpl._sceneFullscreenExterneActive) pTpl._sceneFullscreenExterneActive.set (false);
            if (pTpl._sceneFullscreenActive) pTpl._sceneFullscreenActive.set (true);
            return true;
          }
        return false;
      }
    const tEtaitScenePleinEcran = !!(
      (pTpl._sceneFullscreenActive && pTpl._sceneFullscreenActive.get ())
      || (tRuntime.classList && tRuntime.classList.contains ("scene_native_fullscreen_active"))
    );
    const tActive = scene_est_element_plein_ecran_tpl (pTpl, tRuntime);

    if (tActive)
      {
        pTpl._sceneFullscreenPending = false;
        pTpl._sceneFullscreenElement = tCiblePleinEcran;
        if (pTpl._sceneFullscreenExterneActive) pTpl._sceneFullscreenExterneActive.set (false);
        active_classe_scene_plein_ecran (
          tRuntime,
          {preserverTransitionScene : gLectureSceneTransition.get ()}
        );
        assure_svg_scene_charge_apres_viewport_tpl (pTpl);
        if (pTpl._sceneFullscreenActive) pTpl._sceneFullscreenActive.set (true);
        programme_rafraichissement_viewport_lecture_tpl (pTpl);
        return true;
      }

    if (pTpl._sceneFullscreenPending && !tEtaitScenePleinEcran)
      return false;

    pTpl._sceneFullscreenPending = false;
    pTpl._sceneFullscreenElement = null;
    synchronise_plein_ecran_externe_scene_tpl (pTpl, tRuntime);
    desactive_classe_scene_plein_ecran (
      tRuntime,
      {preserverTransitionScene : gLectureSceneTransition.get ()}
    );
    assure_svg_scene_charge_apres_viewport_tpl (pTpl);
    if (pTpl._sceneFullscreenActive) pTpl._sceneFullscreenActive.set (false);
    if (tEtaitScenePleinEcran && restaure_scene_lecture_apres_sortie_plein_ecran_tpl (pTpl))
      return false;
    programme_rafraichissement_viewport_lecture_tpl (pTpl);
    return false;
  }

function programme_synchronisation_plein_ecran_scene_tpl (pTpl)
  {
    if (!pTpl) return;
    if (pTpl._sceneFullscreenSyncRaf)
      cancelAnimationFrame (pTpl._sceneFullscreenSyncRaf);
    if (pTpl._sceneFullscreenSyncTimer)
      clearTimeout (pTpl._sceneFullscreenSyncTimer);
    pTpl._sceneFullscreenSyncRaf = requestAnimationFrame (() =>
      {
        pTpl._sceneFullscreenSyncRaf = requestAnimationFrame (() =>
          {
            pTpl._sceneFullscreenSyncRaf = null;
            synchronise_etat_plein_ecran_scene_tpl (pTpl);
            pTpl._sceneFullscreenSyncTimer = setTimeout (() =>
              {
                pTpl._sceneFullscreenSyncTimer = null;
                synchronise_etat_plein_ecran_scene_tpl (pTpl);
              }, 180);
          });
      });
  }

function annule_timer_transition_decor_focus_carte_tpl (pTpl)
  {
    if (!pTpl || !pTpl._lectureDecorTransitionTimer) return;
    clearTimeout (pTpl._lectureDecorTransitionTimer);
    pTpl._lectureDecorTransitionTimer = null;
  }

function termine_transition_decor_focus_carte_tpl (pTpl)
  {
    if (!pTpl) return;
    annule_timer_transition_decor_focus_carte_tpl (pTpl);
    pTpl._lectureDecorTransitionDelaiMs = 0;
    pTpl._lectureDecorTransitionVueDepartBrute = null;
    pTpl._lectureDecorTransitionVueCibleBrute = null;
    if (!mode_lecture_scene_actif ()) return;
    if (gLectureSceneFocusCarte.get ())
      {
        actualise_transform_calques_cartes_tpl (pTpl);
        return;
      }
    pTpl._lectureBullesSuspendues = false;
    pTpl._lectureBullesAnimateIn = true;
    rafraichit_scene_lecture_tpl (pTpl, false);
  }

function arme_transition_decor_focus_carte_tpl (pTpl, pVueDepartBrute, pVueCibleBrute, pDelaiMs = 0)
  {
    if (!pTpl) return;
    annule_timer_transition_decor_focus_carte_tpl (pTpl);
    const tDelai = Math.max (0, Number (pDelaiMs) || 0);
    pTpl._lectureDecorTransitionDelaiMs = tDelai;
    pTpl._lectureDecorTransitionVueDepartBrute = normalise_vue_carte_client (pVueDepartBrute);
    pTpl._lectureDecorTransitionVueCibleBrute = normalise_vue_carte_client (pVueCibleBrute);
  }

function programme_transition_decor_focus_carte_tpl (pTpl, pDelaiMs)
  {
    if (!pTpl) return;
    annule_timer_transition_decor_focus_carte_tpl (pTpl);
    const tDelai = Math.max (0, Number (pDelaiMs) || 0);
    pTpl._lectureDecorTransitionDelaiMs = tDelai;
    if (tDelai <= 0)
      {
        termine_transition_decor_focus_carte_tpl (pTpl);
        return;
      }
    pTpl._lectureDecorTransitionTimer = setTimeout (() =>
      {
        termine_transition_decor_focus_carte_tpl (pTpl);
      }, tDelai);
  }

function reinitialise_transitions_lecture_tpl (pTpl)
  {
    if (!pTpl) return;
    invalide_cycle_zoom_alternatif_tpl (pTpl);
    nettoie_photos_focus_carte_tpl (pTpl);
    annule_overlays_hype_lecture_tpl (pTpl);
    if (gLectureCarteProgressRaf)
      {
        cancelAnimationFrame (gLectureCarteProgressRaf);
        gLectureCarteProgressRaf = null;
      }
    nettoie_contours_lecture_tpl (pTpl);
    nettoie_timers_pedago_lecture_tpl (pTpl);
    restaure_elements_pedago_lecture_tpl (pTpl);
    restaure_bulle_focus_carte_lecture_tpl (pTpl);
    pTpl._lecturePedago = null;
    if (pTpl._lectureZoomAltTimer)
      {
        clearTimeout (pTpl._lectureZoomAltTimer);
        pTpl._lectureZoomAltTimer = null;
      }
    if (pTpl._lectureZoomAltBrouillonRaf)
      {
        cancelAnimationFrame (pTpl._lectureZoomAltBrouillonRaf);
        pTpl._lectureZoomAltBrouillonRaf = null;
      }
    if (pTpl._lectureZoomAltFocusPreviewRaf)
      {
        cancelAnimationFrame (pTpl._lectureZoomAltFocusPreviewRaf);
        pTpl._lectureZoomAltFocusPreviewRaf = null;
      }
    pTpl._lectureZoomAltReglagesPreview = null;
    annule_timer_transition_decor_focus_carte_tpl (pTpl);
    pTpl._lectureDecorTransitionDelaiMs = 0;
    pTpl._lectureDecorTransitionVueDepartBrute = null;
    pTpl._lectureDecorTransitionVueCibleBrute = null;
    pTpl._lectureBullesSuspendues = false;
    pTpl._lectureBullesAnimateIn = false;
    pTpl._lectureFocusCarteEntreeEnCours = false;
    pTpl._lectureZoomAltPreparation = false;
    pTpl._lectureZoomAltActif = false;
    pTpl._lectureZoomAltCadreSortie = false;
    pTpl._lectureZoomAltMasquagesCarteId = null;
    pTpl._lectureZoomAltSortieEnCours = false;
    pTpl._lectureZoomAltSortieCarteId = null;
    pTpl._lectureZoomAltReaffichageMasquesEnCours = false;
    oublie_canevas_photo_focus_carte_tpl (pTpl);
    pTpl._lectureFocusInteractionsActives = false;
  }

function initialise_parcours_pedago_carte_lecture (pTpl, pCarte)
  {
    if (!mode_lecture_scene_actif () || !pTpl || !pCarte || !pCarte._id) return false;
    const tSceneId = Session.get ("scene_id");
    const tScene = tSceneId ? Scenes.findOne (tSceneId) : null;
    const tTousObjets = objets_autorises_carte_lecture (pTpl, pCarte, tScene);
    const tCarteScore = lecture_score_carte_session (tSceneId, pCarte._id);
    const tObjets = objets_a_rejouer_carte_lecture (tTousObjets, tCarteScore);
    const tRepriseInterrompue = !!(tCarteScore && tCarteScore.interrompue);
    const tObjetsContexte = tRepriseInterrompue
      ? objets_anterieurs_objet_courant_carte_lecture (tTousObjets, tObjets)
      : objets_deja_reussis_carte_lecture (tTousObjets, tCarteScore);
    const tAffichageIntegralNouvelEssai = !tRepriseInterrompue && carte_score_contient_erreur_lecture (tCarteScore);
    nettoie_timers_pedago_lecture_tpl (pTpl);
    pTpl._lecturePedago = {
      sceneId : tSceneId,
      carteId : pCarte._id,
      tousObjets : tTousObjets,
      objets : tObjets,
      objetsContexte : tObjetsContexte,
      affichageIntegralNouvelEssai : tAffichageIntegralNouvelEssai,
      repriseInterrompue : tRepriseInterrompue,
      index : -1,
      actif : true,
      attente : false,
      sequenceEntendsTuLancee : false,
      sequenceEntendsTuTerminee : false,
    };
    if (tAffichageIntegralNouvelEssai)
      restaure_elements_pedago_lecture_tpl (pTpl);
    else
      {
        masque_elements_pedago_carte_lecture (pTpl, tScene, tTousObjets);
        affiche_elements_pedago_carte_lecture (pTpl, tScene, tObjetsContexte);
      }
    actualise_progression_carte_lecture (tTousObjets, tCarteScore);
    return true;
  }

function objet_pedago_courant_lecture (pTpl)
  {
    const tEtat = pTpl && pTpl._lecturePedago;
    if (!tEtat || !Array.isArray (tEtat.objets)) return null;
    return tEtat.objets[tEtat.index] || null;
  }

function clic_objet_autorise_pedago_lecture (pTpl, pId)
  {
    const tEtat = pTpl && pTpl._lecturePedago;
    if (!tEtat || !tEtat.actif) return true;
    if (tEtat.attente) return false;
    const tObj = objet_pedago_courant_lecture (pTpl);
    return !!(tObj && tObj.id && pId === tObj.id);
  }

function affiche_objet_suivant_pedago_lecture (pTpl, pOptions = {})
  {
    const tEtat = pTpl && pTpl._lecturePedago;
    const tAutoriserAttente = !!(pOptions && pOptions.autoriserAttente);
    if (!tEtat || !tEtat.actif || (tEtat.attente && !tAutoriserAttente)) return false;
    const tScene = tEtat.sceneId ? Scenes.findOne (tEtat.sceneId) : null;
    tEtat.index += 1;
    const tObj = tEtat.objets[tEtat.index] || null;
    if (!tObj)
      {
        termine_parcours_pedago_carte_lecture (pTpl);
        return false;
      }
    tEtat.attente = !!(pOptions && pOptions.garderAttente);
    if (!tEtat.affichageIntegralNouvelEssai)
      {
        if (tEtat.repriseInterrompue)
          affiche_elements_pedago_carte_lecture (
            pTpl,
            tScene,
            objets_a_afficher_avec_objet_reprise_interrompue_lecture (tEtat, tObj),
            450
          );
        else
          affiche_objet_pedago_lecture (pTpl, tScene, tObj.id);
      }
    return true;
  }

function delai_affichage_obj_pedago_lecture_ms ()
  {
    return Math.max (
      0,
      Number (reglages_mode_lecture_scene ().delai_affichage_obj_s) || 0
    ) * 1000;
  }

function programme_objet_suivant_pedago_lecture (pTpl)
  {
    const tEtat = pTpl && pTpl._lecturePedago;
    if (!tEtat || !tEtat.actif) return;
    const tCarteId = tEtat.carteId;
    const tIndexSuivant = tEtat.index + 1;
    const tObjSuivant = tEtat.objets[tIndexSuivant] || null;
    const tDelaiAffichageMs = delai_affichage_obj_pedago_lecture_ms ();
    const tDelaiAudioMs = Math.max (0, tDelaiAffichageMs - 300);
    let tAffichageTermine = false;
    let tAudioTermine = !(tObjSuivant && tObjSuivant.id && file_id_audio_objet_lecture_scene (tObjSuivant.id));
    const debloque_si_pret = () =>
      {
        const tCourant = pTpl && pTpl._lecturePedago;
        if (!tCourant || !tCourant.actif || tCourant.carteId !== tCarteId) return false;
        if (tCourant.index !== tIndexSuivant) return false;
        if (!tAffichageTermine || !tAudioTermine) return false;
        tCourant.attente = false;
        return true;
      };

    if (tObjSuivant && tObjSuivant.id)
      {
        timer_pedago_lecture_tpl (pTpl, () =>
          {
            const tCourant = pTpl && pTpl._lecturePedago;
            if (!tCourant || !tCourant.actif || tCourant.carteId !== tCarteId) return;
            if (tCourant.index !== tIndexSuivant - 1 && tCourant.index !== tIndexSuivant) return;
            joue_audio_objet_lecture_scene_promesse (
              tObjSuivant.id,
              {
                onStart : () =>
                  {
                    const tEtatAudio = pTpl && pTpl._lecturePedago;
                    if (!tEtatAudio || !(tEtatAudio.affichageIntegralNouvelEssai || tEtatAudio.repriseInterrompue)) return;
                    if (tEtatAudio.carteId !== tCarteId || tEtatAudio.index + 1 !== tIndexSuivant) return;
                    if (typeof pTpl._afficheContourObjetLecture === "function")
                      pTpl._afficheContourObjetLecture (tObjSuivant.id);
                  },
              }
            )
              .catch (() => false)
              .then (() =>
                {
                  tAudioTermine = true;
                  debloque_si_pret ();
                });
          }, tDelaiAudioMs);
      }

    timer_pedago_lecture_tpl (pTpl, () =>
      {
        const tCourant = pTpl && pTpl._lecturePedago;
        if (!tCourant || !tCourant.actif || tCourant.carteId !== tCarteId) return;
        if (tCourant.index + 1 !== tIndexSuivant) return;
        tAffichageTermine = affiche_objet_suivant_pedago_lecture (
          pTpl,
          {autoriserAttente : true, garderAttente : true}
        );
        debloque_si_pret ();
      }, tDelaiAffichageMs);
  }

function programme_premier_objet_pedago_lecture (pTpl)
  {
    const tEtat = pTpl && pTpl._lecturePedago;
    if (!tEtat || !tEtat.actif) return;
    if (!tEtat.objets.length)
      {
        termine_parcours_pedago_carte_lecture (pTpl);
        return;
      }
    if (tEtat.index >= 0) return;
    const tObjPremier = tEtat.objets[0] || null;
    if (!tObjPremier || !tObjPremier.id) return;
    const tScene = tEtat.sceneId ? Scenes.findOne (tEtat.sceneId) : null;
    const tCarteId = tEtat.carteId;
    const tSequenceItems = fichiers_sequence_entends_tu_objet_lecture_scene (tObjPremier.id);
    const tDeclencheSurObjet = tSequenceItems.some ((pItem) => pItem && pItem.cle === "objet");
    const declenche_fadein_premier_objet = () =>
      {
        const tCourant = pTpl && pTpl._lecturePedago;
        if (!tCourant || !tCourant.actif || tCourant.carteId !== tCarteId) return false;
        if (tCourant.index !== 0 || tCourant.objetPremierAffiche) return false;
        tCourant.objetPremierAffiche = true;
        if (tCourant.affichageIntegralNouvelEssai || tCourant.repriseInterrompue)
          {
            if (tCourant.repriseInterrompue)
              affiche_objet_pedago_lecture (pTpl, tScene, tObjPremier.id);
            if (typeof pTpl._afficheContourObjetCourantLecture === "function")
              pTpl._afficheContourObjetCourantLecture ();
          }
        else
          affiche_objet_pedago_lecture (pTpl, tScene, tObjPremier.id);
        return true;
      };
    tEtat.attente = true;
    tEtat.index = 0;
    tEtat.objetPremierAffiche = false;
    if (!tEtat.sequenceEntendsTuLancee)
      {
        tEtat.sequenceEntendsTuLancee = true;
        joue_sequence_audio_lecture_scene (
          tSequenceItems,
          {
            onItemStart : (pItem, pIndex) =>
              {
                const tCourant = pTpl && pTpl._lecturePedago;
                if (tCourant
                 && ((pItem && pItem.cle === "objet")
                  || (!tCourant.affichageIntegralNouvelEssai && !tDeclencheSurObjet && pIndex === 0)))
                  declenche_fadein_premier_objet ();
              },
          }
        )
          .then ((pOk) =>
            {
              if (pOk)
                {
                  finalise_sequence_entends_tu_premier_objet_lecture (pTpl, tCarteId);
                }
              else
                {
                  declenche_fadein_premier_objet ();
                  debloque_sequence_entends_tu_premier_objet_lecture (pTpl, tCarteId);
                }
            });
        return;
      }
  }

function programme_premier_objet_pedago_lecture_apres_clic_bulle (pTpl)
  {
    const tEtat = pTpl && pTpl._lecturePedago;
    if (!tEtat || !tEtat.actif || tEtat.sequenceEntendsTuLancee) return false;
    const tNow = Date.now ();
    const tClicMemorise = Number (pTpl._lectureBulleClicAtMs) || 0;
    const tClicAt = tClicMemorise > 0 && tNow - tClicMemorise < 10000
      ? tClicMemorise
      : tNow;
    const tDelai = delai_sequence_entends_tu_lecture_ms ();
    const tAttente = Math.max (0, (tClicAt + tDelai) - tNow);
    const tCarteId = tEtat.carteId;
    timer_pedago_lecture_tpl (pTpl, () =>
      {
        const tCourant = pTpl && pTpl._lecturePedago;
        if (!tCourant || !tCourant.actif || tCourant.carteId !== tCarteId) return;
        programme_premier_objet_pedago_lecture (pTpl);
      }, tAttente);
    return true;
  }

function debloque_sequence_entends_tu_premier_objet_lecture (pTpl, pCarteId)
  {
    const tCourant = pTpl && pTpl._lecturePedago;
    if (!tCourant || !tCourant.actif || tCourant.carteId !== pCarteId) return false;
    if (tCourant.index !== 0 || !tCourant.attente) return false;
    tCourant.sequenceEntendsTuTerminee = true;
    tCourant.attente = false;
    return true;
  }

function finalise_sequence_entends_tu_premier_objet_lecture (pTpl, pCarteId)
  {
    const tCourant = pTpl && pTpl._lecturePedago;
    if (!tCourant || !tCourant.actif || tCourant.carteId !== pCarteId) return false;
    if (tCourant.index !== 0 || !tCourant.attente) return false;
    if (tCourant.sequenceEntendsTuTerminee) return false;
    return debloque_sequence_entends_tu_premier_objet_lecture (pTpl, pCarteId);
  }

function reponse_pedago_carte_correcte (pTpl, pReponseOui)
  {
    const tEtat = pTpl && pTpl._lecturePedago;
    const tObj = objet_pedago_courant_lecture (pTpl);
    if (!tEtat || !tObj || tEtat.attente) return false;
    const tScene = tEtat.sceneId ? Scenes.findOne (tEtat.sceneId) : null;
    const tContientSon = objet_contient_son_lecture (tScene, tObj.id);
    const tCorrect = !!pReponseOui === !!tContientSon;
    const tDerniereReponse = tEtat.index >= tEtat.objets.length - 1;
    const tCleFeedback = tCorrect ? "son_reussite" : "son_echec";
    const tSceneIdFeedback = tEtat.sceneId;
    const tCarteIdFeedback = tEtat.carteId;
    const tIndexFeedback = tEtat.index;
    const tCarteScore = lecture_score_carte_session (tEtat.sceneId, tEtat.carteId);
    const tReponses = tCarteScore.reponses && typeof tCarteScore.reponses === "object"
      ? tCarteScore.reponses
      : {};
    const tNouveauScore = {
      ...tCarteScore,
      reponses : {
        ...tReponses,
        [tObj.id] : tCorrect,
      },
      terminee : false,
      interrompue : false,
    };
    ecrit_lecture_score_carte_session (tEtat.sceneId, tEtat.carteId, tNouveauScore);
    actualise_progression_carte_lecture (tEtat.tousObjets, tNouveauScore, {anime : true});
    tEtat.attente = true;
    joue_audio_projet_lecture_scene_promesse (tCleFeedback)
      .catch (() => false)
      .then (() =>
        {
          const tCourant = pTpl && pTpl._lecturePedago;
          if (tCourant !== tEtat
           || !tCourant.actif
           || tCourant.sceneId !== tSceneIdFeedback
           || tCourant.carteId !== tCarteIdFeedback
           || tCourant.index !== tIndexFeedback)
            return;
          if (tDerniereReponse)
            termine_parcours_pedago_carte_lecture (pTpl);
          else
            programme_objet_suivant_pedago_lecture (pTpl);
    });
    return true;
  }

function reponse_pedago_carte_disponible (pTpl)
  {
    const tEtat = pTpl && pTpl._lecturePedago;
    const tObj = objet_pedago_courant_lecture (pTpl);
    return !!(tEtat && tEtat.actif && tObj && !tEtat.attente);
  }

async function termine_parcours_pedago_carte_lecture (pTpl)
  {
    const tEtat = pTpl && pTpl._lecturePedago;
    if (!tEtat || !tEtat.actif) return false;
    const tFinSeq = ((pTpl._lecturePedagoFinSeq || 0) + 1);
    pTpl._lecturePedagoFinSeq = tFinSeq;
    tEtat.actif = false;
    const tCarteScore = lecture_score_carte_session (tEtat.sceneId, tEtat.carteId);
    const tReponses = tCarteScore.reponses && typeof tCarteScore.reponses === "object"
      ? tCarteScore.reponses
      : {};
    const tTousCorrects = tEtat.tousObjets.length > 0
      && tEtat.tousObjets.every ((pObj) => pObj && tReponses[pObj.id] === true);
    ecrit_lecture_score_carte_session (
      tEtat.sceneId,
      tEtat.carteId,
      {
        ...tCarteScore,
        terminee : tTousCorrects,
        interrompue : false,
      }
    );
    if (tTousCorrects)
      {
        joue_audio_projet_lecture_scene ("musique_animation_liane");
        await overlay_widget_hype_recompense_scene_lecture (pTpl, "recompense_carte", "Bravo ! Animation liane à venir...");
        if (!mode_lecture_scene_actif ()
         || pTpl._lecturePedagoFinSeq !== tFinSeq
         || gLectureSceneFocusCarte.get () !== tEtat.carteId)
          return false;
      }
    await quitte_focus_carte_lecture_scene (
      pTpl,
      {depuisPedago : true, masquerBulleAppelante : tTousCorrects}
    );
    if (!mode_lecture_scene_actif () || pTpl._lecturePedagoFinSeq !== tFinSeq)
      return false;
    if (tTousCorrects)
      {
        rafraichit_scene_lecture_tpl (pTpl, false);
        if (marque_scene_terminee_si_complete_lecture (tEtat.sceneId))
          {
            joue_audio_projet_lecture_scene ("musique_pluie_de_bananes");
            await overlay_widget_hype_recompense_scene_lecture (pTpl, "recompense_scene", "Bravo ! Pluie d'étoiles à venir...");
            if (!mode_lecture_scene_actif () || pTpl._lecturePedagoFinSeq !== tFinSeq)
              return false;
            rafraichit_scene_lecture_tpl (pTpl, false);
            execute_retour_menu_scene_lecture (pTpl);
          }
      }
    return true;
  }

function gere_bouton_pedago_lecture (pTpl, pRole)
  {
    if (pRole === "carte_oui") return reponse_pedago_carte_correcte (pTpl, true);
    if (pRole === "carte_non") return reponse_pedago_carte_correcte (pTpl, false);
    if (pRole === "carte_legende")
      return joue_audio_lecture_scene (
        file_id_audio_projet_lecture_scene ("pour_reecouter_le_mot")
      );
    return false;
  }

function conserve_cadre_zoom_alternatif_pour_dezoom (pTpl)
  {
    const tRoot = pTpl && pTpl._svgDoc && pTpl._svgDoc.documentElement;
    if (!tRoot || typeof tRoot.querySelector !== "function") return false;
    const tGroupe = tRoot.querySelector (`.${CARTE_ZOOM_ALTERNATIF_GROUP_CLASS}`);
    if (!tGroupe || typeof tGroupe.querySelector !== "function") return false;
    const tCadre = tGroupe.querySelector (`.${CARTE_ZOOM_ALTERNATIF_FRAME_CLASS}`);
    if (!tCadre) return false;
    const tAssets = tGroupe.querySelector (`.${CARTE_ZOOM_ALTERNATIF_ASSETS_CLASS}`);
    if (pTpl._lectureZoomAltTimer)
      {
        clearTimeout (pTpl._lectureZoomAltTimer);
        pTpl._lectureZoomAltTimer = null;
      }
    if (tAssets && tAssets.parentNode) tAssets.parentNode.removeChild (tAssets);
    pTpl._lectureZoomAltCadreSortie = true;
    return true;
  }

function assets_zoom_alternatif_scene_tpl (pTpl)
  {
    const tGroupe = groupe_zoom_alternatif_scene_tpl (pTpl);
    return tGroupe && typeof tGroupe.querySelector === "function"
      ? tGroupe.querySelector (`.${CARTE_ZOOM_ALTERNATIF_ASSETS_CLASS}`)
      : null;
  }

function cadre_zoom_alternatif_scene_tpl (pTpl)
  {
    const tGroupe = groupe_zoom_alternatif_scene_tpl (pTpl);
    return tGroupe && typeof tGroupe.querySelector === "function"
      ? tGroupe.querySelector (`.${CARTE_ZOOM_ALTERNATIF_FRAME_CLASS}`)
      : null;
  }

function noeuds_cadre_zoom_alternatif_scene_tpl (pTpl)
  {
    const tGroupe = groupe_zoom_alternatif_scene_tpl (pTpl);
    if (!tGroupe || typeof tGroupe.querySelectorAll !== "function") return [];
    return Array.from (
      tGroupe.querySelectorAll (
        `.${CARTE_ZOOM_ALTERNATIF_FRAME_RELIEF_CLASS}, .${CARTE_ZOOM_ALTERNATIF_FRAME_CLASS}`
      )
    );
  }

function masque_cadre_zoom_alternatif_scene_tpl (pTpl)
  {
    noeuds_cadre_zoom_alternatif_scene_tpl (pTpl).forEach ((pNoeud) =>
      {
        if (!pNoeud || !pNoeud.style) return;
        pNoeud.style.transition = "";
        pNoeud.style.opacity = "0";
      });
  }

function animations_noeuds_zoom_alternatif (pNoeuds)
  {
    return (Array.isArray (pNoeuds) ? pNoeuds : [])
      .filter ((pNoeud) => pNoeud && typeof pNoeud.getAnimations === "function")
      .flatMap ((pNoeud) =>
        {
          try { return pNoeud.getAnimations ({subtree : false}); }
          catch (e) { return []; }
        })
      .filter (Boolean);
  }

function attend_animations_noeuds_zoom_alternatif (pNoeuds)
  {
    const tAnimations = animations_noeuds_zoom_alternatif (pNoeuds);
    if (!tAnimations.length) return Promise.resolve ();
    return Promise.allSettled (
      tAnimations.map ((pAnimation) =>
        pAnimation && pAnimation.finished && typeof pAnimation.finished.then === "function"
          ? pAnimation.finished.catch (() => {})
          : Promise.resolve ()
      )
    ).then (() => {});
  }

function millisecondes_transition_css_noeud_zoom_alternatif (pNoeud, pPropriete = "opacity")
  {
    if (!pNoeud || typeof getComputedStyle !== "function") return 0;
    let tStyle = null;
    try { tStyle = getComputedStyle (pNoeud); } catch (e) {}
    if (!tStyle) return 0;

    const tListeMs = (pTexte) =>
      String (pTexte || "")
        .split (",")
        .map ((pValeur) =>
          {
            const tValeur = String (pValeur || "").trim ();
            if (!tValeur) return 0;
            const tNombre = Number.parseFloat (tValeur);
            if (!Number.isFinite (tNombre)) return 0;
            return tValeur.endsWith ("ms") ? tNombre : tNombre * 1000;
          });
    const tProps = String (tStyle.transitionProperty || "")
      .split (",")
      .map ((pValeur) => String (pValeur || "").trim ());
    const tDurees = tListeMs (tStyle.transitionDuration);
    const tDelais = tListeMs (tStyle.transitionDelay);
    const tN = Math.max (tProps.length, tDurees.length, tDelais.length, 1);
    let tMax = 0;
    for (let i = 0; i < tN; i += 1)
      {
        const tProp = tProps[i] || tProps[tProps.length - 1] || "all";
        if (tProp !== "all" && tProp !== pPropriete) continue;
        const tDuree = tDurees[i] ?? tDurees[tDurees.length - 1] ?? 0;
        const tDelai = tDelais[i] ?? tDelais[tDelais.length - 1] ?? 0;
        tMax = Math.max (tMax, tDuree + tDelai);
      }
    return Math.max (0, tMax);
  }

function attend_transition_css_noeuds_zoom_alternatif (pNoeuds, pPropriete = "opacity")
  {
    const tNoeuds = (Array.isArray (pNoeuds) ? pNoeuds : [])
      .filter ((pNoeud) => pNoeud && pNoeud.parentNode && typeof pNoeud.addEventListener === "function");
    const tPromessesTransitions = tNoeuds
      .map ((pNoeud) =>
        {
          const tDureeMs = millisecondes_transition_css_noeud_zoom_alternatif (pNoeud, pPropriete);
          if (tDureeMs <= 0) return null;
          return new Promise ((pResolve) =>
            {
              let tTermine = false;
              let tTimer = null;
              const termine = () =>
                {
                  if (tTermine) return;
                  tTermine = true;
                  if (tTimer) clearTimeout (tTimer);
                  pNoeud.removeEventListener ("transitionend", onTransition);
                  pNoeud.removeEventListener ("transitioncancel", onTransition);
                  pResolve ();
                };
              const onTransition = (pEvent) =>
                {
                  if (!pEvent || pEvent.target !== pNoeud) return;
                  if (pEvent.propertyName && pEvent.propertyName !== pPropriete && pEvent.propertyName !== "all") return;
                  termine ();
                };
              pNoeud.addEventListener ("transitionend", onTransition);
              pNoeud.addEventListener ("transitioncancel", onTransition);
              tTimer = setTimeout (termine, tDureeMs + 120);
            });
        })
      .filter (Boolean);
    const tPromesseAnimations = attend_animations_noeuds_zoom_alternatif (tNoeuds);
    if (!tPromessesTransitions.length) return tPromesseAnimations;
    return Promise.allSettled ([...tPromessesTransitions, tPromesseAnimations]).then (() => {});
  }

function lance_fondu_assets_zoom_alternatif_tpl (pTpl, pAfficher, pDureeMs, pOptions = {})
  {
    const tAssets = assets_zoom_alternatif_scene_tpl (pTpl);
    const tInclureCadre = !(pOptions && pOptions.inclureCadre === false);
    const tNoeudsCadre = tInclureCadre ? noeuds_cadre_zoom_alternatif_scene_tpl (pTpl) : [];
    const tNoeuds = [tAssets, ...tNoeudsCadre].filter ((pNoeud) => pNoeud && pNoeud.style);
    const tDuree = Math.max (0, Number (pDureeMs) || 0);
    if (!tNoeuds.length) return Promise.resolve ();
    if (tAssets && tAssets.style) tAssets.style.pointerEvents = "none";
    if (tDuree <= 0)
      {
        tNoeuds.forEach ((pNoeud) =>
          {
            pNoeud.style.transition = "";
            pNoeud.style.opacity = pAfficher ? "1" : "0";
          });
        if (pAfficher && tAssets && tAssets.style) tAssets.style.pointerEvents = "auto";
        return Promise.resolve ();
      }
    tNoeuds.forEach ((pNoeud) =>
      {
        pNoeud.style.transition = `opacity ${tDuree}ms ease`;
        pNoeud.style.opacity = pAfficher ? "0" : "1";
      });
    return new Promise ((pResolve) =>
      {
        requestAnimationFrame (() =>
          {
            const tActifs = tNoeuds.filter ((pNoeud) => pNoeud.parentNode);
            if (!tActifs.length)
              {
                pResolve ();
                return;
              }
            tActifs.forEach ((pNoeud) =>
              {
                pNoeud.style.opacity = pAfficher ? "1" : "0";
              });
            requestAnimationFrame (() =>
              attend_transition_css_noeuds_zoom_alternatif (tActifs)
                .then (() =>
                  {
                    if (pAfficher && tAssets && tAssets.parentNode) tAssets.style.pointerEvents = "auto";
                    pResolve ();
                  })
            );
          });
      });
  }

function retire_zoom_alternatif_scene_tpl (pTpl)
  {
    const tRoot = pTpl && pTpl._svgDoc && pTpl._svgDoc.documentElement;
    if (!tRoot || typeof tRoot.querySelector !== "function") return false;
    const tGroupe = tRoot.querySelector (`.${CARTE_ZOOM_ALTERNATIF_GROUP_CLASS}`);
    if (!tGroupe || !tGroupe.parentNode) return false;
    if (typeof tGroupe.querySelectorAll === "function")
      Array.from (tGroupe.querySelectorAll ("foreignObject")).forEach ((pNoeud) =>
        {
          if (pNoeud && pNoeud._cogiBlazeView) Blaze.remove (pNoeud._cogiBlazeView);
          if (pNoeud) pNoeud._cogiBlazeView = null;
        });
    tGroupe.parentNode.removeChild (tGroupe);
    return true;
  }

function actualise_brouillon_zoom_carte_tpl (pTpl)
  {
    if (!pTpl || !pTpl._lectureZoomAltActif || pTpl._lectureZoomAltCameraAnimation || pTpl._lectureZoomAltSortieEnCours)
      return false;
    const tRoot = pTpl._svgDoc && pTpl._svgDoc.documentElement;
    const tCarte = carte_focus_lecture_scene ();
    if (!tRoot || !tCarte || !normalise_vue_carte_client (tCarte.vue)) return false;
    const tReglagesOverride = pTpl._lectureZoomAltReglagesPreview || null;
    const tAncien = groupe_zoom_alternatif_scene_tpl (pTpl);
    if (!tAncien) return false;
    const tOpaciteGroupe = tAncien.style ? (tAncien.style.opacity || "1") : "1";
    const tAssets = tAncien.querySelector && tAncien.querySelector (`.${CARTE_ZOOM_ALTERNATIF_ASSETS_CLASS}`);
    const tOpaciteAssets = tAssets && tAssets.style ? (tAssets.style.opacity || "1") : "1";
    const tPointerAssets = tAssets && tAssets.style ? (tAssets.style.pointerEvents || "auto") : "auto";
    retire_zoom_alternatif_scene_tpl (pTpl);
    const tOverlay = ajoute_zoom_alternatif_carte_racine (
      tRoot,
      tCarte,
      {
        sceneViewBox : vue_scene_decorations_cartes_tpl (pTpl),
        focusViewBox : vue_zoom_alternatif_carte_brute_tpl (pTpl, tCarte, tReglagesOverride),
        cartesLayerTransform : transform_calque_cartes_tpl (pTpl),
        reglagesOverride : tReglagesOverride,
      }
    );
    if (!tOverlay || !tOverlay.groupe) return false;
    tOverlay.groupe.style.transition = "";
    tOverlay.groupe.style.opacity = tOpaciteGroupe;
    const tNouveauxAssets = tOverlay.groupe.querySelector (`.${CARTE_ZOOM_ALTERNATIF_ASSETS_CLASS}`);
    if (tNouveauxAssets && tNouveauxAssets.style)
      {
        tNouveauxAssets.style.transition = "";
        tNouveauxAssets.style.opacity = tOpaciteAssets;
        tNouveauxAssets.style.pointerEvents = tPointerAssets;
      }
    return true;
  }

function assure_zoom_alternatif_focus_carte_tpl (pTpl)
  {
    if (!pTpl || !mode_lecture_scene_actif () || !pTpl._lectureZoomAltActif || pTpl._lectureZoomAltSortieEnCours)
      return false;
    const tRoot = pTpl._svgDoc && pTpl._svgDoc.documentElement;
    const tCarte = carte_focus_lecture_scene ();
    if (!tRoot || !tCarte || !normalise_vue_carte_client (tCarte.vue)) return false;

    const tGroupe = groupe_zoom_alternatif_scene_tpl (pTpl);
    const tAssets = tGroupe && tGroupe.querySelector
      ? tGroupe.querySelector (`.${CARTE_ZOOM_ALTERNATIF_ASSETS_CLASS}`)
      : null;
    if (tGroupe && tAssets)
      {
        place_zoom_alternatif_au_premier_plan_racine (tRoot);
        if (tGroupe.style) tGroupe.style.opacity = tGroupe.style.opacity || "1";
        if (tAssets.style)
          {
            tAssets.style.opacity = "1";
            tAssets.style.pointerEvents = "auto";
          }
        reapplique_etat_pedago_lecture_tpl (pTpl);
        return true;
      }

    const tOverlay = ajoute_zoom_alternatif_carte_racine (
      tRoot,
      tCarte,
      {
        sceneViewBox : vue_scene_decorations_cartes_tpl (pTpl),
        focusViewBox : vue_zoom_alternatif_carte_brute_tpl (pTpl, tCarte, pTpl._lectureZoomAltReglagesPreview),
        cartesLayerTransform : transform_calque_cartes_tpl (pTpl),
        reglagesOverride : pTpl._lectureZoomAltReglagesPreview,
      }
    );
    if (!tOverlay || !tOverlay.groupe) return false;
    if (tOverlay.groupe.style)
      {
        tOverlay.groupe.style.opacity = "1";
        tOverlay.groupe.style.transition = "";
      }
    const tNouveauxAssets = tOverlay.groupe.querySelector && tOverlay.groupe.querySelector (`.${CARTE_ZOOM_ALTERNATIF_ASSETS_CLASS}`);
    if (tNouveauxAssets && tNouveauxAssets.style)
      {
        tNouveauxAssets.style.transition = "";
        tNouveauxAssets.style.opacity = "1";
        tNouveauxAssets.style.pointerEvents = "auto";
      }
    reapplique_etat_pedago_lecture_tpl (pTpl);
    return true;
  }

function programme_actualisation_brouillon_zoom_carte_tpl (pTpl)
  {
    if (!pTpl || !mode_lecture_scene_actif () || !pTpl._lectureZoomAltActif) return;
    if (pTpl._lectureZoomAltBrouillonRaf) cancelAnimationFrame (pTpl._lectureZoomAltBrouillonRaf);
    pTpl._lectureZoomAltBrouillonRaf = requestAnimationFrame (() =>
      {
        pTpl._lectureZoomAltBrouillonRaf = null;
        actualise_brouillon_zoom_carte_tpl (pTpl);
      });
  }

function applique_preview_reglages_interface_carte_tpl (pReglages, pTpl = gSceneTemplateActif)
  {
    if (!pTpl) return false;
    pTpl._lectureZoomAltReglagesPreview = pReglages
      ? reglages_interface_carte_pour_stockage (pReglages)
      : null;
    programme_actualisation_brouillon_zoom_carte_tpl (pTpl);
    return true;
  }

function cle_reglage_geometrie_focus_zoom_alternatif (pCle)
  {
    return [
      "zoom_alternatif_focus_facteur",
      "zoom_alternatif_focus_offset_x",
      "zoom_alternatif_focus_offset_y",
    ].includes (pCle);
  }

function applique_preview_geometrie_focus_zoom_alternatif_tpl (pTpl, pPatch)
  {
    const tTpl = pTpl || gSceneTemplateActif;
    const tPatch = pPatch && typeof pPatch === "object" ? pPatch : null;
    if (!tTpl || !mode_lecture_scene_actif () || !tTpl._lectureZoomAltActif || !tPatch)
      return false;
    if (tTpl._lectureZoomAltCameraAnimation || tTpl._lectureZoomAltSortieEnCours)
      return false;
    const tCarte = carte_focus_lecture_scene ();
    if (!tCarte) return false;
    const tOverride = {
      ...(tTpl._lectureZoomAltReglagesPreview || {}),
      ...tPatch,
    };
    const tVueBrute = vue_zoom_alternatif_carte_brute_tpl (tTpl, tCarte, tOverride);
    if (!tVueBrute) return false;
    tTpl._lectureZoomAltReglagesPreview = tOverride;
    applique_vue_carte_dans_scene (tTpl, tVueBrute, false);
    actualise_brouillon_zoom_carte_tpl (tTpl);
    return true;
  }

function programme_preview_geometrie_focus_zoom_alternatif_depuis_champ (pChamp)
  {
    if (!pChamp || !pChamp.getAttribute) return false;
    const tCle = pChamp.getAttribute ("data-cle");
    if (!cle_reglage_geometrie_focus_zoom_alternatif (tCle)) return false;
    const tTexte = typeof pChamp.value === "string" ? pChamp.value.trim () : "";
    const tTpl = gSceneTemplateActif;
    if (!tTpl || !mode_lecture_scene_actif () || !tTpl._lectureZoomAltActif) return false;
    if (!tTexte)
      {
        const tPreview = {...(tTpl._lectureZoomAltReglagesPreview || {})};
        delete tPreview[tCle];
        tTpl._lectureZoomAltReglagesPreview = Object.keys (tPreview).length ? tPreview : null;
        const tCarte = carte_focus_lecture_scene ();
        const tVueBrute = tCarte ? vue_zoom_alternatif_carte_brute_tpl (tTpl, tCarte, tTpl._lectureZoomAltReglagesPreview) : null;
        if (tVueBrute) applique_vue_carte_dans_scene (tTpl, tVueBrute, false);
        programme_actualisation_brouillon_zoom_carte_tpl (tTpl);
        return true;
      }
    const tValeur = Number (tTexte);
    if (!Number.isFinite (tValeur)) return false;
    if (tTpl._lectureZoomAltFocusPreviewRaf)
      cancelAnimationFrame (tTpl._lectureZoomAltFocusPreviewRaf);
    tTpl._lectureZoomAltFocusPreviewRaf = requestAnimationFrame (() =>
      {
        tTpl._lectureZoomAltFocusPreviewRaf = null;
        applique_preview_geometrie_focus_zoom_alternatif_tpl (tTpl, {[tCle] : tValeur});
      });
    return true;
  }

function actualise_geometrie_focus_zoom_alternatif_apres_commit (pChamp)
  {
    if (!pChamp || !pChamp.getAttribute) return false;
    const tCle = pChamp.getAttribute ("data-cle");
    if (!cle_reglage_geometrie_focus_zoom_alternatif (tCle)) return false;
    const tTpl = gSceneTemplateActif;
    if (!tTpl || !mode_lecture_scene_actif () || !tTpl._lectureZoomAltActif) return false;
    if (REGLAGES_INTERFACE_CARTE_CLES_SET.has (tCle))
      {
        actualise_brouillon_zoom_carte_tpl (tTpl);
        return true;
      }
    const tPreview = {...(tTpl._lectureZoomAltReglagesPreview || {})};
    delete tPreview[tCle];
    tTpl._lectureZoomAltReglagesPreview = Object.keys (tPreview).length ? tPreview : null;
    const tCarte = carte_focus_lecture_scene ();
    const tVueBrute = tCarte ? vue_zoom_alternatif_carte_brute_tpl (tTpl, tCarte, tTpl._lectureZoomAltReglagesPreview) : null;
    if (tVueBrute) applique_vue_carte_dans_scene (tTpl, tVueBrute, false);
    actualise_brouillon_zoom_carte_tpl (tTpl);
    return true;
  }

function groupe_zoom_alternatif_scene_tpl (pTpl)
  {
    const tRoot = pTpl && pTpl._svgDoc && pTpl._svgDoc.documentElement;
    return tRoot && typeof tRoot.querySelector === "function"
      ? tRoot.querySelector (`.${CARTE_ZOOM_ALTERNATIF_GROUP_CLASS}`)
      : null;
  }

function lance_fondu_entree_zoom_alternatif (pGroupe, pCarte = null, pOptions = {})
  {
    const tOnStart = pOptions && typeof pOptions.onStart === "function"
      ? pOptions.onStart
      : null;
    let tStartLance = false;
    const lance_start = () =>
      {
        if (tStartLance) return;
        tStartLance = true;
        if (tOnStart) tOnStart ();
      };
    if (!pGroupe || !pGroupe.style)
      {
        lance_start ();
        return Promise.resolve ();
      }
    const tDuree = duree_fondu_entree_zoom_alternatif_ms_lecture (pCarte);
    if (tDuree <= 0)
      {
        lance_start ();
        return Promise.resolve ();
      }
    const tNoeuds = Array.from (pGroupe.children || []).filter ((pNoeud) =>
      {
        if (!pNoeud || !pNoeud.classList) return false;
        return !pNoeud.classList.contains (CARTE_ZOOM_ALTERNATIF_ASSETS_CLASS)
          && !pNoeud.classList.contains (CARTE_ZOOM_ALTERNATIF_FRAME_CLASS)
          && !pNoeud.classList.contains (CARTE_ZOOM_ALTERNATIF_FRAME_RELIEF_CLASS);
      });
    tNoeuds.forEach ((pNoeud) =>
      {
        if (!pNoeud || !pNoeud.style) return;
        pNoeud.style.transition = "none";
        pNoeud.style.opacity = "0";
      });
    return new Promise ((pResolve) =>
      requestAnimationFrame (() =>
        {
          if (!pGroupe.parentNode)
            {
              pResolve ();
              return;
            }
          lance_start ();
          Promise.allSettled (
            tNoeuds.map ((pNoeud) =>
              anime_opacite_noeud_zoom_alternatif (
                pNoeud,
                1,
                tDuree,
                () => !!(pGroupe && pGroupe.parentNode)
              )
            )
          ).then (() => pResolve ());
        })
    );
  }

function opacite_calculee_noeud_zoom_alternatif (pNoeud, pFallback = 1)
  {
    if (!pNoeud) return pFallback;
    let tOpacite = NaN;
    if (typeof getComputedStyle === "function")
      {
        try { tOpacite = Number (getComputedStyle (pNoeud).opacity); } catch (e) {}
      }
    if (!Number.isFinite (tOpacite) && pNoeud.style)
      tOpacite = Number (pNoeud.style.opacity);
    return Number.isFinite (tOpacite) ? Math.max (0, Math.min (1, tOpacite)) : pFallback;
  }

function anime_opacite_noeud_zoom_alternatif (pNoeud, pCible, pDureeMs, pEstValide = null)
  {
    if (!pNoeud || !pNoeud.style) return Promise.resolve ();
    const tDuree = Math.max (0, Number (pDureeMs) || 0);
    const tDepart = opacite_calculee_noeud_zoom_alternatif (pNoeud, 1);
    const tCibleBrute = Number (pCible);
    const tCible = Number.isFinite (tCibleBrute)
      ? Math.max (0, Math.min (1, tCibleBrute))
      : 0;
    const est_valide = () => typeof pEstValide !== "function" || pEstValide ();
    pNoeud.style.transition = "none";
    pNoeud.style.opacity = String (tDepart);
    if (tDuree <= 0 || !est_valide ())
      {
        if (est_valide ()) pNoeud.style.opacity = String (tCible);
        return Promise.resolve ();
      }
    return new Promise ((pResolve) =>
      {
        const tAnim = {debut : performance.now (), duree : tDuree};
        gLectureAnimationsJsAccelerables.add (tAnim);
        const termine = () =>
          {
            gLectureAnimationsJsAccelerables.delete (tAnim);
            pResolve ();
          };
        const anime = (pMaintenant) =>
          {
            if (!pNoeud.parentNode || !est_valide ())
              {
                termine ();
                return;
              }
            const tProgress = Math.max (0, Math.min (1, (pMaintenant - tAnim.debut) / tAnim.duree));
            const tEase = tProgress < 0.5
              ? 2 * tProgress * tProgress
              : 1 - Math.pow (-2 * tProgress + 2, 2) / 2;
            pNoeud.style.opacity = String (tDepart + ((tCible - tDepart) * tEase));
            if (tProgress >= 1)
              {
                pNoeud.style.opacity = String (tCible);
                termine ();
                return;
              }
            requestAnimationFrame (anime);
          };
        requestAnimationFrame (anime);
      });
  }

function lance_fondu_sortie_zoom_alternatif_tpl (pTpl, pOptions = {})
  {
    const tGroupe = groupe_zoom_alternatif_scene_tpl (pTpl);
    const tMasque = tGroupe && tGroupe.querySelector
      ? tGroupe.querySelector (`.${CARTE_ZOOM_ALTERNATIF_MASK_CLASS}`)
      : null;
    const tDuree = duree_fondu_sortie_zoom_alternatif_ms_lecture (
      pOptions && pOptions.carte ? pOptions.carte : null
    );
    const tCycleId = pOptions && Number.isFinite (Number (pOptions.cycleId))
      ? Number (pOptions.cycleId)
      : null;
    const tSceneId = pOptions && pOptions.sceneId;
    if (!tGroupe || !tMasque || !tMasque.style || tDuree <= 0)
      {
        if (!tCycleId || cycle_zoom_alternatif_valide_tpl (pTpl, tCycleId, tSceneId))
          retire_zoom_alternatif_scene_tpl (pTpl, tGroupe);
        return Promise.resolve ();
      }
    const tCarteId = pOptions && pOptions.carte && pOptions.carte._id;
    const tValide = () => !tCycleId || cycle_zoom_alternatif_valide_tpl (
      pTpl,
      tCycleId,
      tSceneId,
      {carteId : tCarteId}
    );
    return anime_opacite_noeud_zoom_alternatif (tMasque, 0, tDuree, tValide)
      .then (() =>
        {
          if (tValide ())
            retire_zoom_alternatif_scene_tpl (pTpl, tGroupe);
        });
  }

function lance_reaffichage_objets_masques_carte_tpl (pTpl, pCarte = null, pOptions = {})
  {
    // immediat : le retour se fait caché sous le couvercle de sortie, un fondu n'y serait qu'un coût.
    const tImmediat = !!(pOptions && pOptions.immediat);
    const tRoot = pTpl && pTpl._svgDoc && pTpl._svgDoc.documentElement;
    if (!pTpl || !pTpl._svgDoc || !tRoot || typeof tRoot.querySelectorAll !== "function")
      return Promise.resolve ();
    const tToken = (Number (pTpl._lectureZoomAltReaffichageMasquesToken) || 0) + 1;
    pTpl._lectureZoomAltReaffichageMasquesToken = tToken;
    const reaffichage_actif = () =>
      !!(pTpl
        && pTpl._lectureZoomAltReaffichageMasquesToken === tToken
        && pTpl._lectureZoomAltReaffichageMasquesEnCours);
    const tScene = Scenes.findOne (Session.get ("scene_id"));
    const tIdsCarte = pCarte
      ? ids_masques_carte_avec_associes_scene (pCarte.ids_masques, tScene)
      : [];
    const tEtatPedago = pTpl._lecturePedago;
    const tIdsPedago = [];
    if (tEtatPedago && Array.isArray (tEtatPedago.tousObjets))
      {
        tEtatPedago.tousObjets.forEach ((pObj) =>
          {
            ids_elements_associes_obj_lecture (tScene, pObj && pObj.id)
              .forEach ((pId) => { if (pId) tIdsPedago.push (pId); });
          });
      }
    if (pTpl._lecturePedagoHiddenIds instanceof Set)
      pTpl._lecturePedagoHiddenIds.forEach ((pId) => { if (pId) tIdsPedago.push (pId); });
    const tIdsReaffichage = ajoute_ids_masques_uniques (tIdsCarte, tIdsPedago);
    const tNoeudsReaffichage = [];
    const tItemsReaffichage = [];
    const tAjouteNoeudItem = (pId) =>
      {
        const tItem = item_interface_scene_par_id (pId, tScene);
        if (!tItem) return;
        const tNoeud = tRoot.querySelector (
          `[data-cogibulle-interface-item='1'][id="${echappe_selecteur_css_svg (pId)}"]`
        );
        if (tNoeud && tNoeud.style && !tItemsReaffichage.includes (tNoeud))
          tItemsReaffichage.push (tNoeud);
      };
    tIdsReaffichage.forEach ((pId) =>
      {
        tAjouteNoeudItem (pId);
        noeuds_calque_svg_et_decors_pour_id (pTpl._svgDoc, pId)
          .forEach ((pNoeud) =>
            {
              if (pNoeud && pNoeud.style && !tNoeudsReaffichage.includes (pNoeud))
                tNoeudsReaffichage.push (pNoeud);
            });
      });
    applique_masquages_carte_svg (
      pTpl._svgDoc,
      [],
      {
        animate : mode_lecture_scene_actif () && !tImmediat,
        duree_in_ms : tImmediat ? 0 : duree_fondu_sortie_zoom_alternatif_ms_lecture (pCarte),
        duree_out_ms : 0,
        decalage_in_ms : 0,
        decalage_out_ms : 0,
      }
    );
    const tDuree = tImmediat ? 0 : duree_fondu_sortie_zoom_alternatif_ms_lecture (pCarte);
    const tPromessesPedago = tIdsPedago.flatMap ((pId) =>
      noeuds_calque_svg_et_decors_pour_id (pTpl._svgDoc, pId)
        .map ((pNoeud) => applique_visibilite_element_lecture (pNoeud, true, tDuree))
    );
    const tPromessesItems = tItemsReaffichage.map ((pNoeud) =>
      {
        const tOpaciteStyle = Number (pNoeud.style.opacity);
        const tOpaciteCible = Math.max (0, Math.min (1, Number.isFinite (tOpaciteStyle) ? tOpaciteStyle : 1));
        const tTransitionInitiale = pNoeud.style.transition || "";
        const tPointerEventsInitial = pNoeud.style.pointerEvents || "";
        pNoeud.style.display = "";
        pNoeud.style.visibility = "visible";
        pNoeud.style.transition = "none";
        pNoeud.style.opacity = "0";
        pNoeud.style.pointerEvents = "none";
        try
          {
            if (typeof pNoeud.getBoundingClientRect === "function") pNoeud.getBoundingClientRect ();
          }
        catch (e) {}
        return new Promise ((pResolve) =>
          {
            requestAnimationFrame (() =>
              {
                if (!reaffichage_actif ()) { pResolve (); return; }
                if (!pNoeud.parentNode) { pResolve (); return; }
                pNoeud.style.transition = `opacity ${tDuree}ms ease`;
                try
                  {
                    if (typeof pNoeud.getBoundingClientRect === "function") pNoeud.getBoundingClientRect ();
                  }
                catch (e) {}
                requestAnimationFrame (() =>
                  {
                    if (!reaffichage_actif ()) { pResolve (); return; }
                    if (!pNoeud.parentNode) { pResolve (); return; }
                    pNoeud.style.opacity = String (tOpaciteCible);
                    setTimeout (() =>
                      {
                        if (!reaffichage_actif ()) { pResolve (); return; }
                        if (pNoeud.parentNode)
                          {
                            pNoeud.style.transition = tTransitionInitiale;
                            pNoeud.style.pointerEvents = tPointerEventsInitial;
                          }
                        pResolve ();
                      }, tDuree + 60);
                  });
              });
          });
      });
    return new Promise ((pResolve) =>
      {
        requestAnimationFrame (() =>
          requestAnimationFrame (() =>
            Promise.allSettled ([
              attend_transition_css_noeuds_zoom_alternatif (tNoeudsReaffichage, "opacity"),
              ...tPromessesPedago,
              ...tPromessesItems,
            ]).then (() =>
              {
                if (!reaffichage_actif ())
                  {
                    pResolve ();
                    return;
                  }
                tIdsPedago.forEach ((pId) =>
                  {
                    applique_aux_noeuds_calque_svg_et_decors (pTpl._svgDoc, pId, (pNoeud) =>
                      {
                        if (!pNoeud || !pNoeud.style) return;
                        const tInitial = pNoeud._cogiLecturePedagoInitial;
                        pNoeud.style.visibility = "visible";
                        pNoeud.style.opacity = "1";
                        if (tInitial)
                          {
                            pNoeud.style.transition = tInitial.transition;
                            pNoeud.style.pointerEvents = tInitial.pointerEvents;
                          }
                        delete pNoeud._cogiLecturePedagoInitial;
                        delete pNoeud._cogiLecturePedagoVisibiliteToken;
                      });
                  });
                pTpl._lecturePedagoHiddenIds = new Set ();
                pResolve ();
              })
          )
        );
      });
  }

function role_zoom_alternatif_depuis_cible (pCible)
  {
    let tNoeud = pCible;
    while (tNoeud)
      {
        if (tNoeud.getAttribute)
          {
            const tRole = tNoeud.getAttribute (CARTE_ZOOM_ALTERNATIF_ROLE_ATTR);
            if (tRole) return tRole;
          }
        if (tNoeud.classList && tNoeud.classList.contains (CARTE_ZOOM_ALTERNATIF_GROUP_CLASS))
          return null;
        tNoeud = tNoeud.parentNode;
      }
    return null;
  }

function noeud_zoom_alternatif_depuis_cible (pCible)
  {
    let tNoeud = pCible;
    while (tNoeud)
      {
        if (tNoeud.getAttribute && tNoeud.getAttribute (CARTE_ZOOM_ALTERNATIF_ROLE_ATTR))
          return tNoeud;
        if (tNoeud.classList && tNoeud.classList.contains (CARTE_ZOOM_ALTERNATIF_GROUP_CLASS))
          return null;
        tNoeud = tNoeud.parentNode;
      }
    return null;
  }

function cle_taille_zoom_alternatif_role (pRole)
  {
    const tRole = typeof pRole === "string" ? pRole.trim () : "";
    return ZOOM_ALTERNATIF_SVG_ROLE_KEYS.has (tRole)
      ? `zoom_alternatif_taille_${tRole}`
      : "";
  }

function cle_offset_zoom_alternatif_role (pRole, pAxe)
  {
    const tRole = typeof pRole === "string" ? pRole.trim () : "";
    const tAxe = pAxe === "y" ? "y" : "x";
    return ZOOM_ALTERNATIF_SVG_ROLE_KEYS.has (tRole)
      ? `zoom_alternatif_offset_${tAxe}_${tRole}`
      : "";
  }

function cle_ancrage_zoom_alternatif_role (pRole)
  {
    const tRole = typeof pRole === "string" ? pRole.trim () : "";
    return tRole === "carte_fermeture"
      ? `zoom_alternatif_ancrage_${tRole}`
      : "";
  }

function taille_zoom_alternatif_role (pRole, pReglages = reglages_mode_lecture_scene ())
  {
    const tCle = cle_taille_zoom_alternatif_role (pRole);
    const tValeur = tCle && pReglages ? Number (pReglages[tCle]) : NaN;
    return Number.isFinite (tValeur) && tValeur > 0 ? tValeur : 1;
  }

function offset_zoom_alternatif_role (pRole, pAxe, pReglages = reglages_mode_lecture_scene ())
  {
    const tCle = cle_offset_zoom_alternatif_role (pRole, pAxe);
    const tValeur = tCle && pReglages ? Number (pReglages[tCle]) : NaN;
    return Number.isFinite (tValeur) ? tValeur : 0;
  }

function ancrage_zoom_alternatif_role (pRole, pReglages = reglages_mode_lecture_scene ())
  {
    const tCle = cle_ancrage_zoom_alternatif_role (pRole);
    const tValeur = tCle && pReglages ? String (pReglages[tCle] || "") : "";
    return tValeur === "gauche" ? "gauche" : "droite";
  }

function survol_interface_carte_zoom_actif_client ()
  {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return true;
    try
      {
        return window.matchMedia ("(hover: hover) and (pointer: fine)").matches;
      }
    catch (e)
      {
        return true;
      }
  }

function annule_timer_finalisation_entree_zoom_alternatif_tpl (pTpl)
  {
    if (!pTpl || !pTpl._lectureZoomAltEntreeFinalisationTimer) return;
    clearTimeout (pTpl._lectureZoomAltEntreeFinalisationTimer);
    pTpl._lectureZoomAltEntreeFinalisationTimer = null;
  }

const LECTURE_ZOOM_ALT_PHASE = Object.freeze ({
  IDLE : "idle",
  PREPARATION : "preparation",
  ATTENTE_ZOOM : "attente_zoom",
  CAMERA_ENTREE : "camera_entree",
  INTERFACE_ENTREE : "interface_entree",
  OUVERTE : "ouverte",
  SORTIE_INTERFACE : "sortie_interface",
  CAMERA_SORTIE : "camera_sortie",
  RESTAURATION : "restauration",
});

function etat_zoom_alternatif_tpl (pTpl)
  {
    if (!pTpl) return null;
    if (!pTpl._lectureZoomAltEtat)
      pTpl._lectureZoomAltEtat = {
        cycleId : Number (pTpl._lectureZoomAltCycleId) || 0,
        phase : LECTURE_ZOOM_ALT_PHASE.IDLE,
        sceneId : null,
        carteId : null,
      };
    return pTpl._lectureZoomAltEtat;
  }

function demarre_cycle_zoom_alternatif_tpl (pTpl, pPhase, pSceneId, pCarteId)
  {
    if (!pTpl) return 0;
    const tCycleId = (Number (pTpl._lectureZoomAltCycleId) || 0) + 1;
    pTpl._lectureZoomAltCycleId = tCycleId;
    pTpl._lectureZoomAltEtat = {
      cycleId : tCycleId,
      phase : pPhase || LECTURE_ZOOM_ALT_PHASE.PREPARATION,
      sceneId : pSceneId || null,
      carteId : pCarteId || null,
    };
    return tCycleId;
  }

function invalide_cycle_zoom_alternatif_tpl (pTpl)
  {
    if (!pTpl) return 0;
    pTpl._lectureZoomAltCycleId = (Number (pTpl._lectureZoomAltCycleId) || 0) + 1;
    pTpl._lectureZoomAltEtat = {
      cycleId : pTpl._lectureZoomAltCycleId,
      phase : LECTURE_ZOOM_ALT_PHASE.IDLE,
      sceneId : null,
      carteId : null,
    };
    return pTpl._lectureZoomAltCycleId;
  }

function cycle_zoom_alternatif_valide_tpl (pTpl, pCycleId, pSceneId, pOptions = {})
  {
    const tEtat = etat_zoom_alternatif_tpl (pTpl);
    const tPhases = pOptions && Array.isArray (pOptions.phases) ? pOptions.phases : null;
    const tCarteId = pOptions && pOptions.carteId;
    return !!(
      pTpl
      && pTpl._lectureZoomAltCycleId === pCycleId
      && tEtat
      && tEtat.cycleId === pCycleId
      && (!pSceneId || Session.get ("scene_id") === pSceneId)
      && (!pSceneId || !tEtat.sceneId || tEtat.sceneId === pSceneId)
      && (!tCarteId || !tEtat.carteId || tEtat.carteId === tCarteId)
      && (!tPhases || tPhases.includes (tEtat.phase))
      && pTpl === gSceneTemplateActif
    );
  }

function definit_phase_zoom_alternatif_tpl (pTpl, pCycleId, pPhase, pOptions = {})
  {
    if (!cycle_zoom_alternatif_valide_tpl (pTpl, pCycleId, pOptions && pOptions.sceneId, {
      carteId : pOptions && pOptions.carteId,
      phases : pOptions && pOptions.phases,
    }))
      return false;
    const tEtat = etat_zoom_alternatif_tpl (pTpl);
    tEtat.phase = pPhase || tEtat.phase;
    if (pOptions && Object.prototype.hasOwnProperty.call (pOptions, "sceneId"))
      tEtat.sceneId = pOptions.sceneId || null;
    if (pOptions && Object.prototype.hasOwnProperty.call (pOptions, "carteId"))
      tEtat.carteId = pOptions.carteId || null;
    return true;
  }

function commence_animation_camera_zoom_alternatif_tpl (pTpl)
  {
    if (!pTpl) return;
    pTpl._lectureZoomAltCameraAnimation = true;
  }

function termine_animation_camera_zoom_alternatif_tpl (pTpl)
  {
    if (!pTpl) return;
    pTpl._lectureZoomAltCameraAnimation = false;
    actualise_transform_calques_cartes_tpl (pTpl);
  }

function arrete_flottement_bulles_lecture_tpl (pTpl)
  {
    if (!pTpl) return;
    if (pTpl._lectureBullesFlottementRaf)
      {
        cancelAnimationFrame (pTpl._lectureBullesFlottementRaf);
        pTpl._lectureBullesFlottementRaf = null;
      }
    pTpl._lectureBullesFlottementHorlogeMs = null;
    pTpl._lectureBullesFlottementDerniereFrame = 0;
  }

function flottement_bulles_lecture_suspendu_tpl (pTpl)
  {
    return !!(
      !mode_lecture_scene_actif ()
      || (pTpl && pTpl._lectureBullesSuspendues)
    );
  }

// Carte en cours d'ouverture, ouverte ou en cours de fermeture : les bulles sont masquées ou figées
// sous la photo. Chaque écriture de transform dans le SVG y coûte un cycle de peinture complet de
// l'illustration ; le flottement est donc mis en pause, et son horloge avec lui, pour qu'il reprenne
// exactement où il s'était arrêté.
function flottement_bulles_lecture_en_pause_tpl (pTpl)
  {
    return !!(pTpl && (
      pTpl._lectureZoomAltPreparation
      || pTpl._lectureFocusCarteEntreeEnCours
      || pTpl._lectureZoomAltActif
      || pTpl._lectureZoomAltSortieEnCours
      || pTpl._lecturePhotoFocusCarteNode
      || (pTpl._lectureFlottementOverlaysLourds instanceof Set && pTpl._lectureFlottementOverlaysLourds.size > 0)
    ));
  }

// Diaporamas (morphings de zoogs, items d'interface) et récompenses Hype : ces overlays animent
// déjà toute la scène. Le flottement des bulles et des items est donc mis en pause tant que l'un
// d'eux est affiché, fondu de fermeture compris, et ne reprend qu'une fois le dernier retiré.
function marque_overlay_lourd_flottement_lecture_tpl (pTpl, pOverlay, pActif)
  {
    if (!pTpl || !pOverlay) return;
    if (!(pTpl._lectureFlottementOverlaysLourds instanceof Set))
      pTpl._lectureFlottementOverlaysLourds = new Set ();
    const tOverlays = pTpl._lectureFlottementOverlaysLourds;
    if (pActif)
      {
        tOverlays.add (pOverlay);
        return;
      }
    if (!tOverlays.delete (pOverlay) || tOverlays.size > 0) return;
    demarre_flottement_bulles_lecture_tpl (pTpl);
  }

// Appui sur un bouton, une bulle, un item ou un zoog : sur un appareil peu puissant, le flottement
// (réécriture des transforms de toutes les bulles et items à chaque frame) saccade l'animation
// d'enfoncement et de rebond. Il s'arrête donc net dès l'appui et ne reprend, en douceur, qu'une fois
// le rebond terminé et l'action du bouton lancée. Filet : reprise forcée si la fin de l'appui se perd.
const LECTURE_FLOTTEMENT_APPUI_MAX_MS = 4000;

function suspend_flottement_appui_lecture_tpl (pTpl, pAppui, pActif)
  {
    if (!pTpl || !pAppui) return;
    if (pAppui._flottementFiletTimer)
      {
        clearTimeout (pAppui._flottementFiletTimer);
        pAppui._flottementFiletTimer = null;
      }
    if (pActif)
      {
        arrete_flottement_bulles_lecture_tpl (pTpl);
        pAppui._flottementFiletTimer = setTimeout (
          () =>
            {
              if (pTpl.view && pTpl.view.isDestroyed) return;
              suspend_flottement_appui_lecture_tpl (pTpl, pAppui, false);
            },
          LECTURE_FLOTTEMENT_APPUI_MAX_MS
        );
      }
    marque_overlay_lourd_flottement_lecture_tpl (pTpl, pAppui, pActif);
  }

// Appui pendant un fondu (sortie de diaporama ou de récompense, couvercle de sortie de carte, photo,
// réapparition d'éléments…) : le fondu disputait la puissance à l'animation d'appui et à l'action du
// bouton. Tous les fondus en cours sont donc achevés en accéléré, depuis leur point actuel, en
// LECTURE_ACHEVEMENT_ANIMATIONS_MS : la scène rejoint son état final sans saut, et les nettoyages
// prévus en fin de fondu (transitionend, minuteries) s'exécutent normalement.
// - transitions CSS de la scène (HTML et SVG en ligne) : vitesse de lecture relevée ;
// - fondus pilotés en JS : ils s'inscrivent dans gLectureAnimationsJsAccelerables et exposent accelere ().
// Les animations internes des widgets Hype ne sont pas touchées.
const LECTURE_ACHEVEMENT_ANIMATIONS_MS = 140;
const LECTURE_ACHEVEMENT_ANIMATIONS_EXCLUES = ".item_hype_widget, .hub_hype_embed_host, .HYPE_document";
const gLectureAnimationsJsAccelerables = new Set ();

// Recale une animation JS (début, durée) pour qu'elle garde sa progression actuelle et finisse dans pMs.
function recale_animation_js_accelerable_lecture (pEtat, pMs, pNow)
  {
    if (!pEtat || !(pEtat.duree > 0)) return;
    const tProgress = Math.max (0, Math.min (1, (pNow - pEtat.debut) / pEtat.duree));
    if (tProgress >= 1 || (1 - tProgress) * pEtat.duree <= pMs) return;
    pEtat.duree = pMs / (1 - tProgress);
    pEtat.debut = pNow - (tProgress * pEtat.duree);
  }

// Fin d'un fondu d'opacité de pNoeud : pFin est appelée dès la transitionend (fondu accéléré compris)
// ou, à défaut, par la minuterie d'origine à qui l'on passe la fonction renvoyée. Un seul appel.
function fin_fondu_opacite_lecture (pNoeud, pFin)
  {
    let tFait = false;
    const surFin = (pEvent) =>
      {
        if (pEvent.target === pNoeud && pEvent.propertyName === "opacity") fin ();
      };
    const fin = () =>
      {
        if (tFait) return;
        tFait = true;
        pNoeud.removeEventListener ("transitionend", surFin);
        pFin ();
      };
    pNoeud.addEventListener ("transitionend", surFin);
    return fin;
  }

function acheve_animations_en_cours_lecture_tpl (pTpl)
  {
    const tMs = LECTURE_ACHEVEMENT_ANIMATIONS_MS;
    const tNow = performance.now ();
    gLectureAnimationsJsAccelerables.forEach ((pEtat) => recale_animation_js_accelerable_lecture (pEtat, tMs, tNow));
    const tScene = element_cadre_scene_tpl (pTpl);
    if (!tScene || typeof tScene.getAnimations !== "function") return;
    let tAnimations = [];
    try { tAnimations = tScene.getAnimations ({subtree : true}); }
    catch (e) { return; }
    tAnimations.forEach ((pAnim) =>
      {
        try
          {
            // Transitions CSS seulement : les animations @keyframes peuvent boucler.
            if (!pAnim || pAnim.playState !== "running" || !("transitionProperty" in pAnim)) return;
            const tCible = pAnim.effect && pAnim.effect.target;
            if (tCible && typeof tCible.closest === "function" && tCible.closest (LECTURE_ACHEVEMENT_ANIMATIONS_EXCLUES)) return;
            const tFin = Number (pAnim.effect.getComputedTiming ().endTime);
            const tCourant = Number (pAnim.currentTime);
            const tVitesse = Math.abs (Number (pAnim.playbackRate) || 1);
            if (!Number.isFinite (tFin) || !Number.isFinite (tCourant)) return;
            if ((tFin - tCourant) / tVitesse <= tMs) return;
            const tNouvelleVitesse = (tFin - tCourant) / tMs;
            if (typeof pAnim.updatePlaybackRate === "function") pAnim.updatePlaybackRate (tNouvelleVitesse);
            else pAnim.playbackRate = tNouvelleVitesse;
          }
        catch (e) {}
      });
  }

function applique_flottement_bulles_lecture_tpl (pTpl, pNow, pSeulId = null)
  {
    if (!pTpl || !pTpl._svgDoc || !pTpl._svgDoc.documentElement) return;
    const tRoot = pTpl._svgDoc.documentElement;
    const tEtats = pTpl._lectureBullesFlottementEtats;
    if (!(tEtats instanceof Map) || tEtats.size === 0) return;
    const tNoeuds = pTpl._lectureBullesFlottementNoeuds;
    if (!(tNoeuds instanceof Map) || tNoeuds.size === 0) return;

    if (!Number.isFinite (Number (pTpl._lectureBullesFlottementTempsMs)))
      pTpl._lectureBullesFlottementTempsMs = Number (pNow) || 0;
    const tSec = (Number (pTpl._lectureBullesFlottementTempsMs) || 0) / 1000;
    tNoeuds.forEach ((pNoeud, pCarteId) =>
      {
        if (pSeulId && pCarteId !== pSeulId) return;
        if (!pNoeud || (typeof tRoot.contains === "function" && !tRoot.contains (pNoeud))) return;
        const tEtat = pCarteId ? tEtats.get (pCarteId) : null;
        if (!tEtat) return;

        const tDx =
          Math.sin ((tSec * Math.PI * 2 * tEtat.freq_x) + tEtat.phase_x) * tEtat.amp_x
          + Math.sin ((tSec * Math.PI * 2 * (tEtat.freq_x * 1.73)) + (tEtat.phase_x * 0.7)) * (tEtat.amp_x * 0.22);
        const tDy =
          Math.sin ((tSec * Math.PI * 2 * tEtat.freq_y) + tEtat.phase_y) * tEtat.amp_y
          + Math.sin ((tSec * Math.PI * 2 * (tEtat.freq_y * 1.61)) + (tEtat.phase_y * 0.8)) * (tEtat.amp_y * 0.2);
        const tRotSin = Math.sin ((tSec * Math.PI * 2 * tEtat.freq_rot) + tEtat.phase_rot);
        const tRotMidFactor = (tEtat.rot_factor_min + tEtat.rot_factor_max) / 2;
        const tRotSpanFactor = Math.max (0, (tEtat.rot_factor_max - tEtat.rot_factor_min) / 2);
        const tBaseAbs = Math.abs (tEtat.base_rotation || 0);
        const tRotCenter = tBaseAbs >= 1
          ? (tEtat.base_rotation * tRotMidFactor)
          : 0;
        const tRotAmplitude = tBaseAbs >= 1
          ? (tBaseAbs * tRotSpanFactor)
          : (6 * tRotSpanFactor);
	        const tRot = tRotCenter + (tRotSin * tRotAmplitude);
	        const tScaleWaveA = (Math.sin ((tSec * Math.PI * 2 * tEtat.freq_scale) + tEtat.phase_scale) + 1) / 2;
	        const tScaleWaveB = (Math.sin ((tSec * Math.PI * 2 * tEtat.freq_scale_2) + tEtat.phase_scale_2) + 1) / 2;
	        const tScaleWave = (tScaleWaveA * 0.72) + (tScaleWaveB * 0.28);
	        const tScale = tEtat.scale_min + ((tEtat.scale_max - tEtat.scale_min) * tScaleWave);
	        const tPressScale = Math.max (0.01, Number (tEtat.press_scale) || 1);

	        const tScaleFinal = Math.max (0.1, tScale * tPressScale).toFixed (5);
	        if (tEtat.type === "item")
	          {
	            const tCx = Math.max (8, Number (tEtat.item_w) || 96) / 2;
	            const tCy = Math.max (8, Number (tEtat.item_h) || 96) / 2;
	            pNoeud.setAttribute (
	              "transform",
	              `translate(${(tEtat.base_x + tDx).toFixed (3)} ${(tEtat.base_y + tDy).toFixed (3)}) rotate(${(normalise_rotation_degres_client (tEtat.static_rotation) + tRot).toFixed (3)} ${tCx} ${tCy}) translate(${tCx} ${tCy}) scale(${tScaleFinal}) translate(${-tCx} ${-tCy})`
	            );
	            return;
	          }
	        pNoeud.setAttribute (
	          "transform",
	          `translate(${(tEtat.base_x + tDx).toFixed (3)} ${(tEtat.base_y + tDy).toFixed (3)}) rotate(${tRot.toFixed (3)}) scale(${tScaleFinal})`
	        );
	      });
	  }

function demarre_flottement_bulles_lecture_tpl (pTpl)
  {
    if (!pTpl || pTpl._lectureBullesFlottementRaf) return;
    if (flottement_bulles_lecture_en_pause_tpl (pTpl))
      {
        // En pause : pas de boucle, mais les nœuds (re)créés entre-temps prennent la pose figée.
        applique_flottement_bulles_lecture_tpl (pTpl, performance.now ());
        return;
      }
    const tStep = (pNow) =>
      {
        if (flottement_bulles_lecture_suspendu_tpl (pTpl) || flottement_bulles_lecture_en_pause_tpl (pTpl))
          {
            pTpl._lectureBullesFlottementRaf = null;
            pTpl._lectureBullesFlottementHorlogeMs = null;
            return;
          }
        const tDelaiFrame = delai_frame_animation_lecture_scene ();
        const tDerniereFrame = Number (pTpl._lectureBullesFlottementDerniereFrame) || 0;
        if (tDelaiFrame <= 0 || !tDerniereFrame || pNow - tDerniereFrame >= tDelaiFrame)
          {
            // Horloge propre au flottement : elle n'avance que lorsqu'il tourne.
            const tHorlogePrecedente = Number (pTpl._lectureBullesFlottementHorlogeMs) || 0;
            if (!Number.isFinite (Number (pTpl._lectureBullesFlottementTempsMs)))
              pTpl._lectureBullesFlottementTempsMs = pNow;
            if (tHorlogePrecedente > 0)
              {
                const tDepuisReprise = pNow - (Number (pTpl._lectureBullesFlottementRepriseMs) || pNow);
                const tTaux = Math.max (0, Math.min (1, tDepuisReprise / LECTURE_BULLES_FLOTTEMENT_REPRISE_MS));
                const tPas = Math.max (0, Math.min (LECTURE_BULLES_FLOTTEMENT_PAS_MAX_MS, pNow - tHorlogePrecedente));
                pTpl._lectureBullesFlottementTempsMs = Number (pTpl._lectureBullesFlottementTempsMs) + (tPas * tTaux);
              }
            else
              pTpl._lectureBullesFlottementRepriseMs = pNow;
            pTpl._lectureBullesFlottementHorlogeMs = pNow;
            pTpl._lectureBullesFlottementDerniereFrame = pNow;
            applique_flottement_bulles_lecture_tpl (pTpl, pNow);
          }
        pTpl._lectureBullesFlottementRaf = requestAnimationFrame (tStep);
      };
    pTpl._lectureBullesFlottementRaf = requestAnimationFrame (tStep);
  }

// État de flottement d'un item d'interface, créé ou remis à jour dans pEtats ; renvoie sa clé.
function assure_etat_flottement_item_lecture (pEtats, pItem, pReglages)
  {
    const tItemId = `item:${pItem.id}`;
    const tBulleLike = {
      carte_id : tItemId,
      x : Number (pItem.item_x) || 0,
      y : Number (pItem.item_y) || 0,
      rotation : 0,
    };
    const tEtat = pEtats.get (tItemId) || fabrique_etat_flottement_bulle_lecture_client (tBulleLike, pReglages);
    const tNouveau = fabrique_etat_flottement_bulle_lecture_client (tBulleLike, pReglages);
    tEtat.amp_x = tNouveau.amp_x;
    tEtat.amp_y = tNouveau.amp_y;
    tEtat.freq_x = tNouveau.freq_x;
    tEtat.freq_y = tNouveau.freq_y;
    tEtat.freq_rot = tNouveau.freq_rot;
    tEtat.freq_scale = tNouveau.freq_scale;
    tEtat.freq_scale_2 = tNouveau.freq_scale_2;
    tEtat.scale_min = tNouveau.scale_min;
    tEtat.scale_max = tNouveau.scale_max;
    tEtat.rot_factor_min = tNouveau.rot_factor_min;
    tEtat.rot_factor_max = tNouveau.rot_factor_max;
    tEtat.base_x = tBulleLike.x;
    tEtat.base_y = tBulleLike.y;
    tEtat.base_rotation = 0;
    tEtat.static_rotation = normalise_rotation_degres_client (pItem.item_rotation_deg);
    tEtat.type = "item";
    tEtat.item_w = Math.max (8, Number (pItem.item_w) || 96);
    tEtat.item_h = Math.max (8, Number (pItem.item_h) || 96);
    if (!Number.isFinite (Number (tEtat.press_scale)))
      tEtat.press_scale = 1;
    pEtats.set (tItemId, tEtat);
    return tItemId;
  }

// Les nœuds d'items viennent d'être (re)construits, donc à leur pose de base, et les tables du
// flottement pointent encore sur les anciens. On y rattache tout de suite les items flottants affichés
// et on leur applique la pose de l'horloge, qu'elle tourne ou soit en pause : sans cela un item
// reconstruit hors du rafraîchissement des décorations (sortie de carte, sous le couvercle) restait
// figé à sa pose de base puis sautait sur sa trajectoire à la reprise du flottement.
function rattache_items_flottement_lecture_tpl (pTpl)
  {
    if (!pTpl || flottement_bulles_lecture_suspendu_tpl (pTpl)) return;
    const tRoot = pTpl._svgDoc && pTpl._svgDoc.documentElement;
    if (!tRoot || typeof tRoot.querySelectorAll !== "function") return;
    const tParId = new Map ();
    items_interface_scene ().forEach ((pItem) =>
      {
        if (pItem && pItem.id && pItem.item_animation_bulles === true && !pItem.item_masque)
          tParId.set (pItem.id, pItem);
      });
    if (tParId.size === 0) return;
    const tEtats = pTpl._lectureBullesFlottementEtats instanceof Map
      ? pTpl._lectureBullesFlottementEtats
      : new Map ();
    const tNoeuds = pTpl._lectureBullesFlottementNoeuds instanceof Map
      ? pTpl._lectureBullesFlottementNoeuds
      : new Map ();
    const tReglages = reglages_bulles_projet_courant ();
    let tRattaches = 0;
    tRoot.querySelectorAll ("[data-cogibulle-interface-item='1']").forEach ((pNoeud) =>
      {
        const tItem = tParId.get (pNoeud && pNoeud.getAttribute && pNoeud.getAttribute ("id"));
        if (!tItem) return;
        tNoeuds.set (assure_etat_flottement_item_lecture (tEtats, tItem, tReglages), pNoeud);
        tRattaches += 1;
      });
    if (!tRattaches) return;
    pTpl._lectureBullesFlottementEtats = tEtats;
    pTpl._lectureBullesFlottementNoeuds = tNoeuds;
    applique_flottement_bulles_lecture_tpl (pTpl, performance.now ());
    demarre_flottement_bulles_lecture_tpl (pTpl);
  }

function synchronise_flottement_bulles_lecture_tpl (pTpl, pBulles)
  {
    if (!pTpl) return;
    if (!mode_lecture_scene_actif ())
      {
        arrete_flottement_bulles_lecture_tpl (pTpl);
        pTpl._lectureBullesFlottementEtats = new Map ();
        pTpl._lectureBullesFlottementNoeuds = new Map ();
        return;
      }

    const tBulles = Array.isArray (pBulles)
      ? pBulles.filter ((pBulle) => pBulle && pBulle.carte_id)
      : [];
    const tItems = (!items_interface_masques_lecture_tpl (pTpl) && categorie_scene_visible (CALQUES_CATEGORIE_INTERFACE))
      ? items_interface_scene ().filter ((pItem) => pItem
        && pItem.item_animation_bulles === true
        && !pItem.item_masque
        && objet_visible_mode_affichage_diffusion_client (pItem))
      : [];
    const tReglages = reglages_bulles_projet_courant ();
    if (!tBulles.length && !tItems.length)
      {
        arrete_flottement_bulles_lecture_tpl (pTpl);
        pTpl._lectureBullesFlottementEtats = new Map ();
        pTpl._lectureBullesFlottementNoeuds = new Map ();
        return;
      }

    const tEtats = pTpl._lectureBullesFlottementEtats instanceof Map
      ? pTpl._lectureBullesFlottementEtats
      : new Map ();
    const tIdsPresents = new Set ();

    tBulles.forEach ((pBulle) =>
      {
        const tCarteId = pBulle.carte_id;
        tIdsPresents.add (tCarteId);
        const tEtat = tEtats.get (tCarteId) || fabrique_etat_flottement_bulle_lecture_client (pBulle, tReglages);
        const tNouveau = fabrique_etat_flottement_bulle_lecture_client (pBulle, tReglages);
        tEtat.amp_x = tNouveau.amp_x;
        tEtat.amp_y = tNouveau.amp_y;
        tEtat.freq_x = tNouveau.freq_x;
        tEtat.freq_y = tNouveau.freq_y;
        tEtat.freq_rot = tNouveau.freq_rot;
        tEtat.freq_scale = tNouveau.freq_scale;
        tEtat.freq_scale_2 = tNouveau.freq_scale_2;
        tEtat.scale_min = tNouveau.scale_min;
        tEtat.scale_max = tNouveau.scale_max;
	        tEtat.rot_factor_min = tNouveau.rot_factor_min;
	        tEtat.rot_factor_max = tNouveau.rot_factor_max;
	        tEtat.base_x = Number (pBulle.x) || 0;
	        tEtat.base_y = Number (pBulle.y) || 0;
	        tEtat.base_rotation = Number (pBulle.rotation) || 0;
        if (!Number.isFinite (Number (tEtat.press_scale)))
          tEtat.press_scale = 1;
        tEtats.set (tCarteId, tEtat);
      });

    tItems.forEach ((pItem) => tIdsPresents.add (assure_etat_flottement_item_lecture (tEtats, pItem, tReglages)));

    Array.from (tEtats.keys ()).forEach ((pCarteId) =>
      {
        if (!tIdsPresents.has (pCarteId)) tEtats.delete (pCarteId);
      });

    pTpl._lectureBullesFlottementEtats = tEtats;
    const tNoeuds = new Map ();
    const tRoot = pTpl._svgDoc && pTpl._svgDoc.documentElement;
    if (tRoot && typeof tRoot.querySelectorAll === "function")
      {
        tRoot.querySelectorAll (`.${CARTE_DECOR_BULLE_CLASS}[data-carte-id]`).forEach ((pNoeud) =>
          {
            const tCarteId = pNoeud && pNoeud.getAttribute && pNoeud.getAttribute ("data-carte-id");
            if (tCarteId && tEtats.has (tCarteId)) tNoeuds.set (tCarteId, pNoeud);
          });
        tRoot.querySelectorAll ("[data-cogibulle-interface-item='1']").forEach ((pNoeud) =>
          {
            const tItemId = pNoeud && pNoeud.getAttribute && pNoeud.getAttribute ("id");
            const tCle = tItemId ? `item:${tItemId}` : "";
            if (tCle && tEtats.has (tCle)) tNoeuds.set (tCle, pNoeud);
          });
      }
    pTpl._lectureBullesFlottementNoeuds = tNoeuds;
    demarre_flottement_bulles_lecture_tpl (pTpl);
  }

function vue_zoom_alternatif_carte_brute_tpl (pTpl, pCarte, pReglagesOverride = null)
  {
    if (!pTpl || !pCarte || !pCarte.vue) return null;
    const tDepart = vue_camera_brute_courante_scene (pTpl)
      || vue_camera_brute_depuis_vue_svg_scene (pTpl, pTpl._svgVB);
    const tCarte = normalise_vue_carte_client (pCarte.vue);
    const tBase = normalise_vue_carte_client (pTpl._svgVBNatifBrut)
      || normalise_vue_carte_client (pTpl._svgVBInitBrut)
      || tDepart;
    if (!tDepart || !tCarte || !tBase) return null;
    const tRatio = tBase.w / tBase.h;
    const tReglages = reglages_interface_carte_effectifs (pCarte, pReglagesOverride);
    const tFacteur = Math.max (0.2, Number (tReglages.zoom_alternatif_focus_facteur) || 2);
    const tCibleW = Math.max (1, tBase.w / tFacteur);
    const tCibleH = tCibleW / tRatio;
    const tUniteParPxFocus = tCibleW / CARTE_ZOOM_ALTERNATIF_REFERENCE_PX;
    const tOffsetX = (Number (tReglages.zoom_alternatif_focus_offset_x) || 0) * tUniteParPxFocus;
    const tOffsetY = (Number (tReglages.zoom_alternatif_focus_offset_y) || 0) * tUniteParPxFocus;
    const tCx = tCarte.x + (tCarte.w / 2);
    const tCy = tCarte.y + (tCarte.h / 2);
    return {
      x : tCx - (tCibleW / 2) - tOffsetX,
      y : tCy - (tCibleH / 2) - tOffsetY,
      w : tCibleW,
      h : tCibleH,
    };
  }

function vue_cadre_zoom_alternatif_carte_brute_tpl (pTpl, pCarte, pReglagesOverride = null)
  {
    const tCarte = normalise_vue_carte_client (pCarte && pCarte.vue);
    const tVueFocus = vue_zoom_alternatif_carte_brute_tpl (pTpl, pCarte, pReglagesOverride);
    if (!tCarte || !tVueFocus) return tCarte;
    const tReglages = reglages_interface_carte_effectifs (pCarte, pReglagesOverride);
    const tUniteParPxFocus = Math.max (0.000001, tVueFocus.w / CARTE_ZOOM_ALTERNATIF_REFERENCE_PX);
    const marge_cadre = (pCle) =>
      Math.max (0, Number (tReglages[pCle]) || 0) * tUniteParPxFocus;
    const tMargeHaut = marge_cadre ("zoom_alternatif_cadre_epaisseur_haut");
    const tMargeDroite = marge_cadre ("zoom_alternatif_cadre_epaisseur_droite");
    const tMargeBas = marge_cadre ("zoom_alternatif_cadre_epaisseur_bas");
    const tMargeGauche = marge_cadre ("zoom_alternatif_cadre_epaisseur_gauche");
    return {
      x : tCarte.x - tMargeGauche,
      y : tCarte.y - tMargeHaut,
      w : tCarte.w + tMargeGauche + tMargeDroite,
      h : tCarte.h + tMargeHaut + tMargeBas,
    };
  }

function rayon_rect_zoom_alternatif (pVue, pRayon)
  {
    const tVue = normalise_vue_carte_client (pVue);
    if (!tVue) return 0;
    return Math.max (0, Math.min (Number (pRayon) || 0, tVue.w / 2, tVue.h / 2));
  }

function path_rect_arrondi_zoom_alternatif (pVue, pRayon = 0, pOffsetX = 0, pOffsetY = 0)
  {
    const tVue = normalise_vue_carte_client (pVue);
    if (!tVue) return "";
    const tX = tVue.x + (Number (pOffsetX) || 0);
    const tY = tVue.y + (Number (pOffsetY) || 0);
    const tR = rayon_rect_zoom_alternatif (tVue, pRayon);
    if (tR <= 0)
      return [
        `M ${tX} ${tY}`,
        `h ${tVue.w}`,
        `v ${tVue.h}`,
        `h ${-tVue.w}`,
        "Z",
      ].join (" ");
    return [
      `M ${tX + tR} ${tY}`,
      `H ${tX + tVue.w - tR}`,
      `A ${tR} ${tR} 0 0 1 ${tX + tVue.w} ${tY + tR}`,
      `V ${tY + tVue.h - tR}`,
      `A ${tR} ${tR} 0 0 1 ${tX + tVue.w - tR} ${tY + tVue.h}`,
      `H ${tX + tR}`,
      `A ${tR} ${tR} 0 0 1 ${tX} ${tY + tVue.h - tR}`,
      `V ${tY + tR}`,
      `A ${tR} ${tR} 0 0 1 ${tX + tR} ${tY}`,
      "Z",
    ].join (" ");
  }

function path_cadre_zoom_alternatif_carte (pFrameVue, pCarteVue, pOffsetX = 0, pOffsetY = 0, pDecaleTrou = false, pRayon = 0)
  {
    const tFrame = normalise_vue_carte_client (pFrameVue);
    const tCarte = normalise_vue_carte_client (pCarteVue);
    if (!tFrame || !tCarte) return "";
    const tX = Number (pOffsetX) || 0;
    const tY = Number (pOffsetY) || 0;
    const tTrouX = pDecaleTrou ? tX : 0;
    const tTrouY = pDecaleTrou ? tY : 0;
    return [
      path_rect_arrondi_zoom_alternatif (tFrame, pRayon, tX, tY),
      path_rect_arrondi_zoom_alternatif (tCarte, pRayon, tTrouX, tTrouY),
    ].join (" ");
  }

function config_relief_cadre_zoom_alternatif (pIndex)
  {
    if (pIndex === 2)
      return {
        index : 2,
        active : "zoom_alternatif_cadre_arriere_plan_2_active",
        couleur : "zoom_alternatif_cadre_arriere_plan_2_couleur",
        offsetX : "zoom_alternatif_cadre_arriere_plan_2_offset_x",
        offsetY : "zoom_alternatif_cadre_arriere_plan_2_offset_y",
        opacite : "zoom_alternatif_cadre_arriere_plan_2_opacite",
        rotation : "zoom_alternatif_cadre_arriere_plan_2_rotation",
        largeurPct : "zoom_alternatif_cadre_arriere_plan_2_largeur_pct",
        hauteurPct : "zoom_alternatif_cadre_arriere_plan_2_hauteur_pct",
        arrondi : "zoom_alternatif_cadre_arriere_plan_2_arrondi",
        ombreActive : "zoom_alternatif_cadre_arriere_plan_2_ombre_svg_active",
        ombreCouleur : "zoom_alternatif_cadre_arriere_plan_2_ombre_svg_couleur",
        ombreOffsetX : "zoom_alternatif_cadre_arriere_plan_2_ombre_svg_offset_x",
        ombreOffsetY : "zoom_alternatif_cadre_arriere_plan_2_ombre_svg_offset_y",
        ombreOpacite : "zoom_alternatif_cadre_arriere_plan_2_ombre_svg_opacite",
        ombreFlou : "zoom_alternatif_cadre_arriere_plan_2_ombre_svg_flou",
      };
    return {
      index : 1,
      active : "zoom_alternatif_cadre_ombre_active",
      couleur : "zoom_alternatif_cadre_ombre_couleur",
      offsetX : "zoom_alternatif_cadre_ombre_offset_x",
      offsetY : "zoom_alternatif_cadre_ombre_offset_y",
      opacite : "zoom_alternatif_cadre_ombre_opacite",
      rotation : "zoom_alternatif_cadre_ombre_rotation",
      largeurPct : "zoom_alternatif_cadre_ombre_largeur_pct",
      hauteurPct : "zoom_alternatif_cadre_ombre_hauteur_pct",
      arrondi : "zoom_alternatif_cadre_ombre_arrondi",
      ombreActive : "zoom_alternatif_cadre_ombre_svg_active",
      ombreCouleur : "zoom_alternatif_cadre_ombre_svg_couleur",
      ombreOffsetX : "zoom_alternatif_cadre_ombre_svg_offset_x",
      ombreOffsetY : "zoom_alternatif_cadre_ombre_svg_offset_y",
      ombreOpacite : "zoom_alternatif_cadre_ombre_svg_opacite",
      ombreFlou : "zoom_alternatif_cadre_ombre_svg_flou",
    };
  }

function relief_cadre_zoom_alternatif (pFrameVue, pReglages, pIndex = 1)
  {
    const tFrame = normalise_vue_carte_client (pFrameVue);
    if (!tFrame) return null;
    const tReglages = pReglages || REGLAGES_MODE_LECTURE_PROJET_DEFAUTS;
    const tConfig = config_relief_cadre_zoom_alternatif (pIndex);
    if (!tReglages[tConfig.active]) return null;
    const tUniteParPxCadre = Math.max (0.000001, tFrame.w / CARTE_ZOOM_ALTERNATIF_REFERENCE_PX);
    const tOffsetX = (Number (tReglages[tConfig.offsetX]) || 0) * tUniteParPxCadre;
    const tOffsetY = (Number (tReglages[tConfig.offsetY]) || 0) * tUniteParPxCadre;
    const tRotation = Number (tReglages[tConfig.rotation]) || 0;
    const tLargeurPct = Math.max (0.1, Number (tReglages[tConfig.largeurPct]) || 100);
    const tHauteurPct = Math.max (0.1, Number (tReglages[tConfig.hauteurPct]) || 100);
    const tOmbreW = tFrame.w * tLargeurPct / 100;
    const tOmbreH = tFrame.h * tHauteurPct / 100;
    const tOmbreSvgActive = !!tReglages[tConfig.ombreActive];
    const tOmbreSvg = tOmbreSvgActive
      ? {
        couleur : tReglages[tConfig.ombreCouleur] || "#000000",
        offsetX : (Number (tReglages[tConfig.ombreOffsetX]) || 0) * tUniteParPxCadre,
        offsetY : (Number (tReglages[tConfig.ombreOffsetY]) || 0) * tUniteParPxCadre,
        opacite : Math.max (0, Math.min (1, Number (tReglages[tConfig.ombreOpacite]) || 0)),
        flou : Math.max (0, Number (tReglages[tConfig.ombreFlou]) || 0) * tUniteParPxCadre,
      }
      : null;
    return {
      index : tConfig.index,
      offsetX : tOffsetX,
      offsetY : tOffsetY,
      rotation : tRotation,
      couleur : tReglages[tConfig.couleur] || "#000000",
      opacite : Math.max (0, Math.min (1, Number (tReglages[tConfig.opacite]) || 0)),
      centreX : tFrame.x + tFrame.w / 2,
      centreY : tFrame.y + tFrame.h / 2,
      rectVue : {
        x : tFrame.x + (tFrame.w - tOmbreW) / 2,
        y : tFrame.y + (tFrame.h - tOmbreH) / 2,
        w : tOmbreW,
        h : tOmbreH,
      },
      arrondi : (Number (tReglages[tConfig.arrondi]) || 0) * tUniteParPxCadre,
      cadreArrondi : (Number (tReglages.zoom_alternatif_cadre_arrondi) || 0) * tUniteParPxCadre,
      ombreSvg : tOmbreSvg,
    };
  }

function reliefs_cadre_zoom_alternatif (pFrameVue, pReglages)
  {
    return [2, 1]
      .map ((pIndex) => relief_cadre_zoom_alternatif (pFrameVue, pReglages, pIndex))
      .filter (Boolean);
  }

function points_exterieurs_relief_cadre_zoom_alternatif (pFrameVue, pRelief)
  {
    const tFrame = normalise_vue_carte_client ((pRelief && pRelief.rectVue) || pFrameVue);
    if (!tFrame || !pRelief) return [];
    const tAngle = (Number (pRelief.rotation) || 0) * Math.PI / 180;
    const tCos = Math.cos (tAngle);
    const tSin = Math.sin (tAngle);
    return [
      {x : tFrame.x, y : tFrame.y},
      {x : tFrame.x + tFrame.w, y : tFrame.y},
      {x : tFrame.x + tFrame.w, y : tFrame.y + tFrame.h},
      {x : tFrame.x, y : tFrame.y + tFrame.h},
    ].map ((pPoint) =>
      {
        const tX = pPoint.x + pRelief.offsetX;
        const tY = pPoint.y + pRelief.offsetY;
        const tDx = tX - pRelief.centreX;
        const tDy = tY - pRelief.centreY;
        return {
          x : pRelief.centreX + tDx * tCos - tDy * tSin,
          y : pRelief.centreY + tDx * tSin + tDy * tCos,
        };
      });
  }

function path_relief_cadre_zoom_alternatif_carte (pFrameVue, pCarteVue, pRelief)
  {
    if (!pCarteVue || !pRelief) return "";
    if (!(Number (pRelief.arrondi) || 0))
      {
        const tPoints = points_exterieurs_relief_cadre_zoom_alternatif (pFrameVue, pRelief);
        if (tPoints.length !== 4) return "";
        return [
          `M ${tPoints[0].x} ${tPoints[0].y}`,
          `L ${tPoints[1].x} ${tPoints[1].y}`,
          `L ${tPoints[2].x} ${tPoints[2].y}`,
          `L ${tPoints[3].x} ${tPoints[3].y}`,
          "Z",
        ].join (" ");
      }
    return path_rect_arrondi_zoom_alternatif (
      pRelief.rectVue || pFrameVue,
      pRelief.arrondi || 0,
      pRelief.offsetX || 0,
      pRelief.offsetY || 0
    );
  }

function transform_relief_cadre_zoom_alternatif (pRelief)
  {
    if (!pRelief || !(Number (pRelief.arrondi) || 0) || !(Number (pRelief.rotation) || 0)) return "";
    return `rotate(${Number (pRelief.rotation) || 0} ${pRelief.centreX} ${pRelief.centreY})`;
  }

function assure_defs_zoom_alternatif_relief (pRoot)
  {
    if (!pRoot) return null;
    const tDoc = (pRoot && pRoot.ownerDocument) || document;
    const tDefs = tDoc.createElementNS ("http://www.w3.org/2000/svg", "defs");
    tDefs.setAttribute ("data-cogi-zoom-alt-relief", "1");
    pRoot.insertBefore (tDefs, pRoot.firstChild || null);
    return tDefs;
  }

function ajoute_ombre_svg_relief_cadre_zoom_alternatif (pRoot, pParent, pReliefNode, pFrameVue, pRelief)
  {
    if (!pRoot || !pParent || !pReliefNode || !pRelief || !pRelief.ombreSvg) return null;
    const tOmbre = pRelief.ombreSvg;
    if (!(tOmbre.opacite > 0) || !(tOmbre.flou > 0 || tOmbre.offsetX || tOmbre.offsetY)) return null;
    const tReliefOmbre = {
      ...pRelief,
      offsetX : (pRelief.offsetX || 0) + (tOmbre.offsetX || 0),
      offsetY : (pRelief.offsetY || 0) + (tOmbre.offsetY || 0),
      ombreSvg : null,
    };
    const tFrame = normalise_vue_carte_client (pFrameVue);
    const tPoints = points_exterieurs_relief_cadre_zoom_alternatif (tFrame, tReliefOmbre);
    if (!tFrame || tPoints.length !== 4) return null;
    const tDoc = (pRoot && pRoot.ownerDocument) || document;
    const tOmbreNode = pReliefNode.cloneNode (false);
    tOmbreNode.setAttribute ("class", `${CARTE_ZOOM_ALTERNATIF_FRAME_RELIEF_CLASS} ${CARTE_ZOOM_ALTERNATIF_FRAME_RELIEF_CLASS}-shadow`);
    tOmbreNode.setAttribute ("d", path_relief_cadre_zoom_alternatif_carte (pFrameVue, pFrameVue, tReliefOmbre));
    tOmbreNode.setAttribute ("fill", tOmbre.couleur || "#000000");
    tOmbreNode.setAttribute ("fill-opacity", String (tOmbre.opacite));
    tOmbreNode.setAttribute ("stroke", "none");
    const tReliefTransform = transform_relief_cadre_zoom_alternatif (tReliefOmbre);
    if (tReliefTransform) tOmbreNode.setAttribute ("transform", tReliefTransform);
    if (tOmbreNode.style) tOmbreNode.style.pointerEvents = "none";
    if (tOmbre.flou > 0)
      {
        const tDefs = assure_defs_zoom_alternatif_relief (pRoot);
        if (!tDefs) return null;
        const tId = `cogi_zoom_alt_relief_shadow_${Date.now ()}_${++gZoomAlternatifReliefMaskSeq}`;
        const tPad = Math.max (1, (tOmbre.flou || 0) * 4 + Math.max (tFrame.w, tFrame.h) * 0.04);
        const tXs = tPoints.map ((pPoint) => pPoint.x);
        const tYs = tPoints.map ((pPoint) => pPoint.y);
        const tFilter = tDoc.createElementNS ("http://www.w3.org/2000/svg", "filter");
        tFilter.setAttribute ("id", tId);
        tFilter.setAttribute ("filterUnits", "userSpaceOnUse");
        tFilter.setAttribute ("x", String (Math.min (...tXs) - tPad));
        tFilter.setAttribute ("y", String (Math.min (...tYs) - tPad));
        tFilter.setAttribute ("width", String (Math.max (...tXs) - Math.min (...tXs) + tPad * 2));
        tFilter.setAttribute ("height", String (Math.max (...tYs) - Math.min (...tYs) + tPad * 2));
        tFilter.setAttribute ("color-interpolation-filters", "sRGB");
        const tBlur = tDoc.createElementNS ("http://www.w3.org/2000/svg", "feGaussianBlur");
        tBlur.setAttribute ("in", "SourceGraphic");
        tBlur.setAttribute ("stdDeviation", String (tOmbre.flou || 0));
        tFilter.appendChild (tBlur);
        tDefs.appendChild (tFilter);
        tOmbreNode.setAttribute ("filter", `url(#${tId})`);
      }
    applique_masque_relief_cadre_zoom_alternatif (
      pRoot,
      tOmbreNode,
      pFrameVue,
      tReliefOmbre,
      {
        surfaceLibre : true,
        pad : Math.max (0, Number (tOmbre.flou) || 0) * 4,
      }
    );
    pParent.appendChild (tOmbreNode);
    return tOmbreNode;
  }

function applique_masque_relief_cadre_zoom_alternatif (pRoot, pReliefNode, pFrameVue, pRelief, pOpt = {})
  {
    const tFrame = normalise_vue_carte_client (pFrameVue);
    const tPoints = points_exterieurs_relief_cadre_zoom_alternatif (tFrame, pRelief);
    if (!pRoot || !pReliefNode || !tFrame || tPoints.length !== 4) return;
    const tDefs = assure_defs_zoom_alternatif_relief (pRoot);
    if (!tDefs) return;
    const tDoc = (pRoot && pRoot.ownerDocument) || document;
    const tId = `cogi_zoom_alt_relief_mask_${Date.now ()}_${++gZoomAlternatifReliefMaskSeq}`;
    const tMinX = Math.min (tFrame.x, ...tPoints.map ((pPoint) => pPoint.x));
    const tMinY = Math.min (tFrame.y, ...tPoints.map ((pPoint) => pPoint.y));
    const tMaxX = Math.max (tFrame.x + tFrame.w, ...tPoints.map ((pPoint) => pPoint.x));
    const tMaxY = Math.max (tFrame.y + tFrame.h, ...tPoints.map ((pPoint) => pPoint.y));
    const tPad = Math.max (1, Math.max (tFrame.w, tFrame.h) * 0.05, Number (pOpt.pad) || 0);
    const tMask = tDoc.createElementNS ("http://www.w3.org/2000/svg", "mask");
    tMask.setAttribute ("id", tId);
    tMask.setAttribute ("maskUnits", "userSpaceOnUse");
    tMask.setAttribute ("maskContentUnits", "userSpaceOnUse");
    tMask.setAttribute ("x", String (tMinX - tPad));
    tMask.setAttribute ("y", String (tMinY - tPad));
    tMask.setAttribute ("width", String (tMaxX - tMinX + tPad * 2));
    tMask.setAttribute ("height", String (tMaxY - tMinY + tPad * 2));

    if (pOpt.surfaceLibre)
      {
        const tForme = tDoc.createElementNS ("http://www.w3.org/2000/svg", "rect");
        tForme.setAttribute ("x", String (tMinX - tPad));
        tForme.setAttribute ("y", String (tMinY - tPad));
        tForme.setAttribute ("width", String (tMaxX - tMinX + tPad * 2));
        tForme.setAttribute ("height", String (tMaxY - tMinY + tPad * 2));
        tForme.setAttribute ("fill", "#ffffff");
        tMask.appendChild (tForme);
      }
    else
      {
        const tForme = tDoc.createElementNS ("http://www.w3.org/2000/svg", "path");
        tForme.setAttribute ("d", path_relief_cadre_zoom_alternatif_carte (tFrame, tFrame, pRelief));
        tForme.setAttribute ("fill", "#ffffff");
        const tReliefTransform = transform_relief_cadre_zoom_alternatif (pRelief);
        if (tReliefTransform) tForme.setAttribute ("transform", tReliefTransform);
        tMask.appendChild (tForme);
      }

    const tCacheCarte = tDoc.createElementNS ("http://www.w3.org/2000/svg", "path");
    tCacheCarte.setAttribute ("d", path_rect_arrondi_zoom_alternatif (tFrame, pRelief.cadreArrondi || 0));
    tCacheCarte.setAttribute ("fill", "#000000");
    tMask.appendChild (tCacheCarte);

    tDefs.appendChild (tMask);
    pReliefNode.setAttribute ("mask", `url(#${tId})`);
  }

function vue_photo_focus_carte_avec_relief (pFrameVue, pReglages = null)
  {
    const tFrame = normalise_vue_carte_client (pFrameVue);
    if (!tFrame) return null;
    const tReliefs = reliefs_cadre_zoom_alternatif (tFrame, pReglages);
    if (!tReliefs.length) return tFrame;
    const tCorners = tReliefs.flatMap ((pRelief) =>
      points_exterieurs_relief_cadre_zoom_alternatif (tFrame, pRelief));
    const tOmbreCorners = tReliefs.flatMap ((pRelief) =>
      {
        if (!pRelief || !pRelief.ombreSvg) return [];
        const tOmbre = pRelief.ombreSvg;
        const tPad = Math.max (0, Number (tOmbre.flou) || 0) * 4;
        return points_exterieurs_relief_cadre_zoom_alternatif (tFrame, pRelief).flatMap ((pPoint) =>
          [
            {x : pPoint.x + (tOmbre.offsetX || 0) - tPad, y : pPoint.y + (tOmbre.offsetY || 0) - tPad},
            {x : pPoint.x + (tOmbre.offsetX || 0) + tPad, y : pPoint.y + (tOmbre.offsetY || 0) + tPad},
          ]);
      });
    const tPoints = [...tCorners, ...tOmbreCorners];
    const tMinX = Math.min (tFrame.x, ...tPoints.map ((pPoint) => pPoint.x));
    const tMinY = Math.min (tFrame.y, ...tPoints.map ((pPoint) => pPoint.y));
    const tMaxX = Math.max (tFrame.x + tFrame.w, ...tPoints.map ((pPoint) => pPoint.x));
    const tMaxY = Math.max (tFrame.y + tFrame.h, ...tPoints.map ((pPoint) => pPoint.y));
    return {
      x : tMinX,
      y : tMinY,
      w : tMaxX - tMinX,
      h : tMaxY - tMinY,
    };
  }

function nettoie_clone_svg_pour_photo_focus_carte (pClone)
  {
    if (!pClone || !pClone.querySelectorAll) return pClone;
    const tSelecteurs = [
      `.${CARTE_DECOR_FOND_CLASS}`,
      `.${CARTE_DECOR_MASQUE_CLASS}`,
      `.${CARTE_DECOR_BULLES_LAYER_CLASS}`,
      `.${CARTE_ZOOM_ALTERNATIF_GROUP_CLASS}`,
      `.${HOVER_BULLE_GROUP_CLASS}`,
      `.${SELECTION_BULLE_GROUP_CLASS}`,
      `.${CATEGORIE_BULLES_GROUP_CLASS}`,
      ".fond_transition_camera_lecture",
      ".masque_transition_camera_lecture",
    ].join (", ");
    Array.from (pClone.querySelectorAll (tSelecteurs)).forEach ((pNoeud) =>
      {
        if (pNoeud && pNoeud.parentNode) pNoeud.parentNode.removeChild (pNoeud);
      });
    Array.from (pClone.querySelectorAll ("[data-cogi-carte-masked='1']")).forEach ((pNoeud) =>
      {
        if (!pNoeud || !pNoeud.style) return;
        const tPrevDisplay = pNoeud.getAttribute (CARTE_MASK_PREV_DISPLAY_ATTR);
        const tPrevOpacity = pNoeud.getAttribute (CARTE_MASK_PREV_OPACITY_ATTR);
        if (tPrevDisplay) pNoeud.style.display = tPrevDisplay;
        else              pNoeud.style.removeProperty ("display");
        if (tPrevOpacity) pNoeud.style.opacity = tPrevOpacity;
        else              pNoeud.style.removeProperty ("opacity");
        pNoeud.removeAttribute (CARTE_MASK_ATTR);
        pNoeud.removeAttribute (CARTE_MASK_STATE_ATTR);
      });
    return pClone;
  }

function rects_vues_intersectent (pA, pB)
  {
    if (!pA || !pB) return false;
    const tAx = Number (pA.x);
    const tAy = Number (pA.y);
    const tAw = Number (pA.w || pA.width);
    const tAh = Number (pA.h || pA.height);
    const tBx = Number (pB.x);
    const tBy = Number (pB.y);
    const tBw = Number (pB.w || pB.width);
    const tBh = Number (pB.h || pB.height);
    if (![tAx, tAy, tAw, tAh, tBx, tBy, tBw, tBh].every (Number.isFinite)) return false;
    if (tAw <= 0 || tAh <= 0 || tBw <= 0 || tBh <= 0) return false;
    return tAx < tBx + tBw
        && tAx + tAw > tBx
        && tAy < tBy + tBh
        && tAy + tAh > tBy;
  }

function bbox_noeud_svg_safe (pNoeud)
  {
    if (!pNoeud || typeof pNoeud.getBBox !== "function") return null;
    try
      {
        const tBBox = pNoeud.getBBox ();
        if (!tBBox || !Number.isFinite (tBBox.x) || !Number.isFinite (tBBox.y)
         || !Number.isFinite (tBBox.width) || !Number.isFinite (tBBox.height)
         || tBBox.width <= 0 || tBBox.height <= 0)
          return null;
        return tBBox;
      }
    catch (e)
      { return null; }
  }

function exclusions_photo_focus_carte_scene ()
  {
    const tScene = Scenes.findOne (Session.get ("scene_id"));
    const tExclus = new Set ();
    const tObjets = svg_objets_scene_normalises (tScene);
    const tBoutons = tObjets && Array.isArray (tObjets[CALQUES_CATEGORIE_BOUTONS])
      ? tObjets[CALQUES_CATEGORIE_BOUTONS]
      : [];
    tBoutons.forEach ((pEntree) =>
      {
        if (!pEntree || !pEntree.id) return;
        tExclus.add (pEntree.id);
        ids_parts_associes_a_objet_scene (tScene, pEntree.id)
          .forEach ((pId) => { if (pId) tExclus.add (pId); });
      });
    items_interface_scene (tScene).forEach ((pItem) =>
      {
        if (pItem && pItem.id && !item_interface_est_source_obj (pItem))
          tExclus.add (pItem.id);
      });
    return tExclus;
  }

function noeud_svg_cache_pour_photo_focus (pNoeud, pExclusions = null)
  {
    if (!pNoeud) return true;
    if (pNoeud.nodeType !== 1) return false;
    const tTag = String (pNoeud.tagName || "").toLowerCase ();
    if (tTag === "defs" || tTag === "style" || tTag === "script"
     || tTag === "foreignobject" || tTag === "iframe" || tTag === "object" || tTag === "embed")
      return true;
    const tExclusions = pExclusions instanceof Set ? pExclusions : null;
    const tId = pNoeud.getAttribute && pNoeud.getAttribute ("id");
    if (tId && tExclusions && tExclusions.has (tId)) return true;
    const tDecorId = id_source_decor_calque_svg (pNoeud);
    if (tDecorId && tExclusions && tExclusions.has (tDecorId)) return true;
    const tItemNoeud = pNoeud.closest && pNoeud.closest ("[data-cogibulle-interface-item='1'][id]");
    if (tItemNoeud && tItemNoeud.getAttribute)
      {
        const tItemId = tItemNoeud.getAttribute ("id");
        if (tItemId && tExclusions && tExclusions.has (tItemId)) return true;
      }
    if (pNoeud.classList)
      {
        if (pNoeud.classList.contains (CARTE_DECOR_FOND_CLASS)
         || pNoeud.classList.contains (CARTE_DECOR_MASQUE_CLASS)
         || pNoeud.classList.contains (CARTE_DECOR_BULLES_LAYER_CLASS)
         || pNoeud.classList.contains (CARTE_ZOOM_ALTERNATIF_GROUP_CLASS)
         || pNoeud.classList.contains (HOVER_BULLE_GROUP_CLASS)
         || pNoeud.classList.contains (SELECTION_BULLE_GROUP_CLASS)
         || pNoeud.classList.contains (CATEGORIE_BULLES_GROUP_CLASS))
          return true;
      }
    try
      {
        if (pNoeud.matches
         && pNoeud.matches (".fond_transition_camera_lecture, .masque_transition_camera_lecture"))
          return true;
      }
    catch (e) {}
    return false;
  }

function clone_noeud_svg_visible_photo_focus (pNoeud, pVue, pExclusions = null)
  {
    if (!pNoeud || noeud_svg_cache_pour_photo_focus (pNoeud, pExclusions)) return null;

    const tBBox = bbox_noeud_svg_safe (pNoeud);
    if (tBBox && !rects_vues_intersectent (tBBox, pVue)) return null;

    const tEnfants = [];
    Array.from (pNoeud.childNodes || []).forEach ((pEnfant) =>
      {
        if (!pEnfant || pEnfant.nodeType !== 1) return;
        const tClone = clone_noeud_svg_visible_photo_focus (pEnfant, pVue, pExclusions);
        if (tClone) tEnfants.push (tClone);
      });

    if (!tEnfants.length && !rects_vues_intersectent (tBBox, pVue)) return null;

    let tClone = null;
    try { tClone = pNoeud.cloneNode (false); }
    catch (e) { return null; }
    tEnfants.forEach ((pEnfant) =>
      {
        try { tClone.appendChild (pEnfant); } catch (e) {}
      });
    return nettoie_clone_svg_pour_photo_focus_carte (tClone);
  }

function cree_wrapper_svg_photo_focus_carte (pTpl, pCarte, pFrameVue, pCarteVue, pCanvasW, pCanvasH)
  {
    const tRoot = pTpl && pTpl._svgDoc && pTpl._svgDoc.documentElement;
    const tFrameVue = normalise_vue_carte_client (pFrameVue);
    const tCarteVue = normalise_vue_carte_client (pCarteVue);
    if (!tRoot || !tFrameVue || !tCarteVue) return null;
    const tReglages = reglages_interface_carte_effectifs (pCarte);
    const tExclusions = exclusions_photo_focus_carte_scene ();
    const tNs = "http://www.w3.org/2000/svg";
    const tReliefsReglages = reliefs_cadre_zoom_alternatif (tFrameVue, tReglages);
    const tWrapperVue = vue_photo_focus_carte_avec_relief (tFrameVue, tReglages) || tFrameVue;
    const tWrapper = document.createElementNS (tNs, "svg");
    tWrapper.setAttribute ("xmlns", tNs);
    tWrapper.setAttribute ("xmlns:xlink", "http://www.w3.org/1999/xlink");
    tWrapper.setAttribute ("viewBox", `${tWrapperVue.x} ${tWrapperVue.y} ${tWrapperVue.w} ${tWrapperVue.h}`);
    tWrapper.setAttribute ("width", String (Math.max (1, Math.round (pCanvasW))));
    tWrapper.setAttribute ("height", String (Math.max (1, Math.round (pCanvasH))));
    tWrapper.setAttribute ("preserveAspectRatio", "none");

    Array.from (tRoot.children || []).forEach ((pNoeud) =>
      {
        const tNom = String ((pNoeud && pNoeud.tagName) || "").toLowerCase ();
        if (tNom !== "defs" && tNom !== "style") return;
        try { tWrapper.appendChild (pNoeud.cloneNode (true)); } catch (e) {}
      });

    tReliefsReglages.forEach ((tReliefReglages) =>
      {
        const tRelief = document.createElementNS (tNs, "path");
        tRelief.setAttribute ("class", CARTE_ZOOM_ALTERNATIF_FRAME_RELIEF_CLASS);
        tRelief.setAttribute ("data-cogi-arriere-plan", String (tReliefReglages.index || 1));
        tRelief.setAttribute ("d", path_relief_cadre_zoom_alternatif_carte (tFrameVue, tCarteVue, tReliefReglages));
        tRelief.setAttribute ("fill-rule", "evenodd");
        tRelief.setAttribute ("clip-rule", "evenodd");
        tRelief.setAttribute ("fill", tReliefReglages.couleur);
        tRelief.setAttribute ("fill-opacity", String (tReliefReglages.opacite));
        tRelief.setAttribute ("stroke", "none");
        const tReliefTransform = transform_relief_cadre_zoom_alternatif (tReliefReglages);
        if (tReliefTransform) tRelief.setAttribute ("transform", tReliefTransform);
        ajoute_ombre_svg_relief_cadre_zoom_alternatif (tWrapper, tWrapper, tRelief, tFrameVue, tReliefReglages);
        applique_masque_relief_cadre_zoom_alternatif (tWrapper, tRelief, tFrameVue, tReliefReglages);
        tWrapper.appendChild (tRelief);
      });

    const tCadre = document.createElementNS (tNs, "path");
    tCadre.setAttribute ("d", path_cadre_zoom_alternatif_carte (
      tFrameVue,
      tCarteVue,
      0,
      0,
      false,
      (Number (tReglages.zoom_alternatif_cadre_arrondi) || 0)
        * Math.max (0.000001, tFrameVue.w / CARTE_ZOOM_ALTERNATIF_REFERENCE_PX)
    ));
    tCadre.setAttribute ("fill-rule", "evenodd");
    tCadre.setAttribute ("clip-rule", "evenodd");
    tCadre.setAttribute ("fill", (tReglages && tReglages.zoom_alternatif_cadre_couleur) || "#f6efd8");
    tCadre.setAttribute ("stroke", (tReglages && tReglages.zoom_alternatif_cadre_couleur) || "#f6efd8");
    tCadre.setAttribute ("stroke-width", "0");
    tWrapper.appendChild (tCadre);

    const tSceneClone = document.createElementNS (tNs, "svg");
    tSceneClone.setAttribute ("x", String (tCarteVue.x));
    tSceneClone.setAttribute ("y", String (tCarteVue.y));
    tSceneClone.setAttribute ("width", String (tCarteVue.w));
    tSceneClone.setAttribute ("height", String (tCarteVue.h));
    tSceneClone.setAttribute ("viewBox", `${tCarteVue.x} ${tCarteVue.y} ${tCarteVue.w} ${tCarteVue.h}`);
    tSceneClone.setAttribute ("preserveAspectRatio", "none");
    Array.from (tRoot.children || []).forEach ((pNoeud) =>
      {
        if (noeud_svg_cache_pour_photo_focus (pNoeud, tExclusions)) return;
        const tClone = clone_noeud_svg_visible_photo_focus (pNoeud, tCarteVue, tExclusions);
        if (!tClone) return;
        try { tSceneClone.appendChild (tClone); } catch (e) {}
      });
    tWrapper.appendChild (tSceneClone);
    rend_urls_svg_absolues_pour_serialisation (tWrapper);
    return tWrapper;
  }

async function cree_canevas_photo_focus_carte_tpl (pTpl, pCarte)
  {
    const tRoot = pTpl && pTpl._svgDoc && pTpl._svgDoc.documentElement;
    const tCarteVue = normalise_vue_carte_client (pCarte && pCarte.vue);
    if (!tRoot || !tCarteVue) return null;
    const tFrameVue = vue_cadre_zoom_alternatif_carte_brute_tpl (pTpl, pCarte);
    if (!tFrameVue) return null;
    const tWebkit = navigateur_webkit_sans_chrome_client ();
    const tDprMax = tWebkit ? 1.25 : 2;
    const tDpr = Math.min (tDprMax, Math.max (1, Number (window.devicePixelRatio) || 1));
    const tFocusVue = vue_zoom_alternatif_carte_brute_tpl (pTpl, pCarte) || vue_camera_brute_courante_scene (pTpl);
    const tSceneElt = element_svg_scene_tpl (pTpl);
    let tSceneRect = null;
    try { tSceneRect = tSceneElt ? tSceneElt.getBoundingClientRect () : null; } catch (e) {}
    const tPxParUnite = tSceneRect && tFocusVue
      ? Math.max (tSceneRect.width / Math.max (1, tFocusVue.w), tSceneRect.height / Math.max (1, tFocusVue.h))
      : 1;
    const tPhotoVue = vue_photo_focus_carte_avec_relief (tFrameVue, reglages_interface_carte_effectifs (pCarte)) || tFrameVue;
    const tW = Math.max (1, Math.ceil (tPhotoVue.w * tPxParUnite * tDpr));
    const tH = Math.max (1, Math.ceil (tPhotoVue.h * tPxParUnite * tDpr));
    const tMaxPixels = tWebkit ? 1200000 : 3500000;
    const tScaleMax = Math.min (1, Math.sqrt (tMaxPixels / Math.max (1, tW * tH)));
    const tCanvasW = Math.max (1, Math.floor (tW * tScaleMax));
    const tCanvasH = Math.max (1, Math.floor (tH * tScaleMax));
    const tWrapper = cree_wrapper_svg_photo_focus_carte (
      pTpl,
      pCarte,
      tFrameVue,
      tCarteVue,
      tCanvasW,
      tCanvasH
    );
    if (!tWrapper) return null;
    await embarque_images_svg_pour_serialisation (tWrapper);
    const tTexte = new XMLSerializer ().serializeToString (tWrapper);
    tWrapper.textContent = "";
    return rasterise_svg_en_canvas (tTexte, tCanvasW, tCanvasH, {preserveViewBox : true});
  }

function rect_photo_focus_carte_tpl (pTpl, pCameraVue, pFrameVue)
  {
    const tSvg = element_svg_scene_tpl (pTpl);
    let tSceneRect = null;
    try { tSceneRect = tSvg ? tSvg.getBoundingClientRect () : null; } catch (e) {}
    const tCadre = cadre_pixels_scene_local (tSceneRect);
    return rect_selection_ecran_local_non_borne_depuis_vues_brutes (pCameraVue, pFrameVue, tCadre);
  }

function applique_rect_photo_focus_carte (pNoeud, pRect)
  {
    if (!pNoeud || !pNoeud.style || !pRect) return false;
    const tRect = rect_normalise_photo_focus_carte (pRect);
    pNoeud._cogiPhotoFocusBaseRect = tRect;
    pNoeud._cogiPhotoFocusCurrentRect = tRect;
    pNoeud.style.left = `${tRect.x}px`;
    pNoeud.style.top = `${tRect.y}px`;
    pNoeud.style.width = `${tRect.w}px`;
    pNoeud.style.height = `${tRect.h}px`;
    pNoeud.style.transformOrigin = "0 0";
    pNoeud.style.transform = "translate3d(0, 0, 0) scale(1, 1)";
    return true;
  }

function rect_normalise_photo_focus_carte (pRect)
  {
    return {
      x : Number (pRect.x) || 0,
      y : Number (pRect.y) || 0,
      w : Math.max (0.001, Number (pRect.w) || 0.001),
      h : Math.max (0.001, Number (pRect.h) || 0.001),
    };
  }

function prepare_base_photo_focus_carte_pour_cible (pNoeud, pRectCible)
  {
    const tBase = pNoeud && pNoeud._cogiPhotoFocusBaseRect;
    if (!pNoeud || !pNoeud.style || !tBase || !pRectCible) return false;
    const tCible = rect_normalise_photo_focus_carte (pRectCible);
    if (tCible.w <= tBase.w && tCible.h <= tBase.h) return false;
    const tRectCourant = pNoeud._cogiPhotoFocusCurrentRect || tBase;
    const tTransition = pNoeud.style.transition || "";
    pNoeud.style.transition = "none";
    pNoeud._cogiPhotoFocusBaseRect = tCible;
    pNoeud.style.left = `${tCible.x}px`;
    pNoeud.style.top = `${tCible.y}px`;
    pNoeud.style.width = `${tCible.w}px`;
    pNoeud.style.height = `${tCible.h}px`;
    pNoeud.style.transform = transform_photo_focus_carte_depuis_rect (pNoeud, tRectCourant);
    if (typeof pNoeud.getBoundingClientRect === "function")
      {
        try { pNoeud.getBoundingClientRect (); } catch (e) {}
      }
    pNoeud.style.transition = tTransition;
    return true;
  }

function transform_photo_focus_carte_depuis_rect (pNoeud, pRect)
  {
    const tBase = pNoeud && pNoeud._cogiPhotoFocusBaseRect;
    if (!tBase || !pRect) return "translate3d(0, 0, 0) scale(1, 1)";
    const tX = Number (pRect.x) || 0;
    const tY = Number (pRect.y) || 0;
    const tW = Math.max (0.001, Number (pRect.w) || 0.001);
    const tH = Math.max (0.001, Number (pRect.h) || 0.001);
    const tDx = tX - tBase.x;
    const tDy = tY - tBase.y;
    const tSx = tW / Math.max (0.001, tBase.w);
    const tSy = tH / Math.max (0.001, tBase.h);
    return `translate3d(${tDx}px, ${tDy}px, 0) scale(${tSx}, ${tSy})`;
  }

function libere_photo_focus_carte_noeud (pNoeud)
  {
    if (!pNoeud) return false;
    const tObjectUrl = pNoeud._cogiPhotoFocusObjectUrl || null;
    pNoeud._cogiPhotoFocusObjectUrl = null;
    pNoeud._cogiPhotoFocusBaseRect = null;
    pNoeud._cogiPhotoFocusCurrentRect = null;
    pNoeud._cogiPhotoFocusDecodePromise = null;
    if (pNoeud.style) pNoeud.style.transition = "";
    try { pNoeud.removeAttribute ("src"); } catch (e) {}
    if (pNoeud.parentNode) pNoeud.parentNode.removeChild (pNoeud);
    if (typeof pNoeud.getContext === "function")
      {
        pNoeud.width = 0;
        pNoeud.height = 0;
      }
    if (tObjectUrl && /^blob:/i.test (tObjectUrl) && typeof URL !== "undefined" && URL.revokeObjectURL)
      {
        try { URL.revokeObjectURL (tObjectUrl); } catch (e) {}
      }
    return true;
  }

function nettoie_photos_focus_carte_tpl (pTpl)
  {
    const tScene = element_cadre_scene_tpl (pTpl);
    if (pTpl)
      {
        pTpl._lecturePhotoFocusCarteNode = null;
        oublie_canevas_photo_focus_carte_tpl (pTpl);
      }
    if (!tScene || !tScene.querySelectorAll) return false;
    let tOk = false;
    retire_couvercles_sortie_focus_carte_tpl (pTpl);
    Array.from (tScene.querySelectorAll (".lecture_focus_carte_photo"))
      .forEach ((pNoeud) =>
        {
          // Photo d'une carte interrompue en train de s'estomper : elle se libère d'elle-même.
          if (pNoeud._cogiRaccordEstompe) return;
          tOk = libere_photo_focus_carte_noeud (pNoeud) || tOk;
        });
    return tOk;
  }

// Oublie le canevas source de la photo de carte en rendant sa mémoire tout de suite.
function oublie_canevas_photo_focus_carte_tpl (pTpl)
  {
    const tCanevas = pTpl && pTpl._lecturePhotoFocusCarteCanevas;
    if (pTpl) pTpl._lecturePhotoFocusCarteCanevas = null;
    if (tCanevas && typeof tCanevas.getContext === "function")
      {
        tCanevas.width = 0;
        tCanevas.height = 0;
      }
  }

// Le calque photo est un canevas recopié depuis le canevas source (copie côté GPU) : un nœud neuf à
// chaque usage, comme l'était l'<img>, pour qu'une libération tardive d'une séquence interrompue ne
// retire jamais la photo de la séquence suivante.
function assure_photo_focus_carte_tpl (pTpl, pCanevasSource, pRectDepart)
  {
    const tScene = element_cadre_scene_tpl (pTpl);
    if (!tScene || !pCanevasSource || !pCanevasSource.width || !pCanevasSource.height || !pRectDepart) return null;
    const tPhoto = document.createElement ("canvas");
    tPhoto.width = pCanevasSource.width;
    tPhoto.height = pCanevasSource.height;
    try { tPhoto.getContext ("2d").drawImage (pCanevasSource, 0, 0); }
    catch (e) { tPhoto.width = 0; tPhoto.height = 0; return null; }
    retire_photo_focus_carte_tpl (pTpl, {immediat : true});
    nettoie_photos_focus_carte_tpl (pTpl);
    tPhoto.className = "lecture_focus_carte_photo";
    tPhoto.style.opacity = "0";
    applique_rect_photo_focus_carte (tPhoto, pRectDepart);
    tScene.appendChild (tPhoto);
    pTpl._lecturePhotoFocusCarteNode = tPhoto;
    return tPhoto;
  }

function transition_photo_focus_carte (pNoeud, pRect, pDureeMs, pOpacity = null, pOptions = {})
  {
    if (!pNoeud || !pNoeud.style) return Promise.resolve (false);
    const tDuree = Math.max (0, Number (pDureeMs) || 0);
    const tOpacityOnly = !!(pOptions && pOptions.opacityOnly);
    const tRectCible = pRect ? rect_normalise_photo_focus_carte (pRect) : null;
    const tDecodePromise = pNoeud._cogiPhotoFocusDecodePromise || Promise.resolve ();
    if (!tOpacityOnly && tRectCible) prepare_base_photo_focus_carte_pour_cible (pNoeud, tRectCible);
    if (typeof pNoeud.getBoundingClientRect === "function")
      {
        try { pNoeud.getBoundingClientRect (); } catch (e) {}
      }
    pNoeud.style.transition = tDuree > 0
      ? (tOpacityOnly
        ? `opacity ${tDuree}ms ease`
        : `transform ${tDuree}ms ease, opacity ${tDuree}ms ease`)
      : "";
    return new Promise ((pResolve) =>
      {
        let tTermine = false;
        let tTimer = null;
        const termine = () =>
          {
            if (tTermine) return;
            tTermine = true;
            if (tTimer) clearTimeout (tTimer);
            pNoeud.removeEventListener ("transitionend", onTransition);
            pNoeud.removeEventListener ("transitioncancel", onTransition);
            pResolve (true);
          };
        const onTransition = (pEvent) =>
          {
            if (!pEvent || pEvent.target !== pNoeud) return;
            if (tOpacityOnly && pEvent.propertyName && pEvent.propertyName !== "opacity") return;
            if (!tOpacityOnly && pEvent.propertyName && pEvent.propertyName !== "transform" && pEvent.propertyName !== "opacity") return;
            termine ();
          };
        if (tDuree > 0)
          {
            pNoeud.addEventListener ("transitionend", onTransition);
            pNoeud.addEventListener ("transitioncancel", onTransition);
          }
        Promise.resolve (tDecodePromise).then (() =>
          requestAnimationFrame (() =>
            {
              if (tRectCible)
                {
                  pNoeud.style.transform = transform_photo_focus_carte_depuis_rect (pNoeud, tRectCible);
                  pNoeud._cogiPhotoFocusCurrentRect = tRectCible;
                }
              if (pOpacity !== null) pNoeud.style.opacity = String (pOpacity);
              if (tDuree <= 0) termine ();
              else tTimer = setTimeout (termine, tDuree + 120);
            })
        );
      });
  }

function retire_photo_focus_carte_tpl (pTpl, pOptions = {})
  {
    const tNoeud = pTpl && pTpl._lecturePhotoFocusCarteNode;
    if (!tNoeud) return Promise.resolve (false);
    pTpl._lecturePhotoFocusCarteNode = null;
    if (pOptions.immediat)
      {
        libere_photo_focus_carte_noeud (tNoeud);
        return Promise.resolve (true);
      }
    const tDuree = pOptions.immediat ? 0 : Math.max (0, Number (pOptions.duree_ms) || 0);
    if (pOptions.rect)
      {
        tNoeud.style.transition = "";
        applique_rect_photo_focus_carte (tNoeud, pOptions.rect);
        if (typeof tNoeud.getBoundingClientRect === "function")
          {
            try { tNoeud.getBoundingClientRect (); } catch (e) {}
          }
      }
    return transition_photo_focus_carte (tNoeud, null, tDuree, 0, {opacityOnly : true})
      .then (() =>
        {
          libere_photo_focus_carte_noeud (tNoeud);
          return true;
        });
  }

function stoppe_animations_focus_carte_tpl (pTpl)
  {
    if (!pTpl) return;
    commande_items_hype_tpl (pTpl, "pause");
    commande_items_svg_inline_tpl (pTpl, "pause");
  }

function reprend_animations_focus_carte_tpl (pTpl)
  {
    if (!pTpl || !mode_lecture_scene_actif ()) return;
    programme_synchronise_items_hype_tpl (pTpl);
    relance_items_svg_inline_lecture_tpl (pTpl);
    demarre_flottement_bulles_lecture_tpl (pTpl);
  }

function noeud_bulle_carte_lecture_tpl (pTpl, pCarteId)
  {
    const tRoot = pTpl && pTpl._svgDoc && pTpl._svgDoc.documentElement;
    const tCarteId = typeof pCarteId === "string" ? pCarteId : "";
    if (!tRoot || !tCarteId || typeof tRoot.querySelector !== "function") return null;
    return tRoot.querySelector (
      `.${CARTE_DECOR_BULLE_CLASS}[data-carte-id="${echappe_selecteur_css_svg (tCarteId)}"]`
    );
  }

function noeuds_bulles_focus_carte_lecture_tpl (pTpl, pCarte)
  {
    const tRoot = pTpl && pTpl._svgDoc && pTpl._svgDoc.documentElement;
    const tVue = normalise_vue_carte_client (pCarte && pCarte.vue);
    const tCarteId = typeof (pCarte && pCarte._id) === "string" ? pCarte._id : "";
    if (!tRoot || !tVue || typeof tRoot.querySelectorAll !== "function") return [];
    return Array.from (tRoot.querySelectorAll (`.${CARTE_DECOR_BULLE_CLASS}[data-carte-id]`))
      .filter ((pNoeud) =>
        {
          if (!pNoeud || !pNoeud.style) return false;
          const tId = pNoeud.getAttribute && pNoeud.getAttribute ("data-carte-id");
          if (tId && tId === tCarteId) return true;
          return vue_carte_intersecte_bbox (
            tVue,
            bbox_racine_pour_element (tRoot, pNoeud)
          );
        });
  }

function masque_bulle_focus_carte_lecture_tpl (pTpl, pCarteId)
  {
    if (!pTpl || !pCarteId) return false;
    restaure_bulle_focus_carte_lecture_tpl (pTpl);
    const tCarte = carte_source_pour_bulle_scene (pCarteId);
    const tNoeuds = noeuds_bulles_focus_carte_lecture_tpl (pTpl, tCarte);
    if (!tNoeuds.length) return false;
    pTpl._lectureBullesFocusMasquees = tNoeuds.map ((pNoeud) =>
      ({
        carteId : pNoeud.getAttribute && pNoeud.getAttribute ("data-carte-id"),
        noeud : pNoeud,
        visibility : pNoeud.style.visibility || "",
        opacity : pNoeud.style.opacity || "",
        pointerEvents : pNoeud.style.pointerEvents || "",
      }));
    pTpl._lectureBulleFocusMasquee = null;
    tNoeuds.forEach ((pNoeud) =>
      {
        pNoeud.style.visibility = "hidden";
        pNoeud.style.opacity = "0";
        pNoeud.style.pointerEvents = "none";
      });
    return true;
  }

function restaure_bulle_focus_carte_lecture_tpl (pTpl, pOptions = {})
  {
    const tEtats = pTpl && Array.isArray (pTpl._lectureBullesFocusMasquees)
      ? pTpl._lectureBullesFocusMasquees
      : (pTpl && pTpl._lectureBulleFocusMasquee ? [pTpl._lectureBulleFocusMasquee] : []);
    if (!tEtats.length) return false;
    const tSaufCarteId = typeof (pOptions && pOptions.saufCarteId) === "string"
      ? pOptions.saufCarteId
      : "";
    tEtats.forEach ((pEtat) =>
      {
        if (tSaufCarteId && pEtat && pEtat.carteId === tSaufCarteId) return;
        const tNoeud = (pEtat.noeud && pEtat.noeud.parentNode)
          ? pEtat.noeud
          : noeud_bulle_carte_lecture_tpl (pTpl, pEtat.carteId);
        if (tNoeud && tNoeud.style)
          {
            tNoeud.style.visibility = pEtat.visibility || "";
            tNoeud.style.opacity = pEtat.opacity || "";
            tNoeud.style.pointerEvents = pEtat.pointerEvents || "";
          }
      });
    pTpl._lectureBullesFocusMasquees = null;
    pTpl._lectureBulleFocusMasquee = null;
    return true;
  }

function attend_frame_scene ()
  {
    return new Promise ((pResolve) => requestAnimationFrame (() => pResolve ()));
  }

// Raccord d'une ouverture ou d'une sortie de carte interrompue : l'état de la carte est remis à zéro
// d'un coup, mais le voile de la carte (couvercle déjà posé, ou couvercle HTML neuf copiant le masque
// SVG) et la photo sont détachés de la carte et s'estompent en LECTURE_ACHEVEMENT_ANIMATIONS_MS par-dessus
// la scène déjà dans son état final. Éléments HTML : fondu par le compositeur, sans redessin du SVG.
function estompe_raccord_interruption_carte_tpl (pTpl)
  {
    const tScene = element_cadre_scene_tpl (pTpl);
    if (!tScene || !tScene.querySelectorAll) return;
    let tCouvercles = Array.from (tScene.querySelectorAll (`.${LECTURE_FOCUS_CARTE_COUVERCLE_CLASS}`))
      .filter ((pNoeud) => !pNoeud._cogiRaccordEstompe);
    if (!tCouvercles.length)
      {
        const tCouvercle = pose_couvercle_sortie_focus_carte_tpl (pTpl);
        if (tCouvercle) tCouvercles = [tCouvercle];
      }
    const tPhoto = pTpl._lecturePhotoFocusCarteNode || null;
    pTpl._lecturePhotoFocusCarteNode = null;
    const tDuree = LECTURE_ACHEVEMENT_ANIMATIONS_MS;
    const estompe = (pNoeud, pRetire) =>
      {
        if (!pNoeud || !pNoeud.style || !pNoeud.parentNode)
          {
            pRetire (pNoeud);
            return;
          }
        pNoeud._cogiRaccordEstompe = true;
        let tFait = false;
        const retire = () =>
          {
            if (tFait) return;
            tFait = true;
            pRetire (pNoeud);
          };
        pNoeud.style.pointerEvents = "none";
        pNoeud.style.transition = `opacity ${tDuree}ms ease-out`;
        try { pNoeud.getBoundingClientRect (); } catch (e) {}
        pNoeud.addEventListener ("transitionend", (pEvent) =>
          {
            if (pEvent.target === pNoeud && pEvent.propertyName === "opacity") retire ();
          });
        setTimeout (retire, tDuree + 80);
        pNoeud.style.opacity = "0";
      };
    tCouvercles.forEach ((pNoeud) =>
      estompe (pNoeud, (pN) => { if (pN && pN.parentNode) pN.parentNode.removeChild (pN); }));
    if (tPhoto) estompe (tPhoto, libere_photo_focus_carte_noeud);
  }

function interrompt_sortie_focus_carte_lecture_tpl (pTpl)
  {
    if (!pTpl || !scene_lecture_a_focus_carte_en_cours_tpl (pTpl)) return false;
    estompe_raccord_interruption_carte_tpl (pTpl);
    invalide_cycle_zoom_alternatif_tpl (pTpl);
    reinitialise_transitions_lecture_tpl (pTpl);
    annule_timer_finalisation_entree_zoom_alternatif_tpl (pTpl);
    termine_animation_camera_zoom_alternatif_tpl (pTpl);
    annule_animation_viewbox_tpl (pTpl, {commitCurrent : false});
    libere_photo_focus_carte_noeud (pTpl._lecturePhotoFocusCarteNode);
    retire_couvercles_sortie_focus_carte_tpl (pTpl);
    pTpl._lecturePhotoFocusCarteNode = null;
    oublie_canevas_photo_focus_carte_tpl (pTpl);
    pTpl._lectureZoomAltReaffichageMasquesToken = (Number (pTpl._lectureZoomAltReaffichageMasquesToken) || 0) + 1;
    retire_zoom_alternatif_scene_tpl (pTpl);
    restaure_bulle_focus_carte_lecture_tpl (pTpl);
    nettoie_contours_lecture_tpl (pTpl);
    pTpl._lectureZoomAltActif = false;
    pTpl._lectureZoomAltPreparation = false;
    pTpl._lectureFocusCarteEntreeEnCours = false;
    pTpl._lectureZoomAltTimer = null;
    pTpl._lectureZoomAltMasquagesCarteId = null;
    pTpl._lectureZoomAltReaffichageMasquesEnCours = false;
    pTpl._lectureZoomAltSortieEnCours = false;
    pTpl._lectureZoomAltSortieCarteId = null;
    pTpl._lectureFocusInteractionsActives = false;
    gLectureSceneFocusCarte.set (null);
    if (pTpl._svgDoc)
      applique_masquages_carte_svg (pTpl._svgDoc, [], {animate : false});
    synchronise_items_interface_svg_tpl (pTpl);
    actualise_transform_calques_cartes_tpl (pTpl);
    return true;
  }

function ouvre_focus_carte_lecture_depuis_bulle_tpl (pTpl, pCarteId)
  {
    return ouvre_focus_carte_lecture_depuis_bulle_impl_tpl (pTpl, pCarteId);
  }

function annule_ou_ferme_interaction_protegee_lecture_tpl (pTpl, pOptions = {})
  {
    const tTpl = pTpl || gSceneTemplateActif;
    if (!tTpl) return false;
    const tAvaitOuverture = !!(
      tTpl._lectureOuvertureBullePromise
      || tTpl._lectureOuvertureBulleCarteId
      || tTpl._lectureZoomAltPreparation
      || tTpl._lectureFocusCarteEntreeEnCours
      || gLectureSceneFocusCarte.get ()
    );
    tTpl._lectureOuvertureBulleSeq = (Number (tTpl._lectureOuvertureBulleSeq) || 0) + 1;
    tTpl._lectureOuvertureBulleCarteId = null;
    tTpl._lectureOuvertureBullePromise = null;
    tTpl._lectureBulleClicAtMs = 0;
    try { nettoie_timers_pedago_lecture_tpl (tTpl); } catch (e) {}
    try
      {
        const tEtatPedago = tTpl._lecturePedago;
        if (tEtatPedago) tEtatPedago.actif = false;
        tTpl._lecturePedagoFinSeq = (Number (tTpl._lecturePedagoFinSeq) || 0) + 1;
      }
    catch (e) {}
    try { arrete_morphing_lecture_scene (tTpl, {garderAudio : !!(pOptions && pOptions.garderAudio)}); } catch (e) {}
    try { arrete_audio_lecture_scene ({interne : !!(pOptions && pOptions.garderAudio)}); } catch (e) {}
    try
      {
        invalide_cycle_zoom_alternatif_tpl (tTpl);
        reinitialise_transitions_lecture_tpl (tTpl);
        annule_timer_finalisation_entree_zoom_alternatif_tpl (tTpl);
        termine_animation_camera_zoom_alternatif_tpl (tTpl);
        annule_animation_viewbox_tpl (tTpl, {commitCurrent : false});
        libere_photo_focus_carte_noeud (tTpl._lecturePhotoFocusCarteNode);
        tTpl._lecturePhotoFocusCarteNode = null;
        oublie_canevas_photo_focus_carte_tpl (tTpl);
        retire_zoom_alternatif_scene_tpl (tTpl);
        restaure_bulle_focus_carte_lecture_tpl (tTpl);
        nettoie_contours_lecture_tpl (tTpl);
        restaure_elements_pedago_lecture_tpl (tTpl);
        reprend_animations_focus_carte_tpl (tTpl);
      }
    catch (e) {}
    tTpl._lectureZoomAltActif = false;
    tTpl._lectureZoomAltPreparation = false;
    tTpl._lectureFocusCarteEntreeEnCours = false;
    tTpl._lectureZoomAltTimer = null;
    tTpl._lectureZoomAltMasquagesCarteId = null;
    tTpl._lectureZoomAltReaffichageMasquesEnCours = false;
    tTpl._lectureZoomAltSortieEnCours = false;
    tTpl._lectureZoomAltSortieCarteId = null;
    tTpl._lectureFocusInteractionsActives = false;
    tTpl._lecturePedago = null;
    if (tTpl._lectureCartesApercuLimiteDiffusion instanceof Set)
      tTpl._lectureCartesApercuLimiteDiffusion.clear ();
    gLectureSceneFocusCarte.set (null);
    if (tTpl._svgDoc)
      {
        try { applique_masquages_carte_svg (tTpl._svgDoc, [], {animate : false}); } catch (e) {}
      }
    try { synchronise_items_interface_svg_tpl (tTpl); } catch (e) {}
    try { actualise_transform_calques_cartes_tpl (tTpl); } catch (e) {}
    return tAvaitOuverture;
  }

async function ouvre_focus_carte_lecture_depuis_bulle_impl_tpl (pTpl, pCarteId)
  {
    const tTpl = pTpl || gSceneTemplateActif;
    const tCarteId = typeof pCarteId === "string" ? pCarteId : "";
    if (!tTpl || !tCarteId) return false;
    if (tTpl._lectureOuvertureBulleCarteId === tCarteId && tTpl._lectureOuvertureBullePromise)
      return tTpl._lectureOuvertureBullePromise;
    const tOuvertureAutreEnCours = !!(
      tTpl._lectureOuvertureBullePromise
      && tTpl._lectureOuvertureBulleCarteId
      && tTpl._lectureOuvertureBulleCarteId !== tCarteId
    );
    const tFocusCarteId = gLectureSceneFocusCarte.get ();
    let tInterruption = false;
    if (tOuvertureAutreEnCours
        || (tTpl._lectureZoomAltSortieEnCours && tTpl._lectureZoomAltSortieCarteId !== tCarteId)
        || (tFocusCarteId
          && tFocusCarteId !== tCarteId
          && !tTpl._lectureFocusInteractionsActives))
      tInterruption = interrompt_sortie_focus_carte_lecture_tpl (tTpl);
    const tSeq = (Number (tTpl._lectureOuvertureBulleSeq) || 0) + 1;
    tTpl._lectureOuvertureBulleSeq = tSeq;
    tTpl._lectureOuvertureBulleCarteId = tCarteId;
    const tPromise = (async () =>
      {
        if (tInterruption)
          {
            await attend_frame_scene ();
            if (tTpl._lectureOuvertureBulleSeq !== tSeq) return false;
          }
        return prepare_zoom_alternatif_carte_lecture_scene (tTpl, tCarteId);
      }) ()
      .finally (() =>
        {
          if (tTpl._lectureOuvertureBulleSeq !== tSeq) return;
          tTpl._lectureOuvertureBulleCarteId = null;
          tTpl._lectureOuvertureBullePromise = null;
        });
    tTpl._lectureOuvertureBullePromise = tPromise;
    return tPromise;
  }

function duree_fondu_photo_entree_zoom_alternatif_ms_lecture (pCarte = null)
  {
    const tReglages = reglages_interface_carte_effectifs (pCarte);
    return Math.max (0, Number (tReglages.zoom_alternatif_photo_fade_in_ms) || 0);
  }

function duree_fondu_photo_sortie_zoom_alternatif_ms_lecture (pCarte = null)
  {
    const tReglages = reglages_interface_carte_effectifs (pCarte);
    return Math.max (0, Number (tReglages.zoom_alternatif_photo_fade_out_ms) || 0);
  }

function delai_dezoom_photo_sortie_zoom_alternatif_ms_lecture (pCarte = null)
  {
    const tReglages = reglages_interface_carte_effectifs (pCarte);
    const tDelai = Number (tReglages.zoom_alternatif_dezoom_offset_ms);
    return Number.isFinite (tDelai) ? tDelai : 0;
  }

function verrouille_masque_entree_zoom_alternatif (pGroupe)
  {
    if (!pGroupe || !pGroupe.querySelector) return;
    const tMasque = pGroupe.querySelector (`.${CARTE_ZOOM_ALTERNATIF_MASK_CLASS}`);
    if (!tMasque || !tMasque.style) return;
    tMasque.style.transition = "none";
    tMasque.style.opacity = "1";
  }

async function prepare_focus_photo_carte_lecture_scene (pTpl, pCarteId)
  {
    if (!mode_lecture_scene_actif ()) return false;
    const tSceneId = Session.get ("scene_id");
    const tTpl = pTpl || gSceneTemplateActif;
    const tCarte = carte_source_pour_bulle_scene (pCarteId);
    const tCarteVue = normalise_vue_carte_client (tCarte && tCarte.vue);
    if (!tTpl || !tTpl._svgDoc || !tCarte || !tCarteVue) return false;
    if (scene_lecture_a_focus_carte_en_cours_tpl (tTpl))
      {
        const tFocusCarteId = gLectureSceneFocusCarte.get ();
        if (tFocusCarteId === tCarte._id && !tTpl._lectureZoomAltSortieEnCours)
          return true;
        if (!tTpl._lectureZoomAltSortieEnCours && tTpl._lectureFocusInteractionsActives)
          return false;
        interrompt_sortie_focus_carte_lecture_tpl (tTpl);
      }

    let tCycleId = 0;
    let tPhoto = null;
    try
      {
        reinitialise_transitions_lecture_tpl (tTpl);
        annule_timer_finalisation_entree_zoom_alternatif_tpl (tTpl);
        retire_zoom_alternatif_scene_tpl (tTpl);
        tCycleId = demarre_cycle_zoom_alternatif_tpl (
          tTpl,
          LECTURE_ZOOM_ALT_PHASE.PREPARATION,
          tSceneId,
          tCarte._id
        );
        tTpl._lectureZoomAltPreparation = true;
        tTpl._lectureFocusCarteEntreeEnCours = true;
        stoppe_animations_focus_carte_tpl (tTpl);
        const tPrechargeInterface = charge_modeles_zoom_alternatif_interface_carte (tCarte);
        const tVueDepartBrute = vue_camera_brute_courante_scene (tTpl);
        const tVueFocusBrute = vue_zoom_alternatif_carte_brute_tpl (tTpl, tCarte);
        const tFrameVue = vue_cadre_zoom_alternatif_carte_brute_tpl (tTpl, tCarte);
        const tPhotoVue = vue_photo_focus_carte_avec_relief (tFrameVue, reglages_interface_carte_effectifs (tCarte)) || tFrameVue;
        const tRectDepart = rect_photo_focus_carte_tpl (tTpl, tVueDepartBrute, tPhotoVue);
        const tRectFocus = rect_photo_focus_carte_tpl (tTpl, tVueFocusBrute, tPhotoVue);
        const tPhotoCanevas = await cree_canevas_photo_focus_carte_tpl (tTpl, tCarte);
        if (!tPhotoCanevas || !tRectDepart || !tRectFocus)
          throw new Error ("Instantané de carte indisponible");
        tPhoto = assure_photo_focus_carte_tpl (tTpl, tPhotoCanevas, tRectDepart);
        if (!tPhoto) throw new Error ("Calque photo indisponible");
        tTpl._lecturePhotoFocusCarteCanevas = tPhotoCanevas;

        if (!tTpl._lectureSonAppareilPhotoBulleJusqua || Date.now () > tTpl._lectureSonAppareilPhotoBulleJusqua)
          {
            arrete_audio_lecture_scene ();
            joue_son_appareil_photo_bulle_lecture (tTpl);
          }
        tTpl._lectureZoomAltMasquagesCarteId = tCarte._id;
        tTpl._lectureFocusInteractionsActives = false;
        await transition_photo_focus_carte (tPhoto, null, duree_fondu_photo_entree_zoom_alternatif_ms_lecture (tCarte), 1);
        if (!cycle_zoom_alternatif_valide_tpl (tTpl, tCycleId, tSceneId, {
          carteId : tCarte._id,
          phases : [LECTURE_ZOOM_ALT_PHASE.PREPARATION],
        }))
          throw new Error ("Cycle focus carte interrompu après apparition photo");
        retire_decorations_cartes_racine (
          tTpl._svgDoc && tTpl._svgDoc.documentElement,
          {
            preserveZoomAlt : true,
            preserveBulles : true,
          }
        );
        definit_phase_zoom_alternatif_tpl (tTpl, tCycleId, LECTURE_ZOOM_ALT_PHASE.ATTENTE_ZOOM, {
          sceneId : tSceneId,
          carteId : tCarte._id,
          phases : [LECTURE_ZOOM_ALT_PHASE.PREPARATION],
        });
        await attend_ms_scene (delai_lancement_zoom_alternatif_ms_lecture (tCarte));
        if (!cycle_zoom_alternatif_valide_tpl (tTpl, tCycleId, tSceneId, {
          carteId : tCarte._id,
          phases : [LECTURE_ZOOM_ALT_PHASE.ATTENTE_ZOOM],
        }))
          throw new Error ("Cycle focus carte interrompu avant zoom photo");
        definit_phase_zoom_alternatif_tpl (tTpl, tCycleId, LECTURE_ZOOM_ALT_PHASE.CAMERA_ENTREE, {
          sceneId : tSceneId,
          carteId : tCarte._id,
          phases : [LECTURE_ZOOM_ALT_PHASE.ATTENTE_ZOOM],
        });
        await transition_photo_focus_carte (tPhoto, tRectFocus, reglages_interface_carte_effectifs (tCarte).zoom_carte_duree_ms, 1);
        if (!cycle_zoom_alternatif_valide_tpl (tTpl, tCycleId, tSceneId, {
          carteId : tCarte._id,
          phases : [LECTURE_ZOOM_ALT_PHASE.CAMERA_ENTREE],
        }))
          throw new Error ("Cycle focus carte interrompu après zoom photo");
        await tPrechargeInterface;
        if (!cycle_zoom_alternatif_valide_tpl (tTpl, tCycleId, tSceneId, {
          carteId : tCarte._id,
          phases : [LECTURE_ZOOM_ALT_PHASE.CAMERA_ENTREE],
        }))
          throw new Error ("Cycle focus carte interrompu après chargement interface");
        const tOverlayInterface = assure_overlay_zoom_alternatif_carte_tpl (
          tTpl,
          tCarte,
          {
            focusViewBox : tVueFocusBrute,
            verrouilleMasque : false,
            masquePlein : true,
            preserveBulles : true,
          }
        );
        if (!tOverlayInterface)
          throw new Error ("Calque carte indisponible après zoom photo");
        initialise_parcours_pedago_carte_lecture (tTpl, tCarte);
        const tOptionsFonduMasque = {
          onStart : () =>
            {
              const tEtat = tTpl && tTpl._lecturePedago;
              if (!tEtat || !tEtat.actif || tEtat.carteId !== tCarte._id) return;
              programme_premier_objet_pedago_lecture (tTpl);
            },
        };
        const tCouvercleEntree = await lance_fondu_entree_couvercle_carte_tpl (
          tTpl,
          tOverlayInterface && tOverlayInterface.groupe,
          tCarte,
          tOptionsFonduMasque
        );
        if (!tCouvercleEntree)
          await lance_fondu_entree_zoom_alternatif (
            tOverlayInterface && tOverlayInterface.groupe,
            tCarte,
            tOptionsFonduMasque
          );
        if (!cycle_zoom_alternatif_valide_tpl (tTpl, tCycleId, tSceneId, {
          carteId : tCarte._id,
          phases : [LECTURE_ZOOM_ALT_PHASE.CAMERA_ENTREE],
        }))
          throw new Error ("Cycle focus carte interrompu après fondu masque");
        // Bascule de vue : l'illustration entière est redessinée à la nouvelle échelle. Sur un GPU
        // modeste, les tuiles arrivent en plusieurs frames (scène zoomée sans voile, bords blancs) :
        // tout se fait donc sous le couvercle d'entrée, retiré une fois le rendu stabilisé.
        masque_bulle_focus_carte_lecture_tpl (tTpl, tCarte._id);
        applique_masquages_carte_tpl (tTpl, {duree_out_ms : 0, decalage_out_ms : 0});
        applique_vue_carte_dans_scene (tTpl, tVueFocusBrute, false);
        actualise_masque_zoom_alternatif_groupe (
          tOverlayInterface && tOverlayInterface.groupe,
          vue_scene_decorations_cartes_tpl (tTpl),
          tCarte.vue
        );
        tTpl._lectureZoomAltActif = true;
        gLectureSceneFocusCarte.set (tCarte._id);
        synchronise_items_interface_svg_tpl (tTpl);
        if (tCouvercleEntree)
          {
            await attend_rendu_stabilise_scene ();
            if (tCouvercleEntree.parentNode && !tCouvercleEntree._cogiRaccordEstompe)
              tCouvercleEntree.parentNode.removeChild (tCouvercleEntree);
            if (!cycle_zoom_alternatif_valide_tpl (tTpl, tCycleId, tSceneId, {
              carteId : tCarte._id,
              phases : [LECTURE_ZOOM_ALT_PHASE.CAMERA_ENTREE],
            }))
              throw new Error ("Cycle focus carte interrompu pendant la bascule de vue");
          }
        definit_phase_zoom_alternatif_tpl (tTpl, tCycleId, LECTURE_ZOOM_ALT_PHASE.INTERFACE_ENTREE, {
          sceneId : tSceneId,
          carteId : tCarte._id,
          phases : [LECTURE_ZOOM_ALT_PHASE.CAMERA_ENTREE],
        });
        await Promise.all ([
          tOverlayInterface
            ? lance_fondu_assets_zoom_alternatif_tpl (
              tTpl,
              true,
              duree_fondu_entree_interface_zoom_alternatif_ms_lecture (tCarte)
            )
            : Promise.resolve (),
          retire_photo_focus_carte_tpl (
            tTpl,
            {
              duree_ms : duree_fondu_photo_sortie_zoom_alternatif_ms_lecture (tCarte),
              rect : tRectFocus,
            }
          ),
        ]);
        if (!cycle_zoom_alternatif_valide_tpl (tTpl, tCycleId, tSceneId, {
          carteId : tCarte._id,
          phases : [LECTURE_ZOOM_ALT_PHASE.INTERFACE_ENTREE],
        }))
          throw new Error ("Cycle focus carte interrompu avant ouverture interface");
        tTpl._lectureFocusInteractionsActives = true;
        tTpl._lectureZoomAltPreparation = false;
        tTpl._lectureFocusCarteEntreeEnCours = false;
        definit_phase_zoom_alternatif_tpl (tTpl, tCycleId, LECTURE_ZOOM_ALT_PHASE.OUVERTE, {
          sceneId : tSceneId,
          carteId : tCarte._id,
          phases : [LECTURE_ZOOM_ALT_PHASE.INTERFACE_ENTREE],
        });
        synchronise_selection_bulle_apres_focus_lecture (tTpl, tCarte._id);
        return true;
      }
    catch (pErreur)
      {
        console.warn ("[lecture] focus photo carte:", pErreur && (pErreur.message || pErreur));
        if (tTpl && tTpl._lectureZoomAltCycleId === tCycleId)
          {
            retire_couvercles_sortie_focus_carte_tpl (tTpl);
            retire_photo_focus_carte_tpl (tTpl, {immediat : true});
            retire_zoom_alternatif_scene_tpl (tTpl);
            restaure_bulle_focus_carte_lecture_tpl (tTpl);
            invalide_cycle_zoom_alternatif_tpl (tTpl);
            tTpl._lectureZoomAltActif = false;
            tTpl._lectureZoomAltPreparation = false;
            tTpl._lectureFocusCarteEntreeEnCours = false;
            tTpl._lectureZoomAltMasquagesCarteId = null;
            tTpl._lectureZoomAltReaffichageMasquesEnCours = false;
            tTpl._lectureFocusInteractionsActives = false;
            oublie_canevas_photo_focus_carte_tpl (tTpl);
            gLectureSceneFocusCarte.set (null);
            reprend_animations_focus_carte_tpl (tTpl);
          }
        else
          libere_photo_focus_carte_noeud (tPhoto);
        return false;
      }
  }

// Attend que le rendu « respire » à nouveau après une opération lourde (bascule de vue…). Le
// navigateur retient les frames du fil principal tant que les tuiles redessinées ne sont pas prêtes :
// une suite d'intervalles courts entre deux requestAnimationFrame signale donc que l'affichage est à
// jour. Sur une machine rapide l'attente vaut quelques frames ; elle est plafonnée (minuterie, car les
// rAF ne tournent pas dans un onglet masqué) pour ne jamais bloquer une séquence.
function attend_rendu_stabilise_scene (pOptions = {})
  {
    const tPlafond = Math.max (0, Number (pOptions.plafond_ms) || 2500);
    const tSeuil = Math.max (1, Number (pOptions.seuil_ms) || 42);
    const tRequis = Math.max (1, Number (pOptions.frames) || 4);
    return new Promise ((pResolve) =>
      {
        let tTermine = false;
        let tTimer = null;
        let tBons = 0;
        let tDernier = 0;
        const termine = () =>
          {
            if (tTermine) return;
            tTermine = true;
            if (tTimer) clearTimeout (tTimer);
            pResolve ();
          };
        const step = (pNow) =>
          {
            if (tTermine) return;
            if (tDernier) tBons = (pNow - tDernier) < tSeuil ? tBons + 1 : 0;
            tDernier = pNow;
            if (tBons >= tRequis) { termine (); return; }
            requestAnimationFrame (step);
          };
        tTimer = setTimeout (termine, tPlafond);
        requestAnimationFrame (step);
      });
  }

const LECTURE_FOCUS_CARTE_COUVERCLE_CLASS = "lecture_focus_carte_couvercle";
// Même courbe que le fondu du masque SVG qu'il remplace (ease-in-out quadratique).
const LECTURE_FOCUS_CARTE_COUVERCLE_EASING_CSS = "cubic-bezier(0.45, 0, 0.55, 1)";

function retire_couvercles_sortie_focus_carte_tpl (pTpl)
  {
    const tScene = element_cadre_scene_tpl (pTpl);
    if (!tScene || !tScene.querySelectorAll) return;
    Array.from (tScene.querySelectorAll (`.${LECTURE_FOCUS_CARTE_COUVERCLE_CLASS}`))
      .forEach ((pNoeud) =>
        {
          if (pNoeud._cogiRaccordEstompe) return;
          if (pNoeud.parentNode) pNoeud.parentNode.removeChild (pNoeud);
        });
  }

// Couvercle HTML de sortie de carte : il prend la place du masque SVG plein (même teinte, même opacité)
// sous la photo. Élément HTML hors du SVG, son fondu est pris en charge par le compositeur sans
// redessiner l'illustration, contrairement à une opacité animée dans le SVG.
function pose_couvercle_sortie_focus_carte_tpl (pTpl, pOptions = {})
  {
    const tScene = element_cadre_scene_tpl (pTpl);
    const tSvg = element_svg_scene_tpl (pTpl);
    const tGroupe = groupe_zoom_alternatif_scene_tpl (pTpl);
    const tMasque = tGroupe && tGroupe.querySelector
      ? tGroupe.querySelector (`.${CARTE_ZOOM_ALTERNATIF_MASK_CLASS}`)
      : null;
    if (!tScene || !tSvg || !tMasque) return null;
    retire_couvercles_sortie_focus_carte_tpl (pTpl);
    let tRect = null;
    try { tRect = tSvg.getBoundingClientRect (); } catch (e) {}
    if (!tRect || !tRect.width || !tRect.height) return null;
    const opacite_noeud = (pNoeud) =>
      {
        let tValeur = NaN;
        try { tValeur = parseFloat (getComputedStyle (pNoeud).opacity); } catch (e) {}
        return Number.isFinite (tValeur) ? tValeur : 1;
      };
    const tOpaciteRemplissage = Number (tMasque.getAttribute ("fill-opacity"));
    const tOpacite = Math.max (0, Math.min (1,
      (Number.isFinite (tOpaciteRemplissage) ? tOpaciteRemplissage : 1)
      * (Number.isFinite (pOptions.opaciteMasque) ? pOptions.opaciteMasque : opacite_noeud (tMasque))
      * opacite_noeud (tGroupe)));
    const tCouvercle = document.createElement ("div");
    tCouvercle.className = LECTURE_FOCUS_CARTE_COUVERCLE_CLASS;
    // Même repère que le calque photo : l'origine de .scene est celle du SVG.
    tCouvercle.style.left = "0px";
    tCouvercle.style.top = "0px";
    tCouvercle.style.width = `${tRect.width}px`;
    tCouvercle.style.height = `${tRect.height}px`;
    tCouvercle.style.backgroundColor = tMasque.getAttribute ("fill") || "#ffffff";
    tCouvercle.style.opacity = String (tOpacite);
    tScene.appendChild (tCouvercle);
    return tCouvercle;
  }

// Entrée de carte : le voile apparaît sous la forme d'un couvercle HTML (même teinte, même opacité
// finale que le masque SVG plein) dont le fondu est pris en charge par le compositeur. Animer
// l'opacité du masque dans le SVG redessinait toute l'illustration à chaque frame. À la fin du fondu,
// le masque SVG est posé d'un coup sous le couvercle (échange invisible) ; le couvercle reste en place
// pour cacher la bascule de vue qui suit, et c'est à l'appelant de le retirer. Renvoie null si le
// couvercle ne peut être posé (l'appelant se rabat alors sur lance_fondu_entree_zoom_alternatif).
function lance_fondu_entree_couvercle_carte_tpl (pTpl, pGroupe, pCarte = null, pOptions = {})
  {
    if (!pGroupe || !pGroupe.children) return Promise.resolve (null);
    const tNoeuds = Array.from (pGroupe.children).filter ((pNoeud) =>
      pNoeud && pNoeud.classList && pNoeud.style
      && !pNoeud.classList.contains (CARTE_ZOOM_ALTERNATIF_ASSETS_CLASS)
      && !pNoeud.classList.contains (CARTE_ZOOM_ALTERNATIF_FRAME_CLASS)
      && !pNoeud.classList.contains (CARTE_ZOOM_ALTERNATIF_FRAME_RELIEF_CLASS));
    tNoeuds.forEach ((pNoeud) =>
      {
        pNoeud.style.transition = "none";
        pNoeud.style.opacity = "0";
      });
    const tCouvercle = pose_couvercle_sortie_focus_carte_tpl (pTpl, {opaciteMasque : 1});
    if (!tCouvercle) return Promise.resolve (null);
    const tCible = tCouvercle.style.opacity;
    const tDuree = duree_fondu_entree_zoom_alternatif_ms_lecture (pCarte);
    tCouvercle.style.opacity = "0";
    const pose_masque = () =>
      tNoeuds.forEach ((pNoeud) => { if (pNoeud.style) pNoeud.style.opacity = "1"; });
    return new Promise ((pResolve) =>
      {
        let tTermine = false;
        let tTimer = null;
        const termine = () =>
          {
            if (tTermine) return;
            tTermine = true;
            if (tTimer) clearTimeout (tTimer);
            tCouvercle.removeEventListener ("transitionend", surFin);
            if (tCouvercle.parentNode) pose_masque ();
            pResolve (tCouvercle.parentNode ? tCouvercle : null);
          };
        const surFin = (pEvent) =>
          {
            if (pEvent.target === tCouvercle && pEvent.propertyName === "opacity") termine ();
          };
        tCouvercle.addEventListener ("transitionend", surFin);
        tCouvercle.style.transition = tDuree > 0 ? `opacity ${tDuree}ms ${LECTURE_FOCUS_CARTE_COUVERCLE_EASING_CSS}` : "";
        try { tCouvercle.getBoundingClientRect (); } catch (e) {}
        requestAnimationFrame (() =>
          {
            if (pOptions && typeof pOptions.onStart === "function") pOptions.onStart ();
            tCouvercle.style.opacity = tCible;
            if (tDuree <= 0) termine ();
            else tTimer = setTimeout (termine, tDuree + 120);
          });
      });
  }

function retire_couvercle_sortie_focus_carte_tpl (pTpl, pCouvercle, pDureeMs)
  {
    if (!pCouvercle || !pCouvercle.parentNode) return Promise.resolve (false);
    const tDuree = Math.max (0, Number (pDureeMs) || 0);
    const retire = () => { if (pCouvercle.parentNode) pCouvercle.parentNode.removeChild (pCouvercle); };
    if (tDuree <= 0)
      {
        retire ();
        return Promise.resolve (true);
      }
    return new Promise ((pResolve) =>
      {
        let tTermine = false;
        let tTimer = null;
        const termine = () =>
          {
            if (tTermine) return;
            tTermine = true;
            if (tTimer) clearTimeout (tTimer);
            pCouvercle.removeEventListener ("transitionend", termine);
            pCouvercle.removeEventListener ("transitioncancel", termine);
            retire ();
            pResolve (true);
          };
        pCouvercle.addEventListener ("transitionend", termine);
        pCouvercle.addEventListener ("transitioncancel", termine);
        pCouvercle.style.transition = `opacity ${tDuree}ms ${LECTURE_FOCUS_CARTE_COUVERCLE_EASING_CSS}`;
        try { pCouvercle.getBoundingClientRect (); } catch (e) {}
        requestAnimationFrame (() =>
          {
            pCouvercle.style.opacity = "0";
            tTimer = setTimeout (termine, tDuree + 120);
          });
      });
  }

async function quitte_focus_photo_carte_lecture_scene (pTpl, pOptions = {})
  {
    if (!mode_lecture_scene_actif ()) return false;
    const tSceneId = Session.get ("scene_id");
    const tTpl = pTpl || gSceneTemplateActif;
    const tFocusCarteId = gLectureSceneFocusCarte.get ();
    const tFocusCarte = tFocusCarteId ? carte_source_pour_bulle_scene (tFocusCarteId) : null;
    if (!tTpl || !tFocusCarte) return false;
    if (tTpl._lectureZoomAltSortieEnCours) return true;
    const tPhotoCanevas = tTpl._lecturePhotoFocusCarteCanevas || null;
    if (!tPhotoCanevas) return false;
    tTpl._lectureZoomAltSortieEnCours = true;
    tTpl._lectureZoomAltSortieCarteId = tFocusCarteId;
    nettoie_contours_lecture_tpl (tTpl);
    if (!(pOptions && pOptions.depuisPedago))
      {
        const tEtatPedago = tTpl._lecturePedago;
        if (tEtatPedago && tEtatPedago.actif && tEtatPedago.sceneId && tEtatPedago.carteId)
          {
            const tCarteScore = lecture_score_carte_session (tEtatPedago.sceneId, tEtatPedago.carteId);
            ecrit_lecture_score_carte_session (tEtatPedago.sceneId, tEtatPedago.carteId, {
              ...tCarteScore,
              terminee : false,
              interrompue : true,
            });
          }
        nettoie_timers_pedago_lecture_tpl (tTpl);
        if (tTpl._lecturePedago) tTpl._lecturePedago.actif = false;
        tTpl._lecturePedagoFinSeq = (tTpl._lecturePedagoFinSeq || 0) + 1;
        annule_overlays_hype_lecture_tpl (tTpl);
      }
    arrete_audio_lecture_scene ();
    stoppe_animations_focus_carte_tpl (tTpl);
    const tCycleId = demarre_cycle_zoom_alternatif_tpl (
      tTpl,
      LECTURE_ZOOM_ALT_PHASE.SORTIE_INTERFACE,
      tSceneId,
      tFocusCarteId
    );
    const tVueFocusBrute = vue_zoom_alternatif_carte_brute_tpl (tTpl, tFocusCarte);
    const tVueDepartBrute = vue_reinitialisation_scene_brute (tTpl);
    const tFrameVue = vue_cadre_zoom_alternatif_carte_brute_tpl (tTpl, tFocusCarte);
    const tPhotoVue = vue_photo_focus_carte_avec_relief (tFrameVue, reglages_interface_carte_effectifs (tFocusCarte)) || tFrameVue;
    const tRectFocus = rect_photo_focus_carte_tpl (tTpl, tVueFocusBrute, tPhotoVue);
    const tRectDepart = rect_photo_focus_carte_tpl (tTpl, tVueDepartBrute, tPhotoVue);
    const tDelaiDezoom = delai_dezoom_photo_sortie_zoom_alternatif_ms_lecture (tFocusCarte);
    const tPhoto = assure_photo_focus_carte_tpl (tTpl, tPhotoCanevas, tRectFocus);
    if (!tPhoto)
      {
        tTpl._lectureZoomAltSortieEnCours = false;
        tTpl._lectureZoomAltSortieCarteId = null;
        return false;
      }
    const tPromesseAffichagePhoto = transition_photo_focus_carte (
      tPhoto,
      null,
      duree_fondu_photo_entree_zoom_alternatif_ms_lecture (tFocusCarte),
      1,
      {opacityOnly : true}
    );
    const tPromesseMasquageInterface = lance_fondu_assets_zoom_alternatif_tpl (
      tTpl,
      false,
      duree_fondu_sortie_interface_zoom_alternatif_ms_lecture (tFocusCarte),
      {inclureCadre : false}
    );
    await Promise.all ([tPromesseAffichagePhoto, tPromesseMasquageInterface]);
    if (!cycle_zoom_alternatif_valide_tpl (tTpl, tCycleId, tSceneId, {carteId : tFocusCarteId}))
      return false;
    // La photo recouvre la carte et l'interface a disparu. Un couvercle HTML prend la place du masque
    // SVG (échange invisible) et tout le travail lourd se fait caché dessous : bascule de la vue,
    // retour des objets et des bulles. Redessiner l'illustration pendant le dézoom ou pendant un fondu
    // du masque dans le SVG les rendait saccadés sur les petits GPU.
    const tCouvercle = pose_couvercle_sortie_focus_carte_tpl (tTpl);
    retire_zoom_alternatif_scene_tpl (tTpl);
    restaure_bulle_focus_carte_lecture_tpl (
      tTpl,
      pOptions && pOptions.masquerBulleAppelante ? {saufCarteId : tFocusCarteId} : {}
    );
    applique_vue_carte_dans_scene (tTpl, tVueDepartBrute, false);
    actualise_transform_calques_cartes_tpl (tTpl);
    tTpl._lectureZoomAltReaffichageMasquesEnCours = true;
    synchronise_items_interface_svg_tpl (tTpl);
    await lance_reaffichage_objets_masques_carte_tpl (tTpl, tFocusCarte, {immediat : !!tCouvercle});
    if (!cycle_zoom_alternatif_valide_tpl (tTpl, tCycleId, tSceneId, {carteId : tFocusCarteId}))
      {
        retire_couvercles_sortie_focus_carte_tpl (tTpl);
        return false;
      }
    gLectureSceneFocusCarte.set (null);
    tTpl._lectureZoomAltActif = false;
    tTpl._lectureFocusInteractionsActives = false;
    await attend_rendu_stabilise_scene ();
    if (!cycle_zoom_alternatif_valide_tpl (tTpl, tCycleId, tSceneId, {carteId : tFocusCarteId}))
      {
        retire_couvercles_sortie_focus_carte_tpl (tTpl);
        libere_photo_focus_carte_noeud (tPhoto);
        return false;
      }
    tTpl._lecturePedago = null;
    // Révélation : le couvercle s'efface à la durée du fondu du masque pendant que la photo dézoome,
    // deux animations prises en charge par le compositeur. Le réglage « décalage du dézoom » garde son
    // sens : il était compté depuis le début de la sortie, la révélation commençant à la fin de
    // l'apparition de la photo ; seul leur écart compte désormais.
    const tDecalageDezoom = tDelaiDezoom - duree_fondu_photo_entree_zoom_alternatif_ms_lecture (tFocusCarte);
    const tPromesseRevelation = attend_ms_scene (Math.max (0, -tDecalageDezoom))
      .then (() => retire_couvercle_sortie_focus_carte_tpl (
        tTpl,
        tCouvercle,
        duree_fondu_sortie_zoom_alternatif_ms_lecture (tFocusCarte)
      ));
    const tPromesseDezoom = attend_ms_scene (Math.max (0, tDecalageDezoom))
      .then (() =>
        {
          if (!cycle_zoom_alternatif_valide_tpl (tTpl, tCycleId, tSceneId, {carteId : tFocusCarteId}))
            return false;
          return transition_photo_focus_carte (
            tPhoto,
            tRectDepart,
            reglages_interface_carte_effectifs (tFocusCarte).zoom_carte_duree_ms,
            1
          );
        });
    await Promise.all ([tPromesseRevelation, tPromesseDezoom]);
    if (!cycle_zoom_alternatif_valide_tpl (tTpl, tCycleId, tSceneId, {carteId : tFocusCarteId}))
      {
        retire_couvercles_sortie_focus_carte_tpl (tTpl);
        libere_photo_focus_carte_noeud (tPhoto);
        return false;
      }
    preserve_bulles_lecture_refresh_tpl (tTpl, 4);
    tTpl._lectureZoomAltReaffichageMasquesEnCours = false;
    await retire_photo_focus_carte_tpl (tTpl, {duree_ms : duree_fondu_photo_sortie_zoom_alternatif_ms_lecture (tFocusCarte)});
    oublie_canevas_photo_focus_carte_tpl (tTpl);
    tTpl._lectureZoomAltMasquagesCarteId = null;
    tTpl._lectureZoomAltReaffichageMasquesEnCours = false;
    tTpl._lectureZoomAltSortieEnCours = false;
    tTpl._lectureZoomAltSortieCarteId = null;
    invalide_cycle_zoom_alternatif_tpl (tTpl);
    restaure_lecture_sans_focus_carte_tpl (tTpl, {recentrer : false, animateBullesRetour : false});
    reprend_animations_focus_carte_tpl (tTpl);
    return true;
  }

function assure_overlay_zoom_alternatif_carte_tpl (pTpl, pCarte, pOptions = {})
  {
    const tRoot = pTpl && pTpl._svgDoc && pTpl._svgDoc.documentElement;
    if (!tRoot || !pCarte || !normalise_vue_carte_client (pCarte.vue)) return null;

    const tGroupeExistant = groupe_zoom_alternatif_scene_tpl (pTpl);
    const tAssetsExistants = tGroupeExistant && tGroupeExistant.querySelector
      ? tGroupeExistant.querySelector (`.${CARTE_ZOOM_ALTERNATIF_ASSETS_CLASS}`)
      : null;
    if (tGroupeExistant && tAssetsExistants)
      {
        place_zoom_alternatif_au_premier_plan_racine (tRoot);
        return {
          groupe : tGroupeExistant,
          assets : tAssetsExistants,
          recree : false,
        };
      }

    if (tGroupeExistant)
      retire_zoom_alternatif_scene_tpl (pTpl, tGroupeExistant);

    const tOverlay = ajoute_zoom_alternatif_carte_racine (
      tRoot,
      pCarte,
      {
        sceneViewBox : vue_scene_decorations_cartes_tpl (pTpl),
        focusViewBox : pOptions.focusViewBox || vue_zoom_alternatif_carte_brute_tpl (
          pTpl,
          pCarte,
          pOptions.reglagesOverride || null
        ),
        masquePlein : !!pOptions.masquePlein,
        cartesLayerTransform : transform_calque_cartes_tpl (pTpl),
        reglagesOverride : pOptions.reglagesOverride || null,
        preserveBulles : !!pOptions.preserveBulles,
      }
    );
    const tGroupe = tOverlay && tOverlay.groupe;
    const tAssets = tGroupe && tGroupe.querySelector
      ? tGroupe.querySelector (`.${CARTE_ZOOM_ALTERNATIF_ASSETS_CLASS}`)
      : null;
    if (!tGroupe || !tAssets) return null;
    if (tGroupe.style)
      {
        tGroupe.style.transition = "";
        tGroupe.style.opacity = "1";
      }
    if (pOptions.verrouilleMasque === false)
      {
        Array.from (tGroupe.children || []).forEach ((pNoeud) =>
          {
            if (!pNoeud || !pNoeud.classList || !pNoeud.style) return;
            if (pNoeud.classList.contains (CARTE_ZOOM_ALTERNATIF_ASSETS_CLASS)) return;
            pNoeud.style.transition = "";
            pNoeud.style.opacity = "0";
          });
      }
    else
      verrouille_masque_entree_zoom_alternatif (tGroupe);
    if (tAssets.style)
      {
        tAssets.style.transition = "";
        tAssets.style.opacity = "0";
        tAssets.style.pointerEvents = "none";
      }
    return {
      groupe : tGroupe,
      assets : tAssets,
      recree : true,
    };
  }

function zoom_alternatif_sur_carte (pTpl, pCarteId)
  {
    const tVB = pTpl && pTpl._svgVB;
    const tApp = pTpl && pTpl._svgApplique;
    const tCarte = carte_source_pour_bulle_scene (pCarteId);
    if (!tVB || !tApp || !tCarte || !tCarte.vue) return false;
    const tCibleBrute = vue_zoom_alternatif_carte_brute_tpl (pTpl, tCarte);
    const tCible = vue_camera_svg_depuis_vue_brute_scene (pTpl, tCibleBrute);
    if (!tCible || !tCibleBrute) return false;
    const tReglages = reglages_interface_carte_effectifs (tCarte);
    commence_animation_camera_zoom_alternatif_tpl (pTpl);
    anime_viewbox_vers (tVB, tCible, tApp, pTpl, {
      duree : tReglages.zoom_carte_duree_ms,
      easing : lecture_ease_in_out_cubic,
      fps : fps_animation_lecture_scene (),
      composite : true,
      onComplete : () =>
        {
          termine_animation_camera_zoom_alternatif_tpl (pTpl);
          const tDureeInterface = duree_fondu_entree_interface_zoom_alternatif_ms_lecture (tCarte);
          lance_fondu_assets_zoom_alternatif_tpl (pTpl, true, tDureeInterface)
            .then (() =>
              {
                pTpl._lectureFocusInteractionsActives = true;
                termine_transition_decor_focus_carte_tpl (pTpl);
              });
        },
    });
    return true;
  }

async function prepare_zoom_alternatif_carte_lecture_scene (pTpl, pCarteId)
  {
    return prepare_focus_photo_carte_lecture_scene (pTpl, pCarteId);
  }

async function quitte_focus_carte_lecture_scene (pTpl, pOptions = {})
  {
    return quitte_focus_photo_carte_lecture_scene (pTpl, pOptions);
  }

if (typeof window !== "undefined")
  {
    window.cogibulleDiffusionFermeCarteActive = function ()
      {
        quitte_focus_carte_lecture_scene (gSceneTemplateActif, {garderAudio : true});
        return true;
      };
    window.cogibulleDiffusionAnnuleOuFermeInteractionProtegee = function ()
      {
        annule_ou_ferme_interaction_protegee_lecture_tpl (gSceneTemplateActif, {garderAudio : true});
        return true;
      };
  }

function chemin_scene_projet_courant (pScene)
  {
    if (typeof window !== "undefined" && typeof window.cogibulleDiffusionCheminScene === "function")
      {
        const tCheminDiffusion = window.cogibulleDiffusionCheminScene (pScene);
        if (tCheminDiffusion) return tCheminDiffusion;
      }
    const tVersionId = Session.get ("diffusion_version_id");
    if (tVersionId && pScene && pScene.slug)
      return `/jouer/${encodeURIComponent (tVersionId)}/${encodeURIComponent (pScene.slug)}`;
    const tSlugs = Session.get ("projet_slugs") || {};
    if (!pScene || !pScene.slug || !tSlugs.user || !tSlugs.projet) return null;
    return `/projets/${tSlugs.user}/${tSlugs.projet}/${pScene.slug}`;
  }

function annule_transition_lecture_scene ()
  {
    gLectureSceneTransitionSequence += 1;
    if (gLectureSceneTransitionTimer)
      {
        clearTimeout (gLectureSceneTransitionTimer);
        gLectureSceneTransitionTimer = null;
      }
    if (gLectureSceneTransitionComputation)
      {
        gLectureSceneTransitionComputation.stop ();
        gLectureSceneTransitionComputation = null;
      }
    gLectureSceneTransitionSortie.set (false);
    gLectureSceneTransitionDureeMs.set (null);
    gLectureSceneTransition.set (false);
    retire_overlay_transition_lecture_scene ();
  }

function duree_transition_lecture_scene_ms ()
  {
    const tDureeTransition = gLectureSceneTransitionDureeMs.get ();
    if (tDureeTransition !== null && tDureeTransition !== undefined)
      return Math.max (0, Number (tDureeTransition) || 0);
    return Math.max (0, Number (reglages_mode_lecture_scene ().transition_scene_fade_ms) || 0);
  }

function assure_overlay_transition_lecture_scene ()
  {
    if (typeof document === "undefined" || !document.body) return null;
    if (gLectureSceneTransitionOverlay && gLectureSceneTransitionOverlay.isConnected)
      return gLectureSceneTransitionOverlay;
    const tOverlay = document.createElement ("div");
    tOverlay.className = "lecture_scene_global_transition_overlay";
    tOverlay.setAttribute ("aria-hidden", "true");
    tOverlay.style.opacity = "0";
    document.body.appendChild (tOverlay);
    gLectureSceneTransitionOverlay = tOverlay;
    return tOverlay;
  }

function stoppe_animation_overlay_transition_lecture_scene ()
  {
    const tAnimation = gLectureSceneTransitionOverlayAnimation;
    gLectureSceneTransitionOverlayAnimation = null;
    if (gLectureSceneTransitionOverlayTimer)
      {
        clearTimeout (gLectureSceneTransitionOverlayTimer);
        gLectureSceneTransitionOverlayTimer = null;
      }
    if (tAnimation && typeof tAnimation.cancel === "function")
      {
        try { tAnimation.cancel (); }
        catch (e) {}
      }
  }

function opacite_overlay_transition_lecture_scene (pOverlay)
  {
    if (!pOverlay) return 0;
    if (typeof window !== "undefined" && typeof window.getComputedStyle === "function")
      {
        const tOpacite = Number (window.getComputedStyle (pOverlay).opacity);
        if (Number.isFinite (tOpacite)) return Math.max (0, Math.min (1, tOpacite));
      }
    const tStyle = Number (pOverlay.style && pOverlay.style.opacity);
    return Number.isFinite (tStyle) ? Math.max (0, Math.min (1, tStyle)) : 0;
  }

function anime_overlay_transition_lecture_scene (pOpacite, pDureeMs)
  {
    const tOverlay = assure_overlay_transition_lecture_scene ();
    if (!tOverlay) return Promise.resolve (false);
    stoppe_animation_overlay_transition_lecture_scene ();
    const tCible = Math.max (0, Math.min (1, Number (pOpacite) || 0));
    const tDureeMs = Math.max (0, Number (pDureeMs) || 0);
    const tDepart = opacite_overlay_transition_lecture_scene (tOverlay);
    if (tDureeMs <= 0 || Math.abs (tDepart - tCible) < 0.001)
      {
        tOverlay.style.transition = "";
        tOverlay.style.opacity = String (tCible);
        return Promise.resolve (true);
      }
    return new Promise ((pResolve) =>
      {
        let tDejaTermine = false;
        let tControleur = null;
        const tRaf = typeof requestAnimationFrame === "function"
          ? requestAnimationFrame
          : (pCb) => setTimeout (pCb, 16);
        const tNettoie = () =>
          {
            tOverlay.removeEventListener ("transitionend", tSurFinTransition);
            tOverlay.removeEventListener ("transitioncancel", tAnnule);
            if (gLectureSceneTransitionOverlayTimer)
              {
                clearTimeout (gLectureSceneTransitionOverlayTimer);
                gLectureSceneTransitionOverlayTimer = null;
              }
            if (gLectureSceneTransitionOverlayAnimation === tControleur)
              gLectureSceneTransitionOverlayAnimation = null;
          };
        const tTermine = () =>
          {
            if (tDejaTermine) return;
            tDejaTermine = true;
            tNettoie ();
            tOverlay.style.transition = "";
            tOverlay.style.opacity = String (tCible);
            pResolve (true);
          };
        const tAnnule = () =>
          {
            if (tDejaTermine) return;
            tDejaTermine = true;
            tNettoie ();
            tOverlay.style.transition = "";
            pResolve (false);
          };
        function tSurFinTransition (pEvt)
          {
            if (pEvt && pEvt.target !== tOverlay) return;
            if (pEvt && pEvt.propertyName && pEvt.propertyName !== "opacity") return;
            tTermine ();
          }
        tControleur = {cancel : tAnnule};
        gLectureSceneTransitionOverlayAnimation = tControleur;
        tOverlay.style.transition = "none";
        tOverlay.style.opacity = String (tDepart);
        if (typeof tOverlay.offsetWidth === "number") void tOverlay.offsetWidth;
        tOverlay.addEventListener ("transitionend", tSurFinTransition);
        tOverlay.addEventListener ("transitioncancel", tAnnule);
        gLectureSceneTransitionOverlayTimer = setTimeout (tTermine, tDureeMs + 120);
        tRaf (() =>
          {
            if (tDejaTermine) return;
            tOverlay.style.transition = `opacity ${tDureeMs}ms ease-in-out`;
            tOverlay.style.opacity = String (tCible);
          });
      });
  }

function retire_overlay_transition_lecture_scene ()
  {
    stoppe_animation_overlay_transition_lecture_scene ();
    if (gLectureSceneTransitionOverlay && gLectureSceneTransitionOverlay.parentNode)
      gLectureSceneTransitionOverlay.parentNode.removeChild (gLectureSceneTransitionOverlay);
    gLectureSceneTransitionOverlay = null;
  }

function svg_scene_document_pret_tpl (pTpl, pSceneId = "")
  {
    const tTpl = pTpl || gSceneTemplateActif;
    const tSceneId = typeof pSceneId === "string" ? pSceneId : "";
    if (!tTpl || (tSceneId && Session.get ("scene_id") !== tSceneId)) return false;
    if (!(tTpl._svgObjectReady && tTpl._svgObjectReady.get ())) return false;
    if ((tTpl._svgObjectReadySourceKey || null) !== (tTpl._svgObjectSourceKey || null)) return false;
    const tSvgFrame = tTpl.find && tTpl.find (".contenu_svg_scene");
    return !!(tSvgFrame && document_svg_scene_depuis_conteneur (tSvgFrame, {verifierSource : true}));
  }

function svg_scene_document_affichable_tpl (pTpl, pSceneId = "")
  {
    const tTpl = pTpl || gSceneTemplateActif;
    const tSceneId = typeof pSceneId === "string" ? pSceneId : "";
    if (!tTpl || (tSceneId && Session.get ("scene_id") !== tSceneId)) return false;
    if (tSceneId)
      {
        const tSourceKey = String (tTpl._svgObjectSourceKey || "");
        if (!tSourceKey || !tSourceKey.startsWith (`${tSceneId}:`)) return false;
      }
    const tSvgFrame = tTpl.find && tTpl.find (".contenu_svg_scene");
    const tDoc = tSvgFrame && document_svg_scene_depuis_conteneur (tSvgFrame, {verifierSource : false});
    return !!(tDoc && tDoc.documentElement);
  }

function svg_scene_transition_pret_tpl (pTpl, pSceneId = "")
  {
    return svg_scene_document_pret_tpl (pTpl, pSceneId)
      || svg_scene_document_affichable_tpl (pTpl, pSceneId);
  }

function termine_transition_lecture_scene_apres_rendu (pSceneId, pFadeMs, pSequence = gLectureSceneTransitionSequence)
  {
    const tSceneId = typeof pSceneId === "string" ? pSceneId : "";
    const tFadeMs = Math.max (120, Number (pFadeMs) || 0);
    const tTransitionSequence = pSequence;
    const tTransitionValide = () => gLectureSceneTransitionSequence === tTransitionSequence;
    const tDebutMs = Date.now ();
    const tAttenteMaxMs = Math.max (500, Math.min (1500, Math.round (tFadeMs * 1.2)));
    const tRaf = typeof requestAnimationFrame === "function"
      ? requestAnimationFrame
      : (pCb) => setTimeout (pCb, 16);
    let tFonduSortieDemarre = false;
    if (gLectureSceneTransitionComputation)
      {
        gLectureSceneTransitionComputation.stop ();
        gLectureSceneTransitionComputation = null;
      }
    gLectureSceneTransitionDureeMs.set (tFadeMs);
    const tTermineTransition = () =>
      {
        if (!tTransitionValide ()) return;
        if (tSceneId && Session.get ("scene_id") !== tSceneId) return;
        gLectureSceneTransition.set (false);
        gLectureSceneTransitionSortie.set (false);
        gLectureSceneTransitionDureeMs.set (null);
        retire_overlay_transition_lecture_scene ();
      };
    const tLanceFonduSortie = () =>
      {
        if (tFonduSortieDemarre) return;
        if (!tTransitionValide ()) return;
        if (!gLectureSceneTransition.get ()) return;
        if (tSceneId && Session.get ("scene_id") !== tSceneId) return;
        tFonduSortieDemarre = true;
        if (gLectureSceneTransitionComputation)
          {
            gLectureSceneTransitionComputation.stop ();
            gLectureSceneTransitionComputation = null;
          }
        if (gLectureSceneTransitionTimer)
          {
            clearTimeout (gLectureSceneTransitionTimer);
            gLectureSceneTransitionTimer = null;
          }
        gLectureSceneTransitionSortie.set (false);
        if (mode_lecture_scene_actif ())
          rafraichit_scene_lecture_tpl (gSceneTemplateActif, true);
        tRaf (() => tRaf (() =>
          {
            if (!tTransitionValide ()) return;
            if (!gLectureSceneTransition.get ()) return;
            if (tSceneId && Session.get ("scene_id") !== tSceneId) return;
            gLectureSceneTransitionSortie.set (true);
            anime_overlay_transition_lecture_scene (0, tFadeMs)
              .then (() =>
                {
                  if (!tTransitionValide ()) return;
                  tTermineTransition ();
                });
          }));
      };
    const tProgrammeFonduSortie = () =>
      {
        if (!tTransitionValide ()) return;
        if (tSceneId && Session.get ("scene_id") !== tSceneId) return;
        tLanceFonduSortie ();
      };
    const tAttendRendu = () =>
      {
        gLectureSceneTransitionTimer = null;
        if (!tTransitionValide ()) return;
        if (!gLectureSceneTransition.get ()) return;
        const tTpl = gSceneTemplateActif;
        const tBonneScene = !tSceneId || Session.get ("scene_id") === tSceneId;
        if (tBonneScene && svg_scene_transition_pret_tpl (tTpl, tSceneId))
          {
            tRaf (() => tRaf (tProgrammeFonduSortie));
            return;
          }
        if (Date.now () - tDebutMs >= tAttenteMaxMs)
          {
            console.warn ("[lecture] transition scène en attente du SVG prêt", tSceneId || Session.get ("scene_id"));
            tLanceFonduSortie ();
            return;
          }
        gLectureSceneTransitionTimer = setTimeout (tAttendRendu, 40);
      };
    gLectureSceneTransitionTimer = setTimeout (tAttendRendu, 40);
    const tComputation = Tracker.autorun ((pComp) =>
      {
        const tTpl = gSceneTemplateActif;
        const tBonneScene = !tSceneId || Session.get ("scene_id") === tSceneId;
        if (!tTransitionValide ())
          {
            pComp.stop ();
            if (gLectureSceneTransitionComputation === pComp)
              gLectureSceneTransitionComputation = null;
            return;
          }
        if (!tBonneScene || !svg_scene_transition_pret_tpl (tTpl, tSceneId)) return;
        pComp.stop ();
        if (gLectureSceneTransitionComputation === pComp)
          gLectureSceneTransitionComputation = null;
        if (gLectureSceneTransitionTimer)
          {
            clearTimeout (gLectureSceneTransitionTimer);
            gLectureSceneTransitionTimer = null;
          }
        tRaf (() => tRaf (tProgrammeFonduSortie));
      });
    gLectureSceneTransitionComputation = tComputation;
    if (tComputation.stopped && gLectureSceneTransitionComputation === tComputation)
      gLectureSceneTransitionComputation = null;
  }

export async function ouvre_scene_lecture_avec_fondu (pTpl, pSceneId)
  {
    if (!mode_lecture_scene_actif ()) return false;
    const tSceneId = typeof pSceneId === "string" ? pSceneId.trim () : "";
    if (intercepte_action_scene_verrouillee_diffusion (tSceneId)) return true;
    const tScene = tSceneId ? Scenes.findOne (tSceneId) : null;
    const tChemin = chemin_scene_projet_courant (tScene);
    const tFadeMs = Math.max (0, Number (reglages_mode_lecture_scene ().transition_scene_fade_ms) || 0);
    if (!tScene || !tChemin) return false;
    if (scene_terminee_lecture_session (tScene._id))
      {
        const tRejouer = await demande_rejouer_scene_terminee_lecture (tScene._id);
        if (!tRejouer) return true;
        efface_lecture_score_scene_session (tScene._id);
      }

    arrete_audio_lecture_scene ();
    gLectureSceneFocusCarte.set (null);
    reinitialise_transitions_lecture_tpl (pTpl || gSceneTemplateActif);
    if (tScene._id === Session.get ("scene_id"))
      {
        rafraichit_scene_lecture_tpl (pTpl || gSceneTemplateActif, true);
        lance_animations_entree_scene_tpl (pTpl || gSceneTemplateActif);
        gLectureMorphingAutoSceneId = tScene._id;
        Tracker.afterFlush (() => consomme_navigation_interne_morphing_si_prete (pTpl || gSceneTemplateActif));
        return true;
      }

    annule_transition_lecture_scene ();

    if (tFadeMs <= 0)
      {
        gLectureMorphingAutoSceneId = tScene._id;
        FlowRouter.go (tChemin);
        return true;
      }

    const tTransitionSequence = gLectureSceneTransitionSequence + 1;
    gLectureSceneTransitionSequence = tTransitionSequence;
    const tAvantNavigationMs = Math.max (120, tFadeMs);
    const tApresNavigationMs = Math.max (120, tFadeMs);
    gLectureSceneTransitionSortie.set (false);
    gLectureSceneTransitionDureeMs.set (tAvantNavigationMs);
    gLectureSceneTransition.set (true);
    anime_overlay_transition_lecture_scene (1, tAvantNavigationMs)
      .then (() =>
        {
          if (gLectureSceneTransitionSequence !== tTransitionSequence) return;
          gLectureMorphingAutoSceneId = tScene._id;
          FlowRouter.go (tChemin);
          termine_transition_lecture_scene_apres_rendu (tScene._id, tApresNavigationMs, tTransitionSequence);
        });
    return true;
  }

function execute_action_bouton_lecture_scene (pTpl, pId)
  {
    const tConfig = config_objet_scene_courante (pId) || item_interface_scene_par_id (pId);
    const tAction = normalise_action_interactivite_objet (tConfig && tConfig.interactivite_action);
    if (!tAction) return false;

    if (tAction === INTERACTIVITE_ACTION_SCENE)
      {
        if (intercepte_action_scene_verrouillee_diffusion (tConfig && tConfig.interactivite_scene_id)) return true;
        if (intercepte_action_scene_commerciale_embarque (pTpl, tConfig && tConfig.interactivite_scene_id)) return true;
        return ouvre_scene_lecture_avec_fondu (pTpl, tConfig && tConfig.interactivite_scene_id);
      }
    if (tAction === INTERACTIVITE_ACTION_SON)
      return joue_audio_voix_lecture_scene (tConfig && tConfig.audio_file_id);
    if (tAction === INTERACTIVITE_ACTION_URL)
      return ouvre_url_dans_fenetre_active_interactivite (tConfig && tConfig.interactivite_url);
    if (tAction === INTERACTIVITE_ACTION_VIDEO)
      return ouvre_apercu_video_inspecteur (tConfig && tConfig.interactivite_video_url);
    if (tAction === INTERACTIVITE_ACTION_DIAPORAMA)
      {
        joue_diaporama_objet_lecture_scene (pId, {tpl : pTpl, categorie : CALQUES_CATEGORIE_BOUTONS});
        return true;
      }
    if (tAction === INTERACTIVITE_ACTION_COMPTE)
      {
        window.dispatchEvent (new CustomEvent ("diffusion:account-overlay-open"));
        return true;
      }
    if (tAction === INTERACTIVITE_ACTION_INIT_AUDIO_MOBILE)
      return execute_action_initialisation_audio_mobile_lecture_scene ();
    return false;
  }

function execute_retour_menu_scene_lecture (pTpl)
  {
    const tRetour = bouton_retour_menu_scene_lecture_courante ();
    if (!tRetour || !tRetour.id) return false;
    return execute_action_bouton_lecture_scene (pTpl, tRetour.id);
  }

function fadeout_noeud_initialisation_audio_mobile (pNoeud)
  {
    if (!pNoeud || !pNoeud.style || pNoeud.getAttribute ("data-cogi-mobile-audio-init-hidden") === "1") return;
    pNoeud.setAttribute ("data-cogi-mobile-audio-init-hidden", "1");
    pNoeud.style.pointerEvents = "none";
    pNoeud.style.transition = "opacity 260ms ease";
    requestAnimationFrame (() =>
      {
        if (pNoeud && pNoeud.style) pNoeud.style.opacity = "0";
      });
  }

function execute_action_initialisation_audio_mobile_lecture_scene (pNoeud)
  {
    deverrouille_audio_instant_lecture_scene ();
    fadeout_noeud_initialisation_audio_mobile (pNoeud);
    return true;
  }

export function demarre_lecture_scene_courante ()
  {
    const tSceneId = Session.get ("scene_id");
    if (!tSceneId) return false;
    const tTransitionSceneEnCours = gLectureSceneTransition.get ();

    gLectureSceneActive.set (true);
    commande_items_hype_tpl (gSceneTemplateActif, "reset-lecture");
    commande_items_svg_inline_tpl (gSceneTemplateActif, "reset-lecture");
    gLectureSceneFocusCarte.set (null);
    reinitialise_transitions_lecture_tpl (gSceneTemplateActif);
    if (!tTransitionSceneEnCours)
      annule_transition_lecture_scene ();
    arrete_audio_lecture_scene ();
    efface_selection_calques_scene ();
    reinitialise_selection_arbre_carte ();
    synchronise_ambiance_lecture_scene ();
    prepare_audios_interface_lecture_scene ();

    Tracker.afterFlush (() =>
      {
        rafraichit_scene_lecture_tpl (gSceneTemplateActif, true);
        relance_items_svg_inline_lecture_tpl (gSceneTemplateActif);
        lance_animations_entree_scene_tpl (gSceneTemplateActif);
        if (scene_terminee_lecture_session (tSceneId))
          demande_rejouer_scene_terminee_lecture (tSceneId).then ((pRejouer) =>
            {
              if (!pRejouer || Session.get ("scene_id") !== tSceneId) return;
              efface_lecture_score_scene_session (tSceneId);
              rafraichit_scene_lecture_tpl (gSceneTemplateActif, true);
            });
      });
    return true;
  }

export function arrete_lecture_scene_courante ()
  {
    if (!mode_lecture_scene_actif ()) return false;

    repond_dialog_rejouer_scene_lecture (false);
    commande_items_hype_tpl (gSceneTemplateActif, "pause-edition");
    commande_items_svg_inline_tpl (gSceneTemplateActif, "pause-edition");
    annule_animations_entree_scene_tpl (gSceneTemplateActif);
    gLectureSceneActive.set (false);
    gLectureSceneFocusCarte.set (null);
    reinitialise_transitions_lecture_tpl (gSceneTemplateActif);
    annule_transition_lecture_scene ();
    arrete_audio_lecture_scene ();
    arrete_ambiance_lecture_scene ();
    dispose_audio_instant_lecture_scene ();
    efface_selection_calques_scene ();
    reinitialise_selection_arbre_carte ();
    Tracker.afterFlush (() =>
      {
        rafraichit_scene_lecture_tpl (gSceneTemplateActif, true);
      });
    return true;
  }

function fabrique_brouillon_bulle_depuis_carte (pCarte)
  {
    if (!pCarte || !pCarte._id) return null;
    const tNom = typeof pCarte.nom === "string" ? pCarte.nom : "";
    const tVue = normalise_vue_carte_client (pCarte.vue);
    const tIdsMasques = normalise_ids_masques_carte_client (pCarte.ids_masques);
    const tOrdreObjets = normalise_ordre_objets_carte_client (pCarte.ordre_objets);
    const tCouleurFond = couleur_fond_bulle_carte_effective_client (pCarte);
    const tPlanBulles = plan_bulles_projet_scene_editee_client ({cartesOverride : [pCarte]});
    const tConfigBulle = configuration_bulle_carte_effective_client (
      pCarte,
      {planBulles : tPlanBulles, seed : pCarte._id}
    );
    const tCouleurBulle = tConfigBulle.couleur_bulle;
    const tOpaciteBulle = opacite_bulle_carte_effective_client (
      pCarte,
      {planBulles : tPlanBulles, seed : pCarte._id, configuration : tConfigBulle}
    );
    const tTailleBulle = taille_bulle_carte_effective_client (
      pCarte,
      {planBulles : tPlanBulles, seed : pCarte._id, configuration : tConfigBulle}
    );
    const tRotationBulle = rotation_bulle_carte_effective_client (
      pCarte,
      {planBulles : tPlanBulles, seed : pCarte._id, configuration : tConfigBulle}
    );
    const tPositionBulle = normalise_position_bulle_carte_client (pCarte.position_bulle)
      || position_bulle_carte_initiale_client (tVue, tTailleBulle);
    const tInterfaceCarte = pCarte.reglages_interface_carte
      ? reglages_interface_carte_pour_stockage (pCarte.reglages_interface_carte)
      : null;
    return {
      carte_id : pCarte._id,
      nom : tNom,
      vue : tVue,
      ids_masques : tIdsMasques,
      ordre_objets : tOrdreObjets,
      ...(tCouleurFond ? {couleur_fond : tCouleurFond} : {}),
      couleur_bulle : tCouleurBulle,
      svg_bulle_id : tConfigBulle.svg_bulle_id,
      opacite_bulle : tOpaciteBulle,
      taille_bulle : tTailleBulle,
      rotation_bulle : tRotationBulle,
      position_bulle : tPositionBulle,
      ...(tInterfaceCarte ? {reglages_interface_carte : tInterfaceCarte} : {}),
      base_nom : tNom,
      base_vue : tVue,
      base_ids_masques : tIdsMasques,
      base_ordre_objets : tOrdreObjets,
      ...(tCouleurFond ? {base_couleur_fond : tCouleurFond} : {}),
      base_couleur_bulle : tCouleurBulle,
      base_svg_bulle_id : tConfigBulle.svg_bulle_id,
      base_opacite_bulle : tOpaciteBulle,
      base_taille_bulle : tTailleBulle,
      base_rotation_bulle : tRotationBulle,
      base_position_bulle : tPositionBulle,
      ...(tInterfaceCarte ? {base_reglages_interface_carte : tInterfaceCarte} : {}),
    };
  }

function brouillon_bulle_courant ()
  {
    const tCarteId = gBulleSelectionnee.get ();
    const tBrouillon = gBulleBrouillon.get ();
    return tBrouillon && tBrouillon.carte_id === tCarteId ? tBrouillon : null;
  }

function bulle_brouillon_modifie ()
  {
    const tCarte = carte_bulle_selectionnee_scene ();
    const tBrouillon = brouillon_bulle_courant ();
    if (!tCarte || !tBrouillon) return false;
    const tNomCarte = typeof tBrouillon.base_nom === "string"
      ? tBrouillon.base_nom.trim ()
      : (typeof tCarte.nom === "string" ? tCarte.nom.trim () : "");
    const tNomBrouillon = typeof tBrouillon.nom === "string" ? tBrouillon.nom.trim () : "";
    const tBaseCouleurFond = Object.prototype.hasOwnProperty.call (tBrouillon, "base_couleur_fond")
      ? couleur_fond_bulle_carte_effective_client ({couleur_fond : tBrouillon.base_couleur_fond})
      : null;
    const tCouleurFondBrouillon = Object.prototype.hasOwnProperty.call (tBrouillon, "couleur_fond")
      ? couleur_fond_bulle_carte_effective_client ({couleur_fond : tBrouillon.couleur_fond})
      : null;
    const tSvgBulleBase = normalise_svg_bulle_id_carte_client (tBrouillon.base_svg_bulle_id);
    const tSvgBulleCourante = normalise_svg_bulle_id_carte_client (tBrouillon.svg_bulle_id);
    return tNomBrouillon !== tNomCarte
      || !vues_cartes_equivalentes (tBrouillon.base_vue, tBrouillon.vue)
      || tBaseCouleurFond !== tCouleurFondBrouillon
      || tSvgBulleBase !== tSvgBulleCourante
      || couleur_bulle_carte_effective_client (
      {
        couleur_bulle : tBrouillon.base_couleur_bulle,
        svg_bulle_id : tBrouillon.base_svg_bulle_id,
        carte_id : tBrouillon.carte_id,
      },
      tBrouillon.carte_id
    ) !== couleur_bulle_carte_effective_client (
      {
        couleur_bulle : tBrouillon.couleur_bulle,
        svg_bulle_id : tBrouillon.svg_bulle_id,
        carte_id : tBrouillon.carte_id,
      },
      tBrouillon.carte_id
    )
      || opacite_bulle_carte_effective_client ({
        opacite_bulle : tBrouillon.base_opacite_bulle,
        svg_bulle_id : tBrouillon.base_svg_bulle_id,
        carte_id : tBrouillon.carte_id,
      }) !== opacite_bulle_carte_effective_client ({
        opacite_bulle : tBrouillon.opacite_bulle,
        svg_bulle_id : tBrouillon.svg_bulle_id,
        carte_id : tBrouillon.carte_id,
      })
      || taille_bulle_carte_effective_client ({
        taille_bulle : tBrouillon.base_taille_bulle,
        svg_bulle_id : tBrouillon.base_svg_bulle_id,
        carte_id : tBrouillon.carte_id,
      }) !== taille_bulle_carte_effective_client ({
        taille_bulle : tBrouillon.taille_bulle,
        svg_bulle_id : tBrouillon.svg_bulle_id,
        carte_id : tBrouillon.carte_id,
      })
      || rotation_bulle_carte_effective_client ({
        rotation_bulle : tBrouillon.base_rotation_bulle,
      }) !== rotation_bulle_carte_effective_client ({
        rotation_bulle : tBrouillon.rotation_bulle,
      })
      || signature_ids_masques_carte_client (tBrouillon.base_ids_masques || tCarte.ids_masques)
          !== signature_ids_masques_carte_client (tBrouillon.ids_masques)
      || signature_ordre_objets_carte_client (tBrouillon.base_ordre_objets || tCarte.ordre_objets)
          !== signature_ordre_objets_carte_client (tBrouillon.ordre_objets)
      || signature_position_bulle_carte_client (tBrouillon.base_position_bulle)
          !== signature_position_bulle_carte_client (tBrouillon.position_bulle)
      || reglages_interface_carte_signature (tBrouillon.base_reglages_interface_carte || {})
          !== reglages_interface_carte_signature (tBrouillon.reglages_interface_carte || {});
  }

function reinitialise_selection_bulle_calque ()
  {
    gBulleSelectionnee.set (null);
    gBulleBrouillon.set (null);
    ferme_menus_locaux_inspecteur_bulle_client ();
    reinitialise_selection_arbre_carte ();
  }

function efface_selection_calques_scene ()
  {
    abandon_brouillon_bulle_si_necessaire ();
    reinitialise_selection_bulle_calque ();
    gCalqueSelectionne.set (null);
    gCategorieSelectionnee.set (null);
    gInspecteurCalquesMode.set (null);
  }

function abandon_brouillon_bulle_si_necessaire (pCarteIdCible = null, pOpt = {})
  {
    const tCourant = gBulleSelectionnee.get ();
    if (!tCourant) return true;
    if (!pOpt.force && pCarteIdCible && pCarteIdCible === tCourant) return true;
    if (!bulle_brouillon_modifie ()) return true;

    // En mode Calques, quitter la bulle sélectionnée abandonne toujours le
    // brouillon local non enregistré au lieu d'ouvrir une confirmation.
    reinitialise_selection_bulle_calque ();
    return true;
  }

function ouvre_editeur_bulle_calque (pCarteId, pOpt = {})
  {
    const tCarte = carte_source_pour_bulle_scene (pCarteId);
    if (!tCarte) return false;
    if (!abandon_brouillon_bulle_si_necessaire (tCarte._id, pOpt)) return false;

    const tMemeCarte = gBulleSelectionnee.get () === tCarte._id;
    gCategorieSelectionnee.set (null);
    gCalqueSelectionne.set (null);
    gBulleSelectionnee.set (tCarte._id);
    gInspecteurCalquesMode.set ("bulle");
    ferme_menus_locaux_inspecteur_bulle_client ();
    const tSceneId = Session.get ("scene_id");
    if (tSceneId) ecrit_derniere_selection_calques_scene (tSceneId, "bulle", tCarte._id);

    const tBrouillon = brouillon_bulle_courant ();
    if (!tBrouillon || tBrouillon.carte_id !== tCarte._id || pOpt.force)
      gBulleBrouillon.set (fabrique_brouillon_bulle_depuis_carte (tCarte));
    if (!tMemeCarte || pOpt.force)
      reinitialise_selection_arbre_carte ();
    return true;
  }

function selectionne_bulle_calque (pCarteId, pOpt = {})
  {
    annule_autoplay_prochaine_maj_fichier_inspecteur ();
    aligne_autoplay_maj_fichier_sur_selection_inspecteur (null);
    if (gWS.instance && gWS.instance.isPlaying ()) gWS.instance.pause ();
    return ouvre_editeur_bulle_calque (pCarteId, pOpt);
  }

function selectionne_bulle_calque_lecture_passive (pCarteId)
  {
    const tCarte = carte_source_pour_bulle_scene (pCarteId);
    if (!tCarte || !mode_lecture_scene_actif ()) return false;
    gCategorieSelectionnee.set (null);
    gCalqueSelectionne.set (null);
    gBulleSelectionnee.set (tCarte._id);
    gInspecteurCalquesMode.set ("bulle");
    ferme_menus_locaux_inspecteur_bulle_client ();
    reinitialise_selection_arbre_carte ();
    return true;
  }

function synchronise_selection_bulle_apres_focus_lecture (pTpl, pCarteId)
  {
    const tTpl = pTpl || gSceneTemplateActif;
    const tSceneId = Session.get ("scene_id") || null;
    if (!tTpl || !pCarteId || !mode_lecture_scene_actif ()) return false;
    Tracker.afterFlush (() =>
      {
        if (!mode_lecture_scene_actif ()) return;
        if ((Session.get ("scene_id") || null) !== tSceneId) return;
        if (!tTpl._lectureZoomAltActif || gLectureSceneFocusCarte.get () !== pCarteId) return;
        if (gBulleSelectionnee.get () === pCarteId) return;
        selectionne_bulle_calque_lecture_passive (pCarteId);
      });
    return true;
  }

function rebase_brouillon_bulle_apres_sauvegarde (pCarteId, pSauvegarde)
  {
    const tBrouillon = gBulleBrouillon.get ();
    if (!tBrouillon || tBrouillon.carte_id !== pCarteId) return;

    const tSauvegarde = pSauvegarde && typeof pSauvegarde === "object" ? pSauvegarde : {};
    const tNom = typeof (pSauvegarde && pSauvegarde.nom) === "string"
      ? pSauvegarde.nom
      : tBrouillon.nom;
    const tVue = normalise_vue_carte_client (pSauvegarde && pSauvegarde.vue, tBrouillon.vue);
    const tIdsMasques = Object.prototype.hasOwnProperty.call (tSauvegarde, "ids_masques")
      ? normalise_ids_masques_carte_client (tSauvegarde.ids_masques)
      : normalise_ids_masques_carte_client (tBrouillon.ids_masques);
    const tOrdreObjets = Object.prototype.hasOwnProperty.call (tSauvegarde, "ordre_objets")
      ? normalise_ordre_objets_carte_client (tSauvegarde.ordre_objets)
      : normalise_ordre_objets_carte_client (tBrouillon.ordre_objets);
    const tCouleurFond = Object.prototype.hasOwnProperty.call (tSauvegarde, "couleur_fond")
      ? couleur_fond_bulle_carte_effective_client ({couleur_fond : tSauvegarde.couleur_fond})
      : (Object.prototype.hasOwnProperty.call (tBrouillon, "couleur_fond")
        ? couleur_fond_bulle_carte_effective_client ({couleur_fond : tBrouillon.couleur_fond})
        : null);
    const tSvgBulleId = Object.prototype.hasOwnProperty.call (tSauvegarde, "svg_bulle_id")
      ? normalise_svg_bulle_id_carte_client (tSauvegarde && tSauvegarde.svg_bulle_id)
      : normalise_svg_bulle_id_carte_client (tBrouillon.svg_bulle_id);
    const tCouleurBulle = couleur_bulle_carte_effective_client (
      {
        couleur_bulle : pSauvegarde && pSauvegarde.couleur_bulle,
        svg_bulle_id : tSvgBulleId,
        carte_id : pCarteId,
      },
      pCarteId
    );
    const tOpaciteBulle = normalise_opacite_bulle_carte_client (
      pSauvegarde && pSauvegarde.opacite_bulle,
      tBrouillon.opacite_bulle
    );
    const tTailleBulle = normalise_taille_bulle_carte_client (
      pSauvegarde && pSauvegarde.taille_bulle,
      tBrouillon.taille_bulle
    );
    const tRotationBulle = normalise_rotation_bulle_carte_client (
      pSauvegarde && pSauvegarde.rotation_bulle,
      tBrouillon.rotation_bulle
    );
    const tPositionBulle = normalise_position_bulle_carte_client (
      pSauvegarde && pSauvegarde.position_bulle,
      tBrouillon.position_bulle
    );
    const tInterfaceCarte = Object.prototype.hasOwnProperty.call (tSauvegarde, "reglages_interface_carte")
      ? reglages_interface_carte_pour_stockage (tSauvegarde.reglages_interface_carte)
      : reglages_interface_carte_pour_stockage (tBrouillon.reglages_interface_carte || {});

    gBulleBrouillon.set (
      {
        ...tBrouillon,
        nom : tNom,
        vue : tVue,
        ids_masques : tIdsMasques,
        ordre_objets : tOrdreObjets,
        ...(tCouleurFond ? {couleur_fond : tCouleurFond} : {}),
        couleur_bulle : tCouleurBulle,
        svg_bulle_id : tSvgBulleId,
        opacite_bulle : tOpaciteBulle,
        taille_bulle : tTailleBulle,
        rotation_bulle : tRotationBulle,
        position_bulle : tPositionBulle,
        reglages_interface_carte : tInterfaceCarte,
        base_nom : tNom,
        base_vue : tVue,
        base_ids_masques : tIdsMasques,
        base_ordre_objets : tOrdreObjets,
        ...(tCouleurFond ? {base_couleur_fond : tCouleurFond} : {}),
        base_couleur_bulle : tCouleurBulle,
        base_svg_bulle_id : tSvgBulleId,
        base_opacite_bulle : tOpaciteBulle,
        base_taille_bulle : tTailleBulle,
        base_rotation_bulle : tRotationBulle,
        base_position_bulle : tPositionBulle,
        base_reglages_interface_carte : tInterfaceCarte,
      }
    );
  }

function synchronise_brouillon_carte_apres_sauvegarde_bulle (pCarteId, pSauvegarde)
  {
    const tBrouillon = gCarteBrouillon.get ();
    if (!tBrouillon || tBrouillon.carte_id !== pCarteId) return;

    const tSauvegarde = pSauvegarde && typeof pSauvegarde === "object" ? pSauvegarde : {};
    const tNom = typeof (pSauvegarde && pSauvegarde.nom) === "string"
      ? pSauvegarde.nom
      : tBrouillon.nom;
    const tVue = normalise_vue_carte_client (pSauvegarde && pSauvegarde.vue, tBrouillon.vue);
    const tIdsMasques = Object.prototype.hasOwnProperty.call (tSauvegarde, "ids_masques")
      ? normalise_ids_masques_carte_client (tSauvegarde.ids_masques)
      : normalise_ids_masques_carte_client (tBrouillon.ids_masques);
    const tOrdreObjets = Object.prototype.hasOwnProperty.call (tSauvegarde, "ordre_objets")
      ? normalise_ordre_objets_carte_client (tSauvegarde.ordre_objets)
      : normalise_ordre_objets_carte_client (tBrouillon.ordre_objets);
    const tCouleurFond = normalise_couleur_carte_client (
      pSauvegarde && pSauvegarde.couleur_fond,
      tBrouillon.couleur_fond
    );
    const tSvgBulleId = Object.prototype.hasOwnProperty.call (tSauvegarde, "svg_bulle_id")
      ? normalise_svg_bulle_id_carte_client (pSauvegarde && pSauvegarde.svg_bulle_id)
      : normalise_svg_bulle_id_carte_client (tBrouillon.svg_bulle_id);
    const tCouleurBulle = couleur_bulle_carte_effective_client (
      {
        couleur_bulle : pSauvegarde && pSauvegarde.couleur_bulle,
        svg_bulle_id : tSvgBulleId,
        carte_id : pCarteId,
      },
      pCarteId
    );
    const tOpaciteBulle = normalise_opacite_bulle_carte_client (
      pSauvegarde && pSauvegarde.opacite_bulle,
      tBrouillon.opacite_bulle
    );
    const tTailleBulle = normalise_taille_bulle_carte_client (
      pSauvegarde && pSauvegarde.taille_bulle,
      tBrouillon.taille_bulle
    );
    const tRotationBulle = normalise_rotation_bulle_carte_client (
      pSauvegarde && pSauvegarde.rotation_bulle,
      tBrouillon.rotation_bulle
    );
    const tPositionBulle = normalise_position_bulle_carte_client (
      pSauvegarde && pSauvegarde.position_bulle,
      tBrouillon.position_bulle
    );
    const tInterfaceCarte = Object.prototype.hasOwnProperty.call (tSauvegarde, "reglages_interface_carte")
      ? reglages_interface_carte_pour_stockage (tSauvegarde.reglages_interface_carte)
      : reglages_interface_carte_pour_stockage (tBrouillon.reglages_interface_carte || {});

    gCarteBrouillon.set (
      {
        ...tBrouillon,
        nom : tNom,
        vue : tVue,
        ids_masques : tIdsMasques,
        ordre_objets : tOrdreObjets,
        couleur_fond : tCouleurFond,
        couleur_bulle : tCouleurBulle,
        svg_bulle_id : tSvgBulleId,
        opacite_bulle : tOpaciteBulle,
        taille_bulle : tTailleBulle,
        rotation_bulle : tRotationBulle,
        position_bulle : tPositionBulle,
        reglages_interface_carte : tInterfaceCarte,
        base_nom : tNom,
        base_vue : tVue,
        base_ids_masques : tIdsMasques,
        base_ordre_objets : tOrdreObjets,
        base_couleur_fond : tCouleurFond,
        base_couleur_bulle : tCouleurBulle,
        base_svg_bulle_id : tSvgBulleId,
        base_opacite_bulle : tOpaciteBulle,
        base_taille_bulle : tTailleBulle,
        base_rotation_bulle : tRotationBulle,
        base_position_bulle : tPositionBulle,
        base_reglages_interface_carte : tInterfaceCarte,
      }
    );
  }

function fabrique_brouillon_carte_depuis_doc (pCarte)
  {
    if (!pCarte || !pCarte._id) return null;
    const tNom = typeof pCarte.nom === "string" ? pCarte.nom : "";
    const tVue = normalise_vue_carte_client (pCarte.vue);
    const tIdsMasques = normalise_ids_masques_carte_client (pCarte.ids_masques);
    const tOrdreObjets = normalise_ordre_objets_carte_client (pCarte.ordre_objets);
    const tCouleurFond = couleur_fond_carte_effective_client (pCarte);
    const tPlanBulles = plan_bulles_projet_scene_editee_client ({cartesOverride : [pCarte]});
    const tConfigBulle = configuration_bulle_carte_effective_client (
      pCarte,
      {planBulles : tPlanBulles, seed : pCarte._id}
    );
    const tCouleurBulle = tConfigBulle.couleur_bulle;
    const tOpaciteBulle = opacite_bulle_carte_effective_client (
      pCarte,
      {planBulles : tPlanBulles, seed : pCarte._id, configuration : tConfigBulle}
    );
    const tTailleBulle = taille_bulle_carte_effective_client (
      pCarte,
      {planBulles : tPlanBulles, seed : pCarte._id, configuration : tConfigBulle}
    );
    const tRotationBulle = rotation_bulle_carte_effective_client (
      pCarte,
      {planBulles : tPlanBulles, seed : pCarte._id, configuration : tConfigBulle}
    );
    const tPositionBulle = normalise_position_bulle_carte_client (pCarte.position_bulle)
      || position_bulle_carte_initiale_client (tVue, tTailleBulle);
    const tInterfaceCarte = pCarte.reglages_interface_carte
      ? reglages_interface_carte_pour_stockage (pCarte.reglages_interface_carte)
      : null;
    return {
      carte_id : pCarte._id,
      nom : tNom,
      vue : tVue,
      ids_masques : tIdsMasques,
      ordre_objets : tOrdreObjets,
      couleur_fond : tCouleurFond,
      couleur_bulle : tCouleurBulle,
      svg_bulle_id : tConfigBulle.svg_bulle_id,
      opacite_bulle : tOpaciteBulle,
      taille_bulle : tTailleBulle,
      rotation_bulle : tRotationBulle,
      position_bulle : tPositionBulle,
      ...(tInterfaceCarte ? {reglages_interface_carte : tInterfaceCarte} : {}),
      base_nom : tNom,
      base_vue : tVue,
      base_ids_masques : tIdsMasques,
      base_ordre_objets : tOrdreObjets,
      base_couleur_fond : tCouleurFond,
      base_couleur_bulle : tCouleurBulle,
      base_svg_bulle_id : tConfigBulle.svg_bulle_id,
      base_opacite_bulle : tOpaciteBulle,
      base_taille_bulle : tTailleBulle,
      base_rotation_bulle : tRotationBulle,
      base_position_bulle : tPositionBulle,
      ...(tInterfaceCarte ? {base_reglages_interface_carte : tInterfaceCarte} : {}),
    };
  }

function brouillon_carte_courant ()
  {
    const tCarteId = gCarteSelectionnee.get ();
    const tBrouillon = gCarteBrouillon.get ();
    return tBrouillon && tBrouillon.carte_id === tCarteId ? tBrouillon : null;
  }

function initialise_brouillon_carte_depuis_doc (pCarte)
  {
    reinitialise_notice_cadrage_carte ();
    gCarteAdoptCurrentSuggested.set (false);
    gCarteBrouillon.set (fabrique_brouillon_carte_depuis_doc (pCarte));
  }

function reinitialise_selection_arbre_carte ()
  {
    gCarteArbreSelectionne.set (null);
    gCarteArbreCategorieSelectionnee.set (null);
    carte_obj_reorder_cleanup ();
  }

function reinitialise_notice_cadrage_carte ()
  {
    gCartePreviewNoticeArmed.set (false);
  }

function arme_notice_cadrage_carte ()
  {
    if (!carte_editor_est_actif ()) return;
    if (gCartePreviewNoticeArmed.get ()) return;
    gCartePreviewNoticeArmed.set (true);
  }

function reinitialise_etat_cartes_scene ()
  {
    gCarteSelectionnee.set (null);
    gCarteMode.set (CARTE_MODE_LISTE);
    gCarteBrouillon.set (null);
    reinitialise_selection_bulle_calque ();
    gCarteAdoptCurrentSuggested.set (false);
    reinitialise_notice_cadrage_carte ();
    reinitialise_selection_arbre_carte ();
    gCarteCategoriesOuvertes.clear ();
    calques_cartes_reorder_cleanup ();
    categories_calques_reorder_cleanup ();
  }

function carte_editor_est_actif ()
  {
    return false;
  }

function carte_arbre_calques_est_actif ()
  {
    return gOngletDroite.get () === "calques" && !!gBulleSelectionnee.get ();
  }

function carte_arbre_est_actif ()
  {
    return carte_editor_est_actif () || carte_arbre_calques_est_actif ();
  }

function carte_arbre_brouillon_courant ()
  {
    if (carte_editor_est_actif ()) return brouillon_carte_courant ();
    if (carte_arbre_calques_est_actif ()) return brouillon_bulle_courant ();
    return null;
  }

function carte_arbre_carte_courante_scene ()
  {
    if (carte_editor_est_actif ()) return carte_courante_scene ();
    if (carte_arbre_calques_est_actif ()) return carte_bulle_selectionnee_scene ();
    return null;
  }

function carte_arbre_ids_masques_effectifs ()
  {
    const tBrouillon = carte_arbre_brouillon_courant ();
    if (tBrouillon && Object.prototype.hasOwnProperty.call (tBrouillon, "ids_masques"))
      return normalise_ids_masques_carte_client (tBrouillon.ids_masques);
    const tCarte = carte_arbre_carte_courante_scene ();
    return normalise_ids_masques_carte_client (tCarte && tCarte.ids_masques);
  }

function carte_arbre_ordre_objets_effectif ()
  {
    const tBrouillon = carte_arbre_brouillon_courant ();
    if (tBrouillon && Object.prototype.hasOwnProperty.call (tBrouillon, "ordre_objets"))
      return normalise_ordre_objets_carte_client (tBrouillon.ordre_objets);
    const tCarte = carte_arbre_carte_courante_scene ();
    return normalise_ordre_objets_carte_client (tCarte && tCarte.ordre_objets);
  }

function vue_camera_courante_scene (pTpl)
  {
    const tVueBrute = vue_camera_brute_courante_scene (pTpl);
    if (tVueBrute) return tVueBrute;
    const tSceneId = Session.get ("scene_id");
    const tScene = tSceneId ? Scenes.findOne (tSceneId) : null;
    return normalise_vue_carte_client (tScene && tScene.vue_defaut);
  }

function vue_nouvelle_carte_centree_scene (pVueSource)
  {
    const tVueSource = normalise_vue_carte_client (pVueSource);
    if (!tVueSource) return null;
    const tCentreX = tVueSource.x + (tVueSource.w / 2);
    const tCentreY = tVueSource.y + (tVueSource.h / 2);
    return normalise_vue_carte_client (
      {
        x : tCentreX - (CARTE_CREATION_LARGEUR_DEFAUT / 2),
        y : tCentreY - (CARTE_CREATION_HAUTEUR_DEFAUT / 2),
        w : CARTE_CREATION_LARGEUR_DEFAUT,
        h : CARTE_CREATION_HAUTEUR_DEFAUT,
      }
    );
  }

function vue_camera_courante_scene_reactive (pTpl)
  {
    gPanX.get ();
    gPanY.get ();
    gZoom.get ();
    return vue_camera_courante_scene (pTpl);
  }

function vue_reference_arbre_cartes (pTpl)
  {
    const tBrouillon = carte_arbre_brouillon_courant ();
    if (tBrouillon && tBrouillon.vue)
      return normalise_vue_carte_client (tBrouillon.vue);
    const tCarte = carte_arbre_carte_courante_scene ();
    if (tCarte && tCarte.vue)
      return normalise_vue_carte_client (tCarte.vue);
    return vue_camera_courante_scene_reactive (pTpl);
  }

function zoom_depuis_vue_carte (pTpl, pVue)
  {
    const tNat = pTpl && (pTpl._svgVBNatifBrut || vue_camera_brute_courante_scene (pTpl) || pTpl._svgVBNatif);
    if (!tNat || !pVue || !pVue.w) return 1;
    return Math.round ((tNat.w / pVue.w) * 1000) / 1000;
  }

function vue_depuis_coordonnees_carte (pTpl, pCoordonnees, pFallback = null)
  {
    const tNat = pTpl && (pTpl._svgVBNatifBrut || vue_camera_brute_courante_scene (pTpl) || pTpl._svgVBNatif);
    if (!tNat || !pCoordonnees || typeof pCoordonnees !== "object")
      return pFallback ? {...pFallback} : null;
    const tX = Number (pCoordonnees.x);
    const tY = Number (pCoordonnees.y);
    const tZoom = Number (pCoordonnees.zoom);
    if (![tX, tY, tZoom].every (Number.isFinite) || tZoom <= 0)
      return pFallback ? {...pFallback} : null;
    const tW = tNat.w / Math.max (1 / 50, Math.min (50, tZoom));
    const tH = tNat.h / Math.max (1 / 50, Math.min (50, tZoom));
    return normalise_vue_carte_client ({x : tX, y : tY, w : tW, h : tH}, pFallback);
  }

function vues_cartes_equivalentes (pA, pB, pTolerance = 0.001)
  {
    const tA = normalise_vue_carte_client (pA);
    const tB = normalise_vue_carte_client (pB);
    if (!tA || !tB) return false;
    return Math.abs (tA.x - tB.x) <= pTolerance
        && Math.abs (tA.y - tB.y) <= pTolerance
        && Math.abs (tA.w - tB.w) <= pTolerance
        && Math.abs (tA.h - tB.h) <= pTolerance;
  }

function carte_brouillon_modifie ()
  {
    const tCarte = carte_courante_scene ();
    const tBrouillon = brouillon_carte_courant ();
    if (!tCarte || !tBrouillon) return false;
    const tNomCarte = typeof tBrouillon.base_nom === "string"
      ? tBrouillon.base_nom.trim ()
      : (typeof tCarte.nom === "string" ? tCarte.nom.trim () : "");
    const tNomBrouillon = typeof tBrouillon.nom === "string" ? tBrouillon.nom.trim () : "";
    const tCouleurFondBase = normalise_couleur_carte_client (
      tBrouillon.base_couleur_fond,
      CARTE_COULEUR_FOND_DEFAUT
    );
    const tCouleurFondCourante = normalise_couleur_carte_client (
      tBrouillon.couleur_fond,
      CARTE_COULEUR_FOND_DEFAUT
    );
    const tSvgBulleBase = normalise_svg_bulle_id_carte_client (tBrouillon.base_svg_bulle_id);
    const tSvgBulleCourante = normalise_svg_bulle_id_carte_client (tBrouillon.svg_bulle_id);
    const tCouleurBulleBase = couleur_bulle_carte_effective_client (
      {
        couleur_bulle : tBrouillon.base_couleur_bulle,
        svg_bulle_id : tBrouillon.base_svg_bulle_id,
        carte_id : tBrouillon.carte_id,
      },
      tBrouillon.carte_id
    );
    const tCouleurBulleCourante = couleur_bulle_carte_effective_client (
      {
        couleur_bulle : tBrouillon.couleur_bulle,
        svg_bulle_id : tBrouillon.svg_bulle_id,
        carte_id : tBrouillon.carte_id,
      },
      tBrouillon.carte_id
    );
    const tOpaciteBase = opacite_bulle_carte_effective_client ({
      opacite_bulle : tBrouillon.base_opacite_bulle,
      svg_bulle_id : tBrouillon.base_svg_bulle_id,
      carte_id : tBrouillon.carte_id,
    });
    const tOpaciteCourante = opacite_bulle_carte_effective_client ({
      opacite_bulle : tBrouillon.opacite_bulle,
      svg_bulle_id : tBrouillon.svg_bulle_id,
      carte_id : tBrouillon.carte_id,
    });
    const tTailleBase = taille_bulle_carte_effective_client ({
      taille_bulle : tBrouillon.base_taille_bulle,
      svg_bulle_id : tBrouillon.base_svg_bulle_id,
      carte_id : tBrouillon.carte_id,
    });
    const tTailleCourante = taille_bulle_carte_effective_client ({
      taille_bulle : tBrouillon.taille_bulle,
      svg_bulle_id : tBrouillon.svg_bulle_id,
      carte_id : tBrouillon.carte_id,
    });
    return tNomCarte !== tNomBrouillon
        || !vues_cartes_equivalentes (tBrouillon.base_vue || tCarte.vue, tBrouillon.vue)
        || signature_ids_masques_carte_client (tBrouillon.base_ids_masques || tCarte.ids_masques)
            !== signature_ids_masques_carte_client (tBrouillon.ids_masques)
        || signature_ordre_objets_carte_client (tBrouillon.base_ordre_objets || tCarte.ordre_objets)
          !== signature_ordre_objets_carte_client (tBrouillon.ordre_objets)
        || tCouleurFondBase !== tCouleurFondCourante
        || tSvgBulleBase !== tSvgBulleCourante
        || tCouleurBulleBase !== tCouleurBulleCourante
        || tOpaciteBase !== tOpaciteCourante
        || tTailleBase !== tTailleCourante
        || rotation_bulle_carte_effective_client ({
          rotation_bulle : tBrouillon.base_rotation_bulle,
        }) !== rotation_bulle_carte_effective_client ({
          rotation_bulle : tBrouillon.rotation_bulle,
        })
        || signature_position_bulle_carte_client (tBrouillon.base_position_bulle)
            !== signature_position_bulle_carte_client (tBrouillon.position_bulle)
        || reglages_interface_carte_signature (tBrouillon.base_reglages_interface_carte || {})
            !== reglages_interface_carte_signature (tBrouillon.reglages_interface_carte || {});
  }

function carte_brouillon_vue_modifiee ()
  {
    const tCarte = carte_courante_scene ();
    const tBrouillon = brouillon_carte_courant ();
    if (!tCarte || !tBrouillon || !tBrouillon.vue) return false;
    return !vues_cartes_equivalentes (tBrouillon.base_vue || tCarte.vue, tBrouillon.vue, 0.2);
  }

function cartes_preview_differe_du_brouillon (pTpl)
  {
    if (gCarteMode.get () !== CARTE_MODE_EDITEUR) return false;
    const tBrouillon = brouillon_carte_courant ();
    if (!tBrouillon || !tBrouillon.vue) return false;
    const tPreview = vue_camera_courante_scene_reactive (pTpl);
    if (!tPreview) return false;
    return !vues_cartes_equivalentes (tPreview, tBrouillon.vue, 0.2);
  }

function cartes_preview_notice_est_visible (pTpl)
  {
    return !!(gCartePreviewNoticeArmed.get ()
      && cartes_preview_differe_du_brouillon (pTpl));
  }

function ids_masques_carte_intersectant_vue (pTpl, pIds, pVue)
  {
    const tIds = normalise_ids_masques_carte_client (pIds);
    const tVue = normalise_vue_carte_client (pVue);
    const tDoc = pTpl && pTpl._svgDoc;
    const tRoot = tDoc && tDoc.documentElement;
    if (!tIds.length || !tVue || !tRoot) return [];
    return tIds.filter ((pId) =>
      vue_carte_intersecte_bbox (
        tVue,
        bbox_racine_cachee_pour_id (pTpl, tRoot, pId)
      ));
  }

function ids_boutons_scene_intersectant_vue (pTpl, pVue, pScene = null)
  {
    const tVue = normalise_vue_carte_client (pVue);
    const tScene = pScene || Scenes.findOne (Session.get ("scene_id"));
    const tDoc = pTpl && pTpl._svgDoc;
    const tRoot = tDoc && tDoc.documentElement;
    if (!tVue || !tScene || !tRoot) return [];
    const tBoutons = svg_objets_scene_normalises (tScene)[CALQUES_CATEGORIE_BOUTONS] || [];
    return tBoutons
      .map ((pBouton) => pBouton && pBouton.id)
      .filter (Boolean)
      .filter ((pId) =>
        vue_carte_intersecte_bbox (
          tVue,
          bbox_racine_cachee_pour_id (pTpl, tRoot, pId)
        ));
  }

function ajoute_ids_masques_uniques (pIds, pIdsSupplementaires)
  {
    return [...new Set ([
      ...normalise_ids_masques_carte_client (pIds),
      ...normalise_ids_masques_carte_client (pIdsSupplementaires),
    ])];
  }

function cartes_ids_masques_effectifs (pTpl = null)
  {
    if (mode_lecture_scene_actif ())
      {
        const tCarteAltId = pTpl && pTpl._lectureZoomAltMasquagesCarteId;
        const tCarte = carte_focus_lecture_scene ()
          || (tCarteAltId ? carte_source_pour_bulle_scene (tCarteAltId) : null);
        if (!tCarte) return [];
        return ajoute_ids_masques_uniques (
          ids_masques_carte_intersectant_vue (pTpl, tCarte.ids_masques, tCarte.vue),
          ids_boutons_scene_intersectant_vue (pTpl, tCarte.vue)
        );
      }

    if (!gBulleSelectionnee.get ()) return [];

    const tIds = carte_arbre_ids_masques_effectifs ();
    const tVue = vue_reference_arbre_cartes (pTpl);
    return ajoute_ids_masques_uniques (
      ids_masques_carte_intersectant_vue (pTpl, tIds, tVue),
      ids_boutons_scene_intersectant_vue (pTpl, tVue)
    );
  }

function retire_masquages_carte_racine (pRoot)
  {
    if (!pRoot || typeof pRoot.querySelectorAll !== "function") return;
    const tMasques = pRoot.querySelectorAll (`[${CARTE_MASK_ATTR}="1"]`);
    tMasques.forEach ((pElt) =>
      {
        if (pElt._cogiCarteMaskTimer)
          {
            clearTimeout (pElt._cogiCarteMaskTimer);
            delete pElt._cogiCarteMaskTimer;
          }
        const tPrevDisplay = pElt.getAttribute (CARTE_MASK_PREV_DISPLAY_ATTR);
        const tPrevOpacity = pElt.getAttribute (CARTE_MASK_PREV_OPACITY_ATTR);
        const tPrevTransition = pElt.getAttribute (CARTE_MASK_PREV_TRANSITION_ATTR);
        const tPrevPointerEvents = pElt.getAttribute (CARTE_MASK_PREV_POINTER_EVENTS_ATTR);
        if (tPrevDisplay) pElt.style.display = tPrevDisplay;
        else              pElt.style.removeProperty ("display");
        if (tPrevOpacity) pElt.style.opacity = tPrevOpacity;
        else              pElt.style.removeProperty ("opacity");
        if (tPrevTransition) pElt.style.transition = tPrevTransition;
        else                 pElt.style.removeProperty ("transition");
        if (tPrevPointerEvents) pElt.style.pointerEvents = tPrevPointerEvents;
        else                    pElt.style.removeProperty ("pointer-events");
        pElt.removeAttribute (CARTE_MASK_PREV_DISPLAY_ATTR);
        pElt.removeAttribute (CARTE_MASK_PREV_OPACITY_ATTR);
        pElt.removeAttribute (CARTE_MASK_PREV_VISIBLE_OPACITY_ATTR);
        pElt.removeAttribute (CARTE_MASK_PREV_TRANSITION_ATTR);
        pElt.removeAttribute (CARTE_MASK_PREV_POINTER_EVENTS_ATTR);
        pElt.removeAttribute (CARTE_MASK_STATE_ATTR);
        pElt.removeAttribute (CARTE_MASK_ATTR);
      });
  }

function applique_ids_masques_carte_racine (pRoot, pIds, pOptions = {})
  {
    if (!pRoot) return;
    const tScene = Scenes.findOne (Session.get ("scene_id"));
    const tIdsMasquage = ids_masques_carte_avec_associes_scene (pIds, tScene);
    const tAnimate = !!(pOptions && pOptions.animate);
    const tDecalageDepartMs = Math.max (0, Number (pOptions && pOptions.decalage_depart_ms) || 0);
    const tDecalageEntreeMs = Math.max (
      0,
      Number (pOptions.decalage_in_ms ?? pOptions.decalage_entree_ms ?? tDecalageDepartMs) || 0
    );
    const tDecalageSortieMs = Math.max (
      0,
      Number (pOptions.decalage_out_ms ?? pOptions.decalage_sortie_ms ?? tDecalageDepartMs) || 0
    );
    const tDureeSortie = tAnimate
      ? Math.max (
        0,
        Number (
          pOptions.duree_out_ms ?? pOptions.duree_sortie_ms ?? pOptions.duree_ms
        ) || 0
      )
      : 0;
    const tDureeEntree = tAnimate
      ? Math.max (
        0,
        Number (
          pOptions.duree_in_ms ?? pOptions.duree_entree_ms ?? pOptions.duree_ms
        ) || 0
      )
      : 0;

    const memorise_styles_masquage = (pElt) =>
      {
        if (!pElt || !pElt.style || typeof pElt.getAttribute !== "function") return;
        if (!pElt.hasAttribute (CARTE_MASK_PREV_DISPLAY_ATTR))
          pElt.setAttribute (CARTE_MASK_PREV_DISPLAY_ATTR, pElt.style.display || "");
        if (!pElt.hasAttribute (CARTE_MASK_PREV_OPACITY_ATTR))
          pElt.setAttribute (CARTE_MASK_PREV_OPACITY_ATTR, pElt.style.opacity || "");
        if (!pElt.hasAttribute (CARTE_MASK_PREV_VISIBLE_OPACITY_ATTR))
          {
            let tVisibleOpacity = "1";
            try
              {
                tVisibleOpacity = window.getComputedStyle (pElt).opacity || "1";
              }
            catch (e) {}
            pElt.setAttribute (CARTE_MASK_PREV_VISIBLE_OPACITY_ATTR, tVisibleOpacity);
          }
        if (!pElt.hasAttribute (CARTE_MASK_PREV_TRANSITION_ATTR))
          pElt.setAttribute (CARTE_MASK_PREV_TRANSITION_ATTR, pElt.style.transition || "");
        if (!pElt.hasAttribute (CARTE_MASK_PREV_POINTER_EVENTS_ATTR))
          pElt.setAttribute (CARTE_MASK_PREV_POINTER_EVENTS_ATTR, pElt.style.pointerEvents || "");
      };

    const annule_timer_masquage = (pElt) =>
      {
        if (!pElt || !pElt._cogiCarteMaskTimer) return;
        clearTimeout (pElt._cogiCarteMaskTimer);
        delete pElt._cogiCarteMaskTimer;
      };

    const force_rendu_element = (pElt) =>
      {
        if (!pElt) return;
        try
          {
            if (typeof pElt.getBoundingClientRect === "function")
              pElt.getBoundingClientRect ();
          }
        catch (e) {}
        try
          {
            if (typeof pElt.getBBox === "function") pElt.getBBox ();
          }
        catch (e) {}
      };

    const restaure_style_inline = (pElt, pProp, pValeur) =>
      {
        if (!pElt || !pElt.style) return;
        if (pValeur) pElt.style[pProp] = pValeur;
        else if (pProp === "pointerEvents") pElt.style.removeProperty ("pointer-events");
        else                                  pElt.style.removeProperty (pProp.replace (/[A-Z]/g, (pChar) => `-${pChar.toLowerCase ()}`));
      };

    const demasque_element_avec_fondu = (pElt) =>
      {
        if (!pElt || !pElt.style || !pElt.hasAttribute (CARTE_MASK_ATTR)) return;
        const tState = pElt.getAttribute (CARTE_MASK_STATE_ATTR);
        if (tState === "showing") return;
        if (tDureeEntree <= 0)
          {
            retire_masquages_carte_racine (
              {
                querySelectorAll : () => [pElt],
              }
            );
            return;
          }
        annule_timer_masquage (pElt);
        const tPrevDisplay = pElt.getAttribute (CARTE_MASK_PREV_DISPLAY_ATTR);
        const tVisibleOpacity = pElt.getAttribute (CARTE_MASK_PREV_VISIBLE_OPACITY_ATTR) || "1";
        const tPrevPointerEvents = pElt.getAttribute (CARTE_MASK_PREV_POINTER_EVENTS_ATTR);
        if (tPrevDisplay) pElt.style.display = tPrevDisplay;
        else              pElt.style.removeProperty ("display");
        pElt.style.transition = "none";
        pElt.style.opacity = "0";
        pElt.setAttribute (CARTE_MASK_STATE_ATTR, "showing");
        force_rendu_element (pElt);
        requestAnimationFrame (() =>
          {
            if (pElt.getAttribute (CARTE_MASK_STATE_ATTR) !== "showing") return;
            pElt.style.transition = `opacity ${tDureeEntree}ms ease ${tDecalageEntreeMs}ms`;
            force_rendu_element (pElt);
            requestAnimationFrame (() =>
              {
                if (pElt.getAttribute (CARTE_MASK_STATE_ATTR) !== "showing") return;
                restaure_style_inline (pElt, "pointerEvents", tPrevPointerEvents);
                pElt.style.opacity = tVisibleOpacity;
              });
          });
        pElt._cogiCarteMaskTimer = setTimeout (() =>
          {
            if (pElt.getAttribute (CARTE_MASK_STATE_ATTR) !== "showing") return;
            annule_timer_masquage (pElt);
            retire_masquages_carte_racine (
              {
                querySelectorAll : () => [pElt],
              }
            );
            }, tDecalageEntreeMs + tDureeEntree + 60);
      };

    const masque_element_avec_fondu = (pElt) =>
      {
        if (!pElt || !pElt.style) return;
        const tState = pElt.getAttribute (CARTE_MASK_STATE_ATTR);
        if (tState === "hidden" || tState === "hiding") return;
        memorise_styles_masquage (pElt);
        annule_timer_masquage (pElt);
        const tPrevDisplay = pElt.getAttribute (CARTE_MASK_PREV_DISPLAY_ATTR);
        const tVisibleOpacity = pElt.getAttribute (CARTE_MASK_PREV_VISIBLE_OPACITY_ATTR) || "1";
        if (tDureeSortie <= 0)
          {
            if (tPrevDisplay) pElt.style.display = tPrevDisplay;
            else              pElt.style.removeProperty ("display");
            pElt.style.opacity = tVisibleOpacity;
            pElt.style.pointerEvents = "none";
            pElt.setAttribute (CARTE_MASK_ATTR, "1");
            pElt.style.display = "none";
            pElt.setAttribute (CARTE_MASK_STATE_ATTR, "hidden");
            return;
          }
        if (tPrevDisplay) pElt.style.display = tPrevDisplay;
        else              pElt.style.removeProperty ("display");
        pElt.style.transition = `opacity ${tDureeSortie}ms ease ${tDecalageSortieMs}ms`;
        pElt.style.opacity = tVisibleOpacity;
        pElt.style.pointerEvents = "none";
        pElt.setAttribute (CARTE_MASK_ATTR, "1");
        pElt.setAttribute (CARTE_MASK_STATE_ATTR, "hiding");
        requestAnimationFrame (() =>
          {
            if (pElt.getAttribute (CARTE_MASK_STATE_ATTR) !== "hiding") return;
            pElt.style.opacity = "0";
          });
        pElt._cogiCarteMaskTimer = setTimeout (() =>
          {
            if (pElt.getAttribute (CARTE_MASK_STATE_ATTR) !== "hiding") return;
            annule_timer_masquage (pElt);
            pElt.style.display = "none";
            pElt.setAttribute (CARTE_MASK_STATE_ATTR, "hidden");
            }, tDecalageSortieMs + tDureeSortie + 60);
      };

    if (!tAnimate || (tDureeSortie <= 0 && tDureeEntree <= 0))
      {
        retire_masquages_carte_racine (pRoot);
        const tDoc = pRoot.ownerDocument || pRoot;
        for (const tId of tIdsMasquage)
          {
            applique_aux_noeuds_calque_svg_et_decors (tDoc, tId, (tElt) =>
              {
                if (!tElt || (typeof pRoot.contains === "function" && !pRoot.contains (tElt))) return;
                tElt.setAttribute (CARTE_MASK_PREV_DISPLAY_ATTR, tElt.style.display || "");
                tElt.style.display = "none";
                tElt.setAttribute (CARTE_MASK_ATTR, "1");
              });
          }
        return;
      }

    const tIdsCibles = new Set (tIdsMasquage);
    const tMasquesActifs = pRoot.querySelectorAll (`[${CARTE_MASK_ATTR}="1"]`);
    tMasquesActifs.forEach ((pElt) =>
      {
        const tId = (pElt.getAttribute && pElt.getAttribute ("id"))
          || id_source_decor_calque_svg (pElt);
        if (!tId || !tIdsCibles.has (tId))
          demasque_element_avec_fondu (pElt);
      });

    const tDoc = pRoot.ownerDocument || pRoot;
    for (const tId of tIdsCibles)
      {
        applique_aux_noeuds_calque_svg_et_decors (tDoc, tId, (tElt) =>
          {
            if (!tElt || (typeof pRoot.contains === "function" && !pRoot.contains (tElt))) return;
            masque_element_avec_fondu (tElt);
          });
      }
  }

function fige_masquages_carte_racine (pRoot)
  {
    if (!pRoot || typeof pRoot.querySelectorAll !== "function") return;
    const tMasques = pRoot.querySelectorAll (`[${CARTE_MASK_ATTR}="1"]`);
    tMasques.forEach ((pElt) =>
      {
        if (pElt._cogiCarteMaskTimer)
          {
            clearTimeout (pElt._cogiCarteMaskTimer);
            delete pElt._cogiCarteMaskTimer;
          }
        pElt.removeAttribute (CARTE_MASK_PREV_DISPLAY_ATTR);
        pElt.removeAttribute (CARTE_MASK_PREV_OPACITY_ATTR);
        pElt.removeAttribute (CARTE_MASK_PREV_VISIBLE_OPACITY_ATTR);
        pElt.removeAttribute (CARTE_MASK_PREV_TRANSITION_ATTR);
        pElt.removeAttribute (CARTE_MASK_PREV_POINTER_EVENTS_ATTR);
        pElt.removeAttribute (CARTE_MASK_STATE_ATTR);
        pElt.removeAttribute (CARTE_MASK_ATTR);
      });
  }

function applique_masquages_carte_svg (pDoc, pIds, pOptions = {})
  {
    if (!pDoc || !pDoc.documentElement) return;
    applique_ids_masques_carte_racine (pDoc.documentElement, pIds, pOptions);
  }

function applique_masquages_carte_tpl (pTpl, pOptions = {})
  {
    if (!pTpl || !pTpl._svgDoc) return;
    if (pTpl._lectureZoomAltReaffichageMasquesEnCours) return;
    const tReglages = reglages_mode_lecture_scene ();
    const tFadeLegacy = Math.max (0, Number (tReglages.fade_masque_ms) || 0);
    const tDureeIn = Number.isFinite (Number (pOptions.duree_in_ms))
      ? Math.max (0, Number (pOptions.duree_in_ms))
      : Math.max (
        0,
        Number.isFinite (Number (tReglages.fade_masque_in_ms))
          ? Number (tReglages.fade_masque_in_ms)
          : tFadeLegacy
      );
    const tDureeOut = Number.isFinite (Number (pOptions.duree_out_ms))
      ? Math.max (0, Number (pOptions.duree_out_ms))
      : Math.max (
        0,
        Number.isFinite (Number (tReglages.fade_masque_out_ms))
          ? Number (tReglages.fade_masque_out_ms)
          : tFadeLegacy
      );
    applique_masquages_carte_svg (
      pTpl._svgDoc,
      cartes_ids_masques_effectifs (pTpl),
      {
        animate : mode_lecture_scene_actif (),
        duree_in_ms : tDureeIn,
        duree_out_ms : tDureeOut,
        decalage_in_ms : Number.isFinite (Number (pOptions.decalage_in_ms))
          ? Math.max (0, Number (pOptions.decalage_in_ms))
          : delai_retour_decor_focus_carte_ms_lecture (pTpl),
        decalage_out_ms : Number.isFinite (Number (pOptions.decalage_out_ms))
          ? Math.max (0, Number (pOptions.decalage_out_ms))
          : 0,
      }
    );
  }

function duree_masquage_sortie_carte_ms_lecture ()
  {
    const tReglages = reglages_mode_lecture_scene ();
    const tFadeLegacy = Math.max (0, Number (tReglages.fade_masque_ms) || 0);
    return Math.max (
      0,
      Number.isFinite (Number (tReglages.fade_masque_out_ms))
        ? Number (tReglages.fade_masque_out_ms)
        : tFadeLegacy
    );
  }

function attend_ms_scene (pDelaiMs)
  {
    const tDelai = Math.max (0, Number (pDelaiMs) || 0);
    if (tDelai <= 0) return Promise.resolve ();
    return new Promise ((pResolve) => setTimeout (pResolve, tDelai));
  }

function reglages_interface_carte_lecture (pCarte = null)
  {
    const tCarte = pCarte || carte_source_pour_bulle_scene (gLectureSceneFocusCarte.get ());
    return tCarte
      ? reglages_interface_carte_effectifs (tCarte)
      : reglages_mode_lecture_scene ();
  }

function delai_lancement_zoom_alternatif_ms_lecture (pCarte = null)
  {
    const tReglages = reglages_interface_carte_lecture (pCarte);
    return Math.max (0, Number (tReglages.zoom_alternatif_delai_lancement_s) || 0) * 1000;
  }

function duree_fondu_entree_zoom_alternatif_ms_lecture (pCarte = null)
  {
    const tReglages = reglages_interface_carte_lecture (pCarte);
    return Math.max (0, Number (tReglages.zoom_alternatif_masque_fade_in_ms) || 0);
  }

function duree_fondu_sortie_zoom_alternatif_ms_lecture (pCarte = null)
  {
    const tReglages = reglages_interface_carte_lecture (pCarte);
    return Math.max (0, Number (tReglages.zoom_alternatif_masque_fade_out_ms) || 0);
  }

function duree_fondu_entree_interface_zoom_alternatif_ms_lecture (pCarte = null)
  {
    const tReglages = reglages_interface_carte_lecture (pCarte);
    return Math.max (0, Number (tReglages.zoom_alternatif_interface_fade_in_ms) || 0);
  }

function duree_fondu_sortie_interface_zoom_alternatif_ms_lecture (pCarte = null)
  {
    const tReglages = reglages_interface_carte_lecture (pCarte);
    return Math.max (0, Number (tReglages.zoom_alternatif_interface_fade_out_ms) || 0);
  }

function ids_masques_carte_focus_alternatif (pTpl, pCarte)
  {
    return pCarte
      ? ids_masques_carte_intersectant_vue (pTpl, pCarte.ids_masques, pCarte.vue)
      : [];
  }

function premier_noeud_dessinable_svg (pRoot)
  {
    if (!pRoot) return null;
    let tEnfant = pRoot.firstElementChild;
    while (tEnfant)
      {
        const tNom = (tEnfant.tagName || "").toLowerCase ();
        if (!["defs", "metadata", "desc", "title", "style", "script"].includes (tNom))
          return tEnfant;
        tEnfant = tEnfant.nextElementSibling;
      }
    return null;
  }

function retire_decorations_cartes_racine (pRoot, pOptions = {})
  {
    if (!pRoot || typeof pRoot.querySelectorAll !== "function") return;
    const tMasquePreserve = pOptions && pOptions.preserveMask;
    const tZoomAltPreserve = pOptions && pOptions.preserveZoomAlt;
    const tBullesPreserve = pOptions && pOptions.preserveBulles;
    const tNoeuds = pRoot.querySelectorAll (
      `.${CARTE_DECOR_FOND_CLASS}, .${CARTE_DECOR_MASQUE_CLASS}, .${CARTE_DECOR_BULLES_LAYER_CLASS}:not(.${LECTURE_BULLES_FADE_CLASS}), .${CARTE_ZOOM_ALTERNATIF_GROUP_CLASS}`
    );
    tNoeuds.forEach ((pNoeud) =>
      {
        if (tMasquePreserve && pNoeud === tMasquePreserve) return;
        if (tZoomAltPreserve && pNoeud.classList && pNoeud.classList.contains (CARTE_ZOOM_ALTERNATIF_GROUP_CLASS)) return;
        if (tBullesPreserve && pNoeud.classList && pNoeud.classList.contains (CARTE_DECOR_BULLES_LAYER_CLASS)) return;
        if (pNoeud.parentNode) pNoeud.parentNode.removeChild (pNoeud);
      });
  }

function place_zoom_alternatif_au_premier_plan_racine (pRoot)
  {
    if (!pRoot || typeof pRoot.querySelector !== "function") return;
    const tGroupe = pRoot.querySelector (`.${CARTE_ZOOM_ALTERNATIF_GROUP_CLASS}`);
    if (tGroupe && tGroupe.parentNode === pRoot) pRoot.appendChild (tGroupe);
  }

function signature_masque_selection_carte_client (pVueScene, pVueSelection, pOpt = {})
  {
    const tVueScene = normalise_vue_carte_client (pVueScene);
    const tVueSelection = normalise_vue_carte_client (pVueSelection);
    if (!tVueScene || !tVueSelection) return "";
    const tNombre = (pValeur) => Number (pValeur || 0).toFixed (3);
    return [
      tNombre (tVueScene.x),
      tNombre (tVueScene.y),
      tNombre (tVueScene.w),
      tNombre (tVueScene.h),
      tNombre (tVueSelection.x),
      tNombre (tVueSelection.y),
      tNombre (tVueSelection.w),
      tNombre (tVueSelection.h),
      pOpt.fill || "#ffffff",
      tNombre (pOpt.opacity ?? 0.34),
    ].join ("|");
  }

function encode_vue_carte_attr_client (pVue)
  {
    const tVue = normalise_vue_carte_client (pVue);
    if (!tVue) return "";
    return [tVue.x, tVue.y, tVue.w, tVue.h]
      .map ((pValeur) => Number (pValeur || 0).toFixed (6))
      .join (" ");
  }

function decode_vue_carte_attr_client (pValeur)
  {
    if (typeof pValeur !== "string" || !pValeur.trim ()) return null;
    const tParties = pValeur.trim ().split (/\s+/).map (Number);
    if (tParties.length < 4 || !tParties.every (Number.isFinite)) return null;
    return normalise_vue_carte_client (
      {
        x : tParties[0],
        y : tParties[1],
        w : tParties[2],
        h : tParties[3],
      }
    );
  }

function actualise_path_masque_selection_carte (pPath, pVueScene, pVueSelection, pOpt = {})
  {
    const tVueScene = normalise_vue_carte_client (pVueScene);
    const tVueSelection = normalise_vue_carte_client (pVueSelection);
    if (!pPath || !tVueScene || !tVueSelection) return false;
    const tMarge = Math.max (
      tVueScene.w,
      tVueScene.h,
      tVueSelection.w,
      tVueSelection.h,
      1
    ) * CARTE_DECOR_MASQUE_OVERSCAN_FACTOR;
    const tExterieur = {
      x : Math.min (tVueScene.x, tVueSelection.x) - tMarge,
      y : Math.min (tVueScene.y, tVueSelection.y) - tMarge,
      w : Math.max (tVueScene.x + tVueScene.w, tVueSelection.x + tVueSelection.w)
        - Math.min (tVueScene.x, tVueSelection.x)
        + (tMarge * 2),
      h : Math.max (tVueScene.y + tVueScene.h, tVueSelection.y + tVueSelection.h)
        - Math.min (tVueScene.y, tVueSelection.y)
        + (tMarge * 2),
    };
    pPath.setAttribute (CARTE_DECOR_MASQUE_SELECTION_VIEW_ATTR, encode_vue_carte_attr_client (tVueSelection));
    pPath.setAttribute (
      "data-cogi-mask-signature",
      signature_masque_selection_carte_client (tVueScene, tVueSelection, pOpt)
    );
    pPath.setAttribute (
      "d",
      [
        `M ${tExterieur.x} ${tExterieur.y}`,
        `h ${tExterieur.w}`,
        `v ${tExterieur.h}`,
        `h ${-tExterieur.w}`,
        "Z",
        `M ${tVueSelection.x} ${tVueSelection.y}`,
        `h ${tVueSelection.w}`,
        `v ${tVueSelection.h}`,
        `h ${-tVueSelection.w}`,
        "Z",
      ].join (" ")
    );
    return true;
  }

function actualise_geometrie_masque_fond_cartes_racine (pRoot, pVueScene)
  {
    const tVueScene = normalise_vue_carte_client (pVueScene);
    if (!pRoot || !tVueScene || typeof pRoot.querySelectorAll !== "function") return;

    pRoot.querySelectorAll (`.${CARTE_DECOR_FOND_CLASS}`).forEach ((pFond) =>
      {
        pFond.setAttribute ("x", tVueScene.x);
        pFond.setAttribute ("y", tVueScene.y);
        pFond.setAttribute ("width", tVueScene.w);
        pFond.setAttribute ("height", tVueScene.h);
      });

    pRoot.querySelectorAll (`.${CARTE_DECOR_MASQUE_CLASS}`).forEach ((pMasque) =>
      {
        const tVueSelection = decode_vue_carte_attr_client (
          pMasque.getAttribute (CARTE_DECOR_MASQUE_SELECTION_VIEW_ATTR)
        );
        if (!tVueSelection) return;
        const tOpacity = Number (pMasque.getAttribute ("fill-opacity"));
        actualise_path_masque_selection_carte (
          pMasque,
          tVueScene,
          tVueSelection,
          {
            fill : pMasque.getAttribute ("fill") || "#ffffff",
            opacity : Number.isFinite (tOpacity) ? tOpacity : 0.34,
          }
        );
      });
  }

function duree_fade_masque_in_ms_lecture ()
  {
    const tReglages = reglages_mode_lecture_scene ();
    const tLegacy = Math.max (0, Number (tReglages.fade_masque_ms) || 0);
    const tIn = Number (tReglages.fade_masque_in_ms);
    return Math.max (0, Number.isFinite (tIn) ? tIn : tLegacy);
  }

function duree_fade_masque_out_ms_lecture ()
  {
    const tReglages = reglages_mode_lecture_scene ();
    const tLegacy = Math.max (0, Number (tReglages.fade_masque_ms) || 0);
    const tOut = Number (tReglages.fade_masque_out_ms);
    return Math.max (0, Number.isFinite (tOut) ? tOut : tLegacy);
  }

function delai_zoom_carte_entree_ms_lecture ()
  {
    const tDelai = Number (reglages_mode_lecture_scene ().zoom_carte_zoom_delai_ms);
    return Math.max (0, Number.isFinite (tDelai) ? tDelai : 0);
  }

function delai_zoom_carte_sortie_ms_lecture ()
  {
    const tDelai = Number (reglages_mode_lecture_scene ().zoom_carte_dezoom_delai_ms);
    return Math.max (0, Number.isFinite (tDelai) ? tDelai : 0);
  }

function delai_retour_decor_focus_carte_ms_lecture (pTpl)
  {
    const tDelai = Number (pTpl && pTpl._lectureDecorTransitionDelaiMs);
    return Math.max (0, Number.isFinite (tDelai) ? tDelai : 0);
  }

function clone_masque_carte_pour_fondu_sortie (pRoot, pOptions = {})
  {
    if (!pRoot || typeof pRoot.querySelector !== "function") return null;
    const tMasque = pRoot.querySelector (`.${CARTE_DECOR_MASQUE_CLASS}`);
    if (!tMasque) return null;
    const tDuree = duree_fade_masque_in_ms_lecture ();
    if (tDuree <= 0) return null;
    const tDecalage = Math.max (0, Number (pOptions && pOptions.decalage_ms) || 0);
    const tClone = tMasque.cloneNode (true);
    const tVueScene = normalise_vue_carte_client (pOptions && pOptions.sceneViewBox);
    const tVueSelection = decode_vue_carte_attr_client (
      tMasque.getAttribute (CARTE_DECOR_MASQUE_SELECTION_VIEW_ATTR)
    );
    const tOpacity = Number (tMasque.getAttribute ("fill-opacity"));
    if (tVueScene && tVueSelection)
      actualise_path_masque_selection_carte (
        tClone,
        tVueScene,
        tVueSelection,
        {
          fill : tMasque.getAttribute ("fill") || "#ffffff",
          opacity : Number.isFinite (tOpacity) ? tOpacity : 0.34,
        }
      );
    tClone.setAttribute ("class", LECTURE_MASK_FADE_CLASS);
    tClone.style.pointerEvents = "none";
    tClone.style.opacity = tMasque.style.opacity || "1";
    tClone.style.transition = `opacity ${tDuree}ms ease ${tDecalage}ms`;
    return tClone;
  }

function clone_layer_bulles_pour_fondu_sortie (pRoot)
  {
    if (!pRoot || typeof pRoot.querySelector !== "function") return null;
    const tLayer = pRoot.querySelector (`.${CARTE_DECOR_BULLES_LAYER_CLASS}:not(.${LECTURE_BULLES_FADE_CLASS})`);
    if (!tLayer) return null;
    const tDuree = duree_fade_masque_out_ms_lecture ();
    if (tDuree <= 0) return null;
    const tClone = tLayer.cloneNode (true);
    tClone.setAttribute ("class", `${CARTE_DECOR_BULLES_LAYER_CLASS} ${LECTURE_BULLES_FADE_CLASS}`);
    tClone.style.pointerEvents = "none";
    tClone.style.opacity = tLayer.style.opacity || tLayer.getAttribute ("opacity") || "1";
    tClone.style.transition = `opacity ${tDuree}ms ease`;
    return tClone;
  }

function lance_fondu_sortie_masque_carte (pRoot, pMasque, pOptions = {})
  {
    if (!pRoot || !pMasque) return;
    const tDuree = duree_fade_masque_in_ms_lecture ();
    if (tDuree <= 0) return;
    const tDecalage = Math.max (0, Number (pOptions && pOptions.decalage_ms) || 0);
    pRoot.appendChild (pMasque);
    requestAnimationFrame (() =>
      {
        pMasque.style.opacity = "0";
      });
    setTimeout (() =>
      {
        if (pMasque.parentNode) pMasque.parentNode.removeChild (pMasque);
      }, tDecalage + tDuree + 80);
  }

function lance_fondu_sortie_layer_bulles (pRoot, pLayer)
  {
    if (!pRoot || !pLayer) return;
    const tDuree = duree_fade_masque_out_ms_lecture ();
    if (tDuree <= 0) return;
    pLayer.style.opacity = "1";
    pRoot.appendChild (pLayer);
    requestAnimationFrame (() =>
      {
        pLayer.style.opacity = "0";
      });
    setTimeout (() =>
      {
        if (pLayer.parentNode) pLayer.parentNode.removeChild (pLayer);
      }, tDuree + 80);
  }

function anime_entree_layer_bulles (pLayer)
  {
    if (!pLayer) return;
    const tDuree = duree_fade_masque_in_ms_lecture ();
    if (tDuree <= 0) return;
    pLayer.style.opacity = "0";
    pLayer.style.transition = `opacity ${tDuree}ms ease`;
    requestAnimationFrame (() =>
      {
        if (!pLayer.parentNode) return;
        if (typeof pLayer.getBoundingClientRect === "function")
          pLayer.getBoundingClientRect ();
        requestAnimationFrame (() =>
          {
            if (!pLayer.parentNode) return;
            pLayer.style.opacity = "1";
          });
      });
  }

function cree_element_svg_decor (pRoot, pTag)
  {
    const tDoc = (pRoot && pRoot.ownerDocument) || document;
    return tDoc.createElementNS ("http://www.w3.org/2000/svg", pTag);
  }

function construit_descripteur_bulle_carte_client (pCarteLike, pOpt = {})
  {
    const tVue = normalise_vue_carte_client (pCarteLike && pCarteLike.vue);
    const tConfigBulle = configuration_bulle_carte_effective_client (pCarteLike, pOpt);
    const tPosition = position_bulle_carte_effective_client (
      pCarteLike,
      tVue,
      {...pOpt, configuration : tConfigBulle}
    );
    if (!tVue || !tPosition) return null;
    const tBase = {
      carte_id : (pCarteLike && (pCarteLike._id || pCarteLike.carte_id)) || null,
      mode : tConfigBulle.mode,
      couleur : tConfigBulle.couleur_bulle,
      svg_bulle_id : tConfigBulle.svg_bulle_id || null,
      opacite : opacite_bulle_carte_effective_client (pCarteLike, {...pOpt, configuration : tConfigBulle}),
      taille : taille_bulle_carte_effective_client (pCarteLike, {...pOpt, configuration : tConfigBulle}),
      rotation : rotation_bulle_carte_effective_client (pCarteLike, {...pOpt, configuration : tConfigBulle}),
      rayon : rayon_bulle_carte_effectif_client (pCarteLike, tVue, {...pOpt, configuration : tConfigBulle}),
      x : tPosition.x,
      y : tPosition.y,
      attenuee : !!pOpt.attenuee,
      opacite_groupe : Number.isFinite (pOpt.opacite_groupe)
        ? pOpt.opacite_groupe
        : 1,
      bulle_ombre_asset_file_id : (typeof (pCarteLike && pCarteLike.bulle_ombre_asset_file_id) === "string" && pCarteLike.bulle_ombre_asset_file_id) || null,
      bulle_ombre_asset_file_name : (typeof (pCarteLike && pCarteLike.bulle_ombre_asset_file_name) === "string" && pCarteLike.bulle_ombre_asset_file_name) || null,
      bulle_ombre_asset_signature : (typeof (pCarteLike && pCarteLike.bulle_ombre_asset_signature) === "string" && pCarteLike.bulle_ombre_asset_signature) || null,
      bulle_ombre_asset_url : (typeof (pCarteLike && pCarteLike.bulle_ombre_asset_url) === "string" && pCarteLike.bulle_ombre_asset_url) || null,
    };

    if (tConfigBulle.mode !== "custom" || !tConfigBulle.svg_bulle_id)
      return tBase;

    const tDimensions = dimensions_modele_svg_bulle_projet_client (
      tConfigBulle.svg_bulle_id,
      tBase.taille
    );
    return {
      ...tBase,
      svg_couleur : tConfigBulle.couleur_bulle,
      svg_href : uri_modele_svg_bulle_projet_client (
        tConfigBulle.svg_bulle_id,
        tConfigBulle.couleur_bulle
      ),
      svg_largeur : tDimensions.largeur,
      svg_hauteur : tDimensions.hauteur,
    };
  }

function ajoute_fond_carte_racine (pRoot, pVueScene, pCouleur, pTransform = "")
  {
    const tVue = normalise_vue_carte_client (pVueScene, vue_svg_racine_client (pRoot));
    if (!pRoot || !tVue || !pCouleur) return;

    const tRect = cree_element_svg_decor (pRoot, "rect");
    tRect.setAttribute ("class", CARTE_DECOR_FOND_CLASS);
    tRect.setAttribute ("x", tVue.x);
    tRect.setAttribute ("y", tVue.y);
    tRect.setAttribute ("width", tVue.w);
    tRect.setAttribute ("height", tVue.h);
    tRect.setAttribute ("fill", pCouleur);
    tRect.style.pointerEvents = "none";
    applique_transform_calque_cartes_noeud (tRect, pTransform);

    const tAvant = premier_noeud_dessinable_svg (pRoot);
    if (tAvant) pRoot.insertBefore (tRect, tAvant);
    else        pRoot.appendChild (tRect);
  }

function ajoute_masque_selection_carte_racine (pRoot, pVueScene, pVueSelection, pOpt = {})
  {
    const tVueScene = normalise_vue_carte_client (pVueScene, vue_svg_racine_client (pRoot));
    const tVueSelection = normalise_vue_carte_client (pVueSelection);
    if (!pRoot || !tVueScene || !tVueSelection) return;

    const tPath = cree_element_svg_decor (pRoot, "path");
    tPath.setAttribute ("class", CARTE_DECOR_MASQUE_CLASS);
    applique_transform_calque_cartes_noeud (tPath, pOpt.cartesLayerTransform || "");
    tPath.setAttribute ("fill-rule", "evenodd");
    tPath.setAttribute ("clip-rule", "evenodd");
    tPath.setAttribute ("fill", pOpt.fill || "#ffffff");
    tPath.setAttribute ("fill-opacity", String (pOpt.opacity ?? 0.34));
    actualise_path_masque_selection_carte (tPath, tVueScene, tVueSelection, pOpt);
    tPath.style.pointerEvents = "none";
    if (pOpt.animateIn)
      {
        const tDuree = duree_fade_masque_in_ms_lecture ();
        if (tDuree > 0)
          {
            const tDecalage = Math.max (0, Number (pOpt.animateInDelayMs) || 0);
            tPath.style.opacity = "0";
            tPath.style.transition = `opacity ${tDuree}ms ease ${tDecalage}ms`;
          }
      }
    pRoot.appendChild (tPath);
    if (pOpt.animateIn)
      requestAnimationFrame (() =>
        {
          if (!tPath.parentNode) return;
          if (typeof tPath.getBoundingClientRect === "function")
            tPath.getBoundingClientRect ();
          requestAnimationFrame (() =>
            {
              if (!tPath.parentNode) return;
              tPath.style.opacity = "1";
            });
        });
  }

function actualise_path_zoom_alternatif (pPath, pVueScene, pVueSelection = null)
  {
    const tVueScene = normalise_vue_carte_client (pVueScene);
    const tVueSelection = normalise_vue_carte_client (pVueSelection);
    if (!pPath || !tVueScene) return false;
    const tExterieur = {
      x : tVueScene.x - (tVueScene.w * 2),
      y : tVueScene.y - (tVueScene.h * 2),
      w : tVueScene.w * 5,
      h : tVueScene.h * 5,
    };
    const tCommandes = [
      `M ${tExterieur.x} ${tExterieur.y}`,
      `h ${tExterieur.w}`,
      `v ${tExterieur.h}`,
      `h ${-tExterieur.w}`,
      "Z",
    ];
    if (tVueSelection)
      tCommandes.push (
        `M ${tVueSelection.x} ${tVueSelection.y}`,
        `h ${tVueSelection.w}`,
        `v ${tVueSelection.h}`,
        `h ${-tVueSelection.w}`,
        "Z"
      );
    pPath.setAttribute (
      "d",
      tCommandes.join (" ")
    );
    return true;
  }

function actualise_masque_zoom_alternatif_groupe (pGroupe, pVueScene, pVueSelection = null)
  {
    const tPath = pGroupe && pGroupe.querySelector
      ? pGroupe.querySelector (`.${CARTE_ZOOM_ALTERNATIF_MASK_CLASS}`)
      : null;
    if (!tPath) return false;
    const tTransition = tPath.style ? tPath.style.transition : "";
    if (tPath.style) tPath.style.transition = "none";
    const tOk = actualise_path_zoom_alternatif (tPath, pVueScene, pVueSelection);
    if (typeof tPath.getBoundingClientRect === "function")
      {
        try { tPath.getBoundingClientRect (); } catch (e) {}
      }
    if (tPath.style) tPath.style.transition = tTransition;
    return tOk;
  }

function cree_placeholder_zoom_alternatif_svg (pRoot, pRole, pTaille)
  {
    const tG = cree_element_svg_decor (pRoot, "g");
    const tTaille = Math.max (1, Number (pTaille) || 48);
    const tRect = cree_element_svg_decor (pRoot, "rect");
    tRect.setAttribute ("x", "0");
    tRect.setAttribute ("y", "0");
    tRect.setAttribute ("width", String (tTaille));
    tRect.setAttribute ("height", String (tTaille));
    tRect.setAttribute ("rx", String (Math.max (2, tTaille * 0.08)));
    tRect.setAttribute ("fill", "#ffffff");
    tRect.setAttribute ("fill-opacity", "0.82");
    tRect.setAttribute ("stroke", "#000000");
    tRect.setAttribute ("stroke-opacity", "0.22");
    tRect.setAttribute ("stroke-width", String (Math.max (1, tTaille * 0.035)));
    const tText = cree_element_svg_decor (pRoot, "text");
    tText.setAttribute ("x", String (tTaille / 2));
    tText.setAttribute ("y", String (tTaille * 0.66));
    tText.setAttribute ("text-anchor", "middle");
    tText.setAttribute ("font-size", String (tTaille * 0.58));
    tText.setAttribute ("font-family", "system-ui, -apple-system, sans-serif");
    tText.setAttribute ("font-weight", "700");
    tText.setAttribute ("fill", "#000000");
    tText.setAttribute ("fill-opacity", "0.58");
    tText.textContent = "?";
    const tTitle = cree_element_svg_decor (pRoot, "title");
    tTitle.textContent = pRole || "SVG manquant";
    tG.appendChild (tTitle);
    tG.appendChild (tRect);
    tG.appendChild (tText);
    return {node : tG, viewBox : {x : 0, y : 0, w : tTaille, h : tTaille}};
  }

function navigateur_webkit_sans_chrome_client ()
  {
    if (typeof navigator === "undefined") return false;
    const tUa = navigator.userAgent || "";
    return /AppleWebKit/i.test (tUa)
      && !/Chrome|Chromium|CriOS|Edg|OPR|Firefox|FxiOS/i.test (tUa);
  }

function ombres_svg_rasterisees_scene_client ()
  {
    return true;
  }

function limite_cache_ombres_raster_svg_scene_client ()
  {
    while (gOmbresRasterSvgSceneCache.size > OMBRES_RASTER_SVG_SCENE_CACHE_MAX)
      {
        const tPremier = gOmbresRasterSvgSceneCache.keys ().next ().value;
        if (!tPremier) break;
        const tEntree = gOmbresRasterSvgSceneCache.get (tPremier);
        if (tEntree && tEntree.status === "pending") break;
        libere_entree_ombre_raster_svg_scene_client (tEntree);
        gOmbresRasterSvgSceneCache.delete (tPremier);
      }
  }

function libere_entree_ombre_raster_svg_scene_client (pEntree)
  {
    if (!pEntree) return;
    pEntree.annulee = true;
    pEntree.callbacks = [];
    if (pEntree.url && /^blob:/i.test (pEntree.url) && typeof URL !== "undefined" && URL.revokeObjectURL)
      {
        try { URL.revokeObjectURL (pEntree.url); } catch (e) {}
      }
    pEntree.url = null;
  }

function dimensions_ombre_raster_svg_scene_client (pWidth, pHeight)
  {
    const tW = Math.max (1, Math.ceil (Number (pWidth) || 1));
    const tH = Math.max (1, Math.ceil (Number (pHeight) || 1));
    const tScale = Math.min (
      1,
      OMBRES_RASTER_SVG_SCENE_COTE_MAX / tW,
      OMBRES_RASTER_SVG_SCENE_COTE_MAX / tH,
      Math.sqrt (OMBRES_RASTER_SVG_SCENE_PIXELS_MAX / Math.max (1, tW * tH))
    );
    return {
      width : Math.max (1, Math.ceil (tW * tScale)),
      height : Math.max (1, Math.ceil (tH * tScale)),
    };
  }

function lance_file_ombres_raster_svg_scene_client ()
  {
    while (gOmbresRasterSvgSceneActives < OMBRES_RASTER_SVG_SCENE_CONCURRENCE_MAX && gOmbresRasterSvgSceneFile.length)
      {
        const tTache = gOmbresRasterSvgSceneFile.shift ();
        gOmbresRasterSvgSceneActives++;
        Promise.resolve ()
          .then (tTache)
          .catch (() => {})
          .finally (() =>
            {
              gOmbresRasterSvgSceneActives = Math.max (0, gOmbresRasterSvgSceneActives - 1);
              lance_file_ombres_raster_svg_scene_client ();
            });
      }
  }

function ajoute_file_ombre_raster_svg_scene_client (pTache)
  {
    gOmbresRasterSvgSceneFile.push (pTache);
    lance_file_ombres_raster_svg_scene_client ();
  }

async function prepare_svg_ombre_raster_scene_client (pSvgText)
  {
    if (typeof DOMParser === "undefined") return pSvgText;
    const tDoc = new DOMParser ().parseFromString (String (pSvgText || ""), "image/svg+xml");
    const tRoot = tDoc && tDoc.documentElement;
    if (!tRoot || (tRoot.nodeName || "").toLowerCase () === "parsererror") return pSvgText;
    await embarque_images_svg_pour_serialisation (tRoot);
    return new XMLSerializer ().serializeToString (tRoot);
  }

function genere_asset_ombre_svg_scene_client (pSignature, pSvgText, pWidth, pHeight)
  {
    const tW = Math.max (1, Math.ceil (Number (pWidth) || 1));
    const tH = Math.max (1, Math.ceil (Number (pHeight) || 1));
    return new Promise ((pResolve, pReject) =>
      {
        prepare_svg_ombre_raster_scene_client (pSvgText)
          .then ((pTexteSvg) =>
            {
              Meteor.call (
                "scene.shadowAsset.genere",
                {
                  signature : pSignature,
                  svgText : pTexteSvg,
                  width : tW,
                  height : tH,
                  scene_id : Session.get ("scene_id") || null,
                },
                (pErr, pRes) =>
                  {
                    if (pErr) { pReject (pErr); return; }
                    const tUrl = url_hubfile_client_par_id_nom (
                      pRes && pRes.file_id,
                      pRes && pRes.file_name
                    );
                    const tUrlAsset = pRes && pRes.url
                      ? new URL (pRes.url, window.location.origin).href
                      : tUrl;
                    if (!tUrlAsset) pReject (new Error ("Asset d’ombre indisponible"));
                    else pResolve (tUrlAsset);
                  }
              );
            })
          .catch (pReject);
      });
  }

function image_raster_ombre_svg_scene_client (pSignature)
  {
    const tEntree = pSignature ? gOmbresRasterSvgSceneCache.get (pSignature) : null;
    if (!tEntree || tEntree.status !== "ready") return null;
    gOmbresRasterSvgSceneCache.delete (pSignature);
    gOmbresRasterSvgSceneCache.set (pSignature, tEntree);
    return tEntree.url;
  }

function hachage_texte_simple_scene_client (pTexte)
  {
    const tTexte = String (pTexte || "");
    let tHash = 2166136261;
    for (let i = 0; i < tTexte.length; i++)
      {
        tHash ^= tTexte.charCodeAt (i);
        tHash = Math.imul (tHash, 16777619);
      }
    return (tHash >>> 0).toString (36);
  }

function programme_raster_ombre_svg_scene_client (pSignature, pSvgText, pWidth, pHeight, pOnReady)
  {
    if (!OMBRES_RASTER_SVG_SCENE_GENERATION_ACTIVE) return;
    if (!pSignature || !pSvgText) return;
    const tExistante = gOmbresRasterSvgSceneCache.get (pSignature);
    if (tExistante)
      {
        if (tExistante.status === "ready")
          {
            if (typeof pOnReady === "function") pOnReady (tExistante.url);
            return;
          }
        if (tExistante.status === "pending" && typeof pOnReady === "function")
          tExistante.callbacks.push (pOnReady);
        return;
      }
    const tPending = Array.from (gOmbresRasterSvgSceneCache.values ())
      .filter ((pEntree) => pEntree && pEntree.status === "pending").length;
    if (tPending >= OMBRES_RASTER_SVG_SCENE_PENDING_MAX)
      return;
    const tEntree = {status : "pending", url : null, callbacks : typeof pOnReady === "function" ? [pOnReady] : [], annulee : false};
    gOmbresRasterSvgSceneCache.set (pSignature, tEntree);
    limite_cache_ombres_raster_svg_scene_client ();
    const tLance = () =>
      {
        ajoute_file_ombre_raster_svg_scene_client (() => genere_asset_ombre_svg_scene_client (pSignature, pSvgText, pWidth, pHeight)
          .then ((pUrl) =>
            {
              if (tEntree.annulee)
                {
                  if (pUrl && /^blob:/i.test (pUrl) && typeof URL !== "undefined" && URL.revokeObjectURL)
                    URL.revokeObjectURL (pUrl);
                  return;
                }
              tEntree.status = "ready";
              tEntree.url = pUrl;
              const tCallbacks = tEntree.callbacks.slice ();
              tEntree.callbacks.length = 0;
              tCallbacks.forEach ((pCb) =>
                {
                  try { pCb (pUrl); } catch (e) {}
                });
            })
          .catch ((pErr) =>
            {
              if (tEntree.annulee) return;
              tEntree.status = "error";
              tEntree.callbacks.length = 0;
              if (gOmbresRasterSvgSceneCache.get (pSignature) === tEntree)
                gOmbresRasterSvgSceneCache.delete (pSignature);
              if (typeof pOnReady === "function")
                {
                  try { pOnReady (null); } catch (e) {}
                }
              console.warn ("[scene] ombre SVG raster impossible:", pErr && (pErr.reason || pErr.message) || pErr);
            }));
      };
    if (typeof requestIdleCallback === "function")
      requestIdleCallback (tLance, {timeout : 900});
    else
      setTimeout (tLance, 80);
  }

function cree_image_svg_zoom_alternatif_noeud (pRoot, pUrl, pViewBox = null)
  {
    if (!pRoot || !pUrl) return null;
    const tVue = normalise_vue_carte_client (pViewBox, {x : 0, y : 0, w : 100, h : 100});
    const tImage = cree_element_svg_decor (pRoot, "image");
    tImage.setAttribute ("x", "0");
    tImage.setAttribute ("y", "0");
    tImage.setAttribute ("width", String (tVue.w));
    tImage.setAttribute ("height", String (tVue.h));
    tImage.setAttribute ("preserveAspectRatio", "xMidYMid meet");
    tImage.setAttribute ("href", pUrl);
    tImage.setAttributeNS ("http://www.w3.org/1999/xlink", "href", pUrl);
    return {node : tImage, viewBox : {x : 0, y : 0, w : tVue.w, h : tVue.h}};
  }

function isole_svg_inline_zoom_alternatif (pSvg, pPrefix)
  {
    if (!pSvg || typeof pSvg.querySelectorAll !== "function") return;
    const tPrefix = typeof pPrefix === "string" && pPrefix ? pPrefix : "cogi_zoom_alt";
    const tIdMap = new Map ();
    const tClassMap = new Map ();
    const tTous = [pSvg].concat (Array.from (pSvg.querySelectorAll ("*")));

    tTous.forEach ((pNoeud) =>
      {
        if (!pNoeud || typeof pNoeud.getAttribute !== "function") return;
        const tId = pNoeud.getAttribute ("id");
        if (tId && !tIdMap.has (tId)) tIdMap.set (tId, `${tPrefix}_id_${tId}`);
        const tClasse = pNoeud.getAttribute ("class");
        if (!tClasse) return;
        tClasse.trim ().split (/\s+/).filter (Boolean).forEach ((pClasse) =>
          {
            if (!tClassMap.has (pClasse)) tClassMap.set (pClasse, `${tPrefix}_cls_${pClasse}`);
          });
      });

    const remplace_refs = (pValeur) =>
      {
        if (typeof pValeur !== "string" || (!tIdMap.size && !tClassMap.size)) return pValeur;
        let tValeur = pValeur;
        tIdMap.forEach ((pNouveau, pAncien) =>
          {
            const tEsc = pAncien.replace (/[.*+?^${}()|[\]\\]/g, "\\$&");
            tValeur = tValeur
              .replace (new RegExp (`url\\(#${tEsc}\\)`, "g"), `url(#${pNouveau})`)
              .replace (new RegExp (`#${tEsc}\\b`, "g"), `#${pNouveau}`);
          });
        tClassMap.forEach ((pNouveau, pAncien) =>
          {
            const tEsc = pAncien.replace (/[.*+?^${}()|[\]\\]/g, "\\$&");
            tValeur = tValeur.replace (new RegExp (`\\.${tEsc}\\b`, "g"), `.${pNouveau}`);
          });
        return tValeur;
      };

    tTous.forEach ((pNoeud) =>
      {
        if (!pNoeud || typeof pNoeud.getAttribute !== "function") return;
        const tId = pNoeud.getAttribute ("id");
        if (tId && tIdMap.has (tId)) pNoeud.setAttribute ("id", tIdMap.get (tId));
        const tClasse = pNoeud.getAttribute ("class");
        if (tClasse)
          {
            pNoeud.setAttribute (
              "class",
              tClasse.trim ().split (/\s+/).filter (Boolean)
                .map ((pClasse) => tClassMap.get (pClasse) || pClasse)
                .join (" ")
            );
          }
        Array.from (pNoeud.attributes || []).forEach ((pAttr) =>
          {
            if (!pAttr || pAttr.name === "id" || pAttr.name === "class") return;
            const tValeur = remplace_refs (pAttr.value);
            if (tValeur !== pAttr.value) pNoeud.setAttribute (pAttr.name, tValeur);
          });
        if ((pNoeud.tagName || "").toLowerCase () === "style")
          pNoeud.textContent = remplace_refs (pNoeud.textContent || "");
      });
  }

function importe_svg_zoom_alternatif_noeud (pRoot, pRole, pCarte = null)
  {
    const tFichier = fichier_zoom_alternatif_role_courant (pRole, pCarte);
    const tUrl = tFichier && tFichier.url;
    if (!tUrl)
      return cree_placeholder_zoom_alternatif_svg (pRoot, pRole, 72);

    const tModele = modele_svg_zoom_alternatif_client (pRole, pCarte);
    if (navigateur_webkit_sans_chrome_client ())
      {
        const tDataUrl = tModele && tModele.status === "ready" && tModele.svg_text
          ? uri_data_svg_bulle_projet_client (tModele.svg_text)
          : null;
        return cree_image_svg_zoom_alternatif_noeud (
          pRoot,
          tDataUrl || tUrl,
          tModele && tModele.viewBox
        ) || cree_placeholder_zoom_alternatif_svg (pRoot, pRole, 72);
      }

    if (tModele && tModele.status === "ready" && tModele.svg_text)
      {
        try
          {
            const tParser = new DOMParser ();
            const tDoc = tParser.parseFromString (tModele.svg_text, "image/svg+xml");
            const tSvg = tDoc && tDoc.documentElement;
            const tErreur = tDoc && typeof tDoc.querySelector === "function"
              ? tDoc.querySelector ("parsererror")
              : null;
            if (!tSvg || tSvg.nodeName.toLowerCase () !== "svg" || tErreur)
              throw new Error ("SVG invalide");

            const tVue = normalise_vue_carte_client (
              tModele.viewBox,
              vue_modele_svg_bulle_projet_client (tSvg)
            ) || {x : 0, y : 0, w : 100, h : 100};
            const tImporte = pRoot.ownerDocument.importNode (tSvg, true);
            isole_svg_inline_zoom_alternatif (
              tImporte,
              `cogi_zoom_alt_${pRole.replace (/[^a-z0-9_:-]/gi, "_")}_${Date.now ().toString (36)}`
            );
            tImporte.setAttribute ("x", "0");
            tImporte.setAttribute ("y", "0");
            tImporte.setAttribute ("width", String (tVue.w));
            tImporte.setAttribute ("height", String (tVue.h));
            tImporte.setAttribute ("viewBox", `${tVue.x} ${tVue.y} ${tVue.w} ${tVue.h}`);
            tImporte.setAttribute ("preserveAspectRatio", "xMidYMid meet");
            tImporte.setAttribute ("overflow", "visible");
            tImporte.style.overflow = "visible";
            tImporte.style.pointerEvents = "inherit";
            return {node : tImporte, viewBox : {x : 0, y : 0, w : tVue.w, h : tVue.h}};
          }
        catch (e) {}
      }

    return cree_placeholder_zoom_alternatif_svg (pRoot, pRole, 72);
  }

function cree_jauge_visuelle_zoom_alternatif_noeud (pRoot, pReglages)
  {
    if (!pRoot || !pRoot.ownerDocument || !Template.bananaProgressGauge)
      return importe_svg_zoom_alternatif_noeud (pRoot, JAUGE_VISUELLE_ROLE);

    const tDoc = pRoot.ownerDocument;
    const tFo = cree_element_svg_decor (pRoot, "foreignObject");
    tFo.setAttribute ("x", "0");
    tFo.setAttribute ("y", "0");
    tFo.setAttribute ("width", String (JAUGE_VISUELLE_VIEWBOX.w));
    tFo.setAttribute ("height", String (JAUGE_VISUELLE_VIEWBOX.h));
    tFo.setAttribute ("overflow", "visible");
    tFo.style.overflow = "visible";

    const tHost = tDoc.createElementNS ("http://www.w3.org/1999/xhtml", "div");
    tHost.setAttribute ("class", "cogi-carte-jauge-visuelle-host");
    tHost.setAttribute (
      "style",
      "width:100%;height:100%;overflow:visible;display:block;pointer-events:none;"
    );

    const tStyle = tDoc.createElementNS ("http://www.w3.org/1999/xhtml", "style");
    tStyle.textContent = [
      ".cogi-carte-jauge-visuelle-host .banana-progress-gauge{width:100%;height:100%;display:block;overflow:visible;}",
      ".cogi-carte-jauge-visuelle-host .banana-progress-gauge_svg{width:100%;height:100%;display:block;overflow:visible;}",
    ].join ("");
    tHost.appendChild (tStyle);
    tFo.appendChild (tHost);

    const tSource = source_jauge_visuelle_projet ();
    try
      {
        tFo._cogiBlazeView = Blaze.renderWithData (
          Template.bananaProgressGauge,
          {
            progress : () => gLectureCarteProgress.get (),
            config : pReglages && pReglages.jauge_visuelle,
            sourceSvgUrl : tSource.url,
            sourceSvgText : tSource.svgText,
            sourceViewBox : tSource.viewBox,
          },
          tHost
        );
      }
    catch (e)
      {
        return importe_svg_zoom_alternatif_noeud (pRoot, JAUGE_VISUELLE_ROLE);
      }

    return {
      node : tFo,
      viewBox : {
        x : JAUGE_VISUELLE_VIEWBOX.x,
        y : JAUGE_VISUELLE_VIEWBOX.y,
        w : JAUGE_VISUELLE_VIEWBOX.w,
        h : JAUGE_VISUELLE_VIEWBOX.h,
      },
    };
  }

function ajoute_zoom_alternatif_carte_racine (pRoot, pCarte, pOpt = {})
  {
    const tVueCarte = normalise_vue_carte_client (pCarte && pCarte.vue);
    const tVueScene = normalise_vue_carte_client (pOpt.sceneViewBox, vue_svg_racine_client (pRoot));
    const tVueFocus = normalise_vue_carte_client (pOpt.focusViewBox, tVueScene);
    if (!pRoot || !tVueCarte || !tVueScene) return null;
    const tUniteParPxFocus = Math.max (0.000001, tVueFocus.w / CARTE_ZOOM_ALTERNATIF_REFERENCE_PX);

    retire_decorations_cartes_racine (pRoot, {preserveBulles : !!pOpt.preserveBulles});
    const tMasque = mode_lecture_masque_scene_courant ();
    const tGroupe = cree_element_svg_decor (pRoot, "g");
    tGroupe.setAttribute ("class", CARTE_ZOOM_ALTERNATIF_GROUP_CLASS);
    if (Number.isFinite (Number (pOpt.initialOpacity)))
      tGroupe.style.opacity = String (Math.max (0, Math.min (1, Number (pOpt.initialOpacity))));
    applique_transform_calque_cartes_noeud (tGroupe, pOpt.cartesLayerTransform || "");

    const tPath = cree_element_svg_decor (pRoot, "path");
    tPath.setAttribute ("class", CARTE_ZOOM_ALTERNATIF_MASK_CLASS);
    tPath.setAttribute ("fill-rule", "evenodd");
    tPath.setAttribute ("clip-rule", "evenodd");
    tPath.setAttribute ("fill", couleur_effective_mode_lecture_masque_scene_client (tMasque) || "#ffffff");
    tPath.setAttribute ("fill-opacity", String (tMasque.opacite ?? 0.34));
    tPath.style.cursor = "pointer";
    tPath.style.pointerEvents = "auto";
    actualise_path_zoom_alternatif (tPath, tVueScene, pOpt.masquePlein ? null : tVueCarte);
    tGroupe.appendChild (tPath);

    const tReglages = reglages_interface_carte_effectifs (pCarte, pOpt.reglagesOverride || null);
    const marge_cadre = (pCle) =>
      Math.max (0, Number (tReglages[pCle]) || 0) * tUniteParPxFocus;
    const tCadreMargeHaut = marge_cadre ("zoom_alternatif_cadre_epaisseur_haut");
    const tCadreMargeDroite = marge_cadre ("zoom_alternatif_cadre_epaisseur_droite");
    const tCadreMargeBas = marge_cadre ("zoom_alternatif_cadre_epaisseur_bas");
    const tCadreMargeGauche = marge_cadre ("zoom_alternatif_cadre_epaisseur_gauche");
    const tCadreX = tVueCarte.x - tCadreMargeGauche;
    const tCadreY = tVueCarte.y - tCadreMargeHaut;
    const tCadreW = tVueCarte.w + tCadreMargeGauche + tCadreMargeDroite;
    const tCadreH = tVueCarte.h + tCadreMargeHaut + tCadreMargeBas;
    const tCadreCouleur = tReglages.zoom_alternatif_cadre_couleur || "#f6efd8";
    const tFrameVue = {x : tCadreX, y : tCadreY, w : tCadreW, h : tCadreH};
    const tReliefsReglages = reliefs_cadre_zoom_alternatif (tFrameVue, tReglages);
    tReliefsReglages.forEach ((tReliefReglages) =>
      {
        const tRelief = cree_element_svg_decor (pRoot, "path");
        tRelief.setAttribute ("class", CARTE_ZOOM_ALTERNATIF_FRAME_RELIEF_CLASS);
        tRelief.setAttribute ("data-cogi-arriere-plan", String (tReliefReglages.index || 1));
        tRelief.setAttribute (
          "d",
          path_relief_cadre_zoom_alternatif_carte (tFrameVue, tVueCarte, tReliefReglages)
        );
        tRelief.setAttribute ("fill-rule", "evenodd");
        tRelief.setAttribute ("clip-rule", "evenodd");
        tRelief.setAttribute ("fill", tReliefReglages.couleur);
        tRelief.setAttribute ("fill-opacity", String (tReliefReglages.opacite));
        tRelief.setAttribute ("stroke", "none");
        tRelief.style.pointerEvents = "none";
        const tReliefTransform = transform_relief_cadre_zoom_alternatif (tReliefReglages);
        if (tReliefTransform) tRelief.setAttribute ("transform", tReliefTransform);
        ajoute_ombre_svg_relief_cadre_zoom_alternatif (pRoot, tGroupe, tRelief, tFrameVue, tReliefReglages);
        applique_masque_relief_cadre_zoom_alternatif (pRoot, tRelief, tFrameVue, tReliefReglages);
        tGroupe.appendChild (tRelief);
      });

    const tCadre = cree_element_svg_decor (pRoot, "path");
    tCadre.setAttribute ("class", CARTE_ZOOM_ALTERNATIF_FRAME_CLASS);
    tCadre.setAttribute ("d", path_cadre_zoom_alternatif_carte (
      tFrameVue,
      tVueCarte,
      0,
      0,
      false,
      (Number (tReglages.zoom_alternatif_cadre_arrondi) || 0) * tUniteParPxFocus
    ));
    tCadre.setAttribute ("fill-rule", "evenodd");
    tCadre.setAttribute ("clip-rule", "evenodd");
    tCadre.setAttribute ("fill", tCadreCouleur);
    tCadre.setAttribute ("stroke", tCadreCouleur);
    tCadre.setAttribute ("stroke-width", "0");
    tCadre.style.pointerEvents = "none";
    tGroupe.appendChild (tCadre);

    const tZoneRelecture = cree_element_svg_decor (pRoot, "rect");
    tZoneRelecture.setAttribute (CARTE_ZOOM_ALTERNATIF_ROLE_ATTR, CARTE_ZOOM_ALTERNATIF_RELECTURE_ROLE);
    tZoneRelecture.setAttribute ("x", String (tVueCarte.x));
    tZoneRelecture.setAttribute ("y", String (tVueCarte.y));
    tZoneRelecture.setAttribute ("width", String (tVueCarte.w));
    tZoneRelecture.setAttribute ("height", String (tVueCarte.h));
    tZoneRelecture.setAttribute ("fill", "#ffffff");
    tZoneRelecture.setAttribute ("fill-opacity", "0.001");
    tZoneRelecture.style.pointerEvents = "all";
    tZoneRelecture.style.cursor = "pointer";
    tZoneRelecture.style.touchAction = "none";
    tGroupe.appendChild (tZoneRelecture);

    const tAssets = cree_element_svg_decor (pRoot, "g");
    tAssets.setAttribute ("class", CARTE_ZOOM_ALTERNATIF_ASSETS_CLASS);
    tAssets.style.opacity = "0";
    tAssets.style.pointerEvents = "none";
    const tGap = CARTE_ZOOM_ALTERNATIF_ASSET_GAP_PX * tUniteParPxFocus;
    const tMarge = CARTE_ZOOM_ALTERNATIF_ASSET_OFFSET_PX * tUniteParPxFocus;
    const tItemTaille = CARTE_ZOOM_ALTERNATIF_ASSET_SIZE_PX * tUniteParPxFocus;
    const tRolesTailles = ZOOM_ALTERNATIF_SVG_ROLES
      .map ((pRole) =>
        ({
          ...pRole,
          taille : taille_zoom_alternatif_role (pRole.cle, tReglages),
        }));
    const tDimensions = new Map ();
    tRolesTailles.forEach ((pRole) =>
      {
        const tImported = pRole.cle === JAUGE_VISUELLE_ROLE
          ? cree_jauge_visuelle_zoom_alternatif_noeud (pRoot, tReglages)
          : importe_svg_zoom_alternatif_noeud (pRoot, pRole.cle, pCarte);
        const tNode = tImported.node;
        const tVue = normalise_vue_carte_client (tImported.viewBox, {x : 0, y : 0, w : 100, h : 100});
        const tTailleFinale = tItemTaille * pRole.taille;
        const tScale = tTailleFinale / Math.max (tVue.w, tVue.h, 1);
        tDimensions.set (
          pRole.cle,
          {
            node : tNode,
            vue : tVue,
            scale : tScale,
            w : tVue.w * tScale,
            h : tVue.h * tScale,
          }
        );
      });
    const tDim = (pRole) => tDimensions.get (pRole) || {w : tItemTaille, h : tItemTaille};
    const tSlotY = (pIndex) => pIndex * (tItemTaille + tGap);
    const tPosition = (pRole) =>
      {
        const tD = tDim (pRole);
        const tOffsetX = offset_zoom_alternatif_role (pRole, "x", tReglages) * tUniteParPxFocus;
        const tOffsetY = offset_zoom_alternatif_role (pRole, "y", tReglages) * tUniteParPxFocus;
        const tAncrageCarte = ancrage_zoom_alternatif_role (pRole, tReglages);
        const tXCarteAncree = tAncrageCarte === "gauche"
          ? tVueCarte.x + tMarge + tOffsetX
          : tVueCarte.x + tVueCarte.w - tD.w - tMarge + tOffsetX;
        if (pRole === "carte_fermeture")
          return {
            x : tXCarteAncree,
            y : tVueCarte.y + tMarge + tOffsetY,
          };
        if (pRole === "carte_jauge")
          return {
            x : tVueFocus.x + tVueFocus.w - tD.w - tMarge + tOffsetX,
            y : tVueFocus.y + tMarge + tOffsetY,
          };
        if (pRole === "carte_oui")
          return {
            x : tVueFocus.x + tVueFocus.w - tD.w - tMarge + tOffsetX,
            y : tVueFocus.y + tMarge + tSlotY (1) + tOffsetY,
          };
        if (pRole === "carte_non")
          return {
            x : tVueFocus.x + tVueFocus.w - tD.w - tMarge + tOffsetX,
            y : tVueFocus.y + tMarge + tSlotY (2) + tOffsetY,
          };
        if (pRole === "carte_legende")
          return {
            x : tVueCarte.x + ((tVueCarte.w - tD.w) / 2) + tOffsetX,
            y : tVueCarte.y + tVueCarte.h - (tD.h * 0.55) + tOffsetY,
          };
        return {
          x : tVueCarte.x + tVueCarte.w + tMarge + tOffsetX,
          y : tVueCarte.y + tMarge + tOffsetY,
        };
      };
    tRolesTailles.forEach ((pRole) =>
      {
        const tInfo = tDimensions.get (pRole.cle);
        if (!tInfo) return;
        const tNode = tInfo.node;
        const tVue = tInfo.vue;
        const tBouton = pRole.cle !== "carte_jauge";
        const tPos = tPosition (pRole.cle);
        tNode.setAttribute (CARTE_ZOOM_ALTERNATIF_ROLE_ATTR, pRole.cle);
        tNode.setAttribute (
          "transform",
          `translate(${tPos.x} ${tPos.y}) scale(${tInfo.scale}) translate(${-tVue.x} ${-tVue.y})`
        );
        tNode.style.pointerEvents = tBouton ? "all" : "none";
        if (tBouton) tNode.style.cursor = "pointer";
        tAssets.appendChild (tNode);
      });
    tGroupe.appendChild (tAssets);
    pRoot.appendChild (tGroupe);
    return {groupe : tGroupe, masque : tPath};
  }

function ajoute_bulle_carte_racine (pRoot, pBulle, pOpts = {})
  {
    if (!pRoot || !pBulle) return;

    let tLayer = (pOpts && pOpts.layerBulles) || pRoot.querySelector (`.${CARTE_DECOR_BULLES_LAYER_CLASS}:not(.${LECTURE_BULLES_FADE_CLASS})`);
    if (!tLayer)
      {
        tLayer = cree_element_svg_decor (pRoot, "g");
        tLayer.setAttribute ("class", CARTE_DECOR_BULLES_LAYER_CLASS);
        pRoot.appendChild (tLayer);
      }
    applique_transform_calque_cartes_noeud (tLayer, pOpts.cartesLayerTransform || "");

    const tInteractive = !!(
      pOpts.interactiveAllBulles
      || (Array.isArray (pOpts.interactiveCardIds)
          && pOpts.interactiveCardIds.includes (pBulle.carte_id))
      || (pOpts.interactiveCardId && pOpts.interactiveCardId === pBulle.carte_id)
    );
    const tGroupe = cree_element_svg_decor (pRoot, "g");
    tGroupe.setAttribute ("class", CARTE_DECOR_BULLE_CLASS);
    if (pBulle.carte_id)
      tGroupe.setAttribute ("data-carte-id", pBulle.carte_id);
    tGroupe.setAttribute ("transform", `translate(${pBulle.x} ${pBulle.y}) rotate(${pBulle.rotation || 0})`);
    const tVisuel = cree_element_svg_decor (pRoot, "g");
    tVisuel.setAttribute ("class", CARTE_DECOR_BULLE_VISUAL_CLASS);
    tVisuel.style.pointerEvents = "none";
    if (pBulle.attenuee)
      {
        tGroupe.setAttribute (
          "opacity",
          Math.max (0.12, Math.min (1, Number (pBulle.opacite_groupe) || 1)).toFixed (3)
        );
      }

    const tReglagesBulles = normalise_reglages_bulles_projet (
      (pOpts && pOpts.reglagesBulles)
      || reglages_bulles_projet_courant (pOpts && pOpts.projet)
    );
    const tOmbreOpacite = Math.max (0, Math.min (1, Number (tReglagesBulles.ombre_opacite) || 0));
    if (tReglagesBulles.ombre_active === true && tOmbreOpacite > 0)
      {
        const tOmbreBlur = Math.max (0, Number (tReglagesBulles.ombre_blur) || 0);
        const tOmbreX = Number (tReglagesBulles.ombre_x) || 0;
        const tOmbreY = Number (tReglagesBulles.ombre_y) || 0;
        const tOmbreCouleur = normalise_couleur_style_interface (tReglagesBulles.ombre_couleur, "#000000");
        const tOmbreZonesDessinees = tReglagesBulles.ombre_zones_dessinees_seules !== false;
        const tEstCustom = pBulle.mode === "custom" && pBulle.svg_bulle_id && pBulle.svg_href;
        const tDimensionsCustom = pBulle.mode === "custom" && pBulle.svg_bulle_id;
        const tLargeur = tDimensionsCustom
          ? Math.max (1, Number (pBulle.svg_largeur) || pBulle.taille || (pBulle.rayon * 2))
          : Math.max (1, pBulle.rayon * 2.16);
        const tHauteur = tDimensionsCustom
          ? Math.max (1, Number (pBulle.svg_hauteur) || pBulle.taille || (pBulle.rayon * 2))
          : Math.max (1, pBulle.rayon * 2.16);
        const tX = -tLargeur / 2;
        const tY = -tHauteur / 2;
        const tMarge = Math.ceil (tOmbreBlur * 4 + Math.max (Math.abs (tOmbreX), Math.abs (tOmbreY), 8));
        // L'ombre exportée avec les assets d'abord (seule disponible dans
        // l'appli embarquée, sans serveur), comme pour les items et les calques.
        const tImageRaster = url_asset_diffusion_scene_client (pBulle.bulle_ombre_asset_url)
          || url_ombre_asset_scene_client (
            pBulle.bulle_ombre_asset_file_id,
            pBulle.bulle_ombre_asset_file_name
          );
        if (tImageRaster)
          {
            const tImage = cree_element_svg_decor (pRoot, "image");
            tImage.setAttribute ("x", String (tX - tMarge));
            tImage.setAttribute ("y", String (tY - tMarge));
            tImage.setAttribute ("width", String (tLargeur + tMarge * 2));
            tImage.setAttribute ("height", String (tHauteur + tMarge * 2));
            tImage.setAttribute ("preserveAspectRatio", "none");
            tImage.setAttribute ("href", tImageRaster);
            tImage.setAttributeNS ("http://www.w3.org/1999/xlink", "href", tImageRaster);
            tImage.style.pointerEvents = "none";
            tGroupe.appendChild (tImage);
          }
      }

    if (pBulle.mode === "custom" && pBulle.svg_bulle_id && pBulle.svg_href)
      {
        const tImage = cree_element_svg_decor (pRoot, "image");
        const tLargeur = Math.max (1, Number (pBulle.svg_largeur) || pBulle.taille || (pBulle.rayon * 2));
        const tHauteur = Math.max (1, Number (pBulle.svg_hauteur) || pBulle.taille || (pBulle.rayon * 2));
        tImage.setAttribute ("x", (-tLargeur / 2).toFixed (3));
        tImage.setAttribute ("y", (-tHauteur / 2).toFixed (3));
        tImage.setAttribute ("width", tLargeur.toFixed (3));
        tImage.setAttribute ("height", tHauteur.toFixed (3));
        tImage.setAttribute ("preserveAspectRatio", "xMidYMid meet");
        tImage.setAttribute ("opacity", pBulle.opacite.toFixed (3));
        tImage.setAttribute ("href", pBulle.svg_href);
        tImage.setAttributeNS ("http://www.w3.org/1999/xlink", "href", pBulle.svg_href);
        tImage.style.pointerEvents = "none";
        tVisuel.appendChild (tImage);
      }
    else
      {
        const tHalo = cree_element_svg_decor (pRoot, "circle");
        tHalo.setAttribute ("r", (pBulle.rayon * 1.08).toFixed (3));
        tHalo.setAttribute ("fill-opacity", Math.max (0.08, Math.min (0.3, pBulle.opacite * 0.34)).toFixed (3));
        tHalo.style.pointerEvents = "none";

        const tCorps = cree_element_svg_decor (pRoot, "circle");
        tCorps.setAttribute ("r", pBulle.rayon.toFixed (3));
        tCorps.setAttribute ("fill", pBulle.couleur);
        tCorps.setAttribute ("fill-opacity", pBulle.opacite.toFixed (3));
        tCorps.setAttribute ("stroke", "#ffffff");
        tCorps.setAttribute ("stroke-opacity", Math.max (0.35, Math.min (0.9, pBulle.opacite + 0.18)).toFixed (3));
        tCorps.setAttribute ("stroke-width", Math.max (1.8, pBulle.rayon * 0.06).toFixed (3));
        tCorps.style.pointerEvents = "none";

        const tAnneau = cree_element_svg_decor (pRoot, "circle");
        tAnneau.setAttribute ("r", (pBulle.rayon * 0.82).toFixed (3));
        tAnneau.setAttribute ("fill", "none");
        tAnneau.setAttribute ("stroke", "#ffffff");
        tAnneau.setAttribute ("stroke-opacity", Math.max (0.12, Math.min (0.32, pBulle.opacite * 0.45)).toFixed (3));
        tAnneau.setAttribute ("stroke-width", Math.max (1.1, pBulle.rayon * 0.035).toFixed (3));
        tAnneau.style.pointerEvents = "none";

        const tReflet = cree_element_svg_decor (pRoot, "ellipse");
        tReflet.setAttribute ("cx", (-pBulle.rayon * 0.3).toFixed (3));
        tReflet.setAttribute ("cy", (-pBulle.rayon * 0.36).toFixed (3));
        tReflet.setAttribute ("rx", (pBulle.rayon * 0.24).toFixed (3));
        tReflet.setAttribute ("ry", (pBulle.rayon * 0.15).toFixed (3));
        tReflet.setAttribute ("fill", "#ffffff");
        tReflet.setAttribute ("fill-opacity", Math.max (0.18, Math.min (0.48, pBulle.opacite * 0.72)).toFixed (3));
        tReflet.setAttribute ("transform", `rotate(-26 ${(-pBulle.rayon * 0.3).toFixed (3)} ${(-pBulle.rayon * 0.36).toFixed (3)})`);
        tReflet.style.pointerEvents = "none";

        const tRefletSecondaire = cree_element_svg_decor (pRoot, "circle");
        tRefletSecondaire.setAttribute ("cx", (pBulle.rayon * 0.34).toFixed (3));
        tRefletSecondaire.setAttribute ("cy", (-pBulle.rayon * 0.12).toFixed (3));
        tRefletSecondaire.setAttribute ("r", Math.max (2.8, pBulle.rayon * 0.11).toFixed (3));
        tRefletSecondaire.setAttribute ("fill", "#ffffff");
        tRefletSecondaire.setAttribute ("fill-opacity", Math.max (0.08, Math.min (0.18, pBulle.opacite * 0.24)).toFixed (3));
        tRefletSecondaire.style.pointerEvents = "none";

        tVisuel.appendChild (tHalo);
        tVisuel.appendChild (tCorps);
        tVisuel.appendChild (tAnneau);
        tVisuel.appendChild (tReflet);
        tVisuel.appendChild (tRefletSecondaire);
      }
    tGroupe.appendChild (tVisuel);

    if (tInteractive)
      {
        const tCurseur = (typeof pOpts.interactiveCursor === "string" && pOpts.interactiveCursor)
          ? pOpts.interactiveCursor
          : "grab";
        const tZone = cree_element_svg_decor (pRoot, "circle");
        tZone.setAttribute ("class", CARTE_DECOR_BULLE_HIT_CLASS);
        tZone.setAttribute ("data-carte-id", pBulle.carte_id);
        tZone.setAttribute ("r", (pBulle.rayon * 1.35).toFixed (3));
        tZone.setAttribute ("fill", "#ffffff");
        tZone.setAttribute ("fill-opacity", "0.001");
        tZone.style.pointerEvents = "all";
        tZone.style.cursor = tCurseur;
        tZone.style.touchAction = "none";
        tGroupe.appendChild (tZone);
      }

    tLayer.appendChild (tGroupe);
  }

function applique_decorations_cartes_racine (pRoot, pOptions = {})
  {
    if (!pRoot) return;
    const tMasqueExistant = pRoot.querySelector && pRoot.querySelector (`.${CARTE_DECOR_MASQUE_CLASS}`);
    const tLayerBullesExistant = pRoot.querySelector && pRoot.querySelector (`.${CARTE_DECOR_BULLES_LAYER_CLASS}:not(.${LECTURE_BULLES_FADE_CLASS})`);
    const tMasquePresent = !!tMasqueExistant;
    const tAnimeMasque = !!(pOptions && pOptions.animateSelectionMask);
    const tAnimeBulles = !!(pOptions && pOptions.animateBullesZoom);
    const tPreserveBulles = !!(pOptions && pOptions.preserveBulles);
    const tBulles = Array.isArray (pOptions.bulles) ? pOptions.bulles.filter ((b) => b && !b.masquee) : [];
    const tTransformCartes = pOptions.cartesLayerTransform || "";
    const tDelaiSortieMasque = Math.max (0, Number (pOptions.selectionMaskOutDelayMs) || 0);
    const tConserveMasque = !!(
      tMasqueExistant
      && pOptions.selectionMaskView
    );
    const tMasqueSortie = (tAnimeMasque && tMasquePresent && !pOptions.selectionMaskView)
      ? clone_masque_carte_pour_fondu_sortie (
        pRoot,
        {
          decalage_ms : tDelaiSortieMasque,
          sceneViewBox : pOptions.sceneViewBox,
        }
      )
      : null;
    const tLayerBullesSortie = (!tPreserveBulles && tAnimeBulles && tLayerBullesExistant && !tBulles.length)
      ? clone_layer_bulles_pour_fondu_sortie (pRoot)
      : null;
    const tNouveauLayerBulles = (!tPreserveBulles && tBulles.length)
      ? cree_element_svg_decor (pRoot, "g")
      : null;
    if (tNouveauLayerBulles)
      {
        tNouveauLayerBulles.setAttribute ("class", CARTE_DECOR_BULLES_LAYER_CLASS);
        applique_transform_calque_cartes_noeud (tNouveauLayerBulles, tTransformCartes);
        tBulles.forEach ((pBulle) =>
          ajoute_bulle_carte_racine (
            pRoot,
            pBulle,
            {...pOptions, layerBulles : tNouveauLayerBulles}
          ));
      }
    retire_decorations_cartes_racine (
      pRoot,
      {
        preserveMask : tConserveMasque ? tMasqueExistant : null,
        preserveZoomAlt : !!pOptions.preserveZoomAlt,
        preserveBulles : tPreserveBulles,
      }
    );
    if (tMasqueSortie) lance_fondu_sortie_masque_carte (pRoot, tMasqueSortie, {decalage_ms : tDelaiSortieMasque});
    if (tLayerBullesSortie) lance_fondu_sortie_layer_bulles (pRoot, tLayerBullesSortie);

    if (pOptions.fondColor)
      ajoute_fond_carte_racine (pRoot, pOptions.sceneViewBox, pOptions.fondColor, tTransformCartes);

    if (pOptions.selectionMaskView && !tConserveMasque)
      ajoute_masque_selection_carte_racine (
        pRoot,
        pOptions.sceneViewBox,
        pOptions.selectionMaskView,
        {
          fill : pOptions.selectionMaskFill,
          opacity : pOptions.selectionMaskOpacity,
          animateIn : tAnimeMasque && !tMasquePresent,
          animateInDelayMs : pOptions.selectionMaskInDelayMs,
          cartesLayerTransform : tTransformCartes,
        }
      );
    if (tConserveMasque)
      {
        tMasqueExistant.setAttribute ("fill", pOptions.selectionMaskFill || "#ffffff");
        tMasqueExistant.setAttribute ("fill-opacity", String (pOptions.selectionMaskOpacity ?? 0.34));
        actualise_path_masque_selection_carte (
          tMasqueExistant,
          pOptions.sceneViewBox,
          pOptions.selectionMaskView,
          {
            fill : pOptions.selectionMaskFill,
            opacity : pOptions.selectionMaskOpacity,
          }
        );
        applique_transform_calque_cartes_noeud (tMasqueExistant, tTransformCartes);
      }

    if (tNouveauLayerBulles)
      {
        pRoot.appendChild (tNouveauLayerBulles);
        if (pOptions.animateBullesIn)
          anime_entree_layer_bulles (tNouveauLayerBulles);
      }
    if (pOptions.preserveZoomAlt)
      place_zoom_alternatif_au_premier_plan_racine (pRoot);
  }

function applique_reglages_calques_svg_tpl (pTpl)
  {
    const tDoc = pTpl && pTpl._svgDoc;
    const tSceneId = Session.get ("scene_id");
    const tScene = tSceneId ? Scenes.findOne (tSceneId) : null;
    if (!tDoc || !tScene || !Array.isArray (tScene.objets_config)) return;
    if (tDoc.querySelectorAll)
      tDoc.querySelectorAll ("[data-cogi-calque-svg-decor-for]").forEach ((pNoeud) => pNoeud.remove ());
    const tNs = "http://www.w3.org/2000/svg";
    const tNormaliseCouleur = (pValeur, pDefaut) =>
      {
        const tTexte = typeof pValeur === "string" ? pValeur.trim () : "";
        return /^#[0-9a-f]{6}$/i.test (tTexte) ? tTexte : pDefaut;
      };
    const tNombre = (pValeur, pDefaut, pMin = null, pMax = null) =>
      {
        let tValeur = Number (pValeur);
        if (!Number.isFinite (tValeur)) tValeur = pDefaut;
        if (Number.isFinite (pMin)) tValeur = Math.max (pMin, tValeur);
        if (Number.isFinite (pMax)) tValeur = Math.min (pMax, tValeur);
        return tValeur;
      };
    const tRetireIds = (pNoeud) =>
      {
        if (!pNoeud || !pNoeud.removeAttribute) return;
        pNoeud.removeAttribute ("id");
        if (pNoeud.querySelectorAll)
          pNoeud.querySelectorAll ("[id]").forEach ((pEnfant) => pEnfant.removeAttribute ("id"));
      };
    const tNettoieEtatLectureCloneOmbre = (pNoeud) =>
      {
        const tNoeuds = [pNoeud].concat (
          pNoeud && pNoeud.querySelectorAll
            ? Array.from (pNoeud.querySelectorAll ("*"))
            : []
        );
        tNoeuds.forEach ((pElt) =>
          {
            if (!pElt || !pElt.removeAttribute) return;
            pElt.removeAttribute (CARTE_MASK_ATTR);
            pElt.removeAttribute (CARTE_MASK_STATE_ATTR);
            pElt.removeAttribute (CARTE_MASK_PREV_DISPLAY_ATTR);
            pElt.removeAttribute (CARTE_MASK_PREV_OPACITY_ATTR);
            pElt.removeAttribute (CARTE_MASK_PREV_VISIBLE_OPACITY_ATTR);
            pElt.removeAttribute (CARTE_MASK_PREV_TRANSITION_ATTR);
            pElt.removeAttribute (CARTE_MASK_PREV_POINTER_EVENTS_ATTR);
          });
      };
    const tAjouteFiltreOmbre = (pGroupe, pId, pCouleur, pOpacite, pBlur, pX, pY, pZone) =>
      {
        const tDefs = tDoc.createElementNS (tNs, "defs");
        const tFiltre = tDoc.createElementNS (tNs, "filter");
        const tFiltreId = "cogi-calque-svg-ombre-" + String (pId).replace (/[^a-z0-9_-]/gi, "-");
        const tZone = pZone || {x : 0, y : 0, w : 1, h : 1};
        const tMarge = Math.ceil (Number (pBlur || 0) * 4 + Math.max (Math.abs (Number (pX) || 0), Math.abs (Number (pY) || 0), 8));
        tFiltre.setAttribute ("id", tFiltreId);
        tFiltre.setAttribute ("filterUnits", "userSpaceOnUse");
        tFiltre.setAttribute ("x", String (Number (tZone.x) - tMarge));
        tFiltre.setAttribute ("y", String (Number (tZone.y) - tMarge));
        tFiltre.setAttribute ("width", String (Math.max (1, Number (tZone.w) || 1) + tMarge * 2));
        tFiltre.setAttribute ("height", String (Math.max (1, Number (tZone.h) || 1) + tMarge * 2));
        tFiltre.setAttribute ("color-interpolation-filters", "sRGB");
        const tBlurNode = tDoc.createElementNS (tNs, "feGaussianBlur");
        tBlurNode.setAttribute ("in", "SourceAlpha");
        tBlurNode.setAttribute ("stdDeviation", String (pBlur));
        tBlurNode.setAttribute ("result", "blur");
        const tOffset = tDoc.createElementNS (tNs, "feOffset");
        tOffset.setAttribute ("in", "blur");
        tOffset.setAttribute ("dx", String (pX));
        tOffset.setAttribute ("dy", String (pY));
        tOffset.setAttribute ("result", "offset");
        const tFlood = tDoc.createElementNS (tNs, "feFlood");
        tFlood.setAttribute ("flood-color", pCouleur);
        tFlood.setAttribute ("flood-opacity", String (pOpacite));
        tFlood.setAttribute ("result", "color");
        const tComposite = tDoc.createElementNS (tNs, "feComposite");
        tComposite.setAttribute ("in", "color");
        tComposite.setAttribute ("in2", "offset");
        tComposite.setAttribute ("operator", "in");
        tComposite.setAttribute ("result", "shadow");
        tFiltre.appendChild (tBlurNode);
        tFiltre.appendChild (tOffset);
        tFiltre.appendChild (tFlood);
        tFiltre.appendChild (tComposite);
        tDefs.appendChild (tFiltre);
        pGroupe.appendChild (tDefs);
        return "url(#" + tFiltreId + ")";
      };
    const tSvgOmbreRasterCalque = (pSource, pConfig, pZone, pMarge, pFiltreId, pFiltre, pRectFallback = null) =>
      {
        const tDocRaster = document.implementation.createDocument (tNs, "svg", null);
        const tSvg = tDocRaster.documentElement;
        const tW = Math.max (1, Number (pZone.w) + pMarge * 2);
        const tH = Math.max (1, Number (pZone.h) + pMarge * 2);
        tSvg.setAttribute ("xmlns", tNs);
        tSvg.setAttribute ("width", String (Math.ceil (tW)));
        tSvg.setAttribute ("height", String (Math.ceil (tH)));
        tSvg.setAttribute ("viewBox", `${Number (pZone.x) - pMarge} ${Number (pZone.y) - pMarge} ${tW} ${tH}`);
        const tDefs = tDocRaster.createElementNS (tNs, "defs");
        const tFiltre = pFiltre.cloneNode (true);
        tFiltre.setAttribute ("id", pFiltreId);
        tDefs.appendChild (tFiltre);
        tSvg.appendChild (tDefs);
        const tOmbre = tDocRaster.createElementNS (tNs, "g");
        tOmbre.setAttribute ("filter", `url(#${pFiltreId})`);
        if (pSource && pConfig.calque_ombre_zones_dessinees_seules !== false)
          {
            const tClone = pSource.cloneNode (true);
            tRetireIds (tClone);
            tNettoieEtatLectureCloneOmbre (tClone);
            tClone.removeAttribute ("transform");
            tClone.removeAttribute ("data-cogi-transform-original");
            tClone.removeAttribute ("display");
            tClone.setAttribute ("overflow", "visible");
            tClone.style.pointerEvents = "none";
            tClone.style.opacity = "1";
            tClone.style.display = "";
            tClone.style.overflow = "visible";
            tClone.style.visibility = "visible";
            tOmbre.appendChild (tDocRaster.importNode (tClone, true));
          }
        else if (pRectFallback)
          {
            const tRect = tDocRaster.createElementNS (tNs, "rect");
            tRect.setAttribute ("x", String (pRectFallback.x));
            tRect.setAttribute ("y", String (pRectFallback.y));
            tRect.setAttribute ("width", String (pRectFallback.w));
            tRect.setAttribute ("height", String (pRectFallback.h));
            tRect.setAttribute ("rx", String (pRectFallback.r || 0));
            tRect.setAttribute ("ry", String (pRectFallback.r || 0));
            tRect.setAttribute ("fill", "#000000");
            tOmbre.appendChild (tRect);
          }
        tSvg.appendChild (tOmbre);
        return {
          svgText : new XMLSerializer ().serializeToString (tSvg),
          width : tW,
          height : tH,
          x : Number (pZone.x) - pMarge,
          y : Number (pZone.y) - pMarge,
        };
      };
    for (const tConfig of tScene.objets_config)
      {
        if (!tConfig || !tConfig.id || tConfig.type === "item") continue;
        const tCategorie = trouve_categorie_pour_id (tConfig.id);
        if (!tCategorie || !CATEGORIES_CALQUE_SVG_REGLABLES.has (tCategorie)) continue;
        const tNoeud = tDoc.getElementById && tDoc.getElementById (tConfig.id);
        if (!tNoeud || !tNoeud.setAttribute) continue;
        if (!tNoeud.hasAttribute ("data-cogi-transform-original"))
          tNoeud.setAttribute ("data-cogi-transform-original", tNoeud.getAttribute ("transform") || "");
        const tOriginalTransform = tNoeud.getAttribute ("data-cogi-transform-original") || "";
        const tOffsetX = Number.isFinite (Number (tConfig.calque_x)) ? Number (tConfig.calque_x) : 0;
        const tOffsetY = Number.isFinite (Number (tConfig.calque_y)) ? Number (tConfig.calque_y) : 0;
        let tBBox = null;
        try { tBBox = typeof tNoeud.getBBox === "function" ? tNoeud.getBBox () : null; }
        catch (e) { tBBox = null; }
        const tBBoxW = tBBox && Number.isFinite (Number (tBBox.width)) && Number (tBBox.width) > 0 ? Number (tBBox.width) : 1;
        const tBBoxH = tBBox && Number.isFinite (Number (tBBox.height)) && Number (tBBox.height) > 0 ? Number (tBBox.height) : 1;
        const tScalePct = Number.isFinite (Number (tConfig.calque_scale_pct))
          ? Math.max (1, Math.min (500, Number (tConfig.calque_scale_pct)))
          : 100;
        const tLargeur = Number.isFinite (Number (tConfig.calque_w)) && Number (tConfig.calque_w) > 0
          ? Math.max (1, Number (tConfig.calque_w))
          : tBBoxW * (tScalePct / 100);
        const tHauteur = Number.isFinite (Number (tConfig.calque_h)) && Number (tConfig.calque_h) > 0
          ? Math.max (1, Number (tConfig.calque_h))
          : tBBoxH * (tScalePct / 100);
        const tScaleX = tBBoxW > 0 ? tLargeur / tBBoxW : 1;
        const tScaleY = tBBoxH > 0 ? tHauteur / tBBoxH : 1;
        const tRotation = normalise_rotation_degres_client (tConfig.calque_rotation_deg);
        const tCentreX = tBBox ? Number (tBBox.x) + tBBoxW / 2 : 0;
        const tCentreY = tBBox ? Number (tBBox.y) + tBBoxH / 2 : 0;
        const tTransformExtra = [
          tOffsetX || tOffsetY ? `translate(${tOffsetX} ${tOffsetY})` : "",
          tRotation ? `rotate(${tRotation} ${tCentreX} ${tCentreY})` : "",
          Math.abs (tScaleX - 1) > 0.0001 || Math.abs (tScaleY - 1) > 0.0001 ? `scale(${tScaleX} ${tScaleY})` : "",
        ].filter (Boolean).join (" ");
        const tTransform = [tOriginalTransform, tTransformExtra].filter (Boolean).join (" ");
        if (tTransform) tNoeud.setAttribute ("transform", tTransform);
        else tNoeud.removeAttribute ("transform");
        const tMasqueModeAffichage = !objet_visible_mode_affichage_diffusion_client (tConfig);
        const tCalqueMasque = tConfig.calque_masque === true;
        tNoeud.style.display = (tCalqueMasque || tMasqueModeAffichage) ? "none" : "";
        if (typeof tConfig.calque_opacite !== "undefined")
          tNoeud.style.opacity = String (Math.max (0, Math.min (1, Number (tConfig.calque_opacite) || 0)));
        else tNoeud.style.opacity = "";
	        const tMasque = tCalqueMasque || tMasqueModeAffichage;
	        const tFondActif = tConfig.calque_fond_actif === true;
	        const tOmbreActive = tConfig.calque_ombre_active === true;
	        const tOmbreRaster = tOmbreActive && ombres_svg_rasterisees_scene_client ();
	        if (!tMasque && (tFondActif || tOmbreActive) && tNoeud.parentNode && tBBox)
	          {
            const tGroupeDecor = tDoc.createElementNS (tNs, "g");
            tGroupeDecor.setAttribute ("data-cogi-calque-svg-decor-for", tConfig.id);
            if (tTransform) tGroupeDecor.setAttribute ("transform", tTransform);
            tGroupeDecor.setAttribute ("overflow", "visible");
            tGroupeDecor.style.overflow = "visible";
            const tPadTop = tNombre (tConfig.calque_padding_top, 0, 0);
            const tPadRight = tNombre (tConfig.calque_padding_right, 0, 0);
            const tPadBottom = tNombre (tConfig.calque_padding_bottom, 0, 0);
            const tPadLeft = tNombre (tConfig.calque_padding_left, 0, 0);
            const tRectX = Number (tBBox.x) - tPadLeft;
            const tRectY = Number (tBBox.y) - tPadTop;
            const tRectW = tBBoxW + tPadLeft + tPadRight;
            const tRectH = tBBoxH + tPadTop + tPadBottom;
            const tRayon = Math.max (0, Math.min (100, Number (tConfig.calque_radius_pct) || 0)) / 100 * Math.min (tRectW, tRectH) / 2;
	            if (tOmbreActive && tOmbreRaster)
	              {
	                const tCouleurOmbre = tNormaliseCouleur (tConfig.calque_ombre_couleur, OMBRE_OBJET_DEFAUT.couleur);
	                const tOpaciteOmbre = tNombre (tConfig.calque_ombre_opacite, OMBRE_OBJET_DEFAUT.opacite, 0, 1);
	                const tBlurOmbre = tNombre (tConfig.calque_ombre_blur, OMBRE_OBJET_DEFAUT.blur, 0, 80);
	                const tXOmbre = tNombre (tConfig.calque_ombre_x, OMBRE_OBJET_DEFAUT.x, -200, 200);
	                const tYOmbre = tNombre (tConfig.calque_ombre_y, OMBRE_OBJET_DEFAUT.y, -200, 200);
	                const tMargeOmbre = Math.ceil (tBlurOmbre * 4 + Math.max (Math.abs (tXOmbre), Math.abs (tYOmbre), 8));
	                const tFiltre = tAjouteFiltreOmbre (
	                  tGroupeDecor,
	                  tConfig.id,
	                  tCouleurOmbre,
	                  tOpaciteOmbre,
	                  tBlurOmbre,
	                  tXOmbre,
	                  tYOmbre,
	                  {x : tRectX, y : tRectY, w : tRectW, h : tRectH}
	                );
	                if (tOmbreRaster)
	                  {
	                    const tFiltreNode = tGroupeDecor.querySelector && tGroupeDecor.querySelector ("filter");
	                    const tDefsFiltre = tFiltreNode && tFiltreNode.parentNode;
	                    const tCloneSignature = tNoeud.cloneNode (true);
	                    if (tCloneSignature && tCloneSignature.removeAttribute)
	                      {
	                        tCloneSignature.removeAttribute ("transform");
	                        tCloneSignature.removeAttribute ("data-cogi-transform-original");
	                      }
	                    const tSourceHash = hachage_texte_simple_scene_client (
	                      new XMLSerializer ().serializeToString (tCloneSignature)
	                    );
	                    const tSignatureRaster = JSON.stringify ([
	                      "calque",
	                      Session.get ("scene_id") || "",
	                      tScene.svg_id || "",
	                      tConfig.id,
	                      tSourceHash,
	                      tRectX, tRectY, tRectW, tRectH, tRayon,
	                      tCouleurOmbre, tOpaciteOmbre, tBlurOmbre, tXOmbre, tYOmbre,
	                      tConfig.calque_ombre_zones_dessinees_seules !== false,
	                    ]);
	                    const tAssetExporte = url_asset_diffusion_scene_client (tConfig.calque_ombre_asset_url, tScene)
	                      || url_ombre_asset_scene_client (tConfig.calque_ombre_asset_file_id, tConfig.calque_ombre_asset_file_name);
	                    const tAssetPersistant = tAssetExporte;
	                    const tImageRaster = tAssetPersistant || image_raster_ombre_svg_scene_client (tSignatureRaster);
	                    if (tImageRaster)
	                      {
	                        const tImage = tDoc.createElementNS (tNs, "image");
	                        tImage.setAttribute ("x", String (tRectX - tMargeOmbre));
	                        tImage.setAttribute ("y", String (tRectY - tMargeOmbre));
	                        tImage.setAttribute ("width", String (tRectW + tMargeOmbre * 2));
	                        tImage.setAttribute ("height", String (tRectH + tMargeOmbre * 2));
	                        tImage.setAttribute ("preserveAspectRatio", "none");
	                        tImage.setAttribute ("href", tImageRaster);
	                        tImage.setAttributeNS ("http://www.w3.org/1999/xlink", "href", tImageRaster);
	                        tImage.style.pointerEvents = "none";
	                        tGroupeDecor.appendChild (tImage);
	                      }
                    else if (OMBRES_RASTER_SVG_SCENE_GENERATION_ACTIVE)
                      {
                        const tRaster = tFiltreNode && tSvgOmbreRasterCalque (
	                          tNoeud,
	                          tConfig,
	                          {x : tRectX, y : tRectY, w : tRectW, h : tRectH},
	                          tMargeOmbre,
	                          "ombre-raster-" + hachage_texte_simple_scene_client (tSignatureRaster),
	                          tFiltreNode,
	                          {x : tRectX, y : tRectY, w : tRectW, h : tRectH, r : tRayon}
	                        );
	                        if (tRaster)
	                          programme_raster_ombre_svg_scene_client (
	                            tSignatureRaster,
	                            tRaster.svgText,
	                            tRaster.width,
	                            tRaster.height,
	                            () =>
	                              {
	                                if (pTpl && pTpl._svgDoc === tDoc)
	                                  {
	                                    pTpl._calquesSvgReglagesSignature = null;
	                                    applique_reglages_calques_svg_tpl (pTpl);
	                                  }
	                              }
                          );
                      }
                    if (tDefsFiltre && tDefsFiltre.parentNode) tDefsFiltre.parentNode.removeChild (tDefsFiltre);
	                  }
	                else
	                  {
	                    const tOmbreGroupe = tDoc.createElementNS (tNs, "g");
	                    tOmbreGroupe.setAttribute ("filter", tFiltre);
	                    tOmbreGroupe.setAttribute ("overflow", "visible");
	                    tOmbreGroupe.style.pointerEvents = "none";
	                    tOmbreGroupe.style.overflow = "visible";
	                    if (tConfig.calque_ombre_zones_dessinees_seules !== false)
	                      {
	                        const tClone = tNoeud.cloneNode (true);
	                        tRetireIds (tClone);
	                        tNettoieEtatLectureCloneOmbre (tClone);
	                        tClone.removeAttribute ("transform");
	                        tClone.removeAttribute ("data-cogi-transform-original");
	                        tClone.removeAttribute ("display");
	                        tClone.setAttribute ("overflow", "visible");
	                        tClone.style.pointerEvents = "none";
	                        tClone.style.opacity = "1";
	                        tClone.style.display = "";
	                        tClone.style.overflow = "visible";
	                        tClone.style.visibility = "visible";
	                        tOmbreGroupe.appendChild (tClone);
	                      }
	                    else
	                      {
	                        const tOmbreRect = tDoc.createElementNS (tNs, "rect");
	                        tOmbreRect.setAttribute ("x", String (tRectX));
	                        tOmbreRect.setAttribute ("y", String (tRectY));
	                        tOmbreRect.setAttribute ("width", String (tRectW));
	                        tOmbreRect.setAttribute ("height", String (tRectH));
	                        tOmbreRect.setAttribute ("rx", String (tRayon));
	                        tOmbreRect.setAttribute ("ry", String (tRayon));
	                        tOmbreRect.setAttribute ("fill", "#000");
	                        tOmbreGroupe.appendChild (tOmbreRect);
	                      }
	                    tGroupeDecor.appendChild (tOmbreGroupe);
	                  }
	              }
            if (tFondActif)
              {
                const tFond = tDoc.createElementNS (tNs, "rect");
                tFond.setAttribute ("x", String (tRectX));
                tFond.setAttribute ("y", String (tRectY));
                tFond.setAttribute ("width", String (tRectW));
                tFond.setAttribute ("height", String (tRectH));
                tFond.setAttribute ("rx", String (tRayon));
                tFond.setAttribute ("ry", String (tRayon));
                tFond.setAttribute ("fill", tNormaliseCouleur (tConfig.calque_couleur, "#ffffff"));
                tFond.setAttribute ("fill-opacity", String (tNombre (tConfig.calque_fond_opacite, 1, 0, 1)));
                tGroupeDecor.appendChild (tFond);
              }
            tNoeud.parentNode.insertBefore (tGroupeDecor, tNoeud);
          }
      }
  }

function applique_decorations_cartes_tpl (pTpl)
  {
    const tDoc = pTpl && pTpl._svgDoc;
    if (!tDoc || !tDoc.documentElement) return;

    const tOnglet = gOngletDroite.get ();
    const tLectureActive = mode_lecture_scene_actif ();
    const tLectureFocusCarteId = gLectureSceneFocusCarte.get ();
    const tLectureSortieCarteId = pTpl._lectureZoomAltSortieEnCours
      ? pTpl._lectureZoomAltSortieCarteId
      : null;
    const tLecturePreparationCarteId = pTpl._lectureZoomAltMasquagesCarteId || null;
    const tLectureFocusCarteEffectifId = tLectureFocusCarteId || tLectureSortieCarteId || tLecturePreparationCarteId;
    const tLectureZoomAltFocus = !!(
      tLectureActive
      && tLectureFocusCarteEffectifId
      && (pTpl._lectureZoomAltActif || pTpl._lectureZoomAltSortieEnCours)
    );
    const tLectureZoomAltPreparation = !!(
      tLectureActive
      && !tLectureZoomAltFocus
      && tLecturePreparationCarteId
    );
    const tLectureZoomAltCycleDecor = !!(
      tLectureActive
      && pTpl._lectureZoomAltEtat
      && pTpl._lectureZoomAltEtat.phase
      && pTpl._lectureZoomAltEtat.phase !== LECTURE_ZOOM_ALT_PHASE.IDLE
    );
    const tLectureZoomAltSuspendDecor = !!(
      tLectureZoomAltFocus
      || tLectureZoomAltPreparation
      || tLectureZoomAltCycleDecor
    );
    const tLectureZoomAltPreserve = !!(tLectureZoomAltSuspendDecor || pTpl._lectureZoomAltCadreSortie);
    const tBullesVisibles = bulles_scene_visibles ();
    const tBullesVerrouillees = categorie_scene_verrouillee (CALQUES_CATEGORIE_BULLES);
    const tReglagesBullesPreview = brouillon_reglages_bulles_projet ();
    const tCarteEditorActive = carte_editor_est_actif ();
    const tCarteId = tCarteEditorActive ? gCarteSelectionnee.get () : null;
    const tCarteSelectionneeCalques = tLectureActive ? null : gBulleSelectionnee.get ();
    const tBrouillon = tCarteEditorActive ? brouillon_carte_courant () : null;
    const tBrouillonBulle = tLectureActive ? null : brouillon_bulle_courant ();
    const tCartes = cartes_scene_liste ().map ((pCarte) =>
      fusionne_carte_avec_brouillon_bulle_client (
        fusionne_carte_avec_brouillon_client (pCarte, tBrouillon),
        tBrouillonBulle
      )
    );
    const tPlanBulles = planifie_affectations_bulles_projet_scene_client (
      tCartes,
      {autogenLegacy : true}
    );

    let tCarteActive = null;
    let tCarteMasqueSelection = null;
    let tBulles = [];

    if (tLectureActive)
      {
        tCarteMasqueSelection = (!tLectureZoomAltSuspendDecor && tLectureFocusCarteEffectifId)
          ? (tCartes.find ((pCarte) => pCarte && pCarte._id === tLectureFocusCarteEffectifId) || null)
          : null;
        tBulles = pTpl._lectureBullesSuspendues
          ? []
          : tCartes.filter ((pCarte) => pCarte && !score_carte_terminee_lecture (Session.get ("scene_id"), pCarte._id));
      }
    else if (tCarteEditorActive)
      {
        tCarteActive = tCartes.find ((pCarte) => pCarte && pCarte._id === tCarteId)
          || (tBrouillon
            ? {
              _id : tBrouillon.carte_id,
              vue : tBrouillon.vue,
              couleur_fond : tBrouillon.couleur_fond,
              couleur_bulle : tBrouillon.couleur_bulle,
              opacite_bulle : tBrouillon.opacite_bulle,
              taille_bulle : tBrouillon.taille_bulle,
              position_bulle : tBrouillon.position_bulle,
            }
            : null);
        if (tCarteActive) tBulles = [tCarteActive];
      }
    else if (tBullesVisibles)
      {
        tBulles = tCartes;
        tCarteMasqueSelection = tBulles.find ((pCarte) => pCarte && pCarte._id === tCarteSelectionneeCalques) || null;
      }

    const tMasqueScene = mode_lecture_masque_scene_courant ();
    const tSelectionMaskFill = tLectureActive ? couleur_effective_mode_lecture_masque_scene_client (tMasqueScene) : "#08192d";
    const tSelectionMaskOpacity = tLectureActive ? tMasqueScene.opacite : 0.6;
    const tCarteSelectionneeId = tCarteMasqueSelection && tCarteMasqueSelection._id;
    const tVueSelectionnee = tCarteMasqueSelection
      ? normalise_vue_carte_client (tCarteMasqueSelection.vue)
      : null;
    const tAnimateBullesIn = !!(tLectureActive && !tLectureFocusCarteId && pTpl._lectureBullesAnimateIn);
    const tPreserveBullesRefreshPasses = Math.max (
      0,
      Math.floor (Number (pTpl._lecturePreserveBullesRefreshPasses) || 0)
    );
    const tPreserveBullesRefresh = !!pTpl._lecturePreserveBullesRefresh || tPreserveBullesRefreshPasses > 0;
    const tBullesDescripteurs = tBulles.map ((pCarte) =>
      {
        const tDesc = construit_descripteur_bulle_carte_client (pCarte, {planBulles : tPlanBulles});
        if (!tDesc) return null;
        if (tCarteSelectionneeId && pCarte && pCarte._id !== tCarteSelectionneeId && tVueSelectionnee)
          {
            const tBulleBBox = tDesc.mode === "custom" && tDesc.svg_largeur && tDesc.svg_hauteur
              ? {
                x : tDesc.x - (tDesc.svg_largeur / 2),
                y : tDesc.y - (tDesc.svg_hauteur / 2),
                w : tDesc.svg_largeur,
                h : tDesc.svg_hauteur,
              }
              : {
                x : tDesc.x - tDesc.rayon,
                y : tDesc.y - tDesc.rayon,
                w : tDesc.rayon * 2,
                h : tDesc.rayon * 2,
              };
            if (vue_carte_intersecte_bbox (tVueSelectionnee, tBulleBBox))
              return {...tDesc, masquee : true};
          }
        return tDesc;
      }).filter (Boolean);

    applique_decorations_cartes_racine (
      tDoc.documentElement,
      {
        sceneViewBox : vue_scene_decorations_cartes_tpl (pTpl) || pTpl._svgVBInitBrut || pTpl._svgVBNatifBrut || vue_svg_racine_client (tDoc.documentElement),
        cartesLayerTransform : transform_calque_cartes_tpl (pTpl),
        fondColor : tCarteActive ? couleur_fond_carte_effective_client (tCarteActive)
                  : ((tLectureActive || tCarteMasqueSelection) && tCarteMasqueSelection
                    ? couleur_fond_bulle_carte_effective_client (tCarteMasqueSelection)
                    : null),
        selectionMaskView : (tLectureActive || tVueSelectionnee) ? tVueSelectionnee : null,
        selectionMaskFill : tSelectionMaskFill,
        selectionMaskOpacity : tSelectionMaskOpacity,
        reglagesBulles : tReglagesBullesPreview,
        selectionMaskOutDelayMs : delai_retour_decor_focus_carte_ms_lecture (pTpl),
        animateSelectionMask : tLectureActive,
        animateBullesZoom : tLectureActive,
        animateBullesIn : tAnimateBullesIn,
        preserveZoomAlt : tLectureZoomAltPreserve,
        preserveBulles : tLectureZoomAltSuspendDecor || tPreserveBullesRefresh,
        bulles : tBullesDescripteurs,
        interactiveCardId : tCarteEditorActive ? tCarteId : null,
        interactiveAllBulles : tLectureActive
          ? !tLectureFocusCarteEffectifId
          : (tBullesVisibles && !tBullesVerrouillees),
        interactiveCursor : tLectureActive ? "pointer" : "grab",
      }
    );
    dessine_surbrillance_categorie_bulles_svg (
      tDoc,
      cartes_ids_surbrillance_categorie_calques_scene ()
    );
    synchronise_items_interface_svg_tpl (pTpl);
    applique_decorations_scene_reussies_tpl (pTpl);
    if (!tLectureZoomAltSuspendDecor)
      synchronise_flottement_bulles_lecture_tpl (
        pTpl,
        tBullesDescripteurs.filter ((pBulle) => pBulle && !pBulle.masquee)
      );
    if (tLectureActive)
      {
        applique_masquages_carte_tpl (pTpl);
        reapplique_etat_pedago_lecture_tpl (pTpl);
      }
    if (tAnimateBullesIn) pTpl._lectureBullesAnimateIn = false;
    if (tPreserveBullesRefreshPasses > 1)
      {
        pTpl._lecturePreserveBullesRefresh = true;
        pTpl._lecturePreserveBullesRefreshPasses = tPreserveBullesRefreshPasses - 1;
      }
    else if (tPreserveBullesRefresh)
      {
        pTpl._lecturePreserveBullesRefresh = false;
        pTpl._lecturePreserveBullesRefreshPasses = 0;
      }
  }

function reconstruit_cache_bbox_svg (pTpl)
  {
    const tDoc = pTpl && pTpl._svgDoc;
    const tRoot = tDoc && tDoc.documentElement;
    const tIds = pTpl && pTpl._idsPertinents;
    const tCache = new Map ();
    if (!pTpl)
      return tCache;
    if (!tDoc || !tRoot || !tIds || !tIds.size)
      {
        pTpl._bboxParId = tCache;
        return tCache;
      }

    for (const tId of tIds)
      {
        const tElt = tDoc.getElementById (tId);
        tCache.set (tId, bbox_racine_pour_element (tRoot, tElt));
      }
    pTpl._bboxParId = tCache;
    return tCache;
  }

function bbox_racine_cachee_pour_id (pTpl, pRoot, pId)
  {
    const tCache = pTpl && pTpl._bboxParId;
    if (tCache && tCache.has (pId))
      return tCache.get (pId) || null;
    const tDoc = pRoot && pRoot.ownerDocument;
    const tElt = pId && tDoc && typeof tDoc.getElementById === "function"
      ? tDoc.getElementById (pId)
      : null;
    const tBBox = bbox_racine_pour_element (pRoot, tElt);
    if (tCache) tCache.set (pId, tBBox || null);
    return tBBox || null;
  }

function bbox_racine_pour_element (pRoot, pElt)
  {
    if (!pRoot || !pElt || typeof pElt.getBBox !== "function") return null;
    let tBBox;
    try { tBBox = pElt.getBBox (); }
    catch (e) { return null; }
    if (!tBBox || (!tBBox.width && !tBBox.height)) return null;
    try
      {
        const tEcranElt = pElt.getScreenCTM && pElt.getScreenCTM ();
        const tEcranRoot = pRoot.getScreenCTM && pRoot.getScreenCTM ();
        if (!tEcranElt || !tEcranRoot) return null;
        const tM = tEcranRoot.inverse ().multiply (tEcranElt);
        const tCoins = [
          {x : tBBox.x, y : tBBox.y},
          {x : tBBox.x + tBBox.width, y : tBBox.y},
          {x : tBBox.x, y : tBBox.y + tBBox.height},
          {x : tBBox.x + tBBox.width, y : tBBox.y + tBBox.height},
        ].map ((pPoint) =>
          ({
            x : pPoint.x * tM.a + pPoint.y * tM.c + tM.e,
            y : pPoint.x * tM.b + pPoint.y * tM.d + tM.f,
          }));
        const tXs = tCoins.map ((pPoint) => pPoint.x);
        const tYs = tCoins.map ((pPoint) => pPoint.y);
        const tMinX = Math.min (...tXs);
        const tMinY = Math.min (...tYs);
        const tMaxX = Math.max (...tXs);
        const tMaxY = Math.max (...tYs);
        return {x : tMinX, y : tMinY, w : tMaxX - tMinX, h : tMaxY - tMinY};
      }
    catch (e) { return null; }
  }

function vue_carte_intersecte_bbox (pVue, pBBox)
  {
    if (!pVue || !pBBox) return false;
    return pBBox.x + pBBox.w >= pVue.x
        && pBBox.y + pBBox.h >= pVue.y
        && pBBox.x <= pVue.x + pVue.w
        && pBBox.y <= pVue.y + pVue.h;
  }

function compare_calques_objets_carte (pA, pB, pOrdreParId)
  {
    const tAOrdonne = pOrdreParId && pOrdreParId.has (pA.id);
    const tBOrdonne = pOrdreParId && pOrdreParId.has (pB.id);
    if (tAOrdonne && tBOrdonne)
      {
        const tDelta = pOrdreParId.get (pA.id) - pOrdreParId.get (pB.id);
        if (tDelta) return tDelta;
      }
    else if (tAOrdonne !== tBOrdonne)
      return tAOrdonne ? -1 : 1;
    return pA.cle_tri.localeCompare (pB.cle_tri, "fr", {sensitivity : "base"});
  }

function cartes_categories_visibles_dans_vue (pTpl, pVue)
  {
    const tSceneId = Session.get ("scene_id");
    if (!tSceneId || !pVue) return [];
    const tScene = Scenes.findOne (tSceneId);
    const tObjets = svg_objets_scene_normalises (tScene);
    const tDoc = pTpl && pTpl._svgDoc;
    const tRoot = tDoc && tDoc.documentElement;
    if (!tObjets || !tDoc || !tRoot) return [];
    const tMasques = new Set (ids_masques_carte_avec_associes_scene (carte_arbre_ids_masques_effectifs (), tScene));
    const tOrdreObjets = carte_arbre_ordre_objets_effectif ();
    const tOrdreObjetsParId = new Map (tOrdreObjets.map ((pId, pIndex) => [pId, pIndex]));
    const tSel = gCarteArbreSelectionne.get ();
    const tSelCat = gCarteArbreCategorieSelectionnee.get ();
    const tListe = [];

    for (const tNom of CATEGORIES_PERTINENTES)
      {
        const tBrut = tObjets[tNom];
        if (!Array.isArray (tBrut) || !tBrut.length) continue;
        const tCategorieObjets = tNom === "obj";
        const tCalques = [];
        for (const tCalque of tBrut)
          {
            if (!tCalque || !tCalque.id) continue;
            const tBBox = bbox_racine_cachee_pour_id (pTpl, tRoot, tCalque.id);
            if (!tBBox || !vue_carte_intersecte_bbox (pVue, tBBox)) continue;
            tCalques.push (
              {
                id : tCalque.id,
                nom : nom_affiche_calque_arbre (tCalque["data-name"], tNom) || tCalque.id,
                cle_tri : cle_tri_calque (tCalque["data-name"], tNom),
                selectionne : tCalque.id === tSel,
                masque : tMasques.has (tCalque.id),
                toggle_label : tMasques.has (tCalque.id) ? "Montrer" : "Masquer",
                toggle_class : tMasques.has (tCalque.id) ? "est_actif" : "",
                sortable_class : tCategorieObjets ? "js_carte_tree_obj_item est_reordonnable" : "",
              }
            );
          }

        if (!tCalques.length) continue;
        tCalques.sort ((a, b) => tCategorieObjets
          ? compare_calques_objets_carte (a, b, tOrdreObjetsParId)
          : a.cle_tri.localeCompare (b.cle_tri, "fr", {sensitivity : "base"}));
        tListe.push (
          {
            nom : tNom,
            ouverte : gCarteCategoriesOuvertes.get (tNom) !== false,
            selectionnee : tSelCat === tNom,
            sortable_class : tCategorieObjets ? "js_carte_tree_obj_sortable" : "",
            calques : tCalques,
          }
        );
      }

    return tListe;
  }

function construit_liste_visible_cartes (pTpl)
  {
    const tVue = vue_reference_arbre_cartes (pTpl);
    const tCategories = cartes_categories_visibles_dans_vue (pTpl, tVue);
    if (!tCategories.length) return null;
    const tItems = [];
    for (const tCategorie of tCategories)
      {
        tItems.push ({type : "categorie", nom : tCategorie.nom});
        if (!tCategorie.ouverte) continue;
        for (const tCalque of tCategorie.calques)
          tItems.push ({type : "calque", id : tCalque.id, categorie : tCategorie.nom});
      }
    return {categories : tCategories.map ((pCat) => ({nom : pCat.nom})), items : tItems};
  }

function cartes_ids_surbrillance_scene (pTpl)
  {
    if (!carte_arbre_est_actif ()) return [];
    const tSel = gCarteArbreSelectionne.get ();
    if (tSel) return [tSel];
    const tSelCat = gCarteArbreCategorieSelectionnee.get ();
    if (!tSelCat) return [];
    const tVue = vue_reference_arbre_cartes (pTpl);
    const tCategories = cartes_categories_visibles_dans_vue (pTpl, tVue);
    const tCategorie = tCategories.find ((pCategorie) => pCategorie && pCategorie.nom === tSelCat);
    return tCategorie ? tCategorie.calques.map ((pCalque) => pCalque.id) : [];
  }

function cartes_ids_surbrillance_categorie_calques_scene ()
  {
    if (carte_arbre_est_actif ()) return [];
    if (gCalqueSelectionne.get () || gBulleSelectionnee.get ()) return [];
    if (gCategorieSelectionnee.get () !== CALQUES_CATEGORIE_BULLES) return [];
    return cartes_scene_liste ()
      .map ((pCarte) => pCarte && pCarte._id)
      .filter (Boolean);
  }

function id_selection_scene_active ()
  {
    return carte_arbre_est_actif () ? gCarteArbreSelectionne.get () : gCalqueSelectionne.get ();
  }

function categorie_selection_scene_active ()
  {
    return carte_arbre_est_actif ()
      ? gCarteArbreCategorieSelectionnee.get ()
      : gCategorieSelectionnee.get ();
  }

function bulle_selection_scene_active ()
  {
    return (!carte_editor_est_actif ()
         && bulles_scene_visibles ())
      ? gBulleSelectionnee.get ()
      : null;
  }

function cible_zoom_selection_scene_active ()
  {
    const tId = id_selection_scene_active ();
    if (carte_arbre_calques_est_actif () && tId) return {type : "calque", id : tId};

    const tCarteId = bulle_selection_scene_active ();
    if (tCarteId) return {type : "carte", id : tCarteId};

    return tId ? {type : "calque", id : tId} : null;
  }

function zoom_sur_selection_scene_active (pTpl)
  {
    const tCible = cible_zoom_selection_scene_active ();
    if (!pTpl || !tCible) return false;
    if (tCible.type === "carte")
      {
        zoom_sur_carte (pTpl, tCible.id);
        return true;
      }
    zoom_sur_id (pTpl, tCible.id);
    return true;
  }

function selectionne_carte_arbre_categorie (pNom)
  {
    gCarteArbreSelectionne.set (null);
    gCarteArbreCategorieSelectionnee.set (pNom || null);
  }

function selectionne_carte_arbre_calque (pId)
  {
    gCarteArbreCategorieSelectionnee.set (null);
    gCarteArbreSelectionne.set (pId || null);
  }

function cartes_toggle_masquage_brouillon (pId)
  {
    const tBrouillon = carte_arbre_brouillon_courant ();
    if (!tBrouillon || !pId) return;
    const tIds = normalise_ids_masques_carte_client (tBrouillon.ids_masques);
    const tExiste = tIds.includes (pId);
    const tNouveauBrouillon =
      {
        ...tBrouillon,
        ids_masques : tExiste ? tIds.filter ((tCourant) => tCourant !== pId) : [...tIds, pId],
      };
    if (carte_editor_est_actif ())
      gCarteBrouillon.set (tNouveauBrouillon);
    else if (carte_arbre_calques_est_actif ())
      gBulleBrouillon.set (tNouveauBrouillon);
  }

function maj_ordre_objets_brouillon_carte (pIdsVisiblesOrdonnes)
  {
    const tBrouillon = carte_arbre_brouillon_courant ();
    const tIdsVisibles = normalise_ordre_objets_carte_client (pIdsVisiblesOrdonnes);
    if (!tBrouillon || !tIdsVisibles.length) return false;
    const tIdsVisiblesSet = new Set (tIdsVisibles);
    const tOrdreCourant = normalise_ordre_objets_carte_client (tBrouillon.ordre_objets)
      .filter ((pId) => !tIdsVisiblesSet.has (pId));
    const tOrdreObjets = [...tIdsVisibles, ...tOrdreCourant];
    if (signature_ordre_objets_carte_client (tOrdreObjets)
     === signature_ordre_objets_carte_client (tBrouillon.ordre_objets))
      return false;

    const tNouveauBrouillon = {...tBrouillon, ordre_objets : tOrdreObjets};
    if (carte_editor_est_actif ())
      gCarteBrouillon.set (tNouveauBrouillon);
    else if (carte_arbre_calques_est_actif ())
      gBulleBrouillon.set (tNouveauBrouillon);
    else
      return false;
    return true;
  }

function applique_vue_carte_dans_scene (pTpl, pVue, pAnime = false)
  {
    const tVue = normalise_vue_carte_client (pVue);
    if (!pTpl || !pTpl._svgVB || !pTpl._svgApplique || !tVue) return false;
    const tVueSvg = vue_camera_svg_depuis_vue_brute_scene (pTpl, tVue) || tVue;
    pTpl._svgVBBrut = {...tVue};
    if (pAnime)
      {
        anime_viewbox_vers (pTpl._svgVB, tVueSvg, pTpl._svgApplique, pTpl);
        return true;
      }
    annule_animation_viewbox_tpl (pTpl);
    pTpl._svgVB.x = tVueSvg.x;
    pTpl._svgVB.y = tVueSvg.y;
    pTpl._svgVB.w = tVueSvg.w;
    pTpl._svgVB.h = tVueSvg.h;
    pTpl._svgApplique ();
    return true;
  }

function abandon_brouillon_carte_si_necessaire (pCarteIdCible = null, pOpt = {})
  {
    if (pOpt.force) return true;
    if (!carte_editor_est_actif ()) return true;
    const tCarteCouranteId = gCarteSelectionnee.get ();
    if (pCarteIdCible && tCarteCouranteId && pCarteIdCible === tCarteCouranteId)
      return true;
    if (!carte_brouillon_modifie ()) return true;
    return confirm ("Abandonner les modifications non enregistrées de cette carte ?");
  }

function rebase_brouillon_carte_apres_sauvegarde (pCarteId, pSauvegarde)
  {
    const tBrouillon = gCarteBrouillon.get ();
    if (!tBrouillon || tBrouillon.carte_id !== pCarteId) return;

    const tNomSauve = typeof (pSauvegarde && pSauvegarde.nom) === "string"
      ? pSauvegarde.nom
      : "";
    const tVueSauvee = normalise_vue_carte_client (pSauvegarde && pSauvegarde.vue, tBrouillon.vue);
    const tIdsSauves = normalise_ids_masques_carte_client (pSauvegarde && pSauvegarde.ids_masques);
    const tOrdreObjetsSauve = Object.prototype.hasOwnProperty.call (pSauvegarde || {}, "ordre_objets")
      ? normalise_ordre_objets_carte_client (pSauvegarde.ordre_objets)
      : normalise_ordre_objets_carte_client (tBrouillon.ordre_objets);
    const tCouleurFondSauvee = normalise_couleur_carte_client (
      pSauvegarde && pSauvegarde.couleur_fond,
      tBrouillon.couleur_fond
    );
    const tCouleurBulleSauvee = couleur_bulle_carte_effective_client (
      {
        couleur_bulle : pSauvegarde && pSauvegarde.couleur_bulle,
        carte_id : pCarteId,
      },
      pCarteId
    );
    const tOpaciteBulleSauvee = normalise_opacite_bulle_carte_client (
      pSauvegarde && pSauvegarde.opacite_bulle,
      tBrouillon.opacite_bulle
    );
    const tTailleBulleSauvee = normalise_taille_bulle_carte_client (
      pSauvegarde && pSauvegarde.taille_bulle,
      tBrouillon.taille_bulle
    );
    const tPositionBulleSauvee = normalise_position_bulle_carte_client (
      pSauvegarde && pSauvegarde.position_bulle,
      tBrouillon.position_bulle
    );
    const tInterfaceCarteSauvee = Object.prototype.hasOwnProperty.call (pSauvegarde || {}, "reglages_interface_carte")
      ? reglages_interface_carte_pour_stockage (pSauvegarde.reglages_interface_carte)
      : reglages_interface_carte_pour_stockage (tBrouillon.reglages_interface_carte || {});

    gCarteBrouillon.set (
      {
        ...tBrouillon,
        couleur_fond : tCouleurFondSauvee,
        couleur_bulle : tCouleurBulleSauvee,
        opacite_bulle : tOpaciteBulleSauvee,
        taille_bulle : tTailleBulleSauvee,
        position_bulle : tPositionBulleSauvee,
        reglages_interface_carte : tInterfaceCarteSauvee,
        ordre_objets : tOrdreObjetsSauve,
        base_nom : tNomSauve,
        base_vue : tVueSauvee,
        base_ids_masques : tIdsSauves,
        base_ordre_objets : tOrdreObjetsSauve,
        base_couleur_fond : tCouleurFondSauvee,
        base_couleur_bulle : tCouleurBulleSauvee,
        base_opacite_bulle : tOpaciteBulleSauvee,
        base_taille_bulle : tTailleBulleSauvee,
        base_position_bulle : tPositionBulleSauvee,
        base_reglages_interface_carte : tInterfaceCarteSauvee,
      }
    );
    rebase_brouillon_bulle_apres_sauvegarde (pCarteId, pSauvegarde);
    reinitialise_notice_cadrage_carte ();
  }

function selectionne_carte_creee (pTpl, pCarteId, pOpt = {})
  {
    if (!pCarteId) return;

    let tStopTimer = null;
    const tComputation = Tracker.autorun ((pComputation) =>
      {
        const tCarte = Cartes.findOne (pCarteId);
        if (!tCarte) return;
        pComputation.stop ();
        if (tStopTimer) clearTimeout (tStopTimer);

        ecrit_ouverture_categorie_scene (CALQUES_CATEGORIE_BULLES, true);
        selectionne_bulle_calque (pCarteId, {force : true});
      });

    tStopTimer = setTimeout (() =>
      {
        if (!tComputation.stopped) tComputation.stop ();
      }, 5000);
  }

function cree_carte_depuis_vue_courante (pTpl, pOpt = {})
  {
    const tSceneId = Session.get ("scene_id");
    const tScene = tSceneId ? Scenes.findOne (tSceneId) : null;
    const tVueCamera = vue_camera_courante_scene (pTpl);
    const tVue = vue_nouvelle_carte_centree_scene (tVueCamera);
    if (!tSceneId || !tScene || !tVue)
      {
        alert ("Le SVG de la scene doit etre charge avant de creer une carte.");
        return;
      }

    const tCouleurFond = CARTE_COULEUR_FOND_DEFAUT;
    const tCarteTemp =
      {
        _id : `nouvelle-carte-${Date.now ()}`,
        ordre : cartes_scene_liste ().length + 1,
        vue : tVue,
        couleur_bulle : genere_couleur_bulle_aleatoire_client (),
      };
    const tPlanBulles = plan_bulles_projet_scene_editee_client ({carteSupplementaire : tCarteTemp});
    const tConfigBulle = tPlanBulles.hasCustomFiles
      ? configuration_bulle_carte_effective_client (tCarteTemp, {planBulles : tPlanBulles, seed : tCarteTemp._id})
      : {
        mode : "default",
        svg_bulle_id : null,
        couleur_bulle : tCarteTemp.couleur_bulle,
      };
    const tOpaciteBulle = tConfigBulle.mode === "custom"
      ? CARTE_OPACITE_BULLE_CUSTOM_DEFAUT
      : CARTE_OPACITE_BULLE_DEFAUT;
    const tTailleBulle = taille_bulle_carte_fallback_client (
      tCarteTemp,
      {configuration : tConfigBulle}
    );
    const tPositionBulle = position_bulle_carte_initiale_client (tVue, tTailleBulle);
    const tRotationBulle = rotation_bulle_aleatoire_creation_client (projet_courant_scene ());
    const tCouleurBulle = tConfigBulle.couleur_bulle;
    const tSvgBulleId = tConfigBulle.svg_bulle_id;

    new Promise ((resolve, reject) =>
      {
        Meteor.call (
          "cree_carte_scene",
          {
            scene_id : tSceneId,
            vue : tVue,
            ids_masques : [],
            ordre_objets : [],
            couleur_fond : tCouleurFond,
            couleur_bulle : tCouleurBulle,
            svg_bulle_id : tSvgBulleId,
            opacite_bulle : tOpaciteBulle,
            taille_bulle : tTailleBulle,
            rotation_bulle : tRotationBulle,
            position_bulle : tPositionBulle,
          },
          (pErr, pCarteId) =>
            {
              if (pErr) reject (pErr);
              else      resolve (pCarteId);
            }
        );
      })
      .then ((pCarteId) =>
        {
          selectionne_carte_creee (pTpl, pCarteId, pOpt);
        })
      .catch ((pErr) =>
        {
          alert ("Erreur : " + ((pErr && (pErr.reason || pErr.message)) || pErr));
        });
  }

function enregistre_carte_brouillon (pTpl)
  {
    const tCarte = carte_courante_scene ();
    const tBrouillon = brouillon_carte_courant ();
    if (!tCarte || !tBrouillon || !tBrouillon.vue) return;
    const tNomSauve = tBrouillon.nom || "";
    const tVueSauvee = normalise_vue_carte_client (tBrouillon.vue);
    const tIdsSauves = normalise_ids_masques_carte_client (tBrouillon.ids_masques);
    const tOrdreObjetsSauve = normalise_ordre_objets_carte_client (tBrouillon.ordre_objets);
    const tCouleurFondSauvee = couleur_fond_carte_effective_client (tBrouillon);
    const tSvgBulleIdSauve = normalise_svg_bulle_id_carte_client (tBrouillon.svg_bulle_id);
    const tCouleurBulleSauvee = couleur_bulle_carte_effective_client (tBrouillon, tCarte._id);
    const tOpaciteBulleSauvee = opacite_bulle_carte_effective_client (tBrouillon);
    const tTailleBulleSauvee = taille_bulle_carte_effective_client (tBrouillon);
    const tRotationBulleSauvee = rotation_bulle_carte_effective_client (tBrouillon);
    const tPositionBulleSauvee = normalise_position_bulle_carte_pour_vue_client (
      tBrouillon.position_bulle,
      tVueSauvee
    );
    const tInterfaceCarteSauvee = reglages_interface_carte_pour_stockage (
      tBrouillon.reglages_interface_carte || tCarte.reglages_interface_carte || {}
    );

    new Promise ((resolve, reject) =>
      {
        Meteor.call (
          "maj_carte_scene",
          {
            carte_id : tCarte._id,
            nom : tNomSauve,
            vue : tVueSauvee,
            ids_masques : tIdsSauves,
            ordre_objets : tOrdreObjetsSauve,
            couleur_fond : tCouleurFondSauvee,
            couleur_bulle : tCouleurBulleSauvee,
            svg_bulle_id : tSvgBulleIdSauve,
            opacite_bulle : tOpaciteBulleSauvee,
            taille_bulle : tTailleBulleSauvee,
            rotation_bulle : tRotationBulleSauvee,
            position_bulle : tPositionBulleSauvee,
            reglages_interface_carte : tInterfaceCarteSauvee,
          },
          (pErr) =>
            {
              if (pErr) reject (pErr);
              else      resolve ();
            }
        );
      })
      .then (() =>
        {
          gCarteAdoptCurrentSuggested.set (false);
          rebase_brouillon_carte_apres_sauvegarde (
            tCarte._id,
            {
              nom : tNomSauve,
              vue : tVueSauvee,
              ids_masques : tIdsSauves,
              ordre_objets : tOrdreObjetsSauve,
              couleur_fond : tCouleurFondSauvee,
              couleur_bulle : tCouleurBulleSauvee,
              svg_bulle_id : tSvgBulleIdSauve,
              opacite_bulle : tOpaciteBulleSauvee,
              taille_bulle : tTailleBulleSauvee,
              position_bulle : tPositionBulleSauvee,
              reglages_interface_carte : tInterfaceCarteSauvee,
            }
          );
        })
      .catch ((pErr) =>
        {
          alert ("Erreur : " + ((pErr && (pErr.reason || pErr.message)) || pErr));
        });
  }

function maj_brouillon_bulle_calque (pPatch)
  {
    const tBrouillon = brouillon_bulle_courant ();
    if (!tBrouillon) return false;
    gBulleBrouillon.set ({...tBrouillon, ...(pPatch || {})});
    return true;
  }

function maj_brouillon_nom_bulle_calque (pNom)
  {
    const tBrouillon = brouillon_bulle_courant ();
    if (!tBrouillon) return false;
    return maj_brouillon_bulle_calque ({nom : typeof pNom === "string" ? pNom : ""});
  }

function maj_brouillon_vue_bulle_calque (pPatch)
  {
    const tBrouillon = brouillon_bulle_courant ();
    if (!tBrouillon || !tBrouillon.vue) return false;
    const tVue = normalise_vue_carte_client ({...tBrouillon.vue, ...(pPatch || {})}, tBrouillon.vue);
    if (!tVue || tVue.w < CARTE_VUE_MIN_TAILLE || tVue.h < CARTE_VUE_MIN_TAILLE) return false;
    return maj_brouillon_bulle_calque (
      {
        vue : tVue,
        position_bulle : normalise_position_bulle_carte_pour_vue_client (
          tBrouillon.position_bulle,
          tVue
        ),
      }
    );
  }

function deplace_brouillon_bulle_calque (pDx, pDy)
  {
    const tBrouillon = brouillon_bulle_courant ();
    if (!tBrouillon || !tBrouillon.vue) return false;
    const tDx = Number (pDx);
    const tDy = Number (pDy);
    if (!Number.isFinite (tDx) || !Number.isFinite (tDy)) return false;

    const tVue = normalise_vue_carte_client (
      {
        ...tBrouillon.vue,
        x : tBrouillon.vue.x + tDx,
        y : tBrouillon.vue.y + tDy,
      },
      tBrouillon.vue
    );
    if (!tVue) return false;

    const tPositionBrute = normalise_position_bulle_carte_client (tBrouillon.position_bulle);
    const tPositionDeplacee = tPositionBrute
      ? {
          x : tPositionBrute.x + tDx,
          y : tPositionBrute.y + tDy,
        }
      : null;

    return maj_brouillon_bulle_calque (
      {
        vue : tVue,
        position_bulle : tPositionDeplacee
          ? normalise_position_bulle_carte_pour_vue_client (tPositionDeplacee, tVue)
          : null,
      }
    );
  }

function enregistre_brouillon_bulle_calque (pTpl)
  {
    const tCarteId = gBulleSelectionnee.get ();
    const tCarte = tCarteId ? Cartes.findOne (tCarteId) : null;
    const tBrouillon = brouillon_bulle_courant ();
    if (!tCarte || !tBrouillon || !tBrouillon.vue) return;

    const tVueSauvee = normalise_vue_carte_client (tBrouillon.vue, tCarte.vue);
    const tIdsSauves = normalise_ids_masques_carte_client (tBrouillon.ids_masques);
    const tOrdreObjetsSauve = normalise_ordre_objets_carte_client (tBrouillon.ordre_objets);
    const tNomSauve = typeof tBrouillon.nom === "string" ? tBrouillon.nom : "";
    const tCouleurFondSauvee = Object.prototype.hasOwnProperty.call (tBrouillon, "couleur_fond")
      ? normalise_couleur_carte_client (
        tBrouillon.couleur_fond,
        couleur_fond_carte_effective_client (tCarte)
      )
      : null;
    const tSvgBulleIdSauve = normalise_svg_bulle_id_carte_client (tBrouillon.svg_bulle_id);
    const tCouleurBulleSauvee = couleur_bulle_carte_effective_client (tBrouillon, tCarte._id);
    const tOpaciteBulleSauvee = opacite_bulle_carte_effective_client (tBrouillon);
    const tTailleBulleSauvee = taille_bulle_carte_effective_client (tBrouillon);
    const tRotationBulleSauvee = rotation_bulle_carte_effective_client (tBrouillon);
    const tPositionBulleSauvee = normalise_position_bulle_carte_pour_vue_client (
      tBrouillon.position_bulle,
      tVueSauvee
    );
    const tInterfaceCarteSauvee = reglages_interface_carte_pour_stockage (
      tBrouillon.reglages_interface_carte || tCarte.reglages_interface_carte || {}
    );

    new Promise ((resolve, reject) =>
      {
        Meteor.call (
          "maj_carte_scene",
          {
            carte_id : tCarte._id,
            nom : tNomSauve,
            vue : tVueSauvee,
            ids_masques : tIdsSauves,
            ordre_objets : tOrdreObjetsSauve,
            ...(tCouleurFondSauvee ? {couleur_fond : tCouleurFondSauvee} : {}),
            couleur_bulle : tCouleurBulleSauvee,
            svg_bulle_id : tSvgBulleIdSauve,
            opacite_bulle : tOpaciteBulleSauvee,
            taille_bulle : tTailleBulleSauvee,
            rotation_bulle : tRotationBulleSauvee,
            position_bulle : tPositionBulleSauvee,
            reglages_interface_carte : tInterfaceCarteSauvee,
          },
          (pErr) =>
            {
              if (pErr) reject (pErr);
              else      resolve ();
            }
        );
      })
      .then (() =>
        {
          const tSauvegarde =
            {
              nom : tNomSauve,
              vue : tVueSauvee,
              ids_masques : tIdsSauves,
              ordre_objets : tOrdreObjetsSauve,
              ...(tCouleurFondSauvee ? {couleur_fond : tCouleurFondSauvee} : {}),
              couleur_bulle : tCouleurBulleSauvee,
              svg_bulle_id : tSvgBulleIdSauve,
              opacite_bulle : tOpaciteBulleSauvee,
              taille_bulle : tTailleBulleSauvee,
              rotation_bulle : tRotationBulleSauvee,
              position_bulle : tPositionBulleSauvee,
              reglages_interface_carte : tInterfaceCarteSauvee,
            };
          rebase_brouillon_bulle_apres_sauvegarde (tCarte._id, tSauvegarde);
          synchronise_brouillon_carte_apres_sauvegarde_bulle (tCarte._id, tSauvegarde);
        })
      .catch ((pErr) =>
        {
          alert ("Erreur : " + ((pErr && (pErr.reason || pErr.message)) || pErr));
        });
  }

function evenement_vise_controle_edition (pEvent)
  {
    const tCible = pEvent && pEvent.target;
    if (!tCible || typeof tCible.closest !== "function") return false;
    if (tCible.isContentEditable) return true;
    return !!tCible.closest (
      "input, textarea, select, button, a, [contenteditable], .js_arbre_cartes"
    );
  }

function attache_raccourci_espace_cartes_doc (pDoc, pHandler)
  {
    if (!pDoc || typeof pDoc.addEventListener !== "function" || typeof pHandler !== "function")
      return;
    if (pDoc._cogi_cartes_space_handler === pHandler) return;
    if (pDoc._cogi_cartes_space_handler)
      pDoc.removeEventListener ("keydown", pDoc._cogi_cartes_space_handler);
    pDoc.addEventListener ("keydown", pHandler);
    pDoc._cogi_cartes_space_handler = pHandler;
  }

function detache_raccourci_espace_cartes_doc (pDoc, pHandler = null)
  {
    if (!pDoc || typeof pDoc.removeEventListener !== "function") return;
    const tHandler = pHandler || pDoc._cogi_cartes_space_handler;
    if (!tHandler) return;
    pDoc.removeEventListener ("keydown", tHandler);
    if (!pHandler || pDoc._cogi_cartes_space_handler === tHandler)
      pDoc._cogi_cartes_space_handler = null;
  }

function maj_brouillon_coordonnees_carte (pTpl, pPatch)
  {
    const tBrouillon = brouillon_carte_courant ();
    if (!tBrouillon || !tBrouillon.vue) return false;
    const tCoordonnees =
      {
        x : tBrouillon.vue.x,
        y : tBrouillon.vue.y,
        zoom : zoom_depuis_vue_carte (pTpl, tBrouillon.vue),
        ...(pPatch || {}),
      };
    const tVue = vue_depuis_coordonnees_carte (pTpl, tCoordonnees, tBrouillon.vue);
    if (!tVue) return false;
    arme_notice_cadrage_carte ();
    gCarteAdoptCurrentSuggested.set (true);
    gCarteBrouillon.set ({...tBrouillon, vue : tVue});
    applique_vue_carte_dans_scene (pTpl, tVue, false);
    return true;
  }

function adopte_cadrage_courant_dans_brouillon (pTpl)
  {
    const tBrouillon = brouillon_carte_courant ();
    const tVue = vue_camera_courante_scene (pTpl);
    if (!tBrouillon || !tVue) return false;
    gCarteAdoptCurrentSuggested.set (false);
    reinitialise_notice_cadrage_carte ();
    gCarteBrouillon.set ({...tBrouillon, vue : tVue});
    return true;
  }

function cartes_reorder_ids_dom (pContainer, pSelector = ".js_carte_item", pAttr = "data-id")
  {
    if (!pContainer) return [];
    return Array.from (pContainer.querySelectorAll (pSelector))
      .map ((pElt) => pElt.getAttribute (pAttr))
      .filter (Boolean);
  }

function cartes_reorder_persiste_ids (pIds)
  {
    const tSceneId = Session.get ("scene_id");
    const tIds = Array.isArray (pIds) ? pIds.filter (Boolean) : [];
    if (!tSceneId || !tIds.length) return;
    Meteor.call ("reordonne_cartes_scene", tSceneId, tIds,
      (pErr) =>
        {
          if (pErr) alert ("Erreur : " + (pErr.reason || pErr.message));
        });
  }

function calques_cartes_reorder_ids_dom (pContainer)
  {
    return cartes_reorder_ids_dom (
      pContainer,
      ".js_calques_tree_carte_item",
      "data-bulle-carte-id"
    );
  }

function calques_cartes_reorder_cleanup ()
  {
    if (gCalquesCartesReorder.sortable)
      {
        try { gCalquesCartesReorder.sortable.destroy (); }
        catch (e) {}
      }
    if (gCalquesCartesReorder.container)
      gCalquesCartesReorder.container.classList.remove ("arbre_cartes_reorder_actif");
    document.body.style.userSelect = "";
    document.body.style.cursor = "";
    gCalquesCartesReorder.sortable = null;
    gCalquesCartesReorder.container = null;
    gCalquesCartesReorder.tpl = null;
  }

function calques_cartes_reorder_sync (pTpl)
  {
    const tActif = gOngletDroite.get () === "calques";
    const tContainer = tActif && pTpl ? pTpl.find (".js_calques_tree_cartes_sortable") : null;
    if (!tContainer)
      {
        if (gCalquesCartesReorder.tpl === pTpl) calques_cartes_reorder_cleanup ();
        return;
      }

    const tIds = calques_cartes_reorder_ids_dom (tContainer);
    if (tIds.length < 2)
      {
        if (gCalquesCartesReorder.tpl === pTpl) calques_cartes_reorder_cleanup ();
        return;
      }

    if (gCalquesCartesReorder.container === tContainer && gCalquesCartesReorder.sortable) return;
    calques_cartes_reorder_cleanup ();

    if (!Sortable)
      {
        charge_sortable_edition ()
          .then (() => calques_cartes_reorder_sync (pTpl))
          .catch ((e) => console.warn ("[scene] chargement Sortable impossible:", e));
        return;
      }

    gCalquesCartesReorder.sortable = Sortable.create (tContainer,
      {
        draggable : ".js_calques_tree_carte_item",
        filter : "button, input, textarea, select, label, a",
        preventOnFilter : false,
        animation : 160,
        easing : "cubic-bezier(0.22, 1, 0.36, 1)",
        forceFallback : true,
        fallbackOnBody : true,
        fallbackTolerance : 4,
        delayOnTouchOnly : true,
        delay : 130,
        touchStartThreshold : 4,
        dragClass : "arbre_calque_dragging",
        chosenClass : "arbre_calque_prise",
        ghostClass : "arbre_calque_placeholder",
        fallbackClass : "arbre_calque_drag_helper",
        direction : "vertical",
        swapThreshold : 0.6,
        invertSwap : false,
        onStart ()
          {
            document.body.style.userSelect = "none";
            document.body.style.cursor = "grabbing";
            tContainer.classList.add ("arbre_cartes_reorder_actif");
          },
        onEnd (pEvt)
          {
            gCalquesCartesReorder.ignoreClickUntil = Date.now () + 320;
            document.body.style.userSelect = "";
            document.body.style.cursor = "";
            tContainer.classList.remove ("arbre_cartes_reorder_actif");
            if (!pEvt || pEvt.oldIndex === pEvt.newIndex) return;
            cartes_reorder_persiste_ids (calques_cartes_reorder_ids_dom (tContainer));
          },
      });
    gCalquesCartesReorder.container = tContainer;
    gCalquesCartesReorder.tpl = pTpl;
  }

function carte_obj_reorder_ids_dom (pContainer)
  {
    if (!pContainer) return [];
    return Array.from (pContainer.children)
      .filter ((pElt) => pElt && pElt.classList && pElt.classList.contains ("js_carte_tree_obj_item"))
      .map ((pElt) => pElt.getAttribute ("data-id"))
      .filter (Boolean);
  }

function carte_obj_reorder_ids_deplaces (pIds, pIdDrag, pIndexCible)
  {
    const tIds = normalise_ordre_objets_carte_client (pIds);
    const tId = typeof pIdDrag === "string" ? pIdDrag : "";
    if (!tIds.length || !tId || !tIds.includes (tId)) return [];
    const tSansDrag = tIds.filter ((pId) => pId !== tId);
    const tIndex = Math.max (0, Math.min (tSansDrag.length, Number.isInteger (pIndexCible) ? pIndexCible : tSansDrag.length));
    tSansDrag.splice (tIndex, 0, tId);
    return tSansDrag;
  }

function carte_obj_reorder_items_cibles ()
  {
    const tContainer = gCarteObjReorder.container;
    if (!tContainer) return [];
    return Array.from (tContainer.children)
      .filter ((pElt) => pElt
        && pElt !== gCarteObjReorder.item
        && pElt !== gCarteObjReorder.placeholder
        && pElt.classList
        && pElt.classList.contains ("js_carte_tree_obj_item"));
  }

function carte_obj_reorder_index_depuis_y (pClientY)
  {
    const tItems = carte_obj_reorder_items_cibles ();
    for (let i = 0; i < tItems.length; i += 1)
      {
        const tRect = tItems[i].getBoundingClientRect ();
        if (pClientY < tRect.top + tRect.height / 2) return i;
      }
    return tItems.length;
  }

function carte_obj_reorder_place_placeholder (pIndex)
  {
    const tContainer = gCarteObjReorder.container;
    const tPlaceholder = gCarteObjReorder.placeholder;
    if (!tContainer || !tPlaceholder) return;
    const tItems = carte_obj_reorder_items_cibles ();
    const tIndex = Math.max (0, Math.min (tItems.length, Number.isInteger (pIndex) ? pIndex : tItems.length));
    tContainer.insertBefore (tPlaceholder, tItems[tIndex] || null);
  }

function carte_obj_reorder_index_placeholder ()
  {
    const tContainer = gCarteObjReorder.container;
    const tPlaceholder = gCarteObjReorder.placeholder;
    if (!tContainer || !tPlaceholder) return null;
    const tItems = Array.from (tContainer.children)
      .filter ((pElt) => pElt
        && pElt !== gCarteObjReorder.item
        && pElt.classList
        && (pElt === tPlaceholder || pElt.classList.contains ("js_carte_tree_obj_item")));
    const tIndex = tItems.indexOf (tPlaceholder);
    return tIndex >= 0 ? tIndex : null;
  }

function carte_obj_reorder_positionne_helper (pEvent)
  {
    const tHelper = gCarteObjReorder.helper;
    if (!tHelper || !pEvent) return;
    const tX = Math.round (pEvent.clientX - gCarteObjReorder.offsetX);
    const tY = Math.round (pEvent.clientY - gCarteObjReorder.offsetY);
    tHelper.style.transform = `translate3d(${tX}px, ${tY}px, 0)`;
  }

function carte_obj_reorder_cleanup ()
  {
    if (gCarteObjReorder.moveHandler)
      document.removeEventListener ("pointermove", gCarteObjReorder.moveHandler);
    if (gCarteObjReorder.endHandler)
      {
        document.removeEventListener ("pointerup", gCarteObjReorder.endHandler);
        document.removeEventListener ("pointercancel", gCarteObjReorder.endHandler);
      }
    if (gCarteObjReorder.item)
      {
        gCarteObjReorder.item.classList.remove ("arbre_calque_prise", "arbre_calque_dragging");
        gCarteObjReorder.item.style.display = "";
      }
    if (gCarteObjReorder.placeholder && gCarteObjReorder.placeholder.parentNode)
      gCarteObjReorder.placeholder.parentNode.removeChild (gCarteObjReorder.placeholder);
    if (gCarteObjReorder.helper && gCarteObjReorder.helper.parentNode)
      gCarteObjReorder.helper.parentNode.removeChild (gCarteObjReorder.helper);
    if (gCarteObjReorder.container)
      gCarteObjReorder.container.classList.remove ("arbre_obj_reorder_actif");
    document.body.style.userSelect = "";
    document.body.style.cursor = "";
    gCarteObjReorder.container = null;
    gCarteObjReorder.tpl = null;
    gCarteObjReorder.active = false;
    gCarteObjReorder.sourceIds = null;
    gCarteObjReorder.dragId = null;
    gCarteObjReorder.pointerId = null;
    gCarteObjReorder.item = null;
    gCarteObjReorder.placeholder = null;
    gCarteObjReorder.helper = null;
    gCarteObjReorder.startX = 0;
    gCarteObjReorder.startY = 0;
    gCarteObjReorder.offsetX = 0;
    gCarteObjReorder.offsetY = 0;
    gCarteObjReorder.moveHandler = null;
    gCarteObjReorder.endHandler = null;
  }

function carte_obj_reorder_active_drag (pEvent)
  {
    if (gCarteObjReorder.active) return;
    const tItem = gCarteObjReorder.item;
    const tContainer = gCarteObjReorder.container;
    if (!tItem || !tContainer) return;

    const tRect = tItem.getBoundingClientRect ();
    const tPlaceholder = document.createElement ("li");
    tPlaceholder.className = "arbre_calque arbre_calque_placeholder arbre_obj_reorder_placeholder";
    tPlaceholder.style.height = `${Math.max (1, Math.round (tRect.height))}px`;

    const tHelper = tItem.cloneNode (true);
    tHelper.classList.add ("arbre_calque_drag_helper", "arbre_calque_dragging", "arbre_obj_reorder_helper");
    tHelper.style.position = "fixed";
    tHelper.style.left = "0";
    tHelper.style.top = "0";
    tHelper.style.width = `${Math.round (tRect.width)}px`;
    tHelper.style.minHeight = `${Math.round (tRect.height)}px`;
    tHelper.style.pointerEvents = "none";
    tHelper.style.zIndex = "5000";

    gCarteObjReorder.offsetX = pEvent.clientX - tRect.left;
    gCarteObjReorder.offsetY = pEvent.clientY - tRect.top;
    gCarteObjReorder.placeholder = tPlaceholder;
    gCarteObjReorder.helper = tHelper;
    gCarteObjReorder.active = true;

    tContainer.insertBefore (tPlaceholder, tItem);
    document.body.appendChild (tHelper);
    tItem.classList.add ("arbre_calque_prise");
    tItem.style.display = "none";
    tContainer.classList.add ("arbre_obj_reorder_actif");
    document.body.style.userSelect = "none";
    document.body.style.cursor = "grabbing";
    carte_obj_reorder_positionne_helper (pEvent);
    carte_obj_reorder_place_placeholder (carte_obj_reorder_index_depuis_y (pEvent.clientY));
  }

function carte_obj_reorder_pointermove (pEvent)
  {
    if (!gCarteObjReorder.item) return;
    const tDistanceX = pEvent.clientX - gCarteObjReorder.startX;
    const tDistanceY = pEvent.clientY - gCarteObjReorder.startY;
    if (!gCarteObjReorder.active)
      {
        if (Math.hypot (tDistanceX, tDistanceY) < 5) return;
        gCarteObjReorder.ignoreClickUntil = Date.now () + 320;
        carte_obj_reorder_active_drag (pEvent);
      }
    if (!gCarteObjReorder.active) return;
    pEvent.preventDefault ();
    carte_obj_reorder_positionne_helper (pEvent);
    carte_obj_reorder_place_placeholder (carte_obj_reorder_index_depuis_y (pEvent.clientY));
  }

function carte_obj_reorder_pointerend (pEvent)
  {
    const tActif = gCarteObjReorder.active;
    const tSourceIds = gCarteObjReorder.sourceIds;
    const tDragId = gCarteObjReorder.dragId;
    const tIndex = tActif ? carte_obj_reorder_index_placeholder () : null;
    if (tActif) gCarteObjReorder.ignoreClickUntil = Date.now () + 320;
    carte_obj_reorder_cleanup ();
    if (!tActif || !Number.isInteger (tIndex)) return;
    const tIdsOrdonnes = carte_obj_reorder_ids_deplaces (tSourceIds, tDragId, tIndex);
    if (tIdsOrdonnes.length) maj_ordre_objets_brouillon_carte (tIdsOrdonnes);
  }

function carte_obj_reorder_debut (pEvt, pTpl)
  {
    const tEvent = pEvt.originalEvent || pEvt;
    if (!tEvent || (tEvent.pointerType === "mouse" && tEvent.button !== 0)) return;
    if (pEvt.target && pEvt.target.closest ("button, input, textarea, select, label, a, .js_carte_tree_mask_toggle")) return;

    const tItem = pEvt.currentTarget;
    const tContainer = tItem && tItem.closest (".js_carte_tree_obj_sortable");
    const tId = tItem && tItem.getAttribute ("data-id");
    const tIds = carte_obj_reorder_ids_dom (tContainer);
    if (!tContainer || !tId || tIds.length < 2) return;

    carte_obj_reorder_cleanup ();
    gCarteObjReorder.container = tContainer;
    gCarteObjReorder.tpl = pTpl;
    gCarteObjReorder.sourceIds = tIds;
    gCarteObjReorder.dragId = tId;
    gCarteObjReorder.pointerId = tEvent.pointerId;
    gCarteObjReorder.item = tItem;
    gCarteObjReorder.startX = tEvent.clientX;
    gCarteObjReorder.startY = tEvent.clientY;
    gCarteObjReorder.moveHandler = carte_obj_reorder_pointermove;
    gCarteObjReorder.endHandler = carte_obj_reorder_pointerend;
    document.addEventListener ("pointermove", gCarteObjReorder.moveHandler, {passive : false});
    document.addEventListener ("pointerup", gCarteObjReorder.endHandler);
    document.addEventListener ("pointercancel", gCarteObjReorder.endHandler);
  }

function carte_obj_reorder_sync (pTpl)
  {
    if (gCarteObjReorder.active) return;
    const tActif = carte_arbre_est_actif ();
    const tContainer = tActif && pTpl ? pTpl.find (".js_carte_tree_obj_sortable") : null;
    if (!tContainer)
      {
        if (gCarteObjReorder.tpl === pTpl) carte_obj_reorder_cleanup ();
        return;
      }

    const tIds = carte_obj_reorder_ids_dom (tContainer);
    if (tIds.length < 2)
      {
        if (gCarteObjReorder.tpl === pTpl) carte_obj_reorder_cleanup ();
        return;
      }

    gCarteObjReorder.container = tContainer;
    gCarteObjReorder.tpl = pTpl;
  }

function categories_calques_reorder_ids_dom (pContainer)
  {
    if (!pContainer) return [];
    return Array.from (pContainer.children)
      .filter ((pElt) => pElt && pElt.classList && pElt.classList.contains ("js_calques_categorie_item"))
      .map ((pElt) => pElt.getAttribute ("data-categorie"))
      .filter (Boolean);
  }

function categories_calques_reorder_items_cibles ()
  {
    const tContainer = gCategoriesCalqueReorder.container;
    if (!tContainer) return [];
    return Array.from (tContainer.children)
      .filter ((pElt) => pElt
        && pElt !== gCategoriesCalqueReorder.item
        && pElt !== gCategoriesCalqueReorder.placeholder
        && pElt.classList
        && pElt.classList.contains ("js_calques_categorie_item"));
  }

function categories_calques_reorder_index_depuis_y (pClientY)
  {
    const tItems = categories_calques_reorder_items_cibles ();
    for (let i = 0; i < tItems.length; i += 1)
      {
        const tRect = tItems[i].getBoundingClientRect ();
        if (pClientY < tRect.top + tRect.height / 2) return i;
      }
    return tItems.length;
  }

function categories_calques_reorder_place_placeholder (pIndex)
  {
    const tContainer = gCategoriesCalqueReorder.container;
    const tPlaceholder = gCategoriesCalqueReorder.placeholder;
    if (!tContainer || !tPlaceholder) return;
    const tItems = categories_calques_reorder_items_cibles ();
    const tIndex = Math.max (0, Math.min (tItems.length, Number.isInteger (pIndex) ? pIndex : tItems.length));
    tContainer.insertBefore (tPlaceholder, tItems[tIndex] || null);
  }

function categories_calques_reorder_index_placeholder ()
  {
    const tContainer = gCategoriesCalqueReorder.container;
    const tPlaceholder = gCategoriesCalqueReorder.placeholder;
    if (!tContainer || !tPlaceholder) return null;
    const tItems = Array.from (tContainer.children)
      .filter ((pElt) => pElt
        && pElt !== gCategoriesCalqueReorder.item
        && pElt.classList
        && (pElt === tPlaceholder || pElt.classList.contains ("js_calques_categorie_item")));
    const tIndex = tItems.indexOf (tPlaceholder);
    return tIndex >= 0 ? tIndex : null;
  }

function categories_calques_reorder_positionne_helper (pEvent)
  {
    const tHelper = gCategoriesCalqueReorder.helper;
    if (!tHelper || !pEvent) return;
    const tX = Math.round (pEvent.clientX - gCategoriesCalqueReorder.offsetX);
    const tY = Math.round (pEvent.clientY - gCategoriesCalqueReorder.offsetY);
    tHelper.style.transform = `translate3d(${tX}px, ${tY}px, 0)`;
  }

function categories_calques_reorder_cleanup ()
  {
    if (gCategoriesCalqueReorder.moveHandler)
      document.removeEventListener ("pointermove", gCategoriesCalqueReorder.moveHandler);
    if (gCategoriesCalqueReorder.endHandler)
      {
        document.removeEventListener ("pointerup", gCategoriesCalqueReorder.endHandler);
        document.removeEventListener ("pointercancel", gCategoriesCalqueReorder.endHandler);
      }
    if (gCategoriesCalqueReorder.item)
      {
        gCategoriesCalqueReorder.item.classList.remove ("arbre_calque_prise", "arbre_calque_dragging");
        gCategoriesCalqueReorder.item.style.display = "";
      }
    if (gCategoriesCalqueReorder.placeholder && gCategoriesCalqueReorder.placeholder.parentNode)
      gCategoriesCalqueReorder.placeholder.parentNode.removeChild (gCategoriesCalqueReorder.placeholder);
    if (gCategoriesCalqueReorder.helper && gCategoriesCalqueReorder.helper.parentNode)
      gCategoriesCalqueReorder.helper.parentNode.removeChild (gCategoriesCalqueReorder.helper);
    if (gCategoriesCalqueReorder.container)
      gCategoriesCalqueReorder.container.classList.remove ("arbre_categories_reorder_actif");
    document.body.style.userSelect = "";
    document.body.style.cursor = "";
    gCategoriesCalqueReorder.container = null;
    gCategoriesCalqueReorder.tpl = null;
    gCategoriesCalqueReorder.sceneId = null;
    gCategoriesCalqueReorder.active = false;
    gCategoriesCalqueReorder.ordreSource = null;
    gCategoriesCalqueReorder.dragCategorie = null;
    gCategoriesCalqueReorder.pointerId = null;
    gCategoriesCalqueReorder.item = null;
    gCategoriesCalqueReorder.placeholder = null;
    gCategoriesCalqueReorder.helper = null;
    gCategoriesCalqueReorder.startX = 0;
    gCategoriesCalqueReorder.startY = 0;
    gCategoriesCalqueReorder.offsetX = 0;
    gCategoriesCalqueReorder.offsetY = 0;
    gCategoriesCalqueReorder.moveHandler = null;
    gCategoriesCalqueReorder.endHandler = null;
  }

function categories_calques_reorder_active_drag (pEvent)
  {
    if (gCategoriesCalqueReorder.active) return;
    const tItem = gCategoriesCalqueReorder.item;
    const tContainer = gCategoriesCalqueReorder.container;
    if (!tItem || !tContainer) return;

    const tRect = tItem.getBoundingClientRect ();
    const tEntete = tItem.querySelector (".arbre_categorie_entete");
    const tEnteteRect = tEntete ? tEntete.getBoundingClientRect () : tRect;

    const tPlaceholder = document.createElement ("li");
    tPlaceholder.className = "arbre_categorie arbre_calque_placeholder arbre_obj_reorder_placeholder arbre_categories_reorder_placeholder";
    tPlaceholder.style.height = `${Math.max (1, Math.round (tRect.height))}px`;

    const tHelper = document.createElement ("li");
    tHelper.className = "arbre_categorie arbre_calque_drag_helper arbre_obj_reorder_helper arbre_categories_reorder_helper";
    if (tEntete)
      tHelper.appendChild (tEntete.cloneNode (true));
    else
      tHelper.appendChild (tItem.cloneNode (true));
    tHelper.setAttribute ("aria-hidden", "true");
    tHelper.style.position = "fixed";
    tHelper.style.left = "0";
    tHelper.style.top = "0";
    tHelper.style.width = `${Math.round (tRect.width)}px`;
    tHelper.style.minHeight = `${Math.max (1, Math.round (tEnteteRect.height))}px`;
    tHelper.style.pointerEvents = "none";
    tHelper.style.zIndex = "5000";

    gCategoriesCalqueReorder.offsetX = pEvent.clientX - tRect.left;
    gCategoriesCalqueReorder.offsetY = pEvent.clientY - tEnteteRect.top;
    gCategoriesCalqueReorder.placeholder = tPlaceholder;
    gCategoriesCalqueReorder.helper = tHelper;
    gCategoriesCalqueReorder.active = true;

    tContainer.insertBefore (tPlaceholder, tItem);
    document.body.appendChild (tHelper);
    tItem.classList.add ("arbre_calque_prise");
    tItem.style.display = "none";
    tContainer.classList.add ("arbre_categories_reorder_actif");
    document.body.style.userSelect = "none";
    document.body.style.cursor = "grabbing";
    categories_calques_reorder_positionne_helper (pEvent);
    categories_calques_reorder_place_placeholder (categories_calques_reorder_index_depuis_y (pEvent.clientY));
  }

function categories_calques_reorder_pointermove (pEvent)
  {
    if (!gCategoriesCalqueReorder.item) return;
    const tDistanceX = pEvent.clientX - gCategoriesCalqueReorder.startX;
    const tDistanceY = pEvent.clientY - gCategoriesCalqueReorder.startY;
    if (!gCategoriesCalqueReorder.active)
      {
        if (Math.hypot (tDistanceX, tDistanceY) < 5) return;
        gCategoriesCalqueReorder.ignoreClickUntil = Date.now () + 320;
        categories_calques_reorder_active_drag (pEvent);
      }
    if (!gCategoriesCalqueReorder.active) return;
    pEvent.preventDefault ();
    categories_calques_reorder_positionne_helper (pEvent);
    categories_calques_reorder_place_placeholder (categories_calques_reorder_index_depuis_y (pEvent.clientY));
  }

function categories_calques_reorder_pointerend ()
  {
    const tActif = gCategoriesCalqueReorder.active;
    const tSceneId = gCategoriesCalqueReorder.sceneId;
    const tOrdreSource = gCategoriesCalqueReorder.ordreSource;
    const tDragCategorie = gCategoriesCalqueReorder.dragCategorie;
    const tIndex = tActif ? categories_calques_reorder_index_placeholder () : null;
    if (tActif) gCategoriesCalqueReorder.ignoreClickUntil = Date.now () + 320;
    categories_calques_reorder_cleanup ();
    if (!tActif || !tSceneId || !Number.isInteger (tIndex)) return;
    const tOrdre = carte_obj_reorder_ids_deplaces (tOrdreSource, tDragCategorie, tIndex);
    if (tOrdre.length) ecrit_ordre_categories_calques_scene (tSceneId, tOrdre);
  }

function categories_calques_reorder_debut (pEvt, pTpl)
  {
    const tEvent = pEvt.originalEvent || pEvt;
    if (!tEvent || (tEvent.pointerType === "mouse" && tEvent.button !== 0)) return;
    if (pEvt.target && pEvt.target.closest ("button, input, textarea, select, label, a, .js_toggle_categorie")) return;

    const tItem = pEvt.currentTarget && pEvt.currentTarget.closest (".js_calques_categorie_item");
    const tContainer = tItem && tItem.closest (".js_calques_categories_sortable");
    const tCategorie = tItem && tItem.getAttribute ("data-categorie");
    const tSceneId = Session.get ("scene_id");
    const tOrdre = categories_calques_reorder_ids_dom (tContainer);
    if (!tSceneId || !tContainer || !tCategorie || tOrdre.length < 2) return;

    categories_calques_reorder_cleanup ();
    gCategoriesCalqueReorder.container = tContainer;
    gCategoriesCalqueReorder.tpl = pTpl;
    gCategoriesCalqueReorder.sceneId = tSceneId;
    gCategoriesCalqueReorder.ordreSource = tOrdre;
    gCategoriesCalqueReorder.dragCategorie = tCategorie;
    gCategoriesCalqueReorder.pointerId = tEvent.pointerId;
    gCategoriesCalqueReorder.item = tItem;
    gCategoriesCalqueReorder.startX = tEvent.clientX;
    gCategoriesCalqueReorder.startY = tEvent.clientY;
    gCategoriesCalqueReorder.moveHandler = categories_calques_reorder_pointermove;
    gCategoriesCalqueReorder.endHandler = categories_calques_reorder_pointerend;
    document.addEventListener ("pointermove", gCategoriesCalqueReorder.moveHandler, {passive : false});
    document.addEventListener ("pointerup", gCategoriesCalqueReorder.endHandler);
    document.addEventListener ("pointercancel", gCategoriesCalqueReorder.endHandler);
  }

function categories_calques_reorder_sync (pTpl)
  {
    if (gCategoriesCalqueReorder.active) return;
    const tActif = gOngletDroite.get () === "calques";
    const tContainer = tActif && pTpl ? pTpl.find (".js_calques_categories_sortable") : null;
    if (!tContainer)
      {
        if (gCategoriesCalqueReorder.tpl === pTpl) categories_calques_reorder_cleanup ();
        return;
      }

    const tIds = categories_calques_reorder_ids_dom (tContainer);
    if (tIds.length < 2)
      {
        if (gCategoriesCalqueReorder.tpl === pTpl) categories_calques_reorder_cleanup ();
        return;
      }

    gCategoriesCalqueReorder.container = tContainer;
    gCategoriesCalqueReorder.tpl = pTpl;
  }

function commence_redim_col_droite (pEvt, tpl)
  {
    const tEvent = pEvt.originalEvent || pEvt;
    if (tEvent.pointerType === "mouse" && tEvent.button !== 0) return;
    pEvt.preventDefault ();

    const tCol = tpl.find (".colonne_droite_scene");
    const tCadre = tpl.find (".cadre_scene");
    if (!tCol || !tCadre) return;

    const tLargeurDepart = tCol.getBoundingClientRect ().width;
    const tXDepart       = tEvent.clientX;
    const tPointerId     = tEvent.pointerId;

    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";

    const tOverlay = document.createElement ("div");
    tOverlay.style.cssText =
        "position:fixed;inset:0;z-index:99999;cursor:col-resize;"
      + "background:transparent;touch-action:none;";
    document.body.appendChild (tOverlay);

    const tOnMove = (ev) =>
      {
        if (tPointerId !== undefined && ev.pointerId !== tPointerId) return;
        const tDelta = tXDepart - ev.clientX;
        const tMaxDispo = tCadre.getBoundingClientRect ().width - 200;
        const tMax = Math.min (LARGEUR_MAX_COL_DROITE, Math.max (LARGEUR_MIN_COL_DROITE, tMaxDispo));
        const tNouv = Math.max (LARGEUR_MIN_COL_DROITE,
                                Math.min (tMax, tLargeurDepart + tDelta));
        gLargeurColDroite.set (tNouv);
      };

    const tOnEnd = (ev) =>
      {
        if (ev && tPointerId !== undefined && ev.pointerId !== tPointerId) return;
        document.removeEventListener ("pointermove",   tOnMove);
        document.removeEventListener ("pointerup",     tOnEnd);
        document.removeEventListener ("pointercancel", tOnEnd);
        document.body.style.cursor = "";
        document.body.style.userSelect = "";
        if (tOverlay.parentNode) tOverlay.parentNode.removeChild (tOverlay);
        ecrit_largeur_col_droite (gLargeurColDroite.get ());
      };

    document.addEventListener ("pointermove",   tOnMove);
    document.addEventListener ("pointerup",     tOnEnd);
    document.addEventListener ("pointercancel", tOnEnd);
  }

function segment_data_name_correspond_categorie (pSegment, pCategorie)
  {
    const tSegment = typeof pSegment === "string" ? pSegment.trim ().toLowerCase () : "";
    const tCategorie = typeof pCategorie === "string" ? pCategorie.trim ().toLowerCase () : "";
    if (!tSegment || !tCategorie) return false;
    if (tCategorie === CALQUES_CATEGORIE_BOUTONS)
      return tSegment.includes ("bouton");
    return tSegment === tCategorie;
  }

// Retire le premier segment (avant la 1ère virgule) du data-name s'il
// correspond à la catégorie (évite la redondance avec le nom de branche).
function nom_court_calque (pDataName, pCategorie)
  {
    if (typeof pDataName !== "string" || !pDataName) return "";
    const tParts = pDataName.split (",");
    if (tParts.length <= 1) return pDataName.trim ();
    const tPremier = (tParts[0] || "").trim ();
    if (segment_data_name_correspond_categorie (tPremier, pCategorie))
      return tParts.slice (1).map ((s) => s.trim ()).filter (Boolean).join (", ");
    return pDataName.trim ();
  }

function nom_court_calque_pour_carte (pDataName, pCategorie)
  {
    if (typeof pDataName !== "string" || !pDataName) return "";
    const tParts = pDataName.split (",").map ((s) => s.trim ()).filter (Boolean);
    if (!tParts.length) return "";
    if (segment_data_name_correspond_categorie (tParts[0], pCategorie)) tParts.shift ();
    return tParts[0] || nom_court_calque (pDataName, pCategorie);
  }

function detail_affiche_calque_part (pDataName, pCategorie)
  {
    if (pCategorie !== CALQUES_CATEGORIE_PART) return "";
    if (typeof pDataName !== "string" || !pDataName) return "";
    const tParts = pDataName.split (",").map ((s) => s.trim ()).filter (Boolean);
    if (!tParts.length) return "";
    if (segment_data_name_correspond_categorie (tParts[0], pCategorie)) tParts.shift ();
    return tParts[1] || "";
  }

function nom_affiche_calque_arbre (pDataName, pCategorie)
  {
    const tNom = nom_court_calque_pour_carte (pDataName, pCategorie)
      || nom_court_calque (pDataName, pCategorie);
    const tDetail = detail_affiche_calque_part (pDataName, pCategorie);
    return tDetail ? `${tNom} - ${tDetail}` : tNom;
  }

// Renvoie la clé utilisée pour trier alphabétiquement les calques d'une
// catégorie : il s'agit du 2e segment du data-name (ex. "bateau" pour
// "obj, bateau, son-oui"). On retombe sur le data-name complet à défaut.
function cle_tri_calque (pDataName, pCategorie)
  {
    if (typeof pDataName !== "string" || !pDataName) return "";
    const tParts = pDataName.split (",").map ((s) => s.trim ()).filter (Boolean);
    if (!tParts.length) return "";
    if (pCategorie === "div") return (tParts[0] || "").toLowerCase ();
    if (pCategorie && segment_data_name_correspond_categorie (tParts[0], pCategorie))
      return (tParts[1] || tParts[0] || "").toLowerCase ();
    if (tParts.length >= 2 && tParts[1]) return tParts[1].toLowerCase ();
    return (tParts[0] || "").toLowerCase ();
  }

// Renvoie le data-name d'un objet identifié par son id dans la scène courante.
function get_data_name_pour_id (pSceneId, pId)
  {
    if (!pSceneId || !pId) return null;
    const tScene = Scenes.findOne (pSceneId);
    const tItem = item_interface_scene_par_id (pId, tScene);
    if (tItem) return CALQUES_CATEGORIE_INTERFACE + ", " + (tItem.item_nom || tItem.nom || "item");
    const tObjets = svg_objets_scene_normalises (tScene);
    if (!tObjets) return null;
    for (const tNom of CATEGORIES_PERTINENTES)
      {
        const tArr = tObjets[tNom];
        if (!Array.isArray (tArr)) continue;
        for (const c of tArr)
          if (c.id === pId) return c["data-name"] || null;
      }
    return null;
  }

function nom_objet_inspecteur_depuis_id (pId, pSceneId = Session.get ("scene_id"))
  {
    if (!pId) return "";
    const tDN = get_data_name_pour_id (pSceneId, pId);
    if (!tDN) return "";
    const tCategorie = trouve_categorie_pour_id (pId);
    return nom_objet_inspecteur_depuis_data_name (tDN, tCategorie);
  }

function segments_data_name_sans_categorie (pDataName, pCategorie = null)
  {
    if (typeof pDataName !== "string" || !pDataName) return [];
    const tParts = pDataName.split (",").map ((s) => s.trim ()).filter (Boolean);
    if (!tParts.length) return [];
    if (pCategorie && segment_data_name_correspond_categorie (tParts[0], pCategorie)) tParts.shift ();
    return tParts;
  }

function cle_famille_objet_depuis_data_name (pDataName, pCategorie = null)
  {
    const tParts = segments_data_name_sans_categorie (pDataName, pCategorie);
    return tParts[0] || "";
  }

function entree_svg_scene_depuis_id (pScene, pId)
  {
    if (!pScene || !pId) return null;
    const tItem = item_interface_scene_par_id (pId, pScene);
    if (tItem)
      return {categorie : CALQUES_CATEGORIE_INTERFACE, entree : {id : tItem.id, "data-name" : CALQUES_CATEGORIE_INTERFACE + ", " + (tItem.item_nom || tItem.nom || "item")}};
    const tObjets = svg_objets_scene_normalises (pScene);
    if (!tObjets) return null;
    for (const tNom of CATEGORIES_PERTINENTES)
      {
        const tArr = tObjets[tNom];
        if (!Array.isArray (tArr)) continue;
        const tEntree = tArr.find ((pItem) => pItem && pItem.id === pId);
        if (tEntree)
          return {categorie : tNom, entree : tEntree};
      }
    return null;
  }

function ids_parts_associes_a_objet_scene (pScene, pId)
  {
    const tEntree = entree_svg_scene_depuis_id (pScene, pId);
    if (!tEntree || !tEntree.entree) return [];
    const tCategorie = tEntree.categorie;
    if (tCategorie !== CALQUES_CATEGORIE_OBJ && tCategorie !== CALQUES_CATEGORIE_PART) return [];
    const tCleFamille = cle_famille_objet_depuis_data_name (tEntree.entree["data-name"], tCategorie);
    if (!tCleFamille) return [];

    const tObjets = svg_objets_scene_normalises (pScene);
    const tParts = Array.isArray (tObjets && tObjets[CALQUES_CATEGORIE_PART])
      ? tObjets[CALQUES_CATEGORIE_PART]
      : [];
    return tParts
      .filter ((pPart) =>
        cle_famille_objet_depuis_data_name (
          pPart && pPart["data-name"],
          CALQUES_CATEGORIE_PART
        ) === tCleFamille)
      .map ((pPart) => pPart && pPart.id)
      .filter (Boolean);
  }

function id_obj_parent_pour_part_scene (pScene, pPartId)
  {
    const tEntree = entree_svg_scene_depuis_id (pScene, pPartId);
    if (!tEntree || tEntree.categorie !== CALQUES_CATEGORIE_PART) return null;
    const tCleFamille = cle_famille_objet_depuis_data_name (
      tEntree.entree && tEntree.entree["data-name"],
      CALQUES_CATEGORIE_PART
    );
    if (!tCleFamille) return null;
    const tObjets = svg_objets_scene_normalises (pScene);
    const tObjs = Array.isArray (tObjets && tObjets[CALQUES_CATEGORIE_OBJ])
      ? tObjets[CALQUES_CATEGORIE_OBJ]
      : [];
    const tParent = tObjs.find ((pObj) =>
      cle_famille_objet_depuis_data_name (
        pObj && pObj["data-name"],
        CALQUES_CATEGORIE_OBJ
      ) === tCleFamille);
    return (tParent && tParent.id) || null;
  }

// Renvoie l'entrée objets_config correspondant à un id d'objet, ou null.
// ─── Animations d'entrée (calques SVG et items d'interface) ──────────────────────
// Réglages objets_config[].animation_entree publiés par le hub ; calcul et lecteur partagés avec le
// hub (imports/scene_animation_entree_settings.js, client/imports/animation_entree_lecteur.js).
// Elles partent à l'ouverture de chaque scène ; tant que l'écran d'entrée couvre la première scène,
// elles restent tenues à leur départ et ne démarrent (délais compris) qu'à l'appui du joueur.

function entrees_animations_entree_scene ()
  {
    const tScene = Scenes.findOne (Session.get ("scene_id"));
    return (Array.isArray (tScene && tScene.objets_config) ? tScene.objets_config : [])
      .filter ((pConfig) => pConfig && pConfig.id && pConfig.animation_entree)
      .map ((pConfig) => ({id : pConfig.id, animation_entree : pConfig.animation_entree}));
  }

function vue_scene_animation_entree_tpl (pTpl)
  {
    const tBrute = vue_reinitialisation_scene_brute (pTpl);
    return normalise_vue_carte_client (vue_camera_svg_depuis_vue_brute_scene (pTpl, tBrute) || tBrute);
  }

function document_svg_scene_courante_tpl (pTpl)
  {
    const tDoc = pTpl && pTpl._svgDoc;
    if (!tDoc || !tDoc.documentElement) return null;
    return pTpl._svgDocSceneId && pTpl._svgDocSceneId === Session.get ("scene_id") ? tDoc : null;
  }

function lance_animations_entree_scene_tpl (pTpl)
  {
    const tDoc = document_svg_scene_courante_tpl (pTpl);
    if (!tDoc) return 0;
    return Tracker.nonreactive (() => joue_animations_entree_scene (
      tDoc,
      entrees_animations_entree_scene (),
      {
        vue : vue_scene_animation_entree_tpl (pTpl),
        enAttente : audio_entry_gate_en_attente (),
      }
    ));
  }

function annule_animations_entree_scene_tpl (pTpl)
  {
    if (pTpl && pTpl._svgDoc) annule_animations_entree_scene (pTpl._svgDoc);
  }

if (typeof window !== "undefined")
  window.addEventListener ("diffusion:audio-entry-activate", () =>
    {
      const tDoc = document_svg_scene_courante_tpl (gSceneTemplateActif);
      if (tDoc) reprend_animations_entree_scene (tDoc);
    });

function get_objet_config (pScene, pId)
  {
    if (!pScene || !Array.isArray (pScene.objets_config) || !pId) return null;
    return pScene.objets_config.find ((c) => c.id === pId) || null;
  }

function config_objet_selectionne_scene ()
  {
    const tId = gCalqueSelectionne.get ();
    const tSceneId = Session.get ("scene_id");
    const tScene = (tId && tSceneId) ? Scenes.findOne (tSceneId) : null;
    return tScene ? get_objet_config (tScene, tId) : null;
  }

function reglages_survol_contour_objet_depuis_config (pConfig)
  {
    return normalise_reglages_survol_contour_objet (
      pConfig && pConfig.survol_contour,
      reglages_survol_contour_projet_mode_lecture ()
    );
  }

function reglages_survol_contour_objet_ou_parent_pour_id_scene (pScene, pId)
  {
    const tDefaut = reglages_survol_contour_projet_mode_lecture ();
    if (!pScene || !pId) return tDefaut;
    const tEntree = entree_svg_scene_depuis_id (pScene, pId);
    if (!tEntree) return tDefaut;
    if (tEntree.categorie !== CALQUES_CATEGORIE_PART)
      return reglages_survol_contour_objet_depuis_config (get_objet_config (pScene, pId));
    const tParentId = id_obj_parent_pour_part_scene (pScene, pId);
    if (!tParentId) return tDefaut;
    return reglages_survol_contour_objet_depuis_config (get_objet_config (pScene, tParentId));
  }

function reglages_survol_contour_projet_mode_lecture ()
  {
    const tReglages = reglages_mode_lecture_scene ();
    return normalise_reglages_survol_contour_objet ({
      couleur : tReglages.survol_contour_couleur,
      epaisseur : tReglages.survol_contour_epaisseur,
      opacite : tReglages.survol_contour_opacite,
    });
  }

function reactive_var_brouillon_contour_categorie (pCategorie)
  {
    return pCategorie === CALQUES_CATEGORIE_BOUTONS
      ? gInspecteurBoutonsContourDraft
      : gInspecteurObjetsContourDraft;
  }

function contour_categorie_supportee (pCategorie)
  {
    return pCategorie === CALQUES_CATEGORIE_OBJ || pCategorie === CALQUES_CATEGORIE_BOUTONS;
  }

function reglages_survol_contour_categorie_brouillon (pCategorie)
  {
    const tDraft = reactive_var_brouillon_contour_categorie (pCategorie).get ();
    if (tDraft)
      return normalise_reglages_survol_contour_objet (tDraft);
    return reglages_survol_contour_projet_mode_lecture ();
  }

function modifie_reglages_survol_contour_categorie_brouillon (pCategorie, pPatch = {})
  {
    const tCourant = reglages_survol_contour_categorie_brouillon (pCategorie);
    reactive_var_brouillon_contour_categorie (pCategorie).set (
      normalise_reglages_survol_contour_objet ({...tCourant, ...(pPatch || {})})
    );
    gInspecteurContoursGlobalMessage.set (null);
  }

function reglages_survol_contour_objet_selectionne_scene ()
  {
    const tId = gCalqueSelectionne.get ();
    const tSceneId = Session.get ("scene_id");
    const tScene = (tId && tSceneId) ? Scenes.findOne (tSceneId) : null;
    return reglages_survol_contour_objet_ou_parent_pour_id_scene (tScene, tId);
  }

function objet_selectionne_est_bouton ()
  {
    const tId = gCalqueSelectionne.get ();
    return !!tId && trouve_categorie_pour_id (tId) === CALQUES_CATEGORIE_BOUTONS;
  }

function objet_selectionne_est_item_interface ()
  {
    const tId = gCalqueSelectionne.get ();
    return !!tId && trouve_categorie_pour_id (tId) === CALQUES_CATEGORIE_INTERFACE;
  }

function objet_selectionne_est_bouton_ou_item ()
  {
    return objet_selectionne_est_bouton () || objet_selectionne_est_item_interface ();
  }

function categorie_est_bouton_lecture (pCategorie)
  {
    return pCategorie === CALQUES_CATEGORIE_BOUTONS || pCategorie === CALQUES_CATEGORIE_INTERFACE;
  }

function objet_selectionne_est_part ()
  {
    const tId = gCalqueSelectionne.get ();
    return !!tId && trouve_categorie_pour_id (tId) === CALQUES_CATEGORIE_PART;
  }

function objet_selectionne_est_zoog ()
  {
    const tId = gCalqueSelectionne.get ();
    return !!tId && trouve_categorie_pour_id (tId) === CALQUES_CATEGORIE_ZOOG;
  }

function normalise_images_morphing_client (pValeur)
  {
    if (!Array.isArray (pValeur)) return [];
    const tIds = new Set ();
    return pValeur
      .map ((pItem) =>
        {
          const tSource = pItem && typeof pItem === "object" ? pItem : {};
          const tFileId = typeof tSource.file_id === "string" ? tSource.file_id.trim () : "";
          if (!tFileId || tIds.has (tFileId)) return null;
          tIds.add (tFileId);
          return {
            id : (typeof tSource.id === "string" && tSource.id.trim ())
              || ("morph-" + Date.now ().toString (36) + "-" + Math.random ().toString (36).slice (2, 8)),
            file_id : tFileId,
            nom : (typeof tSource.nom === "string" && tSource.nom.trim ()) || "Image",
            offset_x_pct : Number.isFinite (Number (tSource.offset_x_pct)) ? Number (tSource.offset_x_pct) : 0,
            offset_y_pct : Number.isFinite (Number (tSource.offset_y_pct)) ? Number (tSource.offset_y_pct) : 0,
            taille_pct : Number.isFinite (Number (tSource.taille_pct)) ? Math.max (1, Number (tSource.taille_pct)) : 100,
          };
        })
      .filter (Boolean);
  }

function images_morphing_objet_selectionne ()
  {
    const tConfig = config_objet_selectionne_scene ();
    return normalise_images_morphing_client (tConfig && tConfig.morphing_images);
  }

function url_image_morphing_client (pFileId)
  {
    const tFichier = pFileId ? HubFiles.findOne ({_id : pFileId}) : null;
    return url_hubfile_client (tFichier);
  }

function persiste_images_morphing_selection (pImages, pCallback)
  {
    if (!objet_selectionne_est_zoog ()) return false;
    met_a_jour_objet_selectionne_scene (
      {morphing_images : normalise_images_morphing_client (pImages)},
      (err) =>
        {
          if (err) alert ("Erreur : " + (err.reason || err.message));
          if (typeof pCallback === "function") pCallback (err);
        }
    );
    return true;
  }

function normalise_action_interactivite_objet (pAction)
  {
    const tAction = typeof pAction === "string" ? pAction.trim ().toLowerCase () : "";
    return INTERACTIVITE_ACTIONS.some ((pItem) => pItem.value === tAction)
      ? tAction
      : INTERACTIVITE_ACTION_AUCUNE;
  }

function action_interactivite_objet_selectionne ()
  {
    const tConfig = config_objet_selectionne_scene ();
    return normalise_action_interactivite_objet (tConfig && tConfig.interactivite_action);
  }

function scene_cible_interactivite_objet_selectionne ()
  {
    const tConfig = config_objet_selectionne_scene ();
    const tSceneCibleId = tConfig && tConfig.interactivite_scene_id;
    return (typeof tSceneCibleId === "string" && tSceneCibleId) ? tSceneCibleId : null;
  }

function objet_selectionne_affiche_section_voix ()
  {
    const tId = gCalqueSelectionne.get ();
    if (!tId) return false;
    if (objet_selectionne_est_part ()) return false;
    if (!objet_selectionne_est_bouton ()) return true;
    return [
      INTERACTIVITE_ACTION_SON,
      INTERACTIVITE_ACTION_DIAPORAMA,
    ].includes (action_interactivite_objet_selectionne ());
  }

function normalise_url_testable_interactivite (pValeur)
  {
    const tTexte = typeof pValeur === "string" ? pValeur.trim () : "";
    if (!tTexte) return null;
    try { return new URL (tTexte).toString (); }
    catch (e) {}
    try { return new URL ("https://" + tTexte).toString (); }
    catch (e) {}
    return null;
  }

function ouvre_url_dans_nouvelle_fenetre_interactivite (pValeur)
  {
    const tUrl = normalise_url_testable_interactivite (pValeur);
    if (!tUrl)
      {
        alert ("Entrez une URL valide.");
        return false;
      }
    const tFenetre = window.open (tUrl, "_blank", "noopener,noreferrer");
    if (!tFenetre)
      {
        alert ("Impossible d'ouvrir cette URL dans une nouvelle fenêtre.");
        return false;
      }
    return true;
  }

function ouvre_url_dans_fenetre_active_interactivite (pValeur)
  {
    const tUrl = normalise_url_testable_interactivite (pValeur);
    if (!tUrl)
      {
        console.warn ("URL d'interactivité invalide.");
        return false;
      }
    // Mode embarqué : naviguer remplacerait la WebView, donc l'appli entière.
    // Un document embarqué (aide, mentions légales) s'ouvre dans l'appli, hors
    // ligne. Tout autre lien sort de l'appli (navigateur du système), derrière
    // la gate parentale exigée pour tout lien externe d'une appli pour enfants.
    if (est_runtime_embarque ())
      {
        // tUrl est normalisée : la table est indexée par l'URL du manifest.
        if (ouvre_document_embarque (pValeur) || ouvre_document_embarque (tUrl)) return true;
        // Scène commerciale : la gate a déjà été passée à l'entrée.
        if (gSceneCommercialeDeverrouilleeId
            && gSceneCommercialeDeverrouilleeId === Session.get ("scene_id"))
          return sort_vers_url_externe_embarque (tUrl);
        return ouvre_gate_parentale_embarque (() => sort_vers_url_externe_embarque (tUrl));
      }
    window.location.assign (tUrl);
    return true;
  }

// Sortie vers le navigateur du système : la WebView continue de tourner en
// arrière-plan, la voix et l'ambiance se superposeraient à la page ouverte.
// La voix s'arrête, l'ambiance se met en pause et reprend au retour dans
// l'appli (visibilitychange).
let gAmbianceSuspendueSortieExterne = null;

function sort_vers_url_externe_embarque (pUrl)
  {
    arrete_audio_lecture_scene ();
    const tAmbiance = gLectureSceneAmbianceAudio;
    if (tAmbiance && !tAmbiance.paused)
      {
        try { tAmbiance.pause (); } catch (e) {}
        gAmbianceSuspendueSortieExterne = tAmbiance;
      }
    return ouvre_url_externe_embarque (pUrl);
  }

if (typeof document !== "undefined" && typeof document.addEventListener === "function")
  document.addEventListener ("visibilitychange", () =>
    {
      if (document.visibilityState !== "visible") return;
      const tAmbiance = gAmbianceSuspendueSortieExterne;
      gAmbianceSuspendueSortieExterne = null;
      // Reprise seulement si la scène n'a pas changé d'ambiance entre-temps.
      if (tAmbiance && tAmbiance === gLectureSceneAmbianceAudio)
        tAmbiance.play ().catch (() => {});
    });

function ferme_apercu_video_inspecteur ()
  {
    gInspecteurVideoPreviewUrl.set (null);
  }

function ouvre_apercu_video_inspecteur (pValeur)
  {
    const tUrl = normalise_url_testable_interactivite (pValeur);
    if (!tUrl)
      {
        alert ("Entrez une URL vidéo valide.");
        return false;
      }
    gInspecteurVideoPreviewUrl.set (tUrl);
    return true;
  }

function met_a_jour_objet_selectionne_scene (pPatch, pCb = null)
  {
    const tId = gCalqueSelectionne.get ();
    const tSceneId = Session.get ("scene_id");
    if (!tId || !tSceneId) return false;
    Meteor.call (
      "maj_objet_scene",
      {
        scene_id : tSceneId,
        objet_id : tId,
        ...(pPatch || {}),
      },
      pCb
    );
    return true;
  }

function met_a_jour_survol_contour_objet_selectionne_scene (pReglages, pCb = null)
  {
    const tReglages = normalise_reglages_survol_contour_objet (pReglages);
    return met_a_jour_objet_selectionne_scene ({survol_contour : tReglages}, pCb);
  }

function url_client_hubfile (pFichier)
  {
    if (!pFichier) return null;
    for (const tCle of ["url", "path_public", "public_url", "href"])
      {
        const tUrl = typeof pFichier[tCle] === "string" ? pFichier[tCle].trim () : "";
        if (tUrl) return url_asset_diffusion_protegee (tUrl, pFichier._diffusion_version_id);
      }
    if (typeof pFichier.link === "function")
      {
        try { return pFichier.link (undefined, window.location.origin); }
        catch (e)
          {
            try { return pFichier.link (); }
            catch (e2) {}
          }
      }
    if (typeof HubFiles.link === "function")
      {
        try { return HubFiles.link (pFichier, undefined, window.location.origin); }
        catch (e)
          {
            try { return HubFiles.link (pFichier); }
            catch (e2) {}
          }
      }
    const tId = typeof pFichier._id === "string" ? pFichier._id.trim () : "";
    if (!tId) return null;
    const tFichierHydrate = HubFiles.findOne ({_id : tId});
    if (!tFichierHydrate || tFichierHydrate === pFichier) return null;
    return url_client_hubfile (tFichierHydrate);
  }

function url_hubfile_client_par_id_nom (pFileId, pNom = null)
  {
    const tId = typeof pFileId === "string" ? pFileId.trim () : "";
    if (!tId) return null;
    const tNom = (typeof pNom === "string" && pNom.trim ()) ? pNom.trim () : `${tId}.png`;
    const tSegments = ["cdn", "storage", "hubFiles", tId, "original", tNom]
      .map ((pSegment) => encodeURIComponent (pSegment));
    return `${window.location.origin}/${tSegments.join ("/")}`;
  }

function url_ombre_asset_scene_client (pFileId, pNom = null)
  {
    const tId = typeof pFileId === "string" ? pFileId.trim () : "";
    if (!tId) return null;
    const tNom = (typeof pNom === "string" && pNom.trim ()) ? pNom.trim () : `${tId}.png`;
    return `${window.location.origin}/api/scene-shadow-assets/${encodeURIComponent (tId)}/${encodeURIComponent (tNom)}`;
  }

function url_asset_diffusion_scene_client (pUrl, pScene = Scenes.findOne (Session.get ("scene_id")))
  {
    const tUrl = typeof pUrl === "string" ? pUrl.trim () : "";
    if (!tUrl) return null;
    return url_asset_diffusion_protegee (tUrl, pScene && pScene._diffusion_version_id);
  }

function lit_nombre_champ_inspecteur (pElement, pFallback)
  {
    const tValeur = Number (pElement && pElement.value);
    return Number.isFinite (tValeur) ? tValeur : pFallback;
  }

function lit_reglages_survol_contour_depuis_tpl (tpl)
  {
    const tCourant = reglages_survol_contour_objet_selectionne_scene ();
    const tCouleur = tpl && tpl.find (".js_inspecteur_survol_contour_couleur");
    const tEpaisseur = tpl && tpl.find (".js_inspecteur_survol_contour_epaisseur");
    const tOpacite = tpl && tpl.find (".js_inspecteur_survol_contour_opacite");
    return normalise_reglages_survol_contour_objet (
      {
        ...tCourant,
        couleur : tCouleur ? tCouleur.value : tCourant.couleur,
        epaisseur : lit_nombre_champ_inspecteur (tEpaisseur, tCourant.epaisseur),
        opacite : lit_nombre_champ_inspecteur (tOpacite, tCourant.opacite * 100) / 100,
      },
      tCourant
    );
  }

function nettoie_apercu_contour_objet_selectionne_scene ()
  {
    if (typeof gInspecteurApercuContourCleanup === "function")
      gInspecteurApercuContourCleanup ();
    gInspecteurApercuContourCleanup = null;
  }

function nettoie_apercu_contours_categorie_tpl (pTpl = gSceneTemplateActif)
  {
    const tTpl = pTpl || gSceneTemplateActif;
    const tRoot = tTpl && tTpl._svgDoc && tTpl._svgDoc.documentElement;
    if (!tRoot || typeof tRoot.querySelectorAll !== "function") return;
    tRoot.querySelectorAll ("[data-cogi-contour-category-preview='1']")
      .forEach ((pNoeud) =>
        {
          if (pNoeud && pNoeud.parentNode) pNoeud.parentNode.removeChild (pNoeud);
        });
  }

function retire_ids_clone_apercu_contour (pNoeud)
  {
    if (!pNoeud || !pNoeud.removeAttribute) return;
    pNoeud.removeAttribute ("id");
    if (typeof pNoeud.querySelectorAll !== "function") return;
    pNoeud.querySelectorAll ("[id]").forEach ((pElt) => pElt.removeAttribute ("id"));
  }

function affiche_apercu_contour_objet_selectionne_scene (pReglages)
  {
    const tTpl = gSceneTemplateActif;
    const tDoc = tTpl && tTpl._svgDoc;
    const tId = gCalqueSelectionne.get ();
    if (!tDoc || !tId) return;
    const tNoeud = tDoc.getElementById (tId);
    if (!tNoeud || !tNoeud.parentNode) return;

    nettoie_apercu_contour_objet_selectionne_scene ();

    const tContour = normalise_reglages_survol_contour_objet (pReglages, reglages_survol_contour_objet_selectionne_scene ());
    const tEpaisseur = Math.max (0, Number (tContour.epaisseur) || 0);
    const tLargeurTrait = Math.max (0.5, tEpaisseur);
    const tOpacite = Math.max (0, Math.min (1, Number (tContour.opacite) || 0));

    const tClone = tNoeud.cloneNode (true);
    retire_ids_clone_apercu_contour (tClone);
    const tNoeudsGraphiques = [tClone].concat (
      typeof tClone.querySelectorAll === "function"
        ? Array.from (tClone.querySelectorAll ("*"))
        : []
    );
    tNoeudsGraphiques.forEach ((pElt) =>
      {
        if (!pElt || !pElt.setAttribute) return;
        const tTag = (pElt.tagName || "").toLowerCase ();
        if (["defs", "clipPath", "mask", "pattern", "marker", "linearGradient", "radialGradient", "filter", "style", "script", "title", "desc"].includes (tTag))
          return;
        pElt.removeAttribute ("filter");
        if (pElt.style) pElt.style.filter = "";
        pElt.setAttribute ("fill", "none");
        pElt.setAttribute ("stroke", tContour.couleur);
        pElt.setAttribute ("stroke-opacity", tOpacite.toFixed (3));
        pElt.setAttribute ("stroke-width", String (tLargeurTrait));
        pElt.setAttribute ("stroke-linejoin", "round");
        pElt.setAttribute ("stroke-linecap", "round");
        pElt.setAttribute ("vector-effect", "non-scaling-stroke");
      });

    tClone.style.pointerEvents = "none";
    tClone.style.opacity = tOpacite.toFixed (3);
    tClone.setAttribute ("data-cogi-contour-preview", "1");
    tNoeud.parentNode.insertBefore (tClone, tNoeud);

    gInspecteurApercuContourCleanup = () =>
      {
        if (tClone && tClone.parentNode) tClone.parentNode.removeChild (tClone);
      };
  }

function rafraichit_apercu_contour_objet_selectionne_scene ()
  {
    const tId = gCalqueSelectionne.get ();
    if (!tId)
      {
        nettoie_apercu_contour_objet_selectionne_scene ();
        return;
      }
    const tConfig = config_objet_selectionne_scene ();
    affiche_apercu_contour_objet_selectionne_scene (tConfig && tConfig.survol_contour);
  }

function affiche_apercu_contours_categorie_tpl (pTpl = gSceneTemplateActif)
  {
    const tTpl = pTpl || gSceneTemplateActif;
    const tDoc = tTpl && tTpl._svgDoc;
    const tRoot = tDoc && tDoc.documentElement;
    if (!tRoot) return;

    const tCategorieSelectionnee = gCategorieSelectionnee.get ();
    if (!contour_categorie_supportee (tCategorieSelectionnee))
      {
        nettoie_apercu_contours_categorie_tpl (tTpl);
        return;
      }

    const tSceneId = Session.get ("scene_id");
    const tScene = tSceneId ? Scenes.findOne (tSceneId) : null;
    const tObjets = svg_objets_scene_normalises (tScene);
    const tObjetsCategorie = Array.isArray (tObjets && tObjets[tCategorieSelectionnee])
      ? tObjets[tCategorieSelectionnee]
      : [];
    const tContourGlobal = reglages_survol_contour_categorie_brouillon (tCategorieSelectionnee);

    nettoie_apercu_contours_categorie_tpl (tTpl);

    tObjetsCategorie.forEach ((pObjet) =>
      {
        const tId = pObjet && pObjet.id;
        if (!tId) return;
        const tNoeud = tDoc.getElementById (tId);
        if (!tNoeud || !tNoeud.parentNode) return;

        const tContourObjet = normalise_reglages_survol_contour_objet (tContourGlobal);
        const tEpaisseur = Math.max (0, Number (tContourObjet.epaisseur) || 0);
        const tLargeurTrait = Math.max (0.5, tEpaisseur);
        const tOpacite = Math.max (0, Math.min (1, Number (tContourObjet.opacite) || 0));

        const tClone = tNoeud.cloneNode (true);
        retire_ids_clone_apercu_contour (tClone);
        const tNoeudsGraphiques = [tClone].concat (
          typeof tClone.querySelectorAll === "function"
            ? Array.from (tClone.querySelectorAll ("*"))
            : []
        );
        tNoeudsGraphiques.forEach ((pElt) =>
          {
            if (!pElt || !pElt.setAttribute) return;
            const tTag = (pElt.tagName || "").toLowerCase ();
            if (["defs", "clipPath", "mask", "pattern", "marker", "linearGradient", "radialGradient", "filter", "style", "script", "title", "desc"].includes (tTag))
              return;
            pElt.removeAttribute ("filter");
            if (pElt.style) pElt.style.filter = "";
            pElt.setAttribute ("fill", "none");
            pElt.setAttribute ("stroke", tContourObjet.couleur);
            pElt.setAttribute ("stroke-opacity", tOpacite.toFixed (3));
            pElt.setAttribute ("stroke-width", String (tLargeurTrait));
            pElt.setAttribute ("stroke-linejoin", "round");
            pElt.setAttribute ("stroke-linecap", "round");
            pElt.setAttribute ("vector-effect", "non-scaling-stroke");
          });
        tClone.style.pointerEvents = "none";
        tClone.style.opacity = tOpacite.toFixed (3);
        tClone.setAttribute ("data-cogi-contour-category-preview", "1");
        tNoeud.parentNode.insertBefore (tClone, tNoeud);
      });
  }

function enregistre_message_survol_inspecteur (pErr, pSucces = null)
  {
    if (pErr)
      {
        gInspecteurSurvolMessage.set ({
          texte : "Erreur : " + (pErr.reason || pErr.message || pErr),
          type : "erreur",
        });
        return;
      }
    if (pSucces)
      {
        gInspecteurSurvolMessage.set ({texte : pSucces, type : "succes"});
        return;
      }
    gInspecteurSurvolMessage.set (null);
  }

function nom_hubfile_client (pFichier, pFallback = "Fichier sans nom")
  {
    if (!pFichier) return pFallback;
    const tNom = typeof pFichier.name === "string" && pFichier.name.trim ()
      ? pFichier.name.trim ()
      : (typeof pFichier.fileName === "string" && pFichier.fileName.trim ()
        ? pFichier.fileName.trim ()
        : pFallback);
    return tNom;
  }

function fichier_est_svg_bulle_projet (pFichier)
  {
    if (!pFichier) return false;
    const tNom = typeof pFichier.name === "string" ? pFichier.name.toLowerCase () : "";
    return pFichier.type === "image/svg+xml" || tNom.endsWith (".svg");
  }

function fichier_est_svg_zoom_alternatif_projet (pFichier)
  {
    return fichier_est_svg_bulle_projet (pFichier);
  }

function fichiers_bulles_projet_courant (pProjet = projet_courant_scene ())
  {
    if (!pProjet || !pProjet._id) return [];
    return HubFiles.find (
      {
        'meta.projet_id' : pProjet._id,
        'meta.kind' : 'project_bubble_svg',
      },
      {sort : {name : 1, _id : 1}}
    ).fetch ().map ((pFichierBrut) =>
      HubFiles.findOne ({_id : pFichierBrut._id}) || pFichierBrut
    ).map ((pFichier) =>
      ({
        _id : pFichier._id,
        nom : nom_hubfile_client (pFichier, "Bulle SVG"),
        url : url_client_hubfile (pFichier),
      }));
  }

function fichiers_zoom_alternatif_projet_courant (pProjet = projet_courant_scene ())
  {
    if (!pProjet || !pProjet._id) return new Map ();
    const tMap = new Map ();
    HubFiles.find (
      {
        'meta.projet_id' : pProjet._id,
        'meta.kind' : 'project_alt_zoom_svg',
      },
      {sort : {'meta.role' : 1, name : 1, _id : 1}}
    ).fetch ().forEach ((pFichierBrut) =>
      {
        const tFichier = HubFiles.findOne ({_id : pFichierBrut._id}) || pFichierBrut;
        const tRole = tFichier && tFichier.meta && tFichier.meta.role;
        if (!ZOOM_ALTERNATIF_SVG_ROLE_KEYS.has (tRole) || tMap.has (tRole)) return;
        tMap.set (
          tRole,
          {
            _id : tFichier._id,
            role : tRole,
            nom : nom_hubfile_client (tFichier, "SVG zoom alternatif"),
            url : url_client_hubfile (tFichier),
          }
        );
      });
    return tMap;
  }

function fichiers_zoom_alternatif_scene_courante (pScene = Scenes.findOne (Session.get ("scene_id")))
  {
    if (!pScene || !pScene._id) return new Map ();
    const tMap = new Map ();
    HubFiles.find (
      {
        'meta.scene_id' : pScene._id,
        'meta.kind' : 'scene_alt_zoom_svg',
      },
      {sort : {'meta.role' : 1, name : 1, _id : 1}}
    ).fetch ().forEach ((pFichierBrut) =>
      {
        const tFichier = HubFiles.findOne ({_id : pFichierBrut._id}) || pFichierBrut;
        const tRole = tFichier && tFichier.meta && tFichier.meta.role;
        if (!ZOOM_ALTERNATIF_SVG_ROLE_KEYS.has (tRole) || tMap.has (tRole)) return;
        tMap.set (
          tRole,
          {
            _id : tFichier._id,
            role : tRole,
            nom : nom_hubfile_client (tFichier, "SVG zoom alternatif"),
            url : url_client_hubfile (tFichier),
          }
        );
      });
    return tMap;
  }

function fichier_zoom_alternatif_projet_role (pRole, pProjet = projet_courant_scene ())
  {
    const tRole = typeof pRole === "string" ? pRole.trim () : "";
    if (!ZOOM_ALTERNATIF_SVG_ROLE_KEYS.has (tRole)) return null;
    return fichiers_zoom_alternatif_projet_courant (pProjet).get (tRole) || null;
  }

function carte_fermeture_override_explicite (pCarte = null)
  {
    const tFileId = typeof (pCarte && pCarte.carte_fermeture_svg_id) === "string"
      ? pCarte.carte_fermeture_svg_id.trim ()
      : "";
    const tUrl = typeof (pCarte && pCarte.carte_fermeture_svg_url) === "string"
      ? pCarte.carte_fermeture_svg_url.trim ()
      : "";
    return !!(tFileId || tUrl);
  }

function fichier_zoom_alternatif_carte_fermeture (pCarte = null)
  {
    const tFileId = typeof (pCarte && pCarte.carte_fermeture_svg_id) === "string"
      ? pCarte.carte_fermeture_svg_id.trim ()
      : "";
    const tUrlManifeste = typeof (pCarte && pCarte.carte_fermeture_svg_url) === "string"
      ? pCarte.carte_fermeture_svg_url.trim ()
      : "";
    if (!tFileId && !tUrlManifeste) return null;
    const tFichier = tFileId ? HubFiles.findOne ({_id : tFileId}) : null;
    if (!tFichier)
      {
        const tUrl = tUrlManifeste
          ? url_asset_diffusion_protegee (tUrlManifeste, pCarte && pCarte._diffusion_version_id)
          : null;
        if (!tUrl) return null;
        return {
          _id : tFileId || tUrlManifeste,
          role : "carte_fermeture",
          nom : "SVG carte_fermeture",
          url : tUrl,
          carteOverride : true,
        };
      }
    return {
      _id : tFichier._id,
      role : "carte_fermeture",
      nom : nom_hubfile_client (tFichier, "SVG carte_fermeture"),
      url : url_client_hubfile (tFichier),
      carteOverride : true,
    };
  }

function fichier_zoom_alternatif_role_courant (pRole, pCarte = null)
  {
    const tRole = typeof pRole === "string" ? pRole.trim () : "";
    if (!ZOOM_ALTERNATIF_SVG_ROLE_KEYS.has (tRole)) return null;
    if (tRole === "carte_fermeture")
      {
        const tCarteFile = fichier_zoom_alternatif_carte_fermeture (pCarte);
        if (tCarteFile) return tCarteFile;
        if (carte_fermeture_override_explicite (pCarte)) return null;
        const tScene = fichiers_zoom_alternatif_scene_courante ().get (tRole);
        if (tScene) return tScene;
      }
    return fichier_zoom_alternatif_projet_role (tRole);
  }

function fichier_zoom_alternatif_scene_role (pRole)
  {
    const tRole = typeof pRole === "string" ? pRole.trim () : "";
    if (!ZOOM_ALTERNATIF_SVG_ROLE_KEYS.has (tRole)) return null;
    return fichiers_zoom_alternatif_scene_courante ().get (tRole) || null;
  }

function cle_cache_modele_svg_zoom_alternatif (pRole, pFichier)
  {
    const tRole = typeof pRole === "string" ? pRole.trim () : "";
    const tFileId = pFichier && pFichier._id;
    const tUrl = pFichier && pFichier.url;
    return `${tRole}:${tFileId || tUrl || "__placeholder__"}`;
  }

function lance_chargement_modele_svg_zoom_alternatif_client (pRole, pUrl, pFichier = null)
  {
    const tRole = typeof pRole === "string" ? pRole.trim () : "";
    if (!ZOOM_ALTERNATIF_SVG_ROLE_KEYS.has (tRole) || !pUrl) return;
    const tCacheKey = cle_cache_modele_svg_zoom_alternatif (tRole, pFichier || {url : pUrl});
    const tEtat = gZoomAlternatifSvgModeles.get (tCacheKey) || {status : "idle"};
    if (tEtat.status === "loading" && tEtat.url === pUrl) return tEtat.promise || null;
    if (tEtat.status === "ready" && tEtat.url === pUrl) return Promise.resolve (tEtat);

    const tPromise = fetch (pUrl, {credentials : "same-origin"})
      .then ((pRes) =>
        {
          if (!pRes || !pRes.ok)
            throw new Error (`HTTP ${pRes ? pRes.status : "?"}`);
          return pRes.text ();
        })
      .then ((pTexteSvg) =>
        {
          const tParser = new DOMParser ();
          const tDoc = tParser.parseFromString (pTexteSvg, "image/svg+xml");
          const tRoot = tDoc && tDoc.documentElement;
          const tErreur = tDoc && typeof tDoc.querySelector === "function"
            ? tDoc.querySelector ("parsererror")
            : null;
          if (!tRoot || tRoot.nodeName.toLowerCase () !== "svg" || tErreur)
            throw new Error ("SVG invalide");
          tRoot.setAttribute ("xmlns", "http://www.w3.org/2000/svg");
          gZoomAlternatifSvgModeles.set (
            tCacheKey,
            {
              status : "ready",
              url : pUrl,
              svg_text : new XMLSerializer ().serializeToString (tRoot),
              viewBox : vue_modele_svg_bulle_projet_client (tRoot),
            }
          );
          gZoomAlternatifSvgCacheRevision.set (gZoomAlternatifSvgCacheRevision.get () + 1);
          return gZoomAlternatifSvgModeles.get (tCacheKey);
        })
      .catch ((pErr) =>
        {
          gZoomAlternatifSvgModeles.set (
            tCacheKey,
            {
              status : "error",
              url : pUrl,
              error : (pErr && (pErr.message || pErr.reason)) || String (pErr || "Erreur SVG"),
            }
          );
          gZoomAlternatifSvgCacheRevision.set (gZoomAlternatifSvgCacheRevision.get () + 1);
          return gZoomAlternatifSvgModeles.get (tCacheKey);
        });
    gZoomAlternatifSvgModeles.set (tCacheKey, {status : "loading", url : pUrl, promise : tPromise});
    return tPromise;
  }

function modele_svg_zoom_alternatif_client (pRole, pCarte = null)
  {
    gZoomAlternatifSvgCacheRevision.get ();
    const tRole = typeof pRole === "string" ? pRole.trim () : "";
    if (!ZOOM_ALTERNATIF_SVG_ROLE_KEYS.has (tRole)) return null;
    const tFichier = fichier_zoom_alternatif_role_courant (tRole, pCarte);
    const tCacheKey = cle_cache_modele_svg_zoom_alternatif (tRole, tFichier);
    const tEtat = gZoomAlternatifSvgModeles.get (tCacheKey) || null;
    const tUrl = tFichier && tFichier.url;
    if (tEtat && tEtat.status === "ready" && (!tUrl || tEtat.url === tUrl)) return tEtat;
    if (tUrl) lance_chargement_modele_svg_zoom_alternatif_client (tRole, tUrl, tFichier);
    return null;
  }

function charge_modele_svg_zoom_alternatif_client (pRole, pCarte = null)
  {
    const tRole = typeof pRole === "string" ? pRole.trim () : "";
    if (!ZOOM_ALTERNATIF_SVG_ROLE_KEYS.has (tRole)) return Promise.resolve (null);
    const tFichier = fichier_zoom_alternatif_role_courant (tRole, pCarte);
    const tUrl = tFichier && tFichier.url;
    if (!tUrl) return Promise.resolve (null);
    const tEtat = gZoomAlternatifSvgModeles.get (cle_cache_modele_svg_zoom_alternatif (tRole, tFichier)) || null;
    if (tEtat && tEtat.status === "ready" && tEtat.url === tUrl)
      return Promise.resolve (tEtat);
    if (tEtat && tEtat.status === "loading" && tEtat.url === tUrl && tEtat.promise)
      return tEtat.promise;
    return Promise.resolve (lance_chargement_modele_svg_zoom_alternatif_client (tRole, tUrl, tFichier));
  }

function charge_modeles_zoom_alternatif_interface_carte (pCarte = null)
  {
    return Promise.all (
      ZOOM_ALTERNATIF_SVG_ROLES.map ((pRole) =>
        Promise.resolve ()
          .then (() => charge_modele_svg_zoom_alternatif_client (pRole && pRole.cle, pCarte))
          .catch (() => null)
      )
    );
  }

function limite_cache_prechauffage_assets_lecture_scene ()
  {
    while (gPrechauffageAssetsLectureScene.size > 96)
      {
        const tPremier = gPrechauffageAssetsLectureScene.keys ().next ().value;
        if (!tPremier) break;
        gPrechauffageAssetsLectureScene.delete (tPremier);
      }
  }

function prechauffe_url_asset_lecture_scene (pUrl, pOptions = {})
  {
    const tUrl = typeof pUrl === "string" ? pUrl.trim () : "";
    if (!tUrl) return Promise.resolve (null);
    const tExistant = gPrechauffageAssetsLectureScene.get (tUrl);
    if (tExistant && tExistant.status === "ready") return Promise.resolve (tExistant);
    if (tExistant && tExistant.status === "loading" && tExistant.promise) return tExistant.promise;

    const tPromise = fetch (
      tUrl,
      {
        credentials : "include",
        cache : "force-cache",
      }
    )
      .then ((pRes) =>
        {
          if (!pRes || !pRes.ok) throw new Error (`HTTP ${pRes ? pRes.status : "?"}`);
          return (pOptions && pOptions.text) ? pRes.text () : pRes.blob ();
        })
      .then (() =>
        {
          const tEtat = {status : "ready", url : tUrl, at : Date.now ()};
          gPrechauffageAssetsLectureScene.set (tUrl, tEtat);
          limite_cache_prechauffage_assets_lecture_scene ();
          return tEtat;
        })
      .catch ((pErr) =>
        {
          const tEtat = {
            status : "error",
            url : tUrl,
            error : (pErr && (pErr.message || pErr.reason)) || String (pErr || "Erreur asset"),
          };
          gPrechauffageAssetsLectureScene.set (tUrl, tEtat);
          limite_cache_prechauffage_assets_lecture_scene ();
          return tEtat;
        });
    gPrechauffageAssetsLectureScene.set (tUrl, {status : "loading", url : tUrl, promise : tPromise});
    limite_cache_prechauffage_assets_lecture_scene ();
    return tPromise;
  }

function ajoute_url_prechauffage_lecture_scene (pUrls, pVus, pUrl, pOptions = {})
  {
    const tUrl = typeof pUrl === "string" ? pUrl.trim () : "";
    if (!tUrl || pVus.has (tUrl)) return;
    try
      {
        const tBase = typeof window !== "undefined" && window.location
          ? window.location.origin
          : "http://diffusion.local";
        const tParsed = new URL (tUrl, tBase);
        if (typeof window !== "undefined" && window.location && tParsed.origin !== window.location.origin)
          return;
        if (!/^\/(?:assets|hype)\//.test (tParsed.pathname)) return;
      }
    catch (e)
      {
        if (!/^\/(?:assets|hype)\//.test (tUrl)) return;
      }
    pVus.add (tUrl);
    pUrls.push ({url : tUrl, options : pOptions || {}});
  }

function ajoute_file_id_prechauffage_lecture_scene (pUrls, pVus, pFileId, pOptions = {})
  {
    const tId = typeof pFileId === "string" ? pFileId.trim () : "";
    if (!tId) return;
    const tFichier = HubFiles.findOne ({_id : tId});
    ajoute_url_prechauffage_lecture_scene (pUrls, pVus, url_client_hubfile (tFichier), pOptions);
  }

function collecte_objet_prechauffage_lecture_scene (pUrls, pVus, pObjet, pScene)
  {
    if (!pObjet || typeof pObjet !== "object") return;
    ajoute_file_id_prechauffage_lecture_scene (pUrls, pVus, pObjet.item_image_file_id);
    ajoute_file_id_prechauffage_lecture_scene (pUrls, pVus, pObjet.item_ombre_asset_file_id);
    ajoute_file_id_prechauffage_lecture_scene (pUrls, pVus, pObjet.calque_ombre_asset_file_id);
    ajoute_url_prechauffage_lecture_scene (
      pUrls,
      pVus,
      url_asset_diffusion_scene_client (pObjet.item_image_url, pScene)
    );
    ajoute_url_prechauffage_lecture_scene (
      pUrls,
      pVus,
      url_asset_diffusion_scene_client (pObjet.item_ombre_asset_url, pScene)
    );
    ajoute_url_prechauffage_lecture_scene (
      pUrls,
      pVus,
      url_asset_diffusion_scene_client (pObjet.calque_ombre_asset_url, pScene)
    );
    (Array.isArray (pObjet.morphing_images) ? pObjet.morphing_images : [])
      .forEach ((pImage) =>
        ajoute_file_id_prechauffage_lecture_scene (pUrls, pVus, pImage && pImage.file_id)
      );
  }

function execute_prechauffage_lecture_scene (pTaches, pConcurrence = 3)
  {
    const tTaches = Array.isArray (pTaches) ? pTaches.filter (Boolean) : [];
    const tConcurrence = Math.max (1, Math.min (4, Math.floor (Number (pConcurrence) || 3)));
    let tIndex = 0;
    const tWorker = () =>
      {
        const tTache = tTaches[tIndex++];
        if (!tTache) return Promise.resolve ();
        return Promise.resolve ()
          .then (tTache)
          .catch (() => null)
          .then (tWorker);
      };
    return Promise.all (Array.from ({length : Math.min (tConcurrence, tTaches.length)}, () => tWorker ()));
  }

export function prechauffe_assets_scene_lecture_diffusion (pSceneId, pOptions = {})
  {
    const tSceneId = typeof pSceneId === "string" ? pSceneId.trim () : "";
    if (!tSceneId) return Promise.resolve (false);
    const tScene = Scenes.findOne (tSceneId);
    if (!tScene) return Promise.resolve (false);
    const tUrls = [];
    const tVus = new Set ();
    const tTaches = [];
    const tInfosSvg = infos_svg_scene_courante (tSceneId);
    ajoute_url_prechauffage_lecture_scene (tUrls, tVus, tInfosSvg.url, {text : true});

    const tCartes = Cartes.find ({scene_id : tSceneId}, {sort : {ordre : 1, nom : 1}}).fetch ();
    tCartes.forEach ((pCarte) =>
      {
        if (pCarte && pCarte.svg_bulle_id)
          {
            const tFichier = HubFiles.findOne ({_id : pCarte.svg_bulle_id});
            const tUrl = url_client_hubfile (tFichier);
            ajoute_url_prechauffage_lecture_scene (tUrls, tVus, tUrl, {text : true});
            tTaches.push (() =>
              {
                modele_svg_bulle_projet_client (pCarte.svg_bulle_id);
                return null;
              });
          }
        tTaches.push (() => charge_modeles_zoom_alternatif_interface_carte (pCarte));
      });

    (Array.isArray (tScene.objets_config) ? tScene.objets_config : [])
      .forEach ((pObjet) => collecte_objet_prechauffage_lecture_scene (tUrls, tVus, pObjet, tScene));

    tUrls.slice (0, Math.max (8, Number (pOptions.maxUrls) || 28))
      .forEach ((pEntree) =>
        {
          tTaches.push (() => prechauffe_url_asset_lecture_scene (pEntree.url, pEntree.options));
        });

    return execute_prechauffage_lecture_scene (tTaches, pOptions.concurrence || 3)
      .then (() => true);
  }

function supprime_cache_zoom_alternatif_role_client (pRole)
  {
    const tRole = typeof pRole === "string" ? pRole.trim () : "";
    if (!tRole) return;
    const tPrefixe = `${tRole}:`;
    Array.from (gZoomAlternatifSvgModeles.keys ()).forEach ((pCle) =>
      {
        if (String (pCle || "").startsWith (tPrefixe))
          gZoomAlternatifSvgModeles.delete (pCle);
      });
  }

function documents_svg_bulles_projet_courant (pProjet = projet_courant_scene ())
  {
    if (!pProjet || !pProjet._id) return [];
    return HubFiles.find (
      {
        'meta.projet_id' : pProjet._id,
        'meta.kind' : 'project_bubble_svg',
      },
      {sort : {name : 1, _id : 1}}
    ).fetch ().map ((pFichierBrut) =>
      HubFiles.findOne ({_id : pFichierBrut._id}) || pFichierBrut
    ).filter (Boolean);
  }

function ids_svg_bulles_projet_courant (pProjet = projet_courant_scene ())
  {
    return documents_svg_bulles_projet_courant (pProjet)
      .map ((pFichier) => pFichier && pFichier._id)
      .filter ((pId) => typeof pId === "string" && pId.trim ());
  }

function normalise_svg_bulle_id_carte_client (pValeur, pIdsDisponibles = null)
  {
    const tId = typeof pValeur === "string" ? pValeur.trim () : "";
    if (!tId) return null;
    if (Array.isArray (pIdsDisponibles) && !pIdsDisponibles.includes (tId))
      return null;
    return tId;
  }

function normalise_couleur_auto_bulle_projet_client (pValeur, pCouleursDisponibles = [])
  {
    const tCouleur = typeof pValeur === "string" ? pValeur.trim ().toLowerCase () : "";
    if (!/^#[0-9a-f]{6}$/.test (tCouleur)) return null;
    if (Array.isArray (pCouleursDisponibles) && pCouleursDisponibles.length)
      return pCouleursDisponibles.includes (tCouleur) ? tCouleur : null;
    return tCouleur;
  }

function nom_cible_couleur_bulle_svg_client (pNoeud)
  {
    if (!pNoeud || typeof pNoeud.getAttribute !== "function") return "";
    const tAttrs = ["id", "data-name", "name", "label", "inkscape:label", "sodipodi:label"];
    for (const tAttr of tAttrs)
      {
        const tValeur = pNoeud.getAttribute (tAttr);
        if (typeof tValeur === "string" && tValeur.trim ()) return tValeur.trim ();
      }
    return "";
  }

function personnalise_texte_modele_svg_bulle_client (pTexteSvg, pCouleur)
  {
    if (typeof pTexteSvg !== "string" || !pTexteSvg) return "";
    if (!pCouleur) return pTexteSvg;

    try
      {
        const tParser = new DOMParser ();
        const tDoc = tParser.parseFromString (pTexteSvg, "image/svg+xml");
        const tRoot = tDoc && tDoc.documentElement;
        if (!tRoot || tRoot.nodeName.toLowerCase () !== "svg") return pTexteSvg;

        Array.from (tRoot.querySelectorAll ("*")).forEach ((pNoeud) =>
          {
            if (nom_cible_couleur_bulle_svg_client (pNoeud) !== "couleur_1") return;
            pNoeud.setAttribute ("fill", pCouleur);
            if (pNoeud.style && typeof pNoeud.style.setProperty === "function")
              pNoeud.style.setProperty ("fill", pCouleur);
          });

        tRoot.setAttribute ("xmlns", "http://www.w3.org/2000/svg");
        return new XMLSerializer ().serializeToString (tRoot);
      }
    catch (e)
      {
        return pTexteSvg;
      }
  }

function uri_data_svg_bulle_projet_client (pTexteSvg)
  {
    if (typeof pTexteSvg !== "string" || !pTexteSvg) return null;
    return `data:image/svg+xml;charset=utf-8,${encodeURIComponent (pTexteSvg)}`;
  }

function vue_modele_svg_bulle_projet_client (pRoot)
  {
    const tVue = vue_svg_racine_client (pRoot);
    return tVue || {x : 0, y : 0, w : 100, h : 100};
  }

function lance_chargement_modele_svg_bulle_projet_client (pFichierId, pUrl)
  {
    const tId = normalise_svg_bulle_id_carte_client (pFichierId);
    if (!tId || !pUrl) return;

    const tEtat = gBullesProjetSvgModeles.get (tId) || {status : "idle", dataUris : new Map ()};
    if (tEtat.status === "loading" || tEtat.status === "ready") return;

    tEtat.status = "loading";
    tEtat.url = pUrl;
    gBullesProjetSvgModeles.set (tId, tEtat);

    fetch (pUrl, {credentials : "same-origin"})
      .then ((pRes) =>
        {
          if (!pRes || !pRes.ok)
            throw new Error (`HTTP ${pRes ? pRes.status : "?"}`);
          return pRes.text ();
        })
      .then ((pTexteSvg) =>
        {
          const tParser = new DOMParser ();
          const tDoc = tParser.parseFromString (pTexteSvg, "image/svg+xml");
          const tRoot = tDoc && tDoc.documentElement;
          const tErreur = tDoc && typeof tDoc.querySelector === "function"
            ? tDoc.querySelector ("parsererror")
            : null;
          if (!tRoot || tRoot.nodeName.toLowerCase () !== "svg" || tErreur)
            throw new Error ("SVG invalide");

          tRoot.setAttribute ("xmlns", "http://www.w3.org/2000/svg");
          const tTexteNormalise = new XMLSerializer ().serializeToString (tRoot);
          gBullesProjetSvgModeles.set (tId,
            {
              status : "ready",
              url : pUrl,
              svg_text : tTexteNormalise,
              viewBox : vue_modele_svg_bulle_projet_client (tRoot),
              dataUris : tEtat.dataUris || new Map (),
            });
          gBullesProjetSvgCacheRevision.set (gBullesProjetSvgCacheRevision.get () + 1);
        })
      .catch ((pErr) =>
        {
          gBullesProjetSvgModeles.set (tId,
            {
              status : "error",
              url : pUrl,
              error : (pErr && (pErr.message || pErr.reason)) || String (pErr || "Erreur SVG"),
              dataUris : tEtat.dataUris || new Map (),
            });
          gBullesProjetSvgCacheRevision.set (gBullesProjetSvgCacheRevision.get () + 1);
        });
  }

function modele_svg_bulle_projet_client (pFichierId)
  {
    gBullesProjetSvgCacheRevision.get ();
    const tId = normalise_svg_bulle_id_carte_client (pFichierId);
    if (!tId) return null;

    const tEtat = gBullesProjetSvgModeles.get (tId) || null;
    if (tEtat && tEtat.status === "ready") return tEtat;

    const tFichier = HubFiles.findOne ({_id : tId});
    const tUrl = url_client_hubfile (tFichier);
    if (tUrl) lance_chargement_modele_svg_bulle_projet_client (tId, tUrl);
    return null;
  }

function uri_modele_svg_bulle_projet_client (pFichierId, pCouleur)
  {
    const tModele = modele_svg_bulle_projet_client (pFichierId);
    if (!tModele || tModele.status !== "ready") return null;
    const tCle = pCouleur || "__base__";
    if (tModele.dataUris && tModele.dataUris.has (tCle))
      return tModele.dataUris.get (tCle) || null;

    const tTexte = personnalise_texte_modele_svg_bulle_client (tModele.svg_text, pCouleur || null);
    const tUri = uri_data_svg_bulle_projet_client (tTexte);
    if (tModele.dataUris) tModele.dataUris.set (tCle, tUri);
    return tUri;
  }

function dimensions_modele_svg_bulle_projet_client (pFichierId, pTaille)
  {
    const tTaille = Math.max (1, Number (pTaille) || CARTE_TAILLE_BULLE_DEFAUT);
    const tModele = modele_svg_bulle_projet_client (pFichierId);
    const tVue = tModele && tModele.viewBox;
    if (!tVue || !Number.isFinite (tVue.w) || !Number.isFinite (tVue.h) || tVue.w <= 0 || tVue.h <= 0)
      return {largeur : tTaille, hauteur : tTaille};
    const tMax = Math.max (tVue.w, tVue.h, 1);
    return {
      largeur : Math.max (1, (tVue.w / tMax) * tTaille),
      hauteur : Math.max (1, (tVue.h / tMax) * tTaille),
    };
  }

function meteor_call_scene_promise (pNom, ...pArgs)
  {
    return new Promise ((resolve, reject) =>
      {
        Meteor.call (pNom, ...pArgs,
          (pErr, pRes) =>
            {
              if (pErr) reject (pErr);
              else resolve (pRes);
            });
      });
  }

function ferme_menu_contextuel_inspecteur_projet_bulles ()
  {
    gInspecteurProjetBullesContextMenu.set (null);
  }

function position_menu_contextuel_inspecteur_projet_bulles (pX, pY)
  {
    const tMargin = 8;
    const tWidth = 190;
    const tHeight = 58;
    const tViewportW = typeof window !== "undefined" ? window.innerWidth : (tWidth + tMargin * 2);
    const tViewportH = typeof window !== "undefined" ? window.innerHeight : (tHeight + tMargin * 2);
    const tPosX = Number.isFinite (pX) ? pX : tMargin;
    const tPosY = Number.isFinite (pY) ? pY : tMargin;
    return {
      x : Math.max (tMargin, Math.min (tViewportW - tWidth - tMargin, Math.round (tPosX))),
      y : Math.max (tMargin, Math.min (tViewportH - tHeight - tMargin, Math.round (tPosY))),
    };
  }

function ouvre_menu_contextuel_inspecteur_projet_bulles (pFileId, pNom, pClientX, pClientY)
  {
    if (!pFileId) return;
    const tPosition = position_menu_contextuel_inspecteur_projet_bulles (pClientX, pClientY);
    gInspecteurProjetBullesContextMenu.set (
      {
        fileId : pFileId,
        nom : pNom || "",
        x : tPosition.x,
        y : tPosition.y,
      }
    );
  }

function annule_appui_long_inspecteur_projet_bulles ()
  {
    if (gInspecteurProjetBullesContextMenuLongPress.timer)
      clearTimeout (gInspecteurProjetBullesContextMenuLongPress.timer);
    if (gInspecteurProjetBullesContextMenuLongPress.moveHandler)
      document.removeEventListener ("pointermove", gInspecteurProjetBullesContextMenuLongPress.moveHandler);
    if (gInspecteurProjetBullesContextMenuLongPress.endHandler)
      {
        document.removeEventListener ("pointerup", gInspecteurProjetBullesContextMenuLongPress.endHandler);
        document.removeEventListener ("pointercancel", gInspecteurProjetBullesContextMenuLongPress.endHandler);
      }
    gInspecteurProjetBullesContextMenuLongPress.timer = null;
    gInspecteurProjetBullesContextMenuLongPress.pointerId = null;
    gInspecteurProjetBullesContextMenuLongPress.fileId = null;
    gInspecteurProjetBullesContextMenuLongPress.nom = "";
    gInspecteurProjetBullesContextMenuLongPress.startX = 0;
    gInspecteurProjetBullesContextMenuLongPress.startY = 0;
    gInspecteurProjetBullesContextMenuLongPress.clientX = 0;
    gInspecteurProjetBullesContextMenuLongPress.clientY = 0;
    gInspecteurProjetBullesContextMenuLongPress.moveHandler = null;
    gInspecteurProjetBullesContextMenuLongPress.endHandler = null;
  }

function demarre_appui_long_inspecteur_projet_bulles (pEvent)
  {
    if (!utilisateur_peut_modifier_projet_inspecteur ()) return;
    const tEvent = pEvent && (pEvent.originalEvent || pEvent);
    if (!tEvent || tEvent.pointerType === "mouse" && tEvent.button !== 0) return;
    const tItem = pEvent.currentTarget;
    const tFileId = tItem && tItem.dataset && tItem.dataset.id;
    if (!tItem || !tFileId) return;

    annule_appui_long_inspecteur_projet_bulles ();

    const tRect = tItem.getBoundingClientRect ();
    gInspecteurProjetBullesContextMenuLongPress.pointerId = tEvent.pointerId;
    gInspecteurProjetBullesContextMenuLongPress.fileId = tFileId;
    gInspecteurProjetBullesContextMenuLongPress.nom = (tItem.dataset && tItem.dataset.name) || "";
    gInspecteurProjetBullesContextMenuLongPress.startX = Number.isFinite (tEvent.clientX)
      ? tEvent.clientX
      : tRect.left + tRect.width / 2;
    gInspecteurProjetBullesContextMenuLongPress.startY = Number.isFinite (tEvent.clientY)
      ? tEvent.clientY
      : tRect.top + tRect.height / 2;
    gInspecteurProjetBullesContextMenuLongPress.clientX = gInspecteurProjetBullesContextMenuLongPress.startX;
    gInspecteurProjetBullesContextMenuLongPress.clientY = gInspecteurProjetBullesContextMenuLongPress.startY;

    gInspecteurProjetBullesContextMenuLongPress.moveHandler = (pMoveEvent) =>
      {
        if (gInspecteurProjetBullesContextMenuLongPress.pointerId !== null
            && pMoveEvent.pointerId !== gInspecteurProjetBullesContextMenuLongPress.pointerId) return;
        gInspecteurProjetBullesContextMenuLongPress.clientX = pMoveEvent.clientX;
        gInspecteurProjetBullesContextMenuLongPress.clientY = pMoveEvent.clientY;
        const tDistance = Math.hypot (
          pMoveEvent.clientX - gInspecteurProjetBullesContextMenuLongPress.startX,
          pMoveEvent.clientY - gInspecteurProjetBullesContextMenuLongPress.startY
        );
        if (tDistance > DISTANCE_APPUI_LONG_INSPECTEUR_PROJET_BULLES)
          annule_appui_long_inspecteur_projet_bulles ();
      };

    gInspecteurProjetBullesContextMenuLongPress.endHandler = (pEndEvent) =>
      {
        if (gInspecteurProjetBullesContextMenuLongPress.pointerId !== null
            && pEndEvent.pointerId !== gInspecteurProjetBullesContextMenuLongPress.pointerId) return;
        annule_appui_long_inspecteur_projet_bulles ();
      };

    document.addEventListener ("pointermove", gInspecteurProjetBullesContextMenuLongPress.moveHandler, {passive : true});
    document.addEventListener ("pointerup", gInspecteurProjetBullesContextMenuLongPress.endHandler);
    document.addEventListener ("pointercancel", gInspecteurProjetBullesContextMenuLongPress.endHandler);

    gInspecteurProjetBullesContextMenuLongPress.timer = setTimeout (() =>
      {
        const tFileIdTimer = gInspecteurProjetBullesContextMenuLongPress.fileId;
        const tNomTimer = gInspecteurProjetBullesContextMenuLongPress.nom;
        const tX = gInspecteurProjetBullesContextMenuLongPress.clientX;
        const tY = gInspecteurProjetBullesContextMenuLongPress.clientY;
        annule_appui_long_inspecteur_projet_bulles ();
        ouvre_menu_contextuel_inspecteur_projet_bulles (tFileIdTimer, tNomTimer, tX, tY);
      },
      DUREE_APPUI_LONG_INSPECTEUR_PROJET_BULLES);
  }

async function televerse_svg_bulles_projet (pFichiers)
  {
    const tProjet = projet_courant_scene ();
    if (!tProjet || !tProjet._id || !utilisateur_peut_modifier_projet_inspecteur ()) return;

    const tListe = Array.from (pFichiers || []).filter (Boolean);
    if (!tListe.length) return;

    let tSucces = 0;
    const tErreurs = [];

    for (let i = 0; i < tListe.length; i += 1)
      {
        const tFichier = tListe[i];
        const tNom = nom_hubfile_client (tFichier, "bulle.svg");
        if (!fichier_est_svg_bulle_projet (tFichier))
          {
            tErreurs.push (`${tNom} : fichier non SVG`);
            continue;
          }

        let tUploadFile = null;
        try
          {
            gInspecteurProjetBullesUpload.set (
              {index : i + 1, total : tListe.length, nom : tNom, progress : 0}
            );
            tUploadFile = await televerse_hubfile (
              tFichier,
              tNom,
              "image/svg+xml",
              {
                user_id : Meteor.userId (),
                ownerId : Meteor.userId (),
                kind : "project_bubble_svg_temp",
              },
              (pPct) =>
                {
                  const tEtat = gInspecteurProjetBullesUpload.get ();
                  if (!tEtat) return;
                  gInspecteurProjetBullesUpload.set ({...tEtat, progress : pPct});
                }
            );

            await meteor_call_scene_promise (
              "attache_svg_bulle_projet",
              {projet_id : tProjet._id, file_id : tUploadFile._id}
            );
            tSucces += 1;
          }
        catch (pErr)
          {
            if (tUploadFile && tUploadFile._id)
              Meteor.call ("supprime_vignette_provisoire", tUploadFile._id, () => {});
            tErreurs.push (`${tNom} : ${(pErr && (pErr.reason || pErr.message)) || pErr}`);
          }
      }

    gInspecteurProjetBullesUpload.set (null);

    if (tErreurs.length && !tSucces)
      {
        gInspecteurProjetBullesMessage.set ({
          texte : `Aucun SVG importé. ${tErreurs[0]}`,
          type : "erreur",
        });
        return;
      }

    if (tErreurs.length)
      {
        gInspecteurProjetBullesMessage.set ({
          texte : `${tSucces} SVG importé(s). ${tErreurs.length} échec(s).`,
          type : "info",
        });
        return;
      }

    gInspecteurProjetBullesMessage.set ({
      texte : `${tSucces} SVG importé(s) pour les bulles du projet.`,
      type : "succes",
    });
  }

async function televerse_svg_zoom_alternatif_projet (pRole, pFichiers)
  {
    const tProjet = projet_courant_scene ();
    const tRole = typeof pRole === "string" ? pRole.trim () : "";
    if (!tProjet || !tProjet._id || !utilisateur_peut_modifier_projet_inspecteur ()) return;
    if (!ZOOM_ALTERNATIF_SVG_ROLE_KEYS.has (tRole)) return;

    const tFichier = Array.from (pFichiers || []).filter (Boolean)[0];
    if (!tFichier) return;
    const tNom = nom_hubfile_client (tFichier, `${tRole}.svg`);
    if (!fichier_est_svg_zoom_alternatif_projet (tFichier))
      {
        gInspecteurProjetModeLectureMessage.set ({
          texte : `${tNom} : fichier non SVG`,
          type : "erreur",
        });
        return;
      }

    let tUploadFile = null;
    try
      {
        gInspecteurProjetZoomAltUpload.set ({role : tRole, nom : tNom, progress : 0});
        tUploadFile = await televerse_hubfile (
          tFichier,
          tNom,
          "image/svg+xml",
          {
            user_id : Meteor.userId (),
            ownerId : Meteor.userId (),
            kind : "project_alt_zoom_svg_temp",
            role : tRole,
          },
          (pPct) =>
            {
              const tEtat = gInspecteurProjetZoomAltUpload.get ();
              if (!tEtat || tEtat.role !== tRole) return;
              gInspecteurProjetZoomAltUpload.set ({...tEtat, progress : pPct});
            }
        );
        await meteor_call_scene_promise (
          "attache_svg_zoom_alternatif_projet",
          {projet_id : tProjet._id, file_id : tUploadFile._id, role : tRole}
        );
        supprime_cache_zoom_alternatif_role_client (tRole);
        gZoomAlternatifSvgCacheRevision.set (gZoomAlternatifSvgCacheRevision.get () + 1);
        programme_actualisation_brouillon_zoom_carte_tpl (gSceneTemplateActif);
        gInspecteurProjetModeLectureMessage.set ({
          texte : `${tNom} attaché au zoom alternatif.`,
          type : "succes",
        });
      }
    catch (pErr)
      {
        if (tUploadFile && tUploadFile._id)
          Meteor.call ("supprime_vignette_provisoire", tUploadFile._id, () => {});
        gInspecteurProjetModeLectureMessage.set ({
          texte : "Erreur : " + ((pErr && (pErr.reason || pErr.message)) || pErr),
          type : "erreur",
        });
      }
    finally
      {
        gInspecteurProjetZoomAltUpload.set (null);
      }
  }

async function supprime_tous_svg_bulles_projet_client ()
  {
    const tProjet = projet_courant_scene ();
    if (!tProjet || !tProjet._id || !utilisateur_peut_modifier_projet_inspecteur ())
      return {total : 0, deletedCount : 0, errorCount : 0, firstError : null};

    const tFileIds = fichiers_bulles_projet_courant (tProjet)
      .map ((pFichier) => pFichier && pFichier._id)
      .filter (Boolean);
    if (!tFileIds.length)
      return {total : 0, deletedCount : 0, errorCount : 0, firstError : null};

    let tDeletedCount = 0;
    let tFirstError = null;

    for (const tFileId of tFileIds)
      {
        try
          {
            await meteor_call_scene_promise (
              "supprime_svg_bulle_projet",
              {projet_id : tProjet._id, file_id : tFileId}
            );
            tDeletedCount += 1;
          }
        catch (pErr)
          {
            if (!tFirstError) tFirstError = pErr;
          }
      }

    return {
      total : tFileIds.length,
      deletedCount : tDeletedCount,
      errorCount : tFileIds.length - tDeletedCount,
      firstError : tFirstError,
    };
  }

function infos_svg_scene_courante (pSceneId = Session.get ("scene_id"))
  {
    if (!pSceneId) return {scene : null, fichier : null, url : null};
    const tScene = Scenes.findOne (pSceneId);
    if (!tScene || !tScene.svg_id)
      return {scene : tScene || null, fichier : null, url : null};
    const tRuntimeValide = Number (tScene.svg_runtime_version) === SVG_RUNTIME_COMPILATION_VERSION;
    const tRuntime = tRuntimeValide && tScene.svg_runtime_id
      ? HubFiles.findOne ({_id : tScene.svg_runtime_id})
      : null;
    const tFichier = tRuntime || HubFiles.findOne ({_id : tScene.svg_id});
    return {
      scene   : tScene,
      fichier : tFichier || null,
      url     : url_client_hubfile (tFichier),
      runtime : !!tRuntime,
    };
  }

function ajoute_parametre_url_scene_svg (pUrl, pNom, pValeur)
  {
    const tUrl = typeof pUrl === "string" ? pUrl.trim () : "";
    if (!tUrl || !pNom) return tUrl || null;
    try
      {
        const tBase = typeof window !== "undefined" && window.location
          ? window.location.origin
          : "http://diffusion.local";
        const tParsed = new URL (tUrl, tBase);
        tParsed.searchParams.set (pNom, String (pValeur || ""));
        return tParsed.pathname + tParsed.search + tParsed.hash;
      }
    catch (e)
      {
        const tSep = tUrl.includes ("?") ? "&" : "?";
        return `${tUrl}${tSep}${encodeURIComponent (pNom)}=${encodeURIComponent (String (pValeur || ""))}`;
      }
  }

function url_absolue_scene_svg_client (pUrl)
  {
    const tUrl = typeof pUrl === "string" ? pUrl.trim () : "";
    if (!tUrl) return "";
    try
      {
        const tBase = typeof window !== "undefined" && window.location
          ? window.location.href
          : "http://diffusion.local/";
        return (new URL (tUrl, tBase)).href;
      }
    catch (e)
      {
        return tUrl;
      }
  }

function signature_url_document_scene_svg_client (pUrl)
  {
    const tUrl = url_absolue_scene_svg_client (pUrl);
    if (!tUrl) return "";
    try
      {
        const tParsed = new URL (tUrl);
        return `${tParsed.origin}${tParsed.pathname}`;
      }
    catch (e)
      {
        return tUrl.split ("?")[0].split ("#")[0];
      }
  }

function document_svg_scene_correspond_source (pDoc, pObj)
  {
    if (!pDoc || !pObj) return false;
    const tSource = (pObj.getAttribute && (pObj.getAttribute ("data-svg-url") || pObj.getAttribute ("data")))
      || pObj.data
      || "";
    if (!tSource) return true;
    let tDocUrl = "";
    try
      {
        tDocUrl = (pDoc.location && pDoc.location.href)
          || pDoc.URL
          || "";
      }
    catch (e)
      {
        tDocUrl = "";
      }
    if (!tDocUrl || tDocUrl === "about:blank") return false;
    return signature_url_document_scene_svg_client (tDocUrl)
      === signature_url_document_scene_svg_client (tSource);
  }

function selecteur_id_svg_inline (pId)
  {
    const tId = String (pId || "");
    if (!tId) return null;
    if (typeof CSS !== "undefined" && CSS.escape)
      return "#" + CSS.escape (tId);
    return "[id=\"" + tId.replace (/\\/g, "\\\\").replace (/"/g, "\\\"") + "\"]";
  }

function cree_document_svg_inline_scene (pHost, pRoot, pUrl)
  {
    const tUrl = url_absolue_scene_svg_client (pUrl || "");
    return {
      _cogiInlineSvg : true,
      _cogiHost : pHost,
      documentElement : pRoot,
      defaultView : window,
      URL : tUrl,
      location : {href : tUrl},
      createElementNS : (...pArgs) => document.createElementNS (...pArgs),
      createTextNode : (...pArgs) => document.createTextNode (...pArgs),
      createDocumentFragment : (...pArgs) => document.createDocumentFragment (...pArgs),
      importNode : (...pArgs) => document.importNode (...pArgs),
      getElementById (pId)
        {
          if (!pRoot) return null;
          if (pRoot.id === pId) return pRoot;
          const tSelecteur = selecteur_id_svg_inline (pId);
          return tSelecteur ? pRoot.querySelector (tSelecteur) : null;
        },
      querySelector : (...pArgs) => pRoot.querySelector (...pArgs),
      querySelectorAll : (...pArgs) => pRoot.querySelectorAll (...pArgs),
      addEventListener (pType, pHandler, pOptions)
        {
          pHost.addEventListener (pType, pHandler, pOptions);
          if (!pHost._cogiSvgAdapterListeners) pHost._cogiSvgAdapterListeners = [];
          pHost._cogiSvgAdapterListeners.push ({type : pType, handler : pHandler, options : pOptions});
        },
      removeEventListener (pType, pHandler, pOptions)
        {
          pHost.removeEventListener (pType, pHandler, pOptions);
          if (!pHost._cogiSvgAdapterListeners) return;
          pHost._cogiSvgAdapterListeners = pHost._cogiSvgAdapterListeners.filter ((pItem) =>
            !(pItem.type === pType && pItem.handler === pHandler)
          );
        },
    };
  }

function nettoie_svg_inline_scene_host (pHost, pOptions = {})
  {
    if (!pHost) return;
    const tDoc = pHost._cogiSvgDoc || null;
    neutralise_ressources_svg_scene_doc (tDoc);
    (pHost._cogiSvgAdapterListeners || []).forEach ((pItem) =>
      {
        try { pHost.removeEventListener (pItem.type, pItem.handler, pItem.options); } catch (e) {}
      });
    pHost._cogiSvgAdapterListeners = [];
    pHost._cogiSvgDoc = null;
    pHost._cogiSvgSource = null;
    if (!(pOptions && pOptions.garderContenu))
      pHost.replaceChildren ();
  }

function neutralise_ressources_svg_scene_doc (pDoc)
  {
    const tRoot = pDoc && pDoc.documentElement;
    if (!tRoot) return;
    try
      {
        const tNoeuds = typeof tRoot.querySelectorAll === "function"
          ? [
              tRoot,
              ...Array.from (tRoot.querySelectorAll (
                "image, use, [href], [xlink\\:href], [src], [poster], [style*='url(']"
              )),
            ]
          : [tRoot];
        tNoeuds.forEach ((pNoeud) =>
          {
            if (!pNoeud || typeof pNoeud.removeAttribute !== "function") return;
            pNoeud.removeAttribute ("href");
            pNoeud.removeAttribute ("xlink:href");
            pNoeud.removeAttribute ("src");
            pNoeud.removeAttribute ("poster");
            if (typeof pNoeud.removeAttributeNS === "function")
              pNoeud.removeAttributeNS ("http://www.w3.org/1999/xlink", "href");
            if (pNoeud.style)
              {
                pNoeud.style.removeProperty ("background-image");
                pNoeud.style.removeProperty ("mask-image");
                pNoeud.style.removeProperty ("-webkit-mask-image");
              }
          });
        if (typeof tRoot.replaceChildren === "function")
          tRoot.replaceChildren ();
      }
    catch (e) {}
  }

function assainit_svg_inline_scene (pRoot)
  {
    if (!pRoot || typeof pRoot.querySelectorAll !== "function") return pRoot;
    pRoot.querySelectorAll ("script, object, iframe, embed, foreignObject")
      .forEach ((pNoeud) => { if (pNoeud.parentNode) pNoeud.parentNode.removeChild (pNoeud); });
    [pRoot, ...Array.from (pRoot.querySelectorAll ("*"))].forEach ((pNoeud) =>
      {
        Array.from (pNoeud.attributes || []).forEach ((pAttr) =>
          {
            const tNom = String (pAttr.name || "");
            const tValeur = String (pAttr.value || "");
            if (/^on/i.test (tNom) || /^\s*javascript:/i.test (tValeur))
              pNoeud.removeAttribute (tNom);
          });
      });
    return pRoot;
  }

function erreur_abort_scene_svg (pErr)
  {
    return !!(
      pErr
      && (
        pErr.name === "AbortError"
        || pErr.code === 20
        || /abort/i.test (String (pErr.message || pErr))
      )
    );
  }

function signal_abort_scene_svg (pOptions = {})
  {
    const tSignal = pOptions && pOptions.signal;
    if (tSignal && tSignal.aborted)
      {
        const tErreur = new Error ("Chargement SVG annulé");
        tErreur.name = "AbortError";
        throw tErreur;
      }
    if (pOptions && typeof pOptions.valideRemplacement === "function"
        && !pOptions.valideRemplacement ())
      {
        const tErreur = new Error ("Chargement SVG obsolète");
        tErreur.name = "AbortError";
        throw tErreur;
      }
  }

async function charge_svg_inline_scene_host (pHost, pUrl, pOptions = {})
  {
    const tUrl = typeof pUrl === "string" ? pUrl.trim () : "";
    if (!pHost || !tUrl) return null;
    signal_abort_scene_svg (pOptions);
    const tFetchOptions = {credentials : "include"};
    if (pOptions && pOptions.signal) tFetchOptions.signal = pOptions.signal;
    const tReponse = await fetch (tUrl, tFetchOptions);
    signal_abort_scene_svg (pOptions);
    if (!tReponse.ok) throw new Error ("Chargement SVG impossible");
    const tTexte = await tReponse.text ();
    signal_abort_scene_svg (pOptions);
    const tDoc = new DOMParser ().parseFromString (tTexte, "image/svg+xml");
    signal_abort_scene_svg (pOptions);
    const tErreur = tDoc.querySelector && tDoc.querySelector ("parsererror");
    const tRoot = tDoc && tDoc.documentElement;
    if (tErreur || !tRoot || String (tRoot.tagName || "").toLowerCase () !== "svg")
      throw new Error ("SVG invalide");

    assainit_svg_inline_scene (tRoot);
    signal_abort_scene_svg (pOptions);
    const tSvg = document.importNode (tRoot, true);
    tSvg.setAttribute ("focusable", "false");
    tSvg.setAttribute ("aria-hidden", "true");
    signal_abort_scene_svg (pOptions);
    nettoie_svg_inline_scene_host (pHost, {garderContenu : true});
    pHost.replaceChildren (tSvg);
    pHost._cogiSvgDoc = cree_document_svg_inline_scene (pHost, tSvg, tUrl);
    pHost._cogiSvgSource = tUrl;
    if (!pHost._cogiSvgFocusHandler)
      {
        pHost._cogiSvgFocusHandler = () =>
          {
            try { pHost.focus ({preventScroll : true}); } catch (e) {}
          };
        pHost.addEventListener ("pointerdown", pHost._cogiSvgFocusHandler, true);
      }
    return pHost._cogiSvgDoc;
  }

function document_svg_scene_valide (pDoc)
  {
    const tRoot = pDoc && pDoc.documentElement;
    if (!tRoot || !tRoot.tagName) return false;
    return String (tRoot.tagName).toLowerCase () === "svg";
  }

function document_svg_scene_depuis_conteneur (pObj, pOptions = {})
  {
    if (!pObj) return null;
    try
      {
        const tDoc = pObj._cogiSvgDoc || null;
        if (!document_svg_scene_valide (tDoc)) return null;
        if (pOptions.verifierSource && !document_svg_scene_correspond_source (tDoc, pObj))
          return null;
        return tDoc;
      }
    catch (e)
      {
        return null;
      }
  }

function annule_suivi_chargement_svg_tpl (pTpl)
  {
    if (pTpl && pTpl._svgInlineLoadTimer)
      {
        clearTimeout (pTpl._svgInlineLoadTimer);
        pTpl._svgInlineLoadTimer = null;
      }
    if (pTpl && pTpl._svgInlineLoadRaf)
      {
        cancelAnimationFrame (pTpl._svgInlineLoadRaf);
        pTpl._svgInlineLoadRaf = null;
      }
    if (pTpl)
      pTpl._svgInlineLoadRafKey = null;
    if (pTpl && pTpl._svgInlineAbort && typeof pTpl._svgInlineAbort.abort === "function")
      {
        try { pTpl._svgInlineAbort.abort (); } catch (e) {}
      }
    if (pTpl)
      pTpl._svgInlineAbort = null;
    if (!pTpl || !pTpl._svgObjectPollTimer) return;
    clearTimeout (pTpl._svgObjectPollTimer);
    pTpl._svgObjectPollTimer = null;
  }

function programme_chargement_svg_scene_tpl (pTpl, pSourceKey, pHandler)
  {
    if (!pTpl || typeof pHandler !== "function") return;
    if (pTpl._svgInlineLoadTimer)
      {
        clearTimeout (pTpl._svgInlineLoadTimer);
        pTpl._svgInlineLoadTimer = null;
      }
    if (pTpl._svgInlineLoadRaf)
      {
        cancelAnimationFrame (pTpl._svgInlineLoadRaf);
        pTpl._svgInlineLoadRaf = null;
      }
    pTpl._svgInlineLoadRafKey = pSourceKey || null;
    pTpl._svgInlineLoadRaf = requestAnimationFrame (() =>
      {
        pTpl._svgInlineLoadRaf = null;
        if ((pTpl._svgInlineLoadRafKey || null) !== (pSourceKey || null)) return;
        if ((pTpl._svgObjectSourceKey || null) !== (pSourceKey || null)) return;
        if (pTpl._svgObjectLoadHandler !== pHandler) return;
        pHandler ();
      });
  }

function reinitialise_etat_document_svg_tpl (pTpl)
  {
    if (!pTpl) return;
    if (pTpl._svgObjectReady) pTpl._svgObjectReady.set (false);
    pTpl._svgObjectReadySourceKey = null;
    annule_suivi_chargement_svg_tpl (pTpl);
    annule_animation_viewbox_tpl (pTpl, {commitCurrent : false});
    nettoie_photos_focus_carte_tpl (pTpl);
    if (pTpl._svgPanZoomUiRaf)
      {
        cancelAnimationFrame (pTpl._svgPanZoomUiRaf);
        pTpl._svgPanZoomUiRaf = null;
      }
	    arrete_flottement_bulles_lecture_tpl (pTpl);
	    pTpl._lectureBullesFlottementEtats = new Map ();
	    pTpl._lectureBullesFlottementNoeuds = new Map ();
      oublie_canevas_photo_focus_carte_tpl (pTpl);
	    detache_raccourci_espace_cartes_doc (pTpl._svgDoc, pTpl._cartesSpaceHandler);
	    if (pTpl._calquesEscHandler && pTpl._svgDoc)
	      pTpl._svgDoc.removeEventListener ("keydown", pTpl._calquesEscHandler);
	    nettoie_svg_inline_scene_host (pTpl._svgObjectNode);
	    pTpl._svgDoc = null;
	    pTpl._svgDocSceneId = null;
    pTpl._svgVBSourceBrut = null;
    pTpl._svgVBNatifBrut = null;
    pTpl._svgVBNatif = null;
    pTpl._svgVBBrut = null;
    pTpl._svgVBInitBrut = null;
    pTpl._svgVBInit = null;
    pTpl._svgVB = null;
    pTpl._svgApplique = null;
    pTpl._sceneGeometrieActive = null;
    pTpl._sceneRatioKey = null;
    pTpl._sceneGeometrieKey = null;
    const tSceneElt = pTpl.find && pTpl.find (".scene");
    if (tSceneElt)
      {
        tSceneElt.style.removeProperty ("--scene-frame-ratio");
        tSceneElt.style.removeProperty ("--scene-frame-ratio-num");
        tSceneElt.style.removeProperty ("--scene-frame-max-width");
      }
    gPanX.set (0);
    gPanY.set (0);
    gZoom.set (1);
  }

function ratio_cadrage_scene_tpl (pTpl)
  {
    const tRapport = rapport_lh_scene_projet_courant ();
    return {
      ratio : ratio_rapport_lh_scene_projet (tRapport),
      css : format_css_rapport_lh_scene_projet (tRapport),
    };
  }

function programme_maj_cadre_scene_dimensions_tpl (pTpl)
  {
    if (!pTpl || pTpl._sceneFrameResizeRaf) return;
    pTpl._sceneFrameResizeRaf = requestAnimationFrame (() =>
      {
        pTpl._sceneFrameResizeRaf = null;
        maj_cadre_scene_dimensions_tpl (pTpl);
      });
  }

function maj_cadre_scene_dimensions_tpl (pTpl)
  {
    if (!pTpl || typeof pTpl.find !== "function") return;
    const tSceneElt = pTpl.find (".scene");
    const tPrincipal = pTpl.find (".cadre_scene_principal");
    if (!tSceneElt || !tPrincipal) return;

    const tRatio = ratio_cadrage_scene_tpl (pTpl);
    if (tRatio && Number.isFinite (tRatio.ratio) && tRatio.ratio > 0)
      {
        tSceneElt.style.setProperty ("--scene-frame-ratio", tRatio.css);
        tSceneElt.style.setProperty ("--scene-frame-ratio-num", String (tRatio.ratio));
      }
    else
      {
        tSceneElt.style.removeProperty ("--scene-frame-ratio");
        tSceneElt.style.removeProperty ("--scene-frame-ratio-num");
      }

    const tRatioNum = (tRatio && Number.isFinite (tRatio.ratio) && tRatio.ratio > 0)
      ? tRatio.ratio
      : (16 / 9);
    const tStyle = (typeof window !== "undefined" && window.getComputedStyle)
      ? window.getComputedStyle (tPrincipal)
      : null;
    const tPaddingH = tStyle
      ? (parseFloat (tStyle.paddingTop) || 0) + (parseFloat (tStyle.paddingBottom) || 0)
      : 0;
    const tGap = tStyle
      ? (parseFloat (tStyle.rowGap || tStyle.gap) || 0)
      : 0;
    const tReglages = pTpl.find (".reglages_scene");
    const tHauteurReglages = tReglages ? tReglages.getBoundingClientRect ().height : 0;
    const tHauteurDisponible = tPrincipal.clientHeight
      - tPaddingH
      - tHauteurReglages
      - (tReglages ? tGap : 0);

    if (!Number.isFinite (tHauteurDisponible) || tHauteurDisponible <= 120)
      {
        tSceneElt.style.removeProperty ("--scene-frame-max-width");
        return;
      }

    const tMaxWidth = Math.max (280, Math.floor (tHauteurDisponible * tRatioNum));
    tSceneElt.style.setProperty ("--scene-frame-max-width", `${tMaxWidth}px`);
  }

function synchronise_observateur_cadre_scene_tpl (pTpl)
  {
    if (!pTpl || typeof pTpl.find !== "function") return;
    const tPrincipal = pTpl.find (".cadre_scene_principal");
    const tReglages = pTpl.find (".reglages_scene");
    if (!tPrincipal) return;

    const tTargets = pTpl._sceneFrameResizeTargets || {};
    const tMemesCibles = tTargets.principal === tPrincipal && tTargets.reglages === tReglages;
    if (!tMemesCibles)
      {
        if (pTpl._sceneFrameResizeObserver)
          pTpl._sceneFrameResizeObserver.disconnect ();
        pTpl._sceneFrameResizeObserver = null;
        if (typeof ResizeObserver !== "undefined")
          {
            pTpl._sceneFrameResizeObserver = new ResizeObserver (() =>
              {
                programme_maj_cadre_scene_dimensions_tpl (pTpl);
              });
            pTpl._sceneFrameResizeObserver.observe (tPrincipal);
            if (tReglages) pTpl._sceneFrameResizeObserver.observe (tReglages);
          }
        pTpl._sceneFrameResizeTargets = {principal : tPrincipal, reglages : tReglages};
      }

    if (!pTpl._sceneFrameWindowResizeHandler && typeof window !== "undefined")
      {
        pTpl._sceneFrameWindowResizeHandler = () =>
          {
            programme_maj_cadre_scene_dimensions_tpl (pTpl);
          };
        window.addEventListener ("resize", pTpl._sceneFrameWindowResizeHandler);
      }
    programme_maj_cadre_scene_dimensions_tpl (pTpl);
  }

function nettoie_observateur_cadre_scene_tpl (pTpl)
  {
    if (!pTpl) return;
    if (pTpl._sceneFrameResizeRaf)
      {
        cancelAnimationFrame (pTpl._sceneFrameResizeRaf);
        pTpl._sceneFrameResizeRaf = null;
      }
    if (pTpl._sceneFrameResizeObserver)
      pTpl._sceneFrameResizeObserver.disconnect ();
    pTpl._sceneFrameResizeObserver = null;
    pTpl._sceneFrameResizeTargets = null;
    if (pTpl._sceneFrameWindowResizeHandler && typeof window !== "undefined")
      window.removeEventListener ("resize", pTpl._sceneFrameWindowResizeHandler);
    pTpl._sceneFrameWindowResizeHandler = null;
  }

function definition_inspecteur_categorie_scene (pCategorie)
  {
    if (!pCategorie) return null;
    if (INSPECTEURS_CATEGORIES_SCENE[pCategorie])
      return INSPECTEURS_CATEGORIES_SCENE[pCategorie];
    return {
      titre : `Éléments "${pCategorie}" de la scène`,
      description : "",
      countLabel : "Nombre d'éléments",
    };
  }

function compte_elements_categorie_scene (pCategorie)
  {
    if (!pCategorie) return 0;
    if (pCategorie === CALQUES_CATEGORIE_BULLES) return cartes_scene_liste ().length;

    const tSceneId = Session.get ("scene_id");
    if (!tSceneId) return 0;
    const tScene = Scenes.findOne (tSceneId);
    const tObjets = svg_objets_scene_normalises (tScene);
    const tBrut = tObjets && tObjets[pCategorie];
    return Array.isArray (tBrut) ? tBrut.length : 0;
  }

function inspecteur_categorie_active_scene ()
  {
    const tCategorie = gCategorieSelectionnee.get ();
    if (!tCategorie) return null;

    const tDefinition = definition_inspecteur_categorie_scene (tCategorie);
    if (!tDefinition) return null;

    const tCount = compte_elements_categorie_scene (tCategorie);
    const tBase = {
      templateName : "sceneInspecteurCategorieResume",
      categorie : tCategorie,
      titre : tDefinition.titre,
      description : tDefinition.description,
      countLabel : tDefinition.countLabel,
      globalCount : tCount,
      messageText : null,
      messageType : "",
      primaryActionClass : null,
      primaryActionDisabledAttr : null,
      primaryActionLabel : null,
      contourEditionVisible : false,
      contourCouleur : null,
      contourEpaisseur : null,
      contourOpacitePct : null,
    };

    if (tCategorie === CALQUES_CATEGORIE_BULLES)
      {
        const tRecreationEnCours = gInspecteurCartesGlobalAction.get () === "recreation";
        const tInterfaceEnCours = gInspecteurCartesInterfaceAction.get () === "recreation";
        const tMessage = gInspecteurCartesGlobalMessage.get ();
        const tReglagesInterface = brouillon_reglages_interface_carte_scene ()
          || reglages_interface_carte_pour_stockage (base_reglages_interface_carte ());

        return {
          ...tBase,
          messageText : tMessage ? tMessage.texte : null,
          messageType : tMessage ? tMessage.type : "",
          primaryActionClass : "btn btn-primary js_inspecteur_cartes_recreer_bulles",
          primaryActionDisabledAttr : (tRecreationEnCours || !tCount) ? "disabled" : null,
          primaryActionLabel : tRecreationEnCours
            ? "Recréation..."
            : "Recréer toutes les bulles",
          interfaceCarteVisible : true,
          interfaceCarteItems : items_interface_carte_inspecteur (
            tReglagesInterface,
            "inspecteur_categorie_interface_carte"
          ),
          interfaceCarteCadreEpaisseurHaut : tReglagesInterface.zoom_alternatif_cadre_epaisseur_haut,
          interfaceCarteCadreEpaisseurDroite : tReglagesInterface.zoom_alternatif_cadre_epaisseur_droite,
          interfaceCarteCadreEpaisseurBas : tReglagesInterface.zoom_alternatif_cadre_epaisseur_bas,
          interfaceCarteCadreEpaisseurGauche : tReglagesInterface.zoom_alternatif_cadre_epaisseur_gauche,
          interfaceCarteCadreCouleur : tReglagesInterface.zoom_alternatif_cadre_couleur,
          interfaceCarteFocusFacteur : tReglagesInterface.zoom_alternatif_focus_facteur,
          interfaceCarteFocusOffsetX : tReglagesInterface.zoom_alternatif_focus_offset_x,
          interfaceCarteFocusOffsetY : tReglagesInterface.zoom_alternatif_focus_offset_y,
          interfaceCarteActionDisabledAttr : (tInterfaceEnCours || !tCount) ? "disabled" : null,
          interfaceCarteActionLabel : tInterfaceEnCours ? "Application..." : "Appliquer à toutes les cartes",
        };
      }

    if (contour_categorie_supportee (tCategorie))
      {
        const tRecreationEnCours = gInspecteurContoursGlobalAction.get () === "recreation";
        const tMessage = gInspecteurContoursGlobalMessage.get ();
        const tContour = reglages_survol_contour_categorie_brouillon (tCategorie);
        const tLibelleCategorie = tCategorie === CALQUES_CATEGORIE_BOUTONS ? "boutons" : "objets";

        return {
          ...tBase,
          contourEditionVisible : true,
          contourCouleur : tContour.couleur,
          contourEpaisseur : tContour.epaisseur,
          contourOpacitePct : Math.round ((tContour.opacite || 0) * 100),
          messageText : tMessage ? tMessage.texte : null,
          messageType : tMessage ? tMessage.type : "",
          primaryActionClass : "btn btn-primary js_inspecteur_categorie_recreer_contours",
          primaryActionDisabledAttr : (tRecreationEnCours || !tCount) ? "disabled" : null,
          contourCategorie : tCategorie,
          primaryActionLabel : tRecreationEnCours
            ? "Recréation..."
            : `Régénérer les contours des ${tLibelleCategorie}`,
        };
      }

    return tBase;
  }

function inspecteur_calques_mode_scene ()
  {
    const tMode = gInspecteurCalquesMode.get ();
    if (tMode === "categorie" && gCategorieSelectionnee.get ()) return "categorie";
    if (tMode === "bulle" && gBulleSelectionnee.get ()) return "bulle";
    if (tMode === "calque" && gCalqueSelectionne.get ()) return "calque";
    if (gCategorieSelectionnee.get ()) return "categorie";
    if (gBulleSelectionnee.get ()) return "bulle";
    if (gCalqueSelectionne.get ()) return "calque";
    return null;
  }

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

Template.scene.helpers
  ({
    scene_courante ()
      {
        const tId = Session.get ("scene_id");
        if (!tId) return null;
        return Scenes.findOne (tId);
      },

    svg_url_scene ()
      {
        const tInfos = infos_svg_scene_courante ();
        let tUrl = tInfos.url;
        if (tUrl && tInfos.runtime)
          tUrl = ajoute_parametre_url_scene_svg (tUrl, "cogi_svg_runtime", "1");
        const tTpl = Template.instance ();
        const tToken = tTpl && tTpl._svgObjectReloadToken
          ? tTpl._svgObjectReloadToken.get ()
          : null;
        return tToken
          ? ajoute_parametre_url_scene_svg (tUrl, "cogi_svg_reload", tToken)
          : tUrl;
      },

    svg_scene_en_attente ()
      {
        const tInfos = infos_svg_scene_courante ();
        return !!(tInfos.scene && tInfos.scene.svg_id && !tInfos.fichier);
      },

    svg_object_visible ()
      {
        const tTpl = Template.instance ();
        return !!(tTpl && tTpl._svgObjectVisible && tTpl._svgObjectVisible.get ());
      },

    ambiance_controles_visible ()
      {
        return !!(
          mode_lecture_scene_actif ()
          && file_id_audio_scene_lecture_scene ("ambiance")
          && reglages_ambiance_lecture_scene_courante ().controles
        );
      },

    ambiance_controles_style_attr ()
      {
        const tReglages = reglages_ambiance_lecture_scene_courante ();
        return [
          `--ambiance-controles-fond: ${couleur_fond_ambiance_rgba (tReglages)}`,
          `--ambiance-controles-couleur: ${tReglages.couleur_controles}`,
          `border-radius: ${tReglages.arrondi_px}px`,
          `padding: ${tReglages.marge_interne_px}px`,
          `left: ${tReglages.marge_laterale_px}px`,
          `right: ${tReglages.marge_laterale_px}px`,
          `bottom: ${tReglages.marge_basse_px}px`,
        ].join ("; ");
      },

    ambiance_controles_en_lecture ()
      {
        return gLectureSceneAmbianceEtat.get ().lecture;
      },

    ambiance_controles_progression_pct ()
      {
        const tEtat = gLectureSceneAmbianceEtat.get ();
        if (!tEtat.duree) return 0;
        return Math.max (0, Math.min (100, (tEtat.position / tEtat.duree) * 100)).toFixed (2);
      },

    ambiance_controles_temps ()
      {
        const tEtat = gLectureSceneAmbianceEtat.get ();
        return formate_temps_ambiance_lecture (tEtat.position) + " / " + formate_temps_ambiance_lecture (tEtat.duree);
      },

    scene_fullscreen_visible ()
      {
        const tTpl = Template.instance ();
        // Appli embarquée : déjà en plein écran, le bouton n'a pas d'objet.
        if (est_runtime_embarque ()) return false;
        return !!(
          mode_lecture_scene_actif ()
          && infos_svg_scene_courante ().url
          && !diffusion_est_webapp_plein_ecran ()
          && !(tTpl && tTpl._sceneFullscreenExterneActive && tTpl._sceneFullscreenExterneActive.get ())
        );
      },

    scene_fullscreen_active ()
      {
        const tTpl = Template.instance ();
        return !!(tTpl && tTpl._sceneFullscreenActive && tTpl._sceneFullscreenActive.get ());
      },

    scene_fullscreen_label ()
      {
        const tTpl = Template.instance ();
        return tTpl && tTpl._sceneFullscreenActive && tTpl._sceneFullscreenActive.get ()
          ? "Quitter le plein écran"
          : "Afficher la scène en plein écran";
      },

    scene_test_banner_visible ()
      {
        return Session.get ("diffusion_mode_test") === true;
      },

    cadre_scene_class ()
      {
        const tClasses = [];
        if (mode_lecture_scene_actif ()) tClasses.push ("mode_lecture_scene");
        if (!inspecteur_scene_est_visible ()) tClasses.push ("inspecteur_scene_masque");
        return tClasses.join (" ");
      },

    scene_class ()
      {
        const tClasses = [];
        if (gUploadSvgEnCours.get ()) tClasses.push ("upload_en_cours");
        if (mode_lecture_scene_actif ())
          {
            tClasses.push ("mode_lecture_scene");
            const tTpl = Template.instance ();
            const tPret = !!(tTpl && tTpl._svgObjectReady && tTpl._svgObjectReady.get ());
            const tInfos = infos_svg_scene_courante ();
            if (tInfos.scene && tInfos.scene.svg_id && !tPret)
              tClasses.push ("svg_scene_non_prete");
          }
        return tClasses.join (" ");
      },

    scene_transition_class ()
      {
        if (!gLectureSceneTransition.get ()) return "";
        return gLectureSceneTransitionSortie.get ()
          ? "transition_lecture_scene transition_lecture_scene_sortie"
          : "transition_lecture_scene";
      },

    dialog_rejouer_scene_visible ()
      {
        return !!gLectureRejouerSceneDialog.get ();
      },

    scene_style_attr ()
      {
        const tMasque = brouillon_mode_lecture_masque_scene ();
        const tRapport = rapport_lh_scene_projet_courant ();
        const tRatioNum = ratio_rapport_lh_scene_projet (tRapport) || (16 / 9);
        return [
          `--scene-transition-fade-ms: ${duree_transition_lecture_scene_ms ()}ms`,
          `--scene-frame-ratio: ${format_css_rapport_lh_scene_projet (tRapport)}`,
          `--scene-frame-ratio-num: ${tRatioNum}`,
          `--scene-background-color: ${tMasque.fond || "#ffffff"}`,
        ].join ("; ") + ";";
      },

    scene_lecture_disabled_attr ()
      {
        return mode_lecture_scene_actif () ? "disabled" : null;
      },

    colonne_droite_scene_visible ()
      {
        return inspecteur_scene_est_visible ();
      },

    scene_help_visible ()
      {
        const tInfos = infos_svg_scene_courante ();
        return !!(tInfos.url && gSceneHelpOpen.get () && !mode_lecture_scene_actif ());
      },

    scene_help_toggle_active_class ()
      {
        return gSceneHelpOpen.get () ? "est_actif" : null;
      },

    svg_objets_scene ()
      {
        const tId = Session.get ("scene_id");
        if (!tId) return null;
        const tScene = Scenes.findOne (tId);
        return svg_objets_scene_normalises (tScene);
      },

    categories_arbre ()
      {
        const tId = Session.get ("scene_id");
        if (!tId) return [];
        const tScene = Scenes.findOne (tId);
        const tObjets = svg_objets_scene_normalises (tScene);
        if (!tObjets) return [];

        const tListeParNom = new Map ();
        for (const tNom of CATEGORIES_PERTINENTES)
          {
            const tBrut = tObjets[tNom];
            if (!Array.isArray (tBrut) || tBrut.length === 0) continue;
            const tVisible = categorie_scene_visible (tNom, tId);
            const tVerrouillee = categorie_scene_verrouillee (tNom, tId);
            const tCalques = tBrut.map ((c) =>
              ({
                id          : c.id,
                nom         : nom_affiche_calque_arbre (c["data-name"], tNom) || c.id,
                cle_tri     : cle_tri_calque (c["data-name"], tNom),
              }));
            tCalques.sort ((a, b) =>
              a.cle_tri.localeCompare (b.cle_tri, "fr", {sensitivity : "base"}));
            tListeParNom.set (
              tNom,
              {
                nom         : tNom,
                ouverte     : categorie_scene_ouverte (tNom, tId),
                calques     : tCalques,
                visible     : tVisible,
                toggle_label: tVisible ? "Masquer" : "Montrer",
                toggle_class: tVisible ? "" : "est_actif",
                verrouillee : tVerrouillee,
                lock_label  : tVerrouillee ? "Déverrouiller" : "Verrouiller",
                lock_class  : tVerrouillee ? "est_actif" : "",
              }
            );
          }
        tListeParNom.set (CALQUES_CATEGORIE_BULLES, categorie_bulles_arbre_calques ());
        return ordre_categories_calques_scene (tId, Array.from (tListeParNom.keys ()))
          .map ((pNom) => tListeParNom.get (pNom))
          .filter (Boolean);
      },

    upload_svg_en_cours ()
      {
        return gUploadSvgEnCours.get ();
      },

    upload_svg_progression ()
      {
        return gUploadSvgProgression.get ();
      },

    upload_svg_phase_label ()
      {
        const tPh = gUploadSvgPhase.get ();
        if (tPh === "svg")      return "Téléversement du SVG…";
        if (tPh === "vignette") return "Création de la vignette…";
        return "Téléversement…";
      },

    sensibilite_zoom ()
      {
        return gSensibiliteZoom.get ();
      },

    zoom_sur_curseur ()
      {
        return gZoomSurCurseur.get ();
      },

    zoom_sur_curseur_checked ()
      {
        return gZoomSurCurseur.get () ? "checked" : null;
      },

    vue_message ()
      {
        const tM = gVueMessage.get ();
        return tM ? tM.texte : null;
      },

    vue_message_type ()
      {
        const tM = gVueMessage.get ();
        return tM ? tM.type : "";
      },

    zoom_sur_selection_disabled_attr ()
      {
        if (mode_lecture_scene_actif ()) return "disabled";
        return cible_zoom_selection_scene_active () ? null : "disabled";
      },

    largeur_colonne_droite ()
      {
        return gLargeurColDroite.get ();
      },

    onglets_droite ()
      {
        const tAct = gOngletDroite.get ();
        return ONGLETS_DROITE.map ((o) => ({...o, actif : o.cle === tAct}));
      },

    onglet_actif_est (pCle)
      {
        return gOngletDroite.get () === pCle;
      },

    onglet_projet_style ()
      {
        return gOngletDroite.get () === "projet" ? "" : "display:none;";
      },

    onglet_voix_style ()
      {
        return gOngletDroite.get () === "voix" ? "" : "display:none;";
      },

    inspecteur_voix_profils ()
      {
        return VoiceProfiles.find ({}, {sort : {createdAt : -1}}).map ((pProfil) =>
          {
            const tSamples = Array.isArray (pProfil.samples) ? pProfil.samples : [];
            return {
              ...pProfil,
              languageLabel : label_langue_voix_scene (pProfil.language),
              sampleCount : tSamples.length,
              pluralSamples : tSamples.length > 1,
            };
          });
      },

    inspecteur_voix_seeds ()
      {
        return Seeds.find ({}, {sort : {createdAt : -1}}).fetch ();
      },

    inspecteur_section_repliable (pCle, pEtatParDefaut)
      {
        return infos_section_repliable_inspecteur (pCle, pEtatParDefaut);
      },

    inspecteur_section_repliable_avec_items (pCle, pEtatParDefaut, pItems)
      {
        return {
          ...infos_section_repliable_inspecteur (pCle, pEtatParDefaut),
          items : Array.isArray (pItems) ? pItems : [],
        };
      },

    inspecteur_projet_mode_lecture_repliee_class ()
      {
        return infos_section_repliable_inspecteur (
          "projet_reglages_mode_lecture",
          "fermee"
        ).replieeClass;
      },

    inspecteur_projet_mode_lecture_aria_expanded ()
      {
        return infos_section_repliable_inspecteur (
          "projet_reglages_mode_lecture",
          "fermee"
        ).ariaExpanded;
      },

    inspecteur_projet_mode_lecture_default_open_attr ()
      {
        return infos_section_repliable_inspecteur (
          "projet_reglages_mode_lecture",
          "fermee"
        ).defaultOpenAttr;
      },

    inspecteur_projet_cartes_repliee_class ()
      {
        return infos_section_repliable_inspecteur (
          "projet_reglages_mode_lecture_cartes",
          "fermee"
        ).replieeClass;
      },

    inspecteur_projet_cartes_aria_expanded ()
      {
        return infos_section_repliable_inspecteur (
          "projet_reglages_mode_lecture_cartes",
          "fermee"
        ).ariaExpanded;
      },

    inspecteur_projet_cartes_default_open_attr ()
      {
        return infos_section_repliable_inspecteur (
          "projet_reglages_mode_lecture_cartes",
          "fermee"
        ).defaultOpenAttr;
      },

    inspecteur_projet_objets_section_style ()
      {
        return inspecteur_section_repliable_est_ouverte (
          "projet_reglages_mode_lecture_cartes",
          "fermee"
        ) ? "" : "display:none;";
      },

    inspecteur_projet_cartes_interface_repliee_class ()
      {
        return infos_section_repliable_inspecteur (
          "projet_reglages_mode_lecture_cartes_interface",
          "ouverte"
        ).replieeClass;
      },

    inspecteur_projet_cartes_interface_aria_expanded ()
      {
        return infos_section_repliable_inspecteur (
          "projet_reglages_mode_lecture_cartes_interface",
          "ouverte"
        ).ariaExpanded;
      },

    inspecteur_projet_cartes_interface_default_open_attr ()
      {
        return infos_section_repliable_inspecteur (
          "projet_reglages_mode_lecture_cartes_interface",
          "ouverte"
        ).defaultOpenAttr;
      },

    inspecteur_modifie ()
      {
        const tId = Session.get ("scene_id");
        if (!tId) return false;
        const tScene = Scenes.findOne (tId);
        if (!tScene) return false;
        const tT = gInspecteurTitre.get ();
        const tS = gInspecteurSlug.get ();
        const tTitre = tT === null ? (tScene.titre || "") : tT;
        const tSlug  = tS === null ? (tScene.slug  || "") : tS;
        return tTitre.trim () !== (tScene.titre || "")
            || tSlug.trim ()  !== (tScene.slug  || "");
      },

    inspecteur_valeur_titre ()
      {
        const tT = gInspecteurTitre.get ();
        if (tT !== null) return tT;
        const tId = Session.get ("scene_id");
        if (!tId) return "";
        const tScene = Scenes.findOne (tId);
        return (tScene && tScene.titre) || "";
      },

    inspecteur_valeur_slug ()
      {
        const tS = gInspecteurSlug.get ();
        if (tS !== null) return tS;
        const tId = Session.get ("scene_id");
        if (!tId) return "";
        const tScene = Scenes.findOne (tId);
        return (tScene && tScene.slug) || "";
      },

    inspecteur_message ()
      {
        const tM = gInspecteurMessage.get ();
        return tM ? tM.texte : null;
      },

    inspecteur_message_type ()
      {
        const tM = gInspecteurMessage.get ();
        return tM ? tM.type : "";
      },

    inspecteur_style_interface_selection_color ()
      {
        return style_interface_scene_previsualise ().selection_color;
      },

    inspecteur_style_interface_hover_color ()
      {
        return style_interface_scene_previsualise ().hover_color;
      },

    inspecteur_style_interface_stroke_width ()
      {
        return style_interface_scene_previsualise ().stroke_width;
      },

    inspecteur_style_interface_trait_options ()
      {
        const tStyle = style_interface_scene_previsualise ();
        return STYLE_INTERFACE_TRAIT_OPTIONS.map ((pOpt) =>
          ({
            ...pOpt,
            selectedAttr : pOpt.value === tStyle.stroke_style ? "selected" : null,
          }));
      },

    inspecteur_style_interface_rainbow_checked ()
      {
        return style_interface_scene_previsualise ().rainbow_category ? "checked" : null;
      },

    inspecteur_style_interface_apply_future_checked ()
      {
        return gInspecteurStyleApplyFuture.get () ? "checked" : null;
      },

    inspecteur_style_interface_message ()
      {
        const tM = gInspecteurStyleMessage.get ();
        return tM ? tM.texte : null;
      },

    inspecteur_style_interface_message_type ()
      {
        const tM = gInspecteurStyleMessage.get ();
        return tM ? tM.type : "";
      },

    inspecteur_style_interface_apply_scene_disabled_attr ()
      {
        return gInspecteurStyleSaving.get () ? "disabled" : null;
      },

    inspecteur_style_interface_apply_project_disabled_attr ()
      {
        return gInspecteurStyleSaving.get () ? "disabled" : null;
      },

    inspecteur_style_interface_apply_scene_label ()
      {
        return gInspecteurStyleSaving.get () === "scene"
          ? "Application…"
          : "Appliquer à la scène";
      },

    inspecteur_style_interface_apply_project_label ()
      {
        return gInspecteurStyleSaving.get () === "project"
          ? "Application…"
          : "Appliquer à toutes les scènes";
      },

    inspecteur_scene_mode_lecture_masque_couleur ()
      {
        return brouillon_mode_lecture_masque_scene ().couleur;
      },

    inspecteur_scene_mode_lecture_masque_opacite ()
      {
        return brouillon_mode_lecture_masque_scene ().opacite;
      },

    inspecteur_scene_mode_lecture_message ()
      {
        const tM = gInspecteurSceneModeLectureMasqueMessage.get ();
        return tM ? tM.texte : null;
      },

    inspecteur_scene_mode_lecture_message_type ()
      {
        const tM = gInspecteurSceneModeLectureMasqueMessage.get ();
        return tM ? tM.type : "";
      },

    inspecteur_scene_mode_lecture_masque_sauvegarde_disabled_attr ()
      {
        return gInspecteurSceneModeLectureMasqueSaving.get () ? "disabled" : null;
      },

    inspecteur_scene_mode_lecture_masque_sauvegarde_label ()
      {
        return gInspecteurSceneModeLectureMasqueSaving.get ()
          === "scene" ? "Application…"
          : "Appliquer à la scène";
      },

    inspecteur_scene_mode_lecture_masque_appliquer_projet_label ()
      {
        return gInspecteurSceneModeLectureMasqueSaving.get ()
          === "project" ? "Application…"
          : "Appliquer à toutes les scènes";
      },

    inspecteur_scene_geometrie_offset_x ()
      {
        return valeur_affichage_geometrie_scene_client ("offset_x", brouillon_geometrie_scene ().offset_x);
      },

    inspecteur_scene_geometrie_offset_y ()
      {
        return valeur_affichage_geometrie_scene_client ("offset_y", brouillon_geometrie_scene ().offset_y);
      },

    inspecteur_scene_geometrie_offset_z ()
      {
        return valeur_affichage_geometrie_scene_client ("offset_z", brouillon_geometrie_scene ().offset_z);
      },

    inspecteur_scene_geometrie_message ()
      {
        const tM = gInspecteurSceneGeometrieMessage.get ();
        return tM ? tM.texte : null;
      },

    inspecteur_scene_geometrie_message_type ()
      {
        const tM = gInspecteurSceneGeometrieMessage.get ();
        return tM ? tM.type : "";
      },

    inspecteur_scene_geometrie_sauvegarde_disabled_attr ()
      {
        return gInspecteurSceneGeometrieSaving.get () ? "disabled" : null;
      },

    inspecteur_scene_geometrie_sauvegarde_label ()
      {
        return gInspecteurSceneGeometrieSaving.get ()
          === "scene" ? "Application…"
          : "Appliquer à la scène";
      },

    inspecteur_projet_valeur_nom ()
      {
        const tProjet = projet_courant_scene ();
        const tVal = gInspecteurProjetNom.get ();
        if (tVal !== null) return tVal;
        return (tProjet && tProjet.nom) || "";
      },

    inspecteur_projet_valeur_slug ()
      {
        const tProjet = projet_courant_scene ();
        const tVal = gInspecteurProjetSlug.get ();
        if (tVal !== null) return tVal;
        return (tProjet && tProjet.slug) || "";
      },

    inspecteur_projet_type ()
      {
        const tProjet = projet_courant_scene ();
        return (tProjet && tProjet.type) || "";
      },

    inspecteur_projet_rapport_lh_scene ()
      {
        return format_rapport_lh_scene_projet (brouillon_rapport_lh_scene_projet ());
      },

    inspecteur_projet_bulles_upload_label ()
      {
        const tEtat = gInspecteurProjetBullesUpload.get ();
        if (!tEtat) return "Ajouter des SVG…";
        return `Téléversement ${tEtat.index}/${tEtat.total} (${tEtat.progress || 0}%)…`;
      },

    inspecteur_projet_bulles_upload_disabled_attr ()
      {
        if (!utilisateur_peut_modifier_projet_inspecteur ()) return "disabled";
        return (gInspecteurProjetBullesUpload.get () || gInspecteurProjetBullesBulkDelete.get ())
          ? "disabled"
          : null;
      },

    inspecteur_projet_bulles_delete_all_label ()
      {
        return gInspecteurProjetBullesBulkDelete.get ()
          ? "Suppression…"
          : "Supprimer tous les SVG";
      },

    inspecteur_projet_bulles_delete_all_disabled_attr ()
      {
        if (!utilisateur_peut_modifier_projet_inspecteur ()) return "disabled";
        if (!fichiers_bulles_projet_courant ().length) return "disabled";
        return (gInspecteurProjetBullesUpload.get () || gInspecteurProjetBullesBulkDelete.get ())
          ? "disabled"
          : null;
      },

    inspecteur_projet_bulles_svgs ()
      {
        return fichiers_bulles_projet_courant ();
      },

    inspecteur_projet_bulles_has_svgs ()
      {
        return fichiers_bulles_projet_courant ().length > 0;
      },

    inspecteur_projet_bulles_color_picker ()
      {
        return couleur_picker_bulles_projet ();
      },

    inspecteur_projet_bulles_couleurs ()
      {
        return brouillon_reglages_bulles_projet ().couleurs_auto.map ((pCouleur) =>
          ({
            value : pCouleur,
            removeDisabledAttr : utilisateur_peut_modifier_projet_inspecteur () ? null : "disabled",
            styleAttr : `background:${pCouleur};`,
          }));
      },

    inspecteur_projet_bulles_has_colors ()
      {
        return brouillon_reglages_bulles_projet ().couleurs_auto.length > 0;
      },

    inspecteur_projet_bulles_taille_bulle_min ()
      {
        return brouillon_reglages_bulles_projet ().taille_bulle_min;
      },

    inspecteur_projet_bulles_taille_bulle_max ()
      {
        return brouillon_reglages_bulles_projet ().taille_bulle_max;
      },

    inspecteur_projet_bulles_taille_bulle_custom_defaut ()
      {
        return brouillon_reglages_bulles_projet ().taille_bulle_custom_defaut;
      },

    inspecteur_projet_bulles_rotation_bulle_min ()
      {
        return brouillon_reglages_bulles_projet ().rotation_bulle_min;
      },

    inspecteur_projet_bulles_rotation_bulle_max ()
      {
        return brouillon_reglages_bulles_projet ().rotation_bulle_max;
      },

    inspecteur_projet_bulles_lecture_flottement_vitesse_deplacement ()
      {
        return brouillon_reglages_bulles_projet ().lecture_flottement_vitesse_deplacement;
      },

    inspecteur_projet_bulles_lecture_flottement_deplacement_max_x ()
      {
        return brouillon_reglages_bulles_projet ().lecture_flottement_deplacement_max_x;
      },

    inspecteur_projet_bulles_lecture_flottement_deplacement_max_y ()
      {
        return brouillon_reglages_bulles_projet ().lecture_flottement_deplacement_max_y;
      },

    inspecteur_projet_bulles_lecture_flottement_vitesse_taille ()
      {
        return brouillon_reglages_bulles_projet ().lecture_flottement_vitesse_taille;
      },

    inspecteur_projet_bulles_lecture_flottement_taille_min_pct ()
      {
        return brouillon_reglages_bulles_projet ().lecture_flottement_taille_min_pct;
      },

    inspecteur_projet_bulles_lecture_flottement_taille_max_pct ()
      {
        return brouillon_reglages_bulles_projet ().lecture_flottement_taille_max_pct;
      },

    inspecteur_projet_bulles_lecture_flottement_vitesse_rotation ()
      {
        return brouillon_reglages_bulles_projet ().lecture_flottement_vitesse_rotation;
      },

    inspecteur_projet_bulles_lecture_flottement_rotation_min_pct ()
      {
        return brouillon_reglages_bulles_projet ().lecture_flottement_rotation_min_pct;
      },

    inspecteur_projet_bulles_lecture_flottement_rotation_max_pct ()
      {
        return brouillon_reglages_bulles_projet ().lecture_flottement_rotation_max_pct;
      },

    inspecteur_projet_bulles_message ()
      {
        const tM = gInspecteurProjetBullesMessage.get ();
        return tM ? tM.texte : null;
      },

    inspecteur_projet_bulles_message_type ()
      {
        const tM = gInspecteurProjetBullesMessage.get ();
        return tM ? tM.type : "";
      },

    inspecteur_projet_bulles_context_menu_open ()
      {
        return !!gInspecteurProjetBullesContextMenu.get ();
      },

    inspecteur_projet_bulles_context_menu_style ()
      {
        const tMenu = gInspecteurProjetBullesContextMenu.get ();
        if (!tMenu) return "display:none;";
        return `left:${tMenu.x}px; top:${tMenu.y}px;`;
      },

    inspecteur_projet_hype_widget (pType)
      {
        const tWidget = widget_hype_projet (type_widget_hype_normalise (pType));
        if (!tWidget) return null;
        return {
          ...tWidget,
          nomAffiche : tWidget.oam_file_name || tWidget.nom || "widget.oam",
          url : url_widget_hype_client (tWidget),
        };
      },

    inspecteur_projet_hype_widget_carte ()
      {
        const tWidget = widget_hype_projet ("recompense_carte");
        if (!tWidget) return null;
        return {
          ...tWidget,
          nomAffiche : tWidget.oam_file_name || tWidget.nom || "widget.oam",
          url : url_widget_hype_client (tWidget),
        };
      },

    inspecteur_projet_hype_upload_label (pType)
      {
        const tType = type_widget_hype_normalise (pType);
        const tUpload = gInspecteurProjetHypeUpload.get (tType);
        if (!tUpload) return "Charger un fichier Hype (.oam)";
        if (tUpload.phase === "traitement") return "Installation…";
        return `Téléversement ${tUpload.progression || 0}%…`;
      },

    inspecteur_projet_hype_upload_disabled_attr (pType)
      {
        const tType = type_widget_hype_normalise (pType);
        if (!utilisateur_peut_modifier_projet_inspecteur ()) return "disabled";
        return gInspecteurProjetHypeUpload.get (tType) ? "disabled" : null;
      },

    inspecteur_projet_hype_message (pType)
      {
        const tType = type_widget_hype_normalise (pType);
        const tM = gInspecteurProjetHypeMessages.get (tType);
        return tM ? tM.texte : null;
      },

    inspecteur_projet_hype_message_type (pType)
      {
        const tType = type_widget_hype_normalise (pType);
        const tM = gInspecteurProjetHypeMessages.get (tType);
        return tM ? tM.type : "";
      },

    inspecteur_projet_hype_preview_style (pType)
      {
        return style_apercu_widget_hype (type_widget_hype_normalise (pType));
      },

    inspecteur_projet_hype_fond_transparent_checked (pType)
      {
        const tReglages = brouillon_reglages_mode_lecture_projet ();
        return valeur_reglage_widget_hype (tReglages, pType, "fond_transparent") ? "checked" : null;
      },

    inspecteur_projet_hype_couleur_fond (pType)
      {
        return valeur_reglage_widget_hype (brouillon_reglages_mode_lecture_projet (), pType, "couleur_fond");
      },

    inspecteur_projet_hype_taille_pct (pType)
      {
        return valeur_reglage_widget_hype (brouillon_reglages_mode_lecture_projet (), pType, "taille_pct");
      },

    inspecteur_projet_hype_align_x_selected (pType, pValeur)
      {
        const tType = pValeur === undefined ? "recompense_carte" : pType;
        const tValeur = pValeur === undefined ? pType : pValeur;
        return valeur_reglage_widget_hype (brouillon_reglages_mode_lecture_projet (), tType, "align_x") === tValeur ? "selected" : null;
      },

    inspecteur_projet_hype_align_y_selected (pType, pValeur)
      {
        const tType = pValeur === undefined ? "recompense_carte" : pType;
        const tValeur = pValeur === undefined ? pType : pValeur;
        return valeur_reglage_widget_hype (brouillon_reglages_mode_lecture_projet (), tType, "align_y") === tValeur ? "selected" : null;
      },

    inspecteur_projet_hype_offset_x_pct (pType)
      {
        return valeur_reglage_widget_hype (brouillon_reglages_mode_lecture_projet (), pType, "offset_x_pct");
      },

    inspecteur_projet_hype_offset_y_pct (pType)
      {
        return valeur_reglage_widget_hype (brouillon_reglages_mode_lecture_projet (), pType, "offset_y_pct");
      },

    inspecteur_projet_hype_disparition_ms (pType)
      {
        return valeur_reglage_widget_hype (brouillon_reglages_mode_lecture_projet (), pType, "disparition_ms");
      },

    inspecteur_projet_hype_fadeout_ms (pType)
      {
        return valeur_reglage_widget_hype (brouillon_reglages_mode_lecture_projet (), pType, "fadeout_ms");
      },

    inspecteur_scene_audio_items ()
      {
        const tSceneId = Session.get ("scene_id");
        const tScene = tSceneId ? Scenes.findOne (tSceneId) : null;
        return AUDIO_SCENE_ITEMS.map ((pItem) =>
          {
            const tEntree = entree_audio_scene (tScene, pItem.cle) || {};
            const tFichier = fichier_audio_scene (tScene, pItem.cle);
            const tUpload = gInspecteurSceneAudioUpload.get (pItem.cle) || null;
            const tActif = gInspecteurSceneAudioActif.get () === pItem.cle;
            const tSeedCourante = seed_audio_scene_courante (pItem.cle, tEntree);
            const tInstructionGeneration = instruction_generation_audio_scene_en_cours (tSceneId, pItem.cle);
            const tMessageLocal = gInspecteurSceneAudioMessages.get (pItem.cle) || null;
            const tPeutModifier = utilisateur_peut_modifier_scene_inspecteur ();
            return {
              ...pItem,
              sectionKey : "scene_audio_" + pItem.cle,
              activeClass : tActif ? "est_audio_actif" : "",
              active : tActif,
              audioUrl : url_hubfile_client (tFichier),
              audioNom : (tFichier && (tFichier.name || tFichier.fileName)) || "Aucun fichier",
              downloadNom : "scene_" + pItem.cle + extension_hubfile_client (tFichier),
              lectureActive : gInspecteurSceneAudioLecture.get () === pItem.cle,
              lectureLabel : gInspecteurSceneAudioLecture.get () === pItem.cle ? "Pause" : "Lire",
              lectureIcone : gInspecteurSceneAudioLecture.get () === pItem.cle ? "■" : "▶",
              audioProfilLabel : tEntree.imported === false && tEntree.voice_profile_id
                ? ((VoiceProfiles.findOne ({backendId : tEntree.voice_profile_id}) || {}).name || null)
                : null,
              audioSeed : tEntree.imported === false && tEntree.seed !== undefined && tEntree.seed !== null
                ? tEntree.seed
                : null,
              uploadEnCours : !!tUpload,
              uploadProgression : tUpload ? (tUpload.progression || 0) : 0,
              uploadLabel : tUpload && tUpload.phase === "traitement" ? "Traitement" : "Téléversement",
              disabledAttr : tPeutModifier ? null : "disabled",
              generationDisabledAttr : tPeutModifier ? null : "disabled",
              generationLabel : libelle_instruction_generation_audio (tInstructionGeneration),
              supprimerDisabledAttr : (!tEntree || !tEntree.file_id || !tPeutModifier) ? "disabled" : null,
              volumePct : volume_audio_entree_pct (tEntree),
              seedValue : valeur_champ_seed_audio_scene (pItem.cle, tEntree),
              seedAleatoireAttr : tSeedCourante === null ? "selected" : null,
              seedPlaceholderAttr : seed_placeholder_selected_attr (tSeedCourante),
              profils : profils_audio_scene (tEntree),
              seeds : seeds_audio_scene (pItem.cle, tEntree),
              prono : tEntree.prononciation || "",
              message : (tMessageLocal || {}).texte || null,
              messageType : (tMessageLocal || {}).type || "",
            };
          });
      },

    inspecteur_projet_audio_items ()
      {
        const tProjet = projet_courant_scene ();
        return AUDIO_PROJET_ITEMS.map ((pItem) =>
          {
            const tEntree = entree_audio_projet (tProjet, pItem.cle) || {};
            const tFichier = fichier_audio_projet (tProjet, pItem.cle);
            const tUpload = gInspecteurProjetAudioUpload.get (pItem.cle) || null;
            const tActif = gInspecteurProjetAudioActif.get () === pItem.cle;
            const tSeedCourante = seed_audio_projet_courante (pItem.cle, tEntree);
            const tInstructionGeneration = instruction_generation_audio_projet_en_cours (
              tProjet && tProjet._id,
              pItem.cle
            );
            const tMessageLocal = gInspecteurProjetAudioMessages.get (pItem.cle) || null;
            const tPeutModifier = utilisateur_peut_modifier_projet_inspecteur ();
            return {
              ...pItem,
              sectionKey : "projet_audio_" + pItem.cle,
              activeClass : tActif ? "est_audio_actif" : "",
              active : tActif,
              audioUrl : url_hubfile_client (tFichier),
              audioNom : (tFichier && (tFichier.name || tFichier.fileName)) || "Aucun fichier",
              downloadNom : pItem.cle + extension_hubfile_client (tFichier),
              lectureActive : gInspecteurProjetAudioLecture.get () === pItem.cle,
              lectureLabel : gInspecteurProjetAudioLecture.get () === pItem.cle ? "Pause" : "Lire",
              lectureIcone : gInspecteurProjetAudioLecture.get () === pItem.cle ? "■" : "▶",
              audioProfilLabel : tEntree.imported === false && tEntree.voice_profile_id
                ? ((VoiceProfiles.findOne ({backendId : tEntree.voice_profile_id}) || {}).name || null)
                : null,
              audioSeed : tEntree.imported === false && tEntree.seed !== undefined && tEntree.seed !== null
                ? tEntree.seed
                : null,
              hasAudio : !!(tEntree && tEntree.file_id),
              uploadEnCours : !!tUpload,
              uploadProgression : tUpload ? (tUpload.progression || 0) : 0,
              uploadLabel : tUpload && tUpload.phase === "traitement" ? "Traitement" : "Téléversement",
              disabledAttr : tPeutModifier ? null : "disabled",
              generationDisabledAttr : tPeutModifier ? null : "disabled",
              generationLabel : libelle_instruction_generation_audio (tInstructionGeneration),
              supprimerDisabledAttr : (!tEntree || !tEntree.file_id || !tPeutModifier) ? "disabled" : null,
              volumePct : volume_audio_entree_pct (tEntree),
              delaiSequenceVisible : pItem.cle === "entends_tu_le_son_1",
              delaiSequenceMs : delai_sequence_entends_tu_entree_ms (tEntree),
              seedValue : valeur_champ_seed_audio_projet (pItem.cle, tEntree),
              seedAleatoireAttr : tSeedCourante === null ? "selected" : null,
              seedPlaceholderAttr : seed_placeholder_selected_attr (tSeedCourante),
              profils : profils_audio_projet (tEntree),
              seeds : seeds_audio_projet (pItem.cle, tEntree),
              prono : tEntree.prononciation || "",
              message : (tMessageLocal || {}).texte || null,
              messageType : (tMessageLocal || {}).type || "",
            };
          });
      },

    inspecteur_projet_audio_ambiance_items ()
      {
        const tProjet = projet_courant_scene ();
        return AUDIO_PROJET_AMBIANCE_ITEMS.map ((pItem) =>
          {
            const tEntree = entree_audio_projet (tProjet, pItem.cle) || {};
            const tFichier = fichier_audio_projet (tProjet, pItem.cle);
            const tUpload = gInspecteurProjetAudioUpload.get (pItem.cle) || null;
            const tActif = gInspecteurProjetAudioActif.get () === pItem.cle;
            return {
              ...pItem,
              sectionKey : "projet_audio_" + pItem.cle,
              activeClass : tActif ? "est_audio_actif" : "",
              active : tActif,
              audioUrl : url_hubfile_client (tFichier),
              audioNom : (tFichier && (tFichier.name || tFichier.fileName)) || "Aucun fichier",
              downloadNom : pItem.cle + extension_hubfile_client (tFichier),
              lectureActive : gInspecteurProjetAudioLecture.get () === pItem.cle,
              lectureLabel : gInspecteurProjetAudioLecture.get () === pItem.cle ? "Pause" : "Lire",
              lectureIcone : gInspecteurProjetAudioLecture.get () === pItem.cle ? "■" : "▶",
              hasAudio : !!(tEntree && tEntree.file_id),
              uploadEnCours : !!tUpload,
              uploadProgression : tUpload ? (tUpload.progression || 0) : 0,
              uploadLabel : tUpload && tUpload.phase === "traitement" ? "Traitement" : "Téléversement",
              disabledAttr : utilisateur_peut_modifier_projet_inspecteur () ? null : "disabled",
              supprimerDisabledAttr : (!tEntree || !tEntree.file_id || !utilisateur_peut_modifier_projet_inspecteur ()) ? "disabled" : null,
              volumePct : volume_audio_entree_pct (tEntree),
              message : (gInspecteurProjetAudioMessages.get (pItem.cle) || {}).texte || null,
              messageType : (gInspecteurProjetAudioMessages.get (pItem.cle) || {}).type || "",
            };
          });
      },

    inspecteur_projet_audio_import_compresser_checked ()
      {
        return brouillon_reglages_import_audio_projet ().compresser ? "checked" : null;
      },

    inspecteur_projet_audio_import_debit_max_kbps ()
      {
        return brouillon_reglages_import_audio_projet ().debit_max_kbps;
      },

    inspecteur_projet_audio_import_compresser_active ()
      {
        return !!brouillon_reglages_import_audio_projet ().compresser;
      },

    inspecteur_projet_audio_import_convertir_mono_checked ()
      {
        return brouillon_reglages_import_audio_projet ().convertir_mono ? "checked" : null;
      },

    inspecteur_projet_audio_import_normaliser_gain_checked ()
      {
        return brouillon_reglages_import_audio_projet ().normaliser_gain ? "checked" : null;
      },

    inspecteur_projet_audio_import_message ()
      {
        const tM = gInspecteurProjetImportAudioMessage.get ();
        return tM ? tM.texte : null;
      },

    inspecteur_projet_audio_import_message_type ()
      {
        const tM = gInspecteurProjetImportAudioMessage.get ();
        return tM ? tM.type : "";
      },

    inspecteur_projet_mode_lecture_visible ()
      {
        const tProjet = projet_courant_scene ();
        return !!(tProjet && tProjet.type === "chasse_aux_sons");
      },

    inspecteur_projet_mode_lecture_hover_transition_ms ()
      {
        return brouillon_reglages_mode_lecture_projet ().hover_transition_ms;
      },

    inspecteur_projet_mode_lecture_survol_obj_scale_pct ()
      {
        return brouillon_reglages_mode_lecture_projet ().survol_obj_scale_pct;
      },

    inspecteur_projet_mode_lecture_survol_contour_couleur ()
      {
        return brouillon_reglages_mode_lecture_projet ().survol_contour_couleur;
      },

    inspecteur_projet_mode_lecture_survol_contour_epaisseur ()
      {
        return brouillon_reglages_mode_lecture_projet ().survol_contour_epaisseur;
      },

    inspecteur_projet_mode_lecture_survol_contour_opacite_pct ()
      {
        return Math.round ((brouillon_reglages_mode_lecture_projet ().survol_contour_opacite || 0) * 100);
      },

    inspecteur_projet_mode_lecture_nouvel_essai_contour_duree_s ()
      {
        return brouillon_reglages_mode_lecture_projet ().nouvel_essai_contour_duree_s;
      },

    inspecteur_projet_mode_lecture_zoom_carte_duree_ms ()
      {
        return brouillon_reglages_interface_carte_projet ().zoom_carte_duree_ms;
      },

    inspecteur_projet_mode_lecture_delai_affichage_obj_s ()
      {
        return brouillon_reglages_interface_carte_projet ().delai_affichage_obj_s;
      },

    inspecteur_projet_mode_lecture_reveil_objet_duree_ms ()
      {
        return brouillon_reglages_mode_lecture_projet ().reveil_objet_duree_ms;
      },

    inspecteur_projet_mode_lecture_reveil_objet_scale_pct ()
      {
        return brouillon_reglages_mode_lecture_projet ().reveil_objet_scale_pct;
      },

    inspecteur_projet_mode_lecture_bulle_click_scale_pct ()
      {
        return brouillon_reglages_mode_lecture_projet ().bulle_click_scale_pct;
      },

    inspecteur_projet_mode_lecture_bulle_click_duree_ms ()
      {
        const tReglages = brouillon_reglages_mode_lecture_projet ();
        const tDuree = Number (tReglages && tReglages.bulle_click_duree_ms);
        if (Number.isFinite (tDuree)) return tDuree;
        const tLegacy = Number (tReglages && tReglages.fade_bulle_ms);
        return Number.isFinite (tLegacy) ? tLegacy : 0;
      },

    inspecteur_projet_mode_lecture_transition_scene_fade_ms ()
      {
        return brouillon_reglages_mode_lecture_projet ().transition_scene_fade_ms;
      },

    inspecteur_projet_mode_lecture_animation_30fps_actif_checked ()
      {
        return brouillon_reglages_mode_lecture_projet ().animation_30fps_actif ? "checked" : null;
      },

    inspecteur_projet_mode_lecture_zoom_alternatif_cadre_epaisseur_haut ()
      {
        return brouillon_reglages_interface_carte_projet ().zoom_alternatif_cadre_epaisseur_haut;
      },

    inspecteur_projet_mode_lecture_zoom_alternatif_cadre_epaisseur_droite ()
      {
        return brouillon_reglages_interface_carte_projet ().zoom_alternatif_cadre_epaisseur_droite;
      },

    inspecteur_projet_mode_lecture_zoom_alternatif_cadre_epaisseur_bas ()
      {
        return brouillon_reglages_interface_carte_projet ().zoom_alternatif_cadre_epaisseur_bas;
      },

    inspecteur_projet_mode_lecture_zoom_alternatif_cadre_epaisseur_gauche ()
      {
        return brouillon_reglages_interface_carte_projet ().zoom_alternatif_cadre_epaisseur_gauche;
      },

    inspecteur_projet_mode_lecture_zoom_alternatif_cadre_couleur ()
      {
        return brouillon_reglages_interface_carte_projet ().zoom_alternatif_cadre_couleur;
      },

    inspecteur_projet_mode_lecture_zoom_alternatif_focus_facteur ()
      {
        return brouillon_reglages_interface_carte_projet ().zoom_alternatif_focus_facteur;
      },

    inspecteur_projet_mode_lecture_zoom_alternatif_focus_offset_x ()
      {
        return brouillon_reglages_interface_carte_projet ().zoom_alternatif_focus_offset_x;
      },

    inspecteur_projet_mode_lecture_zoom_alternatif_focus_offset_y ()
      {
        return brouillon_reglages_interface_carte_projet ().zoom_alternatif_focus_offset_y;
      },

    inspecteur_projet_mode_lecture_zoom_alternatif_delai_lancement_s ()
      {
        return brouillon_reglages_interface_carte_projet ().zoom_alternatif_delai_lancement_s;
      },

    inspecteur_projet_mode_lecture_zoom_alternatif_masque_fade_in_ms ()
      {
        return brouillon_reglages_interface_carte_projet ().zoom_alternatif_masque_fade_in_ms;
      },

    inspecteur_projet_mode_lecture_zoom_alternatif_masque_fade_out_ms ()
      {
        return brouillon_reglages_interface_carte_projet ().zoom_alternatif_masque_fade_out_ms;
      },

    inspecteur_projet_mode_lecture_zoom_alternatif_interface_fade_in_ms ()
      {
        return brouillon_reglages_interface_carte_projet ().zoom_alternatif_interface_fade_in_ms;
      },

    inspecteur_projet_mode_lecture_zoom_alternatif_interface_fade_out_ms ()
      {
        return brouillon_reglages_interface_carte_projet ().zoom_alternatif_interface_fade_out_ms;
      },

    inspecteur_projet_mode_lecture_recreer_cartes_label ()
      {
        return gInspecteurProjetSavingSection.get () === "mode_lecture_interface"
          ? "Application..."
          : "Appliquer à toutes les cartes";
      },

    inspecteur_projet_mode_lecture_recreer_cartes_disabled_attr ()
      {
        return utilisateur_peut_modifier_projet_inspecteur ()
          && gInspecteurProjetSavingSection.get () !== "mode_lecture_interface"
            ? null
            : "disabled";
      },

    inspecteur_projet_zoom_alternatif_uploads ()
      {
        const tUpload = gInspecteurProjetZoomAltUpload.get ();
        return items_interface_carte_inspecteur (
          brouillon_reglages_interface_carte_projet (),
          "inspecteur_projet_zoom_alt",
          {
            uploadVisible : true,
            jaugeEditVisible : true,
            disabledAttr : utilisateur_peut_modifier_projet_inspecteur () && !tUpload ? null : "disabled",
          }
        ).map ((pItem) =>
          ({
            ...pItem,
            uploadLabel : tUpload && tUpload.role === pItem.cle
              ? `Upload ${tUpload.progress || 0}%`
              : "SVG…",
          }));
      },

    inspecteur_projet_jauge_visuelle_open ()
      {
        return gInspecteurProjetJaugeVisuelleOpen.get ();
      },

    inspecteur_projet_jauge_visuelle_config ()
      {
        return config_jauge_visuelle_brouillon ();
      },

    inspecteur_projet_jauge_visuelle_source_url ()
      {
        return source_jauge_visuelle_projet ().url;
      },

    inspecteur_projet_jauge_visuelle_source_text ()
      {
        return source_jauge_visuelle_projet ().svgText;
      },

    inspecteur_projet_jauge_visuelle_source_viewbox ()
      {
        return source_jauge_visuelle_projet ().viewBox;
      },

    inspecteur_projet_jauge_visuelle_fichier_label ()
      {
        return libelle_fichier_jauge_visuelle ();
      },

    inspecteur_projet_jauge_visuelle_message ()
      {
        const tMessage = gInspecteurProjetJaugeVisuelleMessage.get ();
        return tMessage && tMessage.texte;
      },

    inspecteur_projet_jauge_visuelle_message_class ()
      {
        const tMessage = gInspecteurProjetJaugeVisuelleMessage.get ();
        return tMessage && tMessage.type === "erreur" ? "text-danger" : "text-success";
      },

    inspecteur_projet_jauge_visuelle_validate_label ()
      {
        return gInspecteurProjetJaugeVisuelleSaving.get () ? "Enregistrement…" : "Valider";
      },

    inspecteur_projet_jauge_visuelle_validate_disabled_attr ()
      {
        if (!utilisateur_peut_modifier_projet_inspecteur ()) return "disabled";
        return gInspecteurProjetJaugeVisuelleSaving.get () ? "disabled" : null;
      },

    inspecteur_bulle_interface_carte_items ()
      {
        return items_interface_carte_inspecteur (
          reglages_interface_carte_effectifs (carte_bulle_selectionnee_scene ()),
          "inspecteur_bulle_interface_carte"
        );
      },

    inspecteur_bulle_interface_cadre_epaisseur_haut ()
      {
        return reglages_interface_carte_effectifs (carte_bulle_selectionnee_scene ()).zoom_alternatif_cadre_epaisseur_haut;
      },

    inspecteur_bulle_interface_cadre_epaisseur_droite ()
      {
        return reglages_interface_carte_effectifs (carte_bulle_selectionnee_scene ()).zoom_alternatif_cadre_epaisseur_droite;
      },

    inspecteur_bulle_interface_cadre_epaisseur_bas ()
      {
        return reglages_interface_carte_effectifs (carte_bulle_selectionnee_scene ()).zoom_alternatif_cadre_epaisseur_bas;
      },

    inspecteur_bulle_interface_cadre_epaisseur_gauche ()
      {
        return reglages_interface_carte_effectifs (carte_bulle_selectionnee_scene ()).zoom_alternatif_cadre_epaisseur_gauche;
      },

    inspecteur_bulle_interface_cadre_couleur ()
      {
        return reglages_interface_carte_effectifs (carte_bulle_selectionnee_scene ()).zoom_alternatif_cadre_couleur;
      },

    inspecteur_bulle_interface_focus_facteur ()
      {
        return reglages_interface_carte_effectifs (carte_bulle_selectionnee_scene ()).zoom_alternatif_focus_facteur;
      },

    inspecteur_bulle_interface_focus_offset_x ()
      {
        return reglages_interface_carte_effectifs (carte_bulle_selectionnee_scene ()).zoom_alternatif_focus_offset_x;
      },

    inspecteur_bulle_interface_focus_offset_y ()
      {
        return reglages_interface_carte_effectifs (carte_bulle_selectionnee_scene ()).zoom_alternatif_focus_offset_y;
      },

    inspecteur_projet_mode_lecture_message ()
      {
        const tM = gInspecteurProjetModeLectureMessage.get ();
        return tM ? tM.texte : null;
      },

    inspecteur_projet_mode_lecture_message_type ()
      {
        const tM = gInspecteurProjetModeLectureMessage.get ();
        return tM ? tM.type : "";
      },

    inspecteur_projet_sous_onglets ()
      {
        const tActif = gInspecteurProjetSousOnglet.get ();
        return INSPECTEUR_PROJET_SOUS_ONGLETS.map ((pOnglet) =>
          ({
            ...pOnglet,
            actif : pOnglet.cle === tActif,
            selectedAttr : pOnglet.cle === tActif ? "true" : "false",
          }));
      },

    inspecteur_projet_sous_onglet_est (pCle)
      {
        return gInspecteurProjetSousOnglet.get () === pCle;
      },

    inspecteur_projet_reglages_visible ()
      {
        return gInspecteurProjetSousOnglet.get () === "reglages";
      },

    inspecteur_projet_outils_visible ()
      {
        return gInspecteurProjetSousOnglet.get () === "outils";
      },

    inspecteur_projet_participants_visible ()
      {
        return gInspecteurProjetSousOnglet.get () === "participants";
      },

    inspecteur_projet_utilisateurs_visible ()
      {
        return gInspecteurProjetSousOnglet.get () === "utilisateurs";
      },

    inspecteur_projet_reglages_panel_style ()
      {
        return gInspecteurProjetSousOnglet.get () === "reglages" ? "" : "display:none;";
      },

    inspecteur_projet_modifie ()
      {
        const tProjet = projet_courant_scene ();
        return inspecteur_projet_general_est_modifie (tProjet)
          || inspecteur_projet_bulles_est_modifie (tProjet)
          || inspecteur_projet_audio_import_est_modifie (tProjet)
          || inspecteur_projet_mode_lecture_est_modifie (tProjet);
      },

    inspecteur_projet_section_sauvegarde_disabled_attr (pSection)
      {
        return inspecteur_projet_section_sauvegarde_disabled_attr_client (pSection);
      },

    inspecteur_projet_section_sauvegarde_label (pSection)
      {
        return inspecteur_projet_section_sauvegarde_label_client (pSection);
      },

    inspecteur_projet_message ()
      {
        const tM = gInspecteurProjetMessage.get ();
        return tM ? tM.texte : null;
      },

    inspecteur_projet_message_type ()
      {
        const tM = gInspecteurProjetMessage.get ();
        return tM ? tM.type : "";
      },

    inspecteur_projet_owner_disabled_attr ()
      {
        return utilisateur_peut_modifier_projet_inspecteur () ? null : "disabled";
      },

    inspecteur_projet_est_proprietaire ()
      {
        return utilisateur_est_proprietaire_projet_inspecteur ();
      },

    inspecteur_projet_peut_modifier ()
      {
        return utilisateur_peut_modifier_projet_inspecteur ();
      },

    inspecteur_projet_peut_gerer_participants ()
      {
        return utilisateur_peut_gerer_participants_projet_inspecteur ();
      },

    inspecteur_projet_participants_button_disabled_attr ()
      {
        return utilisateur_peut_gerer_participants_projet_inspecteur () ? null : "disabled";
      },

    inspecteur_projet_membres ()
      {
        return inspecteur_projet_membres_liste ();
      },

    inspecteur_projet_dialog_open ()
      {
        return gInspecteurProjetDialogOpen.get ();
      },

    inspecteur_projet_dialog_contacts ()
      {
        return inspecteur_projet_contacts_disponibles ();
      },

    inspecteur_projet_dialog_contacts_count ()
      {
        return inspecteur_projet_contacts_disponibles ().length;
      },

    inspecteur_projet_dialog_has_contacts ()
      {
        return inspecteur_projet_contacts_disponibles ().length > 0;
      },

    inspecteur_projet_dialog_confirm_disabled_attr ()
      {
        if (gInspecteurProjetDialogSaving.get ()) return "disabled";
        const tContacts = inspecteur_projet_contacts_disponibles ();
        if (!tContacts.some ((pContact) => gInspecteurProjetDialogSelection.get (pContact._id)))
          return "disabled";
        return null;
      },

    inspecteur_projet_dialog_confirm_label ()
      {
        return gInspecteurProjetDialogSaving.get () ? "Ajout…" : "Ajouter les participants sélectionnés";
      },

    // ---- Inspecteur calque sélectionné ----

    inspecteur_bulle_selectionnee ()
      {
        return !!gBulleSelectionnee.get ();
      },

    inspecteur_bulle_nom_carte ()
      {
        return libelle_carte_scene (gBulleSelectionnee.get ()) || "Bulle";
      },

    inspecteur_bulle_nom_editable ()
      {
        const tBrouillon = brouillon_bulle_courant ();
        return typeof (tBrouillon && tBrouillon.nom) === "string" ? tBrouillon.nom : "";
      },

    inspecteur_bulle_nom_placeholder ()
      {
        return libelle_carte_scene (gBulleSelectionnee.get ()) || "Carte";
      },

    inspecteur_bulle_couleur ()
      {
        const tInfo = configuration_bulle_inspecteur_courant_client ();
        return (tInfo && tInfo.config && tInfo.config.couleur_bulle) || "#ffffff";
      },

    inspecteur_bulle_has_custom_svg_files ()
      {
        const tInfo = configuration_bulle_inspecteur_courant_client ();
        return !!(tInfo && tInfo.planBulles && tInfo.planBulles.hasCustomFiles);
      },

    inspecteur_bulle_svg_picker_open ()
      {
        return gInspecteurBulleSvgPickerOuvert.get ();
      },

    inspecteur_bulle_svg_picker_aria_expanded ()
      {
        return gInspecteurBulleSvgPickerOuvert.get () ? "true" : "false";
      },

    inspecteur_bulle_svg_picker_label ()
      {
        const tInfo = configuration_bulle_inspecteur_courant_client ();
        const tSvgBulleId = tInfo && tInfo.config && tInfo.config.svg_bulle_id;
        const tFichier = tSvgBulleId
          ? fichiers_bulles_projet_courant ().find ((pFichier) => pFichier && pFichier._id === tSvgBulleId)
          : null;
        return (tFichier && tFichier.nom) || "Choisir une bulle";
      },

    inspecteur_bulle_svg_picker_url ()
      {
        const tInfo = configuration_bulle_inspecteur_courant_client ();
        const tSvgBulleId = tInfo && tInfo.config && tInfo.config.svg_bulle_id;
        const tFichier = tSvgBulleId
          ? fichiers_bulles_projet_courant ().find ((pFichier) => pFichier && pFichier._id === tSvgBulleId)
          : null;
        return (tFichier && tFichier.url) || null;
      },

    inspecteur_bulle_svg_picker_items ()
      {
        const tInfo = configuration_bulle_inspecteur_courant_client ();
        const tSvgBulleId = tInfo && tInfo.config && tInfo.config.svg_bulle_id;
        return fichiers_bulles_projet_courant ().map ((pFichier) =>
          {
            const tSelectionnee = !!(pFichier && pFichier._id === tSvgBulleId);
            return {
              ...pFichier,
              selectedClass : tSelectionnee ? "est_selectionnee" : "",
              ariaChecked : tSelectionnee ? "true" : "false",
            };
          });
      },

    inspecteur_bulle_svg_color_picker_open ()
      {
        return gInspecteurBulleCouleurPickerOuvert.get ();
      },

    inspecteur_bulle_svg_color_picker_aria_expanded ()
      {
        return gInspecteurBulleCouleurPickerOuvert.get () ? "true" : "false";
      },

    inspecteur_bulle_svg_color_picker_label ()
      {
        return valeur_couleur_bulle_inspecteur_client () || "Choisir une couleur";
      },

    inspecteur_bulle_svg_color_picker_swatch_style ()
      {
        const tValeur = valeur_couleur_bulle_inspecteur_client ();
        return tValeur ? `background: ${tValeur};` : null;
      },

    inspecteur_bulle_has_custom_svg_colors ()
      {
        const tInfo = configuration_bulle_inspecteur_courant_client ();
        if (!(tInfo && tInfo.planBulles && tInfo.planBulles.hasCustomFiles)) return false;
        return reglages_bulles_projet_courant ().couleurs_auto.length > 0;
      },

    inspecteur_bulle_svg_color_options ()
      {
        const tCouleurs = reglages_bulles_projet_courant ().couleurs_auto;
        const tValeurCourante = valeur_couleur_bulle_inspecteur_client ();

        return tCouleurs.map ((pCouleur) =>
          {
            const tValeur = normalise_couleur_auto_bulle_projet_client (pCouleur);
            return {
              value : tValeur,
              label : tValeur,
              swatchStyle : `background: ${tValeur};`,
              selectedClass : tValeur === tValeurCourante ? "est_selectionnee" : "",
              ariaChecked : tValeur === tValeurCourante ? "true" : "false",
            };
          });
      },

    inspecteur_bulle_opacite ()
      {
        const tBrouillon = brouillon_bulle_courant ();
        return opacite_bulle_carte_effective_client (tBrouillon).toFixed (2);
      },

    inspecteur_bulle_taille ()
      {
        const tBrouillon = brouillon_bulle_courant ();
        return String (Math.round (taille_bulle_carte_effective_client (tBrouillon)));
      },

    inspecteur_bulle_rotation ()
      {
        const tBrouillon = brouillon_bulle_courant ();
        return String (Math.round (rotation_bulle_carte_effective_client (tBrouillon)));
      },

    inspecteur_bulle_vue_x ()
      {
        const tVue = brouillon_bulle_courant () && brouillon_bulle_courant ().vue;
        return tVue ? String (Math.round (tVue.x * 10) / 10) : "";
      },

    inspecteur_bulle_vue_y ()
      {
        const tVue = brouillon_bulle_courant () && brouillon_bulle_courant ().vue;
        return tVue ? String (Math.round (tVue.y * 10) / 10) : "";
      },

    inspecteur_bulle_vue_w ()
      {
        const tVue = brouillon_bulle_courant () && brouillon_bulle_courant ().vue;
        return tVue ? String (Math.round (tVue.w * 10) / 10) : "";
      },

    inspecteur_bulle_vue_h ()
      {
        const tVue = brouillon_bulle_courant () && brouillon_bulle_courant ().vue;
        return tVue ? String (Math.round (tVue.h * 10) / 10) : "";
      },

    inspecteur_bulle_save_disabled_attr ()
      {
        return bulle_brouillon_modifie () ? null : "disabled";
      },

    cartes_carte_courante_label ()
      {
        const tCarte = carte_arbre_carte_courante_scene ();
        if (!tCarte) return "Aucune carte selectionnee";
        return libelle_carte_scene (tCarte._id || tCarte.carte_id) || tCarte.nom || "Carte";
      },

    cartes_brouillon_hidden_count ()
      {
        const tBrouillon = carte_arbre_brouillon_courant ();
        return normalise_ids_masques_carte_client (tBrouillon && tBrouillon.ids_masques).length;
      },

    cartes_categories_arbre ()
      {
        const tTpl = Template.instance ();
        const tVue = vue_reference_arbre_cartes (tTpl);
        return cartes_categories_visibles_dans_vue (tTpl, tVue);
      },

    cartes_arbre_vide ()
      {
        const tTpl = Template.instance ();
        const tVue = vue_reference_arbre_cartes (tTpl);
        return cartes_categories_visibles_dans_vue (tTpl, tVue).length === 0;
      },

    inspecteur_calque_selectionne ()
      {
        return !!gCalqueSelectionne.get ();
      },

    inspecteur_calques_mode_bulle ()
      {
        return inspecteur_calques_mode_scene () === "bulle";
      },

    inspecteur_calques_mode_calque ()
      {
        return inspecteur_calques_mode_scene () === "calque";
      },

    inspecteur_calques_visible ()
      {
        return !!inspecteur_calques_mode_scene ();
      },

    inspecteur_categorie_active ()
      {
        return inspecteur_categorie_active_scene ();
      },

    inspecteur_calques_legacy_style ()
      {
        return inspecteur_categorie_active_scene () ? "display:none;" : "";
      },

    inspecteur_est_bouton ()
      {
        return objet_selectionne_est_bouton ();
      },

    inspecteur_voix_visible ()
      {
        return objet_selectionne_affiche_section_voix ();
      },

    inspecteur_interactivite_actions ()
      {
        const tAction = action_interactivite_objet_selectionne ();
        return INTERACTIVITE_ACTIONS.map ((pOption) =>
          ({
            ...pOption,
            selectedAttr : pOption.value === tAction ? "selected" : null,
          }));
      },

    inspecteur_interactivite_est_scene ()
      {
        return action_interactivite_objet_selectionne () === INTERACTIVITE_ACTION_SCENE;
      },

    inspecteur_interactivite_est_url ()
      {
        return action_interactivite_objet_selectionne () === INTERACTIVITE_ACTION_URL;
      },

    inspecteur_interactivite_est_son ()
      {
        return action_interactivite_objet_selectionne () === INTERACTIVITE_ACTION_SON;
      },

    inspecteur_interactivite_est_video ()
      {
        return action_interactivite_objet_selectionne () === INTERACTIVITE_ACTION_VIDEO;
      },

    inspecteur_interactivite_url ()
      {
        const tConfig = config_objet_selectionne_scene ();
        return (tConfig && tConfig.interactivite_url) || "";
      },

    inspecteur_interactivite_video_url ()
      {
        const tConfig = config_objet_selectionne_scene ();
        return (tConfig && tConfig.interactivite_video_url) || "";
      },

    inspecteur_interactivite_scenes ()
      {
        if (!objet_selectionne_est_bouton ()) return [];
        const tProjetId = Session.get ("projet_id");
        const tSceneCouranteId = Session.get ("scene_id");
        const tConfig = config_objet_selectionne_scene ();
        const tSceneCibleId = tConfig && tConfig.interactivite_scene_id;
        const tVignetteStyleAttr = style_attr_aspect_ratio_rapport_lh_scene (rapport_lh_scene_projet_courant ());
        return Scenes.find ({projet_id : tProjetId}, {sort : {ordre : 1}}).fetch ().map ((pScene) =>
          ({
            _id : pScene._id,
            titre : (typeof pScene.titre === "string" && pScene.titre.trim ()) || pScene.slug || "Sans titre",
            vignette_url : url_vignette_scene_client (pScene),
            vignetteStyleAttr : tVignetteStyleAttr,
            est_courante : pScene._id === tSceneCouranteId,
            est_selectionnee : pScene._id === tSceneCibleId,
            itemClass : [
              pScene._id === tSceneCibleId ? "est_selectionnee" : "",
              pScene._id === tSceneCouranteId ? "est_courante" : "",
            ].filter (Boolean).join (" "),
            disabledAttr : pScene._id === tSceneCouranteId ? "disabled" : null,
          }));
      },

    inspecteur_video_preview_open ()
      {
        return !!gInspecteurVideoPreviewUrl.get ();
      },

    inspecteur_video_preview_url ()
      {
        return gInspecteurVideoPreviewUrl.get () || null;
      },

    inspecteur_nom_objet ()
      {
        const tId = gCalqueSelectionne.get ();
        return nom_objet_inspecteur_depuis_id (tId);
      },

    inspecteur_valeur_prono ()
      {
        const tP = gInspecteurProno.get ();
        if (tP !== null) return tP;
        const tId = gCalqueSelectionne.get ();
        if (!tId) return "";
        const tSceneId = Session.get ("scene_id");
        const tScene = Scenes.findOne (tSceneId);
        const tConfig = get_objet_config (tScene, tId);
        return (tConfig && tConfig.prononciation) || "";
      },

    inspecteur_son_checked ()
      {
        const tId = gCalqueSelectionne.get ();
        if (!tId) return null;
        const tSceneId = Session.get ("scene_id");
        const tScene   = Scenes.findOne (tSceneId);
        const tConfig  = get_objet_config (tScene, tId);
        if (tConfig && typeof tConfig.son_override === "boolean")
          return tConfig.son_override ? "checked" : null;
        const tDN = get_data_name_pour_id (tSceneId, tId);
        if (!tDN) return null;
        const tParts = tDN.split (",").map ((s) => s.trim ());
        return tParts[2] === "son-oui" ? "checked" : null;
      },

    inspecteur_survol_lecture_visible ()
      {
        const tProjet = projet_courant_scene ();
        if (!(tProjet && tProjet.type === "chasse_aux_sons")) return false;
        return !objet_selectionne_est_part ();
      },

    inspecteur_survol_contour_couleur ()
      {
        return reglages_survol_contour_objet_selectionne_scene ().couleur;
      },

    inspecteur_survol_contour_epaisseur ()
      {
        return reglages_survol_contour_objet_selectionne_scene ().epaisseur;
      },

    inspecteur_survol_contour_opacite_pct ()
      {
        return Math.round ((reglages_survol_contour_objet_selectionne_scene ().opacite || 0) * 100);
      },

    inspecteur_survol_message ()
      {
        const tMessage = gInspecteurSurvolMessage.get ();
        return tMessage ? tMessage.texte : null;
      },

    inspecteur_survol_message_type ()
      {
        const tMessage = gInspecteurSurvolMessage.get ();
        return tMessage ? tMessage.type : "";
      },

    // ---- Génération audio par calque ----------------------------------

    inspecteur_profils ()
      {
        const tId = gCalqueSelectionne.get ();
        if (!tId) return [];
        const tSceneId = Session.get ("scene_id");
        const tScene   = Scenes.findOne (tSceneId);
        const tConfig  = get_objet_config (tScene, tId);
        const tListe = VoiceProfiles.find ({}, {sort : {createdAt : -1}}).fetch ();
        if (tListe.length === 0) return [];
        let tSel = tConfig && tConfig.voice_profile_id;
        // Si pas de sélection persistée, on retombe sur le 1er profil.
        if (!tSel || !tListe.some (p => p.backendId === tSel))
          tSel = tListe[0].backendId;
        return tListe.map (p => ({
          ...p,
          selected     : p.backendId === tSel,
          selectedAttr : p.backendId === tSel ? "selected" : null,
        }));
      },

    inspecteur_seeds ()
      {
        const tId = gCalqueSelectionne.get ();
        if (!tId) return [];
        const tSel = inspecteur_seed_courant ();
        const tSelNum = Number (tSel);
        return Seeds.find ({}, {sort : {createdAt : -1}}).map (s =>
          ({
            ...s,
            selected     : tSel !== null && Number (s.value) === tSelNum,
            selectedAttr : (tSel !== null && Number (s.value) === tSelNum) ? "selected" : null,
          }));
      },

    inspecteur_seed_value ()
      {
        const tId = gCalqueSelectionne.get ();
        if (!tId) return "";
        const tSel = inspecteur_seed_courant ();
        return tSel === null ? "" : String (tSel);
      },

    inspecteur_seed_aleatoire_attr ()
      {
        const tId = gCalqueSelectionne.get ();
        if (!tId) return null;
        const tSel = inspecteur_seed_courant ();
        return tSel === null ? "selected" : null;
      },

    inspecteur_seed_placeholder_attr ()
      {
        const tId = gCalqueSelectionne.get ();
        if (!tId) return null;
        return seed_placeholder_selected_attr (inspecteur_seed_courant ());
      },

    inspecteur_seed_save_display ()
      {
        // "" pour afficher (display:initial), "none" pour masquer.
        const tId = gCalqueSelectionne.get ();
        if (!tId) return "none";
        const tBrut = gInspecteurSeedCustom.get (tId);
        const tStr = (tBrut === undefined || tBrut === null) ? "" : String (tBrut).trim ();
        if (!tStr) return "none";
        const tNum = parseInt (tStr, 10);
        if (isNaN (tNum) || tNum < 0) return "none";
        // Caché si la valeur correspond déjà à une variante enregistrée.
        const tExist = Seeds.findOne ({value : tNum});
        if (tExist) return "none";
        return "";
      },

    inspecteur_seed_dialog_open  () { return gInspecteurSeedDialogOpen.get (); },
    inspecteur_seed_dialog_value () { return gInspecteurSeedDialogValue.get (); },
    inspecteur_seed_dialog_my_projects ()
      {
        return buildSharingPickerData (
          {
            selectedIds : gInspecteurSeedDialogSharedProjectIds.get (),
            defaultProjectId : inspecteur_seed_dialog_projet_id (),
          }
        ).myProjects;
      },
    inspecteur_seed_dialog_contact_projects ()
      {
        return buildSharingPickerData (
          {
            selectedIds : gInspecteurSeedDialogSharedProjectIds.get (),
            defaultProjectId : inspecteur_seed_dialog_projet_id (),
          }
        ).contactProjects;
      },

    inspecteur_autoplay_checked ()
      {
        return gInspecteurAutoplay.get () ? "checked" : null;
      },

    inspecteur_audio_profil_label ()
      {
        const tId = gCalqueSelectionne.get ();
        if (!tId) return null;
        const tSceneId = Session.get ("scene_id");
        const tScene   = Scenes.findOne (tSceneId);
        const tConfig  = get_objet_config (tScene, tId);
        if (!tConfig || !tConfig.audio_file_id) return null;
        if (tConfig.audio_imported) return null;
        const tPid = tConfig.audio_voice_profile_id;
        if (!tPid) return null;
        const tProf = VoiceProfiles.findOne ({backendId : tPid});
        return tProf ? tProf.name : null;
      },

    inspecteur_audio_seed ()
      {
        const tId = gCalqueSelectionne.get ();
        if (!tId) return null;
        const tSceneId = Session.get ("scene_id");
        const tScene   = Scenes.findOne (tSceneId);
        const tConfig  = get_objet_config (tScene, tId);
        if (!tConfig || !tConfig.audio_file_id) return null;
        if (tConfig.audio_imported) return null;
        const tS = tConfig.audio_seed;
        return (tS === null || tS === undefined) ? null : tS;
      },

    inspecteur_audio_url ()
      {
        const tId = gCalqueSelectionne.get ();
        if (!tId) return null;
        const tSceneId = Session.get ("scene_id");
        const tScene   = Scenes.findOne (tSceneId);
        const tConfig  = get_objet_config (tScene, tId);
        if (!tConfig || !tConfig.audio_file_id) return null;
        const tF = HubFiles.findOne ({_id : tConfig.audio_file_id});
        if (!tF) return null;
        try { return tF.link (undefined, window.location.origin); }
        catch (e) { return tF.link (); }
      },

    inspecteur_generation_en_cours ()
      {
        const tId = gCalqueSelectionne.get ();
        if (!tId) return false;
        const tSceneId = Session.get ("scene_id");
        const tInstr = Instructions.findOne (
          {
            kind   : "generate",
            status : {$in : ["pending", "running"]},
            "payload.scope"    : "calque",
            "payload.scene_id" : tSceneId,
            "payload.objet_id" : tId,
          });
        return !!tInstr;
      },

    inspecteur_generation_label ()
      {
        const tId = gCalqueSelectionne.get ();
        if (!tId) return "";
        const tSceneId = Session.get ("scene_id");
        const tInstr = Instructions.findOne (
          {
            kind   : "generate",
            status : {$in : ["pending", "running"]},
            "payload.scope"    : "calque",
            "payload.scene_id" : tSceneId,
            "payload.objet_id" : tId,
          });
        if (!tInstr) return "";
        if (tInstr.status === "pending") return "En attente…";
        if (tInstr.stage  === "warming") return "Lancement du moteur…";
        return "Génération…";
      },

    inspecteur_upload_en_cours ()
      {
        return gInspecteurUploadEnCours.get ();
      },

    inspecteur_upload_progression ()
      {
        return gInspecteurUploadProgression.get ();
      },

    inspecteur_upload_label ()
      {
        return phase_upload_inspecteur_label ();
      },

    inspecteur_generation_disabled_attr ()
      {
        const tId = gCalqueSelectionne.get ();
        if (!tId) return null;
        const tSceneId = Session.get ("scene_id");
        const tInstr = Instructions.findOne (
          {
            kind   : "generate",
            status : {$in : ["pending", "running"]},
            "payload.scope"    : "calque",
            "payload.scene_id" : tSceneId,
            "payload.objet_id" : tId,
          });
        return tInstr ? "disabled" : null;
      },

    inspecteur_upload_disabled_attr ()
      {
        return gInspecteurUploadEnCours.get () ? "disabled" : null;
      },

    inspecteur_audio_supprimer_disabled_attr ()
      {
        const tId = gCalqueSelectionne.get ();
        if (!tId) return "disabled";
        const tSceneId = Session.get ("scene_id");
        const tScene   = Scenes.findOne (tSceneId);
        const tConfig  = get_objet_config (tScene, tId);
        return (tConfig && tConfig.audio_file_id) ? null : "disabled";
      },

    inspecteur_audio_grise_class ()
      {
        const tId = gCalqueSelectionne.get ();
        if (!tId) return "inspecteur_audio--grise";
        const tSceneId = Session.get ("scene_id");
        const tScene   = Scenes.findOne (tSceneId);
        const tConfig  = get_objet_config (tScene, tId);
        return (tConfig && tConfig.audio_file_id) ? "" : "inspecteur_audio--grise";
      },

    inspecteur_audio_download_nom ()
      {
        const tId = gCalqueSelectionne.get ();
        const tSceneId = Session.get ("scene_id");
        const tScene   = tId ? Scenes.findOne (tSceneId) : null;
        const tConfig  = tScene ? get_objet_config (tScene, tId) : null;
        let tBase = "audio";
        if (tId)
          {
            const tDN = get_data_name_pour_id (tSceneId, tId);
            if (tDN)
              {
                const tParts = tDN.split (",").map ((s) => s.trim ());
                const tMot = tParts[1] || tParts[0] || "";
                tBase = fabrique_slug (tMot) || "audio";
              }
          }
        const tExt = (() =>
          {
            if (!tConfig || !tConfig.audio_file_id) return ".wav";
            const tF = HubFiles.findOne ({_id : tConfig.audio_file_id});
            if (!tF) return ".wav";
            const tE = tF.extension || tF.ext;
            if (tE) return "." + String (tE).replace (/^\./, "");
            const tNom = String (tF.name || tF.fileName || "");
            const tMatch = tNom.match (/\.([a-z0-9]+)$/i);
            if (tMatch && tMatch[1]) return "." + tMatch[1].toLowerCase ();
            const tType = String (tF.type || "").toLowerCase ();
            if (tType === "audio/mpeg" || tType === "audio/mp3") return ".mp3";
            if (tType === "audio/wav" || tType === "audio/x-wav" || tType === "audio/wave") return ".wav";
            return ".wav";
          }) ();
        return tBase + tExt;
      },
  });

// ---------------------------------------------------------------------------
// Helpers de l'éditeur audio (wavesurfer)
// ---------------------------------------------------------------------------
Template.scene.helpers
  ({
    inspecteur_ws_save_disabled_attr ()
      {
        return (gEditeur.get ("dirty")
                && !gEditeur.get ("saving")
                && !gEditeur.get ("preview_building"))
          ? null : "disabled";
      },
    inspecteur_ws_delete_disabled_attr ()
      {
        const s = gEditeur.get ("sel_start"), e = gEditeur.get ("sel_end");
        if (gEditeur.get ("saving") || gEditeur.get ("preview_building")) return "disabled";
        if (s === null || e === null || s === undefined || e === undefined) return "disabled";
        return (e - s > 0.02) ? null : "disabled";
      },
    inspecteur_ws_deselect_disabled_attr ()
      {
        const s = gEditeur.get ("sel_start"), e = gEditeur.get ("sel_end");
        if (gEditeur.get ("preview_building")) return "disabled";
        return (s === null || e === null || s === undefined || e === undefined) ? "disabled" : null;
      },
    inspecteur_ws_saving () { return !!gEditeur.get ("saving"); },
  });

// ---------------------------------------------------------------------------
// Drag & drop d'un fichier SVG sur la div .scene
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Lance la génération d'audio pour le calque sélectionné. Le fichier
// résultant n'ira pas dans l'historique général : le serveur le rattache
// directement à objets_config[i].audio_file_id (cf. instructions.complete).
// ---------------------------------------------------------------------------

function lance_generation_calque ()
  {
    const tId = gCalqueSelectionne.get ();
    if (!tId) return;
    if (!objet_selectionne_affiche_section_voix ()) return;
    const tSceneId = Session.get ("scene_id");
    if (!tSceneId) return;
    const tScene  = Scenes.findOne (tSceneId);
    const tConfig = get_objet_config (tScene, tId);

    // Texte : champ "Prononciation" si non vide, sinon 2e terme du data-name.
    let tTexte = "";
    const tProno = (tConfig && tConfig.prononciation) || "";
    if (tProno && tProno.trim ()) tTexte = tProno.trim ();
    else
      {
        const tDN = get_data_name_pour_id (tSceneId, tId);
        if (tDN)
          {
            const tParts = tDN.split (",").map (s => s.trim ());
            tTexte = tParts[1] || "";
          }
      }
    if (!tTexte) { alert ("Aucun texte à lire (renseignez la prononciation)."); return; }

    // Profil : sélection persistée, sinon 1er profil disponible.
    const tProfils = VoiceProfiles.find ({}, {sort : {createdAt : -1}}).fetch ();
    if (tProfils.length === 0)
      { alert ("Aucun profil vocal disponible. Créez-en un d'abord."); return; }
    let tProfileId = tConfig && tConfig.voice_profile_id;
    if (!tProfileId || !tProfils.some (p => p.backendId === tProfileId))
      tProfileId = tProfils[0].backendId;
    const tProfile = tProfils.find (p => p.backendId === tProfileId);

    if (tConfig && tConfig.audio_file_id)
      {
        const tConfirme = confirm (
          "Un fichier audio existe deja pour ce calque. La nouvelle generation le remplacera lorsqu'elle sera terminee. Continuer ?"
        );
        if (!tConfirme) return;
      }

    const tPayload =
      {
        scope            : "calque",
        scene_id         : tSceneId,
        objet_id         : tId,
        text             : tTexte,
        mode             : "voice_clone",
        voice_profile_id : tProfileId,
        language         : (tProfile && tProfile.language) || "Auto",
        // Valeurs par défaut alignées sur tabGenerate
        temperature        : 1.0,
        top_p              : 1.0,
        top_k              : 50,
        repetition_penalty : 1.0,
        max_new_tokens     : 2048,
      };
    if (tConfig && tConfig.seed !== null && tConfig.seed !== undefined)
      tPayload.seed = tConfig.seed;
    // Override par la saisie manuelle si renseignée.
    const tSeedSel = inspecteur_seed_courant ();
    if (tSeedSel !== null) tPayload.seed = tSeedSel;

    const tSelectionKey = fabrique_selection_key_inspecteur (tSceneId, tId);
    autorise_autoplay_prochaine_maj_fichier_inspecteur (tSelectionKey);

    Meteor.call ("hub.generate", tPayload, (err) =>
      {
        if (!err) return;
        annule_autoplay_prochaine_maj_fichier_inspecteur (tSelectionKey);
        alert ("Erreur : " + (err.reason || err.message));
      });
  }

function profils_audio_scene (pEntree)
  {
    const tListe = VoiceProfiles.find ({}, {sort : {createdAt : -1}}).fetch ();
    if (tListe.length === 0) return [];
    let tSel = pEntree && pEntree.voice_profile_id;
    const tProfilSel = tListe.find ((p) => p.backendId === tSel);
    if (!tProfilSel || !profil_voix_materialisable_client (tProfilSel))
      {
        const tDefaut = profil_voix_defaut_client (tListe);
        tSel = tDefaut && tDefaut.backendId;
      }
    return tListe.map ((p) => ({
      ...p,
      selected     : p.backendId === tSel,
      selectedAttr : p.backendId === tSel ? "selected" : null,
    }));
  }

function seed_audio_scene_courante (pCle, pEntree)
  {
    const tBrut = gInspecteurSceneAudioSeedCustom.get (pCle);
    if (tBrut !== undefined && tBrut !== null)
      {
        const tStr = String (tBrut).trim ();
        if (tStr === "") return null;
        const tNum = parseInt (tStr, 10);
        return Number.isFinite (tNum) && tNum >= 0 ? tNum : null;
      }
    const tS = pEntree && pEntree.seed;
    return (tS === null || tS === undefined) ? null : tS;
  }

function valeur_champ_seed_audio_scene (pCle, pEntree)
  {
    const tBrut = gInspecteurSceneAudioSeedCustom.get (pCle);
    if (tBrut !== undefined && tBrut !== null) return String (tBrut);
    const tS = pEntree && pEntree.seed;
    return (tS === null || tS === undefined) ? "" : String (tS);
  }

function seeds_audio_scene (pCle, pEntree)
  {
    const tSel = seed_audio_scene_courante (pCle, pEntree);
    const tSelNum = Number (tSel);
    return Seeds.find ({}, {sort : {createdAt : -1}}).map ((s) =>
      ({
        ...s,
        selected     : tSel !== null && Number (s.value) === tSelNum,
        selectedAttr : (tSel !== null && Number (s.value) === tSelNum) ? "selected" : null,
      }));
  }

function instruction_generation_audio_scene_en_cours (pSceneId, pCle)
  {
    if (!pSceneId || !cle_audio_scene_valide (pCle)) return null;
    return Instructions.findOne (
      {
        kind   : "generate",
        status : {$in : ["pending", "running"]},
        "payload.scope"    : "scene_audio",
        "payload.scene_id" : pSceneId,
        "payload.audio_key": pCle,
      },
      {sort : {createdAt : -1}}
    );
  }

function libelle_instruction_generation_audio (pInstruction)
  {
    if (!pInstruction) return "Générer";
    if (pInstruction.status === "pending") return "En attente…";
    if (pInstruction.stage === "warming") return "Lancement du moteur…";
    return "Génération…";
  }

function instruction_generation_audio_scene_echec_recent (pSceneId, pCle, pEntree)
  {
    if (!pSceneId || !cle_audio_scene_valide (pCle)) return null;
    const tInstruction = Instructions.findOne (
      {
        kind   : "generate",
        status : "failed",
        "payload.scope"    : "scene_audio",
        "payload.scene_id" : pSceneId,
        "payload.audio_key": pCle,
      },
      {sort : {createdAt : -1}}
    );
    if (!tInstruction) return null;
    const tUpdatedAt = pEntree && pEntree.updated_at
      ? new Date (pEntree.updated_at).getTime ()
      : 0;
    const tCreatedAt = tInstruction.createdAt
      ? new Date (tInstruction.createdAt).getTime ()
      : 0;
    if (tUpdatedAt && tCreatedAt && tUpdatedAt > tCreatedAt) return null;
    return tInstruction;
  }

function message_echec_generation_audio (pInstruction)
  {
    if (!pInstruction) return null;
    return {
      texte : "Génération impossible : " + (pInstruction.error || "erreur inconnue"),
      type : "erreur",
    };
  }

function selectionne_audio_scene_actif (pCle)
  {
    if (!cle_audio_scene_valide (pCle)) return null;
    arrete_lecture_audio_projet_inspecteur ();
    arrete_lecture_audio_scene_inspecteur ();
    gInspecteurSceneAudioActif.set (pCle);
    gInspecteurProjetAudioActif.set (null);
    return pCle;
  }

function arrete_lecture_audio_scene_inspecteur ()
  {
    const tAudio = gInspecteurSceneAudioPreview.audio;
    if (tAudio)
      {
        try { tAudio.pause (); } catch (e) {}
        try { tAudio.currentTime = 0; } catch (e) {}
      }
    gInspecteurSceneAudioPreview.audio = null;
    gInspecteurSceneAudioPreview.key = null;
    gInspecteurSceneAudioLecture.set (null);
  }

function joue_audio_scene_inspecteur (pCle, pOpt = {})
  {
    if (!cle_audio_scene_valide (pCle)) return false;
    if (gInspecteurSceneAudioPreview.key === pCle && gInspecteurSceneAudioPreview.audio && !(pOpt && pOpt.force))
      {
        arrete_lecture_audio_scene_inspecteur ();
        return true;
      }

    const tSceneId = Session.get ("scene_id");
    const tScene = tSceneId ? Scenes.findOne (tSceneId) : null;
    const tFichier = fichier_audio_scene (tScene, pCle);
    const tUrl = url_hubfile_client (tFichier);
    if (!tUrl) return false;

    ws_force_pause ();
    arrete_lecture_audio_projet_inspecteur ();
    arrete_lecture_audio_scene_inspecteur ();
    const tAudio = new Audio (tUrl);
    tAudio.preload = "auto";
    applique_volume_audio_element_lecture_scene (tAudio, volume_audio_scene_lecture_scene (pCle));
    gInspecteurSceneAudioPreview.audio = tAudio;
    gInspecteurSceneAudioPreview.key = pCle;
    gInspecteurSceneAudioLecture.set (pCle);
    tAudio.addEventListener ("ended", () =>
      {
        if (gInspecteurSceneAudioPreview.audio === tAudio)
          arrete_lecture_audio_scene_inspecteur ();
      }, {once : true});
    tAudio.addEventListener ("error", () =>
      {
        if (gInspecteurSceneAudioPreview.audio === tAudio)
          arrete_lecture_audio_scene_inspecteur ();
      }, {once : true});
    const tPlay = tAudio.play ();
    if (tPlay && typeof tPlay.catch === "function")
      tPlay.catch (() =>
        {
          if (gInspecteurSceneAudioPreview.audio === tAudio)
            arrete_lecture_audio_scene_inspecteur ();
        });
    return true;
  }

function programme_lecture_audio_scene_apres_generation (pCle, pScene, pEntree)
  {
    if (!pScene || !cle_audio_scene_valide (pCle)) return;
    gInspecteurSceneAudioLectureApresGeneration.set (
      pCle,
      {
        scene_id : pScene._id,
        file_id : (pEntree && pEntree.file_id) || null,
      }
    );
  }

function annule_lecture_audio_scene_apres_generation (pCle)
  {
    if (cle_audio_scene_valide (pCle))
      gInspecteurSceneAudioLectureApresGeneration.set (pCle, null);
  }

function lance_generation_audio_scene (pCle)
  {
    const tCle = selectionne_audio_scene_actif (pCle);
    const tSceneId = Session.get ("scene_id");
    const tScene = tSceneId ? Scenes.findOne (tSceneId) : null;
    const tItem = AUDIO_SCENE_ITEMS_PAR_CLE.get (tCle);
    if (!tScene || !tItem || !tItem.synthese) return;
    const tTexteEl = document.querySelector ('.js_inspecteur_scene_audio_prono[data-key="' + tCle + '"]');
    const tTexte = ((tTexteEl && tTexteEl.value) || "").trim ();
    if (!tTexte) { alert ("Renseignez le texte à synthétiser."); return; }

    const tProfils = VoiceProfiles.find ({}, {sort : {createdAt : -1}}).fetch ();
    if (tProfils.length === 0)
      { alert ("Aucun profil vocal disponible. Créez-en un d'abord."); return; }
    const tProfilEl = document.querySelector ('.js_inspecteur_scene_audio_profil[data-key="' + tCle + '"]');
    let tProfileId = (tProfilEl && tProfilEl.value) || null;
    const tProfilSelectionne = tProfils.find ((p) => p.backendId === tProfileId);
    if (!tProfilSelectionne || !profil_voix_materialisable_client (tProfilSelectionne))
      {
        const tDefaut = profil_voix_defaut_client (tProfils);
        tProfileId = tDefaut && tDefaut.backendId;
      }
    const tProfile = tProfils.find ((p) => p.backendId === tProfileId);
    const tEntree = entree_audio_scene (tScene, tCle);
    if (tEntree && tEntree.file_id
        && !confirm ("Un fichier audio existe déjà pour cette rubrique. La nouvelle génération le remplacera lorsqu'elle sera terminée. Continuer ?"))
      return;

    const tPayload =
      {
        scope            : "scene_audio",
        scene_id         : tScene._id,
        audio_key        : tCle,
        text             : tTexte,
        mode             : "voice_clone",
        voice_profile_id : tProfileId,
        language         : (tProfile && tProfile.language) || "Auto",
        temperature        : 1.0,
        top_p              : 1.0,
        top_k              : 50,
        repetition_penalty : 1.0,
        max_new_tokens     : 2048,
      };
    const tSeed = seed_audio_scene_courante (tCle, tEntree);
    if (tSeed !== null) tPayload.seed = tSeed;
    gInspecteurSceneAudioMessages.set (tCle, null);
    programme_lecture_audio_scene_apres_generation (tCle, tScene, tEntree);
    Meteor.call ("hub.generate", tPayload, (err) =>
      {
        if (err)
          {
            annule_lecture_audio_scene_apres_generation (tCle);
            alert ("Erreur : " + (err.reason || err.message));
          }
      });
  }

function upload_audio_scene_depuis_fichier (pCle, pFile)
  {
    const tSceneId = Session.get ("scene_id");
    const tScene = tSceneId ? Scenes.findOne (tSceneId) : null;
    if (!tScene || !cle_audio_scene_valide (pCle) || !pFile) return;
    selectionne_audio_scene_actif (pCle);
    gInspecteurSceneAudioUpload.set (pCle, {phase : "upload", progression : 0});
    const tUpload = HubFiles.insert (
      {
        file : pFile,
        meta : {
          ownerId : Meteor.userId (),
          user_id : Meteor.userId (),
          projet_id : tScene.projet_id,
          scene_id : tScene._id,
          kind : "scene-audio",
          cle : pCle,
          source : "scene-audio-import",
        },
        chunkSize : "dynamic",
      }, false);
    tUpload.on ("progress", (pPct) =>
      {
        const tPct = Math.max (0, Math.min (100, Math.round (Number (pPct) || 0)));
        gInspecteurSceneAudioUpload.set (pCle, {phase : "upload", progression : tPct});
      });
    tUpload.on ("end", (err, fileObj) =>
      {
        if (err)
          {
            gInspecteurSceneAudioUpload.set (pCle, null);
            alert ("Téléversement échoué : " + err.message);
            return;
          }
        gInspecteurSceneAudioUpload.set (pCle, {phase : "traitement", progression : 100});
        Meteor.call ("maj_audio_scene",
          {
            scene_id : tScene._id,
            cle : pCle,
            audio_file_id : fileObj._id,
            audio_import_source : "scene-audio-import",
          },
          (err2) =>
            {
              gInspecteurSceneAudioUpload.set (pCle, null);
              if (err2) alert ("Erreur : " + (err2.reason || err2.message));
            });
      });
    Tracker.afterFlush (() => { tUpload.start (); });
  }

function persiste_volume_audio_scene (pCle, pValeur)
  {
    const tSceneId = Session.get ("scene_id");
    if (!tSceneId || !cle_audio_scene_valide (pCle)) return;
    const tVolume = normalise_volume_audio_pct_client (pValeur);
    Meteor.call ("regler_audio_scene",
      {
        scene_id : tSceneId,
        cle : pCle,
        volume_pct : tVolume,
      },
      (err) =>
        {
          if (err) alert ("Erreur : " + (err.reason || err.message));
        });
  }

function applique_champ_volume_audio_scene (pChamp)
  {
    if (!pChamp || !pChamp.getAttribute) return;
    const tCle = pChamp.getAttribute ("data-key");
    if (!cle_audio_scene_valide (tCle)) return;
    const tVolume = normalise_volume_audio_pct_client (pChamp.value);
    pChamp.value = String (tVolume);
    persiste_volume_audio_scene (tCle, tVolume);
  }

function audio_projet_entree_active ()
  {
    const tProjet = projet_courant_scene ();
    const tCle = gInspecteurProjetAudioActif.get ();
    if (!tProjet || !cle_audio_projet_valide (tCle)) return null;
    return {projet : tProjet, cle : tCle, item : AUDIO_PROJET_ITEMS_PAR_CLE.get (tCle)};
  }

function profils_audio_projet (pEntree)
  {
    const tListe = VoiceProfiles.find ({}, {sort : {createdAt : -1}}).fetch ();
    if (tListe.length === 0) return [];
    let tSel = pEntree && pEntree.voice_profile_id;
    const tProfilSel = tListe.find ((p) => p.backendId === tSel);
    if (!tProfilSel || !profil_voix_materialisable_client (tProfilSel))
      {
        const tDefaut = profil_voix_defaut_client (tListe);
        tSel = tDefaut && tDefaut.backendId;
      }
    return tListe.map ((p) => ({
      ...p,
      selected     : p.backendId === tSel,
      selectedAttr : p.backendId === tSel ? "selected" : null,
    }));
  }

function seed_audio_projet_courante (pCle, pEntree)
  {
    const tBrut = gInspecteurProjetAudioSeedCustom.get (pCle);
    if (tBrut !== undefined && tBrut !== null)
      {
        const tStr = String (tBrut).trim ();
        if (tStr === "") return null;
        const tNum = parseInt (tStr, 10);
        return Number.isFinite (tNum) && tNum >= 0 ? tNum : null;
      }
    const tS = pEntree && pEntree.seed;
    return (tS === null || tS === undefined) ? null : tS;
  }

function valeur_champ_seed_audio_projet (pCle, pEntree)
  {
    const tBrut = gInspecteurProjetAudioSeedCustom.get (pCle);
    if (tBrut !== undefined && tBrut !== null) return String (tBrut);
    const tS = pEntree && pEntree.seed;
    return (tS === null || tS === undefined) ? "" : String (tS);
  }

function seeds_audio_projet (pCle, pEntree)
  {
    const tSel = seed_audio_projet_courante (pCle, pEntree);
    const tSelNum = Number (tSel);
    return Seeds.find ({}, {sort : {createdAt : -1}}).map ((s) =>
      ({
        ...s,
        selected     : tSel !== null && Number (s.value) === tSelNum,
        selectedAttr : (tSel !== null && Number (s.value) === tSelNum) ? "selected" : null,
      }));
  }

function instruction_generation_audio_projet_en_cours (pProjetId, pCle)
  {
    if (!pProjetId || !cle_audio_projet_valide (pCle)) return null;
    return Instructions.findOne (
      {
        kind   : "generate",
        status : {$in : ["pending", "running"]},
        "payload.scope"     : "projet_audio",
        "payload.projet_id" : pProjetId,
        "payload.audio_key" : pCle,
      },
      {sort : {createdAt : -1}}
    );
  }

function instruction_generation_audio_projet_echec_recent (pProjetId, pCle, pEntree)
  {
    if (!pProjetId || !cle_audio_projet_valide (pCle)) return null;
    const tInstruction = Instructions.findOne (
      {
        kind   : "generate",
        status : "failed",
        "payload.scope"     : "projet_audio",
        "payload.projet_id" : pProjetId,
        "payload.audio_key" : pCle,
      },
      {sort : {createdAt : -1}}
    );
    if (!tInstruction) return null;
    const tUpdatedAt = pEntree && pEntree.updated_at
      ? new Date (pEntree.updated_at).getTime ()
      : 0;
    const tCreatedAt = tInstruction.createdAt
      ? new Date (tInstruction.createdAt).getTime ()
      : 0;
    if (tUpdatedAt && tCreatedAt && tUpdatedAt > tCreatedAt) return null;
    return tInstruction;
  }

function selectionne_audio_projet_actif (pCle)
  {
    if (!cle_audio_projet_valide (pCle)) return null;
    arrete_lecture_audio_scene_inspecteur ();
    arrete_lecture_audio_projet_inspecteur ();
    gInspecteurProjetAudioActif.set (pCle);
    gInspecteurSceneAudioActif.set (null);
    return pCle;
  }

function arrete_lecture_audio_projet_inspecteur ()
  {
    const tAudio = gInspecteurProjetAudioPreview.audio;
    if (tAudio)
      {
        try { tAudio.pause (); } catch (e) {}
        try { tAudio.currentTime = 0; } catch (e) {}
      }
    gInspecteurProjetAudioPreview.audio = null;
    gInspecteurProjetAudioPreview.key = null;
    gInspecteurProjetAudioLecture.set (null);
  }

function joue_audio_projet_inspecteur (pCle, pOpt = {})
  {
    if (!cle_audio_projet_valide (pCle)) return false;
    if (gInspecteurProjetAudioPreview.key === pCle && gInspecteurProjetAudioPreview.audio && !(pOpt && pOpt.force))
      {
        arrete_lecture_audio_projet_inspecteur ();
        return true;
      }

    const tProjet = projet_courant_scene ();
    const tFichier = fichier_audio_projet (tProjet, pCle);
    const tUrl = url_hubfile_client (tFichier);
    if (!tUrl) return false;

    ws_force_pause ();
    arrete_lecture_audio_scene_inspecteur ();
    arrete_lecture_audio_projet_inspecteur ();
    const tAudio = new Audio (tUrl);
    tAudio.preload = "auto";
    applique_volume_audio_element_lecture_scene (tAudio, volume_audio_projet_lecture_scene (pCle));
    gInspecteurProjetAudioPreview.audio = tAudio;
    gInspecteurProjetAudioPreview.key = pCle;
    gInspecteurProjetAudioLecture.set (pCle);
    tAudio.addEventListener ("ended", () =>
      {
        if (gInspecteurProjetAudioPreview.audio === tAudio)
          arrete_lecture_audio_projet_inspecteur ();
      }, {once : true});
    tAudio.addEventListener ("error", () =>
      {
        if (gInspecteurProjetAudioPreview.audio === tAudio)
          arrete_lecture_audio_projet_inspecteur ();
      }, {once : true});
    const tPlay = tAudio.play ();
    if (tPlay && typeof tPlay.catch === "function")
      tPlay.catch (() =>
        {
          if (gInspecteurProjetAudioPreview.audio === tAudio)
            arrete_lecture_audio_projet_inspecteur ();
        });
    return true;
  }

function programme_lecture_audio_projet_apres_generation (pCle, pProjet, pEntree)
  {
    if (!pProjet || !cle_audio_projet_valide (pCle)) return;
    gInspecteurProjetAudioLectureApresGeneration.set (
      pCle,
      {
        projet_id : pProjet._id,
        file_id : (pEntree && pEntree.file_id) || null,
      }
    );
  }

function annule_lecture_audio_projet_apres_generation (pCle)
  {
    if (cle_audio_projet_valide (pCle))
      gInspecteurProjetAudioLectureApresGeneration.set (pCle, null);
  }

function valeur_seed_audio_projet (pCle, pEntree)
  {
    return seed_audio_projet_courante (pCle, pEntree);
  }

function lance_generation_audio_projet (pCle)
  {
    const tCle = selectionne_audio_projet_actif (pCle);
    const tProjet = projet_courant_scene ();
    const tItem = AUDIO_PROJET_ITEMS_PAR_CLE.get (tCle);
    if (!tProjet || !tItem || !tItem.synthese) return;
    const tTexteEl = document.querySelector ('.js_inspecteur_projet_audio_prono[data-key="' + tCle + '"]');
    const tTexte = ((tTexteEl && tTexteEl.value) || "").trim ();
    if (!tTexte) { alert ("Renseignez le texte à synthétiser."); return; }

    const tProfils = VoiceProfiles.find ({}, {sort : {createdAt : -1}}).fetch ();
    if (tProfils.length === 0)
      { alert ("Aucun profil vocal disponible. Créez-en un d'abord."); return; }
    const tProfilEl = document.querySelector ('.js_inspecteur_projet_audio_profil[data-key="' + tCle + '"]');
    let tProfileId = (tProfilEl && tProfilEl.value) || null;
    const tProfilSelectionne = tProfils.find ((p) => p.backendId === tProfileId);
    if (!tProfilSelectionne || !profil_voix_materialisable_client (tProfilSelectionne))
      {
        const tDefaut = profil_voix_defaut_client (tProfils);
        tProfileId = tDefaut && tDefaut.backendId;
      }
    const tProfile = tProfils.find ((p) => p.backendId === tProfileId);
    const tEntree = entree_audio_projet (tProjet, tCle);
    if (tEntree && tEntree.file_id
        && !confirm ("Un fichier audio existe déjà pour cette rubrique. La nouvelle génération le remplacera lorsqu'elle sera terminée. Continuer ?"))
      return;

    const tPayload =
      {
        scope            : "projet_audio",
        projet_id        : tProjet._id,
        audio_key        : tCle,
        text             : tTexte,
        mode             : "voice_clone",
        voice_profile_id : tProfileId,
        language         : (tProfile && tProfile.language) || "Auto",
        temperature        : 1.0,
        top_p              : 1.0,
        top_k              : 50,
        repetition_penalty : 1.0,
        max_new_tokens     : 2048,
      };
    const tSeed = valeur_seed_audio_projet (tCle, tEntree);
    if (tSeed !== null) tPayload.seed = tSeed;
    gInspecteurProjetAudioMessages.set (tCle, null);
    programme_lecture_audio_projet_apres_generation (tCle, tProjet, tEntree);
    Meteor.call ("hub.generate", tPayload, (err) =>
      {
        if (err)
          {
            annule_lecture_audio_projet_apres_generation (tCle);
            alert ("Erreur : " + (err.reason || err.message));
          }
      });
  }

function upload_audio_projet_depuis_fichier (pCle, pFile)
  {
    const tProjet = projet_courant_scene ();
    if (!tProjet || !cle_audio_projet_valide (pCle) || !pFile) return;
    selectionne_audio_projet_actif (pCle);
    gInspecteurProjetAudioUpload.set (pCle, {phase : "upload", progression : 0});
    const tUpload = HubFiles.insert (
      {
        file : pFile,
        meta : {
          ownerId : Meteor.userId (),
          user_id : Meteor.userId (),
          projet_id : tProjet._id,
          kind : "projet-audio",
          cle : pCle,
          source : "project-audio-import",
        },
        chunkSize : "dynamic",
      }, false);
    tUpload.on ("progress", (pPct) =>
      {
        const tPct = Math.max (0, Math.min (100, Math.round (Number (pPct) || 0)));
        gInspecteurProjetAudioUpload.set (pCle, {phase : "upload", progression : tPct});
      });
    tUpload.on ("end", (err, fileObj) =>
      {
        if (err)
          {
            gInspecteurProjetAudioUpload.set (pCle, null);
            alert ("Téléversement échoué : " + err.message);
            return;
          }
        gInspecteurProjetAudioUpload.set (pCle, {phase : "traitement", progression : 100});
        Meteor.call ("maj_audio_projet",
          {
            projet_id : tProjet._id,
            cle : pCle,
            audio_file_id : fileObj._id,
            audio_import_source : "project-audio-import",
          },
          (err2) =>
            {
              gInspecteurProjetAudioUpload.set (pCle, null);
              if (err2) alert ("Erreur : " + (err2.reason || err2.message));
            });
      });
    Tracker.afterFlush (() => { tUpload.start (); });
  }

function upload_widget_hype_projet_depuis_fichier (pType, pFile)
  {
    const tProjet = projet_courant_scene ();
    if (!tProjet || !pFile || !utilisateur_peut_modifier_projet_inspecteur ()) return;
    const tType = pType === "recompense_scene" ? "recompense_scene" : "recompense_carte";
    const tNom = (pFile.name || "").toLowerCase ();
    if (!tNom.endsWith (".oam"))
      {
        gInspecteurProjetHypeMessages.set (tType, {type : "erreur", texte : "Le fichier doit porter l'extension .oam."});
        return;
      }
    gInspecteurProjetHypeMessages.set (tType, null);
    gInspecteurProjetHypeUpload.set (tType, {phase : "upload", progression : 0});
    const tUpload = HubFiles.insert (
      {
        file : pFile,
        meta : {
          ownerId : Meteor.userId (),
          user_id : Meteor.userId (),
          projet_id : tProjet._id,
          kind : "hype-widget-oam",
          widget_type : tType,
          source : "hype-widget-import",
        },
        chunkSize : "dynamic",
      }, false);
    tUpload.on ("progress", (pPct) =>
      {
        const tPct = Math.max (0, Math.min (100, Math.round (Number (pPct) || 0)));
        gInspecteurProjetHypeUpload.set (tType, {phase : "upload", progression : tPct});
      });
    tUpload.on ("end", (err, fileObj) =>
      {
        if (err)
          {
            gInspecteurProjetHypeUpload.set (tType, null);
            gInspecteurProjetHypeMessages.set (tType, {type : "erreur", texte : "Téléversement échoué : " + err.message});
            return;
          }
        gInspecteurProjetHypeUpload.set (tType, {phase : "traitement", progression : 100});
        Meteor.call (
          "installer_widget_hype_projet",
          {
            projet_id : tProjet._id,
            type : tType,
            file_id : fileObj._id,
          },
          (err2) =>
            {
              gInspecteurProjetHypeUpload.set (tType, null);
              if (err2)
                {
                  gInspecteurProjetHypeMessages.set (tType, {type : "erreur", texte : "Installation impossible : " + (err2.reason || err2.message)});
                  return;
                }
              gInspecteurProjetHypeMessages.set (tType, {type : "succes", texte : "Widget installé."});
            }
        );
      });
    Tracker.afterFlush (() => { tUpload.start (); });
  }

function persiste_reglages_audio_projet (pCle, pPatch = {})
  {
    const tProjet = projet_courant_scene ();
    if (!tProjet || !cle_audio_projet_valide (pCle)) return;
    const tEntree = entree_audio_projet (tProjet, pCle) || {};
    const tOp =
      {
        projet_id : tProjet._id,
        cle : pCle,
        volume_pct : normalise_volume_audio_pct_client (
          Object.prototype.hasOwnProperty.call (pPatch, "volume_pct")
            ? pPatch.volume_pct
            : tEntree.volume_pct
        ),
      };
    if (pCle === "entends_tu_le_son_1")
      tOp.delai_sequence_ms = normalise_delai_sequence_entends_tu_ms_client (
        Object.prototype.hasOwnProperty.call (pPatch, "delai_sequence_ms")
          ? pPatch.delai_sequence_ms
          : tEntree.delai_sequence_ms
      );
    Meteor.call ("regler_audio_projet", tOp, (err) =>
      {
        if (err) alert ("Erreur : " + (err.reason || err.message));
      });
  }

function applique_champ_volume_audio_projet (pChamp)
  {
    if (!pChamp || !pChamp.getAttribute) return;
    const tCle = pChamp.getAttribute ("data-key");
    if (!cle_audio_projet_valide (tCle)) return;
    const tVolume = normalise_volume_audio_pct_client (pChamp.value);
    pChamp.value = String (tVolume);
    persiste_reglages_audio_projet (tCle, {volume_pct : tVolume});
  }

function applique_champ_delai_sequence_audio_projet (pChamp)
  {
    if (!pChamp || !pChamp.getAttribute) return;
    const tCle = pChamp.getAttribute ("data-key");
    if (tCle !== "entends_tu_le_son_1") return;
    const tValeur = normalise_delai_sequence_entends_tu_ms_client (pChamp.value);
    pChamp.value = String (tValeur);
    persiste_reglages_audio_projet (tCle, {delai_sequence_ms : tValeur});
  }

// ---------------------------------------------------------------------------
// Editeur audio (wavesurfer.js) : visualisation, trim, gain, fades, normaliser,
// sauvegarde destructive via ffmpeg côté serveur.
// ---------------------------------------------------------------------------

const gAudioCtx = new ReactiveVar (null);

const gInspecteurAudioSource =
  {
    selectionKey : null,
    fileId       : null,
  };

const gInspecteurProjetAudioPreview =
  {
    audio : null,
    key   : null,
  };

const gInspecteurSceneAudioPreview =
  {
    audio : null,
    key   : null,
  };

function cible_audio_scene_pour_cle (pCle)
  {
    if (!cle_audio_scene_valide (pCle)) return null;
    const tSceneId = Session.get ("scene_id");
    const tScene = tSceneId ? Scenes.findOne (tSceneId) : null;
    const tFichier = fichier_audio_scene (tScene, pCle);
    return {
      type : "scene",
      selectionKey : tScene ? ("scene:" + tScene._id + ":" + pCle) : null,
      fileId : tFichier && tFichier._id,
      url : url_hubfile_client (tFichier),
      fichier : tFichier,
      scene : tScene,
      cle : pCle,
    };
  }

function cible_audio_editeur_courante ()
  {
    const tCleScene = gInspecteurSceneAudioActif.get ();
    if (tCleScene && cle_audio_scene_valide (tCleScene))
      return cible_audio_scene_pour_cle (tCleScene);

    const tCleProjet = gInspecteurProjetAudioActif.get ();
    if (gOngletDroite.get () === "projet" && tCleProjet && cle_audio_projet_valide (tCleProjet))
      {
        const tProjet = projet_courant_scene ();
        const tFichier = fichier_audio_projet (tProjet, tCleProjet);
        return {
          type : "projet",
          selectionKey : tProjet ? ("projet:" + tProjet._id + ":" + tCleProjet) : null,
          fileId : tFichier && tFichier._id,
          url : url_hubfile_client (tFichier),
          fichier : tFichier,
          projet : tProjet,
          cle : tCleProjet,
        };
      }

    const tId = gCalqueSelectionne.get ();
    const tSceneId = Session.get ("scene_id");
    const tScene  = tId ? Scenes.findOne (tSceneId) : null;
    const tConfig = tScene ? get_objet_config (tScene, tId) : null;
    const tFileId = tConfig && tConfig.audio_file_id;
    let tFichier = null;
    if (tFileId) tFichier = HubFiles.findOne ({_id : tFileId});
    return {
      type : "calque",
      selectionKey : (tSceneId && tId) ? (tSceneId + ":" + tId) : null,
      fileId : tFileId || null,
      url : url_hubfile_client (tFichier),
      fichier : tFichier,
      scene : tScene,
      objetId : tId,
    };
  }

function get_audio_ctx ()
  {
    let tCtx = gAudioCtx.get ();
    if (tCtx && tCtx.state !== "closed") return tCtx;
    const Ctor = window.AudioContext || window.webkitAudioContext;
    if (!Ctor) return null;
    tCtx = new Ctor ();
    gAudioCtx.set (tCtx);
    return tCtx;
  }

function audio_est_safari ()
  {
    const tUa = navigator.userAgent || "";
    return /Safari/i.test (tUa)
      && !/(Chrome|Chromium|CriOS|FxiOS|Edg|OPR|Android)/i.test (tUa);
  }

function audio_est_ios_ou_ipados ()
  {
    const tUa = navigator.userAgent || "";
    const tPlatform = navigator.platform || "";
    const tTouch = navigator.maxTouchPoints || 0;
    return /iPad|iPhone|iPod/i.test (tUa)
      || (tPlatform === "MacIntel" && tTouch > 1)
      || (/Macintosh/i.test (tUa) && tTouch > 1);
  }

function audio_prefere_backend_media_element ()
  {
    // Safari peut garder une animation WaveSurfer normale avec une sortie
    // WebAudio muette. Le backend média natif est moins ambitieux, mais plus fiable.
    return audio_est_safari ();
  }

async function audio_resume_context (pCtx)
  {
    if (!pCtx || typeof pCtx.resume !== "function") return;
    if (pCtx.state === "suspended" || pCtx.state === "interrupted")
      { try { await pCtx.resume (); } catch (e) {} }
  }

function audio_volume_preview (pVolume)
  {
    const tVolume = Number (pVolume);
    if (!isFinite (tVolume)) return 1;
    if (audio_prefere_backend_media_element ())
      return Math.max (0, Math.min (1, tVolume));
    return Math.max (0, tVolume);
  }

function ws_applique_volume_preview (pEnvelopeVolume = null)
  {
    if (!gWS.instance) return;
    const tEnv = pEnvelopeVolume === null || pEnvelopeVolume === undefined
      ? (gWS.envelope && typeof gWS.envelope.getCurrentVolume === "function"
        ? Number (gWS.envelope.getCurrentVolume ())
        : 1)
      : Number (pEnvelopeVolume);
    const tEnvelopeVolume = isFinite (tEnv) ? tEnv : 1;
    const tGdb = Number (gEditeur.get ("gain_db") || 0);
    const tVolume = audio_volume_preview (tEnvelopeVolume * db_to_lin (tGdb));
    try
      {
        const tMedia = gWS.instance.getMediaElement && gWS.instance.getMediaElement ();
        if (tMedia && typeof tMedia.getGainNode === "function")
          {
            const tGainNode = tMedia.getGainNode ();
            if (tGainNode && tGainNode.gain)
              {
                tGainNode.gain.value = tVolume;
                return;
              }
          }
      }
    catch (e) {}
    try { gWS.instance.setVolume (tVolume); } catch (e) {}
  }

function scene_theme_est_sombre ()
  {
    const tRoot = (typeof document !== "undefined") ? document.documentElement : null;
    if (tRoot && tRoot.dataset && tRoot.dataset.theme === "dark") return true;
    if (tRoot && tRoot.dataset && tRoot.dataset.theme === "light") return false;
    return !!(window.matchMedia
      && window.matchMedia ("(prefers-color-scheme: dark)").matches);
  }

function interface_est_tactile ()
  {
    return audio_est_ios_ou_ipados ()
      || !!(window.matchMedia
            && window.matchMedia ("(pointer: coarse)").matches);
  }

function audio_has_capture_support ()
  {
    return !!(
      (navigator.mediaDevices && navigator.mediaDevices.getUserMedia)
      || navigator.getUserMedia
      || navigator.webkitGetUserMedia
      || navigator.mozGetUserMedia
    );
  }

function audio_get_user_media (pContraintes)
  {
    if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia)
      return navigator.mediaDevices.getUserMedia (pContraintes);
    const tLegacy = navigator.getUserMedia
      || navigator.webkitGetUserMedia
      || navigator.mozGetUserMedia;
    if (!tLegacy)
      return Promise.reject (new Error ("getUserMedia indisponible"));
    return new Promise ((resolve, reject) =>
      tLegacy.call (navigator, pContraintes, resolve, reject));
  }

function rec_unsupported_message ()
  {
    if (!audio_has_capture_support ())
      {
        if (!window.isSecureContext)
          return "Sur iPad/Safari, l'enregistrement audio exige une page HTTPS ou localhost.";
        return "L'accès au micro n'est pas disponible sur ce navigateur.";
      }
    if (!(window.AudioContext || window.webkitAudioContext))
      return "Web Audio n'est pas disponible sur cet appareil.";
    return "";
  }

// Etat de l'éditeur courant (réactif). Une instance Wavesurfer à la fois.
const gEditeur = new ReactiveDict ();
// gEditeur keys : url, dur, start, end, gain_db, fade_in, fade_out,
//                 dirty, saving, playing, cur, peak,
//                 source_url, source_file_id, source_dur,
//                 preview_url, preview_building, delete_ranges

const gWS = { instance: null, region: null, regions: null, envelope: null,
              selection: null, play_end: null, url: null, audioCtx: null,
              ws_wrapper: null, edit_overlay: null, container: null,
              audioBuffer: null, audioBufferUrl: null, audioBufferPromise: null,
              mediaSyncCleanup: null,
              previewBuildId: 0,
              peakCache: new Map () };

function ws_destroy ()
  {
    if (gWS.instance)
      {
        try { gWS.instance.destroy (); } catch (e) {}
      }
    gWS.instance = null;
    gWS.region   = null;
    gWS.selection = null;
    gWS.play_end = null;
    gWS.regions  = null;
    gWS.envelope = null;
    gWS.audioCtx = null;
    gWS.ws_wrapper = null;
    gWS.container = null;
    if (typeof gWS.mediaSyncCleanup === "function")
      { try { gWS.mediaSyncCleanup (); } catch (e) {} }
    gWS.mediaSyncCleanup = null;
    if (gWS.edit_overlay && gWS.edit_overlay.parentNode)
      { try { gWS.edit_overlay.parentNode.removeChild (gWS.edit_overlay); } catch (e) {} }
    gWS.edit_overlay = null;
    gWS.audioBuffer = null;
    gWS.audioBufferUrl = null;
    gWS.audioBufferPromise = null;
    gWS.url      = null;
  }

function db_to_lin (pDb) { return Math.pow (10, pDb / 20); }
function lin_to_db (pLin) { return 20 * Math.log10 (Math.max (1e-6, pLin)); }

function ws_split_channels_overlay (pChannels = 2)
  {
    const tNb = Math.max (1, Math.min (8, Number (pChannels) || 2));
    return Array.from ({length : tNb}, () => ({overlay : true}));
  }

function ws_waveform_options ()
  {
    return {
      normalize     : false,
      barHeight     : REC_WAVEFORM_VERTICAL_SCALE,
      waveColor     : "rgba(0,0,0,0)",
      progressColor : "rgba(0,0,0,0)",
    };
  }

async function ws_get_audio_buffer (pUrl)
  {
    if (!pUrl) return null;
    if (gWS.audioBuffer && gWS.audioBufferUrl === pUrl) return gWS.audioBuffer;
    if (gWS.audioBufferPromise && gWS.audioBufferUrl === pUrl) return gWS.audioBufferPromise;
    const tCtx = get_audio_ctx ();
    if (!tCtx) return null;
    gWS.audioBufferUrl = pUrl;
    gWS.audioBufferPromise = fetch (pUrl)
      .then ((r) => r.arrayBuffer ())
      .then ((ab) => tCtx.decodeAudioData (ab.slice (0)))
      .then ((audio) =>
        {
          if (gWS.audioBufferUrl !== pUrl) return null;
          gWS.audioBuffer = audio;
          gWS.audioBufferPromise = null;
          return audio;
        })
      .catch ((err) =>
        {
          if (gWS.audioBufferUrl === pUrl) gWS.audioBufferPromise = null;
          throw err;
        });
    return gWS.audioBufferPromise;
  }

function ws_revoke_blob_url (pUrl)
  {
    if (typeof pUrl !== "string" || !pUrl.startsWith ("blob:")) return;
    try { URL.revokeObjectURL (pUrl); } catch (e) {}
  }

function ws_normalise_delete_ranges (pRanges, pDur)
  {
    const tDur = Math.max (0, Number (pDur) || 0);
    const tRanges = [];
    if (!Array.isArray (pRanges) || !tDur) return tRanges;
    for (const tRange of pRanges)
      {
        if (!tRange || typeof tRange !== "object") continue;
        const tSBrut = Number (tRange.start);
        const tEBrut = Number (tRange.end);
        if (!isFinite (tSBrut) || !isFinite (tEBrut)) continue;
        const tS = Math.max (0, Math.min (tDur, tSBrut));
        const tE = Math.max (0, Math.min (tDur, tEBrut));
        if (tE - tS <= 0.02) continue;
        tRanges.push ({start : tS, end : tE});
      }
    tRanges.sort ((a, b) => a.start - b.start || a.end - b.end);
    const tFusion = [];
    for (const tRange of tRanges)
      {
        const tPrev = tFusion[tFusion.length - 1];
        if (!tPrev)
          {
            tFusion.push ({...tRange});
            continue;
          }
        if (tRange.start <= tPrev.end + 0.005)
          {
            tPrev.end = Math.max (tPrev.end, tRange.end);
            continue;
          }
        tFusion.push ({...tRange});
      }
    return tFusion;
  }

function ws_delete_ranges_courantes ()
  {
    const tDur = Number (gEditeur.get ("source_dur") || gEditeur.get ("dur") || 0);
    return ws_normalise_delete_ranges (gEditeur.get ("delete_ranges") || [], tDur);
  }

function ws_total_deleted_duration (pRanges)
  {
    return (pRanges || []).reduce ((pSomme, pRange) =>
      pSomme + Math.max (0, Number ((pRange && pRange.end) || 0) - Number ((pRange && pRange.start) || 0)), 0);
  }

function ws_map_preview_time_to_source (pTime, pRanges, pSourceDur)
  {
    const tRanges = ws_normalise_delete_ranges (pRanges, pSourceDur);
    const tSourceDur = Math.max (0, Number (pSourceDur) || 0);
    const tPreviewTime = Math.max (0, Number (pTime) || 0);
    let tOffset = 0;
    for (const tRange of tRanges)
      {
        const tPreviewStart = tRange.start - tOffset;
        if (tPreviewTime < tPreviewStart)
          return Math.max (0, Math.min (tSourceDur, tPreviewTime + tOffset));
        tOffset += (tRange.end - tRange.start);
      }
    return Math.max (0, Math.min (tSourceDur, tPreviewTime + tOffset));
  }

function ws_bornes_source_pour_persistance (pDeleteRanges)
  {
    const tSourceDur = Math.max (0, Number (gEditeur.get ("source_dur") || gEditeur.get ("dur") || 0));
    const tPreviewDur = Math.max (0, Number (gEditeur.get ("dur") || tSourceDur || 0));
    const tRanges = ws_normalise_delete_ranges (pDeleteRanges, tSourceDur);
    const tStartPreviewBrut = Number (gEditeur.get ("start"));
    const tEndPreviewBrut = Number (gEditeur.get ("end"));
    const tStartPreview = isFinite (tStartPreviewBrut) ? tStartPreviewBrut : 0;
    const tEndPreview = isFinite (tEndPreviewBrut) ? tEndPreviewBrut : tPreviewDur;
    const tStartSource = ws_map_preview_time_to_source (tStartPreview, tRanges, tSourceDur);
    const tEndSource = ws_map_preview_time_to_source (tEndPreview, tRanges, tSourceDur);
    const tStart = Math.max (0, Math.min (tSourceDur, tStartSource));
    const tEnd = Math.max (tStart, Math.min (tSourceDur, tEndSource));
    return {start : tStart, end : tEnd};
  }

function ws_audio_buffer_sans_plages (pAudioBuffer, pRanges)
  {
    if (!pAudioBuffer) return null;
    const tDur = pAudioBuffer.duration || 0;
    const tRanges = ws_normalise_delete_ranges (pRanges, tDur);
    if (!tRanges.length) return pAudioBuffer;
    const tCtx = get_audio_ctx ();
    if (!tCtx) return null;
    const tSr = pAudioBuffer.sampleRate;
    const tCh = pAudioBuffer.numberOfChannels;
    const tSegments = [];
    let tCur = 0;
    for (const tRange of tRanges)
      {
        const tS = Math.max (0, Math.min (pAudioBuffer.length, Math.floor (tRange.start * tSr)));
        const tE = Math.max (0, Math.min (pAudioBuffer.length, Math.ceil (tRange.end * tSr)));
        if (tS > tCur) tSegments.push ({start : tCur, end : tS});
        tCur = Math.max (tCur, tE);
      }
    if (tCur < pAudioBuffer.length)
      tSegments.push ({start : tCur, end : pAudioBuffer.length});
    const tNewLen = tSegments.reduce ((pSomme, pSeg) => pSomme + Math.max (0, pSeg.end - pSeg.start), 0);
    if (tNewLen <= 0) return null;
    const tOut = tCtx.createBuffer (tCh, tNewLen, tSr);
    for (let c = 0; c < tCh; c++)
      {
        const tSrc = pAudioBuffer.getChannelData (c);
        const tDst = tOut.getChannelData (c);
        let tOffset = 0;
        for (const tSeg of tSegments)
          {
            const tChunk = tSrc.subarray (tSeg.start, tSeg.end);
            tDst.set (tChunk, tOffset);
            tOffset += tChunk.length;
          }
      }
    return tOut;
  }

function ws_efface_preview_suppressions ()
  {
    gWS.previewBuildId += 1;
    ws_revoke_blob_url (gEditeur.get ("preview_url"));
    gEditeur.set ("preview_url", null);
    gEditeur.set ("delete_ranges", []);
    gEditeur.set ("preview_building", false);
  }

async function ws_regenere_preview_suppressions (pRanges)
  {
    const tSourceUrl = gEditeur.get ("source_url");
    const tSourceDur = Number (gEditeur.get ("source_dur") || 0);
    const tRanges = ws_normalise_delete_ranges (pRanges, tSourceDur);
    if (!tRanges.length)
      {
        ws_efface_preview_suppressions ();
        return;
      }
    if (!tSourceUrl || !tSourceDur)
      throw new Error ("Source audio introuvable pour l'aperçu.");

    const tBuildId = ++gWS.previewBuildId;
    const tAnciennePreviewUrl = gEditeur.get ("preview_url");
    gEditeur.set ("preview_building", true);
    try
      {
        const tSourceBuffer = await ws_get_audio_buffer (tSourceUrl);
        if (!tSourceBuffer)
          throw new Error ("Impossible de décoder l'audio source.");
        if (gWS.previewBuildId !== tBuildId) return;
        if (gEditeur.get ("source_url") !== tSourceUrl) return;

        const tPreviewBuffer = ws_audio_buffer_sans_plages (tSourceBuffer, tRanges);
        if (!tPreviewBuffer)
          throw new Error ("La suppression laisserait un fichier vide.");
        const tPreviewUrl = URL.createObjectURL (rec_encode_wav (tPreviewBuffer));
        if (gWS.previewBuildId !== tBuildId || gEditeur.get ("source_url") !== tSourceUrl)
          {
            ws_revoke_blob_url (tPreviewUrl);
            return;
          }

        if (tAnciennePreviewUrl && tAnciennePreviewUrl !== tPreviewUrl)
          ws_revoke_blob_url (tAnciennePreviewUrl);
        gEditeur.set ("delete_ranges", tRanges);
        gEditeur.set ("preview_url", tPreviewUrl);
      }
    finally
      {
        if (gWS.previewBuildId === tBuildId)
          gEditeur.set ("preview_building", false);
      }
  }

function ws_gain_at_time (pTime, pStart, pEnd, pGainLin, pFadeIn, pFadeOut)
  {
    if (pTime < pStart || pTime > pEnd) return 0;
    let tMul = 1;
    if (pFadeIn > 0 && pTime < pStart + pFadeIn)
      tMul *= Math.max (0, Math.min (1, (pTime - pStart) / pFadeIn));
    if (pFadeOut > 0 && pTime > pEnd - pFadeOut)
      tMul *= Math.max (0, Math.min (1, (pEnd - pTime) / pFadeOut));
    return pGainLin * tMul;
  }

function ws_update_edit_preview ()
  {
    if (!gWS.instance) return;
    const tWrapper = gWS.ws_wrapper;
    if (!tWrapper) return;
    ws_get_audio_buffer (gWS.url)
      .then ((tBuf) =>
        {
          if (!tBuf || gWS.url !== gWS.audioBufferUrl || !gWS.ws_wrapper) return;
          let tOverlay = gWS.edit_overlay;
          if (!tOverlay)
            {
              tOverlay = document.createElement ("canvas");
              tOverlay.className = "js_inspecteur_ws_edit_overlay";
              tOverlay.style.position = "absolute";
              tOverlay.style.left = "0";
              tOverlay.style.top = "0";
              tOverlay.style.pointerEvents = "none";
              // Le wrapper WaveSurfer contient la waveform native, la
              // zone "progress" et le curseur. Si l'overlay reste au
              // même niveau que la couche progress, les deux rendus se
              // mélangent et la preview paraît plus faible que le rendu
              // final. On la place donc au-dessus des canvases natifs,
              // mais sous le curseur.
              tOverlay.style.zIndex = "4";
              if (getComputedStyle (tWrapper).position === "static")
                tWrapper.style.position = "relative";
              tWrapper.appendChild (tOverlay);
              gWS.edit_overlay = tOverlay;
            }
          const tWidthCss  = tWrapper.scrollWidth  || tWrapper.clientWidth || 0;
          const tHeightCss = tWrapper.clientHeight || 0;
          if (!tWidthCss || !tHeightCss) return;
          tOverlay.style.display = "block";
          tOverlay.style.width  = tWidthCss + "px";
          tOverlay.style.height = tHeightCss + "px";
          const tDpr = window.devicePixelRatio || 1;
          const tCw = Math.max (1, Math.round (tWidthCss * tDpr));
          const tCh = Math.max (1, Math.round (tHeightCss * tDpr));
          if (tOverlay.width !== tCw) tOverlay.width = tCw;
          if (tOverlay.height !== tCh) tOverlay.height = tCh;
          const tCtx = tOverlay.getContext ("2d");
          if (!tCtx) return;
          const tDark = scene_theme_est_sombre ();
          tCtx.clearRect (0, 0, tCw, tCh);
          tCtx.fillStyle = tDark ? "#1c1c1e" : "#f4f4f4";
          tCtx.fillRect (0, 0, tCw, tCh);
          const tHalf = tCh / 2;
          tCtx.strokeStyle = tDark ? "rgba(255,255,255,0.10)" : "rgba(0,0,0,0.08)";
          tCtx.lineWidth = 1;
          tCtx.beginPath ();
          tCtx.moveTo (0, tHalf);
          tCtx.lineTo (tCw, tHalf);
          tCtx.stroke ();

          const tDur  = gWS.instance.getDuration () || tBuf.duration || 0;
          const tSr   = tBuf.sampleRate;
          const tHasEdits = Math.abs (Number (gEditeur.get ("gain_db") || 0)) >= 0.05
            || Number (gEditeur.get ("fade_in")  || 0) > 0.001
            || Number (gEditeur.get ("fade_out") || 0) > 0.001;
          const tGainLin = tHasEdits
            ? db_to_lin (Number (gEditeur.get ("gain_db") || 0)) : 1;
          const tStart = tHasEdits ? Number (gEditeur.get ("start") || 0) : 0;
          const tEnd   = tHasEdits ? Number (gEditeur.get ("end")   || tDur) : tDur;
          const tFadeIn  = tHasEdits
            ? Math.max (0, Math.min (tEnd - tStart, Number (gEditeur.get ("fade_in") || 0))) : 0;
          const tFadeOut = tHasEdits
            ? Math.max (0, Math.min (tEnd - tStart, Number (gEditeur.get ("fade_out") || 0))) : 0;

          tCtx.fillStyle = tDark ? "#5ac8fa" : "#0c6ea7";
          for (let x = 0; x < tCw; x++)
            {
              const t0 = (x / tCw) * tDur;
              const t1 = ((x + 1) / tCw) * tDur;
              const i0 = Math.max (0, Math.floor (t0 * tSr));
              const i1 = Math.max (i0 + 1, Math.min (tBuf.length, Math.ceil (t1 * tSr)));
              let mn = 0, mx = 0;
              for (let i = i0; i < i1; i++)
                {
                  const t = i / tSr;
                  const g = ws_gain_at_time (t, tStart, tEnd, tGainLin, tFadeIn, tFadeOut);
                  const v = rec_visual_sample (tBuf, i, g);
                  if (v < mn) mn = v;
                  if (v > mx) mx = v;
                }
              if (mn < -1) mn = -1;
              if (mx > 1) mx = 1;
              const yT = tHalf - mx * tHalf;
              const yB = tHalf - mn * tHalf;
              tCtx.fillRect (x, yT, 1, Math.max (1, yB - yT));
            }
        })
      .catch (() =>
        {
          if (gWS.edit_overlay) gWS.edit_overlay.style.display = "none";
        });
  }

// Bascule lecture / pause sur l'instance wavesurfer courante. Partagé
// entre le bouton ▶ de l'inspecteur et le raccourci clavier "espace".
function ws_selection_bounds ()
  {
    if (!gWS.selection || !gWS.instance) return null;
    const tDur = gWS.instance.getDuration () || 0;
    const s = Math.max (0, Math.min (tDur, Number (gWS.selection.start || 0)));
    const e = Math.max (0, Math.min (tDur, Number (gWS.selection.end   || 0)));
    return (e - s > 0.02) ? {start : s, end : e} : null;
  }

function ws_force_pause ()
  {
    if (!gWS.instance) return;
    gWS.play_end = null;
    try { gWS.instance.pause (); } catch (e) {}
    try
      {
        const tMedia = gWS.instance.getMediaElement ();
        if (tMedia && typeof tMedia.pause === "function") tMedia.pause ();
      }
    catch (e) {}
    gEditeur.set ("playing", false);
  }

function ws_hard_stop (pTime = null)
  {
    if (!gWS.instance) return;
    const tDur = Number (gWS.instance.getDuration () || gEditeur.get ("dur") || 0);
    const tTarget = pTime === null || pTime === undefined
      ? Math.max (0, Math.min (tDur, Number (gEditeur.get ("cur") || 0)))
      : Math.max (0, Math.min (tDur, Number (pTime) || 0));
    ws_force_pause ();
    try { gWS.instance.setTime (tTarget); } catch (e) {}
    gEditeur.set ("cur", +tTarget.toFixed (2));
  }

async function ws_play_pause_toggle ()
  {
    if (!gWS.instance) return;
    let tMedia = null;
    try { tMedia = gWS.instance.getMediaElement (); } catch (e) {}
    const tPlaying = !!gEditeur.get ("playing")
      || (typeof gWS.instance.isPlaying === "function" && gWS.instance.isPlaying ())
      || (tMedia && tMedia.paused === false);
    if (tPlaying)
      {
        ws_force_pause ();
        return;
      }
    // Le backend WebAudio crée son AudioContext en "suspended" : on
    // attend son passage à "running" avant de jouer, sinon le buffer
    // démarre dans le vide (Safari notamment).
    await audio_resume_context (gWS.audioCtx);
    const tDur = gWS.instance.getDuration () || 0;
    const tSel = ws_selection_bounds ();
    let tStart, tEnd = null;
    if (tSel)
      {
        tStart = tSel.start;
        tEnd   = tSel.end;
      }
    else
      {
        tStart = typeof gWS.instance.getCurrentTime === "function"
          ? Number (gWS.instance.getCurrentTime () || 0) : 0;
        if (!isFinite (tStart) || tStart >= Math.max (0, tDur - 0.02)) tStart = 0;
      }
    gWS.play_end = tEnd;
    const p = tEnd === null ? gWS.instance.play (tStart)
                            : gWS.instance.play (tStart, tEnd);
    if (p && typeof p.catch === "function") p.catch (() => {});
  }

// Calcule l'enveloppe (gain + fades, bornée à la région de trim) et
// rafraîchit l'affichage de la forme d'onde (mise à l'échelle verticale
// proportionnelle au gain).
function applique_edits ()
  {
    if (!gWS.instance) return;
    const tDur   = Number (gEditeur.get ("dur")      || 0);
    const tS     = Number (gEditeur.get ("start")    || 0);
    const tE     = Number (gEditeur.get ("end")      || tDur);
    const tGdb   = Number (gEditeur.get ("gain_db")  || 0);
    const tFin   = Math.max (0, Math.min (tE - tS, Number (gEditeur.get ("fade_in")  || 0)));
    const tFout  = Math.max (0, Math.min (tE - tS, Number (gEditeur.get ("fade_out") || 0)));
    const tGl    = db_to_lin (tGdb);
    try { gWS.instance.setOptions (ws_waveform_options ()); }
    catch (e) {}

    // 1bis) re-applique le volume du plugin envelope : son setVolume
    // a été monkey-patché pour multiplier par db_to_lin (gain_db), ce
    // qui rend audibles les boosts > 0 dB sans rerouter la chaîne
    // WebAudio (HTMLAudioElement.volume est plafonné à 1 par le
    // navigateur, mais avec le backend WebAudio le gainNode interne
    // accepte des valeurs > 1).
    if (gWS.envelope)
      { try { gWS.envelope.setVolume (gWS.envelope.getCurrentVolume ()); }
        catch (e) {} }
    else
      ws_applique_volume_preview (1);

    // 2) enveloppe (lecture + visuel des fades)
    if (gWS.envelope)
      {
        // Le plugin envelope insère ses points dans un polyline SVG en
        // cherchant le premier point existant d'abscisse ≥ à celle du
        // nouveau. Deux points partageant exactement le même temps
        // peuvent donc se retrouver dans un ordre inattendu et le
        // « saut vertical » qu'on attend au bord d'une région de trim
        // (sans fade) se rend alors comme une diagonale parasite. On
        // décale d'un epsilon les points extérieurs au trim quand
        // aucun fade ne fait office de coude.
        const tEps = 1e-3;
        const tPts = [];
        // Avant la région de trim : volume 0
        if (tS > 0)
          {
            tPts.push ({time: 0, volume: 0});
            if (tFin <= 0)
              tPts.push ({time: Math.max (0, tS - tEps), volume: 0});
          }
        // Volumes de l'enveloppe : on reste dans [0, 1] (le plugin
        // clampe au-dessus). Le gain est appliqué séparément par le
        // GainNode WebAudio, ce qui permet d'entendre les boosts > 0 dB.
        // Entrée de trim : monte à 1 en tFin secondes (saut net si tFin = 0)
        tPts.push ({time: tS, volume: tFin > 0 ? 0 : 1});
        if (tFin > 0)
          tPts.push ({time: Math.min (tE, tS + tFin), volume: 1});
        // Sortie de trim : descend à 0 en tFout secondes (saut net si tFout = 0)
        if (tFout > 0)
          tPts.push ({time: Math.max (tS, tE - tFout), volume: 1});
        tPts.push ({time: tE, volume: tFout > 0 ? 0 : 1});
        // Après la région de trim : volume 0
        if (tE < tDur)
          {
            if (tFout <= 0)
              tPts.push ({time: Math.min (tDur, tE + tEps), volume: 0});
            tPts.push ({time: tDur, volume: 0});
          }
        tPts.sort ((a, b) => a.time - b.time);
        // Dédoublonne uniquement les points strictement identiques (même
        // temps ET même volume). Conserve les paires à temps égal mais
        // volumes différents : sauts verticaux nécessaires aux bords de
        // la région de trim sans fade.
        const tClean = [];
        for (const p of tPts)
          {
            const tPrev = tClean[tClean.length - 1];
            if (tPrev
                && Math.abs (tPrev.time   - p.time)   < 1e-4
                && Math.abs (tPrev.volume - p.volume) < 1e-4)
              continue;
            tClean.push (p);
          }
        // Le plugin envelope interpole le volume entre deux points
        // consécutifs et divise par (next.time - prev.time). Quand la
        // lecture atteint tDur, le « next » synthétique du plugin vaut
        // {duration, 0} : si notre dernier point est aussi à tDur, le
        // dénominateur est nul ⇒ setVolume (Infinity) en fin de lecture.
        // On décale donc le tout dernier point juste avant tDur.
        if (tClean.length && tDur > 0)
          {
            const tLast = tClean[tClean.length - 1];
            if (tLast.time >= tDur - 1e-4)
              tLast.time = Math.max (0, tDur - 1e-3);
          }
        try { gWS.envelope.setPoints (tClean); } catch (e) {}
        try { gWS.envelope.setVolume (gWS.envelope.getCurrentVolume ()); }
        catch (e) {}
        // Visuel : on n'affiche la polyline d'enveloppe que si au moins
        // un fade est actif. Les points internes restent en place pour
        // que le contrôle de volume du plugin continue de fonctionner.
        const tPoly = gWS.envelope.polyline;
        const tSvg  = tPoly && tPoly.svg;
        if (tSvg)
          {
            const tLine = tSvg.querySelector ("polyline");
            if (tLine)
              tLine.style.opacity = (tFin > 0 || tFout > 0) ? "" : "0";
            // Désactive l'ajout de points par double-clic / appui long
            // sur la SVG : ces interactions natives du plugin créent des
            // points hors-sync avec les sliders et seraient effacées au
            // prochain applique_edits ().
            tSvg.style.pointerEvents = "none";
          }
      }
    ws_update_edit_preview ();
  }

async function ws_init (pTpl, pUrl, pOpt)
  {
    const tOpt = pOpt || {};
    ws_destroy ();
    const tConteneur = tOpt.container || pTpl.find (".js_inspecteur_ws");
    if (!tConteneur) return;
    await charge_wavesurfer_edition ();

    const tDark = scene_theme_est_sombre ();

    const tRegions  = RegionsPlugin.create ();
    const tEnvelope = EnvelopePlugin.create (
      {
        volume          : 1,
        lineColor       : tDark ? "rgba(255, 159, 10, 0.95)" : "rgba(255, 87, 34, 0.95)",
        lineWidth       : "2",
        // dragPointSize: 0 ne fonctionne PAS — le plugin fait
        // `dragPointSize || defaut(10)` à la construction, ce qui
        // remet la taille à 10. On passe une valeur non-falsy mais
        // négligeable, et on rend les pastilles transparentes pour
        // qu'elles n'apparaissent jamais (la manipulation se fait
        // exclusivement par les sliders).
        dragPointSize   : 0.001,
        dragPointFill   : "transparent",
        dragPointStroke : "transparent",
        dragLine        : false,
      });

    const tWsOptions =
      {
        container       : tConteneur,
        url             : pUrl,
        height          : 120,
        waveColor       : tDark ? "#0a84ff" : "#1185c8",
        progressColor   : tDark ? "#5ac8fa" : "#0c6ea7",
        cursorColor     : tDark ? "#ff453a" : "#c0392b",
        cursorWidth     : 1,
        ...ws_waveform_options (),
        minPxPerSec     : 100,
        fillParent      : true,
        autoScroll      : true,
        autoCenter      : true,
        plugins         : [tRegions, tEnvelope],
      };
    if (!audio_prefere_backend_media_element ())
      {
        // Desktop : on garde WebAudio pour bénéficier du GainNode
        // interne et d'une préécoute fidèle des boosts > 0 dB.
        tWsOptions.backend = "WebAudio";
      }
    const tWS = WaveSurfer.create (tWsOptions);

    gWS.instance  = tWS;
    gWS.regions   = tRegions;
    gWS.envelope  = tEnvelope;
    gWS.url       = pUrl;
    gWS.container = tConteneur;

    // Le plugin envelope écrit les fades dans [0, 1] via wavesurfer.setVolume,
    // qui pose gainNode.gain.value côté backend WebAudio (sans plafond
    // navigateur, contrairement à HTMLAudioElement.volume). On monkey-patche
    // setVolume pour multiplier par le gain utilisateur (db_to_lin (gain_db))
    // — un seul GainNode interne porte donc fades + boost, ce qui rend
    // audibles les boosts > 0 dB et reproduit le rendu destructif serveur
    // sans rerouter la chaîne audio (toute coupure de la chaîne ws → dest
    // produirait un silence total).
    tEnvelope.setVolume = function (v)
      {
        this.volume = v;
        ws_applique_volume_preview (v);
      };
    // Capture l'AudioContext pour pouvoir le resume () au play (le
    // backend WebAudio le crée en "suspended" tant qu'aucun geste
    // utilisateur n'a eu lieu).
    try
      {
        const tMedia = tWS.getMediaElement ();
        if (tMedia && typeof tMedia.getGainNode === "function")
          gWS.audioCtx = tMedia.getGainNode ().context;
      }
    catch (e) {}

    tWS.once ("ready", () =>
      {
        const tDur = tWS.getDuration () || 0;
        gEditeur.set ("dur", tDur);
        gEditeur.set ("url", pUrl);
        gEditeur.set ("source_url", tOpt.source_url || null);
        gEditeur.set ("source_file_id", tOpt.source_file_id || null);
        if (tOpt.source_dur !== null && tOpt.source_dur !== undefined)
          gEditeur.set ("source_dur", tOpt.source_dur);
        else if (!gEditeur.get ("source_dur") || (tOpt.source_url && tOpt.source_url === pUrl))
          gEditeur.set ("source_dur", tDur);
        if (tOpt.resetEdits)
          {
            gEditeur.set ("start",    0);
            gEditeur.set ("end",      tDur);
            gEditeur.set ("gain_db",  0);
            gEditeur.set ("fade_in",  0);
            gEditeur.set ("fade_out", 0);
            gEditeur.set ("dirty",    false);
            gEditeur.set ("peak",     null);
          }
        gEditeur.set ("start", 0);
        gEditeur.set ("end",   tDur);
        gEditeur.set ("sel_start", null);
        gEditeur.set ("sel_end",   null);

        tRegions.enableDragSelection ({color: "rgba(255, 87, 34, 0.18)"});
        tRegions.on ("region-created", (r) =>
          {
            if (gWS.selection && gWS.selection !== r)
              { try { gWS.selection.remove (); } catch (e) {} }
            gWS.selection = r;
            r.setOptions ({drag: true, resize: true, color: "rgba(255, 87, 34, 0.22)"});
            gEditeur.set ("sel_start", +r.start.toFixed (3));
            gEditeur.set ("sel_end",   +r.end.toFixed (3));
          });
        tRegions.on ("region-updated", (r) =>
          {
            if (r !== gWS.selection) return;
            gEditeur.set ("sel_start", +r.start.toFixed (3));
            gEditeur.set ("sel_end",   +r.end.toFixed (3));
          });
        tRegions.on ("region-removed", (r) =>
          {
            if (r !== gWS.selection) return;
            gWS.selection = null;
            gEditeur.set ("sel_start", null);
            gEditeur.set ("sel_end",   null);
          });

        // Désélection au clic dans la forme d'onde, hors région.
        tWS.on ("interaction", (pTime) =>
          {
            if (!gWS.selection) return;
            const tT = Number (pTime) || 0;
            const tTol = 0.005;
            if (tT < gWS.selection.start - tTol
                || tT > gWS.selection.end + tTol)
              { ws_deselect_selection (); }
          });

        applique_edits ();
        calcule_peak (pUrl)
          .then ((tPeak) =>
            {
              if (gWS.url !== pUrl) return;
              gEditeur.set ("peak", tPeak);
              applique_edits ();
            })
          .catch (() => {});

        try { gWS.ws_wrapper = tWS.getWrapper (); } catch (e) {}
        ws_update_edit_preview ();

        // Lecture automatique si activée, ou forcée après une génération TTS.
        if (tOpt.autoplay && (inspecteur_autoplay_autorise () || tOpt.forceAutoplay))
          {
            audio_resume_context (gWS.audioCtx);
            const p = tWS.play (0);
            if (p && typeof p.catch === "function") p.catch (() => {});
          }
      });

    tWS.on ("timeupdate", (t) =>
      {
        const tDur = Number (tWS.getDuration () || gEditeur.get ("dur") || 0);
        const tCur = Number (t || 0);
        if (tDur > 0 && tCur >= tDur + 0.05)
          {
            ws_hard_stop (tDur);
            return;
          }
        gEditeur.set ("cur", +Math.max (0, Math.min (tDur || tCur, tCur)).toFixed (2));
        if (gWS.play_end !== null && t >= gWS.play_end - 0.005)
          {
            const tEnd = gWS.play_end;
            ws_hard_stop (tEnd);
          }
      });
    tWS.on ("play",  () => gEditeur.set ("playing", true));
    tWS.on ("pause", () => { gWS.play_end = null; gEditeur.set ("playing", false); });
    tWS.on ("finish", () => ws_hard_stop (tWS.getDuration () || gEditeur.get ("dur") || 0));
    tWS.on ("zoom",   () => ws_update_edit_preview ());
    tWS.on ("scroll", () => ws_update_edit_preview ());
    try
      {
        const tMedia = tWS.getMediaElement ();
        if (tMedia && typeof tMedia.addEventListener === "function")
          {
            const tOnEnded = () => ws_hard_stop (tWS.getDuration () || gEditeur.get ("dur") || 0);
            const tOnPause = () =>
              {
                gWS.play_end = null;
                gEditeur.set ("playing", false);
                const tDur = Number (tWS.getDuration () || gEditeur.get ("dur") || 0);
                const tCur = Number (typeof tWS.getCurrentTime === "function"
                  ? tWS.getCurrentTime () : gEditeur.get ("cur") || 0);
                gEditeur.set ("cur", +Math.max (0, Math.min (tDur || tCur, tCur)).toFixed (2));
              };
            const tOnEmptied = () => ws_hard_stop (0);
            tMedia.addEventListener ("ended", tOnEnded);
            tMedia.addEventListener ("pause", tOnPause);
            tMedia.addEventListener ("emptied", tOnEmptied);
            gWS.mediaSyncCleanup = () =>
              {
                try { tMedia.removeEventListener ("ended", tOnEnded); } catch (e) {}
                try { tMedia.removeEventListener ("pause", tOnPause); } catch (e) {}
                try { tMedia.removeEventListener ("emptied", tOnEmptied); } catch (e) {}
              };
          }
      }
    catch (e) {}
  }

// Calcule le pic crête (linéaire) des échantillons décodés.
async function calcule_peak (pUrl)
  {
    if (gWS.peakCache.has (pUrl)) return gWS.peakCache.get (pUrl);
    const tCtx = get_audio_ctx ();
    if (!tCtx) return 1;
    const tResp = await fetch (pUrl);
    const tBuf  = await tResp.arrayBuffer ();
    const tAudio = await tCtx.decodeAudioData (tBuf.slice (0));
    let tPeak = 0;
    for (let c = 0; c < tAudio.numberOfChannels; c++)
      {
        const tData = tAudio.getChannelData (c);
        for (let i = 0; i < tData.length; i++)
          {
            const v = Math.abs (tData[i]);
            if (v > tPeak) tPeak = v;
          }
      }
    gWS.peakCache.set (pUrl, tPeak);
    return tPeak;
  }

function ws_deselect_selection ()
  {
    if (gWS.selection)
      { try { gWS.selection.remove (); } catch (e) {} }
    gWS.selection = null;
    gEditeur.set ("sel_start", null);
    gEditeur.set ("sel_end",   null);
  }

function ws_delete_selection ()
  {
    const tCible = cible_audio_editeur_courante ();
    if (!tCible.fileId || !gWS.selection || gEditeur.get ("preview_building")) return;

    const tSel = ws_selection_bounds ();
    if (!tSel) return;
    const tSourceDur = Number (gEditeur.get ("source_dur") || gEditeur.get ("dur") || 0);
    const tRangesCourantes = ws_delete_ranges_courantes ();
    const tSourceStart = ws_map_preview_time_to_source (tSel.start, tRangesCourantes, tSourceDur);
    const tSourceEnd   = ws_map_preview_time_to_source (tSel.end,   tRangesCourantes, tSourceDur);
    if (tSourceEnd - tSourceStart <= 0.02) return;
    const tNouvellesRanges = ws_normalise_delete_ranges (
      [...tRangesCourantes, {start : tSourceStart, end : tSourceEnd}],
      tSourceDur
    );
    const tDureePreview = Math.max (0, tSourceDur - ws_total_deleted_duration (tNouvellesRanges));
    if (tDureePreview < 0.05)
      { alert ("La suppression laisserait un fichier trop court."); return; }

    ws_force_pause ();
    ws_deselect_selection ();
    ws_regenere_preview_suppressions (tNouvellesRanges)
      .then (() => { gEditeur.set ("dirty", true); })
      .catch ((err) => { alert ("Erreur : " + (err.reason || err.message || err)); });
  }

// Auto-rendu : à chaque changement de fichier audio sélectionné, (ré)initialise
// l'instance Wavesurfer et son état d'édition.
Template.scene.onRendered (function ()
  {
    if (!edition_hub_active ()) return;
    const tpl = this;
    tpl.autorun (() =>
      {
        const tCible = cible_audio_editeur_courante ();
        const tSelectionKey = tCible.selectionKey;
        const tFileId = tCible.fileId || null;
        const tSourceUrl = tCible.url || null;
        const tF = tCible.fichier || null;
        if (tCible.type === "calque")
          note_transition_autoplay_inspecteur (tSelectionKey, tFileId);
        else
          aligne_autoplay_maj_fichier_sur_selection_inspecteur (null);

        const tSourceAChange = tSelectionKey !== gInspecteurAudioSource.selectionKey
          || (tFileId || null) !== gInspecteurAudioSource.fileId;
        if (tSourceAChange)
          {
            gInspecteurAudioSource.selectionKey = tSelectionKey;
            gInspecteurAudioSource.fileId = tFileId || null;
            ws_efface_preview_suppressions ();
            gEditeur.set ("source_file_id", tFileId || null);
            gEditeur.set ("source_url", tSourceUrl || null);
            gEditeur.set ("source_dur", null);
          }

        const tPreviewUrl = (!tSourceAChange && gEditeur.get ("preview_url")) || null;
        const tUrl = tPreviewUrl || tSourceUrl;
        const tAutoplay = tCible.type === "calque"
          ? autoplay_transition_inspecteur_autorisee (tSelectionKey, tFileId, tF)
          : false;
        const tForceAutoplay = tCible.type === "calque"
          ? autoplay_transition_inspecteur_forcee (tSelectionKey, tFileId)
          : false;
        Tracker.afterFlush (() =>
          {
            const tSelecteur = tCible.type === "projet"
              ? '.js_inspecteur_projet_audio_bloc[data-key="' + tCible.cle + '"] .js_inspecteur_ws'
              : (tCible.type === "scene"
                ? '.js_inspecteur_scene_audio_bloc[data-key="' + tCible.cle + '"] .js_inspecteur_ws'
                : ".js_inspecteur_ws");
            const tConteneur = tpl.find (tSelecteur);
            if (!tUrl || !tConteneur)
              {
                if (gWS.instance) ws_destroy ();
                return;
              }
            // Réinit si URL ou conteneur a changé, ou si pas d'instance.
            if (!gWS.instance || gWS.url !== tUrl || gWS.container !== tConteneur)
              {
                ws_init (tpl, tUrl,
                  {
	                    autoplay       : tAutoplay,
	                    forceAutoplay  : tForceAutoplay,
	                    resetEdits     : tSourceAChange,
                    source_url     : tSourceUrl,
                    source_file_id : tFileId || null,
                    source_dur     : Number (gEditeur.get ("source_dur") || 0) || null,
                    container      : tConteneur,
                  })
                  .catch ((e) => console.warn ("[scene] initialisation WaveSurfer impossible:", e));
                if (tCible.type !== "projet")
                  consomme_transition_autoplay_inspecteur (tSelectionKey, tFileId);
              }
          });
      });

	    // Réinitialise la saisie manuelle de seed quand on change de calque.
	    tpl.autorun (() =>
	      {
	        const tId = gCalqueSelectionne.get ();
	        if (!tId) return;
        if (gInspecteurSeedCustom.get (tId) === undefined)
          {
            const tSceneId = Session.get ("scene_id");
            const tScene   = Scenes.findOne (tSceneId);
            const tConfig  = get_objet_config (tScene, tId);
            const tS = tConfig && tConfig.seed;
            gInspecteurSeedCustom.set (tId,
              (tS === null || tS === undefined) ? "" : String (tS));
	          }
	      });

    tpl.autorun (() =>
      {
        const tSceneId = Session.get ("scene_id");
        const tScene = tSceneId ? Scenes.findOne (tSceneId) : null;
        if (!tScene) return;
        AUDIO_SCENE_ITEMS.forEach ((pItem) =>
          {
            const tAttente = gInspecteurSceneAudioLectureApresGeneration.get (pItem.cle);
            if (!tAttente || tAttente.scene_id !== tScene._id) return;
            const tEntree = entree_audio_scene (tScene, pItem.cle) || {};
            const tFileId = tEntree.file_id || null;
            if (!tFileId || tFileId === tAttente.file_id) return;
            gInspecteurSceneAudioLectureApresGeneration.set (pItem.cle, null);
            gInspecteurSceneAudioSeedCustom.set (pItem.cle, null);
            Tracker.afterFlush (() => { joue_audio_scene_inspecteur (pItem.cle, {force : true}); });
          });
      });

    tpl.autorun (() =>
      {
        const tProjet = projet_courant_scene ();
        if (!tProjet) return;
        AUDIO_PROJET_ITEMS.forEach ((pItem) =>
          {
            const tAttente = gInspecteurProjetAudioLectureApresGeneration.get (pItem.cle);
            if (!tAttente || tAttente.projet_id !== tProjet._id) return;
            const tEntree = entree_audio_projet (tProjet, pItem.cle) || {};
            const tFileId = tEntree.file_id || null;
            if (!tFileId || tFileId === tAttente.file_id) return;
            gInspecteurProjetAudioLectureApresGeneration.set (pItem.cle, null);
            gInspecteurProjetAudioSeedCustom.set (pItem.cle, null);
            Tracker.afterFlush (() => { joue_audio_projet_inspecteur (pItem.cle, {force : true}); });
          });
      });

	    tpl.autorun (() =>
	      {
	        gCalqueSelectionne.get ();
        config_objet_selectionne_scene ();
        gInspecteurSurvolMessage.set (null);
        rafraichit_apercu_contour_objet_selectionne_scene ();
      });

    tpl._autoSelectionCalque = {sceneId : null, pending : false};
    tpl.autorun (() =>
      {
        const tSceneId = Session.get ("scene_id");
        if (tpl._autoSelectionCalque.sceneId !== tSceneId)
          {
            tpl._autoSelectionCalque.sceneId = tSceneId || null;
            tpl._autoSelectionCalque.pending = !!tSceneId;
            if (gCalqueSelectionne.get () || gCategorieSelectionnee.get ())
              selectionne_calque (null, {autoplay : false});
          }

        if (!tSceneId) return;
        const tScene = Scenes.findOne (tSceneId);
        if (!tScene || !tScene.svg_objets) return;

        const tCalques = ids_calques_selectionnables_scene (tScene);
        const tCartes = Cartes.find ({scene_id : tSceneId}, {sort : {ordre : 1}}).fetch ();
        const tCarteIds = tCartes.map ((pCarte) => pCarte && pCarte._id).filter (Boolean);
        const tCourant = gCalqueSelectionne.get ();
        const tCouranteBulle = gBulleSelectionnee.get ();
        const tCourantValide = !!tCourant && tCalques.includes (tCourant);
        const tCouranteBulleValide = !!tCouranteBulle && tCarteIds.includes (tCouranteBulle);
        if (tCourantValide || tCouranteBulleValide)
          {
            tpl._autoSelectionCalque.pending = false;
            return;
          }

        if (!tCalques.length && !tCarteIds.length)
          {
            tpl._autoSelectionCalque.pending = false;
            if (tCourant) selectionne_calque (null, {autoplay : false});
            if (tCouranteBulle) reinitialise_selection_bulle_calque ();
            return;
          }

        const tDoitAutoSelectionner = tpl._autoSelectionCalque.pending
          || ((!!tCourant && !tCourantValide) || (!!tCouranteBulle && !tCouranteBulleValide))
            && !gCategorieSelectionnee.get ();
        if (!tDoitAutoSelectionner) return;

        const tMemorise = decode_derniere_selection_calques_scene (tSceneId);
        const tCible = (tMemorise && tMemorise.type === "bulle" && tCarteIds.includes (tMemorise.id))
          ? tMemorise
          : ((tMemorise && tMemorise.type === "calque" && tCalques.includes (tMemorise.id))
            ? tMemorise
            : (tCalques[0] ? {type : "calque", id : tCalques[0]} : (tCarteIds[0] ? {type : "bulle", id : tCarteIds[0]} : null)));
        tpl._autoSelectionCalque.pending = false;
        if (!tCible) return;

        Tracker.afterFlush (() =>
          {
            if (Session.get ("scene_id") !== tSceneId) return;
            if (tCible.type === "bulle")
              {
                if (gBulleSelectionnee.get () === tCible.id) return;
                selectionne_bulle_calque (tCible.id, {force : true});
                return;
              }
            if (gCalqueSelectionne.get () === tCible.id) return;
            selectionne_calque (tCible.id, {autoplay : false});
          });
      });

    // Synchronise les inputs de l'éditeur audio avec l'état réactif.
    tpl.autorun (() =>
      {
        const tSelStart = gEditeur.get ("sel_start");
        const tSelEnd   = gEditeur.get ("sel_end");
        const tDeleteRanges = ws_delete_ranges_courantes ();
        const tGain    = gEditeur.get ("gain_db");
        const tFin     = gEditeur.get ("fade_in");
        const tFout    = gEditeur.get ("fade_out");
        const tCur     = gEditeur.get ("cur");
        const tDur     = gEditeur.get ("dur");
        const tPlay    = gEditeur.get ("playing");
        Tracker.afterFlush (() =>
          {
            const tSL = tpl.find (".js_inspecteur_ws_selection_label");
            const tIG = tpl.find (".js_inspecteur_ws_gain");
            const tGV = tpl.find (".js_inspecteur_ws_gain_val");
            const tIFi = tpl.find (".js_inspecteur_ws_fadein");
            const tFiV = tpl.find (".js_inspecteur_ws_fadein_val");
            const tIFo = tpl.find (".js_inspecteur_ws_fadeout");
            const tFoV = tpl.find (".js_inspecteur_ws_fadeout_val");
            const tCV  = tpl.find (".js_inspecteur_ws_cur");
            const tDV  = tpl.find (".js_inspecteur_ws_dur");
            const tPL  = tpl.find (".js_inspecteur_ws_play_label");
            const tDeleteLabel = tDeleteRanges.length
              ? " - " + tDeleteRanges.length + " suppression"
                + (tDeleteRanges.length > 1 ? "s" : "") + " en attente"
              : "";
            if (tSL)
              tSL.textContent = (tSelStart === null || tSelEnd === null
                                  || tSelStart === undefined || tSelEnd === undefined)
                ? ("Aucune sélection" + tDeleteLabel)
                : rec_format_seconds (tSelStart) + " → " + rec_format_seconds (tSelEnd)
                  + "  (" + (tSelEnd - tSelStart).toFixed (2) + " s)"
                  + tDeleteLabel;
            if (tIG && document.activeElement !== tIG)
              tIG.value = String (tGain || 0);
            if (tGV) tGV.textContent = (Number (tGain || 0)).toFixed (1) + " dB";
            if (tIFi && document.activeElement !== tIFi)
              tIFi.value = String (tFin || 0);
            if (tFiV) tFiV.textContent = (Number (tFin || 0)).toFixed (2) + " s";
            if (tIFo && document.activeElement !== tIFo)
              tIFo.value = String (tFout || 0);
            if (tFoV) tFoV.textContent = (Number (tFout || 0)).toFixed (2) + " s";
            if (tCV) tCV.textContent = (Number (tCur || 0)).toFixed (2);
            if (tDV) tDV.textContent = (Number (tDur || 0)).toFixed (2);
            if (tPL) tPL.textContent = tPlay ? "❚❚" : "▶";
          });
      });
  });

function champ_actif_document ()
  {
    if (typeof document === "undefined") return null;
    const tChamp = document.activeElement;
    return tChamp && tChamp.classList ? tChamp : null;
  }

const PAIRES_NUMERIQUES_REGLAGES_BULLES_PROJET = Object.freeze ({
  taille_bulle_min : {autre : "taille_bulle_max", role : "min"},
  taille_bulle_max : {autre : "taille_bulle_min", role : "max"},
  rotation_bulle_min : {autre : "rotation_bulle_max", role : "min"},
  rotation_bulle_max : {autre : "rotation_bulle_min", role : "max"},
  lecture_flottement_taille_min_pct : {autre : "lecture_flottement_taille_max_pct", role : "min"},
  lecture_flottement_taille_max_pct : {autre : "lecture_flottement_taille_min_pct", role : "max"},
  lecture_flottement_rotation_min_pct : {autre : "lecture_flottement_rotation_max_pct", role : "min"},
  lecture_flottement_rotation_max_pct : {autre : "lecture_flottement_rotation_min_pct", role : "max"},
});

function valeur_numerique_champ_ou_defaut (pChamp, pDefaut)
  {
    const tTexte = pChamp && typeof pChamp.value === "string"
      ? pChamp.value.trim ()
      : "";
    if (tTexte === "") return pDefaut;
    const tNombre = Number (tTexte);
    return Number.isFinite (tNombre) ? tNombre : pDefaut;
  }

function restaure_champ_numerique (pChamp, pValeur)
  {
    if (!pChamp || pValeur === undefined || pValeur === null) return;
    pChamp.value = String (pValeur);
  }

function patch_reglages_bulles_projet_nombre (pCle, pValeur, pCourant)
  {
    const tPatch = {[pCle] : pValeur};
    const tPaire = PAIRES_NUMERIQUES_REGLAGES_BULLES_PROJET[pCle];
    if (!tPaire || !pCourant) return tPatch;

    const tAutreValeur = Number (pCourant[tPaire.autre]);
    if (!Number.isFinite (tAutreValeur)) return tPatch;
    if (tPaire.role === "min" && pValeur > tAutreValeur)
      tPatch[tPaire.autre] = pValeur;
    else if (tPaire.role === "max" && pValeur < tAutreValeur)
      tPatch[tPaire.autre] = pValeur;
    return tPatch;
  }

function applique_inspecteur_projet_bulles_number_depuis_champ (pChamp)
  {
    if (!pChamp || !pChamp.classList || !pChamp.classList.contains ("js_inspecteur_projet_bulles_number")) return;
    const tCle = pChamp.getAttribute ("data-cle");
    if (!tCle || !Object.prototype.hasOwnProperty.call (REGLAGES_BULLES_PROJET_DEFAUTS, tCle)) return;
    const tCourant = brouillon_reglages_bulles_projet ();
    const tValeur = valeur_numerique_champ_ou_defaut (pChamp, REGLAGES_BULLES_PROJET_DEFAUTS[tCle]);
    modifie_brouillon_reglages_bulles_projet (
      patch_reglages_bulles_projet_nombre (tCle, tValeur, tCourant)
    );
  }

function restaure_inspecteur_projet_bulles_number (pChamp)
  {
    if (!pChamp) return;
    const tCle = pChamp.getAttribute ("data-cle");
    if (!tCle) return;
    const tReglages = brouillon_reglages_bulles_projet ();
    if (!Object.prototype.hasOwnProperty.call (tReglages, tCle)) return;
    restaure_champ_numerique (pChamp, tReglages[tCle]);
  }

function applique_inspecteur_projet_mode_lecture_number_depuis_champ (pChamp)
  {
    if (!pChamp || !pChamp.classList || !pChamp.classList.contains ("js_inspecteur_projet_mode_lecture_number")) return;
    const tCle = pChamp.getAttribute ("data-cle");
    if (!tCle || !Object.prototype.hasOwnProperty.call (REGLAGES_MODE_LECTURE_PROJET_DEFAUTS, tCle)) return;
    const tValeur = valeur_numerique_champ_ou_defaut (pChamp, REGLAGES_MODE_LECTURE_PROJET_DEFAUTS[tCle]);
    const tPatch = {[tCle] : tCle === "survol_contour_opacite" ? (tValeur / 100) : tValeur};
    if (REGLAGES_INTERFACE_CARTE_CLES_SET.has (tCle)
        && !REGLAGES_INTERFACE_CARTE_ANIMATION_CLES_SET.has (tCle))
      modifie_brouillon_reglages_interface_carte_projet (tPatch);
    else
      modifie_brouillon_reglages_mode_lecture_projet (tPatch);
  }

function restaure_inspecteur_projet_mode_lecture_number (pChamp)
  {
    if (!pChamp) return;
    const tCle = pChamp.getAttribute ("data-cle");
    if (!tCle) return;
    const tReglages = REGLAGES_INTERFACE_CARTE_CLES_SET.has (tCle)
      && !REGLAGES_INTERFACE_CARTE_ANIMATION_CLES_SET.has (tCle)
      ? brouillon_reglages_interface_carte_projet ()
      : brouillon_reglages_mode_lecture_projet ();
    if (!Object.prototype.hasOwnProperty.call (tReglages, tCle)) return;
    restaure_champ_numerique (
      pChamp,
      tCle === "survol_contour_opacite"
        ? Math.round ((tReglages[tCle] || 0) * 100)
        : tReglages[tCle]
    );
  }

function applique_inspecteur_projet_audio_import_debit_depuis_champ (pChamp)
  {
    if (!pChamp || !pChamp.classList || !pChamp.classList.contains ("js_inspecteur_projet_audio_import_debit_max_kbps")) return;
    modifie_brouillon_reglages_import_audio_projet (
      {
        debit_max_kbps : valeur_numerique_champ_ou_defaut (
          pChamp,
          REGLAGES_IMPORT_AUDIO_PROJET_DEFAUTS.debit_max_kbps
        ),
      }
    );
  }

function restaure_inspecteur_projet_audio_import_debit (pChamp)
  {
    restaure_champ_numerique (pChamp, brouillon_reglages_import_audio_projet ().debit_max_kbps);
  }

function applique_brouillon_carte_depuis_champ (pChamp, pTpl)
  {
    if (!pChamp || !pChamp.classList) return;
    const tBrouillon = brouillon_carte_courant ();
    if (!tBrouillon) return;

    if (pChamp.classList.contains ("js_carte_nom"))
      {
        gCarteBrouillon.set ({...tBrouillon, nom : pChamp.value || ""});
        return;
      }

    if (pChamp.classList.contains ("js_carte_couleur_fond"))
      {
        gCarteBrouillon.set (
          {
            ...tBrouillon,
            couleur_fond : normalise_couleur_carte_client (
              pChamp.value,
              tBrouillon.couleur_fond
            ),
          }
        );
        return;
      }

    if (pChamp.classList.contains ("js_carte_couleur_bulle"))
      {
        gCarteBrouillon.set (
          {
            ...tBrouillon,
            couleur_bulle : couleur_bulle_carte_effective_client (
              {couleur_bulle : pChamp.value, carte_id : tBrouillon.carte_id},
              tBrouillon.carte_id
            ),
          }
        );
        return;
      }

    if (pChamp.classList.contains ("js_carte_taille_bulle"))
      {
        const tVal = Number (pChamp.value);
        if (!Number.isFinite (tVal)) return;
        gCarteBrouillon.set (
          {
            ...tBrouillon,
            taille_bulle : normalise_taille_bulle_carte_client (
              tVal,
              tBrouillon.taille_bulle
            ),
          }
        );
        return;
      }

    if (pChamp.classList.contains ("js_carte_opacite_bulle_range")
        || pChamp.classList.contains ("js_carte_opacite_bulle_number"))
      {
        const tVal = Number (pChamp.value);
        if (!Number.isFinite (tVal)) return;
        gCarteBrouillon.set (
          {
            ...tBrouillon,
            opacite_bulle : normalise_opacite_bulle_carte_client (
              tVal,
              tBrouillon.opacite_bulle
            ),
          }
        );
        return;
      }

    if (pChamp.classList.contains ("js_carte_x")
        || pChamp.classList.contains ("js_carte_y")
        || pChamp.classList.contains ("js_carte_zoom"))
      {
        const tValeur = parseFloat (pChamp.value);
        if (!Number.isFinite (tValeur)) return;
        let tPatch = null;
        if (pChamp.classList.contains ("js_carte_x"))
          tPatch = {x : tValeur};
        else if (pChamp.classList.contains ("js_carte_y"))
          tPatch = {y : tValeur};
        else if (pChamp.classList.contains ("js_carte_zoom"))
          tPatch = {zoom : tValeur};
        if (tPatch) maj_brouillon_coordonnees_carte (pTpl, tPatch);
      }
  }

function applique_brouillon_bulle_depuis_champ (pChamp)
  {
    if (!pChamp || !pChamp.classList) return;
    const tBrouillon = brouillon_bulle_courant ();
    if (!tBrouillon) return;

    if (pChamp.classList.contains ("js_inspecteur_bulle_nom"))
      {
        maj_brouillon_nom_bulle_calque (pChamp.value || "");
        return;
      }

    if (pChamp.classList.contains ("js_inspecteur_bulle_couleur"))
      {
        maj_brouillon_bulle_calque (
          {
            couleur_bulle : couleur_bulle_carte_effective_client (
              {couleur_bulle : pChamp.value, carte_id : tBrouillon.carte_id},
              tBrouillon.carte_id
            ),
          }
        );
        return;
      }

    if (pChamp.classList.contains ("js_inspecteur_bulle_opacite_range")
        || pChamp.classList.contains ("js_inspecteur_bulle_opacite_number"))
      {
        const tVal = Number (pChamp.value);
        if (!Number.isFinite (tVal)) return;
        maj_brouillon_bulle_calque (
          {
            opacite_bulle : normalise_opacite_bulle_carte_client (
              tVal,
              tBrouillon.opacite_bulle
            ),
          }
        );
        return;
      }

    if (pChamp.classList.contains ("js_inspecteur_bulle_taille")
        || pChamp.classList.contains ("js_inspecteur_bulle_taille_range")
        || pChamp.classList.contains ("js_inspecteur_bulle_taille_number"))
      {
        const tVal = Number (pChamp.value);
        if (!Number.isFinite (tVal)) return;
        maj_brouillon_bulle_calque (
          {
            taille_bulle : normalise_taille_bulle_carte_client (
              tVal,
              tBrouillon.taille_bulle
            ),
          }
        );
        return;
      }

    if (pChamp.classList.contains ("js_inspecteur_bulle_rotation")
        || pChamp.classList.contains ("js_inspecteur_bulle_rotation_range")
        || pChamp.classList.contains ("js_inspecteur_bulle_rotation_number"))
      {
        const tVal = Number (pChamp.value);
        if (!Number.isFinite (tVal)) return;
        maj_brouillon_bulle_calque (
          {
            rotation_bulle : normalise_rotation_bulle_carte_client (
              tVal,
              tBrouillon.rotation_bulle
            ),
          }
        );
        return;
      }

    if (pChamp.classList.contains ("js_inspecteur_bulle_vue_x")
        || pChamp.classList.contains ("js_inspecteur_bulle_vue_y")
        || pChamp.classList.contains ("js_inspecteur_bulle_vue_w")
        || pChamp.classList.contains ("js_inspecteur_bulle_vue_h"))
      {
        const tValeur = parseFloat (pChamp.value);
        if (!Number.isFinite (tValeur)) return;
        let tPatch = null;
        if (pChamp.classList.contains ("js_inspecteur_bulle_vue_x"))
          tPatch = {x : tValeur};
        else if (pChamp.classList.contains ("js_inspecteur_bulle_vue_y"))
          tPatch = {y : tValeur};
        else if (pChamp.classList.contains ("js_inspecteur_bulle_vue_w"))
          tPatch = {w : Math.max (CARTE_VUE_MIN_TAILLE, tValeur)};
        else if (pChamp.classList.contains ("js_inspecteur_bulle_vue_h"))
          tPatch = {h : Math.max (CARTE_VUE_MIN_TAILLE, tValeur)};
        if (tPatch) maj_brouillon_vue_bulle_calque (tPatch);
      }
  }

function applique_inspecteur_scene_depuis_champ (pChamp)
  {
    if (!pChamp || !pChamp.classList) return;

    if (pChamp.classList.contains ("js_inspecteur_titre"))
      {
        const tTitre = pChamp.value || "";
        gInspecteurMessage.set (null);
        if (!gInspecteurSlugManuel.get ())
          gInspecteurSlug.set (fabrique_slug (tTitre));
        gInspecteurTitre.set (tTitre);
        return;
      }

    if (pChamp.classList.contains ("js_inspecteur_slug"))
      {
        gInspecteurSlugManuel.set (true);
        gInspecteurSlug.set (pChamp.value || "");
        gInspecteurMessage.set (null);
      }
  }

function applique_inspecteur_scene_mode_lecture_depuis_champ (pChamp)
  {
    if (!pChamp || !pChamp.classList) return;

    if (pChamp.classList.contains ("js_scene_mode_lecture_masque_couleur"))
      {
        modifie_brouillon_mode_lecture_masque_scene ({couleur : pChamp.value || MODE_LECTURE_MASQUE_SCENE_DEFAUT.couleur});
        return;
      }

    if (pChamp.classList.contains ("js_scene_mode_lecture_masque_opacite"))
      {
        modifie_brouillon_mode_lecture_masque_scene ({opacite : pChamp.value});
        return;
      }
  }

function applique_inspecteur_scene_geometrie_depuis_champ (pChamp)
  {
    if (!pChamp || !pChamp.classList) return;
    if (!pChamp.classList.contains ("js_scene_geometrie_number")) return;
    const tCle = pChamp.getAttribute ("data-cle");
    if (!tCle || !Object.prototype.hasOwnProperty.call (GEOMETRIE_SCENE_DEFAUT, tCle)) return;
    modifie_brouillon_geometrie_scene (
      {
        [tCle] : pChamp.value === ""
          ? GEOMETRIE_SCENE_DEFAUT[tCle]
          : valeur_stockage_geometrie_scene_client (tCle, pChamp.value),
      }
    );
  }

function applique_inspecteur_projet_depuis_champ (pChamp)
  {
    if (!pChamp || !pChamp.classList) return;

    if (pChamp.classList.contains ("js_inspecteur_projet_nom"))
      {
        const tNom = pChamp.value || "";
        gInspecteurProjetNom.set (tNom);
        gInspecteurProjetMessage.set (null);
        if (!gInspecteurProjetSlugManuel.get ())
          gInspecteurProjetSlug.set (fabrique_slug (tNom));
        return;
      }

    if (pChamp.classList.contains ("js_inspecteur_projet_slug"))
      {
        gInspecteurProjetSlugManuel.set (true);
        gInspecteurProjetSlug.set (pChamp.value || "");
        gInspecteurProjetMessage.set (null);
        return;
      }

    if (pChamp.classList.contains ("js_inspecteur_projet_rapport_lh_scene"))
      {
        const tTexte = (pChamp.value || "").trim ();
        if (!tTexte)
          {
            modifie_brouillon_rapport_lh_scene_projet (null);
            return;
          }
        const tRapport = parse_rapport_lh_scene_projet (tTexte);
        if (!tRapport)
          {
            gInspecteurProjetMessage.set ({texte : "Rapport L/H invalide. Exemple : 16/9", type : "erreur"});
            return;
          }
        modifie_brouillon_rapport_lh_scene_projet (tRapport);
        return;
      }

    if (pChamp.classList.contains ("js_inspecteur_projet_bulles_number"))
      {
        applique_inspecteur_projet_bulles_number_depuis_champ (pChamp);
        return;
      }

    if (pChamp.classList.contains ("js_inspecteur_projet_audio_import_debit_max_kbps"))
      {
        applique_inspecteur_projet_audio_import_debit_depuis_champ (pChamp);
        return;
      }

    if (pChamp.classList.contains ("js_inspecteur_projet_mode_lecture_number"))
      {
        applique_inspecteur_projet_mode_lecture_number_depuis_champ (pChamp);
        return;
      }
  }

function clic_zone_vide_arbre (pEvt)
  {
    const tRacine = pEvt && pEvt.currentTarget;
    const tCible = pEvt && pEvt.target;
    if (!tRacine || !tCible || !tRacine.contains (tCible)) return false;
    if (typeof tCible.closest !== "function") return false;
    const tItem = tCible.closest (
      ".arbre_calque, .arbre_categorie_entete, button, input, textarea, select, label, a"
    );
    return !tItem || !tRacine.contains (tItem);
  }

function touche_navigation_arbre (pEvt, pInclureActivation)
  {
    const tTouche = pEvt && (pEvt.key || pEvt.code);
    if (["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight", "Home", "End"].includes (tTouche)) return tTouche;
    if (pInclureActivation && (tTouche === "Enter" || tTouche === "Space" || tTouche === " ")) return tTouche;
    return null;
  }

function garde_focus_arbre_apres_navigation (pArbre, pSelecteurRepli)
  {
    Tracker.afterFlush (() =>
      {
        let tArbre = pArbre;
        if (!tArbre || !tArbre.isConnected)
          tArbre = pSelecteurRepli ? document.querySelector (pSelecteurRepli) : null;
        if (!tArbre || typeof tArbre.focus !== "function") return;
        const tActif = document.activeElement;
        if (tActif && tArbre.contains (tActif)) return;
        try { tArbre.focus ({preventScroll : true}); }
        catch (e) { tArbre.focus (); }
      });
  }

function arbre_cartes_visible_prefere (pTpl, pArbre)
  {
    if (pArbre && pArbre.isConnected) return pArbre;

    const tActif = document.activeElement;
    if (tActif && typeof tActif.closest === "function")
      {
        const tArbreActif = tActif.closest (".js_arbre_cartes");
        if (tArbreActif && tArbreActif.isConnected) return tArbreActif;
      }

    const tArbres = pTpl && typeof pTpl.findAll === "function"
      ? pTpl.findAll (".js_arbre_cartes")
      : [];
    return Array.from (tArbres || []).find ((pElt) => pElt && pElt.getClientRects ().length > 0) || null;
  }

function scrolle_selection_arbre_cartes (pTpl, pArbre)
  {
    const tArbre = arbre_cartes_visible_prefere (pTpl, pArbre);
    if (!tArbre || typeof tArbre.querySelectorAll !== "function") return;

    const tSelCalque = gCarteArbreSelectionne.get ();
    const tSelCat = gCarteArbreCategorieSelectionnee.get ();
    let tLigne = null;

    if (tSelCalque)
      {
        tLigne = Array.from (tArbre.querySelectorAll (".arbre_calque[data-id]"))
          .find ((pElt) => pElt.getAttribute ("data-id") === tSelCalque) || null;
      }
    else if (tSelCat)
      {
        tLigne = Array.from (tArbre.querySelectorAll (".arbre_categorie[data-categorie]"))
          .find ((pElt) => pElt.getAttribute ("data-categorie") === tSelCat) || null;
      }

    if (tLigne && typeof tLigne.scrollIntoView === "function")
      tLigne.scrollIntoView ({block : "nearest", behavior : "auto"});
  }

function scrolle_scene_cible_interactivite_selectionnee (pTpl, pSceneCibleId)
  {
    if (!pTpl || !pSceneCibleId || typeof pTpl.find !== "function") return;
    const tListe = pTpl.find (".inspecteur_scene_cible_liste");
    if (!tListe || typeof tListe.querySelectorAll !== "function") return;

    const tItem = Array.from (tListe.querySelectorAll (".js_inspecteur_interactivite_scene_item[data-scene-id]"))
      .find ((pElt) => pElt.getAttribute ("data-scene-id") === pSceneCibleId) || null;

    if (tItem && typeof tItem.scrollIntoView === "function")
      tItem.scrollIntoView ({block : "nearest", behavior : "auto"});
  }

function applique_audio_calque_destructif_selection ()
  {
    const tCible = cible_audio_editeur_courante ();
    if (!tCible.fileId) return false;

    const tDeleteRanges = ws_delete_ranges_courantes ();
    const tBornesSource = ws_bornes_source_pour_persistance (tDeleteRanges);
    const tStart   = tBornesSource.start;
    const tEnd     = tBornesSource.end;
    const tGain    = Number (gEditeur.get ("gain_db")  || 0);
    const tFin     = Number (gEditeur.get ("fade_in")  || 0);
    const tFout    = Number (gEditeur.get ("fade_out") || 0);

    if (!confirm ("Appliquer les modifications de façon destructive sur le fichier audio ?")) return false;

    gEditeur.set ("saving", true);
    const tMethode = tCible.type === "projet"
      ? "editer_audio_projet"
      : (tCible.type === "scene" ? "editer_audio_scene" : "editer_audio_calque");
    const tOp = tCible.type === "projet"
      ? {
        projet_id     : tCible.projet && tCible.projet._id,
        cle           : tCible.cle,
        audio_file_id : tCible.fileId,
        trim_start    : tStart,
        trim_end      : tEnd,
        delete_ranges : tDeleteRanges,
        gain_db       : tGain,
        fade_in       : tFin,
        fade_out      : tFout,
      }
      : (tCible.type === "scene"
        ? {
          scene_id      : tCible.scene && tCible.scene._id,
          cle           : tCible.cle,
          audio_file_id : tCible.fileId,
          trim_start    : tStart,
          trim_end      : tEnd,
          delete_ranges : tDeleteRanges,
          gain_db       : tGain,
          fade_in       : tFin,
          fade_out      : tFout,
        }
        : {
          scene_id      : Session.get ("scene_id"),
          objet_id      : gCalqueSelectionne.get (),
          audio_file_id : tCible.fileId,
          trim_start    : tStart,
          trim_end      : tEnd,
          delete_ranges : tDeleteRanges,
          gain_db       : tGain,
          fade_in       : tFin,
          fade_out      : tFout,
        });
    Meteor.call (tMethode,
      tOp,
      (err) =>
        {
          gEditeur.set ("saving", false);
          if (err) { alert ("Erreur : " + (err.reason || err.message)); return; }
          gEditeur.set ("dirty", false);
          gEditeur.set ("url", null);
          gWS.peakCache.clear ();
        });
    return true;
  }

function enregistre_bulle_selectionnee_depuis_inspecteur (pTpl)
  {
    applique_brouillon_bulle_depuis_champ (champ_actif_document ());
    if (!bulle_brouillon_modifie ()) return false;
    enregistre_brouillon_bulle_calque (pTpl);
    return true;
  }

function raccourci_validation_scene (pEvt)
  {
    if (!pEvt || pEvt.isComposing) return false;
    const tEnter = pEvt.key === "Enter" || pEvt.code === "Enter" || pEvt.code === "NumpadEnter";
    return tEnter && (pEvt.altKey || pEvt.metaKey);
  }

function calque_selectionne_supporte_validation_destructive ()
  {
    const tCategorie = trouve_categorie_pour_id (gCalqueSelectionne.get ());
    return tCategorie === "obj"
      || tCategorie === "zoog"
      || tCategorie === "prop";
  }

function gere_raccourci_validation_scene (pEvt, pTpl)
  {
    if (!raccourci_validation_scene (pEvt)) return false;
    if (gOngletDroite.get () !== "calques") return false;

    if (gBulleSelectionnee.get ())
      {
        pEvt.preventDefault ();
        enregistre_bulle_selectionnee_depuis_inspecteur (pTpl);
        return true;
      }

    if (calque_selectionne_supporte_validation_destructive ())
      {
        pEvt.preventDefault ();
        applique_audio_calque_destructif_selection ();
        return true;
      }

    return false;
  }

if (Template.sceneInspecteurCategorieResume)
  Template.sceneInspecteurCategorieResume.helpers
    ({
      inspecteur_section_repliable (pCle, pEtatParDefaut)
        {
          return infos_section_repliable_inspecteur (pCle, pEtatParDefaut);
        },

      inspecteur_section_repliable_avec_items (pCle, pEtatParDefaut, pItems)
        {
          return {
            ...infos_section_repliable_inspecteur (pCle, pEtatParDefaut),
            items : Array.isArray (pItems) ? pItems : [],
          };
        },
    });

if (edition_hub_active ())
  Template.scene.events
    ({
    // ----------------------------------------------------------------------
    // Editeur audio (wavesurfer)
    // ----------------------------------------------------------------------
    "pointerdown .js_inspecteur_ws_play" (e, tpl)
      {
        e.preventDefault ();
        e.stopPropagation ();
        if (e.stopImmediatePropagation) e.stopImmediatePropagation ();
        ws_play_pause_toggle ();
      },

    "click .js_inspecteur_ws_play" (e, tpl)
      {
        e.preventDefault ();
        e.stopPropagation ();
        if (e.stopImmediatePropagation) e.stopImmediatePropagation ();
      },

    // Click sur le conteneur de l'onde : focus pour activer les
    // raccourcis clavier (espace = play / pause, Home = retour au début).
    "mousedown .js_inspecteur_ws_conteneur" (e, tpl)
      {
        try { e.currentTarget.focus ({preventScroll: true}); } catch (e2) {}
      },

    "selectstart .js_inspecteur_ws_conteneur, dragstart .js_inspecteur_ws_conteneur" (e)
      {
        e.preventDefault ();
        return false;
      },

    "keydown .js_inspecteur_ws_conteneur" (e, tpl)
      {
        if (!gWS.instance) return;
        // Espace = play / pause. On évite le scroll de page par défaut.
        if (e.key === " " || e.code === "Space")
          {
            e.preventDefault ();
            ws_play_pause_toggle ();
          }
        else if (e.key === "Backspace" || e.key === "Delete")
          {
            if (gWS.selection) { e.preventDefault (); ws_delete_selection (); }
          }
        else if (e.key === "Escape")
          {
            if (gWS.selection) { e.preventDefault (); ws_deselect_selection (); }
          }
        // Home = pause + retour au début du fichier.
        else if (e.key === "Home")
          {
            e.preventDefault ();
            try { gWS.instance.pause (); } catch (e2) {}
            try { gWS.instance.setTime (0); } catch (e2) {}
          }
      },

    "input .js_inspecteur_ws_zoom" (e)
      {
        if (!gWS.instance) return;
        const tV = Number (e.currentTarget.value);
        try { gWS.instance.zoom (Math.max (10, 100 + tV)); } catch (e2) {}
      },

    "click .js_inspecteur_ws_delete_sel" ()
      { ws_delete_selection (); },

    "click .js_inspecteur_ws_deselect" ()
      { ws_deselect_selection (); },

    "input .js_inspecteur_ws_gain" (e)
      {
        gEditeur.set ("gain_db", Number (e.currentTarget.value));
        gEditeur.set ("dirty", true);
        applique_edits ();
      },

    "input .js_inspecteur_ws_fadein" (e)
      {
        gEditeur.set ("fade_in", Number (e.currentTarget.value));
        gEditeur.set ("dirty", true);
        applique_edits ();
      },
    "input .js_inspecteur_ws_fadeout" (e)
      {
        gEditeur.set ("fade_out", Number (e.currentTarget.value));
        gEditeur.set ("dirty", true);
        applique_edits ();
      },

    async "click .js_inspecteur_ws_normalize" (e, tpl)
      {
        const tUrl = gWS.url;
        if (!tUrl) return;
        const tBtn = e.currentTarget;
        const tOld = tBtn.textContent;
        tBtn.disabled = true; tBtn.textContent = "Analyse…";
        try
          {
            const tPeak = await calcule_peak (tUrl);
            // Cible : -1 dBFS = ~0.891
            const tCible = 0.891;
            const tGain = lin_to_db (tCible / Math.max (1e-6, tPeak));
            const tClamp = Math.max (-24, Math.min (24, tGain));
            gEditeur.set ("gain_db", +tClamp.toFixed (2));
            gEditeur.set ("dirty", true);
            applique_edits ();
          }
        catch (err) { alert ("Normalisation échouée : " + err.message); }
        finally    { tBtn.disabled = false; tBtn.textContent = tOld; }
      },

    "click .js_inspecteur_ws_reset" (e, tpl)
      {
        const tDur = Number (gEditeur.get ("source_dur") || gEditeur.get ("dur") || 0);
        ws_force_pause ();
        gEditeur.set ("start",    0);
        gEditeur.set ("end",      tDur);
        gEditeur.set ("gain_db",  0);
        gEditeur.set ("fade_in",  0);
        gEditeur.set ("fade_out", 0);
        gEditeur.set ("dirty",    false);
        ws_deselect_selection ();
        ws_efface_preview_suppressions ();
        applique_edits ();
      },

    "click .js_inspecteur_ws_save" (e, tpl)
      {
        e.preventDefault ();
        applique_audio_calque_destructif_selection ();
      },

    "dragenter .scene, dragover .scene" (e)
      {
        e.preventDefault ();
        e.stopPropagation ();
        const tDt = e.originalEvent && e.originalEvent.dataTransfer;
        if (tDt) tDt.dropEffect = "copy";
        e.currentTarget.classList.add ("drop_actif");
      },

    "dragleave .scene" (e)
      {
        // dragleave se déclenche aussi en survolant un enfant ; on ne
        // retire la classe que si la souris quitte vraiment la zone.
        const tCible = e.currentTarget;
        const tRel   = e.originalEvent && e.originalEvent.relatedTarget;
        if (tRel && tCible.contains (tRel)) return;
        tCible.classList.remove ("drop_actif");
      },

    "drop .scene" (e)
      {
        e.preventDefault ();
        e.stopPropagation ();
        e.currentTarget.classList.remove ("drop_actif");

        if (gUploadSvgEnCours.get ()) return;

        const tSceneId = Session.get ("scene_id");
        if (!tSceneId)
          { alert ("Aucune scène sélectionnée."); return; }

        const tDt = e.originalEvent && e.originalEvent.dataTransfer;
        if (!tDt) return;
        const tFichier = tDt.files && tDt.files[0];
        if (!tFichier)
          { alert ("Aucun fichier détecté."); return; }

        const tNom = (tFichier.name || "").toLowerCase ();
        const tEstSvg =
          tFichier.type === "image/svg+xml" || tNom.endsWith (".svg");
        if (!tEstSvg)
          { alert ("Veuillez déposer un fichier SVG."); return; }

        televerse_svg_pour_scene (tFichier, tSceneId);
      },

    // -------- Arbre des calques --------

    "click .analyse_svg_scene > .arbre_calques" (e)
      {
        if (Date.now () < gCategoriesCalqueReorder.ignoreClickUntil)
          {
            e.preventDefault ();
            return;
          }
        if (!clic_zone_vide_arbre (e)) return;
        e.preventDefault ();
        selectionne_calque (null, {autoplay : false});
      },

    "click .js_toggle_categorie" (e, tpl)
      {
        e.stopPropagation ();
        const tLi  = e.currentTarget.closest (".arbre_categorie");
        if (!tLi) return;
        const tNom = tLi.getAttribute ("data-categorie");
        if (!tNom) return;
        const tEtat = categorie_scene_ouverte (tNom);
        // Par défaut considéré comme ouvert (true) → premier clic ferme.
        ecrit_ouverture_categorie_scene (tNom, !tEtat);
      },

    "click .js_categorie_toggle_visibility" (e)
      {
        e.preventDefault ();
        e.stopPropagation ();
        const tLi = e.currentTarget.closest (".arbre_categorie");
        const tSceneId = Session.get ("scene_id");
        const tNom = tLi && tLi.getAttribute ("data-categorie");
        if (!tSceneId || !tNom) return;
        ecrit_visibilite_categorie_scene (tSceneId, tNom, !categorie_scene_visible (tNom, tSceneId));
        force_rafraichissement_categories_svg_tpl (Template.instance ());
      },

    "click .js_calques_new_carte" (e, tpl)
      {
        e.preventDefault ();
        e.stopPropagation ();
        if (!abandon_brouillon_bulle_si_necessaire ()) return;
        cree_carte_depuis_vue_courante (tpl, {mode_selection : "calques"});
      },

    "click .js_categorie_toggle_lock" (e)
      {
        e.preventDefault ();
        e.stopPropagation ();
        const tLi = e.currentTarget.closest (".arbre_categorie");
        const tSceneId = Session.get ("scene_id");
        const tNom = tLi && tLi.getAttribute ("data-categorie");
        if (!tSceneId || !tNom) return;
        ecrit_verrouillage_categorie_scene (tSceneId, tNom, !categorie_scene_verrouillee (tNom, tSceneId));
        force_rafraichissement_categories_svg_tpl (Template.instance ());
      },

    "click .js_categorie" (e)
      {
        if (Date.now () < gCategoriesCalqueReorder.ignoreClickUntil)
          {
            e.preventDefault ();
            return;
          }
        const tLi = e.currentTarget.closest (".arbre_categorie");
        if (!tLi) return;
        const tNom = tLi.getAttribute ("data-categorie");
        if (tNom) selectionne_categorie (tNom);
      },

    "pointerdown .js_calques_categorie_handle" (e, tpl)
      {
        categories_calques_reorder_debut (e, tpl);
      },

    "click .js_calque" (e, tpl)
      {
        const tId = e.currentTarget.getAttribute ("data-id");
        if (!tId) return;
        const tEvt = e.originalEvent || e;
        const tDoubleGeste = tEvt && tEvt.detail > 1;
        if (tDoubleGeste)
          {
            if (interface_est_tactile ())
              zoom_sur_id (tpl, tId);
            return;
          }
        selectionne_calque (tId);
      },

    "click .js_bulle_calque" (e)
      {
        if (Date.now () < gCalquesCartesReorder.ignoreClickUntil)
          {
            e.preventDefault ();
            return;
          }
        const tCarteId = e.currentTarget.getAttribute ("data-bulle-carte-id");
        if (!tCarteId) return;
        selectionne_bulle_calque (tCarteId);
      },

    async "click .js_inspecteur_cartes_recreer_bulles" (e)
      {
        e.preventDefault ();
        if (gInspecteurCartesGlobalAction.get () === "recreation") return;

        const tCount = cartes_scene_liste ().length;
        if (!tCount)
          {
            gInspecteurCartesGlobalMessage.set ({
              texte : "Aucune carte à traiter dans cette scène.",
              type : "info",
            });
            return;
          }

        if (!confirm (`Recréer les bulles pour ${tCount} carte(s) de cette scène ?`)) return;

        gInspecteurCartesGlobalAction.set ("recreation");
        gInspecteurCartesGlobalMessage.set (null);
        try
          {
            const tRes = await recree_bulles_scene_courante_depuis_reglages_projet_client ();
            const tUpdated = (tRes && Number (tRes.updatedCount)) || 0;
            gInspecteurCartesGlobalMessage.set ({
              texte : `Bulles recréées pour ${tUpdated} carte(s).`,
              type : "succes",
            });
          }
        catch (pErr)
          {
            gInspecteurCartesGlobalMessage.set ({
              texte : "Erreur : " + ((pErr && (pErr.reason || pErr.message)) || pErr),
              type : "erreur",
            });
          }
        finally
          {
            gInspecteurCartesGlobalAction.set (null);
          }
      },

    async "click .js_inspecteur_categorie_recreer_contours" (e)
      {
        e.preventDefault ();
        if (gInspecteurContoursGlobalAction.get () === "recreation") return;
        const tCategorie = e.currentTarget.getAttribute ("data-categorie") || "";
        if (!contour_categorie_supportee (tCategorie)) return;

        const tCount = compte_elements_categorie_scene (tCategorie);
        const tLibelle = tCategorie === CALQUES_CATEGORIE_BOUTONS ? "bouton" : "objet";
        if (!tCount)
          {
            gInspecteurContoursGlobalMessage.set ({
              texte : `Aucun ${tLibelle} à traiter dans cette scène.`,
              type : "info",
            });
            return;
          }

        if (!confirm (`Régénérer les contours pour ${tCount} ${tLibelle}(s) de cette scène ?`)) return;

        gInspecteurContoursGlobalAction.set ("recreation");
        gInspecteurContoursGlobalMessage.set (null);
        try
          {
            const tRes = await recree_contours_scene_courante_depuis_reglages_categorie_client (tCategorie);
            const tUpdated = (tRes && Number (tRes.updatedCount)) || 0;
            gInspecteurContoursGlobalMessage.set ({
              texte : `Contours régénérés pour ${tUpdated} ${tLibelle}(s).`,
              type : "succes",
            });
          }
        catch (pErr)
          {
            gInspecteurContoursGlobalMessage.set ({
              texte : "Erreur : " + ((pErr && (pErr.reason || pErr.message)) || pErr),
              type : "erreur",
            });
          }
        finally
          {
            gInspecteurContoursGlobalAction.set (null);
          }
      },

    "input .js_inspecteur_categorie_contour_couleur" (e)
      {
        e.preventDefault ();
        const tCategorie = e.currentTarget.getAttribute ("data-categorie") || "";
        if (!contour_categorie_supportee (tCategorie)) return;
        modifie_reglages_survol_contour_categorie_brouillon (tCategorie, {couleur : e.currentTarget.value});
      },

    "change .js_inspecteur_categorie_interface_carte_number, blur .js_inspecteur_categorie_interface_carte_number, input .js_inspecteur_categorie_interface_carte_color, change .js_inspecteur_categorie_interface_carte_select" (e)
      {
        const tPatch = patch_reglage_interface_carte_depuis_champ (e.currentTarget);
        if (tPatch) modifie_brouillon_reglages_interface_carte_scene (tPatch);
        actualise_geometrie_focus_zoom_alternatif_apres_commit (e.currentTarget);
      },

    "input .js_inspecteur_categorie_interface_carte_number" (e)
      {
        programme_preview_geometrie_focus_zoom_alternatif_depuis_champ (e.currentTarget);
      },

    async "click .js_inspecteur_categorie_interface_cartes_apply" (e)
      {
        e.preventDefault ();
        if (gInspecteurCartesInterfaceAction.get () === "recreation") return;
        const tSceneId = Session.get ("scene_id");
        if (!tSceneId) return;
        if (!confirm ("Appliquer les changements à toutes les cartes de la scène ?")) return;

        gInspecteurCartesInterfaceAction.set ("recreation");
        gInspecteurCartesGlobalMessage.set (null);
        try
          {
            const tReglages = brouillon_reglages_interface_carte_scene ()
              || reglages_interface_carte_pour_stockage (base_reglages_interface_carte ());
            await new Promise ((resolve, reject) =>
              {
                Meteor.call (
                  "maj_scene_reglages_interface_carte",
                  {scene_id : tSceneId, reglages_interface_carte : tReglages},
                  (pErr, pRes) => pErr ? reject (pErr) : resolve (pRes)
                );
	            });
            gInspecteurCartesInterfaceDraft.set (null);
            applique_preview_reglages_interface_carte_tpl (null);
            gInspecteurCartesGlobalMessage.set ({
              texte : "Réglages d’interface appliqués aux cartes de la scène.",
              type : "succes",
            });
          }
        catch (pErr)
          {
            gInspecteurCartesGlobalMessage.set ({
              texte : "Erreur : " + ((pErr && (pErr.reason || pErr.message)) || pErr),
              type : "erreur",
            });
          }
        finally
          {
            gInspecteurCartesInterfaceAction.set (null);
          }
      },

    "change .js_inspecteur_categorie_contour_epaisseur, blur .js_inspecteur_categorie_contour_epaisseur, change .js_inspecteur_categorie_contour_opacite, blur .js_inspecteur_categorie_contour_opacite" (e)
      {
        e.preventDefault ();
        const tCategorie = e.currentTarget.getAttribute ("data-categorie") || "";
        if (!contour_categorie_supportee (tCategorie)) return;
        const tCle = e.currentTarget && e.currentTarget.classList.contains ("js_inspecteur_categorie_contour_epaisseur")
          ? "epaisseur"
          : "opacite";
        const tDefaut = tCle === "opacite"
          ? Math.round ((reglages_survol_contour_projet_mode_lecture ().opacite || 0) * 100)
          : reglages_survol_contour_projet_mode_lecture ().epaisseur;
        const tValeurBrute = valeur_numerique_champ_ou_defaut (e.currentTarget, tDefaut);
        modifie_reglages_survol_contour_categorie_brouillon (
          tCategorie,
          tCle === "opacite"
            ? {opacite : tValeurBrute / 100}
            : {[tCle] : tValeurBrute}
        );
        const tReglages = reglages_survol_contour_categorie_brouillon (tCategorie);
        if (e.currentTarget && e.currentTarget.classList.contains ("js_inspecteur_categorie_contour_epaisseur"))
          {
            e.currentTarget.value = String (tReglages.epaisseur);
            return;
          }
        e.currentTarget.value = String (Math.round ((tReglages.opacite || 0) * 100));
      },

    "dblclick .js_bulle_calque" (e, tpl)
      {
        e.preventDefault ();
        e.stopPropagation ();
        if (Date.now () < gCalquesCartesReorder.ignoreClickUntil) return;
        const tCarteId = e.currentTarget.getAttribute ("data-bulle-carte-id");
        if (!tCarteId) return;
        const tBrouillon = brouillon_bulle_courant ();
        if (gBulleSelectionnee.get () !== tCarteId
         || !tBrouillon
         || tBrouillon.carte_id !== tCarteId)
          {
            if (!selectionne_bulle_calque (tCarteId)) return;
          }
        zoom_sur_carte (tpl, tCarteId);
      },

    "click .js_inspecteur_bulle_svg_picker_toggle" (e)
      {
        e.preventDefault ();
        e.stopPropagation ();
        gInspecteurBulleCouleurPickerOuvert.set (false);
        gInspecteurBulleSvgPickerOuvert.set (!gInspecteurBulleSvgPickerOuvert.get ());
      },

    "click .js_inspecteur_bulle_svg_picker_item" (e)
      {
        e.preventDefault ();
        e.stopPropagation ();
        const tBrouillon = brouillon_bulle_courant ();
        if (!tBrouillon) return;

        const tSvgBulleId = normalise_svg_bulle_id_carte_client (
          e.currentTarget.getAttribute ("data-svg-id")
        );
        if (!tSvgBulleId) return;

        const tInfo = configuration_bulle_inspecteur_courant_client ();
        const tCouleurs = reglages_bulles_projet_courant ().couleurs_auto;
        maj_brouillon_bulle_calque (
          {
            svg_bulle_id : tSvgBulleId,
            couleur_bulle : couleur_auto_bulle_inspecteur_par_defaut_client (
              tCouleurs,
              tInfo && tInfo.config && tInfo.config.couleur_bulle
            ),
          }
        );
        ferme_menus_locaux_inspecteur_bulle_client ();
      },

    "click .js_inspecteur_bulle_svg_color_picker_toggle" (e)
      {
        e.preventDefault ();
        e.stopPropagation ();
        gInspecteurBulleSvgPickerOuvert.set (false);
        gInspecteurBulleCouleurPickerOuvert.set (!gInspecteurBulleCouleurPickerOuvert.get ());
      },

    "click .js_inspecteur_bulle_svg_color_picker_item" (e)
      {
        e.preventDefault ();
        e.stopPropagation ();
        const tBrouillon = brouillon_bulle_courant ();
        if (!tBrouillon) return;
        maj_brouillon_bulle_calque (
          {
            couleur_bulle : normalise_couleur_auto_bulle_projet_client (
              e.currentTarget.getAttribute ("data-color")
            ),
          }
        );
        ferme_menus_locaux_inspecteur_bulle_client ();
      },

    "keydown .arbre_calques" (e, tpl)
      {
        if (e.currentTarget && e.currentTarget.classList && e.currentTarget.classList.contains ("js_arbre_cartes")) return;
        const tEvt = e.originalEvent || e;
        if (tEvt.key === "Escape" && !tEvt.defaultPrevented)
          {
            e.preventDefault ();
            e.stopPropagation ();
            efface_selection_calques_scene ();
            return;
          }
        const tTouche = touche_navigation_arbre (tEvt, false);
        if (!tTouche) return;
        e.preventDefault ();
        e.stopPropagation ();
        if (e.stopImmediatePropagation) e.stopImmediatePropagation ();
        garde_focus_arbre_apres_navigation (e.currentTarget, ".analyse_svg_scene > .arbre_calques");

        const tInfos = construit_liste_visible (tpl);
        if (!tInfos || tInfos.items.length === 0) return;

        const tSelCalque = gCalqueSelectionne.get ();
        const tSelCat    = gCategorieSelectionnee.get ();
        const tSelBulle  = gBulleSelectionnee.get ();
        const tIdxSel = tInfos.items.findIndex ((it) =>
          (it.type === "calque"    && it.id  === tSelCalque)
       || (it.type === "categorie" && it.nom === tSelCat)
       || (it.type === "bulle" && it.carte_id === tSelBulle));

        const applique_item = (pIdx) =>
          {
            const tIt = tInfos.items[pIdx];
            if (!tIt) return;
            if (tIt.type === "categorie") selectionne_categorie (tIt.nom);
            else if (tIt.type === "bulle") selectionne_bulle_calque (tIt.carte_id);
            else                            selectionne_calque (tIt.id);
          };

        if (tTouche === "ArrowDown")
          {
            const tIdx = tIdxSel < 0 ? 0 : Math.min (tIdxSel + 1, tInfos.items.length - 1);
            applique_item (tIdx);
          }
        else if (tTouche === "End")
          {
            applique_item (tInfos.items.length - 1);
          }
        else if (tTouche === "ArrowUp")
          {
            const tIdx = tIdxSel < 0 ? 0 : Math.max (tIdxSel - 1, 0);
            applique_item (tIdx);
          }
        else if (tTouche === "Home")
          {
            applique_item (0);
          }
        else if (tTouche === "ArrowRight")
          {
            const tIt = tInfos.items[tIdxSel];
            if (tIt && tIt.type === "categorie")
              ecrit_ouverture_categorie_scene (tIt.nom, true);
          }
        else if (tTouche === "ArrowLeft")
          {
            const tIt = tInfos.items[tIdxSel];
            if (tIt && tIt.type === "categorie")
              ecrit_ouverture_categorie_scene (tIt.nom, false);
          }
      },

    "input .js_inspecteur_bulle_couleur" (e)
      {
        const tBrouillon = brouillon_bulle_courant ();
        if (!tBrouillon) return;
        maj_brouillon_bulle_calque (
          {
            couleur_bulle : couleur_bulle_carte_effective_client (
              {couleur_bulle : e.currentTarget.value, carte_id : tBrouillon.carte_id},
              tBrouillon.carte_id
            ),
          }
        );
      },

    "change .js_inspecteur_bulle_interface_carte_number, blur .js_inspecteur_bulle_interface_carte_number, input .js_inspecteur_bulle_interface_carte_color, change .js_inspecteur_bulle_interface_carte_select" (e)
      {
        const tPatch = patch_reglage_interface_carte_depuis_champ (e.currentTarget);
        if (tPatch) modifie_brouillon_reglages_interface_carte_bulle (tPatch);
        actualise_geometrie_focus_zoom_alternatif_apres_commit (e.currentTarget);
      },

    "input .js_inspecteur_bulle_interface_carte_number" (e)
      {
        programme_preview_geometrie_focus_zoom_alternatif_depuis_champ (e.currentTarget);
      },

    "input .js_inspecteur_bulle_opacite_range, change .js_inspecteur_bulle_opacite_range, change .js_inspecteur_bulle_opacite_number" (e)
      {
        applique_brouillon_bulle_depuis_champ (e.currentTarget);
      },

    "input .js_inspecteur_bulle_taille_range, change .js_inspecteur_bulle_taille_range, change .js_inspecteur_bulle_taille_number" (e)
      {
        applique_brouillon_bulle_depuis_champ (e.currentTarget);
      },

    "input .js_inspecteur_bulle_rotation_range, change .js_inspecteur_bulle_rotation_range, change .js_inspecteur_bulle_rotation_number" (e)
      {
        applique_brouillon_bulle_depuis_champ (e.currentTarget);
      },

    "change .js_inspecteur_bulle_nom" (e)
      {
        applique_brouillon_bulle_depuis_champ (e.currentTarget);
      },

    "change .js_inspecteur_bulle_vue_x, change .js_inspecteur_bulle_vue_y, change .js_inspecteur_bulle_vue_w, change .js_inspecteur_bulle_vue_h" (e)
      {
        applique_brouillon_bulle_depuis_champ (e.currentTarget);
      },

    "keydown .js_inspecteur_bulle_nom, keydown .js_inspecteur_bulle_couleur, keydown .js_inspecteur_bulle_opacite_range, keydown .js_inspecteur_bulle_opacite_number, keydown .js_inspecteur_bulle_taille, keydown .js_inspecteur_bulle_taille_range, keydown .js_inspecteur_bulle_taille_number, keydown .js_inspecteur_bulle_rotation, keydown .js_inspecteur_bulle_rotation_range, keydown .js_inspecteur_bulle_rotation_number, keydown .js_inspecteur_bulle_vue_x, keydown .js_inspecteur_bulle_vue_y, keydown .js_inspecteur_bulle_vue_w, keydown .js_inspecteur_bulle_vue_h" (e, tpl)
      {
        if (e.key === "Enter")
          {
            e.preventDefault ();
            if (typeof e.currentTarget.blur === "function") e.currentTarget.blur ();
          }
      },

    "click .js_inspecteur_bulle_save" (e, tpl)
      {
        e.preventDefault ();
        enregistre_bulle_selectionnee_depuis_inspecteur (tpl);
      },

    "click .js_inspecteur_bulle_interface_carte_save" (e, tpl)
      {
        e.preventDefault ();
        enregistre_bulle_selectionnee_depuis_inspecteur (tpl);
      },

    "click .js_inspecteur_bulle_delete" (e)
      {
        e.preventDefault ();
        e.stopPropagation ();
        applique_brouillon_bulle_depuis_champ (champ_actif_document ());
        const tCarteId = gBulleSelectionnee.get ();
        if (!tCarteId) return;
        demande_suppression_carte_scene (tCarteId, titre_carte_pour_actions (tCarteId));
      },

    "click .inspecteur_bulle_arbre_carte .js_arbre_cartes" (e)
      {
        if (!clic_zone_vide_arbre (e)) return;
        e.preventDefault ();
        selectionne_carte_arbre_calque (null);
      },

    "click .js_carte_tree_toggle" (e)
      {
        e.stopPropagation ();
        const tLi = e.currentTarget.closest (".arbre_categorie");
        if (!tLi) return;
        const tNom = tLi.getAttribute ("data-categorie");
        if (!tNom) return;
        const tEtat = gCarteCategoriesOuvertes.get (tNom);
        gCarteCategoriesOuvertes.set (tNom, tEtat === false ? true : false);
      },

    "click .js_carte_tree_categorie" (e)
      {
        const tLi = e.currentTarget.closest (".arbre_categorie");
        if (!tLi) return;
        const tNom = tLi.getAttribute ("data-categorie");
        if (tNom) selectionne_carte_arbre_categorie (tNom);
      },

    "pointerdown .js_carte_tree_obj_item" (e, tpl)
      {
        carte_obj_reorder_debut (e, tpl);
      },

    "click .js_carte_tree_calque" (e)
      {
        if (Date.now () < gCarteObjReorder.ignoreClickUntil)
          {
            e.preventDefault ();
            return;
          }
        const tId = e.currentTarget.getAttribute ("data-id");
        if (!tId) return;
        selectionne_carte_arbre_calque (tId);
      },

    "click .js_carte_tree_mask_toggle" (e)
      {
        e.preventDefault ();
        e.stopPropagation ();
        if (Date.now () < gCarteObjReorder.ignoreClickUntil) return;
        const tId = e.currentTarget.getAttribute ("data-id");
        if (!tId) return;
        if (!carte_arbre_calques_est_actif ()) selectionne_carte_arbre_calque (tId);
        cartes_toggle_masquage_brouillon (tId);
      },

    "keydown .js_arbre_cartes" (e, tpl)
      {
        const tArbreCourant = e.currentTarget;
        const tEvt = e.originalEvent || e;
        if (raccourci_validation_scene (tEvt)) return;
        if (tEvt.key === "Escape" && !tEvt.defaultPrevented)
          {
            e.preventDefault ();
            e.stopPropagation ();
            reinitialise_selection_arbre_carte ();
            return;
          }
        const tTouche = touche_navigation_arbre (tEvt, true);
        if (!tTouche) return;
        e.preventDefault ();
        e.stopPropagation ();
        if (e.stopImmediatePropagation) e.stopImmediatePropagation ();
        garde_focus_arbre_apres_navigation (e.currentTarget, ".inspecteur_bulle_arbre_carte .js_arbre_cartes");

        const tInfos = construit_liste_visible_cartes (tpl);
        if (!tInfos || tInfos.items.length === 0) return;

        const tSelCalque = gCarteArbreSelectionne.get ();
        const tSelCat = gCarteArbreCategorieSelectionnee.get ();
        const tIdxSel = tInfos.items.findIndex ((pItem) =>
          (pItem.type === "calque" && pItem.id === tSelCalque)
          || (pItem.type === "categorie" && pItem.nom === tSelCat));

        const applique_item = (pIdx) =>
          {
            const tItem = tInfos.items[pIdx];
            if (!tItem) return;
            if (tItem.type === "categorie") selectionne_carte_arbre_categorie (tItem.nom);
            else                             selectionne_carte_arbre_calque (tItem.id);
          };

        if (tTouche === "ArrowDown")
          {
            const tIdx = tIdxSel < 0 ? 0 : Math.min (tIdxSel + 1, tInfos.items.length - 1);
            applique_item (tIdx);
          }
        else if (tTouche === "End")
          {
            applique_item (tInfos.items.length - 1);
          }
        else if (tTouche === "ArrowUp")
          {
            const tIdx = tIdxSel < 0 ? 0 : Math.max (tIdxSel - 1, 0);
            applique_item (tIdx);
          }
        else if (tTouche === "Home")
          {
            applique_item (0);
          }
        else if (tTouche === "ArrowRight")
          {
            const tItem = tInfos.items[tIdxSel];
            if (tItem && tItem.type === "categorie")
              gCarteCategoriesOuvertes.set (tItem.nom, true);
          }
        else if (tTouche === "ArrowLeft")
          {
            const tItem = tInfos.items[tIdxSel];
            if (tItem && tItem.type === "categorie")
              gCarteCategoriesOuvertes.set (tItem.nom, false);
          }
        else if ((tTouche === "Enter" || tTouche === " ") && tSelCalque)
          {
            cartes_toggle_masquage_brouillon (tSelCalque);
          }

        Tracker.afterFlush (() => { scrolle_selection_arbre_cartes (tpl, tArbreCourant); });
      },

    "change .js_sensibilite_zoom" (e)
      {
        if (mode_lecture_scene_actif ()) return;
        ecrit_sensibilite_zoom (e.currentTarget.value);
      },

    "change .js_zoom_sur_curseur" (e)
      {
        if (mode_lecture_scene_actif ()) return;
        ecrit_zoom_sur_curseur (!!e.currentTarget.checked);
      },

    "change .js_pan_x, change .js_pan_y, change .js_zoom" (e, tpl)
      {
        if (mode_lecture_scene_actif ()) return;
        applique_panzoom_depuis_inputs (tpl);
      },

    "keydown .js_pan_x, keydown .js_pan_y, keydown .js_zoom" (e, tpl)
      {
        if (mode_lecture_scene_actif ()) return;
        if (e.key === "Enter")
          { e.preventDefault (); applique_panzoom_depuis_inputs (tpl); }
      },

    "click .js_adopter_vue" (e, tpl)
      {
        if (mode_lecture_scene_actif ()) return;
        adopter_vue_courante (tpl, e.currentTarget);
      },

    "click .js_reinitialiser_vue" (e, tpl)
      {
        if (mode_lecture_scene_actif ()) return;
        reinitialise_vue_scene (tpl);
      },

    "click .js_zoomer_sur_selection" (e, tpl)
      {
        e.preventDefault ();
        if (mode_lecture_scene_actif ()) return;
        zoom_sur_selection_scene_active (tpl);
      },

    "click .js_scene_help_toggle" (e)
      {
        e.preventDefault ();
        scene_help_toggle_dialog ();
      },

    "click .js_scene_help_close" (e)
      {
        e.preventDefault ();
        scene_help_close_dialog ();
      },

    "pointerdown .js_scene_help_card" ()
      {
        gSceneHelpCogibulle.notePointerDown ();
      },

    "pointerdown .js_scene_help_drag" (e)
      {
        if (e.target && e.target.closest ("button, input, select, a")) return;
        gSceneHelpCogibulle.startDrag (e.originalEvent || e);
      },

    "pointerdown .js_scene_help_resize" (e)
      {
        gSceneHelpCogibulle.startResize (e.originalEvent || e);
      },

    "input .js_scene_ui_selection_color" (e)
      {
        maj_style_interface_brouillon ({selection_color : e.currentTarget.value});
      },

    "input .js_scene_ui_hover_color" (e)
      {
        maj_style_interface_brouillon ({hover_color : e.currentTarget.value});
      },

    "change .js_scene_ui_stroke_width" (e)
      {
        const tWidth = Number (e.currentTarget.value);
        if (!isFinite (tWidth)) return;
        maj_style_interface_brouillon ({stroke_width : tWidth});
      },

    "change .js_scene_ui_stroke_style" (e)
      {
        maj_style_interface_brouillon ({stroke_style : e.currentTarget.value});
      },

    "change .js_scene_ui_rainbow" (e)
      {
        maj_style_interface_brouillon ({rainbow_category : !!e.currentTarget.checked});
      },

    "change .js_scene_ui_apply_future" (e)
      {
        gInspecteurStyleApplyFuture.set (!!e.currentTarget.checked);
        gInspecteurStyleMessage.set (null);
      },

    "click .js_scene_ui_reset_defaults" ()
      {
        if (!confirm ("Rétablir les valeurs par défaut du style interface ?"))
          return;
        const tCourant = style_interface_scene_previsualise ();
        maj_style_interface_brouillon ({
          selection_color : STYLE_INTERFACE_DEFAUT.selection_color,
          hover_color : STYLE_INTERFACE_DEFAUT.hover_color,
          stroke_width : STYLE_INTERFACE_DEFAUT.stroke_width,
          stroke_style : STYLE_INTERFACE_DEFAUT.stroke_style,
          rainbow_category : tCourant.rainbow_category,
        });
      },

    "click .js_scene_ui_apply_scene" ()
      {
        const tSceneId = Session.get ("scene_id");
        if (!tSceneId) return;
        const tApplyProjectDefault = gInspecteurStyleApplyFuture.get ();
        gInspecteurStyleSaving.set ("scene");
        Meteor.call (
          "maj_scene_interface_style",
          {
            scene_id : tSceneId,
            style : style_interface_scene_previsualise (),
            apply_project_default : tApplyProjectDefault,
          },
          (pErr) =>
            {
              gInspecteurStyleSaving.set (null);
              if (pErr)
                {
                  gInspecteurStyleMessage.set ({
                    texte : "Erreur : " + (pErr.reason || pErr.message),
                    type : "erreur",
                  });
                  return;
                }
              gInspecteurStyleMessage.set ({
                texte : tApplyProjectDefault
                  ? "Style appliqué à la scène courante et enregistré comme défaut pour les futures scènes du projet."
                  : "Style appliqué à la scène courante.",
                type : "succes",
              });
            }
        );
      },

    "click .js_scene_ui_apply_project" ()
      {
        if (!confirm ("Appliquer ce style interface à toutes les scènes du projet ?"))
          return;
        const tProjetId = Session.get ("projet_id");
        if (!tProjetId) return;
        const tApplyProjectDefault = gInspecteurStyleApplyFuture.get ();
        gInspecteurStyleSaving.set ("project");
        Meteor.call (
          "applique_scene_interface_style_projet",
          {
            projet_id : tProjetId,
            style : style_interface_scene_previsualise (),
            apply_project_default : tApplyProjectDefault,
          },
          (pErr, pRes) =>
            {
              gInspecteurStyleSaving.set (null);
              if (pErr)
                {
                  gInspecteurStyleMessage.set ({
                    texte : "Erreur : " + (pErr.reason || pErr.message),
                    type : "erreur",
                  });
                  return;
                }
              const tCount = (pRes && Number (pRes.updatedCount)) || 0;
              gInspecteurStyleMessage.set ({
                texte : tApplyProjectDefault
                  ? `Style appliqué à ${tCount} scène(s) et enregistré comme défaut pour les futures scènes du projet.`
                  : `Style appliqué à ${tCount} scène(s) du projet.`,
                type : "succes",
              });
            }
        );
      },

    "click .js_inspecteur_section_repliable_toggle" (e)
      {
        e.preventDefault ();
        const tCle = e.currentTarget.getAttribute ("data-section-key") || "";
        const tEtatParDefaut = e.currentTarget.getAttribute ("data-default-open") === "0"
          ? "fermee"
          : "ouverte";
        bascule_section_repliable_inspecteur (tCle, tEtatParDefaut);
      },

    "change .js_scene_mode_lecture_masque_couleur, input .js_scene_mode_lecture_masque_couleur" (e)
      {
        applique_inspecteur_scene_mode_lecture_depuis_champ (e.currentTarget);
      },

    "change .js_scene_mode_lecture_masque_opacite" (e)
      {
        applique_inspecteur_scene_mode_lecture_depuis_champ (e.currentTarget);
      },

    "blur .js_scene_mode_lecture_masque_opacite" (e)
      {
        applique_inspecteur_scene_mode_lecture_depuis_champ (e.currentTarget);
        e.currentTarget.value = String (brouillon_mode_lecture_masque_scene ().opacite);
      },

    "click .js_scene_mode_lecture_masque_annuler" ()
      {
        if (!confirm ("Rétablir les valeurs par défaut du masque de carte ?"))
          return;
        gInspecteurSceneModeLectureMasqueDraft.set (
          normalise_mode_lecture_masque_scene_client (MODE_LECTURE_MASQUE_SCENE_DEFAUT)
        );
        gInspecteurSceneModeLectureMasqueMessage.set (null);
      },

    "click .js_scene_mode_lecture_masque_sauver" ()
      {
        const tSceneId = Session.get ("scene_id");
        if (!tSceneId) return;
        const tMasque = brouillon_mode_lecture_masque_scene ();
        gInspecteurSceneModeLectureMasqueSaving.set ("scene");
        Meteor.call (
          "maj_scene_mode_lecture_masque",
          {
            scene_id : tSceneId,
            mode_lecture_masque : tMasque,
          },
          (pErr) =>
            {
              gInspecteurSceneModeLectureMasqueSaving.set (null);
              if (pErr)
                {
                  gInspecteurSceneModeLectureMasqueMessage.set ({
                    texte : "Erreur : " + (pErr.reason || pErr.message),
                    type : "erreur",
                  });
                  return;
                }
              gInspecteurSceneModeLectureMasqueDraft.set (null);
              gInspecteurSceneModeLectureMasqueMessage.set ({
                texte : "Masque de carte mis à jour pour cette scène.",
                type : "succes",
              });
            }
        );
      },

    "click .js_scene_mode_lecture_masque_appliquer_projet" ()
      {
        if (!confirm ("Appliquer ce masque de carte à toutes les scènes du projet ?"))
          return;
        const tProjetId = Session.get ("projet_id");
        if (!tProjetId) return;
        const tMasque = brouillon_mode_lecture_masque_scene ();
        gInspecteurSceneModeLectureMasqueSaving.set ("project");
        Meteor.call (
          "applique_scene_mode_lecture_masque_projet",
          {
            projet_id : tProjetId,
            mode_lecture_masque : tMasque,
          },
          (pErr, pRes) =>
            {
              gInspecteurSceneModeLectureMasqueSaving.set (null);
              if (pErr)
                {
                  gInspecteurSceneModeLectureMasqueMessage.set ({
                    texte : "Erreur : " + (pErr.reason || pErr.message),
                    type : "erreur",
                  });
                  return;
                }
              gInspecteurSceneModeLectureMasqueDraft.set (null);
              const tCount = (pRes && Number (pRes.updatedCount)) || 0;
              gInspecteurSceneModeLectureMasqueMessage.set ({
                texte : `Masque de carte appliqué à ${tCount} scène(s) du projet.`,
                type : "succes",
              });
            }
        );
      },

    "change .js_scene_geometrie_number" (e)
      {
        applique_inspecteur_scene_geometrie_depuis_champ (e.currentTarget);
        const tCle = e.currentTarget.getAttribute ("data-cle");
        if (!tCle) return;
        const tGeom = brouillon_geometrie_scene ();
        if (!Object.prototype.hasOwnProperty.call (tGeom, tCle)) return;
        e.currentTarget.value = String (valeur_affichage_geometrie_scene_client (tCle, tGeom[tCle]));
      },

    "blur .js_scene_geometrie_number" (e)
      {
        applique_inspecteur_scene_geometrie_depuis_champ (e.currentTarget);
        const tCle = e.currentTarget.getAttribute ("data-cle");
        if (!tCle) return;
        const tGeom = brouillon_geometrie_scene ();
        if (!Object.prototype.hasOwnProperty.call (tGeom, tCle)) return;
        e.currentTarget.value = String (valeur_affichage_geometrie_scene_client (tCle, tGeom[tCle]));
      },

    "click .js_scene_geometrie_annuler" ()
      {
        gInspecteurSceneGeometrieDraft.set (
          normalise_geometrie_scene_client (GEOMETRIE_SCENE_DEFAUT)
        );
        gInspecteurSceneGeometrieMessage.set (null);
      },

    "click .js_scene_geometrie_sauver" ()
      {
        const tSceneId = Session.get ("scene_id");
        if (!tSceneId) return;
        const tGeometrie = brouillon_geometrie_scene ();
        gInspecteurSceneGeometrieSaving.set ("scene");
        Meteor.call (
          "maj_scene_geometrie",
          {
            scene_id : tSceneId,
            geometrie : tGeometrie,
          },
          (pErr) =>
            {
              gInspecteurSceneGeometrieSaving.set (null);
              if (pErr)
                {
                  gInspecteurSceneGeometrieMessage.set ({
                    texte : "Erreur : " + (pErr.reason || pErr.message),
                    type : "erreur",
                  });
                  return;
                }
              gInspecteurSceneGeometrieDraft.set (null);
              gInspecteurSceneGeometrieMessage.set ({
                texte : "Géométrie mise à jour pour cette scène.",
                type : "succes",
              });
            }
        );
      },

    "change .js_inspecteur_projet_nom" (e)
      {
        applique_inspecteur_projet_depuis_champ (e.currentTarget);
      },

    "click .js_inspecteur_projet_subtab" (e)
      {
        const tCle = e.currentTarget.getAttribute ("data-subtab") || "";
        if (!INSPECTEUR_PROJET_SOUS_ONGLETS.some ((pOnglet) => pOnglet.cle === tCle)) return;
        gInspecteurProjetSousOnglet.set (tCle);
      },

    "click .js_inspecteur_projet_bulles_upload_button" (e, tpl)
      {
        e.preventDefault ();
        if (!utilisateur_peut_modifier_projet_inspecteur ()) return;
        const tInput = tpl.find (".js_inspecteur_projet_bulles_svg_input");
        if (!tInput) return;
        tInput.value = "";
        tInput.click ();
      },

    async "change .js_inspecteur_projet_bulles_svg_input" (e)
      {
        if (!utilisateur_peut_modifier_projet_inspecteur ()) return;
        await televerse_svg_bulles_projet (e.currentTarget.files);
        e.currentTarget.value = "";
      },

    "click .js_inspecteur_projet_hype_upload_button" (e, tpl)
      {
        e.preventDefault ();
        if (!utilisateur_peut_modifier_projet_inspecteur ()) return;
        const tType = e.currentTarget.getAttribute ("data-type") || "recompense_carte";
        const tInput = tpl.find ('.js_inspecteur_projet_hype_file[data-type="' + tType + '"]');
        if (!tInput) return;
        tInput.value = "";
        tInput.click ();
      },

    "change .js_inspecteur_projet_hype_file" (e)
      {
        const tType = e.currentTarget.getAttribute ("data-type") || "recompense_carte";
        const tFile = e.currentTarget.files && e.currentTarget.files[0];
        e.currentTarget.value = "";
        upload_widget_hype_projet_depuis_fichier (tType, tFile);
      },

    "click .js_inspecteur_hype_preview_reload" (e)
      {
        e.preventDefault ();
        const tType = e.currentTarget.getAttribute ("data-type") || "recompense_carte";
        const tHost = host_apercu_hype_inspecteur (tType, e.currentTarget);
        const tWidget = widget_hype_projet (tType);
        if (tHost && tWidget)
          monte_widget_hype_direct (tHost, tWidget, {instanceKey : "inspecteur", forcer : true})
            .catch ((pErr) => affiche_erreur_hype_direct (tHost, pErr));
      },

    "click .js_inspecteur_hype_preview_play" (e)
      {
        e.preventDefault ();
        const tHost = host_apercu_hype_inspecteur (e.currentTarget.getAttribute ("data-type"), e.currentTarget);
        commande_timeline_hype (tHost && tHost._hubHypeDocument, "play");
      },

    "click .js_inspecteur_hype_preview_pause" (e)
      {
        e.preventDefault ();
        const tHost = host_apercu_hype_inspecteur (e.currentTarget.getAttribute ("data-type"), e.currentTarget);
        commande_timeline_hype (tHost && tHost._hubHypeDocument, "pause");
      },

    "click .js_inspecteur_hype_preview_stop" (e)
      {
        e.preventDefault ();
        const tType = e.currentTarget.getAttribute ("data-type") || "recompense_carte";
        const tHost = host_apercu_hype_inspecteur (tType, e.currentTarget);
        commande_timeline_hype (tHost && tHost._hubHypeDocument, "stop");
      },

    async "click .js_inspecteur_projet_hype_delete" (e)
      {
        e.preventDefault ();
        const tWidgetId = e.currentTarget.getAttribute ("data-widget-id") || "";
        if (!tWidgetId || !utilisateur_peut_modifier_projet_inspecteur ()) return;
        if (!await confirme_action_inspecteur ("Supprimer entièrement ce widget Hype ?")) return;
        Meteor.call ("supprimer_widget_hype_projet", {widget_id : tWidgetId}, (err) =>
          {
            const tType = e.currentTarget.getAttribute ("data-type") || "recompense_carte";
            if (err)
              gInspecteurProjetHypeMessages.set (tType, {type : "erreur", texte : "Suppression impossible : " + (err.reason || err.message)});
            else
              gInspecteurProjetHypeMessages.set (tType, {type : "succes", texte : "Widget supprimé."});
          });
      },

    async "click .js_inspecteur_projet_bulles_delete_all" (e)
      {
        e.preventDefault ();
        e.stopPropagation ();
        if (gInspecteurProjetBullesBulkDelete.get ()) return;

        const tTotal = fichiers_bulles_projet_courant ().length;
  if (!tTotal || !utilisateur_peut_modifier_projet_inspecteur ()) return;
        if (!confirm (`Supprimer les ${tTotal} SVG des bulles du projet ?`)) return;

        ferme_menu_contextuel_inspecteur_projet_bulles ();
        gInspecteurProjetBullesBulkDelete.set (true);
        try
          {
            const tRes = await supprime_tous_svg_bulles_projet_client ();
            if (tRes.errorCount && !tRes.deletedCount)
              throw tRes.firstError || new Error ("Suppression impossible");

            if (tRes.errorCount)
              {
                gInspecteurProjetBullesMessage.set ({
                  texte : `${tRes.deletedCount} SVG supprimé(s). ${tRes.errorCount} échec(s).`,
                  type : "info",
                });
              }
            else
              {
                gInspecteurProjetBullesMessage.set ({
                  texte : `${tRes.deletedCount} SVG supprimé(s).`,
                  type : "succes",
                });
              }
          }
        catch (pErr)
          {
            gInspecteurProjetBullesMessage.set ({
              texte : "Erreur : " + ((pErr && (pErr.reason || pErr.message)) || pErr),
              type : "erreur",
            });
          }
        finally
          {
            gInspecteurProjetBullesBulkDelete.set (false);
          }
      },

    "contextmenu .js_inspecteur_projet_bulle_vignette" (e)
      {
        if (!utilisateur_peut_modifier_projet_inspecteur ()) return;
        e.preventDefault ();
        e.stopPropagation ();
        annule_appui_long_inspecteur_projet_bulles ();
        ouvre_menu_contextuel_inspecteur_projet_bulles (
          e.currentTarget.dataset.id,
          e.currentTarget.dataset.name || "",
          e.clientX,
          e.clientY
        );
      },

    "pointerdown .js_inspecteur_projet_bulle_vignette" (e)
      {
        demarre_appui_long_inspecteur_projet_bulles (e);
      },

    "change .js_inspecteur_projet_bulles_color_picker, input .js_inspecteur_projet_bulles_color_picker" (e)
      {
        gInspecteurProjetBullesColorPicker.set ((e.currentTarget.value || "").trim ().toLowerCase ());
      },

    "click .js_inspecteur_projet_bulles_color_add" (e)
      {
        e.preventDefault ();
        if (!utilisateur_peut_modifier_projet_inspecteur ()) return;
        const tAjoutee = ajoute_couleur_bulles_projet (couleur_picker_bulles_projet ());
        if (!tAjoutee)
          {
            gInspecteurProjetBullesMessage.set ({
              texte : "Cette couleur est déjà présente dans la palette du projet.",
              type : "info",
            });
            return;
          }
        gInspecteurProjetBullesMessage.set (null);
      },

    "click .js_inspecteur_projet_bulles_color_remove" (e)
      {
        e.preventDefault ();
        if (!utilisateur_peut_modifier_projet_inspecteur ()) return;
        retire_couleur_bulles_projet (e.currentTarget.getAttribute ("data-color") || "");
        gInspecteurProjetBullesMessage.set (null);
      },

    "change .js_inspecteur_projet_bulles_number" (e)
      {
        applique_inspecteur_projet_bulles_number_depuis_champ (e.currentTarget);
        restaure_inspecteur_projet_bulles_number (e.currentTarget);
      },

    "blur .js_inspecteur_projet_bulles_number" (e)
      {
        applique_inspecteur_projet_bulles_number_depuis_champ (e.currentTarget);
        restaure_inspecteur_projet_bulles_number (e.currentTarget);
      },

    "keydown .js_inspecteur_projet_bulles_number, keydown .js_inspecteur_projet_audio_import_debit_max_kbps, keydown .js_inspecteur_projet_mode_lecture_number, keydown .js_inspecteur_scene_audio_volume, keydown .js_inspecteur_projet_audio_volume, keydown .js_inspecteur_projet_audio_delai_sequence" (e)
      {
        if (e.key !== "Enter") return;
        e.preventDefault ();
        if (typeof e.currentTarget.blur === "function") e.currentTarget.blur ();
      },

    "change .js_inspecteur_projet_slug" (e)
      {
        applique_inspecteur_projet_depuis_champ (e.currentTarget);
      },

    "click .js_inspecteur_projet_audio_bloc" (e)
      {
        if (e.target && e.target.closest
         && e.target.closest ("button, a, input, select, textarea, .inspecteur_audio_boutons, .inspecteur_audio_inline_actions"))
          return;
        const tCle = e.currentTarget && e.currentTarget.getAttribute ("data-key");
        if (tCle) selectionne_audio_projet_actif (tCle);
      },

    "click .js_inspecteur_scene_audio_bloc" (e)
      {
        if (e.target && e.target.closest
         && e.target.closest ("button, a, input, select, textarea, .inspecteur_audio_boutons, .inspecteur_audio_inline_actions"))
          return;
        const tCle = e.currentTarget && e.currentTarget.getAttribute ("data-key");
        if (tCle) selectionne_audio_scene_actif (tCle);
      },

    "input .js_inspecteur_scene_audio_seed_custom" (e)
      {
        const tCle = e.currentTarget.getAttribute ("data-key");
        if (!cle_audio_scene_valide (tCle)) return;
        gInspecteurSceneAudioSeedCustom.set (tCle, (e.currentTarget.value || "").trim ());
      },

    "change .js_inspecteur_scene_audio_seed" (e)
      {
        const tCle = e.currentTarget.getAttribute ("data-key");
        if (!cle_audio_scene_valide (tCle)) return;
        const tBrut = (e.currentTarget.value || "").trim ();
        if (tBrut === "")
          {
            gInspecteurSceneAudioSeedCustom.set (tCle, "");
            return;
          }
        const tVal = parseInt (tBrut, 10);
        if (!Number.isFinite (tVal) || tVal < 0) return;
        gInspecteurSceneAudioSeedCustom.set (tCle, String (tVal));
      },

    "click .js_inspecteur_scene_audio_generer" (e)
      {
        e.preventDefault ();
        const tCle = e.currentTarget.getAttribute ("data-key");
        lance_generation_audio_scene (tCle);
      },

    "click .js_inspecteur_scene_audio_ajouter" (e, tpl)
      {
        e.preventDefault ();
        const tCle = e.currentTarget.getAttribute ("data-key");
        selectionne_audio_scene_actif (tCle);
        const tInput = tpl.find ('.js_inspecteur_scene_audio_file[data-key="' + tCle + '"]');
        if (tInput) tInput.click ();
      },

    "change .js_inspecteur_scene_audio_file" (e)
      {
        const tCle = e.currentTarget.getAttribute ("data-key");
        const tFile = e.currentTarget.files && e.currentTarget.files[0];
        e.currentTarget.value = "";
        upload_audio_scene_depuis_fichier (tCle, tFile);
      },

    async "click .js_inspecteur_scene_audio_supprimer" (e)
      {
        e.preventDefault ();
        e.stopPropagation ();
        if (e.stopImmediatePropagation) e.stopImmediatePropagation ();
        const tCle = e.currentTarget.getAttribute ("data-key");
        const tSceneId = Session.get ("scene_id");
        if (!tSceneId || !cle_audio_scene_valide (tCle)) return;
        if (!await confirme_action_inspecteur ("Supprimer le fichier audio associé à cette rubrique ?")) return;
        arrete_lecture_audio_scene_inspecteur ();
        if (gInspecteurSceneAudioActif.get () === tCle) gInspecteurSceneAudioActif.set (null);
        gInspecteurSceneAudioUpload.set (tCle, null);
        Meteor.call ("maj_audio_scene",
          {scene_id : tSceneId, cle : tCle, audio_file_id : null},
          (err) =>
            {
              if (err)
                {
                  gInspecteurSceneAudioMessages.set (tCle,
                    {type : "erreur", texte : "Suppression impossible : " + (err.reason || err.message)});
                  alert ("Erreur : " + (err.reason || err.message));
                }
              else
                gInspecteurSceneAudioMessages.set (tCle,
                  {type : "succes", texte : "Fichier audio supprimé."});
            });
      },

    "click .js_inspecteur_scene_audio_editer" (e)
      {
        e.preventDefault ();
        const tCle = e.currentTarget.getAttribute ("data-key");
        selectionne_audio_scene_actif (tCle);
      },

    "click .js_inspecteur_scene_audio_lire" (e)
      {
        e.preventDefault ();
        e.stopPropagation ();
        if (e.stopImmediatePropagation) e.stopImmediatePropagation ();
        const tCle = e.currentTarget.getAttribute ("data-key");
        joue_audio_scene_inspecteur (tCle);
      },

    "change .js_inspecteur_scene_audio_volume, blur .js_inspecteur_scene_audio_volume" (e)
      {
        applique_champ_volume_audio_scene (e.currentTarget);
      },

    "input .js_inspecteur_projet_audio_seed_custom" (e)
      {
        const tCle = e.currentTarget.getAttribute ("data-key");
        if (!cle_audio_projet_valide (tCle)) return;
        gInspecteurProjetAudioSeedCustom.set (tCle, (e.currentTarget.value || "").trim ());
      },

    "change .js_inspecteur_projet_audio_seed" (e)
      {
        const tCle = e.currentTarget.getAttribute ("data-key");
        if (!cle_audio_projet_valide (tCle)) return;
        const tBrut = (e.currentTarget.value || "").trim ();
        if (tBrut === "")
          {
            gInspecteurProjetAudioSeedCustom.set (tCle, "");
            return;
          }
        const tVal = parseInt (tBrut, 10);
        if (!Number.isFinite (tVal) || tVal < 0) return;
        gInspecteurProjetAudioSeedCustom.set (tCle, String (tVal));
      },

    "click .js_inspecteur_projet_audio_generer" (e)
      {
        e.preventDefault ();
        const tCle = e.currentTarget.getAttribute ("data-key");
        lance_generation_audio_projet (tCle);
      },

    "click .js_inspecteur_projet_audio_ajouter" (e, tpl)
      {
        e.preventDefault ();
        const tCle = e.currentTarget.getAttribute ("data-key");
        selectionne_audio_projet_actif (tCle);
        const tInput = tpl.find ('.js_inspecteur_projet_audio_file[data-key="' + tCle + '"]');
        if (tInput) tInput.click ();
      },

    "change .js_inspecteur_projet_audio_file" (e)
      {
        const tCle = e.currentTarget.getAttribute ("data-key");
        const tFile = e.currentTarget.files && e.currentTarget.files[0];
        e.currentTarget.value = "";
        upload_audio_projet_depuis_fichier (tCle, tFile);
      },

    async "click .js_inspecteur_projet_audio_supprimer" (e)
      {
        e.preventDefault ();
        e.stopPropagation ();
        if (e.stopImmediatePropagation) e.stopImmediatePropagation ();
        const tCle = e.currentTarget.getAttribute ("data-key");
        const tProjet = projet_courant_scene ();
        if (!tProjet || !cle_audio_projet_valide (tCle)) return;
        if (!await confirme_action_inspecteur ("Supprimer le fichier audio associé à cette rubrique ?")) return;
        arrete_lecture_audio_projet_inspecteur ();
        if (gInspecteurProjetAudioActif.get () === tCle) gInspecteurProjetAudioActif.set (null);
        gInspecteurProjetAudioUpload.set (tCle, null);
        Meteor.call ("maj_audio_projet",
          {projet_id : tProjet._id, cle : tCle, audio_file_id : null},
          (err) =>
            {
              if (err)
                {
                  gInspecteurProjetAudioMessages.set (tCle,
                    {type : "erreur", texte : "Suppression impossible : " + (err.reason || err.message)});
                  alert ("Erreur : " + (err.reason || err.message));
                }
              else
                gInspecteurProjetAudioMessages.set (tCle,
                  {type : "succes", texte : "Fichier audio supprimé."});
            });
      },

    "click .js_inspecteur_projet_audio_editer" (e)
      {
        e.preventDefault ();
        const tCle = e.currentTarget.getAttribute ("data-key");
        selectionne_audio_projet_actif (tCle);
      },

    "click .js_inspecteur_projet_audio_lire" (e)
      {
        e.preventDefault ();
        e.stopPropagation ();
        if (e.stopImmediatePropagation) e.stopImmediatePropagation ();
        const tCle = e.currentTarget.getAttribute ("data-key");
        joue_audio_projet_inspecteur (tCle);
      },

    "change .js_inspecteur_projet_audio_volume, blur .js_inspecteur_projet_audio_volume" (e)
      {
        applique_champ_volume_audio_projet (e.currentTarget);
      },

    "change .js_inspecteur_projet_audio_delai_sequence, blur .js_inspecteur_projet_audio_delai_sequence" (e)
      {
        applique_champ_delai_sequence_audio_projet (e.currentTarget);
      },

    "change .js_inspecteur_projet_audio_import_compresser" (e)
      {
        modifie_brouillon_reglages_import_audio_projet ({compresser : !!e.currentTarget.checked});
      },

    "change .js_inspecteur_projet_audio_import_debit_max_kbps" (e)
      {
        applique_inspecteur_projet_audio_import_debit_depuis_champ (e.currentTarget);
        restaure_inspecteur_projet_audio_import_debit (e.currentTarget);
      },

    "blur .js_inspecteur_projet_audio_import_debit_max_kbps" (e)
      {
        applique_inspecteur_projet_audio_import_debit_depuis_champ (e.currentTarget);
        restaure_inspecteur_projet_audio_import_debit (e.currentTarget);
      },

    "change .js_inspecteur_projet_audio_import_convertir_mono" (e)
      {
        modifie_brouillon_reglages_import_audio_projet ({convertir_mono : !!e.currentTarget.checked});
      },

    "change .js_inspecteur_projet_audio_import_normaliser_gain" (e)
      {
        modifie_brouillon_reglages_import_audio_projet ({normaliser_gain : !!e.currentTarget.checked});
      },

    "change .js_inspecteur_projet_mode_lecture_number" (e)
      {
        applique_inspecteur_projet_mode_lecture_number_depuis_champ (e.currentTarget);
        restaure_inspecteur_projet_mode_lecture_number (e.currentTarget);
        actualise_geometrie_focus_zoom_alternatif_apres_commit (e.currentTarget);
      },

    "input .js_inspecteur_projet_mode_lecture_number" (e)
      {
        programme_preview_geometrie_focus_zoom_alternatif_depuis_champ (e.currentTarget);
      },

    "input .js_inspecteur_projet_mode_lecture_input, change .js_inspecteur_projet_mode_lecture_input" (e)
      {
        const tCle = e.currentTarget && e.currentTarget.getAttribute ("data-cle");
        if (!["survol_contour_couleur", "zoom_alternatif_cadre_couleur", "hype_recompense_carte_couleur_fond", "hype_recompense_scene_couleur_fond", "morphing_fond_couleur"].includes (tCle)) return;
        const tPatch = {[tCle] : e.currentTarget.value};
        if (REGLAGES_INTERFACE_CARTE_CLES_SET.has (tCle))
          modifie_brouillon_reglages_interface_carte_projet (tPatch);
        else
          modifie_brouillon_reglages_mode_lecture_projet (tPatch);
      },

    "change .js_inspecteur_projet_mode_lecture_select" (e)
      {
        const tCle = e.currentTarget && e.currentTarget.getAttribute ("data-cle");
        if (!tCle || !Object.prototype.hasOwnProperty.call (REGLAGES_MODE_LECTURE_PROJET_DEFAUTS, tCle)) return;
        const tPatch = {[tCle] : e.currentTarget.value};
        if (REGLAGES_INTERFACE_CARTE_CLES_SET.has (tCle))
          modifie_brouillon_reglages_interface_carte_projet (tPatch);
        else
          modifie_brouillon_reglages_mode_lecture_projet (tPatch);
      },

    "change .js_inspecteur_projet_mode_lecture_bool" (e)
      {
        const tCle = e.currentTarget && e.currentTarget.getAttribute ("data-cle");
        if (!tCle || !Object.prototype.hasOwnProperty.call (REGLAGES_MODE_LECTURE_PROJET_DEFAUTS, tCle)) return;
        modifie_brouillon_reglages_mode_lecture_projet ({[tCle] : !!e.currentTarget.checked});
      },

    "blur .js_inspecteur_projet_mode_lecture_number" (e)
      {
        applique_inspecteur_projet_mode_lecture_number_depuis_champ (e.currentTarget);
        restaure_inspecteur_projet_mode_lecture_number (e.currentTarget);
        actualise_geometrie_focus_zoom_alternatif_apres_commit (e.currentTarget);
      },

    async "click .js_inspecteur_projet_mode_lecture_recreer_cartes" (e)
      {
        e.preventDefault ();
        if (gInspecteurProjetSavingSection.get () === "mode_lecture_interface") return;
        const tProjet = projet_courant_scene ();
        if (!tProjet || !utilisateur_peut_modifier_projet_inspecteur ()) return;
        if (!confirm ("Appliquer les changements à toutes les cartes du projet ?")) return;

        gInspecteurProjetSavingSection.set ("mode_lecture_interface");
        gInspecteurProjetModeLectureMessage.set (null);
        try
          {
            const tReglages = reglages_interface_carte_pour_stockage (
              brouillon_reglages_interface_carte_projet ()
            );
            const tRes = await new Promise ((resolve, reject) =>
              {
                Meteor.call (
                  "maj_projet_reglages_interface_carte",
                  {projet_id : tProjet._id, reglages_interface_carte : tReglages},
                  (pErr, pRes) => pErr ? reject (pErr) : resolve (pRes)
                );
              });
            gInspecteurProjetModeLectureMessage.set ({
              texte : `Réglages d’interface appliqués à ${Number (tRes && tRes.updatedCount) || 0} carte(s) du projet.`,
              type : "succes",
            });
            gInspecteurProjetReglagesModeLecture.set (null);
            gInspecteurProjetReglagesInterfaceCarte.set (null);
            applique_preview_reglages_interface_carte_tpl (null);
            Tracker.afterFlush (() => { applique_decorations_cartes_tpl (gSceneTemplateActif); });
          }
        catch (pErr)
          {
            gInspecteurProjetModeLectureMessage.set ({
              texte : "Erreur : " + ((pErr && (pErr.reason || pErr.message)) || pErr),
              type : "erreur",
            });
          }
        finally
          {
            gInspecteurProjetSavingSection.set (null);
          }
      },

    "blur .js_inspecteur_projet_slug" (e)
      {
        gInspecteurProjetSlug.set (fabrique_slug (e.currentTarget.value || ""));
      },

    "change .js_inspecteur_projet_rapport_lh_scene" (e)
      {
        applique_inspecteur_projet_depuis_champ (e.currentTarget);
      },

    "blur .js_inspecteur_projet_rapport_lh_scene" (e)
      {
        applique_inspecteur_projet_depuis_champ (e.currentTarget);
        e.currentTarget.value = format_rapport_lh_scene_projet (brouillon_rapport_lh_scene_projet ());
      },

    "click .js_inspecteur_projet_zoom_alt_upload_button" (e, tpl)
      {
        e.preventDefault ();
        if (!utilisateur_peut_modifier_projet_inspecteur ()) return;
        const tRole = e.currentTarget.getAttribute ("data-role") || "";
        if (!ZOOM_ALTERNATIF_SVG_ROLE_KEYS.has (tRole)) return;
        const tInput = tpl.find (".js_inspecteur_projet_zoom_alt_svg_input");
        if (!tInput) return;
        tpl._inspecteurProjetZoomAltUploadRole = tRole;
        tInput.value = "";
        tInput.click ();
      },

    async "change .js_inspecteur_projet_zoom_alt_svg_input" (e, tpl)
      {
        if (!utilisateur_peut_modifier_projet_inspecteur ()) return;
        const tRole = (tpl && tpl._inspecteurProjetZoomAltUploadRole) || "";
        tpl._inspecteurProjetZoomAltUploadRole = "";
        await televerse_svg_zoom_alternatif_projet (tRole, e.currentTarget.files);
        e.currentTarget.value = "";
      },

    "click .js_inspecteur_projet_jauge_edit" (e)
      {
        e.preventDefault ();
        if (!utilisateur_peut_modifier_projet_inspecteur ()) return;
        inspecteur_jauge_visuelle_open ();
      },

    "click .js_inspecteur_projet_annuler" ()
      {
        reinitialise_inspecteur_projet ();
      },

    "click .js_inspecteur_projet_section_valider" (e)
      {
        e.preventDefault ();
        sauvegarde_inspecteur_projet_section_client (
          e.currentTarget.getAttribute ("data-section") || ""
        );
      },

    "click .js_inspecteur_project_members_open" ()
      {
        if (!utilisateur_peut_gerer_participants_projet_inspecteur ()) return;
        inspecteur_projet_dialog_open ();
      },

    "click .js_inspecteur_projet_add_tool" ()
      {
        alert ("Fonction en cours de développement");
      },

    async "click .js_inspecteur_projet_bulles_context_delete" (e)
      {
        e.preventDefault ();
        e.stopPropagation ();
        const tProjet = projet_courant_scene ();
        const tMenu = gInspecteurProjetBullesContextMenu.get ();
        ferme_menu_contextuel_inspecteur_projet_bulles ();
        if (!tProjet || !tMenu || !tMenu.fileId || !utilisateur_peut_modifier_projet_inspecteur ()) return;
        if (!confirm (`Supprimer ${tMenu.nom || "ce SVG"} des bulles du projet ?`)) return;
        try
          {
            await meteor_call_scene_promise (
              "supprime_svg_bulle_projet",
              {projet_id : tProjet._id, file_id : tMenu.fileId}
            );
            gInspecteurProjetBullesMessage.set ({
              texte : "SVG de bulle supprimé.",
              type : "succes",
            });
          }
        catch (pErr)
          {
            gInspecteurProjetBullesMessage.set ({
              texte : "Erreur : " + ((pErr && (pErr.reason || pErr.message)) || pErr),
              type : "erreur",
            });
          }
      },

    "click .js_inspecteur_project_dialog_close, click .js_inspecteur_project_dialog_cancel" ()
      {
        inspecteur_projet_dialog_close ();
      },

    "change .js_inspecteur_project_contact_checkbox" (e)
      {
        const tId = e.currentTarget.getAttribute ("data-id");
        if (!tId) return;
        gInspecteurProjetDialogSelection.set (tId, !!e.currentTarget.checked);
      },

    "click .js_inspecteur_project_dialog_confirm" ()
      {
        const tProjet = projet_courant_scene ();
        if (!tProjet) return;
        const tIds = inspecteur_projet_contacts_disponibles ()
          .filter ((pContact) => gInspecteurProjetDialogSelection.get (pContact._id))
          .map ((pContact) => pContact._id);
        if (!tIds.length) return;
        if (!confirm (`Ajouter ${tIds.length} participant(s) à ce projet ?`)) return;
        gInspecteurProjetDialogSaving.set (true);
        Meteor.call (
          "ajoute_membres_projet",
          {projet_id : tProjet._id, membre_ids : tIds},
          (pErr) =>
            {
              gInspecteurProjetDialogSaving.set (false);
              if (pErr)
                {
                  alert ("Erreur : " + (pErr.reason || pErr.message));
                  return;
                }
              inspecteur_projet_dialog_close ();
            }
        );
      },

    "pointerdown .js_inspecteur_project_dialog_card" ()
      {
        gInspecteurProjetDialogCogibulle.notePointerDown ();
      },

    "click .js_inspecteur_project_remove_member" (e)
      {
        const tProjet = projet_courant_scene ();
        const tMembreId = e.currentTarget.getAttribute ("data-id");
        if (!utilisateur_peut_retirer_membre_projet_inspecteur (tProjet, tMembreId)) return;
        const tUser = utilisateur_affiche_scene (tMembreId);
        if (!confirm (`Retirer ${tUser.displayName} de ce projet ?`)) return;
        Meteor.call (
          "retire_membre_projet",
          {projet_id : tProjet._id, membre_id : tMembreId},
          (pErr) =>
            {
              if (pErr) alert ("Erreur : " + (pErr.reason || pErr.message));
            }
        );
      },

    "click .js_inspecteur_project_toggle_admin" (e)
      {
        const tProjet = projet_courant_scene ();
        const tMembreId = e.currentTarget.getAttribute ("data-id");
        const tAction = e.currentTarget.getAttribute ("data-action") || "";
        if (!tProjet || !tMembreId || !utilisateur_peut_gerer_admins_projet_inspecteur ()) return;
        const tAdmin = tAction !== "remove";
        const tUser = utilisateur_affiche_scene (tMembreId);
        const tMessage = tAdmin
          ? `Attribuer le statut admin à ${tUser.displayName} ?`
          : `Retirer le statut admin à ${tUser.displayName} ?`;
        if (!confirm (tMessage)) return;
        Meteor.call (
          "definit_admin_projet",
          {projet_id : tProjet._id, membre_id : tMembreId, admin : tAdmin},
          (pErr) =>
            {
              if (pErr) alert ("Erreur : " + (pErr.reason || pErr.message));
            }
        );
      },

    "pointerdown .js_inspecteur_project_dialog_drag" (e)
      {
        if (e.target && e.target.closest ('button, input, select, label, a')) return;
        inspecteur_projet_dialog_drag_start (e.originalEvent || e);
      },

    "pointerdown .js_inspecteur_project_dialog_resize" (e)
      {
        inspecteur_projet_dialog_resize_start (e.originalEvent || e);
      },

    "click .js_inspecteur_jauge_visuelle_close" (e)
      {
        e.preventDefault ();
        inspecteur_jauge_visuelle_close ();
      },

    "click .js_inspecteur_jauge_visuelle_validate" (e)
      {
        e.preventDefault ();
        sauvegarde_jauge_visuelle_projet_client ();
      },

    "pointerdown .js_inspecteur_jauge_visuelle_card" ()
      {
        gInspecteurProjetJaugeVisuelleCogibulle.notePointerDown ();
      },

    "pointerdown .js_inspecteur_jauge_visuelle_drag" (e)
      {
        if (e.target && e.target.closest ("button, input, select, label, a")) return;
        gInspecteurProjetJaugeVisuelleCogibulle.startDrag (e.originalEvent || e);
      },

    "pointerdown .js_inspecteur_jauge_visuelle_resize" (e)
      {
        gInspecteurProjetJaugeVisuelleCogibulle.startResize (e.originalEvent || e);
      },

    // ---- Inspecteur calque sélectionné : prononciation / son ----

    "change .js_inspecteur_prono" (e)
      {
        gInspecteurProno.set (e.currentTarget.value);
      },

    "blur .js_inspecteur_prono" (e)
      {
        const tId = gCalqueSelectionne.get ();
        if (!tId) return;
        const tSceneId = Session.get ("scene_id");
        if (!tSceneId) return;
        const tVal = (e.currentTarget.value || "").trim ();
        Meteor.call ("maj_objet_scene",
          {scene_id : tSceneId, objet_id : tId, prononciation : tVal},
          (err) => { if (err) console.error ("[inspecteur] prono:", err.message || err); });
      },

    "change .js_inspecteur_son" (e)
      {
        const tId = gCalqueSelectionne.get ();
        if (!tId) return;
        const tSceneId = Session.get ("scene_id");
        if (!tSceneId) return;
        Meteor.call ("maj_objet_scene",
          {scene_id : tSceneId, objet_id : tId, son_override : !!e.currentTarget.checked},
          (err) => { if (err) console.error ("[inspecteur] son:", err.message || err); });
      },

    "change .js_inspecteur_interactivite_action" (e)
      {
        const tAction = normalise_action_interactivite_objet (e.currentTarget.value);
        if (tAction !== INTERACTIVITE_ACTION_VIDEO) ferme_apercu_video_inspecteur ();
        met_a_jour_objet_selectionne_scene (
          {interactivite_action : tAction || null},
          (err) => { if (err) console.error ("[inspecteur] interactivité action:", err.message || err); }
        );
      },

    "change .js_inspecteur_interactivite_url" (e)
      {
        const tValeur = (e.currentTarget.value || "").trim ();
        met_a_jour_objet_selectionne_scene (
          {interactivite_url : tValeur || null},
          (err) => { if (err) console.error ("[inspecteur] interactivité url:", err.message || err); }
        );
      },

    "click .js_inspecteur_interactivite_url_test" (e, tpl)
      {
        e.preventDefault ();
        const tInput = tpl.find (".js_inspecteur_interactivite_url");
        ouvre_url_dans_nouvelle_fenetre_interactivite (tInput ? tInput.value : "");
      },

    "click .js_inspecteur_interactivite_scene_item" (e)
      {
        e.preventDefault ();
        const tSceneCibleId = e.currentTarget.getAttribute ("data-scene-id");
        if (!tSceneCibleId || tSceneCibleId === Session.get ("scene_id")) return;
        met_a_jour_objet_selectionne_scene (
          {interactivite_scene_id : tSceneCibleId},
          (err) => { if (err) console.error ("[inspecteur] interactivité scène:", err.message || err); }
        );
      },

    "change .js_inspecteur_interactivite_video_url" (e)
      {
        const tValeur = (e.currentTarget.value || "").trim ();
        met_a_jour_objet_selectionne_scene (
          {interactivite_video_url : tValeur || null},
          (err) => { if (err) console.error ("[inspecteur] interactivité vidéo:", err.message || err); }
        );
      },

    "click .js_inspecteur_interactivite_video_test" (e, tpl)
      {
        e.preventDefault ();
        const tInput = tpl.find (".js_inspecteur_interactivite_video_url");
        ouvre_apercu_video_inspecteur (tInput ? tInput.value : "");
      },

    "click .js_inspecteur_video_preview_close" (e)
      {
        e.preventDefault ();
        ferme_apercu_video_inspecteur ();
      },

    "click .js_inspecteur_video_preview_backdrop" (e)
      {
        if (e.target !== e.currentTarget) return;
        ferme_apercu_video_inspecteur ();
      },

    "change .js_inspecteur_survol_contour_couleur" (e, tpl)
      {
        e.preventDefault ();
        const tReglages = lit_reglages_survol_contour_depuis_tpl (tpl);
        affiche_apercu_contour_objet_selectionne_scene (tReglages);
        met_a_jour_survol_contour_objet_selectionne_scene (
          tReglages,
          (pErr) => enregistre_message_survol_inspecteur (pErr)
        );
      },

    "change .js_inspecteur_survol_contour_epaisseur, blur .js_inspecteur_survol_contour_epaisseur, change .js_inspecteur_survol_contour_opacite, blur .js_inspecteur_survol_contour_opacite" (e, tpl)
      {
        e.preventDefault ();
        const tReglages = lit_reglages_survol_contour_depuis_tpl (tpl);
        affiche_apercu_contour_objet_selectionne_scene (tReglages);
        met_a_jour_survol_contour_objet_selectionne_scene (
          tReglages,
          (pErr) => enregistre_message_survol_inspecteur (pErr)
        );
      },

    "input .js_inspecteur_survol_contour_couleur" (e, tpl)
      {
        e.preventDefault ();
        affiche_apercu_contour_objet_selectionne_scene (lit_reglages_survol_contour_depuis_tpl (tpl));
      },

    "keydown .js_inspecteur_survol_contour_epaisseur, keydown .js_inspecteur_survol_contour_opacite" (e)
      {
        if (e.key !== "Enter") return;
        e.preventDefault ();
        if (typeof e.currentTarget.blur === "function") e.currentTarget.blur ();
      },

    "keydown .js_inspecteur_categorie_contour_epaisseur, keydown .js_inspecteur_categorie_contour_opacite" (e)
      {
        if (e.key !== "Enter") return;
        e.preventDefault ();
        if (typeof e.currentTarget.blur === "function") e.currentTarget.blur ();
      },

    // ---- Inspecteur calque sélectionné : profil / variante / génération ----

    "change .js_inspecteur_profil" (e)
      {
        const tId = gCalqueSelectionne.get ();
        if (!tId) return;
        const tSceneId = Session.get ("scene_id");
        if (!tSceneId) return;
        const tVal = e.currentTarget.value || null;
        Meteor.call ("maj_objet_scene",
          {scene_id : tSceneId, objet_id : tId, voice_profile_id : tVal},
          (err) => { if (err) console.error ("[inspecteur] profil:", err.message || err); });
      },

    "change .js_inspecteur_seed" (e)
      {
        const tId = gCalqueSelectionne.get ();
        if (!tId) return;
        const tSceneId = Session.get ("scene_id");
        if (!tSceneId) return;
        const tBrut = (e.currentTarget.value || "").trim ();
        const tVal = tBrut === "" ? null : parseInt (tBrut, 10);
        if (tVal !== null && (isNaN (tVal) || tVal < 0)) return;
        // Synchronise la saisie manuelle pour rester cohérent.
        gInspecteurSeedCustom.set (tId, tVal === null ? "" : String (tVal));
        Meteor.call ("maj_objet_scene",
          {scene_id : tSceneId, objet_id : tId, seed : tVal},
          (err) => { if (err) console.error ("[inspecteur] seed:", err.message || err); });
      },

    "input .js_inspecteur_seed_custom" (e)
      {
        const tId = gCalqueSelectionne.get ();
        if (!tId) return;
        const tStr = (e.currentTarget.value || "").trim ();
        gInspecteurSeedCustom.set (tId, tStr);
      },

    "change .js_inspecteur_seed_custom" (e)
      {
        const tId = gCalqueSelectionne.get ();
        if (!tId) return;
        const tSceneId = Session.get ("scene_id");
        if (!tSceneId) return;
        const tStr = (e.currentTarget.value || "").trim ();
        gInspecteurSeedCustom.set (tId, tStr);
        const tVal = tStr === "" ? null : parseInt (tStr, 10);
        if (tStr !== "" && (isNaN (tVal) || tVal < 0)) return;
        Meteor.call ("maj_objet_scene",
          {scene_id : tSceneId, objet_id : tId, seed : tVal},
          (err) => { if (err) console.error ("[inspecteur] seed custom:", err.message || err); });
      },

    "click .js_inspecteur_seed_save" ()
      {
        const tId = gCalqueSelectionne.get ();
        if (!tId) return;
        const tBrut = gInspecteurSeedCustom.get (tId);
        const tStr = (tBrut === undefined || tBrut === null) ? "" : String (tBrut).trim ();
        const tNum = parseInt (tStr, 10);
        if (isNaN (tNum) || tNum < 0)
          { alert ("Entrez d'abord un nombre de variante valide."); return; }
        gInspecteurSeedDialogSharedProjectIds.set (null);
        gInspecteurSeedDialogValue.set (tNum);
        gInspecteurSeedDialogOpen.set (true);
      },

    "change .js_inspecteur_seed_share_project" (e, tpl)
      {
        const tRoot = tpl.find (".js_inspecteur_seed_dialog_root") || tpl.firstNode;
        gInspecteurSeedDialogSharedProjectIds.set (
          collectCheckedProjectIds (tRoot, '.js_inspecteur_seed_share_project')
        );
      },

    "click .js_inspecteur_seed_dialog_cancel" ()
      {
        gInspecteurSeedDialogOpen.set (false);
        gInspecteurSeedDialogValue.set (null);
        gInspecteurSeedDialogSharedProjectIds.set (null);
      },

    "click .js_inspecteur_seed_dialog_save" (e, tpl)
      {
        const tNameEl = tpl.find (".js_inspecteur_seed_dialog_name");
        const tDescEl = tpl.find (".js_inspecteur_seed_dialog_desc");
        const tRoot = tpl.find (".js_inspecteur_seed_dialog_root") || tpl.firstNode;
        const tName = tNameEl ? (tNameEl.value || "").trim () : "";
        const tDesc = tDescEl ? (tDescEl.value || "").trim () : "";
        const tValue = gInspecteurSeedDialogValue.get ();
        const tSharedProjectIds = gInspecteurSeedDialogSharedProjectIds.get ()
          || collectCheckedProjectIds (tRoot, '.js_inspecteur_seed_share_project');
        const tProjetId = inspecteur_seed_dialog_projet_id ();
        if (!tName) { alert ("Le nom est requis."); return; }
        if (tValue === null || tValue === undefined)
          { alert ("Valeur de variante manquante."); return; }
        Meteor.call ("hub.seed.insert",
          {
            name : tName,
            value : tValue,
            description : tDesc,
            projet_id : tProjetId,
            shared_project_ids : tSharedProjectIds,
          }, (err) =>
          {
            if (err) { alert ("Erreur : " + (err.reason || err.message)); return; }
            gInspecteurSeedDialogOpen.set (false);
            gInspecteurSeedDialogValue.set (null);
            gInspecteurSeedDialogSharedProjectIds.set (null);
          });
      },

    "click .js_inspecteur_seed_copy" (e)
      {
        const tSeed = e.currentTarget.getAttribute ("data-seed");
        if (tSeed === null || tSeed === undefined) return;
        try { navigator.clipboard.writeText (String (tSeed)); } catch (e2) {}
        const tBtn = e.currentTarget;
        const tPrev = tBtn.textContent;
        tBtn.textContent = "✓ copié";
        setTimeout (() => { tBtn.textContent = tPrev; }, 1500);
      },

    "change .js_inspecteur_autoplay" (e)
      {
        ecrit_inspecteur_autoplay (!!e.currentTarget.checked);
      },

    "click .js_inspecteur_generer" () { lance_generation_calque (); },

    "keydown" (e, tpl)
      {
        const tEv = e.originalEvent || e;
        if (gere_raccourci_validation_scene (tEv, tpl)) return;
        if (tEv.ctrlKey && tEv.altKey && tEv.key === "g")
          {
            if (!gCalqueSelectionne.get ()) return;
            e.preventDefault ();
            lance_generation_calque ();
          }
      },

    async "click .js_inspecteur_audio_supprimer" ()
      {
        const tId = gCalqueSelectionne.get ();
        if (!tId) return;
        const tSceneId = Session.get ("scene_id");
        if (!tSceneId) return;
        if (!await confirme_action_inspecteur ("Supprimer le fichier audio associé à ce calque ?")) return;
        Meteor.call ("maj_objet_scene",
          {scene_id : tSceneId, objet_id : tId, audio_file_id : null},
          (err) => { if (err) alert ("Erreur : " + (err.reason || err.message)); });
      },

    "click .js_inspecteur_audio_ajouter" (e, tpl)
      {
        const tInput = tpl.find (".js_inspecteur_audio_file");
        if (tInput) tInput.click ();
      },

    "change .js_inspecteur_audio_file" (e)
      {
        const tFile = e.currentTarget.files && e.currentTarget.files[0];
        e.currentTarget.value = "";
        if (!tFile) return;
        const tId = gCalqueSelectionne.get ();
        if (!tId) return;
        const tSceneId = Session.get ("scene_id");
        if (!tSceneId) return;
        gInspecteurUploadEnCours.set (true);
        gInspecteurUploadProgression.set (0);
        gInspecteurUploadPhase.set ("upload");
        const tUpload = HubFiles.insert (
          {
            file       : tFile,
            meta       : {ownerId : Meteor.userId (), kind : "calque-audio",
                          scene_id : tSceneId, objet_id : tId,
                          source : "scene-object-import"},
            chunkSize  : "dynamic",
          }, false);
        tUpload.on ("progress", (pPct) =>
          {
            const tPct = Math.max (0, Math.min (100, Math.round (Number (pPct) || 0)));
            gInspecteurUploadProgression.set (tPct);
          });
        tUpload.on ("end", (err, fileObj) =>
          {
            if (err) {
              reinitialise_inspecteur_upload_audio ();
              alert ("Téléversement échoué : " + err.message);
              return;
            }
            gInspecteurUploadProgression.set (100);
            gInspecteurUploadPhase.set ("traitement");
            Meteor.call ("maj_objet_scene",
              {
                scene_id : tSceneId,
                objet_id : tId,
                audio_file_id : fileObj._id,
                audio_import_source : "scene-object-import",
              },
              (err2) =>
                {
                  reinitialise_inspecteur_upload_audio ();
                  if (err2) alert ("Erreur : " + (err2.reason || err2.message));
                });
          });
        Tracker.afterFlush (() => { tUpload.start (); });
      },

    "dblclick .js_calque" (e, tpl)
      {
        if (interface_est_tactile ()) return;
        const tId = e.currentTarget.getAttribute ("data-id");
        if (!tId) return;
        zoom_sur_id (tpl, tId);
      },

    "click .js_onglet_droite" (e)
      {
        const tCle = e.currentTarget.getAttribute ("data-onglet");
        if (tCle) ecrit_onglet_droite (tCle);
      },

    "pointerdown .js_poignee_redim_droite" (e, tpl)
      {
        commence_redim_col_droite (e, tpl);
      },

    // ---- Inspecteur : édition du titre / slug de la scène --------------

    "change .js_inspecteur_titre" (e)
      {
        applique_inspecteur_scene_depuis_champ (e.currentTarget);
      },

    "change .js_inspecteur_slug" (e)
      {
        applique_inspecteur_scene_depuis_champ (e.currentTarget);
      },

    "blur .js_inspecteur_slug" (e)
      {
        const tNorm = fabrique_slug (e.currentTarget.value || "");
        gInspecteurSlug.set (tNorm);
      },

    "keydown .js_inspecteur_titre, keydown .js_inspecteur_slug" (e, tpl)
      {
        if (e.key === "Enter")
          {
            e.preventDefault ();
            applique_inspecteur_scene_depuis_champ (e.currentTarget);
            tpl.find (".js_inspecteur_valider").click ();
          }
        else if (e.key === "Escape")
          {
            e.preventDefault ();
            tpl.find (".js_inspecteur_annuler").click ();
          }
      },

    "click .js_inspecteur_annuler" (e, tpl)
      {
        reinitialise_inspecteur ();
      },

    "click .js_inspecteur_valider" (e, tpl)
      {
        applique_inspecteur_scene_depuis_champ (champ_actif_document ());
        const tSceneId = Session.get ("scene_id");
        if (!tSceneId) return;
        const tScene = Scenes.findOne (tSceneId);
        if (!tScene) return;

        const tT = gInspecteurTitre.get ();
        const tS = gInspecteurSlug.get ();
        const tTitreBrut = (tT === null ? (tScene.titre || "") : tT).trim ();
        if (!tTitreBrut)
          { gInspecteurMessage.set ({texte : "Le titre est requis.", type : "erreur"}); return; }

        const tSlug = fabrique_slug (tS === null ? (tScene.slug || "") : tS);
        if (!tSlug)
          { gInspecteurMessage.set ({texte : "Le slug est requis.", type : "erreur"}); return; }

        // Reflète le slug normalisé
        gInspecteurSlug.set (tSlug);

        if (tTitreBrut === (tScene.titre || "") && tSlug === (tScene.slug || ""))
          { reinitialise_inspecteur (); return; }

        Meteor.call ("maj_scene_titre_slug",
          {
            scene_id : tSceneId,
            titre    : tTitreBrut,
            slug     : tSlug,
          },
          (err, res) =>
            {
              if (err)
                {
                  gInspecteurMessage.set
                    ({texte : "Erreur : " + (err.reason || err.message), type : "erreur"});
                  return;
                }
              if (res === "slug existant")
                {
                  gInspecteurMessage.set
                    ({texte : "Ce slug est déjà utilisé par une autre scène de ce projet.",
                      type  : "erreur"});
                  return;
                }
              const tAncSlug = tScene.slug || "";
              reinitialise_inspecteur ();
              gInspecteurMessage.set ({texte : "Modifications enregistrées.", type : "succes"});
              if (tSlug !== tAncSlug)
                {
                  const tSlugs = Session.get ("projet_slugs");
                  if (tSlugs && tSlugs.user && tSlugs.projet)
                    FlowRouter.go (`/projets/${tSlugs.user}/${tSlugs.projet}/${tSlug}`);
                }
            });
      },
    });

// ---------------------------------------------------------------------------
// Synchronisation arbre <-> SVG : surbrillance et clic dans le SVG.
// ---------------------------------------------------------------------------

Template.scene.onCreated (function ()
  {
    const tpl = this;
    gSceneTemplateActif = tpl;
    tpl._bboxParId = new Map ();
	    tpl._svgObjectVisible = new ReactiveVar (false);
	    tpl._svgObjectReady = new ReactiveVar (false);
	    tpl._svgObjectReadySourceKey = null;
	    tpl._svgObjectReloadToken = new ReactiveVar (null);
	    tpl._svgObjectReloadCount = 0;
	    tpl._svgObjectRenderSourceKey = null;
	    tpl._svgObjectSourceKey = null;
    tpl._svgObjectRenderTimer = null;
    tpl._sceneFullscreenActive = new ReactiveVar (false);
    tpl._sceneFullscreenExterneActive = new ReactiveVar (false);
    tpl._lectureScoresLocalStorageKey = null;
    tpl.autorun (() =>
      {
        Session.get ("projet_id");
        const tRecharge = recharge_scores_lecture_stockes_tpl (tpl);
        if (tRecharge && mode_lecture_scene_actif ())
          Tracker.afterFlush (() => rafraichit_scene_lecture_tpl (tpl, false));
      });
    tpl._sceneFullscreenPending = false;
    tpl._sceneFullscreenElement = null;
    tpl._sceneFullscreenChangeHandler = () =>
      {
        synchronise_etat_plein_ecran_scene_tpl (tpl);
      };
    tpl._sceneFullscreenResizeHandler = () =>
      {
        synchronise_etat_plein_ecran_scene_tpl (tpl);
      };
    EVENEMENTS_CHANGEMENT_PLEIN_ECRAN_SCENE.forEach ((pEventName) =>
      document.addEventListener (pEventName, tpl._sceneFullscreenChangeHandler)
    );
    window.addEventListener ("resize", tpl._sceneFullscreenResizeHandler);
    tpl._lectureViewportResizeHandler = () =>
      {
        programme_rafraichissement_viewport_lecture_tpl (tpl);
      };
    window.addEventListener ("resize", tpl._lectureViewportResizeHandler);

    const annule_rendu_object_svg = () =>
      {
        if (!tpl._svgObjectRenderTimer) return;
        clearTimeout (tpl._svgObjectRenderTimer);
        tpl._svgObjectRenderTimer = null;
      };
    
    if (edition_hub_active ())
      {
        // Réinitialise la sélection à chaque ré-instanciation de la template.
        gCalqueSelectionne.set (null);
        gCategorieSelectionnee.set (null);
        gInspecteurCalquesMode.set (null);
        gInspecteurStyleDraft.set (null);
        gInspecteurStyleApplyFuture.set (false);
        gInspecteurStyleMessage.set (null);
        gInspecteurStyleSaving.set (null);
        reinitialise_inspecteur_projet ();
        reinitialise_dialogue_participants_projet ();
        gInspecteurProjetDialogCogibulle.hideWithoutPersist ();
        gInspecteurProjetJaugeVisuelleCogibulle.hideWithoutPersist ();
        rec_cleanup_dialog_runtime ();
        gRecorderCogibulle.hideWithoutPersist ();
        charge_scene_help_open_projet ();
        reinitialise_inspecteur ();
        reinitialise_inspecteur_upload_audio ();
      }
    reinitialise_etat_cartes_scene ();

    // Réinitialise aussi l'inspecteur dès que l'on change de scène.
    tpl.autorun (() =>
      {
        const tSceneId = Session.get ("scene_id");
        if (edition_hub_active ())
          {
            gCalqueSelectionne.set (null);
            gCategorieSelectionnee.set (null);
            gInspecteurCalquesMode.set (null);
            gInspecteurStyleDraft.set (null);
            gInspecteurStyleApplyFuture.set (false);
            gInspecteurStyleMessage.set (null);
            gInspecteurStyleSaving.set (null);
            reinitialise_inspecteur ();
            reinitialise_inspecteur_upload_audio ();
          }
        tpl._bboxParId = new Map ();
        reinitialise_etat_cartes_scene ();
        Tracker.afterFlush (() => synchronise_etat_plein_ecran_scene_tpl (tpl));
      });

    if (!edition_hub_active ())
      {
        tpl.autorun (() =>
          {
            const tSceneId = Session.get ("scene_id");
            if (!tSceneId || mode_lecture_scene_actif ()) return;
            Tracker.afterFlush (() =>
              {
                if (Session.get ("scene_id") === tSceneId && !mode_lecture_scene_actif ())
                  demarre_lecture_scene_courante ();
              });
          });
      }

    if (edition_hub_active ())
      {
        tpl.autorun (() =>
          {
            Session.get ("projet_id");
            Session.get ("scene_id");
            gLargeurColDroite.set (lit_largeur_col_droite ());
            charge_visibilite_inspecteur_projet ();
          });

        tpl.autorun (() =>
          {
            const tProjetId = projet_id_pour_preferences_scene ();
            if (tpl._inspecteurSectionsProjetId === (tProjetId || null)) return;
            tpl._inspecteurSectionsProjetId = tProjetId || null;
            charge_etats_sections_repliables_inspecteur (tProjetId);
          });

        tpl.autorun (() =>
          {
            if (!Meteor.userId ()) return;
            tpl.subscribe ("hub.profiles", null);
            tpl.subscribe ("hub.seeds", null);
            tpl.subscribe ("hub.instructions.recent", 30);
            tpl.subscribe ("hub.files", null);
            tpl.subscribe ("mes_projets");
            tpl.subscribe ("mes_contacts");
            tpl.subscribe ("mes_contacts_projets");
          });
      }

    tpl.autorun (() =>
      {
        const tInfos = infos_svg_scene_courante ();
        const tScene = tInfos.scene;
        const tKey = tInfos.url
          ? `${(tScene && tScene._id) || ""}:${(tScene && tScene.svg_id) || ""}:${tInfos.url}`
          : "";
	        if (tpl._svgObjectRenderSourceKey === tKey) return;
	        tpl._svgObjectRenderSourceKey = tKey;
        tpl._svgObjectReloadCount = 0;
        if (tpl._svgObjectReloadToken) tpl._svgObjectReloadToken.set (null);
        annule_rendu_object_svg ();
        tpl._svgObjectVisible.set (false);
        tpl._svgObjectReady.set (false);
        tpl._svgObjectReadySourceKey = null;
        if (!tInfos.url) return;
        Tracker.afterFlush (() =>
          {
            annule_rendu_object_svg ();
            tpl._svgObjectRenderTimer = setTimeout (() =>
              {
                tpl._svgObjectRenderTimer = null;
	                if (tpl._svgObjectRenderSourceKey !== tKey) return;
                tpl._svgObjectVisible.set (true);
              }, 0);
          });
      });

    if (edition_hub_active ())
      {
        // Réinitialise la prononciation dès que l'on change d'objet sélectionné.
        tpl.autorun (() =>
          {
            gCalqueSelectionne.get ();
            gInspecteurProno.set (null);
            ferme_apercu_video_inspecteur ();
          });

        tpl._inspecteurInteractiviteSceneScrollKey = null;
        tpl.autorun (() =>
          {
            const tOnglet = gOngletDroite.get ();
            const tObjetId = gCalqueSelectionne.get ();
            const tEstBouton = objet_selectionne_est_bouton ();
            const tAction = action_interactivite_objet_selectionne ();
            const tSceneCibleId = scene_cible_interactivite_objet_selectionne ();
            const tKey = (tOnglet === "calques"
              && tObjetId
              && tEstBouton
              && tAction === INTERACTIVITE_ACTION_SCENE
              && tSceneCibleId)
              ? `${tObjetId}:${tSceneCibleId}`
              : "";

            if (tpl._inspecteurInteractiviteSceneScrollKey === tKey) return;
            tpl._inspecteurInteractiviteSceneScrollKey = tKey;
            if (!tKey) return;

            Tracker.afterFlush (() =>
              {
                scrolle_scene_cible_interactivite_selectionnee (tpl, tSceneCibleId);
              });
          });

        tpl._styleInterfaceSourceKey = null;
        tpl._styleInterfaceSceneId = null;
        tpl.autorun (() =>
          {
            const tSceneId = Session.get ("scene_id");
            const tProjetId = Session.get ("projet_id");
            const tOnglet = gOngletDroite.get ();
            const tScene = tSceneId ? Scenes.findOne (tSceneId) : null;
            const tProjet = tProjetId ? Projets.findOne (tProjetId) : null;
            const tBase = normalise_style_interface_scene_client (
              {
                ...STYLE_INTERFACE_DEFAUT,
                ...((tProjet && tProjet.scene_interface_style_default) || {}),
                ...((tScene && tScene.interface_style) || {}),
              }
            );
            const tSourceKey = JSON.stringify ({sceneId : tSceneId || null, projetId : tProjetId || null, base : tBase});

            if (this._styleInterfaceSceneId !== (tSceneId || null))
              {
                this._styleInterfaceSceneId = tSceneId || null;
                gInspecteurStyleMessage.set (null);
                gInspecteurStyleApplyFuture.set (false);
              }

            if (tOnglet !== "scene")
              {
                if (gInspecteurStyleDraft.get () !== null)
                  gInspecteurStyleDraft.set (null);
                gInspecteurStyleApplyFuture.set (false);
                gInspecteurStyleMessage.set (null);
                this._styleInterfaceSourceKey = tSourceKey;
                return;
              }

            if (this._styleInterfaceSourceKey !== tSourceKey
                || gInspecteurStyleDraft.get () === null)
              {
                gInspecteurStyleDraft.set (tBase);
                this._styleInterfaceSourceKey = tSourceKey;
              }
          });

        tpl.autorun (() =>
          {
            const tProjetId = Session.get ("projet_id");
            const tProjet = tProjetId ? Projets.findOne (tProjetId) : null;
            if (this._inspecteurProjetId !== (tProjetId || null))
              {
                this._inspecteurProjetId = tProjetId || null;
                gInspecteurProjetSousOnglet.set ("reglages");
                reinitialise_inspecteur_projet ();
                reinitialise_dialogue_participants_projet ();
                inspecteur_jauge_visuelle_close ();
              }
            if (!tProjetId)
              {
                reinitialise_inspecteur_projet ();
                inspecteur_projet_dialog_close ();
                inspecteur_jauge_visuelle_close ();
                return;
              }

            if (!tProjet) return;

            if (gInspecteurProjetNom.get () === null) gInspecteurProjetNom.set (tProjet.nom || "");
            if (gInspecteurProjetSlug.get () === null) gInspecteurProjetSlug.set (tProjet.slug || "");
          });

        tpl.autorun (() =>
          {
            const tSceneId = Session.get ("scene_id");
            const tCarteId = gCarteSelectionnee.get ();
            const tCarte = (tSceneId && tCarteId) ? Cartes.findOne ({_id : tCarteId, scene_id : tSceneId}) : null;

            if (tCarteId && !tCarte)
              {
                gCarteSelectionnee.set (null);
                gCarteMode.set (CARTE_MODE_LISTE);
                gCarteBrouillon.set (null);
                reinitialise_selection_arbre_carte ();
                return;
              }

            if (gCarteMode.get () === CARTE_MODE_EDITEUR && tCarte)
              {
                const tBrouillon = brouillon_carte_courant ();
                if (!tBrouillon || tBrouillon.carte_id !== tCarte._id)
                  initialise_brouillon_carte_depuis_doc (tCarte);
              }
          });

        tpl.autorun (() =>
          {
            const tSceneId = Session.get ("scene_id");
            const tCarteId = gBulleSelectionnee.get ();
            const tCarte = (tSceneId && tCarteId) ? Cartes.findOne ({_id : tCarteId, scene_id : tSceneId}) : null;

            if (tCarteId && !tCarte)
              {
                reinitialise_selection_bulle_calque ();
                return;
              }

            if (tCarte)
              {
                const tBrouillon = brouillon_bulle_courant ();
                if (!tBrouillon || tBrouillon.carte_id !== tCarte._id)
                  gBulleBrouillon.set (fabrique_brouillon_bulle_depuis_carte (tCarte));
              }
          });
      }

    tpl.autorun (() =>
      {
        mode_lecture_scene_actif ();
        Session.get ("scene_id");
        synchronise_ambiance_lecture_scene ();
      });

    tpl.autorun (() =>
      {
        const tLectureActive = mode_lecture_scene_actif ();
        gLectureSceneFocusCarte.get ();
        gOngletDroite.get ();
        if (!tLectureActive) gBulleSelectionnee.get ();
        signature_ids_masques_carte_client (cartes_ids_masques_effectifs (tpl));
        Tracker.afterFlush (() =>
          {
            synchronise_items_interface_svg_tpl (tpl);
            applique_masquages_carte_tpl (tpl);
          });
      });

    if (!edition_hub_active ())
      {
        tpl.autorun (() =>
          {
            const tSceneId = Session.get ("scene_id");
            const tScene = tSceneId ? Scenes.findOne (tSceneId) : null;
            const tItems = items_interface_scene (tScene);
            const tObjets = Array.isArray (tScene && tScene.objets_config) ? tScene.objets_config : [];
            const tConnecte = utilisateur_connecte_diffusion_scene ();
            const tSignature = signature_actions_scene_verrouillees_diffusion_client ([...tObjets, ...tItems]);
            void tConnecte;
            void tSignature;
            Tracker.afterFlush (() =>
              {
                synchronise_items_interface_svg_tpl (tpl);
                applique_actions_scene_verrouillees_calques_svg_tpl (tpl);
              });
          });
      }

    tpl.autorun (() =>
      {
        const tLectureActive = mode_lecture_scene_actif ();
        const tLectureFocusCarteId = gLectureSceneFocusCarte.get ();
        const tOnglet = gOngletDroite.get ();
        const tBullesVisibles = bulles_scene_visibles ();
        const tBullesVerrouillees = categorie_scene_verrouillee (CALQUES_CATEGORIE_BULLES);
        const tCarteEditorActive = carte_editor_est_actif ();
        const tCarteId = tCarteEditorActive ? gCarteSelectionnee.get () : null;
        const tMode = gCarteMode.get ();
        const tBrouillon = tCarteEditorActive ? brouillon_carte_courant () : null;
        const tBulleSelectionnee = tLectureActive ? null : gBulleSelectionnee.get ();
        const tBrouillonBulle = tLectureActive ? null : brouillon_bulle_courant ();
        const tCartesFusionnees = cartes_scene_liste ()
          .map ((pCarte) =>
            fusionne_carte_avec_brouillon_bulle_client (
              fusionne_carte_avec_brouillon_client (pCarte, tBrouillon),
              tBrouillonBulle
            )
          );
        const tPlanBulles = planifie_affectations_bulles_projet_scene_client (
          tCartesFusionnees,
          {autogenLegacy : true}
        );
        const tSignature = tCartesFusionnees
          .map ((pCarte) => signature_decoration_carte_client (
            pCarte,
            {planBulles : tPlanBulles, seed : pCarte && pCarte._id}
          ))
          .join ("\n");
        void tOnglet;
        void tBullesVisibles;
        void tBullesVerrouillees;
        void tCarteId;
        void tMode;
        void tCarteEditorActive;
        void tBulleSelectionnee;
        void tLectureActive;
        void tLectureFocusCarteId;
        void tSignature;
        Tracker.afterFlush (() => { applique_decorations_cartes_tpl (tpl); });
      });

    tpl.autorun (() =>
      {
        const tOnglet = gOngletDroite.get ();
        if (tOnglet !== "calques") return;
        Tracker.afterFlush (() =>
          {
            if (!tpl._svgHoverId) return;
            if (carte_id_depuis_hover_info_bulle (tpl._svgHoverId)) return;
            efface_hover_svg (tpl);
          });
      });

    tpl.autorun (() =>
      {
        const tSceneId = Session.get ("scene_id");
        const tSignature = [
          ...CATEGORIES_PERTINENTES,
          CALQUES_CATEGORIE_BULLES,
        ].map ((pNom) =>
          `${pNom}:${categorie_scene_visible (pNom, tSceneId) ? 1 : 0}:${categorie_scene_verrouillee (pNom, tSceneId) ? 1 : 0}`
        ).join ("|");
        void tSignature;
        synchronise_selection_scene_avec_categories ();
        Tracker.afterFlush (() =>
          {
            applique_etats_categories_svg_tpl (tpl);
            if (tpl._svgDoc)
              applique_hover_svg (tpl._svgDoc, tpl._svgHoverId || null);
          });
      });
      
    tpl.autorun (() =>
      {
        const tSceneId = Session.get ("scene_id");
        if (!tSceneId) return;
        tpl.subscribe ("une_scene", { scene_id : tSceneId }, function onReady ()
          {
          });
      });
  });

Template.scene.onDestroyed (function ()
  {
    const tTransitionSceneEnCours = gLectureSceneTransition.get ();
    nettoie_items_hype_tpl (this);
    nettoie_apercu_contour_objet_selectionne_scene ();
    arrete_flottement_bulles_lecture_tpl (this);
	    if (this._svgObjectRenderTimer)
	      clearTimeout (this._svgObjectRenderTimer);
	    annule_suivi_chargement_svg_tpl (this);
	    nettoie_svg_inline_scene_host (this._svgObjectNode);
    if (this._inspecteurProjetViewportResize)
      window.removeEventListener ("resize", this._inspecteurProjetViewportResize);
    if (this._sceneHelpViewportResize)
      window.removeEventListener ("resize", this._sceneHelpViewportResize);
    if (this._lectureViewportResizeHandler)
      window.removeEventListener ("resize", this._lectureViewportResizeHandler);
    if (this._sceneFullscreenChangeHandler)
      {
        EVENEMENTS_CHANGEMENT_PLEIN_ECRAN_SCENE.forEach ((pEventName) =>
          document.removeEventListener (pEventName, this._sceneFullscreenChangeHandler)
        );
      }
    if (this._sceneFullscreenResizeHandler)
      window.removeEventListener ("resize", this._sceneFullscreenResizeHandler);
    if (this._lectureViewportRefreshRaf)
      cancelAnimationFrame (this._lectureViewportRefreshRaf);
    if (this._sceneFullscreenSyncRaf)
      cancelAnimationFrame (this._sceneFullscreenSyncRaf);
    if (this._sceneFullscreenSyncTimer)
      clearTimeout (this._sceneFullscreenSyncTimer);
    this._lectureViewportRefreshRaf = null;
    this._sceneFullscreenSyncRaf = null;
    this._sceneFullscreenSyncTimer = null;
    this._sceneFullscreenPending = false;
    this._sceneFullscreenElement = null;
    desactive_classe_scene_plein_ecran (
      this.find && this.find (".js_scene_fullscreen_runtime"),
      {preserverTransitionScene : tTransitionSceneEnCours}
    );
    if (this._inspecteurBoutonPressDown)
      {
        document.removeEventListener ("pointerdown", this._inspecteurBoutonPressDown, true);
        document.removeEventListener ("mousedown", this._inspecteurBoutonPressDown, true);
        document.removeEventListener ("touchstart", this._inspecteurBoutonPressDown, true);
      }
    if (this._inspecteurProjetBullesContextMenuPointerDown)
      document.removeEventListener ("pointerdown", this._inspecteurProjetBullesContextMenuPointerDown);
    if (this._inspecteurProjetBullesContextMenuKeydown)
      document.removeEventListener ("keydown", this._inspecteurProjetBullesContextMenuKeydown);
    if (this._inspecteurProjetBullesContextMenuResize)
      window.removeEventListener ("resize", this._inspecteurProjetBullesContextMenuResize);
    if (this._inspecteurBulleMenusPointerDown)
      document.removeEventListener ("pointerdown", this._inspecteurBulleMenusPointerDown);
    if (this._inspecteurBulleMenusKeydown)
      document.removeEventListener ("keydown", this._inspecteurBulleMenusKeydown);
    annule_appui_long_inspecteur_projet_bulles ();
    ferme_menu_contextuel_inspecteur_projet_bulles ();
    ferme_menus_locaux_inspecteur_bulle_client ();
    if (this._inspecteurHypePreviewHosts)
      Object.values (this._inspecteurHypePreviewHosts).forEach ((pHost) => nettoie_widget_hype_direct (pHost));
    this._inspecteurHypePreviewHosts = null;
    if (this._svgHoverGuardCleanup) this._svgHoverGuardCleanup ();
    this._svgHoverGuardCleanup = null;
    this._svgHoverGuardTarget = null;
    nettoie_observateur_cadre_scene_tpl (this);
    if (this._etiquetteHover && this._etiquetteHover.parentNode)
      this._etiquetteHover.parentNode.removeChild (this._etiquetteHover);
    this._etiquetteHover = null;
    detache_raccourci_espace_cartes_doc (this._svgDoc, this._cartesSpaceHandler);
    if (this._calquesEscHandler)
      {
        document.removeEventListener ("keydown", this._calquesEscHandler);
        if (this._svgDoc)
          this._svgDoc.removeEventListener ("keydown", this._calquesEscHandler);
      }
    if (gCalquesCartesReorder.tpl === this) calques_cartes_reorder_cleanup ();
    if (gCarteObjReorder.tpl === this) carte_obj_reorder_cleanup ();
    if (gCategoriesCalqueReorder.tpl === this) categories_calques_reorder_cleanup ();
    gInspecteurStyleDraft.set (null);
    gInspecteurStyleApplyFuture.set (false);
    gInspecteurStyleMessage.set (null);
    gInspecteurStyleSaving.set (null);
    this._bboxParId = null;
    reinitialise_inspecteur_projet ();
    reinitialise_dialogue_participants_projet ();
    gInspecteurProjetDialogCogibulle.hideWithoutPersist ();
    gInspecteurProjetJaugeVisuelleCogibulle.hideWithoutPersist ();
    if (typeof rec_cleanup_dialog_runtime === "function")
      rec_cleanup_dialog_runtime ();
    if (typeof gRecorderCogibulle !== "undefined" && gRecorderCogibulle && typeof gRecorderCogibulle.hideWithoutPersist === "function")
      gRecorderCogibulle.hideWithoutPersist ();
    gSceneHelpCogibulle.hideWithoutPersist ();
    arrete_ambiance_lecture_scene ();
    if (this._svgDoc)
      {
        applique_masquages_carte_svg (this._svgDoc, []);
        retire_decorations_cartes_racine (this._svgDoc.documentElement);
      }
    restaure_fond_masque_lecture_scene_tpl (this);
    reinitialise_transitions_lecture_tpl (this);
    if (gSceneTemplateActif === this) gSceneTemplateActif = null;
    if (mode_lecture_scene_actif ())
      {
        commande_items_hype_tpl (this, "pause-edition");
        commande_items_svg_inline_tpl (this, "pause-edition");
        gLectureSceneActive.set (false);
        gLectureSceneFocusCarte.set (null);
        if (!tTransitionSceneEnCours)
          annule_transition_lecture_scene ();
        arrete_audio_lecture_scene ();
      }
    reinitialise_etat_cartes_scene ();
  });

Template.scene.onRendered (function ()
  {
    const tTpl = this;
    gSceneTemplateActif = tTpl;
    synchronise_plein_ecran_externe_scene_tpl (
      tTpl,
      tTpl.find && tTpl.find (".js_scene_fullscreen_runtime")
    );

    tTpl._lectureAudioInterfaceSignature = null;
    tTpl.autorun (() =>
      {
        if (!mode_lecture_scene_actif ()) return;
        const tSignature = signature_audios_interface_lecture_scene ();
        if (tTpl._lectureAudioInterfaceSignature === tSignature) return;
        tTpl._lectureAudioInterfaceSignature = tSignature;
        prepare_audios_interface_lecture_scene ();
      });

    if (edition_hub_active ())
      {
        tTpl._inspecteurBoutonPressDown = (pEvent) =>
          {
            marque_bouton_inspecteur_presse (bouton_inspecteur_depuis_evenement (pEvent));
          };
        document.addEventListener ("pointerdown", tTpl._inspecteurBoutonPressDown, true);
        document.addEventListener ("mousedown", tTpl._inspecteurBoutonPressDown, true);
        document.addEventListener ("touchstart", tTpl._inspecteurBoutonPressDown, true);

        tTpl._inspecteurHypePreviewHosts = {};
        ["recompense_carte", "recompense_scene"].forEach ((pType) =>
          {
            tTpl.autorun (() =>
              {
                const tWidget = widget_hype_projet (pType);
                const tSignature = tWidget ? `${tWidget._id}:${tWidget.updatedAt || ""}` : "";
                void tSignature;
                Tracker.afterFlush (() =>
                  {
                    const tHost = tTpl.find ('.js_inspecteur_hype_preview_host[data-type="' + pType + '"]');
                    if (!tHost)
                      {
                        if (tTpl._inspecteurHypePreviewHosts[pType]) nettoie_widget_hype_direct (tTpl._inspecteurHypePreviewHosts[pType]);
                        tTpl._inspecteurHypePreviewHosts[pType] = null;
                        return;
                      }
                    tTpl._inspecteurHypePreviewHosts[pType] = tHost;
                    if (!tWidget)
                      {
                        nettoie_widget_hype_direct (tHost);
                        return;
                      }
                    if (tHost._hubHypeWidgetSignature === tSignature) return;
                    tHost._hubHypeWidgetSignature = tSignature;
                    monte_widget_hype_direct (tHost, tWidget, {instanceKey : "inspecteur_" + pType})
                      .catch ((pErr) =>
                        {
                          console.warn ("Aperçu Hype impossible", pErr);
                          affiche_erreur_hype_direct (tHost, pErr);
                        });
                  });
              });
          });

        tTpl._inspecteurProjetBullesContextMenuPointerDown = (pEvent) =>
          {
            if (!gInspecteurProjetBullesContextMenu.get ()) return;
            const tCible = pEvent && pEvent.target;
            const tMenu = tTpl.find (".js_inspecteur_projet_bulles_context_menu");
            if (tMenu && tCible && typeof tMenu.contains === "function" && tMenu.contains (tCible)) return;
            ferme_menu_contextuel_inspecteur_projet_bulles ();
          };

        tTpl._inspecteurProjetBullesContextMenuKeydown = (pEvent) =>
          {
            if (!pEvent || pEvent.defaultPrevented) return;
            if (pEvent.key !== "Escape") return;
            if (!gInspecteurProjetBullesContextMenu.get ()) return;
            pEvent.preventDefault ();
            ferme_menu_contextuel_inspecteur_projet_bulles ();
          };

        tTpl._inspecteurProjetBullesContextMenuResize = () => ferme_menu_contextuel_inspecteur_projet_bulles ();
        document.addEventListener ("pointerdown", tTpl._inspecteurProjetBullesContextMenuPointerDown);
        document.addEventListener ("keydown", tTpl._inspecteurProjetBullesContextMenuKeydown);
        window.addEventListener ("resize", tTpl._inspecteurProjetBullesContextMenuResize);

        tTpl._inspecteurBulleMenusPointerDown = (pEvent) =>
          {
            if (!menus_locaux_inspecteur_bulle_ouverts_client ()) return;
            const tCible = pEvent && pEvent.target;
            if (tCible && typeof tCible.closest === "function"
                && tCible.closest (".inspecteur_bulle_svg_picker, .inspecteur_bulle_color_picker")) return;
            ferme_menus_locaux_inspecteur_bulle_client ();
          };

        tTpl._inspecteurBulleMenusKeydown = (pEvent) =>
          {
            if (!pEvent || pEvent.defaultPrevented) return;
            if (pEvent.key !== "Escape") return;
            if (!menus_locaux_inspecteur_bulle_ouverts_client ()) return;
            pEvent.preventDefault ();
            if (typeof pEvent.stopImmediatePropagation === "function") pEvent.stopImmediatePropagation ();
            else if (typeof pEvent.stopPropagation === "function") pEvent.stopPropagation ();
            ferme_menus_locaux_inspecteur_bulle_client ();
          };

        document.addEventListener ("pointerdown", tTpl._inspecteurBulleMenusPointerDown);
        document.addEventListener ("keydown", tTpl._inspecteurBulleMenusKeydown);
      }

    tTpl.autorun (() =>
      {
        Session.get ("scene_id");
        const tInfosSvg = infos_svg_scene_courante ();
        mode_lecture_scene_actif ();
        const tVisible = tTpl._svgObjectVisible && tTpl._svgObjectVisible.get ();
        void tInfosSvg.url;
        void tVisible;
        Tracker.afterFlush (() =>
          {
            synchronise_observateur_cadre_scene_tpl (tTpl);
          });
      });

    tTpl.autorun (() =>
      {
        Session.get ("scene_id");
        const tProjet = projet_courant_scene ();
        const tRapportCss = format_css_rapport_lh_scene_projet (tProjet && tProjet.rapport_lh_scene);
        const tGeometrieSignature = signature_geometrie_scene_previsualisee ();
        void tRapportCss;
        void tGeometrieSignature;
        Tracker.afterFlush (() =>
          {
            if (!tTpl._svgDoc) return;
            actualise_rapport_lh_scene_tpl (tTpl);
          });
      });

    tTpl.autorun (() =>
      {
        const tSceneId = Session.get ("scene_id");
        const tLectureActive = mode_lecture_scene_actif ();
        if (!tSceneId || !tLectureActive) return;
        gLectureSceneFocusCarte.set (null);
        reinitialise_transitions_lecture_tpl (tTpl);
        arrete_audio_lecture_scene ();
        Tracker.afterFlush (() => { rafraichit_scene_lecture_tpl (tTpl, true); });
      });

    tTpl.autorun (() =>
      {
        Session.get ("scene_id");
        mode_lecture_scene_actif ();
        if (tTpl._svgObjectReady) tTpl._svgObjectReady.get ();
        consomme_navigation_interne_morphing_si_prete (tTpl);
      });

    if (edition_hub_active ())
      {
        tTpl._cartesSpaceHandler = (pEvent) =>
          {
            if (!carte_arbre_est_actif ()) return;
            if (!pEvent || pEvent.defaultPrevented || pEvent.repeat) return;
            if (pEvent.altKey || pEvent.ctrlKey || pEvent.metaKey) return;
            if (pEvent.code !== "Space" && pEvent.key !== " ") return;
            if (evenement_vise_controle_edition (pEvent)) return;
            const tId = gCarteArbreSelectionne.get ();
            if (!tId) return;
            pEvent.preventDefault ();
            cartes_toggle_masquage_brouillon (tId);
          };
        attache_raccourci_espace_cartes_doc (document, tTpl._cartesSpaceHandler);

        tTpl._calquesEscHandler = (pEvent) =>
          {
            if (!pEvent || pEvent.defaultPrevented || pEvent.repeat) return;
            if (pEvent.key !== "Escape") return;
            if (gOngletDroite.get () !== "calques") return;
            const tCible = pEvent.target;
            if (tCible && typeof tCible.closest === "function"
                && tCible.closest ("input, textarea, select, [contenteditable]")) return;
            pEvent.preventDefault ();
            efface_selection_calques_scene ();
          };
        document.addEventListener ("keydown", tTpl._calquesEscHandler);
      }

    if (edition_hub_active ())
      {
        tTpl.autorun (() =>
          {
            const tOnglet = gOngletDroite.get ();
            const tMode = gCarteMode.get ();
            categorie_scene_ouverte (CALQUES_CATEGORIE_BULLES);
            const tSceneId = Session.get ("scene_id");
            const tOrdreCategoriesSignature = ordre_categories_calques_scene (tSceneId).join ("|");
            const tSignature = tSceneId
              ? Cartes.find ({scene_id : tSceneId}, {sort : {ordre : 1}, fields : {_id : 1, ordre : 1}})
                  .fetch ()
                  .map ((pCarte) => `${pCarte._id}:${pCarte.ordre}`)
                  .join ("|")
              : "";
            void tOnglet;
            void tMode;
            void tOrdreCategoriesSignature;
            void tSignature;
            Tracker.afterFlush (() =>
              {
                calques_cartes_reorder_sync (tTpl);
                categories_calques_reorder_sync (tTpl);
                applique_selection_arbre_calques_tpl (tTpl);
              });
          });

        tTpl.autorun (() =>
          {
            const tLectureActive = mode_lecture_scene_actif ();
            gOngletDroite.get ();
            gCarteMode.get ();
            if (!tLectureActive) gBulleSelectionnee.get ();
            gCarteCategoriesOuvertes.get ("obj");
            if (tLectureActive) return;
            const tSceneId = Session.get ("scene_id");
            const tScene = tSceneId ? Scenes.findOne (tSceneId) : null;
            const tObjets = svg_objets_scene_normalises (tScene);
            const tObjetsSignature = tObjets && Array.isArray (tObjets.obj)
              ? tObjets.obj.map ((pObj) => pObj && pObj.id).filter (Boolean).join ("|")
              : "";
            const tBrouillon = carte_arbre_brouillon_courant ();
            const tVueSignature = tBrouillon && tBrouillon.vue
              ? `${tBrouillon.vue.x}:${tBrouillon.vue.y}:${tBrouillon.vue.w}:${tBrouillon.vue.h}`
              : "";
            const tOrdreSignature = signature_ordre_objets_carte_client (tBrouillon && tBrouillon.ordre_objets);
            void tObjetsSignature;
            void tVueSignature;
            void tOrdreSignature;
            Tracker.afterFlush (() => { carte_obj_reorder_sync (tTpl); });
          });
      }

    if (edition_hub_active ())
      {
        this.autorun (() =>
          {
            const tSceneId = Session.get ("scene_id");
            const tProjetId = Session.get ("projet_id");
            const tLectureActive = mode_lecture_scene_actif ();
            const tInfos = infos_svg_scene_courante ();
            if (!tSceneId || !tProjetId) return;
            gInspecteurProjetDialogCogibulle.restoreIfPossible ((pOptions) =>
              {
                if (!utilisateur_peut_gerer_participants_projet_inspecteur ()) return false;
                return inspecteur_projet_dialog_open (pOptions);
              });
            gRecorderCogibulle.restoreIfPossible (() => rec_open_dialog_opts ({fromRestore : true}));
            gInspecteurProjetJaugeVisuelleCogibulle.reloadRestoreRequest ();
            gInspecteurProjetJaugeVisuelleCogibulle.restoreIfPossible ((pOptions) =>
              {
                if (!utilisateur_peut_modifier_projet_inspecteur ()) return false;
                return inspecteur_jauge_visuelle_open (pOptions);
              });
            gSceneHelpCogibulle.reloadRestoreRequest ();
            if (!tInfos.url || tLectureActive) return;
            gSceneHelpCogibulle.restoreIfPossible (() => scene_help_open_dialog ({fromRestore : true}));
          });

        this.autorun (() =>
          {
            const tOpen = gInspecteurProjetDialogOpen.get ();
            if (!tOpen) return;
            Tracker.afterFlush (() => { inspecteur_projet_dialog_apply_box (); });
          });

        this.autorun (() =>
          {
            const tOpen = gInspecteurProjetJaugeVisuelleOpen.get ();
            if (!tOpen) return;
            Tracker.afterFlush (() => { gInspecteurProjetJaugeVisuelleCogibulle.applyBox (); });
          });

        this._inspecteurProjetViewportResize = () =>
          {
            gInspecteurProjetDialogCogibulle.syncToViewport ();
            gInspecteurProjetJaugeVisuelleCogibulle.syncToViewport ();
          };
        window.addEventListener ("resize", this._inspecteurProjetViewportResize);
        this._sceneHelpViewportResize = () =>
          {
            gSceneHelpCogibulle.syncToViewport ();
          };
        window.addEventListener ("resize", this._sceneHelpViewportResize);
      }

    // Construit en continu l'ensemble des id pertinents pour la scène
    // courante, et garde un autorun pour appliquer la surbrillance dans
    // le SVG dès que la sélection change.
    if (edition_hub_active ())
      detache_raccourci_espace_cartes_doc (document, this._cartesSpaceHandler);
    tTpl.autorun (() =>
      {
        const tId = Session.get ("scene_id");
        if (!tId) return;
        const tScene = Scenes.findOne (tId);
        const tObjets = svg_objets_scene_normalises (tScene);
        const tIdsPertinents = new Set ();
        const tDataNames     = new Map ();
        const tIdsParCat     = new Map ();
        if (tObjets)
          {
            for (const tNom of CATEGORIES_PERTINENTES)
              {
                const tArr = tObjets[tNom];
                if (!Array.isArray (tArr)) continue;
                const tListe = [];
                for (const c of tArr)
                  if (c && c.id)
                    {
                      tIdsPertinents.add (c.id);
                      tListe.push (c.id);
                      if (typeof c["data-name"] === "string")
                        tDataNames.set (c.id, c["data-name"]);
                    }
                tIdsParCat.set (tNom, tListe);
              }
          }
        const tItemsInterface = items_interface_scene (tScene);
        if (tItemsInterface.length)
          {
            const tListe = [];
            for (const tItem of tItemsInterface)
              {
                if (!tItem || !tItem.id) continue;
                tListe.push (tItem.id);
                tDataNames.set (tItem.id, CALQUES_CATEGORIE_INTERFACE + ", " + (tItem.item_nom || tItem.nom || "item"));
              }
            tIdsParCat.set (CALQUES_CATEGORIE_INTERFACE, tListe);
          }
        tTpl._idsPertinents   = tIdsPertinents;
        tTpl._dataNamesParId  = tDataNames;
        tTpl._idsParCategorie = tIdsParCat;
        Tracker.afterFlush (() => synchronise_items_interface_svg_tpl (tTpl));
      });

    tTpl.autorun (() =>
      {
        const tLectureActive = mode_lecture_scene_actif ();
        const tSelectionCartesActive = carte_arbre_est_actif ();
        const tSel = tSelectionCartesActive
          ? gCarteArbreSelectionne.get ()
          : gCalqueSelectionne.get ();
        const tSelCat = tSelectionCartesActive
          ? gCarteArbreCategorieSelectionnee.get ()
          : gCategorieSelectionnee.get ();
        const tSelBulle = bulle_selection_scene_active ();
        const tBullesVerrouillees = categorie_scene_verrouillee (CALQUES_CATEGORIE_BULLES);
        signature_style_interface_scene_previsualise ();
        void tBullesVerrouillees;
        const tDoc    = tTpl._svgDoc;
        if (tDoc)
          {
            let tIdsCible;
            if (tSelectionCartesActive)
              tIdsCible = cartes_ids_surbrillance_scene (tTpl);
            else if (tSel)
              tIdsCible = [tSel];
            else if (tSelCat && tTpl._idsParCategorie)
              tIdsCible = tTpl._idsParCategorie.get (tSelCat) || [];
            else
              tIdsCible = [];
            applique_surbrillance_svg (tDoc, tLectureActive ? [] : tIdsCible);
            dessine_surbrillance_categorie_bulles_svg (
              tDoc,
              tLectureActive ? [] : cartes_ids_surbrillance_categorie_calques_scene ()
            );
            dessine_selection_bulle_carte (tDoc, tLectureActive ? null : tSelBulle);
            applique_hover_svg (tDoc, tLectureActive ? null : (tTpl._svgHoverId || null));
          }

        // Scrolle la ligne sélectionnée dans l'arbre.
        const tCible = tSel
          ? `.arbre_calque[data-id="${echappe_selecteur_css_svg (tSel)}"]`
          : (tSelBulle
            ? `.arbre_calque[data-bulle-carte-id="${echappe_selecteur_css_svg (tSelBulle)}"]`
            : (tSelCat ? `.arbre_categorie[data-categorie="${echappe_selecteur_css_svg (tSelCat)}"]` : null));
        Tracker.afterFlush (() =>
          {
            applique_selection_arbre_calques_tpl (tTpl);
            if (!tCible) return;
            const tLi = tTpl.find (tCible);
            if (tLi && typeof tLi.scrollIntoView === "function")
              tLi.scrollIntoView ({block : "nearest", behavior : "auto"});
          });
      });

    tTpl.autorun (() =>
      {
        mode_lecture_scene_actif ();
        gCategorieSelectionnee.get ();
        const tCategorie = gCategorieSelectionnee.get ();
        if (contour_categorie_supportee (tCategorie))
          reglages_survol_contour_categorie_brouillon (tCategorie);
        const tSceneId = Session.get ("scene_id");
        const tScene = tSceneId ? Scenes.findOne (tSceneId) : null;
        void (tScene && tScene.objets_config);
        Tracker.afterFlush (() =>
          {
            if (!tTpl._svgDoc || mode_lecture_scene_actif ())
              {
                nettoie_apercu_contours_categorie_tpl (tTpl);
                return;
              }
            affiche_apercu_contours_categorie_tpl (tTpl);
          });
      });

	    // Observe l'apparition du host SVG inline et charge son DOM dans la page.
	    tTpl.autorun (() =>
	      {
	        const tId = Session.get ("scene_id");
	        const tScene = tId && Scenes.findOne (tId);
	        const tInfosSvg = infos_svg_scene_courante (tId);
	        const tSourceUrl = tInfosSvg.url;
	        // Force la dépendance sur svg_id et URL pour ré-attacher après changement.
	        const tSvgId = tInfosSvg.runtime
	          ? (tScene && tScene.svg_runtime_id)
	          : (tScene && tScene.svg_id);
	        const tObjectVisible = tTpl._svgObjectVisible && tTpl._svgObjectVisible.get ();
	        void tSourceUrl;
	        void tObjectVisible;

        Tracker.afterFlush (() =>
          {
            if (Session.get ("scene_id") !== tId) return;
	            const tObj = tTpl.find (".contenu_svg_scene");
	            if (!tObj)
	              {
	                nettoie_svg_inline_scene_host (tTpl._svgObjectNode);
	                tTpl._svgObjectNode = null;
	                tTpl._svgObjectSvgId = null;
	                tTpl._svgObjectSourceKey = null;
	                tTpl._svgObjectLoadHandler = null;
	                reinitialise_etat_document_svg_tpl (tTpl);
                if (tTpl._svgHoverGuardCleanup) tTpl._svgHoverGuardCleanup ();
                tTpl._svgHoverGuardCleanup = null;
                tTpl._svgHoverGuardTarget = null;
                return;
              }
            if (tTpl._svgHoverGuardTarget !== tObj)
              {
                if (tTpl._svgHoverGuardCleanup) tTpl._svgHoverGuardCleanup ();
                const tSceneHost = tObj.closest (".scene") || tObj;
                const tClear = () =>
                  {
                    if (tTpl._svgBubbleDragCarteId) return;
                    efface_hover_svg (tTpl);
                  };
                const tGuard = (e) =>
                  {
                    if (!tTpl._svgHoverId) return;
                    const tRect = tSceneHost.getBoundingClientRect ();
                    if (e.clientX < tRect.left || e.clientX > tRect.right
                        || e.clientY < tRect.top || e.clientY > tRect.bottom)
                      tClear ();
                  };
                tObj.addEventListener ("pointerleave", tClear);
                tObj.addEventListener ("mouseleave",   tClear);
                tSceneHost.addEventListener ("pointerleave", tClear);
                tSceneHost.addEventListener ("mouseleave",   tClear);
                document.addEventListener ("pointermove", tGuard, true);
                window.addEventListener ("blur", tClear);
                tTpl._svgHoverGuardCleanup = () =>
                  {
                    tObj.removeEventListener ("pointerleave", tClear);
                    tObj.removeEventListener ("mouseleave",   tClear);
                    tSceneHost.removeEventListener ("pointerleave", tClear);
                    tSceneHost.removeEventListener ("mouseleave",   tClear);
                    document.removeEventListener ("pointermove", tGuard, true);
                    window.removeEventListener ("blur", tClear);
                  };
                tTpl._svgHoverGuardTarget = tObj;
              }

	            const tSourceObjet = (tObj.getAttribute && (tObj.getAttribute ("data-svg-url") || tObj.getAttribute ("data")))
	              || tSourceUrl
	              || "";
	            const tRuntimeSvg = /(?:[?&])cogi_svg_runtime=1(?:&|$)/.test (String (tSourceObjet || ""));
	            const tSourceKey = `${tId || ""}:${tSvgId || ""}:${tRuntimeSvg ? "runtime" : "source"}:${url_absolue_scene_svg_client (tSourceObjet)}`;
            if (Session.get ("scene_id") !== tId) return;
	            const tObjetSvgChange = tTpl._svgObjectNode !== tObj
	              || (tTpl._svgObjectSourceKey || null) !== tSourceKey;
	            if (tObjetSvgChange)
	              {
	                reinitialise_etat_document_svg_tpl (tTpl);
	                tTpl._svgObjectNode = tObj;
	                tTpl._svgObjectSvgId = tSvgId || null;
	                tTpl._svgObjectSourceKey = tSourceKey;
	                tTpl._svgObjectLoadHandler = null;
	              }
            else if (tTpl._svgObjectLoadHandler)
              {
                return;
              }

	            const tHandler = () =>
	              {
                  if (Session.get ("scene_id") !== tId) return;
                  if (tTpl._svgObjectNode !== tObj) return;
                  if ((tTpl._svgObjectSourceKey || null) !== tSourceKey) return;
	                const tLoadSeq = (tTpl._svgInlineLoadSeq || 0) + 1;
	                tTpl._svgInlineLoadSeq = tLoadSeq;
                  if (tTpl._svgInlineAbort && typeof tTpl._svgInlineAbort.abort === "function")
                    {
                      try { tTpl._svgInlineAbort.abort (); } catch (e) {}
                    }
                  const tAbort = typeof AbortController !== "undefined"
                    ? new AbortController ()
                    : null;
                  tTpl._svgInlineAbort = tAbort;
	                charge_svg_inline_scene_host (
                    tObj,
                    tSourceObjet,
                    {
                      ...(tAbort ? {signal : tAbort.signal} : {}),
                      valideRemplacement : () =>
                        tTpl._svgInlineLoadSeq === tLoadSeq
                        && tTpl._svgObjectNode === tObj
                        && (tTpl._svgObjectSourceKey || null) === tSourceKey,
                    }
                  )
	                  .then ((tDoc) =>
	                    {
	                      if (!tDoc || tTpl._svgInlineLoadSeq !== tLoadSeq) return;
	                      if (tTpl._svgObjectNode !== tObj) return;
	                      if ((tTpl._svgObjectSourceKey || null) !== tSourceKey) return;
	                      tTpl._svgObjectReloadCount = 0;
	                      annule_suivi_chargement_svg_tpl (tTpl);
	                      if (tTpl._svgDoc && tTpl._svgDoc !== tDoc)
	                        {
	                          detache_raccourci_espace_cartes_doc (tTpl._svgDoc, tTpl._cartesSpaceHandler);
	                          if (tTpl._calquesEscHandler)
	                            tTpl._svgDoc.removeEventListener ("keydown", tTpl._calquesEscHandler);
                            neutralise_ressources_svg_scene_doc (tTpl._svgDoc);
	                        }
	                      tTpl._svgDoc = tDoc;
	                      tTpl._svgDocSceneId = tId;
	                      attache_raccourci_espace_cartes_doc (tDoc, tTpl._cartesSpaceHandler);
	                      if (tTpl._calquesEscHandler)
	                        tDoc.addEventListener ("keydown", tTpl._calquesEscHandler);
	                      attache_interactions_svg (tDoc, tTpl);
	                      maj_cadre_scene_dimensions_tpl (tTpl);
	                      synchronise_items_interface_svg_tpl (tTpl);
	                      reconstruit_cache_bbox_svg (tTpl);
                      if (tRuntimeSvg) indexe_decors_calques_svg_doc (tDoc);
                      applique_reglages_calques_svg_tpl (tTpl);
	                      applique_etats_categories_svg_tpl (tTpl);
	                      const tSelCalque = gCalqueSelectionne.get ();
	                      const tSelCategorie = gCategorieSelectionnee.get ();
	                      const tIdsSurbrillance = carte_arbre_est_actif ()
	                        ? cartes_ids_surbrillance_scene (tTpl)
	                        : (tSelCalque
	                          ? [tSelCalque]
	                          : ((tSelCategorie && tTpl._idsParCategorie)
	                            ? (tTpl._idsParCategorie.get (tSelCategorie) || [])
	                            : []));
	                      applique_surbrillance_svg (tDoc, mode_lecture_scene_actif () ? [] : tIdsSurbrillance);
	                      dessine_surbrillance_categorie_bulles_svg (
	                        tDoc,
	                        mode_lecture_scene_actif () ? [] : cartes_ids_surbrillance_categorie_calques_scene ()
	                      );
	                      applique_masquages_carte_tpl (tTpl);
	                      applique_decorations_cartes_tpl (tTpl);
                      tTpl._svgObjectReadySourceKey = tSourceKey;
                      if (tTpl._svgObjectReady) tTpl._svgObjectReady.set (true);
	                      if (mode_lecture_scene_actif ())
	                        {
	                          lance_animations_entree_scene_tpl (tTpl);
	                          Tracker.afterFlush (() => { rafraichit_scene_lecture_tpl (tTpl, true); });
	                        }
	                      Tracker.afterFlush (() => { carte_obj_reorder_sync (tTpl); });
	                    })
	                  .catch ((pErr) =>
	                    {
	                      if (tTpl._svgInlineLoadSeq !== tLoadSeq) return;
                        if (erreur_abort_scene_svg (pErr)) return;
	                      console.warn ("[scene] chargement SVG inline impossible", pErr);
                      if (tTpl._svgObjectReady) tTpl._svgObjectReady.set (false);
                      tTpl._svgObjectReadySourceKey = null;
	                      nettoie_svg_inline_scene_host (tObj);
	                    })
                    .finally (() =>
                      {
                        if (tTpl._svgInlineLoadSeq === tLoadSeq
                         && tTpl._svgInlineAbort === tAbort)
                          tTpl._svgInlineAbort = null;
                      });
	              };
	            tTpl._svgObjectLoadHandler = tHandler;
	            programme_chargement_svg_scene_tpl (tTpl, tSourceKey, tHandler);
	          });
	      });

    // Maintient à jour les champs x / y / zoom de la barre des réglages
    // sans piétiner la saisie en cours (ne touche pas l'input focused).
    tTpl.autorun (() =>
      {
        const tX = gPanX.get ();
        const tY = gPanY.get ();
        const tZ = gZoom.get ();
        Tracker.afterFlush (() =>
          {
            const tFocus = document.activeElement;
            const fmt = (v, n) => (isFinite (v) ? Number (v).toFixed (n) : "");
            const tIx = tTpl.find (".js_pan_x");
            const tIy = tTpl.find (".js_pan_y");
            const tIz = tTpl.find (".js_zoom");
            if (tIx && tIx !== tFocus) tIx.value = fmt (tX, 0);
            if (tIy && tIy !== tFocus) tIy.value = fmt (tY, 0);
            if (tIz && tIz !== tFocus) tIz.value = fmt (tZ, 3);
          });
      });
  });

Template.scene.events
  ({
    "keydown" (e, tpl)
      {
        const tEv = e.originalEvent || e;
        if (!tEv.ctrlKey || !tEv.altKey || String (tEv.key || "").toLowerCase () !== "c") return;
        if (!applique_cheat_cartes_lecture_scene (tpl)) return;
        e.preventDefault ();
        e.stopPropagation ();
      },

    "click .js_lecture_rejouer_oui" (e)
      {
        e.preventDefault ();
        e.stopPropagation ();
        repond_dialog_rejouer_scene_lecture (true);
      },

    "click .js_lecture_rejouer_non" (e)
      {
        e.preventDefault ();
        e.stopPropagation ();
        repond_dialog_rejouer_scene_lecture (false);
      },

    "click .js_ambiance_controles, pointerdown .js_ambiance_controles" (e)
      {
        // La barre est posée sur la scène : ses clics ne doivent pas atteindre
        // les gestionnaires de la scène en lecture.
        e.stopPropagation ();
      },

    "click .js_ambiance_controle_lecture" (e)
      {
        e.preventDefault ();
        e.stopPropagation ();
        lance_ambiance_lecture_scene ();
      },

    "click .js_ambiance_controle_pause" (e)
      {
        e.preventDefault ();
        e.stopPropagation ();
        pause_ambiance_lecture_scene ();
      },

    "click .js_ambiance_controle_stop" (e)
      {
        e.preventDefault ();
        e.stopPropagation ();
        stoppe_ambiance_lecture_scene ();
      },

    "pointerdown .js_ambiance_controle_timeline" (e)
      {
        e.preventDefault ();
        e.stopPropagation ();
        suit_timeline_ambiance_lecture (e);
      },

    "click .js_scene_fullscreen" (e, tpl)
      {
        e.preventDefault ();
        e.stopPropagation ();
        const tRuntime = tpl.find (".js_scene_fullscreen_runtime");
        const tCible = cible_plein_ecran_scene_tpl (tpl, tRuntime);
        if (!tRuntime || !tCible) return;
        const tScenePleinEcran = scene_est_element_plein_ecran_tpl (tpl, tRuntime)
          || (tRuntime.classList && tRuntime.classList.contains ("scene_native_fullscreen_active"));

        if (tScenePleinEcran)
          {
            tpl._sceneFullscreenPending = false;
            quitte_plein_ecran_scene_document ()
              .then (() => programme_synchronisation_plein_ecran_scene_tpl (tpl))
              .catch (() =>
                {
                  desactive_classe_scene_plein_ecran (tRuntime);
                  tpl._sceneFullscreenElement = null;
                  if (tpl._sceneFullscreenActive) tpl._sceneFullscreenActive.set (false);
                  restaure_scene_lecture_apres_sortie_plein_ecran_tpl (tpl);
                });
            return;
          }

        tpl._sceneFullscreenPending = true;
        if (tRuntime.classList) tRuntime.classList.add ("scene_fullscreen_transitioning");
        assure_svg_scene_charge_apres_viewport_tpl (tpl);
        demande_plein_ecran_scene_element (tCible)
          .then (() => programme_synchronisation_plein_ecran_scene_tpl (tpl))
          .catch (() =>
            {
              tpl._sceneFullscreenPending = false;
              tpl._sceneFullscreenElement = null;
              desactive_classe_scene_plein_ecran (tRuntime);
            });
      },
  });

function element_svg_scene_tpl (pTpl)
  {
    return pTpl && typeof pTpl.find === "function"
      ? pTpl.find (".contenu_svg_scene")
      : null;
  }

function element_cadre_scene_tpl (pTpl)
  {
    return pTpl && typeof pTpl.find === "function"
      ? pTpl.find (".scene")
      : null;
  }

function cadre_pixels_scene_local (pRect)
  {
    if (!pRect || !pRect.width || !pRect.height) return null;
    return {
      x : 0,
      y : 0,
      w : pRect.width,
      h : pRect.height,
    };
  }

function rect_selection_ecran_local_depuis_vues_brutes (pTpl, pVueCameraBrute, pVueSelectionBrute, pRectScene = null)
  {
    const tVueCamera = normalise_vue_carte_client (pVueCameraBrute);
    const tVueSelection = normalise_vue_carte_client (pVueSelectionBrute);
    const tCadre = cadre_pixels_scene_local (pRectScene || (() =>
      {
        const tElement = element_svg_scene_tpl (pTpl);
        try { return tElement ? tElement.getBoundingClientRect () : null; } catch (e) { return null; }
      })());
    if (!tVueCamera || !tVueSelection || !tCadre || !tVueCamera.w || !tVueCamera.h) return null;

    const tCoinHG = convertit_point_entre_vues_scene (
      {
        x : tVueSelection.x,
        y : tVueSelection.y,
      },
      tVueCamera,
      tCadre
    );
    const tCoinBD = convertit_point_entre_vues_scene (
      {
        x : tVueSelection.x + tVueSelection.w,
        y : tVueSelection.y + tVueSelection.h,
      },
      tVueCamera,
      tCadre
    );
    if (!tCoinHG || !tCoinBD) return null;

    const tMinX = Math.max (0, Math.min (tCoinHG.x, tCoinBD.x));
    const tMinY = Math.max (0, Math.min (tCoinHG.y, tCoinBD.y));
    const tMaxX = Math.min (tCadre.w, Math.max (tCoinHG.x, tCoinBD.x));
    const tMaxY = Math.min (tCadre.h, Math.max (tCoinHG.y, tCoinBD.y));
    return {
      x : tMinX,
      y : tMinY,
      w : Math.max (0, tMaxX - tMinX),
      h : Math.max (0, tMaxY - tMinY),
    };
  }

function rect_selection_ecran_local_non_borne_depuis_vues_brutes (pVueCameraBrute, pVueSelectionBrute, pCadre)
  {
    const tVueCamera = normalise_vue_carte_client (pVueCameraBrute);
    const tVueSelection = normalise_vue_carte_client (pVueSelectionBrute);
    if (!tVueCamera || !tVueSelection || !pCadre || !tVueCamera.w || !tVueCamera.h) return null;

    const tX1 = ((tVueSelection.x - tVueCamera.x) / tVueCamera.w) * pCadre.w;
    const tY1 = ((tVueSelection.y - tVueCamera.y) / tVueCamera.h) * pCadre.h;
    const tX2 = ((tVueSelection.x + tVueSelection.w - tVueCamera.x) / tVueCamera.w) * pCadre.w;
    const tY2 = ((tVueSelection.y + tVueSelection.h - tVueCamera.y) / tVueCamera.h) * pCadre.h;

    return {
      x : Math.min (tX1, tX2),
      y : Math.min (tY1, tY2),
      w : Math.abs (tX2 - tX1),
      h : Math.abs (tY2 - tY1),
    };
  }

function fenetre_masque_transition_camera_depuis_rect_local (
  pRect,
  pCadre,
  pPadding = LECTURE_CAMERA_TRANSITION_MASK_PADDING_PX,
  pBornesOuverture = null
)
  {
    const tRect = normalise_vue_carte_client (pRect);
    if (!tRect || !pCadre) return null;
    const tBornes = normalise_vue_carte_client (pBornesOuverture);
    const tBorneX1 = tBornes ? Math.max (0, Math.ceil (tBornes.x)) : 0;
    const tBorneY1 = tBornes ? Math.max (0, Math.ceil (tBornes.y)) : 0;
    const tBorneX2 = tBornes ? Math.min (pCadre.w, Math.floor (tBornes.x + tBornes.w)) : pCadre.w;
    const tBorneY2 = tBornes ? Math.min (pCadre.h, Math.floor (tBornes.y + tBornes.h)) : pCadre.h;

    const tX1 = Math.max (0, tBorneX1, Math.floor (tRect.x - pPadding));
    const tY1 = Math.max (0, tBorneY1, Math.floor (tRect.y - pPadding));
    const tX2 = Math.min (pCadre.w, tBorneX2, Math.ceil (tRect.x + tRect.w + pPadding));
    const tY2 = Math.min (pCadre.h, tBorneY2, Math.ceil (tRect.y + tRect.h + pPadding));
    return {
      x : tX1,
      y : tY1,
      w : Math.max (0, tX2 - tX1),
      h : Math.max (0, tY2 - tY1),
    };
  }

function clip_path_camera_transition_depuis_rect (pRect, pCadre, pPadding = LECTURE_CAMERA_TRANSITION_MASK_PADDING_PX)
  {
    const tFenetre = fenetre_masque_transition_camera_depuis_rect_local (pRect, pCadre, pPadding);
    if (!tFenetre || !tFenetre.w || !tFenetre.h || !pCadre) return null;
    const tTop = Math.max (0, tFenetre.y);
    const tLeft = Math.max (0, tFenetre.x);
    const tRight = Math.max (0, pCadre.w - (tFenetre.x + tFenetre.w));
    const tBottom = Math.max (0, pCadre.h - (tFenetre.y + tFenetre.h));
    const tNombre = (pValeur) =>
      Number (pValeur || 0).toFixed (3).replace (/\.0+$/, "").replace (/(\.\d*?)0+$/, "$1");
    return `inset(${tNombre (tTop)}px ${tNombre (tRight)}px ${tNombre (tBottom)}px ${tNombre (tLeft)}px)`;
  }

function vue_contient_vue_camera_transition (pContenant, pContenu, pTolerance = 0.001)
  {
    const tContenant = normalise_vue_carte_client (pContenant);
    const tContenu = normalise_vue_carte_client (pContenu);
    const tTol = Math.max (0, Number (pTolerance) || 0);
    if (!tContenant || !tContenu) return false;
    return tContenu.x >= tContenant.x - tTol
      && tContenu.y >= tContenant.y - tTol
      && tContenu.x + tContenu.w <= tContenant.x + tContenant.w + tTol
      && tContenu.y + tContenu.h <= tContenant.y + tContenant.h + tTol;
  }

function vue_base_transition_camera_compositee (pDepart, pCible)
  {
    const tDepart = normalise_vue_carte_client (pDepart);
    const tCible = normalise_vue_carte_client (pCible);
    if (!tDepart || !tCible || !tDepart.w || !tDepart.h) return tDepart;
    if (vue_contient_vue_camera_transition (tDepart, tCible)) return tDepart;
    const tUnion = union_vues_cartes_client (tDepart, tCible);
    const tRatio = tDepart.w / tDepart.h;
    return adapte_vue_au_ratio_cible (tUnion, tRatio) || tUnion || tDepart;
  }

function bascule_decor_interne_focus_carte_tpl (pTpl, pVisible)
  {
    const tRoot = pTpl && pTpl._svgDoc && pTpl._svgDoc.documentElement;
    if (!tRoot || typeof tRoot.querySelectorAll !== "function") return;
    tRoot.querySelectorAll (`.${CARTE_DECOR_FOND_CLASS}, .${CARTE_DECOR_MASQUE_CLASS}`).forEach ((pNoeud) =>
      {
        if (!pNoeud || !pNoeud.style) return;
        if (pVisible)
          {
            if (pNoeud.getAttribute ("data-cogi-transition-mask-hidden") !== "1") return;
            pNoeud.removeAttribute ("data-cogi-transition-mask-hidden");
            pNoeud.style.removeProperty ("opacity");
          }
        else
          {
            pNoeud.setAttribute ("data-cogi-transition-mask-hidden", "1");
            pNoeud.style.opacity = "0";
          }
      });
  }

function nettoie_masque_transition_camera_compositee_tpl (pTpl)
  {
    if (!pTpl) return;
    if (pTpl._lectureCameraTransitionMaskRaf)
      {
        cancelAnimationFrame (pTpl._lectureCameraTransitionMaskRaf);
        pTpl._lectureCameraTransitionMaskRaf = null;
      }
    pTpl._lectureCameraTransitionMaskState = null;
    if (pTpl._lectureCameraTransitionBackdropNode)
      {
        const tBackdrop = pTpl._lectureCameraTransitionBackdropNode;
        if (tBackdrop.parentNode) tBackdrop.parentNode.removeChild (tBackdrop);
        pTpl._lectureCameraTransitionBackdropNode = null;
      }
    const tScene = element_cadre_scene_tpl (pTpl);
    const tMasque = tScene && tScene.querySelector (`.${LECTURE_CAMERA_TRANSITION_MASK_CLASS}`);
    if (tMasque)
      {
        tMasque.classList.remove ("est_active");
        tMasque.removeAttribute ("viewBox");
      }
    if (tScene)
      {
        if (Object.prototype.hasOwnProperty.call (pTpl, "_lectureCameraTransitionSceneBackground"))
          {
            const tFondInitial = pTpl._lectureCameraTransitionSceneBackground;
            if (typeof tFondInitial === "string" && tFondInitial)
              tScene.style.backgroundColor = tFondInitial;
            else
              tScene.style.removeProperty ("background-color");
            delete pTpl._lectureCameraTransitionSceneBackground;
          }
      }
    const tElement = element_svg_scene_tpl (pTpl);
    if (tElement && Object.prototype.hasOwnProperty.call (pTpl, "_lectureCameraTransitionElementBackground"))
      {
        const tFondInitial = pTpl._lectureCameraTransitionElementBackground;
        if (typeof tFondInitial === "string" && tFondInitial)
          tElement.style.backgroundColor = tFondInitial;
        else
          tElement.style.removeProperty ("background-color");
        delete pTpl._lectureCameraTransitionElementBackground;
      }
    const tElementTransition = element_svg_scene_tpl (pTpl);
    if (tElementTransition && tElementTransition.style)
      {
        tElementTransition.style.removeProperty ("clip-path");
        tElementTransition.style.removeProperty ("-webkit-clip-path");
      }
    bascule_decor_interne_focus_carte_tpl (pTpl, true);
  }

function assure_fond_transition_camera_lecture_tpl (pTpl, pCouleur)
  {
    const tScene = element_cadre_scene_tpl (pTpl);
    if (!tScene || !tScene.ownerDocument) return null;
    let tFond = pTpl && pTpl._lectureCameraTransitionBackdropNode;
    if (!tFond || tFond.parentNode !== tScene)
      {
        tFond = tScene.ownerDocument.createElement ("div");
        tFond.className = "fond_transition_camera_lecture";
        tScene.insertBefore (tFond, tScene.firstChild);
        if (pTpl) pTpl._lectureCameraTransitionBackdropNode = tFond;
      }
    tFond.style.backgroundColor = pCouleur || "#ffffff";
    return tFond;
  }

function applique_masque_transition_camera_compositee_tpl (pTpl, pOptions = {})
  {
    const tSelection = normalise_vue_carte_client (pOptions.selectionSvg || pOptions.selectionBrute);
    const tDepart = normalise_vue_carte_client (pOptions.departCameraSvg || pOptions.departCameraBrute);
    const tCible = normalise_vue_carte_client (pOptions.cibleCameraSvg || pOptions.cibleCameraBrute);
    if (!pTpl || !tSelection) return false;

    const tElement = element_svg_scene_tpl (pTpl);
    const tScene = element_cadre_scene_tpl (pTpl);
    let tRectScene = null;
    try { tRectScene = tElement ? tElement.getBoundingClientRect () : null; } catch (e) {}
    const tCadre = cadre_pixels_scene_local (tRectScene);
    if (!tElement || !tScene || !tCadre || !tDepart || !tCible || !pOptions.cssTransform) return false;

    const tRectDepart = rect_selection_ecran_local_non_borne_depuis_vues_brutes (tDepart, tSelection, tCadre);
    if (!tRectDepart || !tRectDepart.w || !tRectDepart.h) return false;

    if (!Object.prototype.hasOwnProperty.call (pTpl, "_lectureCameraTransitionSceneBackground"))
      pTpl._lectureCameraTransitionSceneBackground = tScene.style.backgroundColor || "";
    if (!Object.prototype.hasOwnProperty.call (pTpl, "_lectureCameraTransitionElementBackground"))
      pTpl._lectureCameraTransitionElementBackground = tElement.style.backgroundColor || "";

    const tMasqueScene = mode_lecture_masque_scene_courant ();
    const tCouleurMasque = typeof pOptions.maskFill === "string" && pOptions.maskFill
      ? pOptions.maskFill
      : (couleur_effective_mode_lecture_masque_scene_client (tMasqueScene) || "#ffffff");
    const tCouleurBackdrop = typeof pOptions.backdropColor === "string" && pOptions.backdropColor
      ? pOptions.backdropColor
      : tCouleurMasque;
    assure_fond_transition_camera_lecture_tpl (pTpl, tCouleurMasque);
    tScene.style.backgroundColor = tCouleurMasque;
    tElement.style.backgroundColor = tCouleurBackdrop;
    const tClipPath = clip_path_camera_transition_depuis_rect (tRectDepart, tCadre);
    if (!tClipPath) return false;
    tElement.style.setProperty ("clip-path", tClipPath);
    tElement.style.setProperty ("-webkit-clip-path", tClipPath);

    pTpl._lectureCameraTransitionMaskState = {
      cadre : tCadre,
      rectDepart : tRectDepart,
      element : tElement,
      departCamera : tDepart,
      cibleCamera : tCible,
      transformCss : pOptions.cssTransform,
      maskFill : tCouleurMasque,
      maskOpacity : pOptions.maskOpacity ?? 0.34,
    };
    const tMasque = tScene.querySelector (`.${LECTURE_CAMERA_TRANSITION_MASK_CLASS}`);
    if (tMasque) tMasque.classList.remove ("est_active");
    bascule_decor_interne_focus_carte_tpl (pTpl, false);
    return true;
  }

function transforme_css_camera_depuis_vues (pVueDepart, pVueCible, pElement)
  {
    const tDepart = normalise_vue_carte_client (pVueDepart);
    const tCible = normalise_vue_carte_client (pVueCible);
    if (!tDepart || !tCible || !tCible.w || !tCible.h || !pElement) return null;
    let tRect = null;
    try { tRect = pElement.getBoundingClientRect (); } catch (e) {}
    if (!tRect || !tRect.width || !tRect.height) return null;

    const tScaleX = tDepart.w / tCible.w;
    const tScaleY = tDepart.h / tCible.h;
    const tTranslateX = ((tDepart.x - tCible.x) / tCible.w) * tRect.width;
    const tTranslateY = ((tDepart.y - tCible.y) / tCible.h) * tRect.height;
    if (![tScaleX, tScaleY, tTranslateX, tTranslateY].every (Number.isFinite)) return null;

    const tNombre = (pValeur, pPrecision = 6) =>
      Number (pValeur || 0).toFixed (pPrecision).replace (/\.0+$/, "").replace (/(\.\d*?)0+$/, "$1");
    return {
      transform : `translate3d(${tNombre (tTranslateX, 3)}px, ${tNombre (tTranslateY, 3)}px, 0) scale3d(${tNombre (tScaleX)}, ${tNombre (tScaleY)}, 1)`,
      scaleX : tScaleX,
      scaleY : tScaleY,
      translateX : tTranslateX,
      translateY : tTranslateY,
      quasiIdentite : Math.abs (tScaleX - 1) < 0.000001
        && Math.abs (tScaleY - 1) < 0.000001
        && Math.abs (tTranslateX) < 0.01
        && Math.abs (tTranslateY) < 0.01,
    };
  }

function nettoie_transition_camera_compositee_tpl (pTpl, pElement = null)
  {
    const tElement = pElement || element_svg_scene_tpl (pTpl);
    if (!tElement || !tElement.classList || !tElement.style) return;
    tElement.classList.remove (LECTURE_CAMERA_TRANSITION_ACTIVE_CLASS);
    tElement.classList.remove (LECTURE_CAMERA_TRANSITION_CLASS);
    tElement.style.removeProperty ("--lecture-camera-transform");
    tElement.style.removeProperty ("--lecture-camera-transform-initial");
    tElement.style.removeProperty ("--lecture-camera-transition-ms");
    tElement.style.removeProperty ("--lecture-camera-transition-easing");
  }

function vue_intermediaire_animation_camera (pEtat)
  {
    if (!pEtat || !pEtat.depart || !pEtat.cible) return null;
    const tDuree = Math.max (0, Number (pEtat.duree) || 0);
    if (tDuree <= 0 || !pEtat.departMs) return {...pEtat.cible};
    const tNow = (typeof performance !== "undefined" && performance.now)
      ? performance.now () : Date.now ();
    const tT = Math.max (0, Math.min (1, (tNow - pEtat.departMs) / tDuree));
    const tEase = typeof pEtat.easing === "function" ? pEtat.easing : lecture_ease_out_cubic;
    const tK = tEase (tT);
    return {
      x : pEtat.depart.x + ((pEtat.cible.x - pEtat.depart.x) * tK),
      y : pEtat.depart.y + ((pEtat.cible.y - pEtat.depart.y) * tK),
      w : pEtat.depart.w + ((pEtat.cible.w - pEtat.depart.w) * tK),
      h : pEtat.depart.h + ((pEtat.cible.h - pEtat.depart.h) * tK),
    };
  }

function annule_transition_camera_compositee_tpl (pTpl, pOptions = {})
  {
    if (!pTpl) return;
    const tEtat = pTpl._svgAnimCompositeEtat || null;
    if (pTpl._svgAnimCompositeTransitionEnd && pTpl._svgAnimCompositeElement)
      pTpl._svgAnimCompositeElement.removeEventListener ("transitionend", pTpl._svgAnimCompositeTransitionEnd);
    if (pTpl._svgAnimCompositeFallbackTimer)
      {
        clearTimeout (pTpl._svgAnimCompositeFallbackTimer);
        pTpl._svgAnimCompositeFallbackTimer = null;
      }

    if (pOptions.commitCurrent !== false && tEtat && tEtat.demarre && tEtat.vb && typeof tEtat.applique === "function")
      {
        const tVue = vue_intermediaire_animation_camera (tEtat);
        if (tVue)
          {
            copie_vue_sur_place (tEtat.vb, tVue);
            tEtat.applique ();
          }
      }

    nettoie_transition_camera_compositee_tpl (pTpl, pTpl._svgAnimCompositeElement);
    nettoie_masque_transition_camera_compositee_tpl (pTpl);
    pTpl._svgAnimCompositeEtat = null;
    pTpl._svgAnimCompositeElement = null;
    pTpl._svgAnimCompositeTransitionEnd = null;
    pTpl._svgAnimCompositeToken = null;
  }

function annule_animation_viewbox_tpl (pTpl, pOptions = {})
  {
    if (!pTpl) return;
    if (pTpl._svgAnimRaf)
      {
        cancelAnimationFrame (pTpl._svgAnimRaf);
        pTpl._svgAnimRaf = null;
      }
    if (pTpl._svgAnimDelayTimer)
      {
        clearTimeout (pTpl._svgAnimDelayTimer);
        pTpl._svgAnimDelayTimer = null;
      }
    annule_transition_camera_compositee_tpl (pTpl, pOptions);
  }

function finalise_animation_camera_compositee (pTpl, pToken, pElement, pVB, pCible, pApplique, pOptions)
  {
    if (!pTpl || pTpl._svgAnimCompositeToken !== pToken) return;
    if (pTpl._svgAnimCompositeTransitionEnd && pElement)
      pElement.removeEventListener ("transitionend", pTpl._svgAnimCompositeTransitionEnd);
    if (pTpl._svgAnimCompositeFallbackTimer)
      {
        clearTimeout (pTpl._svgAnimCompositeFallbackTimer);
        pTpl._svgAnimCompositeFallbackTimer = null;
      }
    copie_vue_sur_place (pVB, pCible);
    pApplique ();
    if (typeof pOptions.onComplete === "function") pOptions.onComplete ();
    nettoie_transition_camera_compositee_tpl (pTpl, pElement);
    nettoie_masque_transition_camera_compositee_tpl (pTpl);
    pTpl._svgAnimCompositeEtat = null;
    pTpl._svgAnimCompositeElement = null;
    pTpl._svgAnimCompositeTransitionEnd = null;
    pTpl._svgAnimCompositeToken = null;
  }

function anime_viewbox_compositee_vers (pVB, pCible, pApplique, pTpl, pOptions = {})
  {
    const tElement = element_svg_scene_tpl (pTpl);
    const tDepart = normalise_vue_carte_client (pVB);
    const tCible = normalise_vue_carte_client (pCible);
    const tBase = vue_base_transition_camera_compositee (tDepart, tCible);
    const tUtiliseBaseElargie = tBase && !vues_cartes_equivalentes (tBase, tDepart);
    const tDepartBrute = vue_camera_brute_depuis_vue_svg_scene (pTpl, tDepart) || vue_camera_brute_courante_scene (pTpl) || tDepart;
    const tBaseBrute = vue_camera_brute_depuis_vue_svg_scene (pTpl, tBase) || tBase;
    const tCibleBrute = vue_camera_brute_depuis_vue_svg_scene (pTpl, tCible) || tCible;
    const tSelectionSvg = pOptions.selectionMaskBrute
      ? vue_camera_svg_depuis_vue_brute_scene (pTpl, pOptions.selectionMaskBrute)
      : null;
    const tTransformInitial = tUtiliseBaseElargie
      ? transforme_css_camera_depuis_vues (tBase, tDepart, tElement)
      : null;
    const tTransform = transforme_css_camera_depuis_vues (tUtiliseBaseElargie ? tBase : tDepart, tCible, tElement);
    if (!tElement || !tTransform) return false;
    if (tUtiliseBaseElargie && !tTransformInitial) return false;
    // Une base elargie anime souvent vers l'identite. Ne pas refuser ce cas :
    // le fallback viewBox/RAF provoque du flickering Chrome en plein ecran.
    if (tTransform.quasiIdentite && !tTransformInitial) return false;

    if (pOptions.selectionMaskBrute)
      {
        const tMasqueOk = applique_masque_transition_camera_compositee_tpl (
          pTpl,
          {
            selectionSvg : tSelectionSvg,
            selectionBrute : pOptions.selectionMaskBrute,
            departCameraSvg : tUtiliseBaseElargie ? tBase : tDepart,
            cibleCameraSvg : tCible,
            departCameraBrute : tUtiliseBaseElargie ? tBaseBrute : tDepartBrute,
            cibleCameraBrute : tCibleBrute,
            cssTransform : tTransform,
            maskFill : pOptions.selectionMaskFill,
            maskOpacity : pOptions.selectionMaskOpacity,
            backdropColor : pOptions.backdropColor,
          }
        );
        if (!tMasqueOk) return false;
      }
    else
      nettoie_masque_transition_camera_compositee_tpl (pTpl);

    const tDuree = Math.max (0, Number (pOptions.duree) || 0);
    const tDelai = Math.max (0, Number (pOptions.delai_ms) || 0);
    const tEasing = lecture_ease_camera_transition;
    const tToken = {};
    pTpl._svgAnimCompositeToken = tToken;
    pTpl._svgAnimCompositeElement = tElement;
    pTpl._svgAnimCompositeEtat = {
      vb : pVB,
      applique : pApplique,
      depart : {...tDepart},
      cible : {...tCible},
      duree : tDuree,
      easing : tEasing,
      demarre : false,
      departMs : 0,
    };
    const tDemarre = () =>
      {
        if (pTpl._svgAnimCompositeToken !== tToken) return;
        pTpl._svgAnimDelayTimer = null;
        if (tDuree <= 0)
          {
            finalise_animation_camera_compositee (pTpl, tToken, tElement, pVB, tCible, pApplique, pOptions);
            return;
          }
        tElement.classList.add (LECTURE_CAMERA_TRANSITION_CLASS);
        tElement.classList.remove (LECTURE_CAMERA_TRANSITION_ACTIVE_CLASS);
        if (tTransformInitial)
          tElement.style.setProperty ("--lecture-camera-transform-initial", tTransformInitial.transform);
        tElement.style.setProperty ("--lecture-camera-transform", tTransform.transform);
        tElement.style.setProperty ("--lecture-camera-transition-ms", `${tDuree}ms`);
        tElement.style.setProperty ("--lecture-camera-transition-easing", LECTURE_CAMERA_TRANSITION_EASING_CSS);
        if (tUtiliseBaseElargie)
          {
            copie_vue_sur_place (pVB, tBase);
            pApplique ();
          }
        try { tElement.getBoundingClientRect (); } catch (e) {}

        const tTransitionEnd = (pEvent) =>
          {
            if (pEvent && pEvent.target !== tElement) return;
            if (pEvent && pEvent.propertyName && pEvent.propertyName !== "transform") return;
            finalise_animation_camera_compositee (pTpl, tToken, tElement, pVB, tCible, pApplique, pOptions);
          };
        pTpl._svgAnimCompositeTransitionEnd = tTransitionEnd;
        tElement.addEventListener ("transitionend", tTransitionEnd);

        requestAnimationFrame (() =>
          {
            if (pTpl._svgAnimCompositeToken !== tToken) return;
            const tEtat = pTpl._svgAnimCompositeEtat;
            if (tEtat)
              {
                tEtat.demarre = true;
                tEtat.departMs = (typeof performance !== "undefined" && performance.now)
                  ? performance.now () : Date.now ();
              }
            tElement.classList.add (LECTURE_CAMERA_TRANSITION_ACTIVE_CLASS);
            pTpl._svgAnimCompositeFallbackTimer = setTimeout (() =>
              {
                finalise_animation_camera_compositee (pTpl, tToken, tElement, pVB, tCible, pApplique, pOptions);
              }, tDuree + 120);
          });
      };

    if (tDelai > 0)
      pTpl._svgAnimDelayTimer = setTimeout (tDemarre, tDelai);
    else
      tDemarre ();
    return true;
  }

// Anime un viewBox depuis ses valeurs courantes vers une cible, en
// appliquant un easing configurable pour un retour fluide.
// En mode lecture, la caméra peut être animée comme couche compositée puis
// committée au viewBox final pour éviter un rendu SVG complet à chaque frame.
function anime_viewbox_vers (pVB, pCible, pApplique, pTpl, pOptions = {})
  {
    annule_animation_viewbox_tpl (pTpl);
    const tDepart = {x : pVB.x, y : pVB.y, w : pVB.w, h : pVB.h};
    const tDelaiDepart = Math.max (0, Number (pOptions && pOptions.delai_ms) || 0);
    const tDuree = Math.max (0, Number (pOptions && pOptions.duree) || 700);
    const tEasing = pOptions && typeof pOptions.easing === "function"
      ? pOptions.easing
      : lecture_ease_out_cubic;
    const tFps = Math.max (0, Number (pOptions && pOptions.fps) || 0);
    const tDelaiFrame = tFps > 0 ? 1000 / tFps : 0;
    let tDerniereFrame = 0;
    if (tDuree <= 0)
      {
        pVB.x = pCible.x;
        pVB.y = pCible.y;
        pVB.w = pCible.w;
        pVB.h = pCible.h;
        pApplique ();
        if (typeof pOptions.onComplete === "function") pOptions.onComplete ();
        return;
      }
    if (pOptions && pOptions.composite
     && anime_viewbox_compositee_vers (pVB, pCible, pApplique, pTpl, {
       delai_ms : tDelaiDepart,
       duree : tDuree,
       easing : tEasing,
       selectionMaskBrute : pOptions.selectionMaskBrute,
       selectionMaskFill : pOptions.selectionMaskFill,
       selectionMaskOpacity : pOptions.selectionMaskOpacity,
       backdropColor : pOptions.backdropColor,
       onComplete : pOptions.onComplete,
     }))
      return;
    let tT0 = 0;

    const tStep = (pNow) =>
      {
        const tT = Math.min (1, (pNow - tT0) / tDuree);
        if (tDelaiFrame > 0 && tT < 1 && tDerniereFrame && pNow - tDerniereFrame < tDelaiFrame)
          {
            pTpl._svgAnimRaf = requestAnimationFrame (tStep);
            return;
          }
        tDerniereFrame = pNow;
        const tK = tEasing (tT);
        pVB.x = tDepart.x + (pCible.x - tDepart.x) * tK;
        pVB.y = tDepart.y + (pCible.y - tDepart.y) * tK;
        pVB.w = tDepart.w + (pCible.w - tDepart.w) * tK;
        pVB.h = tDepart.h + (pCible.h - tDepart.h) * tK;
        pApplique ();
        if (tT < 1)
          pTpl._svgAnimRaf = requestAnimationFrame (tStep);
        else
          {
            pTpl._svgAnimRaf = null;
            if (typeof pOptions.onComplete === "function") pOptions.onComplete ();
          }
      };
    const tDemarre = () =>
      {
        pTpl._svgAnimDelayTimer = null;
        tT0 = (typeof performance !== "undefined" && performance.now)
          ? performance.now () : Date.now ();
        pTpl._svgAnimRaf = requestAnimationFrame (tStep);
      };
    if (tDelaiDepart > 0)
      pTpl._svgAnimDelayTimer = setTimeout (tDemarre, tDelaiDepart);
    else
      tDemarre ();
  }

function vue_zoomee_depuis_rectangle (pVB, pRect, pMarge = 0.08)
  {
    const tRect = normalise_vue_carte_client (pRect);
    if (!pVB || !tRect) return null;
    const tRatioVue = pVB.w / pVB.h;
    let tCibleW = tRect.w / (1 - 2 * pMarge);
    let tCibleH = tRect.h / (1 - 2 * pMarge);
    if (tCibleW / tCibleH > tRatioVue) tCibleH = tCibleW / tRatioVue;
    else                               tCibleW = tCibleH * tRatioVue;
    const tCx = tRect.x + tRect.w / 2;
    const tCy = tRect.y + tRect.h / 2;
    return {
      x : tCx - tCibleW / 2,
      y : tCy - tCibleH / 2,
      w : tCibleW,
      h : tCibleH,
    };
  }

function vue_focus_carte_brute_tpl (pTpl, pCarte)
  {
    if (!pTpl || !pCarte || !pCarte.vue) return null;
    const tVueDepartBrute = vue_camera_brute_courante_scene (pTpl)
      || vue_camera_brute_depuis_vue_svg_scene (pTpl, pTpl._svgVB);
    return vue_zoomee_depuis_rectangle (tVueDepartBrute, pCarte.vue);
  }

function zoom_sur_carte (pTpl, pCarteId)
  {
    const tVB  = pTpl && pTpl._svgVB;
    const tApp = pTpl && pTpl._svgApplique;
    const tCarte = carte_source_pour_bulle_scene (pCarteId);
    if (!tVB || !tApp || !tCarte || !tCarte.vue) return;
    const tCibleBrute = vue_focus_carte_brute_tpl (pTpl, tCarte);
    const tCible = vue_camera_svg_depuis_vue_brute_scene (pTpl, tCibleBrute);
    if (!tCible || !tCibleBrute) return;
    arme_notice_cadrage_carte ();
    const tModeLecture = mode_lecture_scene_actif ();
    const tReglagesModeLecture = tModeLecture ? reglages_mode_lecture_scene () : null;
    const tMasqueScene = tModeLecture ? mode_lecture_masque_scene_courant () : null;
    if (tModeLecture)
      {
        pTpl._lectureFocusCarteEntreeEnCours = true;
        synchronise_items_interface_svg_tpl (pTpl);
      }
    anime_viewbox_vers (tVB, tCible, tApp, pTpl, {
      delai_ms : tModeLecture ? delai_zoom_carte_entree_ms_lecture () : 0,
      duree : tModeLecture ? tReglagesModeLecture.zoom_carte_duree_ms : 700,
      easing : tModeLecture ? lecture_ease_in_out_cubic : lecture_ease_out_cubic,
      fps : tModeLecture ? fps_animation_lecture_scene () : 0,
      composite : tModeLecture,
      selectionMaskBrute : tModeLecture ? normalise_vue_carte_client (tCarte.vue) : null,
      selectionMaskFill : tMasqueScene ? couleur_effective_mode_lecture_masque_scene_client (tMasqueScene) : null,
      selectionMaskOpacity : tMasqueScene ? tMasqueScene.opacite : null,
      backdropColor : tModeLecture ? couleur_fond_bulle_carte_effective_client (tCarte) : null,
      onComplete : tModeLecture
        ? () =>
          {
            pTpl._lectureFocusCarteEntreeEnCours = false;
            pTpl._lectureFocusInteractionsActives = true;
            termine_transition_decor_focus_carte_tpl (pTpl);
          }
        : null,
    });
  }

// Zoom animé sur l'élément SVG d'id donné : la cible occupe presque
// toute la zone visible (largeur ou hauteur, selon le plus contraignant).
function zoom_sur_id (pTpl, pId)
  {
    const tDoc = pTpl._svgDoc;
    const tVB  = pTpl._svgVB;
    const tApp = pTpl._svgApplique;
    if (!tDoc || !tVB || !tApp) return;
    const tRoot = tDoc.documentElement;
    const tElt  = tDoc.getElementById (pId);
    if (!tElt || typeof tElt.getBBox !== "function") return;

    let tBBox;
    try { tBBox = tElt.getBBox (); } catch (e) { return; }
    if (!tBBox || (!tBBox.width && !tBBox.height)) return;

    // Convertit la bbox locale en coords utilisateur de la racine SVG.
    let tM;
    try
      {
        const tEcranElt  = tElt.getScreenCTM ();
        const tEcranRoot = tRoot.getScreenCTM ();
        if (!tEcranElt || !tEcranRoot) return;
        tM = tEcranRoot.inverse ().multiply (tEcranElt);
      }
    catch (e) { return; }

    const tCoins = [
      {x : tBBox.x,                y : tBBox.y},
      {x : tBBox.x + tBBox.width,  y : tBBox.y},
      {x : tBBox.x,                y : tBBox.y + tBBox.height},
      {x : tBBox.x + tBBox.width,  y : tBBox.y + tBBox.height},
    ].map ((p) =>
      ({
        x : p.x * tM.a + p.y * tM.c + tM.e,
        y : p.x * tM.b + p.y * tM.d + tM.f,
      }));
    const tXs = tCoins.map ((p) => p.x);
    const tYs = tCoins.map ((p) => p.y);
    const tMinX = Math.min (...tXs), tMaxX = Math.max (...tXs);
    const tMinY = Math.min (...tYs), tMaxY = Math.max (...tYs);
    const tBW = tMaxX - tMinX;
    const tBH = tMaxY - tMinY;
    if (tBW <= 0 || tBH <= 0) return;

    // Marge : ~8 % autour de l'objet.
    const tCible = vue_zoomee_depuis_rectangle (
      tVB,
      {
        x : tMinX,
        y : tMinY,
        w : tBW,
        h : tBH,
      }
    );
    if (!tCible) return;
    arme_notice_cadrage_carte ();
    anime_viewbox_vers (tVB, tCible, tApp, pTpl);
  }

function vue_reinitialisation_scene_brute (pTpl)
  {
    const tSceneId = Session.get ("scene_id");
    const tScene = tSceneId ? Scenes.findOne (tSceneId) : null;
    return adapte_vue_au_ratio_cible (
      normalise_vue_carte_client (tScene && tScene.vue_defaut)
        || normalise_vue_carte_client (pTpl && pTpl._svgVBInitBrut)
        || normalise_vue_carte_client (pTpl && pTpl._svgVBNatifBrut)
        || vue_camera_brute_courante_scene (pTpl)
        || normalise_vue_carte_client (pTpl && pTpl._svgVB),
      ratio_rapport_lh_scene_projet (rapport_lh_scene_projet_courant ())
    );
  }

function reinitialise_vue_scene (pTpl, pOptions = {})
  {
    if (!pTpl || !pTpl._svgVB || !pTpl._svgApplique) return;
    const tVueCibleBrute = vue_reinitialisation_scene_brute (pTpl);
    const tVueCible = vue_camera_svg_depuis_vue_brute_scene (pTpl, tVueCibleBrute) || tVueCibleBrute;
    if (!tVueCible) return;
    arme_notice_cadrage_carte ();
    const tModeLecture = mode_lecture_scene_actif ();
    const tReglagesModeLecture = tModeLecture ? reglages_mode_lecture_scene () : null;
    const tDelaiAnimation = Number.isFinite (Number (pOptions.delai_ms))
      ? Math.max (0, Number (pOptions.delai_ms))
      : 0;
    anime_viewbox_vers (pTpl._svgVB, tVueCible, pTpl._svgApplique, pTpl, {
      delai_ms : tDelaiAnimation,
      duree : tModeLecture ? tReglagesModeLecture.zoom_carte_duree_ms : 700,
      easing : tModeLecture ? lecture_ease_in_out_cubic : lecture_ease_out_cubic,
      fps : tModeLecture ? fps_animation_lecture_scene () : 0,
      composite : Object.prototype.hasOwnProperty.call (pOptions, "composite")
        ? !!pOptions.composite
        : tModeLecture,
      selectionMaskBrute : pOptions.selectionMaskBrute,
      selectionMaskFill : pOptions.selectionMaskFill,
      selectionMaskOpacity : pOptions.selectionMaskOpacity,
      backdropColor : pOptions.backdropColor,
      onComplete : pOptions.onComplete,
    });
  }

function attache_interactions_svg (pDoc, pTpl)
  {
    if (pDoc._cogi_inter_attache) return;
    pDoc._cogi_inter_attache = true;

    const tRoot = pDoc.documentElement;
  try { tRoot.style.touchAction = "none"; } catch (e) {}
  try { tRoot.style.userSelect = "none"; } catch (e) {}
  try { tRoot.style.webkitUserSelect = "none"; } catch (e) {}
  try { tRoot.style.webkitTapHighlightColor = "transparent"; } catch (e) {}

    // -- État zoom/pan : on s'appuie sur le viewBox de la racine SVG ----
    let tVB;
    const tInit = tRoot.getAttribute ("viewBox");
    if (tInit)
      {
        const p = tInit.split (/[\s,]+/).map (parseFloat);
        tVB = {x : p[0] || 0, y : p[1] || 0, w : p[2] || 0, h : p[3] || 0};
      }
    if (!tVB || !tVB.w || !tVB.h)
      {
        const w = parseFloat (tRoot.getAttribute ("width"))  || 1000;
        const h = parseFloat (tRoot.getAttribute ("height")) || 1000;
        tVB = {x : 0, y : 0, w, h};
        tRoot.setAttribute ("viewBox", `0 0 ${w} ${h}`);
      }
    pTpl._svgVBSourceBrut = {...tVB};
    pTpl._svgVBNatifBrut = {...tVB};
    // Conserve la taille intrinsèque du SVG (sert de référence pour le
    // calcul du facteur de zoom et pour les bornes min/max).
    pTpl._svgVBNatif = {...tVB};

    // Si la scène contient une vue par défaut sauvegardée, l'applique
    // immédiatement comme état initial du viewBox.
    const tSceneId = Session.get ("scene_id");
    const tScene   = tSceneId && Scenes.findOne (tSceneId);
    const tDef     = tScene && tScene.vue_defaut;
    if (tDef
        && isFinite (tDef.x) && isFinite (tDef.y)
        && isFinite (tDef.w) && tDef.w > 0
        && isFinite (tDef.h) && tDef.h > 0)
      {
        tVB = {x : tDef.x, y : tDef.y, w : tDef.w, h : tDef.h};
        tRoot.setAttribute ("viewBox", `${tVB.x} ${tVB.y} ${tVB.w} ${tVB.h}`);
      }
    pTpl._svgVB = tVB;
    pTpl._sceneGeometrieActive = normalise_geometrie_scene_client (GEOMETRIE_SCENE_DEFAUT);
    pTpl._svgVBBrut = {...tVB};
    pTpl._svgVBInit = {...tVB};
    pTpl._svgVBInitBrut = {...pTpl._svgVBBrut};

    let tPanZoomUiRaf = null;
    const synchronise_ui_panzoom = (pImmediat = false) =>
      {
        const tSynchro = () =>
          {
            tPanZoomUiRaf = null;
            pTpl._svgPanZoomUiRaf = null;
            const tNat = pTpl._svgVBNatif;
            gPanX.set (tVB.x);
            gPanY.set (tVB.y);
            gZoom.set ((tNat && tNat.w) ? (tNat.w / tVB.w) : 1);
          };
        if (pImmediat)
          {
            if (tPanZoomUiRaf)
              {
                cancelAnimationFrame (tPanZoomUiRaf);
                tPanZoomUiRaf = null;
                pTpl._svgPanZoomUiRaf = null;
              }
            tSynchro ();
            return;
          }
        if (tPanZoomUiRaf) return;
        tPanZoomUiRaf = requestAnimationFrame (tSynchro);
        pTpl._svgPanZoomUiRaf = tPanZoomUiRaf;
      };

    const applique_vb = () =>
      {
        tRoot.setAttribute ("viewBox",
          `${tVB.x} ${tVB.y} ${tVB.w} ${tVB.h}`);
        pTpl._svgVBBrut = vue_camera_brute_depuis_vue_svg_scene (pTpl, tVB) || {...tVB};
        // Repositionne les rectangles de mise en évidence existants en
        // fonction du nouveau viewBox.
        repositionne_surbrillances_svg (pDoc);
        actualise_transform_calques_cartes_tpl (pTpl);
        programme_synchronise_items_hype_tpl (pTpl);
        // Notifie l'UI du changement courant de pan / zoom.
        synchronise_ui_panzoom ();
      };
    pTpl._svgApplique = applique_vb;
    // Première synchro avec l'UI (pour les inputs au montage).
    {
      synchronise_ui_panzoom (true);
    }
    actualise_rapport_lh_scene_tpl (pTpl, {force : true, recentrer : true});

    // Convertit un point écran en coord. utilisateur racine.
    const point_user = (pX, pY) =>
      {
        const tSvgRoot = tRoot && typeof tRoot.getScreenCTM === "function"
          ? tRoot
          : (pDoc && pDoc.documentElement && typeof pDoc.documentElement.getScreenCTM === "function"
            ? pDoc.documentElement
            : null);
        if (!tSvgRoot) return null;
        const tCtm = tSvgRoot.getScreenCTM ();
        if (!tCtm) return null;
        try
          {
            const tInv = tCtm.inverse ();
            return {
              x : pX * tInv.a + pY * tInv.c + tInv.e,
              y : pX * tInv.b + pY * tInv.d + tInv.f,
            };
          }
        catch (e) { return null; }
      };

    const point_normalise_ecran = (pX, pY) =>
      {
        const tRect = tRoot.getBoundingClientRect ();
        if (!tRect || !tRect.width || !tRect.height) return null;
        return {
          u : Math.max (0, Math.min (1, (pX - tRect.left) / tRect.width)),
          v : Math.max (0, Math.min (1, (pY - tRect.top)  / tRect.height)),
        };
      };

    const annule_anim_svg = () =>
      {
        annule_animation_viewbox_tpl (pTpl);
      };

    const assure_selection_bulle_calque = (pCarteId) =>
      {
        if (!pCarteId) return true;
        const tBrouillon = brouillon_bulle_courant ();
        if (gBulleSelectionnee.get () === pCarteId
         && tBrouillon
         && tBrouillon.carte_id === pCarteId)
          return true;
        return !!selectionne_bulle_calque (pCarteId);
      };

    const cible_bulle_carte = (pTarget) =>
      {
        let tNoeud = pTarget;
        while (tNoeud && tNoeud !== tRoot)
          {
            if (tNoeud.classList && tNoeud.classList.contains (CARTE_DECOR_BULLE_HIT_CLASS))
              return tNoeud.getAttribute ("data-carte-id") || null;
            if (tNoeud.classList && tNoeud.classList.contains (CARTE_DECOR_BULLE_CLASS))
              return tNoeud.getAttribute ("data-carte-id") || null;
            tNoeud = tNoeud.parentNode;
          }
        return null;
      };

    const cible_groupe_bulle_carte = (pTarget) =>
      {
        let tNoeud = pTarget;
        while (tNoeud && tNoeud !== tRoot)
          {
            if (tNoeud.classList && tNoeud.classList.contains (CARTE_DECOR_BULLE_CLASS))
              return tNoeud;
            tNoeud = tNoeud.parentNode;
          }
        return null;
      };

    const cible_visuel_bulle_carte = (pTarget) =>
      {
        const tGroupe = cible_groupe_bulle_carte (pTarget);
        if (!tGroupe || typeof tGroupe.querySelector !== "function") return null;
        return tGroupe.querySelector (`.${CARTE_DECOR_BULLE_VISUAL_CLASS}`) || tGroupe;
      };

    const info_bulle_carte_depuis_point = (pPoint) =>
      {
        if (!pPoint || typeof tRoot.querySelectorAll !== "function") return null;
        const tZones = tRoot.querySelectorAll (`.${CARTE_DECOR_BULLE_HIT_CLASS}`);
        let tMeilleure = null;
        tZones.forEach ((pZone) =>
          {
            const tGroupe = cible_groupe_bulle_carte (pZone);
            const tCarteId = (tGroupe && tGroupe.getAttribute ("data-carte-id"))
              || pZone.getAttribute ("data-carte-id")
              || null;
            const tBBox = bbox_racine_pour_element (tRoot, pZone)
              || bbox_racine_pour_element (tRoot, tGroupe);
            if (!tGroupe || !tCarteId || !tBBox) return;
            const tRayonX = tBBox.w / 2;
            const tRayonY = tBBox.h / 2;
            if (tRayonX <= 0 || tRayonY <= 0) return;
            const tCx = tBBox.x + tRayonX;
            const tCy = tBBox.y + tRayonY;
            const tNorme =
              ((pPoint.x - tCx) * (pPoint.x - tCx)) / (tRayonX * tRayonX)
              + ((pPoint.y - tCy) * (pPoint.y - tCy)) / (tRayonY * tRayonY);
            if (tNorme > 1) return;
            if (!tMeilleure || tNorme < tMeilleure.norme)
              tMeilleure =
                {
                  carteId : tCarteId,
                  node : cible_visuel_bulle_carte (tGroupe),
                  norme : tNorme,
                };
          });
        return tMeilleure;
      };

    const cible_poignee_selection_carte = (pTarget) =>
      {
        let tNoeud = pTarget;
        while (tNoeud && tNoeud !== tRoot)
          {
            if (tNoeud.classList && tNoeud.classList.contains (SELECTION_BULLE_HANDLE_CLASS))
              {
                return {
                  carteId : tNoeud.getAttribute ("data-bulle-carte-id") || null,
                  coin : tNoeud.getAttribute ("data-handle") || null,
                };
              }
            tNoeud = tNoeud.parentNode;
          }
        return null;
      };

    const cible_drag_selection_carte = (pTarget) =>
      {
        let tNoeud = pTarget;
        while (tNoeud && tNoeud !== tRoot)
          {
            if (tNoeud.getAttribute && tNoeud.getAttribute ("data-overlay-role") === "move")
              {
                return {
                  carteId : tNoeud.getAttribute ("data-bulle-carte-id") || null,
                };
              }
            if (tNoeud.classList
             && tNoeud.classList.contains (SELECTION_BULLE_GROUP_CLASS)
             && tNoeud.getAttribute)
              {
                const tCarteId = tNoeud.getAttribute ("data-bulle-carte-id") || null;
                if (tCarteId)
                  {
                    return {
                      carteId : tCarteId,
                    };
                  }
              }
            tNoeud = tNoeud.parentNode;
          }
        return null;
      };

    const masque_focus_traversable_pendant_retour = (pNoeud) =>
      !!(pTpl
        && pTpl._lectureZoomAltReaffichageMasquesEnCours
        && pNoeud
        && pNoeud.getAttribute
        && pNoeud.getAttribute (CARTE_MASK_STATE_ATTR) === "showing");

    const noeud_masque_par_focus_carte = (pNoeud) =>
      {
        let tNoeud = pNoeud;
        while (tNoeud && tNoeud !== tRoot)
          {
            if (tNoeud.getAttribute && tNoeud.getAttribute (CARTE_MASK_ATTR) === "1")
              {
                if (masque_focus_traversable_pendant_retour (tNoeud))
                  {
                    tNoeud = tNoeud.parentNode;
                    continue;
                  }
                return true;
              }
            tNoeud = tNoeud.parentNode;
          }
        return false;
      };

    const cible_id_svg_pertinent = (pTarget) =>
      {
        const tIds = pTpl._idsPertinents;
        let tNoeud = pTarget;
        while (tNoeud && tNoeud !== tRoot)
          {
            if (noeud_masque_par_focus_carte (tNoeud)) return null;
            if (tNoeud.getAttribute && tNoeud.getAttribute ("data-cogibulle-interface-item") === "1")
              return tNoeud.getAttribute ("id") || null;
            const tId = tNoeud.getAttribute && tNoeud.getAttribute ("id");
            if (tId && tIds && tIds.has (tId)) return tId;
            tNoeud = tNoeud.parentNode;
          }
        return null;
      };

    const cible_item_interface_lecture = (pTarget) =>
      {
        const tId = cible_id_svg_pertinent (pTarget);
        if (!tId || trouve_categorie_pour_id (tId) !== CALQUES_CATEGORIE_INTERFACE)
          return null;
        return tId;
      };

    const item_interface_lecture_depuis_point = (pPoint) =>
      {
        if (!pPoint || !categorie_scene_visible (CALQUES_CATEGORIE_INTERFACE)) return null;
        if (categorie_scene_verrouillee (CALQUES_CATEGORIE_INTERFACE)) return null;
        if (items_interface_masques_lecture_tpl (pTpl)) return null;
        const tLayer = pDoc.getElementById (SVG_INTERFACE_LAYER_ID);
        if (!tLayer || tLayer.style.display === "none" || tLayer.style.pointerEvents === "none")
          return null;
        const tNoeuds = Array.from (tLayer.querySelectorAll ("[data-cogibulle-interface-item='1']"));
        for (let i = tNoeuds.length - 1; i >= 0; i--)
          {
            const tNoeud = tNoeuds[i];
            const tId = tNoeud && tNoeud.getAttribute && tNoeud.getAttribute ("id");
            if (!tId) continue;
            if (noeud_masque_par_focus_carte (tNoeud)) continue;
            const tItem = item_interface_scene_par_id (tId);
            if (!tItem || tItem.item_masque || !objet_visible_mode_affichage_diffusion_client (tItem)) continue;
            if (tNoeud.style.display === "none") continue;
            const tBBox = bbox_racine_pour_element (tRoot, tNoeud);
            if (!tBBox || tBBox.w <= 0 || tBBox.h <= 0) continue;
            if (pPoint.x < tBBox.x || pPoint.x > tBBox.x + tBBox.w) continue;
            if (pPoint.y < tBBox.y || pPoint.y > tBBox.y + tBBox.h) continue;
            return tId;
          }
        return null;
      };

    const item_interface_lecture_depuis_evenement = (pEvent) =>
      {
        const tCible = cible_item_interface_lecture (pEvent && pEvent.target);
        if (tCible) return tCible;
        return item_interface_lecture_depuis_point (
          pEvent ? point_user (pEvent.clientX, pEvent.clientY) : null
        );
      };

    const bouton_scene_lecture_depuis_point = (pPoint) =>
      {
        if (!pTpl || !(pTpl._lectureZoomAltReaffichageMasquesEnCours || pTpl._lectureZoomAltSortieEnCours)) return null;
        if (!pPoint || !categorie_scene_visible (CALQUES_CATEGORIE_BOUTONS)) return null;
        if (categorie_scene_verrouillee (CALQUES_CATEGORIE_BOUTONS)) return null;
        if (!tRoot || typeof tRoot.querySelectorAll !== "function") return null;
        const tNoeuds = Array.from (tRoot.querySelectorAll ("[id]"));
        for (let i = tNoeuds.length - 1; i >= 0; i--)
          {
            const tNoeud = tNoeuds[i];
            const tId = tNoeud && tNoeud.getAttribute && tNoeud.getAttribute ("id");
            if (!tId || trouve_categorie_pour_id (tId) !== CALQUES_CATEGORIE_BOUTONS) continue;
            if (noeud_masque_par_focus_carte (tNoeud)) continue;
            if (tNoeud.style && tNoeud.style.display === "none") continue;
            const tBBox = bbox_racine_pour_element (tRoot, tNoeud);
            if (!tBBox || tBBox.w <= 0 || tBBox.h <= 0) continue;
            if (pPoint.x < tBBox.x || pPoint.x > tBBox.x + tBBox.w) continue;
            if (pPoint.y < tBBox.y || pPoint.y > tBBox.y + tBBox.h) continue;
            return tId;
          }
        return null;
      };

    const interrompt_sortie_focus_carte_avant_interaction_scene = () =>
      {
        if (!pTpl || !pTpl._lectureZoomAltSortieEnCours) return false;
        return interrompt_sortie_focus_carte_lecture_tpl (pTpl);
      };

    const tLectureHoverPrev = new WeakMap ();
    const tLectureHoverTimers = new WeakMap ();
    let tLectureHoverInfo = null;

    const duree_hover_lecture_ms = () =>
      reglages_mode_lecture_scene ().hover_transition_ms;

    const amplitude_survol_obj_lecture = () =>
      Math.max (0, Number (reglages_mode_lecture_scene ().survol_obj_scale_pct) || 0) / 100;

    const doit_animer_frame_lecture = (pEtat, pNow, pFinal = false) =>
      {
        const tDelaiFrame = delai_frame_animation_lecture_scene ();
        if (tDelaiFrame <= 0 || pFinal) return true;
        const tDerniereFrame = Number (pEtat && pEtat.derniereFrame) || 0;
        if (tDerniereFrame && pNow - tDerniereFrame < tDelaiFrame) return false;
        if (pEtat) pEtat.derniereFrame = pNow;
        return true;
      };

    const est_objet_survol_lecture = (pType) =>
      pType === "objet_obj" || pType === "objet_zoog" || pType === "objet_part" || pType === "bouton";

    const transition_hover_lecture = (pTransition) =>
      {
        const tDuree = duree_hover_lecture_ms ();
        const tTransition = `filter ${tDuree}ms ease, opacity ${tDuree}ms ease, transform ${tDuree}ms ease`;
        return pTransition ? `${pTransition}, ${tTransition}` : tTransition;
      };

    let tHoverDefs = null;
    let tHoverSerial = 0;

    const defs_effets_lecture = () =>
      {
        if (tHoverDefs && tHoverDefs.parentNode) return tHoverDefs;
        if (!pDoc || !tRoot) return null;
        let tDefs = tRoot.querySelector && tRoot.querySelector ("defs[data-cogi-lecture-effects='1']");
        if (!tDefs)
          {
            tDefs = cree_element_svg_decor (tRoot, "defs");
            tDefs.setAttribute ("data-cogi-lecture-effects", "1");
            tRoot.insertBefore (tDefs, tRoot.firstChild || null);
          }
        tHoverDefs = tDefs;
        return tDefs;
      };

    const ancre_racine_survol_lecture = (pNoeud) =>
      {
        let tNoeud = pNoeud;
        while (tNoeud && tNoeud.parentNode && tNoeud.parentNode !== tRoot)
          tNoeud = tNoeud.parentNode;
        return tNoeud || pNoeud;
      };

    const config_survol_lecture_pour_info = (pInfo) =>
      {
        const tContourDefaut = reglages_survol_contour_projet_mode_lecture ();
        if (!pInfo || !pInfo.id)
          return {contour : tContourDefaut};

        const tSceneId = Session.get ("scene_id");
        const tScene = tSceneId ? Scenes.findOne (tSceneId) : null;
        const tContour = reglages_survol_contour_objet_ou_parent_pour_id_scene (tScene, pInfo.id);
        return {
          contour : tContour,
        };
      };

    const cree_nettoyage_overlay_survol = (pNoeud, pFiltre = null) =>
      {
        let tActif = true;
        return (pImmediat = false) =>
          {
            if (!tActif) return;
            tActif = false;
            const retire = () =>
              {
                if (pNoeud && pNoeud.parentNode) pNoeud.parentNode.removeChild (pNoeud);
                if (pFiltre && pFiltre.parentNode) pFiltre.parentNode.removeChild (pFiltre);
              };
            if (pImmediat)
              {
                retire ();
                return;
              }
            pNoeud.style.opacity = "0";
            setTimeout (retire, duree_hover_lecture_ms () + 40);
          };
      };

    const anime_entree_overlay_survol = (pNoeud) =>
      {
        if (!pNoeud) return;
        const tGeneration = Number (pTpl && pTpl._lectureContourGeneration) || 0;
        pNoeud.style.opacity = "0";
        pNoeud.style.transition = `opacity ${duree_hover_lecture_ms ()}ms ease`;
        requestAnimationFrame (() =>
          {
            if (!pNoeud.parentNode) return;
            if ((Number (pTpl && pTpl._lectureContourGeneration) || 0) !== tGeneration)
              {
                if (pNoeud.parentNode) pNoeud.parentNode.removeChild (pNoeud);
                return;
              }
            pNoeud.style.opacity = "1";
          });
      };

    const cree_overlay_contour_survol_lecture = (pInfo) =>
      {
        const tNoeud = pInfo && pInfo.node;
        const tIdsAssocies = Array.isArray (pInfo && pInfo.contourIds) && pInfo.contourIds.length
          ? pInfo.contourIds
          : ((pInfo && pInfo.id) ? [pInfo.id] : []);
        if (!tNoeud || !tNoeud.parentNode || !tIdsAssocies.length) return null;

        const tClones = [];
        const tContour = config_survol_lecture_pour_info (pInfo).contour;
        const tEpaisseur = Math.max (0, Number (tContour.epaisseur) || 0);
        const tLargeurTrait = Math.max (0.5, tEpaisseur);
        const tOpacite = Math.max (0, Math.min (1, Number (tContour.opacite) || 0));

        tIdsAssocies.forEach ((pId) =>
          {
            const tSource = pId ? pDoc.getElementById (pId) : null;
            if (!tSource || !tSource.parentNode) return;
            let tBBox = null;
            try { tBBox = typeof tSource.getBBox === "function" ? tSource.getBBox () : null; }
            catch (e) { return; }
            if (!tBBox || (!tBBox.width && !tBBox.height)) return;

            const tClone = tSource.cloneNode (true);
            retire_id_clone_svg (tClone);
            tClone.setAttribute ("data-cogi-lecture-contour-overlay", "1");
            enregistre_noeud_contour_lecture_tpl (pTpl, tClone);
            const tNoeudsGraphiques = [tClone].concat (
              typeof tClone.querySelectorAll === "function"
                ? Array.from (tClone.querySelectorAll ("*"))
                : []
            );
            tNoeudsGraphiques.forEach ((pElt) =>
              {
                if (!pElt || !pElt.setAttribute) return;
                const tTag = (pElt.tagName || "").toLowerCase ();
                if (["defs", "clipPath", "mask", "pattern", "marker", "linearGradient", "radialGradient", "filter", "style", "script", "title", "desc"].includes (tTag))
                  return;
                pElt.removeAttribute ("filter");
                if (pElt.style) pElt.style.filter = "";
                pElt.setAttribute ("fill", "none");
                pElt.setAttribute ("stroke", tContour.couleur);
                pElt.setAttribute ("stroke-opacity", tOpacite.toFixed (3));
                pElt.setAttribute ("stroke-width", String (tLargeurTrait));
                pElt.setAttribute ("stroke-linejoin", "round");
                pElt.setAttribute ("stroke-linecap", "round");
                pElt.setAttribute ("vector-effect", "non-scaling-stroke");
                if (pElt.style)
                  {
                    pElt.style.removeProperty ("fill");
                    pElt.style.removeProperty ("stroke");
                    pElt.style.removeProperty ("stroke-opacity");
                    pElt.style.removeProperty ("stroke-width");
                    pElt.style.removeProperty ("stroke-linejoin");
                    pElt.style.removeProperty ("stroke-linecap");
                    pElt.style.removeProperty ("vector-effect");
                  }
              });
            tClone.style.pointerEvents = "none";
            tClone.style.visibility = "visible";
            tClone.style.opacity = tOpacite.toFixed (3);
            tSource.parentNode.insertBefore (tClone, tSource);
            anime_entree_overlay_survol (tClone);
            tClones.push (tClone);
          });
        if (!tClones.length) return null;
        return (pImmediat = false) =>
          {
            tClones.forEach ((pClone) =>
              {
                const tCleanup = cree_nettoyage_overlay_survol (pClone);
                tCleanup (pImmediat);
              });
          };
      };

    const affiche_contour_objet_lecture = (pObjId, pOptions = {}) =>
      {
        const tObjId = typeof pObjId === "string" ? pObjId : "";
        if (!tObjId) return false;
        const tEtat = pTpl && pTpl._lecturePedago;
        if (!mode_lecture_scene_actif () || !tEtat || !tEtat.actif || gLectureSceneFocusCarte.get () !== tEtat.carteId)
          return false;
        nettoie_contours_lecture_tpl (pTpl, false);
        const tSceneId = Session.get ("scene_id");
        const tScene = tSceneId ? Scenes.findOne (tSceneId) : null;
        const tContourIds = ids_elements_associes_obj_lecture (tScene, tObjId);
        const tNode = pDoc.getElementById (tObjId);
        const tCleanup = cree_overlay_contour_survol_lecture (
          {
            type : "objet_obj",
            id : tObjId,
            contourIds : tContourIds,
            node : tNode,
          }
        );
        if (!tCleanup) return false;
        const tCleanupActif = enregistre_nettoyage_contour_lecture_tpl (pTpl, tCleanup);
        if (pOptions && pOptions.sansTimer)
          return tCleanupActif;
        const tDureeMs = Math.max (
          0,
          Number (reglages_mode_lecture_scene ().nouvel_essai_contour_duree_s) || 0
        ) * 1000;
        timer_pedago_lecture_tpl (pTpl, () => tCleanupActif (false), tDureeMs);
        return tCleanupActif;
      };
    const affiche_contour_relecture_objet_courant_lecture = (pOptions = {}) =>
      {
        const tObj = objet_pedago_courant_lecture (pTpl);
        return affiche_contour_objet_lecture (tObj && tObj.id, pOptions);
      };
    pTpl._afficheContourObjetLecture = affiche_contour_objet_lecture;
    pTpl._afficheContourObjetCourantLecture = affiche_contour_relecture_objet_courant_lecture;

    const relit_audio_objet_courant_carte_lecture = () =>
      {
        const tEtat = pTpl && pTpl._lecturePedago;
        const tObj = objet_pedago_courant_lecture (pTpl);
        if (!tEtat || !tEtat.actif || tEtat.attente || !tObj || !tObj.id) return false;
        const tCleanupContour = affiche_contour_relecture_objet_courant_lecture ({sansTimer : true});
        joue_sequence_entends_tu_objet_lecture_scene (tObj.id)
          .then (() =>
            {
              const tCourant = pTpl && pTpl._lecturePedago;
              if (!tCourant || !tCourant.actif || tCourant.carteId !== tEtat.carteId)
                return;
              if (typeof tCleanupContour === "function") tCleanupContour (false);
            });
        return true;
      };

    const applique_style_hover_lecture = (pInfo) =>
      {
        const pNoeud = pInfo && pInfo.node;
        const pType = pInfo && pInfo.type;
        if (!pNoeud || !pNoeud.style) return;
        const tTimer = tLectureHoverTimers.get (pNoeud);
        if (tTimer) clearTimeout (tTimer);
        if (!tLectureHoverPrev.has (pNoeud))
          tLectureHoverPrev.set (pNoeud,
            {
              filter : pNoeud.style.filter || "",
              filterAttr : pNoeud.getAttribute ? (pNoeud.getAttribute ("filter") || "") : "",
              opacity : pNoeud.style.opacity || "",
              transform : pNoeud.style.transform || "",
              transformBox : pNoeud.style.transformBox || "",
              transformOrigin : pNoeud.style.transformOrigin || "",
              cursor : pNoeud.style.cursor || "",
              transition : pNoeud.style.transition || "",
              overlayCleanup : null,
            });

        const tFiltre = pType === "bulle"
          ? "drop-shadow(0 0 9px rgba(255,255,255,0.72)) drop-shadow(0 3px 12px rgba(17,133,200,0.34)) brightness(1.06)"
          : est_objet_survol_lecture (pType)
            ? "drop-shadow(0 0 8px rgba(255,255,255,0.78)) drop-shadow(0 3px 12px rgba(17,133,200,0.38)) brightness(1.1) saturate(1.1)"
            : "drop-shadow(0 0 6px rgba(255,255,255,0.68)) drop-shadow(0 2px 10px rgba(17,133,200,0.32)) brightness(1.05) saturate(1.06)";
        const tPrev = tLectureHoverPrev.get (pNoeud);
        if (tPrev.overlayCleanup)
          {
            tPrev.overlayCleanup (true);
            tPrev.overlayCleanup = null;
          }

        pNoeud.style.transition = transition_hover_lecture (tPrev.transition);
        pNoeud.style.cursor = "pointer";
        if (est_objet_survol_lecture (pType))
          {
            tPrev.overlayCleanup = cree_overlay_contour_survol_lecture (pInfo) || null;
          }
        if (pType !== "bulle")
          {
            pNoeud.style.filter = tFiltre;
            pNoeud.style.transform = tPrev.transform;
            return;
          }

        pNoeud.style.filter = tFiltre;
        if (pType === "bulle")
          {
            const tAmplitude = amplitude_survol_obj_lecture ();
            pNoeud.style.transformBox = "fill-box";
            pNoeud.style.transformOrigin = "center";
            pNoeud.style.transform = tAmplitude > 0
              ? `scale(${(1 + tAmplitude).toFixed (3)})`
              : "";
          }
      };

    const restaure_style_hover_lecture = (pNoeud) =>
      {
        if (!pNoeud || !pNoeud.style || !tLectureHoverPrev.has (pNoeud)) return;
        const tPrev = tLectureHoverPrev.get (pNoeud);
        if (tPrev.overlayCleanup)
          {
            tPrev.overlayCleanup (false);
            tPrev.overlayCleanup = null;
          }
        pNoeud.style.filter = tPrev.filter;
        if (pNoeud.setAttribute)
          {
            if (tPrev.filterAttr) pNoeud.setAttribute ("filter", tPrev.filterAttr);
            else                  pNoeud.removeAttribute ("filter");
          }
        pNoeud.style.opacity = tPrev.opacity;
        pNoeud.style.transform = tPrev.transform;
        pNoeud.style.cursor = tPrev.cursor;
        const tTimer = setTimeout (() =>
          {
            if (!tLectureHoverPrev.has (pNoeud)) return;
            pNoeud.style.transformBox = tPrev.transformBox;
            pNoeud.style.transformOrigin = tPrev.transformOrigin;
            pNoeud.style.transition = tPrev.transition;
            tLectureHoverPrev.delete (pNoeud);
            tLectureHoverTimers.delete (pNoeud);
          }, duree_hover_lecture_ms () + 40);
        tLectureHoverTimers.set (pNoeud, tTimer);
      };

    const efface_hover_lecture = (pImmediat = false) =>
      {
        if (tLectureHoverInfo && tLectureHoverInfo.node)
          {
            const tNoeud = tLectureHoverInfo.node;
            if (pImmediat && tLectureHoverPrev.has (tNoeud))
              {
                const tPrev = tLectureHoverPrev.get (tNoeud);
                if (tPrev && tPrev.overlayCleanup)
                  {
                    tPrev.overlayCleanup (true);
                    tPrev.overlayCleanup = null;
                  }
              }
            restaure_style_hover_lecture (tNoeud);
          }
        tLectureHoverInfo = null;
        tRoot.style.cursor = "";
      };
    pTpl._effaceHoverLectureSvg = efface_hover_lecture;

    const info_interaction_lecture = (e) =>
      {
        if (!mode_lecture_scene_actif ()) return null;
        if (gLectureSceneFocusCarte.get () && !pTpl._lectureFocusInteractionsActives && !pTpl._lectureZoomAltSortieEnCours)
          return null;
        const tPoint = point_user (e.clientX, e.clientY);
        const tPointCarte = point_cartes_brut_depuis_point_svg (pTpl, tPoint);
        const tCarteFocus = carte_focus_lecture_scene ();
        const tBullesCliquables = !tCarteFocus || !!pTpl._lectureZoomAltSortieEnCours;
        const tBulle = tBullesCliquables
          ? info_bulle_carte_depuis_point (tPoint)
            || (() =>
              {
                const tCarteBulleId = cible_bulle_carte (e.target);
                return tCarteBulleId
                  ? {
                      carteId : tCarteBulleId,
                      node : cible_visuel_bulle_carte (e.target),
                    }
                  : null;
              }) ()
          : null;
        if (tBulle && tBulle.carteId)
          return {
            type : "bulle",
            key : `bulle:${tBulle.carteId}`,
            carteId : tBulle.carteId,
            node : tBulle.node,
          };
        const tVueFocus = normalise_vue_carte_client (tCarteFocus && tCarteFocus.vue);
        if (tVueFocus && !pTpl._lectureZoomAltActif && !point_dans_vue_carte (tPointCarte, tVueFocus))
          return {type : "sortie_carte", key : "sortie_carte", node : null};
        const tInterfaceLecture = item_interface_lecture_depuis_evenement (e);
        if (tInterfaceLecture)
          {
            if (!item_interface_a_action_lecture_scene (tInterfaceLecture))
              return {
                type : "interface_blocker",
                key : `interface_blocker:${tInterfaceLecture}`,
                id : tInterfaceLecture,
                node : null,
                bloque : true,
              };
            return {
              type : "bouton",
              key : `bouton:${tInterfaceLecture}`,
              id : tInterfaceLecture,
              node : pDoc.getElementById (tInterfaceLecture),
            };
          }

        const tRoleZoomAlt = tCarteFocus && pTpl._lectureZoomAltActif
          ? role_zoom_alternatif_depuis_cible (e.target)
          : null;
        if (tRoleZoomAlt
         && tRoleZoomAlt !== "carte_jauge"
         && tRoleZoomAlt !== CARTE_ZOOM_ALTERNATIF_RELECTURE_ROLE
         && survol_interface_carte_zoom_actif_client ())
          return {
            type : "zoom_alt_button",
            key : `zoom_alt:${tRoleZoomAlt}`,
            role : tRoleZoomAlt,
            node : noeud_zoom_alternatif_depuis_cible (e.target),
          };

        const tId = cible_id_svg_pertinent (e.target)
          || bouton_scene_lecture_depuis_point (tPoint);
        if (!tId) return null;
        const tCategorie = trouve_categorie_pour_id (tId);
        if (tCategorie === CALQUES_CATEGORIE_INTERFACE
            && !item_interface_a_action_lecture_scene (tId))
          return null;
        if (categorie_est_bouton_lecture (tCategorie))
          return {
            type : "bouton",
            key : `bouton:${tId}`,
            id : tId,
            node : pDoc.getElementById (tId),
          };
        if (tCarteFocus && tCategorie === "obj")
          {
            const tSceneId = Session.get ("scene_id");
            const tScene = tSceneId ? Scenes.findOne (tSceneId) : null;
            const tPartIds = ids_parts_associes_a_objet_scene (tScene, tId);
            const tContourIds = [tId].concat (tPartIds).filter (Boolean);
            const tFamille = tContourIds.join ("|");
            return {
              type : "objet_obj",
              key : `objet:${tId}:parts:${tFamille}`,
              id : tId,
              contourIds : tContourIds,
              node : pDoc.getElementById (tId),
            };
          }
        if (tCarteFocus && tCategorie === CALQUES_CATEGORIE_PART)
          return {
            type : "objet_part",
            key : (() =>
              {
                const tSceneId = Session.get ("scene_id");
                const tScene = tSceneId ? Scenes.findOne (tSceneId) : null;
                const tParentId = id_obj_parent_pour_part_scene (tScene, tId);
                const tPartIds = ids_parts_associes_a_objet_scene (tScene, tId);
                const tContourIds = [tParentId].concat (tPartIds).filter (Boolean);
                const tFamille = tContourIds.length ? tContourIds.join ("|") : tId;
                return `partfam:${tFamille}`;
              }) (),
            id : tId,
            contourIds : (() =>
              {
                const tSceneId = Session.get ("scene_id");
                const tScene = tSceneId ? Scenes.findOne (tSceneId) : null;
                const tParentId = id_obj_parent_pour_part_scene (tScene, tId);
                const tPartIds = ids_parts_associes_a_objet_scene (tScene, tId);
                const tContourIds = [tParentId].concat (tPartIds).filter (Boolean);
                return tContourIds.length ? tContourIds : [tId];
              }) (),
            node : pDoc.getElementById (tId),
          };
        if (tCategorie === CALQUES_CATEGORIE_ZOOG)
          return {
            type : "objet_zoog",
            key : `objet:${tId}`,
            id : tId,
            node : pDoc.getElementById (tId),
          };
        return null;
      };

    const applique_hover_lecture = (pInfo) =>
      {
        if (gLectureSceneFocusCarte.get () && !pTpl._lectureFocusInteractionsActives && !pTpl._lectureZoomAltSortieEnCours)
          pInfo = null;
        if ((Number (pTpl._lectureImpulsionsActives) || 0) > 0)
          {
            if (tLectureHoverInfo && tLectureHoverInfo.node)
              restaure_style_hover_lecture (tLectureHoverInfo.node);
            tLectureHoverInfo = null;
            tRoot.style.cursor = "";
            return;
          }
        const tKey = pInfo && pInfo.key;
        if (tLectureHoverInfo && tLectureHoverInfo.key === tKey) return;
        if (tLectureHoverInfo && tLectureHoverInfo.node)
          restaure_style_hover_lecture (tLectureHoverInfo.node);
        tLectureHoverInfo = pInfo && !pInfo.bloque ? pInfo : null;
        tRoot.style.cursor = (pInfo && !pInfo.bloque) ? "pointer" : "";
        if (pInfo && !pInfo.bloque && pInfo.node)
          applique_style_hover_lecture (pInfo);
      };

    function retire_id_clone_svg (pNoeud)
      {
        if (!pNoeud || !pNoeud.removeAttribute) return;
        const retire_id = (pElt) =>
          {
            if (pElt && pElt.removeAttribute) pElt.removeAttribute ("id");
          };
        retire_id (pNoeud);
        if (typeof pNoeud.querySelectorAll === "function")
          pNoeud.querySelectorAll ("[id]").forEach ((pEnfant) =>
            {
              retire_id (pEnfant);
            });
      }

    const calcule_echelle_impulsion_douce_lecture = (pT, pAmplitude) =>
      {
        const t = Math.max (0, Math.min (1, Number (pT) || 0));
        const tAmplitude = Math.max (0, Number (pAmplitude) || 0);
        const tPulse = Math.sin (Math.PI * t);
        return 1 + (tAmplitude * tPulse * tPulse);
      };

    const anime_impulsion_douce_noeud_lecture = (pNoeud, pDuree, pAmplitude, pOptions = {}) =>
      {
        if (!pNoeud || !pNoeud.parentNode) return false;
        const tDuree = Math.max (0, Number (pDuree) || 0);
        const tAmplitude = Math.max (0, Number (pAmplitude) || 0);
        if (tDuree <= 0 || tAmplitude <= 0) return false;
        if (typeof pNoeud._cogiLectureImpulseCancel === "function")
          pNoeud._cogiLectureImpulseCancel ();
        if (!Number.isFinite (pTpl._lectureImpulsionsActives))
          pTpl._lectureImpulsionsActives = 0;
        pTpl._lectureImpulsionsActives += 1;
        applique_hover_lecture (null);

        const tOnComplete = pOptions && typeof pOptions.onComplete === "function"
          ? pOptions.onComplete
          : null;
        const tOnCancel = pOptions && typeof pOptions.onCancel === "function"
          ? pOptions.onCancel
          : null;
        const tHoverTimer = tLectureHoverTimers.get (pNoeud);
        if (tHoverTimer)
          {
            clearTimeout (tHoverTimer);
            tLectureHoverTimers.delete (pNoeud);
          }

        const tAvant =
          {
            transformAttr : pNoeud.getAttribute ? (pNoeud.getAttribute ("transform") || "") : "",
            transition : pNoeud.style.transition || "",
          };
        pNoeud.style.transition = "none";
        let tBBox = null;
        try { tBBox = typeof pNoeud.getBBox === "function" ? pNoeud.getBBox () : null; }
        catch (e) { tBBox = null; }
        const tCx = tBBox ? Number (tBBox.x) + (Number (tBBox.width) / 2) : 0;
        const tCy = tBBox ? Number (tBBox.y) + (Number (tBBox.height) / 2) : 0;

        const tT0 = (typeof performance !== "undefined" && performance.now)
          ? performance.now ()
          : Date.now ();
        const tFrameEtat = {derniereFrame : 0};
        let tActif = true;
        let tRaf = null;
        const restaure = () =>
          {
            if (pNoeud.setAttribute)
              {
                if (tAvant.transformAttr) pNoeud.setAttribute ("transform", tAvant.transformAttr);
                else                      pNoeud.removeAttribute ("transform");
              }
            pNoeud.style.transition = tAvant.transition;
          };
        const nettoie = (pAnnule = false) =>
          {
            if (!tActif) return;
            tActif = false;
            if (tRaf) cancelAnimationFrame (tRaf);
            restaure ();
            delete pNoeud._cogiLectureImpulseCancel;
            pTpl._lectureImpulsionsActives = Math.max (0, (Number (pTpl._lectureImpulsionsActives) || 0) - 1);
            if (pAnnule)
              {
                if (tOnCancel) tOnCancel ();
                return;
              }
            if (tOnComplete) tOnComplete ();
          };
        pNoeud._cogiLectureImpulseCancel = () => nettoie (true);
        const tStep = (pNow) =>
          {
            if (!tActif) return;
            if (!pNoeud.parentNode)
              {
                nettoie (true);
                return;
              }
            const tT = Math.min (1, (pNow - tT0) / tDuree);
            const tFinal = tT >= 1;
            if (doit_animer_frame_lecture (tFrameEtat, pNow, tFinal))
              {
                const tScale = calcule_echelle_impulsion_douce_lecture (tT, tAmplitude);
                const tImpulse = `translate(${tCx} ${tCy}) scale(${tScale.toFixed (4)}) translate(${-tCx} ${-tCy})`;
                pNoeud.setAttribute ("transform", [tAvant.transformAttr, tImpulse].filter (Boolean).join (" "));
              }
            if (!tFinal)
              {
                tRaf = requestAnimationFrame (tStep);
                return;
              }
            nettoie ();
          };
        tRaf = requestAnimationFrame (tStep);
        return true;
      };

    const anime_reveil_element_lecture = (pId) =>
      {
        const tElt = pId ? pDoc.getElementById (pId) : null;
        const tBBox = bbox_racine_pour_element (tRoot, tElt);
        if (!tElt || !tBBox || tBBox.w <= 0 || tBBox.h <= 0) return;
        const tReglages = reglages_mode_lecture_scene ();
        const tDuree = Math.max (0, Number (tReglages.reveil_objet_duree_ms) || 0);
        const tAmplitude = Math.max (0, Number (tReglages.reveil_objet_scale_pct) || 0) / 100;
        if (tDuree <= 0 || tAmplitude <= 0) return;
        anime_impulsion_douce_noeud_lecture (tElt, tDuree, tAmplitude);
      };

    const selectionne_depuis_interaction_lecture = (pInfo) =>
      {
        if (!pInfo || !mode_lecture_scene_actif ()) return false;
        if (pInfo.type === "bulle" && pInfo.carteId)
          return selectionne_bulle_calque_lecture_passive (pInfo.carteId);
        if (pInfo.id)
          return selectionne_calque (pInfo.id, {autoplay : false});
        return false;
      };

    const restaure_bouton_zoom_alternatif = (pNoeud, pAvant) =>
      {
        if (!pNoeud || !pNoeud.style || !pAvant) return;
        if (pAvant.transformAttr) pNoeud.setAttribute ("transform", pAvant.transformAttr);
        else if (pNoeud.removeAttribute) pNoeud.removeAttribute ("transform");
        pNoeud.style.transform = pAvant.styleTransform || "";
        pNoeud.style.filter = pAvant.filter;
        pNoeud.style.transition = pAvant.transition;
      };

    const transform_bouton_zoom_alternatif = (pAvant, pScale) =>
      {
        const tScale = Math.max (0.01, Number (pScale) || 1);
        const tBase = pAvant && pAvant.transformAttr ? pAvant.transformAttr : "";
        const tCx = Number (pAvant && pAvant.cx) || 0;
        const tCy = Number (pAvant && pAvant.cy) || 0;
        const tPulse = `translate(${tCx} ${tCy}) scale(${tScale}) translate(${-tCx} ${-tCy})`;
        return tBase ? `${tBase} ${tPulse}` : tPulse;
      };

    const anime_scale_bouton_zoom_alternatif = (pNoeud, pAvant, pFrom, pTo, pDuree, pEasing = null, pToken = null) =>
      new Promise ((pResolve) =>
        {
          if (!pNoeud || !pNoeud.parentNode || !pAvant)
            { pResolve (); return; }
          const tToken = pToken === null || pToken === undefined ? null : pToken;
          const tDebut = performance.now ();
          const tDuree = Math.max (0, Number (pDuree) || 0);
          const tFrom = Number (pFrom) || 1;
          const tTo = Number (pTo) || 1;
          const tEase = typeof pEasing === "function"
            ? pEasing
            : ((pT) => 1 - Math.pow (1 - pT, 3));
          const step = (pNow) =>
            {
              if (!pNoeud.parentNode)
                { pResolve (); return; }
              if (tToken !== null && pNoeud._cogiZoomAltButtonAnimToken !== tToken)
                { pResolve (); return; }
              const t = tDuree > 0 ? Math.max (0, Math.min (1, (pNow - tDebut) / tDuree)) : 1;
              const tScale = tFrom + ((tTo - tFrom) * tEase (t));
              pNoeud.setAttribute ("transform", transform_bouton_zoom_alternatif (pAvant, tScale));
              if (t < 1)
                {
                  pNoeud._cogiZoomAltButtonRaf = requestAnimationFrame (step);
                  return;
                }
              pResolve ();
            };
          pNoeud._cogiZoomAltButtonRaf = requestAnimationFrame (step);
        });

    const demarre_appui_bouton_zoom_alternatif = (pNoeud) =>
      {
        if (!pNoeud || !pNoeud.style) return null;
        if (typeof pNoeud._cogiZoomAltButtonCancel === "function")
          pNoeud._cogiZoomAltButtonCancel ();
        else if (pNoeud._cogiZoomAltButtonBase)
          {
            if (pNoeud._cogiZoomAltButtonRaf) cancelAnimationFrame (pNoeud._cogiZoomAltButtonRaf);
            restaure_bouton_zoom_alternatif (pNoeud, pNoeud._cogiZoomAltButtonBase);
            delete pNoeud._cogiZoomAltButtonBase;
          }
        let tBBox = null;
        try { tBBox = typeof pNoeud.getBBox === "function" ? pNoeud.getBBox () : null; }
        catch (e) { tBBox = null; }
        const tAvant =
          {
            transformAttr : pNoeud.getAttribute ? (pNoeud.getAttribute ("transform") || "") : "",
            styleTransform : pNoeud.style.transform || "",
            transition : pNoeud.style.transition || "",
            filter : pNoeud.style.filter || "",
            cx : tBBox ? tBBox.x + (tBBox.width / 2) : 0,
            cy : tBBox ? tBBox.y + (tBBox.height / 2) : 0,
          };
        let tActif = true;
        if (pNoeud._cogiZoomAltButtonRaf) cancelAnimationFrame (pNoeud._cogiZoomAltButtonRaf);
        const tToken = (Number (pNoeud._cogiZoomAltButtonAnimToken) || 0) + 1;
        pNoeud._cogiZoomAltButtonAnimToken = tToken;
        pNoeud._cogiZoomAltButtonBase = tAvant;
        pNoeud.style.transform = "";
        pNoeud.style.transition = "filter 150ms ease";
        pNoeud.style.filter = `${tAvant.filter ? `${tAvant.filter} ` : ""}brightness(0.96)`;
        anime_scale_bouton_zoom_alternatif (pNoeud, tAvant, 1, 0.88, 150, null, tToken);
        pNoeud._cogiZoomAltButtonCancel = () =>
          {
            if (!tActif) return;
            tActif = false;
            pNoeud._cogiZoomAltButtonAnimToken = (Number (pNoeud._cogiZoomAltButtonAnimToken) || 0) + 1;
            if (pNoeud._cogiZoomAltButtonRaf) cancelAnimationFrame (pNoeud._cogiZoomAltButtonRaf);
            restaure_bouton_zoom_alternatif (pNoeud, tAvant);
            delete pNoeud._cogiZoomAltButtonCancel;
            delete pNoeud._cogiZoomAltButtonBase;
          };
        const tAppui = {node : pNoeud, avant : tAvant, token : tToken};
        suspend_flottement_appui_lecture_tpl (pTpl, tAppui, true);
        return tAppui;
      };

	    const termine_appui_bouton_zoom_alternatif = (pEtat, pRebond = true, pOpt = {}) =>
	      {
	        const pNoeud = pEtat && pEtat.node;
	        const tAvant = pEtat && pEtat.avant;
	        const tToken = pEtat && pEtat.token;
	        if (!pNoeud || !pNoeud.style || !tAvant) return Promise.resolve ();
        if (pNoeud._cogiZoomAltButtonRaf) cancelAnimationFrame (pNoeud._cogiZoomAltButtonRaf);
        if (!pRebond)
          {
            restaure_bouton_zoom_alternatif (pNoeud, tAvant);
            delete pNoeud._cogiZoomAltButtonCancel;
            delete pNoeud._cogiZoomAltButtonBase;
            return Promise.resolve ();
          }
        const tOvershoot = Number.isFinite (Number (pOpt.overshoot)) ? Number (pOpt.overshoot) : 1.07;
        const tRetour = Number.isFinite (Number (pOpt.retour)) ? Number (pOpt.retour) : 0.985;
	        pNoeud.style.filter = tAvant.filter;
	        return anime_scale_bouton_zoom_alternatif (pNoeud, tAvant, 0.88, tOvershoot, 120, null, tToken)
	          .then (() => anime_scale_bouton_zoom_alternatif (pNoeud, tAvant, tOvershoot, tRetour, 100, null, tToken))
	          .then (() => anime_scale_bouton_zoom_alternatif (pNoeud, tAvant, tRetour, 1, 120, null, tToken))
	          .then (() =>
	            {
	              if (pNoeud._cogiZoomAltButtonAnimToken !== tToken) return;
	              restaure_bouton_zoom_alternatif (pNoeud, tAvant);
	              delete pNoeud._cogiZoomAltButtonCancel;
	              delete pNoeud._cogiZoomAltButtonBase;
	            });
	      };

	    const etat_flottement_bulle_lecture = (pCarteId, pNoeudFallback = null) =>
	      {
	        const tCarteId = typeof pCarteId === "string" ? pCarteId : "";
	        const tEtats = pTpl && pTpl._lectureBullesFlottementEtats;
	        const tNoeuds = pTpl && pTpl._lectureBullesFlottementNoeuds;
	        const tEtat = tCarteId && tEtats instanceof Map ? tEtats.get (tCarteId) : null;
	        const tNoeud = tCarteId && tNoeuds instanceof Map ? tNoeuds.get (tCarteId) : null;
	        if (!tEtat || !tNoeud) return null;
	        return {
	          carteId : tCarteId,
	          etat : tEtat,
	          node : tNoeud || pNoeudFallback,
	        };
	      };

	    const anime_scale_bulle_lecture = (pEtatBulle, pFrom, pTo, pDuree, pEasing = null, pToken = null) =>
	      new Promise ((pResolve) =>
	        {
	          const tEtat = pEtatBulle && pEtatBulle.etat;
	          const tNoeud = pEtatBulle && pEtatBulle.node;
	          if (!tEtat || !tNoeud || !tNoeud.parentNode)
	            { pResolve (); return; }
	          const tToken = pToken === null || pToken === undefined ? null : pToken;
	          const tDebut = performance.now ();
	          const tDuree = Math.max (0, Number (pDuree) || 0);
	          const tFrom = Number (pFrom) || 1;
	          const tTo = Number (pTo) || 1;
	          const tEase = typeof pEasing === "function"
	            ? pEasing
	            : ((pT) => 1 - Math.pow (1 - pT, 3));
	          const step = (pNow) =>
	            {
	              if (!tNoeud.parentNode)
	                { pResolve (); return; }
	              if (tToken !== null && tEtat.press_token !== tToken)
	                { pResolve (); return; }
	              const t = tDuree > 0 ? Math.max (0, Math.min (1, (pNow - tDebut) / tDuree)) : 1;
	              tEtat.press_scale = tFrom + ((tTo - tFrom) * tEase (t));
	              applique_flottement_bulles_lecture_tpl (pTpl, pNow, pEtatBulle.carteId);
	              if (t < 1)
	                {
	                  tEtat.press_raf = requestAnimationFrame (step);
	                  return;
	                }
	              pResolve ();
	            };
	          tEtat.press_raf = requestAnimationFrame (step);
	        });

	    const demarre_appui_bulle_lecture = (pCarteId, pNoeudFallback = null) =>
	      {
	        const tEtatBulle = etat_flottement_bulle_lecture (pCarteId, pNoeudFallback);
	        const pNoeud = tEtatBulle && tEtatBulle.node;
	        const tEtat = tEtatBulle && tEtatBulle.etat;
	        if (!pNoeud || !pNoeud.style || !tEtat)
	          return demarre_appui_bouton_zoom_alternatif (pNoeudFallback);
	        if (typeof pNoeud._cogiLectureBullePressCancel === "function")
	          pNoeud._cogiLectureBullePressCancel ();
	        if (tEtat.press_raf) cancelAnimationFrame (tEtat.press_raf);
	        const tToken = (Number (tEtat.press_token) || 0) + 1;
	        tEtat.press_token = tToken;
	        const tAvant =
	          {
	            filter : pNoeud.style.filter || "",
	            transition : pNoeud.style.transition || "",
	          };
	        pNoeud.style.transition = "filter 150ms ease";
	        pNoeud.style.filter = `${tAvant.filter ? `${tAvant.filter} ` : ""}brightness(0.96)`;
	        tEtat.press_scale = 1;
	        anime_scale_bulle_lecture (tEtatBulle, 1, 0.88, 150, null, tToken);
	        pNoeud._cogiLectureBullePressCancel = () =>
	          {
	            tEtat.press_token = (Number (tEtat.press_token) || 0) + 1;
	            if (tEtat.press_raf) cancelAnimationFrame (tEtat.press_raf);
	            tEtat.press_scale = 1;
	            pNoeud.style.filter = tAvant.filter;
	            pNoeud.style.transition = tAvant.transition;
	            applique_flottement_bulles_lecture_tpl (pTpl, performance.now ());
	            delete pNoeud._cogiLectureBullePressCancel;
	          };
	        const tAppui = {
	          type : "bulle",
	          carteId : pCarteId,
	          node : pNoeud,
	          etatBulle : tEtatBulle,
	          avant : tAvant,
	          token : tToken,
	        };
	        suspend_flottement_appui_lecture_tpl (pTpl, tAppui, true);
	        return tAppui;
	      };

	    const termine_appui_bulle_lecture = (pEtat, pRebond = true, pOpt = {}) =>
	      {
	        const pNoeud = pEtat && pEtat.node;
	        const tEtatBulle = pEtat && pEtat.etatBulle;
	        const tEtat = tEtatBulle && tEtatBulle.etat;
	        const tAvant = pEtat && pEtat.avant;
	        const tToken = pEtat && pEtat.token;
	        if (!pNoeud || !pNoeud.style || !tEtat || !tAvant)
	          return termine_appui_bouton_zoom_alternatif (pEtat, pRebond, pOpt);
	        delete pNoeud._cogiLectureBullePressCancel;
	        if (tEtat.press_raf) cancelAnimationFrame (tEtat.press_raf);
	        if (!pRebond)
	          {
	            tEtat.press_scale = 1;
	            pNoeud.style.filter = tAvant.filter;
	            pNoeud.style.transition = tAvant.transition;
	            applique_flottement_bulles_lecture_tpl (pTpl, performance.now ());
	            return Promise.resolve ();
	          }
	        const tOvershoot = Number.isFinite (Number (pOpt.overshoot)) ? Number (pOpt.overshoot) : 1.07;
	        const tRetour = Number.isFinite (Number (pOpt.retour)) ? Number (pOpt.retour) : 0.985;
	        pNoeud.style.filter = tAvant.filter;
	        const tDepart = Math.max (0.01, Number (tEtat.press_scale) || 0.88);
	        return anime_scale_bulle_lecture (tEtatBulle, tDepart, tOvershoot, 120, null, tToken)
	          .then (() => anime_scale_bulle_lecture (tEtatBulle, tOvershoot, tRetour, 100, null, tToken))
	          .then (() => anime_scale_bulle_lecture (tEtatBulle, tRetour, 1, 120, null, tToken))
	          .then (() =>
	            {
	              if (tEtat.press_token !== tToken) return;
	              tEtat.press_scale = 1;
	              pNoeud.style.transition = tAvant.transition;
	              applique_flottement_bulles_lecture_tpl (pTpl, performance.now ());
	            });
	      };

	    const gere_clic_lecture_scene = (e) =>
	      {
        e.preventDefault ();
        e.stopPropagation ();
        maj_hover (null);
        applique_hover_lecture (null);

        const tPoint = point_user (e.clientX, e.clientY);
        const tPointCarte = point_cartes_brut_depuis_point_svg (pTpl, tPoint);
        const tCarteFocus = carte_focus_lecture_scene ();
        const tRoleZoomAlt = tCarteFocus && pTpl._lectureZoomAltActif
          ? role_zoom_alternatif_depuis_cible (e.target)
          : null;
        if (tRoleZoomAlt === CARTE_ZOOM_ALTERNATIF_RELECTURE_ROLE)
          {
            return;
          }
        if (tRoleZoomAlt && tRoleZoomAlt !== "carte_jauge")
          {
            return;
          }
        const tBullesCliquables = !tCarteFocus || !!pTpl._lectureZoomAltSortieEnCours;
        const tBulle = tBullesCliquables
          ? info_bulle_carte_depuis_point (tPoint)
            || (() =>
              {
                const tCarteBulleId = cible_bulle_carte (e.target);
                return tCarteBulleId
                  ? {
                      carteId : tCarteBulleId,
                      node : cible_groupe_bulle_carte (e.target),
                    }
                  : null;
              }) ()
          : null;
        const tCarteBulleId = tBulle && tBulle.carteId;
        if (tBullesCliquables && tCarteBulleId)
          {
            selectionne_depuis_interaction_lecture ({type : "bulle", carteId : tCarteBulleId});
            ouvre_focus_carte_lecture_depuis_bulle_tpl (pTpl, tCarteBulleId);
            return;
          }
        const tVueFocus = normalise_vue_carte_client (tCarteFocus && tCarteFocus.vue);
        if (tVueFocus && !pTpl._lectureZoomAltActif && !point_dans_vue_carte (tPointCarte, tVueFocus))
          {
            quitte_focus_carte_lecture_scene (pTpl);
            return;
          }
        const tInterfaceLecture = item_interface_lecture_depuis_evenement (e);
        if (tInterfaceLecture)
          {
            if (item_interface_a_action_lecture_scene (tInterfaceLecture))
              {
                selectionne_depuis_interaction_lecture ({type : "bouton", id : tInterfaceLecture});
                const tItemHype = item_interface_scene_par_id (tInterfaceLecture);
                if (tItemHype && tItemHype.item_hype_widget_id && tItemHype.item_hype_autoplay === false)
                  lance_widget_hype_item_tpl (pTpl, tInterfaceLecture);
                if (tItemHype && item_interface_est_svg_inline (tItemHype) && tItemHype.item_hype_autoplay === false)
                  lance_svg_inline_item_tpl (pTpl, tInterfaceLecture);
              }
            return;
          }
        const tId = cible_id_svg_pertinent (e.target)
          || bouton_scene_lecture_depuis_point (tPoint);
        if (!tId) return;
        const tCategorie = trouve_categorie_pour_id (tId);
        if (tCarteFocus && !pTpl._lectureFocusInteractionsActives && !pTpl._lectureZoomAltSortieEnCours) return;
        if (tCategorie === CALQUES_CATEGORIE_INTERFACE
            && !item_interface_a_action_lecture_scene (tId))
          return;
        if (categorie_est_bouton_lecture (tCategorie))
          {
            selectionne_depuis_interaction_lecture ({type : "bouton", id : tId});
            return;
          }

        if (tCategorie === CALQUES_CATEGORIE_ZOOG)
          {
            selectionne_depuis_interaction_lecture ({type : "objet", id : tId});
            anime_reveil_element_lecture (tId);
            return;
          }

        if (tCarteFocus && (tCategorie === "obj" || tCategorie === CALQUES_CATEGORIE_PART))
          {
            if (!clic_objet_autorise_pedago_lecture (pTpl, tId)) return;
            selectionne_depuis_interaction_lecture ({type : "objet", id : tId});
            anime_reveil_element_lecture (tId);
            joue_audio_objet_lecture_scene (tId);
          }
      };

    const vue_redimensionnee_depuis_poignee = (pVue, pCoin, pPoint, pOptions = {}) =>
      {
        const tVue = normalise_vue_carte_client (pVue);
        if (!tVue || !pPoint || !pCoin) return null;
        const tConserveRatio = !!(pOptions && pOptions.conserverRatio);
        const tDroite = tVue.x + tVue.w;
        const tBas = tVue.y + tVue.h;

        if (tConserveRatio)
          {
            const tScaleMin = Math.max (
              CARTE_VUE_MIN_TAILLE / tVue.w,
              CARTE_VUE_MIN_TAILLE / tVue.h
            );
            const tAncreX = pCoin.includes ("w")
              ? tDroite
              : (pCoin.includes ("e") ? tVue.x : (tVue.x + (tVue.w / 2)));
            const tAncreY = pCoin.includes ("n")
              ? tBas
              : (pCoin.includes ("s") ? tVue.y : (tVue.y + (tVue.h / 2)));
            const tLargeurVoulue = pCoin.includes ("w")
              ? (tAncreX - pPoint.x)
              : (pCoin.includes ("e") ? (pPoint.x - tAncreX) : null);
            const tHauteurVoulue = pCoin.includes ("n")
              ? (tAncreY - pPoint.y)
              : (pCoin.includes ("s") ? (pPoint.y - tAncreY) : null);
            let tScale = tScaleMin;

            if (Number.isFinite (tLargeurVoulue))
              tScale = Math.max (tScale, Math.max (0, tLargeurVoulue) / tVue.w);
            if (Number.isFinite (tHauteurVoulue))
              tScale = Math.max (tScale, Math.max (0, tHauteurVoulue) / tVue.h);

            const tW = tVue.w * tScale;
            const tH = tVue.h * tScale;
            const tX = pCoin.includes ("w")
              ? (tAncreX - tW)
              : (pCoin.includes ("e") ? tAncreX : (tAncreX - (tW / 2)));
            const tY = pCoin.includes ("n")
              ? (tAncreY - tH)
              : (pCoin.includes ("s") ? tAncreY : (tAncreY - (tH / 2)));

            return normalise_vue_carte_client ({x : tX, y : tY, w : tW, h : tH}, tVue);
          }

        let tX = tVue.x;
        let tY = tVue.y;
        let tW = tVue.w;
        let tH = tVue.h;

        if (pCoin.includes ("w"))
          {
            tX = Math.min (pPoint.x, tDroite - CARTE_VUE_MIN_TAILLE);
            tW = tDroite - tX;
          }
        if (pCoin.includes ("e"))
          tW = Math.max (CARTE_VUE_MIN_TAILLE, pPoint.x - tVue.x);
        if (pCoin.includes ("n"))
          {
            tY = Math.min (pPoint.y, tBas - CARTE_VUE_MIN_TAILLE);
            tH = tBas - tY;
          }
        if (pCoin.includes ("s"))
          tH = Math.max (CARTE_VUE_MIN_TAILLE, pPoint.y - tVue.y);

        return normalise_vue_carte_client ({x : tX, y : tY, w : tW, h : tH}, tVue);
      };

    const curseur_poignee_carte = (pCoin) =>
      {
        if (pCoin === "n" || pCoin === "s") return "ns-resize";
        if (pCoin === "e" || pCoin === "w") return "ew-resize";
        return (pCoin === "ne" || pCoin === "sw") ? "nesw-resize" : "nwse-resize";
      };

    const contexte_bulle_drag_active = () =>
      {
        if (carte_editor_est_actif ())
          {
            const tBrouillon = brouillon_carte_courant ();
            const tVue = normalise_vue_carte_client (tBrouillon && tBrouillon.vue);
            if (!tBrouillon || !tVue) return null;
            return {
              carteLike : {...tBrouillon, vue : tVue},
              vue : tVue,
              setPosition : (pPosition) =>
                {
                  gCarteBrouillon.set ({...tBrouillon, position_bulle : pPosition});
                  return true;
                },
            };
          }

        if (gBulleSelectionnee.get ())
          {
            const tCarteId = gBulleSelectionnee.get ();
            const tBrouillon = brouillon_bulle_courant ();
            const tCarte = tCarteId ? carte_source_pour_bulle_scene (tCarteId) : null;
            const tVue = normalise_vue_carte_client (
              tBrouillon && tBrouillon.vue,
              tCarte && tCarte.vue
            );
            if (!tBrouillon || !tVue) return null;
            return {
              carteLike : {...tBrouillon, vue : tVue},
              vue : tVue,
              setPosition : (pPosition) => maj_brouillon_bulle_calque ({position_bulle : pPosition}),
            };
          }

        return null;
      };

    const applique_position_bulle_drag = (pClientX, pClientY, pAncrage = null) =>
      {
        const tContexte = contexte_bulle_drag_active ();
        if (!tContexte || typeof tContexte.setPosition !== "function") return false;
        const tPoint = point_cartes_brut_depuis_point_svg (pTpl, point_user (pClientX, pClientY));
        if (!tPoint) return false;
        const tPointAncre = {
          x : tPoint.x - (Number.isFinite (pAncrage && pAncrage.deltaX) ? pAncrage.deltaX : 0),
          y : tPoint.y - (Number.isFinite (pAncrage && pAncrage.deltaY) ? pAncrage.deltaY : 0),
        };
        const tVue = tContexte.vue;
        const tRayon = rayon_bulle_carte_effectif_client (tContexte.carteLike, tVue);
        const tMinX = tVue.x + Math.min (tRayon, tVue.w / 2);
        const tMaxX = tVue.x + Math.max (tVue.w - tRayon, tVue.w / 2);
        const tMinY = tVue.y + Math.min (tRayon, tVue.h / 2);
        const tMaxY = tVue.y + Math.max (tVue.h - tRayon, tVue.h / 2);
        return !!tContexte.setPosition (
          {
            x : Math.round (Math.max (tMinX, Math.min (tMaxX, tPointAncre.x)) * 1000) / 1000,
            y : Math.round (Math.max (tMinY, Math.min (tMaxY, tPointAncre.y)) * 1000) / 1000,
          }
        );
      };

    const zoom_max_min_ok = (pBaseW, pFacteur) =>
      {
        const tInitW = (pTpl._svgVBNatif && pTpl._svgVBNatif.w)
                    || (pTpl._svgVBInit && pTpl._svgVBInit.w) || pBaseW;
        const tNouvW = pBaseW * pFacteur;
        return tNouvW <= tInitW * 50 && tNouvW >= tInitW / 50;
      };

    const applique_pan_tactile = (pFocusUser, pClientX, pClientY, pBaseVB) =>
      {
        const tNorm = point_normalise_ecran (pClientX, pClientY);
        if (!tNorm || !pFocusUser || !pBaseVB) return false;
        tVB.x = pFocusUser.x - tNorm.u * pBaseVB.w;
        tVB.y = pFocusUser.y - tNorm.v * pBaseVB.h;
        tVB.w = pBaseVB.w;
        tVB.h = pBaseVB.h;
        arme_notice_cadrage_carte ();
        applique_vb ();
        return true;
      };

    const applique_zoom_tactile = (pFocusUser, pClientX, pClientY, pBaseVB, pFacteur) =>
      {
        const tNorm = point_normalise_ecran (pClientX, pClientY);
        if (!tNorm || !pFocusUser || !pBaseVB) return false;
        if (!zoom_max_min_ok (pBaseVB.w, pFacteur)) return false;
        const tNouvW = pBaseVB.w * pFacteur;
        const tNouvH = pBaseVB.h * pFacteur;
        tVB.w = tNouvW;
        tVB.h = tNouvH;
        tVB.x = pFocusUser.x - tNorm.u * tNouvW;
        tVB.y = pFocusUser.y - tNorm.v * tNouvH;
        arme_notice_cadrage_carte ();
        applique_vb ();
        return true;
      };

    let tTouchPoints  = new Map ();
    let tTouchGesture = null;

    const demarre_geste_tactile = () =>
      {
        const tPts = Array.from (tTouchPoints.values ());
        if (!tPts.length)
          {
            tTouchGesture = null;
            return;
          }
        annule_anim_svg ();
        if (tPts.length >= 2)
          {
            const tA = tPts[0];
            const tB = tPts[1];
            const tMidX = (tA.clientX + tB.clientX) / 2;
            const tMidY = (tA.clientY + tB.clientY) / 2;
            const tFocus = point_user (tMidX, tMidY);
            const tDist  = Math.max (12, Math.hypot (tB.clientX - tA.clientX,
                                                     tB.clientY - tA.clientY));
            if (!tFocus)
              {
                tTouchGesture = null;
                return;
              }
            tTouchGesture = {
              mode      : "pinch",
              ids       : [tA.id, tB.id],
              startVB   : {...tVB},
              focusUser : tFocus,
              startMidX : tMidX,
              startMidY : tMidY,
              startDist : tDist,
            };
            return;
          }

        const tP = tPts[0];
        const tFocus = point_user (tP.clientX, tP.clientY);
        if (!tFocus)
          {
            tTouchGesture = null;
            return;
          }
        tTouchGesture = {
          mode      : "pan",
          id        : tP.id,
          startVB   : {...tVB},
          focusUser : tFocus,
          startX    : tP.clientX,
          startY    : tP.clientY,
        };
      };

    // -- Wheel : zoom-to-point ------------------------------------------
    pDoc.addEventListener ("wheel", (e) =>
      {
        e.preventDefault ();
        if (mode_lecture_scene_actif ()) return;
        if (!edition_hub_active ()) return;
        annule_anim_svg ();
        // Sensibilité utilisateur : 1.0 → facteur 1.15 par cran.
        // On élève 1.15 à la puissance "sensibilite" pour des incréments plus
        // ou moins doux ; on plafonne pour éviter les sauts brutaux.
        const tSens   = Math.max (0.01, Math.min (2, gSensibiliteZoom.get () || 1));
        const tFactBase = Math.pow (1.15, tSens);
        const tFacteur = (e.deltaY < 0) ? (1 / tFactBase) : tFactBase;
        const tPt = gZoomSurCurseur.get ()
          ? point_user (e.clientX, e.clientY)
          : (function () {
              // Centre de la zone visible : centre du document SVG (le
              // Le conteneur SVG emplit la div .scene).
              const tW = (pDoc.defaultView && pDoc.defaultView.innerWidth)
                       || tRoot.clientWidth || 0;
              const tH = (pDoc.defaultView && pDoc.defaultView.innerHeight)
                       || tRoot.clientHeight || 0;
              return point_user (tW / 2, tH / 2);
            }) ();
        if (!tPt) return;
        if (!zoom_max_min_ok (tVB.w, tFacteur)) return;
        const tNouvW = tVB.w * tFacteur;
        const tNouvH = tVB.h * tFacteur;
        tVB.x = tPt.x - (tPt.x - tVB.x) * tFacteur;
        tVB.y = tPt.y - (tPt.y - tVB.y) * tFacteur;
        tVB.w = tNouvW;
        tVB.h = tNouvH;
        arme_notice_cadrage_carte ();
        applique_vb ();
      }, {passive : false});

    // -- Double-clic : reset zoom/pan, ou zoom sur l'élément si présent
    //    dans l'arbre des calques.
    pDoc.addEventListener ("dblclick", (e) =>
      {
        if (mode_lecture_scene_actif ())
          {
            e.preventDefault ();
            return;
          }
        if (!edition_hub_active ())
          {
            e.preventDefault ();
            return;
          }
        if (interface_est_tactile ()) return;
        e.preventDefault ();

        const tCarteInteractionCourante = (!carte_editor_est_actif ()
                                        && tInteractionCartePointer
                                        && tInteractionCartePointer.carteId)
          ? tInteractionCartePointer.carteId
          : null;

        const tCarteDeplacee = cible_drag_selection_carte (e.target);
        if ((tCarteDeplacee && tCarteDeplacee.carteId) || tCarteInteractionCourante)
          {
            const tCarteId = (tCarteDeplacee && tCarteDeplacee.carteId) || tCarteInteractionCourante;
            if (!assure_selection_bulle_calque (tCarteId)) return;
            zoom_sur_carte (pTpl, tCarteId);
            return;
          }

        const tPoigneeCarte = cible_poignee_selection_carte (e.target);
        if (tPoigneeCarte && tPoigneeCarte.carteId)
          {
            if (!assure_selection_bulle_calque (tPoigneeCarte.carteId)) return;
            zoom_sur_carte (pTpl, tPoigneeCarte.carteId);
            return;
          }

        const tCarteBulleId = bulles_scene_visibles ()
          ? cible_bulle_carte (e.target)
          : (tCarteInteractionCourante || null);
        if (tCarteBulleId)
          {
            if (!assure_selection_bulle_calque (tCarteBulleId)) return;
            zoom_sur_carte (pTpl, tCarteBulleId);
            return;
          }

        const tIdZoom = cible_id_svg_pertinent (e.target);
        if (tIdZoom)
          {
            const tCat = trouve_categorie_pour_id (tIdZoom);
            if (!carte_editor_est_actif () && tCat && !categorie_scene_interactive (tCat))
              return;
            if (tCat) ecrit_ouverture_categorie_scene (tCat, true);
            zoom_sur_id (pTpl, tIdZoom);
            return;
          }

        // Sinon, retour à la vue d'origine avec inertie.
        reinitialise_vue_scene (pTpl);
      });

    // -- Drag pan : bouton du milieu, ou Shift+gauche, ou bouton droit -
    let tDrag    = null;
    let tDragMov = 0;
    let tBubbleDrag = null;
    let tCarteMove = null;
    let tCarteResize = null;
    let tInteractionCartePointer = null;
    let tZoomAltButtonPress = null;

    const memorise_interaction_carte_pointer = (pCarteId, pRole, pPointerId) =>
      {
        tInteractionCartePointer = pCarteId
          ? {
            carteId : pCarteId,
            role : pRole || null,
            pointerId : pPointerId,
          }
          : null;
      };

    pDoc.addEventListener ("pointerdown", (e) =>
      {
        if (tBubbleDrag || tCarteMove || tCarteResize)
          {
            e.preventDefault ();
            return;
          }
        memorise_interaction_carte_pointer (null);
        if (mode_lecture_scene_actif ())
          {
            deverrouille_audio_instant_lecture_scene ();
            maj_hover (null);
            tDragMov = 0;
            const tCarteFocus = carte_focus_lecture_scene ();
            const tRoleZoomAlt = tCarteFocus && pTpl._lectureZoomAltActif
              ? role_zoom_alternatif_depuis_cible (e.target)
              : null;
            if (tRoleZoomAlt && tRoleZoomAlt !== "carte_jauge" && e.button === 0)
              {
                e.preventDefault ();
                e.stopPropagation ();
                acheve_animations_en_cours_lecture_tpl (pTpl);
                if (tRoleZoomAlt === CARTE_ZOOM_ALTERNATIF_RELECTURE_ROLE)
                  {
                    relit_audio_objet_courant_carte_lecture ();
                    tZoomAltButtonPress =
                      {
                        pointerId : e.pointerId,
                        type : "carte_relecture_audio",
                        role : tRoleZoomAlt,
                        node : e.target,
                        actionPointerDown : true,
                        etat : null,
                      };
                    try { tRoot.setPointerCapture (e.pointerId); } catch (er) {}
                    return;
                  }
                const tNoeudZoomAlt = noeud_zoom_alternatif_depuis_cible (e.target);
                const tActionPointerDown = tRoleZoomAlt === "carte_legende";
                if (tActionPointerDown)
                  gere_bouton_pedago_lecture (pTpl, tRoleZoomAlt);
                const tRetourMenuAudioPointerDown = tRoleZoomAlt === "carte_fermeture";
                if (tRetourMenuAudioPointerDown)
                  joue_audio_retour_menu_lecture_scene ();
                const tClicAudioPointerDown =
                  (tRoleZoomAlt === "carte_oui" || tRoleZoomAlt === "carte_non")
                  && reponse_pedago_carte_disponible (pTpl);
                if (tClicAudioPointerDown)
                  joue_audio_projet_lecture_scene ("son_clic");
                tZoomAltButtonPress =
                  {
                    pointerId : e.pointerId,
                    type : "zoom_alt",
                    role : tRoleZoomAlt,
                    node : tNoeudZoomAlt,
                    actionPointerDown : tActionPointerDown,
                    retourMenuAudioPointerDown : tRetourMenuAudioPointerDown,
                    clicAudioPointerDown : tClicAudioPointerDown,
                    etat : demarre_appui_bouton_zoom_alternatif (tNoeudZoomAlt),
                  };
                try { tRoot.setPointerCapture (e.pointerId); } catch (er) {}
                return;
              }
            const tPointLecture = point_user (e.clientX, e.clientY);
            const tInterfaceLecture = item_interface_lecture_depuis_evenement (e);
            if (tInterfaceLecture && !item_interface_a_action_lecture_scene (tInterfaceLecture))
              {
                e.preventDefault ();
                e.stopPropagation ();
                return;
              }
            if (tInterfaceLecture && e.button === 0)
              {
                e.preventDefault ();
                e.stopPropagation ();
                acheve_animations_en_cours_lecture_tpl (pTpl);
                interrompt_sortie_focus_carte_avant_interaction_scene ();
                const tNoeudBouton = pDoc.getElementById (tInterfaceLecture);
                const tDataNameBouton = data_name_svg_depuis_noeud_lecture (tNoeudBouton || e.target);
                const tRetourMenuAudioPointerDown =
                  bouton_scene_lecture_est_retour_menu (tInterfaceLecture, tNoeudBouton, tDataNameBouton);
                const tInitAudioMobilePointerDown =
                  normalise_action_interactivite_objet (
                    (item_interface_scene_par_id (tInterfaceLecture) || {}).interactivite_action
                  ) === INTERACTIVITE_ACTION_INIT_AUDIO_MOBILE;
                if (tInitAudioMobilePointerDown)
                  execute_action_initialisation_audio_mobile_lecture_scene (tNoeudBouton);
                if (tRetourMenuAudioPointerDown)
                  joue_audio_retour_menu_lecture_scene ();
                const tClicAudioPointerDown = !tRetourMenuAudioPointerDown && !tInitAudioMobilePointerDown;
                if (tClicAudioPointerDown)
                  joue_audio_projet_lecture_scene ("son_clic");
                selectionne_depuis_interaction_lecture ({type : "bouton", id : tInterfaceLecture});
                tZoomAltButtonPress =
                  {
                    pointerId : e.pointerId,
                    type : "scene_button",
                    id : tInterfaceLecture,
                    node : tNoeudBouton,
                    dataName : tDataNameBouton,
                    retourMenuAudioPointerDown : tRetourMenuAudioPointerDown,
                    clicAudioPointerDown : tClicAudioPointerDown,
                    actionPointerDown : tInitAudioMobilePointerDown,
                    etat : demarre_appui_bouton_zoom_alternatif (tNoeudBouton),
                  };
                try { tRoot.setPointerCapture (e.pointerId); } catch (er) {}
                return;
              }
            const tBulleLecture = (!tCarteFocus || pTpl._lectureZoomAltSortieEnCours)
              ? info_bulle_carte_depuis_point (tPointLecture)
                || (() =>
                  {
                    const tCarteBulleId = cible_bulle_carte (e.target);
                    return tCarteBulleId
                      ? {
                          carteId : tCarteBulleId,
                          node : cible_groupe_bulle_carte (e.target),
                        }
                      : null;
                  }) ()
              : null;
            if (tBulleLecture && tBulleLecture.carteId && e.button === 0)
              {
                e.preventDefault ();
                e.stopPropagation ();
                acheve_animations_en_cours_lecture_tpl (pTpl);
                pTpl._lectureBulleClicAtMs = Date.now ();
                selectionne_depuis_interaction_lecture ({type : "bulle", carteId : tBulleLecture.carteId});
	                applique_hover_lecture (null);
                joue_son_appareil_photo_bulle_lecture (pTpl);
                ouvre_focus_carte_lecture_depuis_bulle_tpl (pTpl, tBulleLecture.carteId);
	                const tNoeudBulle = (tBulleLecture && tBulleLecture.node)
	                  || cible_visuel_bulle_carte (e.target)
	                  || cible_groupe_bulle_carte (e.target);
                tZoomAltButtonPress =
                  {
                    pointerId : e.pointerId,
	                    type : "bulle",
	                    carteId : tBulleLecture.carteId,
	                    node : tNoeudBulle,
	                    actionPointerDown : true,
	                    etat : demarre_appui_bulle_lecture (tBulleLecture.carteId, tNoeudBulle),
	                  };
                try { tRoot.setPointerCapture (e.pointerId); } catch (er) {}
                return;
              }
            const tBoutonId = cible_id_svg_pertinent (e.target)
              || bouton_scene_lecture_depuis_point (tPointLecture);
            const tCategorieBouton = tBoutonId ? trouve_categorie_pour_id (tBoutonId) : null;
            if (tCategorieBouton === CALQUES_CATEGORIE_INTERFACE
                && !item_interface_a_action_lecture_scene (tBoutonId))
              {
                e.preventDefault ();
                e.stopPropagation ();
                return;
              }
            if (categorie_est_bouton_lecture (tCategorieBouton) && e.button === 0)
              {
                e.preventDefault ();
                e.stopPropagation ();
                acheve_animations_en_cours_lecture_tpl (pTpl);
                interrompt_sortie_focus_carte_avant_interaction_scene ();
                const tNoeudBouton = pDoc.getElementById (tBoutonId);
                const tDataNameBouton = data_name_svg_depuis_noeud_lecture (tNoeudBouton || e.target);
                const tRetourMenuAudioPointerDown =
                  bouton_scene_lecture_est_retour_menu (tBoutonId, tNoeudBouton, tDataNameBouton);
                const tConfigBouton = config_objet_scene_courante (tBoutonId) || item_interface_scene_par_id (tBoutonId);
                const tInitAudioMobilePointerDown =
                  normalise_action_interactivite_objet (tConfigBouton && tConfigBouton.interactivite_action)
                  === INTERACTIVITE_ACTION_INIT_AUDIO_MOBILE;
                if (tInitAudioMobilePointerDown)
                  execute_action_initialisation_audio_mobile_lecture_scene (tNoeudBouton);
                if (tRetourMenuAudioPointerDown)
                  joue_audio_retour_menu_lecture_scene ();
                const tClicAudioPointerDown = !tRetourMenuAudioPointerDown && !tInitAudioMobilePointerDown;
                if (tClicAudioPointerDown)
                  joue_audio_projet_lecture_scene ("son_clic");
                selectionne_depuis_interaction_lecture ({type : "bouton", id : tBoutonId});
                tZoomAltButtonPress =
                  {
                    pointerId : e.pointerId,
                    type : "scene_button",
                    id : tBoutonId,
                    node : tNoeudBouton,
                    dataName : tDataNameBouton,
                    retourMenuAudioPointerDown : tRetourMenuAudioPointerDown,
                    clicAudioPointerDown : tClicAudioPointerDown,
                    actionPointerDown : tInitAudioMobilePointerDown,
                    etat : demarre_appui_bouton_zoom_alternatif (tNoeudBouton),
                  };
                try { tRoot.setPointerCapture (e.pointerId); } catch (er) {}
                return;
              }
            if (tCategorieBouton === CALQUES_CATEGORIE_ZOOG && e.button === 0)
              {
                e.preventDefault ();
                e.stopPropagation ();
                acheve_animations_en_cours_lecture_tpl (pTpl);
                selectionne_depuis_interaction_lecture ({type : "objet", id : tBoutonId});
                anime_reveil_element_lecture (tBoutonId);
                joue_audio_projet_lecture_scene ("son_clic");
                const tNoeudZoog = pDoc.getElementById (tBoutonId);
                tZoomAltButtonPress =
                  {
                    pointerId : e.pointerId,
                    type : "zoog",
                    id : tBoutonId,
                    node : tNoeudZoog,
                    etat : demarre_appui_bouton_zoom_alternatif (tNoeudZoog),
                  };
                try { tRoot.setPointerCapture (e.pointerId); } catch (er) {}
              }
            return;
          }
        if (!edition_hub_active ())
          {
            e.preventDefault ();
            return;
          }
        const tBullesInteractives = carte_editor_est_actif ()
          || categorie_scene_interactive (CALQUES_CATEGORIE_BULLES);
        const tPoigneeCarte = (!carte_editor_est_actif () && tBullesInteractives)
          ? cible_poignee_selection_carte (e.target)
          : null;
        if (tPoigneeCarte && tPoigneeCarte.carteId && e.button === 0)
          {
            memorise_interaction_carte_pointer (tPoigneeCarte.carteId, "handle", e.pointerId);
            const tBrouillon = brouillon_bulle_courant ();
            const tVue = normalise_vue_carte_client (
              tBrouillon && tBrouillon.carte_id === tPoigneeCarte.carteId ? tBrouillon.vue : null
            );
            if (!tBrouillon || !tVue) return;
            e.preventDefault ();
            annule_anim_svg ();
            tCarteResize = {
              pointerId : e.pointerId,
              carteId : tPoigneeCarte.carteId,
              coin : tPoigneeCarte.coin,
              startX : e.clientX,
              startY : e.clientY,
              vueInit : tVue,
            };
            pTpl._svgBubbleDragCarteId = tPoigneeCarte.carteId;
            tDragMov = 0;
            maj_hover (null);
            try { tRoot.setPointerCapture (e.pointerId); } catch (er) {}
            return;
          }
        const tCarteDrag = (!carte_editor_est_actif () && tBullesInteractives)
          ? cible_drag_selection_carte (e.target)
          : null;
        if (tCarteDrag && tCarteDrag.carteId && e.button === 0)
          {
            memorise_interaction_carte_pointer (tCarteDrag.carteId, "move", e.pointerId);
            const tPoint = point_cartes_brut_depuis_point_svg (pTpl, point_user (e.clientX, e.clientY));
            const tBrouillon = brouillon_bulle_courant ();
            if (!tPoint || !tBrouillon || tBrouillon.carte_id !== tCarteDrag.carteId) return;
            e.preventDefault ();
            annule_anim_svg ();
            tCarteMove = {
              pointerId : e.pointerId,
              carteId : tCarteDrag.carteId,
              startX : e.clientX,
              startY : e.clientY,
              lastPoint : tPoint,
            };
            pTpl._svgBubbleDragCarteId = tCarteDrag.carteId;
            tDragMov = 0;
            maj_hover (null);
            tRoot.style.cursor = "grabbing";
            try { tRoot.setPointerCapture (e.pointerId); } catch (er) {}
            return;
          }
        const tCarteBulleId = tBullesInteractives ? cible_bulle_carte (e.target) : null;
        if (tCarteBulleId && e.button === 0)
          {
            memorise_interaction_carte_pointer (tCarteBulleId, "bubble", e.pointerId);
            if (!carte_editor_est_actif ())
              {
                if (gBulleSelectionnee.get () !== tCarteBulleId)
                  {
                    e.preventDefault ();
                    maj_hover (hover_info_bulle_carte (tCarteBulleId));
                    return;
                  }
                const tBrouillonSelection = brouillon_bulle_courant ();
                if (!tBrouillonSelection || tBrouillonSelection.carte_id !== tCarteBulleId)
                  return;
              }
            const tBrouillon = carte_editor_est_actif ()
              ? brouillon_carte_courant ()
              : brouillon_bulle_courant ();
            if (!tBrouillon || tBrouillon.carte_id !== tCarteBulleId) return;
            const tContexteBulle = contexte_bulle_drag_active ();
            const tPointDepart = point_cartes_brut_depuis_point_svg (pTpl, point_user (e.clientX, e.clientY));
            const tCentreDepart = tContexteBulle
              ? position_bulle_carte_effective_client (tContexteBulle.carteLike, tContexteBulle.vue)
              : null;
            e.preventDefault ();
            annule_anim_svg ();
            tBubbleDrag = {
              pointerId : e.pointerId,
              carteId : tCarteBulleId,
              hitTarget : e.target,
              startX : e.clientX,
              startY : e.clientY,
              deltaX : (tPointDepart && tCentreDepart) ? (tPointDepart.x - tCentreDepart.x) : 0,
              deltaY : (tPointDepart && tCentreDepart) ? (tPointDepart.y - tCentreDepart.y) : 0,
            };
            pTpl._svgBubbleDragCarteId = tCarteBulleId;
            tDragMov = 0;
            maj_hover (hover_info_bulle_carte (tCarteBulleId));
            try { tRoot.setPointerCapture (e.pointerId); } catch (er) {}
            if (tBubbleDrag.hitTarget && tBubbleDrag.hitTarget.style)
              tBubbleDrag.hitTarget.style.cursor = "grabbing";
            return;
          }
        if (e.pointerType === "touch")
          {
            e.preventDefault ();
            maj_hover (null);
            tTouchPoints.set (e.pointerId,
              {id : e.pointerId, clientX : e.clientX, clientY : e.clientY});
            tDragMov = 0;
            try { tRoot.setPointerCapture (e.pointerId); } catch (er) {}
            demarre_geste_tactile ();
            return;
          }
        const tEstPan = (e.button === 1)
                     || (e.button === 0 && (e.shiftKey || e.altKey || e.metaKey));
        if (!tEstPan) return;
        e.preventDefault ();
        annule_anim_svg ();
        const tPt = point_user (e.clientX, e.clientY);
        if (!tPt) return;
        tDrag = {sx : e.clientX, sy : e.clientY,
                 ux : tPt.x,     uy : tPt.y,
                 vbx : tVB.x,    vby : tVB.y};
        tDragMov = 0;
        try { tRoot.setPointerCapture (e.pointerId); } catch (er) {}
      });

    // Évite le menu contextuel si on pan au bouton droit.
    pDoc.addEventListener ("contextmenu", (e) =>
      { if (tDragMov > 3) e.preventDefault (); });

    // -- Hover ----------------------------------------------------------
    const maj_hover = (pId) =>
      {
        const tHoverId = normalise_hover_svg_interactif (pId);
        if (tHoverId === pTpl._svgHoverId) return;
        pTpl._svgHoverId = tHoverId;
        tRoot.style.cursor = tHoverId ? "pointer" : "";
        applique_hover_svg (pDoc, tHoverId);
        maj_etiquette_hover (pTpl, tHoverId);
      };

    pDoc.addEventListener ("pointermove", (e) =>
      {
        if (mode_lecture_scene_actif ())
          {
            maj_hover (null);
            if ((Number (pTpl._lectureImpulsionsActives) || 0) > 0)
              {
                applique_hover_lecture (null);
                return;
              }
            if (e.pointerType === "touch" || interface_est_tactile ())
              {
                applique_hover_lecture (null);
                return;
              }
            applique_hover_lecture (info_interaction_lecture (e));
            return;
          }
        if (!edition_hub_active ())
          {
            maj_hover (null);
            return;
          }
        if (tCarteResize && tCarteResize.pointerId === e.pointerId)
          {
            e.preventDefault ();
            const tPoint = point_cartes_brut_depuis_point_svg (pTpl, point_user (e.clientX, e.clientY));
            const tVue = vue_redimensionnee_depuis_poignee (
              tCarteResize.vueInit,
              tCarteResize.coin,
              tPoint,
              {conserverRatio : !!e.shiftKey}
            );
            if (tVue && maj_brouillon_vue_bulle_calque (tVue))
              {
                tDragMov = Math.max (
                  tDragMov,
                  Math.abs (e.clientX - tCarteResize.startX)
                    + Math.abs (e.clientY - tCarteResize.startY)
                );
                synchronise_items_interface_svg_tpl (pTpl);
                applique_masquages_carte_tpl (
                  pTpl,
                  {duree_in_ms : 0, duree_out_ms : 0, decalage_in_ms : 0, decalage_out_ms : 0}
                );
              }
            maj_hover (null);
            tRoot.style.cursor = curseur_poignee_carte (tCarteResize.coin);
            return;
          }
        if (tCarteMove && tCarteMove.pointerId === e.pointerId)
          {
            e.preventDefault ();
            const tPoint = point_cartes_brut_depuis_point_svg (pTpl, point_user (e.clientX, e.clientY));
            if (!tPoint) return;
            const tDx = tPoint.x - tCarteMove.lastPoint.x;
            const tDy = tPoint.y - tCarteMove.lastPoint.y;
            if (deplace_brouillon_bulle_calque (tDx, tDy))
              {
                tCarteMove.lastPoint = tPoint;
                tDragMov = Math.max (
                  tDragMov,
                  Math.abs (e.clientX - tCarteMove.startX)
                    + Math.abs (e.clientY - tCarteMove.startY)
                );
                synchronise_items_interface_svg_tpl (pTpl);
                applique_masquages_carte_tpl (
                  pTpl,
                  {duree_in_ms : 0, duree_out_ms : 0, decalage_in_ms : 0, decalage_out_ms : 0}
                );
              }
            maj_hover (null);
            tRoot.style.cursor = "grabbing";
            return;
          }
        if (tBubbleDrag && tBubbleDrag.pointerId === e.pointerId)
          {
            e.preventDefault ();
            if (applique_position_bulle_drag (e.clientX, e.clientY, tBubbleDrag))
              {
                tDragMov = Math.max (
                  tDragMov,
                  Math.abs (e.clientX - tBubbleDrag.startX)
                    + Math.abs (e.clientY - tBubbleDrag.startY)
                );
              }
            maj_hover (hover_info_bulle_carte (tBubbleDrag.carteId));
            return;
          }
        if (e.pointerType === "touch" && tTouchPoints.has (e.pointerId))
          {
            e.preventDefault ();
            tTouchPoints.set (e.pointerId,
              {id : e.pointerId, clientX : e.clientX, clientY : e.clientY});
            maj_hover (null);
            if (tTouchGesture)
              {
                if (tTouchGesture.mode === "pinch" && tTouchPoints.size >= 2)
                  {
                    const tPts = tTouchGesture.ids
                      .map ((id) => tTouchPoints.get (id))
                      .filter (Boolean);
                    if (tPts.length >= 2)
                      {
                        const tA = tPts[0];
                        const tB = tPts[1];
                        const tMidX = (tA.clientX + tB.clientX) / 2;
                        const tMidY = (tA.clientY + tB.clientY) / 2;
                        const tDist = Math.max (12, Math.hypot (tB.clientX - tA.clientX,
                                                                tB.clientY - tA.clientY));
                        const tFacteur = tTouchGesture.startDist / tDist;
                        tDragMov = Math.max (
                          tDragMov,
                          Math.abs (tMidX - tTouchGesture.startMidX)
                            + Math.abs (tMidY - tTouchGesture.startMidY),
                          Math.abs (tDist - tTouchGesture.startDist)
                        );
                        applique_zoom_tactile (
                          tTouchGesture.focusUser,
                          tMidX,
                          tMidY,
                          tTouchGesture.startVB,
                          tFacteur
                        );
                      }
                  }
                else if (tTouchGesture.mode === "pan"
                         && tTouchGesture.id === e.pointerId)
                  {
                    tDragMov = Math.max (tDragMov,
                      Math.abs (e.clientX - tTouchGesture.startX)
                      + Math.abs (e.clientY - tTouchGesture.startY));
                    applique_pan_tactile (
                      tTouchGesture.focusUser,
                      e.clientX,
                      e.clientY,
                      tTouchGesture.startVB
                    );
                  }
              }
            return;
          }

        if (tDrag)
          {
            const tPt = point_user (e.clientX, e.clientY);
            if (!tPt) return;
            // Recalcule à partir du delta dans l'espace utilisateur courant.
            // (point_user dépend du viewBox actuel, donc on réutilise les
            // valeurs initiales : on convertit (sx,sy) avec viewBox initial
            // de drag en simulant via l'écart d'écran.)
            const tSvgRoot = tRoot && typeof tRoot.getScreenCTM === "function"
              ? tRoot
              : (pDoc && pDoc.documentElement && typeof pDoc.documentElement.getScreenCTM === "function"
                ? pDoc.documentElement
                : null);
            const tCtm = tSvgRoot && tSvgRoot.getScreenCTM ();
            if (!tCtm) return;
            const tDxEcran = e.clientX - tDrag.sx;
            const tDyEcran = e.clientY - tDrag.sy;
            tDragMov = Math.max (tDragMov, Math.abs (tDxEcran) + Math.abs (tDyEcran));
            // Convertit le delta écran en delta utilisateur (sans translation).
            try
              {
                const tInv = tCtm.inverse ();
                const tDxUser = tDxEcran * tInv.a + tDyEcran * tInv.c;
                const tDyUser = tDxEcran * tInv.b + tDyEcran * tInv.d;
                tVB.x = tDrag.vbx - tDxUser;
                tVB.y = tDrag.vby - tDyUser;
                arme_notice_cadrage_carte ();
                applique_vb ();
              }
            catch (er) {}
            return;
          }

        if (e.pointerType === "touch" || interface_est_tactile ())
          { maj_hover (null); return; }

        const tPoigneeCarteHover = categorie_scene_interactive (CALQUES_CATEGORIE_BULLES)
          ? cible_poignee_selection_carte (e.target)
          : null;
        if (tPoigneeCarteHover && tPoigneeCarteHover.carteId)
          {
            maj_hover (null);
            tRoot.style.cursor = curseur_poignee_carte (tPoigneeCarteHover.coin);
            return;
          }

        const tCarteDragHover = categorie_scene_interactive (CALQUES_CATEGORIE_BULLES)
          ? cible_drag_selection_carte (e.target)
          : null;
        if (tCarteDragHover && tCarteDragHover.carteId)
          {
            maj_hover (null);
            tRoot.style.cursor = "grab";
            return;
          }

        const tCarteBulleHover = categorie_scene_interactive (CALQUES_CATEGORIE_BULLES)
          ? cible_bulle_carte (e.target)
          : null;
        if (tCarteBulleHover)
          {
            maj_hover (hover_info_bulle_carte (tCarteBulleHover));
            tRoot.style.cursor = "pointer";
            return;
          }

        let tFound = cible_id_svg_pertinent (e.target);
        if (tFound)
          {
            const tCategorie = trouve_categorie_pour_id (tFound);
            if (tCategorie && tCategorie !== CALQUES_CATEGORIE_ZOOG && !categorie_scene_interactive (tCategorie)) tFound = null;
          }
        maj_hover (tFound);
        if (tFound) tRoot.style.cursor = "pointer";
        if (tFound) positionne_etiquette_hover (pTpl, e.clientX, e.clientY);
      });

    const fin_drag = (e) =>
      {
        if (tZoomAltButtonPress && tZoomAltButtonPress.pointerId === e.pointerId)
          {
            const tPress = tZoomAltButtonPress;
            tZoomAltButtonPress = null;
            try { tRoot.releasePointerCapture (e.pointerId); } catch (er) {}
	            const tPromiseAppui = tPress.type === "bulle"
	              ? termine_appui_bulle_lecture (tPress.etat, e.type !== "pointercancel")
	              : termine_appui_bouton_zoom_alternatif (tPress.etat, e.type !== "pointercancel");
            tPromiseAppui
              .then (() =>
                {
	                  if (e.type === "pointercancel") return;
	                  if (tPress.type === "carte_relecture_audio")
	                    {
	                      if (!tPress.actionPointerDown)
	                        relit_audio_objet_courant_carte_lecture ();
	                      return;
	                    }
	                  if (tPress.type === "zoom_alt" && tPress.actionPointerDown) return;
                  if (tPress.type === "zoom_alt" && tPress.role === "carte_fermeture")
                    {
                      quitte_focus_carte_lecture_scene (pTpl);
                      if (!tPress.retourMenuAudioPointerDown)
                        joue_audio_retour_menu_lecture_scene ();
                      return;
                    }
                  if (tPress.type === "zoom_alt"
                   && (tPress.role === "carte_oui"
                       || tPress.role === "carte_non"
                       || tPress.role === "carte_legende"))
                    {
                      gere_bouton_pedago_lecture (pTpl, tPress.role);
                      return;
                    }
	                  if (tPress.type === "bulle" && tPress.carteId)
	                    {
	                      if (tPress.actionPointerDown) return;
                      ouvre_focus_carte_lecture_depuis_bulle_tpl (pTpl, tPress.carteId);
	                      return;
	                    }
                  if (tPress.type === "scene_button" && tPress.id)
                    {
                      if (tPress.actionPointerDown) return;
                      if (bouton_scene_lecture_est_retour_menu (tPress.id, tPress.node, tPress.dataName))
                        {
                          if (!tPress.retourMenuAudioPointerDown)
                            joue_audio_retour_menu_lecture_scene ();
                        }
                      else if (!tPress.clicAudioPointerDown)
                        joue_audio_projet_lecture_scene ("son_clic");
                      execute_action_bouton_lecture_scene (pTpl, tPress.id);
                      return;
                    }
                  if (tPress.type === "zoog" && tPress.id)
                    {
                      joue_morphing_zoog_lecture_scene (tPress.id, {tpl : pTpl});
                    }
                })
              .finally (() => suspend_flottement_appui_lecture_tpl (pTpl, tPress.etat, false));
            return;
          }
        if (tCarteMove && tCarteMove.pointerId === e.pointerId)
          {
            pTpl._svgBubbleDragCarteId = null;
            try { tRoot.releasePointerCapture (e.pointerId); } catch (er) {}
            tCarteMove = null;
            return;
          }
        if (tCarteResize && tCarteResize.pointerId === e.pointerId)
          {
            pTpl._svgBubbleDragCarteId = null;
            try { tRoot.releasePointerCapture (e.pointerId); } catch (er) {}
            tCarteResize = null;
            return;
          }
        if (tBubbleDrag && tBubbleDrag.pointerId === e.pointerId)
          {
            pTpl._svgBubbleDragCarteId = null;
            try { tRoot.releasePointerCapture (e.pointerId); } catch (er) {}
            if (tBubbleDrag.hitTarget && tBubbleDrag.hitTarget.style)
              tBubbleDrag.hitTarget.style.cursor = "grab";
            tBubbleDrag = null;
            return;
          }
        if (e.pointerType === "touch")
          {
            try { tRoot.releasePointerCapture (e.pointerId); } catch (er) {}
            tTouchPoints.delete (e.pointerId);
            if (tTouchPoints.size > 0) demarre_geste_tactile ();
            else                       tTouchGesture = null;
            return;
          }
        if (!tDrag) return;
        try { tRoot.releasePointerCapture (e.pointerId); } catch (er) {}
        tDrag = null;
      };
    pDoc.addEventListener ("pointerup",     fin_drag);
    pDoc.addEventListener ("pointercancel", fin_drag);
    pDoc.addEventListener ("pointerleave", () =>
      {
        if (!tBubbleDrag && !tCarteMove && !tCarteResize)
          {
            maj_hover (null);
            applique_hover_lecture (null);
          }
      });
    pDoc.addEventListener ("mouseleave",   () =>
      {
        if (!tBubbleDrag && !tCarteMove && !tCarteResize)
          {
            maj_hover (null);
            applique_hover_lecture (null);
          }
      });

    // -- Clic = sélection (uniquement si pas de drag significatif) ------
    pDoc.addEventListener ("click", (e) =>
      {
        if (tDragMov > 3) { tDragMov = 0; return; }
        if (mode_lecture_scene_actif ())
          {
            gere_clic_lecture_scene (e);
            return;
          }
        if (!edition_hub_active ())
          {
            e.preventDefault ();
            return;
          }
        const tCarteInteractionCourante = (!carte_editor_est_actif ()
                                        && tInteractionCartePointer
                                        && tInteractionCartePointer.carteId)
          ? tInteractionCartePointer.carteId
          : null;
        const tPoigneeCarte = cible_poignee_selection_carte (e.target);
        if ((tPoigneeCarte && tPoigneeCarte.carteId)
         || (tCarteInteractionCourante && tInteractionCartePointer.role === "handle"))
          return;
        const tCarteMove = cible_drag_selection_carte (e.target);
        if ((tCarteMove && tCarteMove.carteId)
         || (tCarteInteractionCourante && tInteractionCartePointer.role === "move"))
          return;
        const tCarteBulleId = cible_bulle_carte (e.target) || tCarteInteractionCourante;
        if (tCarteBulleId)
          {
            selectionne_bulle_calque (tCarteBulleId);
            return;
          }
        const tDoubleGeste = e.detail > 1;
        const tCartesActives = carte_editor_est_actif ();
        const tId = cible_id_svg_pertinent (e.target);
        if (tId)
          {
            const tCat = trouve_categorie_pour_id (tId);
            if (tCartesActives)
              {
                if (tCat) gCarteCategoriesOuvertes.set (tCat, true);
                if (tDoubleGeste)
                  {
                    if (interface_est_tactile ())
                      zoom_sur_id (pTpl, tId);
                    return;
                  }
                selectionne_carte_arbre_calque (tId);
                return;
              }
            if (tCat && !categorie_scene_interactive (tCat)) return;
            if (tCat) ecrit_ouverture_categorie_scene (tCat, true);
            if (tDoubleGeste)
              {
                if (interface_est_tactile ())
                  zoom_sur_id (pTpl, tId);
                return;
              }
            selectionne_calque (tId);
            if (gOngletDroite.get () !== "calques")
              {
                if (inspecteur_autoplay_autorise ())
                  joue_audio_objet_lecture_scene (tId);
                else
                  arrete_audio_lecture_scene ();
              }
            return;
          }
        // Clic sur un élément absent de l'arbre → déselection.
        if (tCartesActives)
          {
            gCarteArbreSelectionne.set (null);
            gCarteArbreCategorieSelectionnee.set (null);
            return;
          }
        selectionne_calque (null);
        selectionne_categorie (null);
      });
  }

// Retrouve la catégorie de premier niveau ("obj"/"part"/"boutons"/"zoog"/"prop"/"div") contenant
// l'id donné dans la scène courante.
function trouve_categorie_pour_id (pId)
  {
    const tSceneId = Session.get ("scene_id");
    if (!tSceneId) return null;
    const tScene = Scenes.findOne (tSceneId);
    if (item_interface_scene_par_id (pId, tScene)) return CALQUES_CATEGORIE_INTERFACE;
    const tObjets = svg_objets_scene_normalises (tScene);
    if (!tObjets) return null;
    for (const tNom of CATEGORIES_PERTINENTES)
      {
        const tArr = tObjets[tNom];
        if (!Array.isArray (tArr)) continue;
        if (tArr.some ((c) => c && c.id === pId)) return tNom;
      }
    return null;
  }

// Construit la liste à plat des items visibles (catégories + calques
// dans les catégories ouvertes) pour la navigation clavier des arbres.
function construit_liste_visible (pTpl)
  {
    const tSceneId = Session.get ("scene_id");
    if (!tSceneId) return null;
    const tScene = Scenes.findOne (tSceneId);
    const tObjets = svg_objets_scene_normalises (tScene);
    if (!tObjets) return null;

    const tCategories = [];
    const tItemsParCategorie = new Map ();
    for (const tNom of noms_categories_svg_scene (tScene, tSceneId))
      {
        const tArr = tObjets[tNom];
        if (!Array.isArray (tArr) || tArr.length === 0) continue;
        tCategories.push ({nom : tNom});
        const tItemsCategorie = [];
        if (categorie_scene_ouverte (tNom, tSceneId))
          {
            // On reproduit ici le tri appliqué dans le helper categories_arbre,
            // afin que la navigation au clavier corresponde à l'ordre affiché.
            const tTries = tArr.slice ().sort ((a, b) =>
              cle_tri_calque (a && a["data-name"], tNom).localeCompare (
                cle_tri_calque (b && b["data-name"], tNom),
                "fr", {sensitivity : "base"}));
            for (const c of tTries)
              if (c && c.id)
                tItemsCategorie.push ({type : "calque", id : c.id, categorie : tNom});
          }
        tItemsParCategorie.set (tNom, tItemsCategorie);
      }

    const tBulles = bulles_arbre_calques_scene ();
    if (tBulles.length)
      tCategories.push ({nom : CALQUES_CATEGORIE_BULLES});
    const tItemsInterface = items_interface_scene (tScene);
    tCategories.push ({nom : CALQUES_CATEGORIE_INTERFACE});
    tItemsParCategorie.set (
      CALQUES_CATEGORIE_INTERFACE,
      categorie_scene_ouverte (CALQUES_CATEGORIE_INTERFACE, tSceneId)
        ? tItemsInterface.map ((pItem) => ({type : "calque", id : pItem.id, categorie : CALQUES_CATEGORIE_INTERFACE}))
        : []
    );

    const tCategoriesOrdonnees = ordre_categories_calques_scene (
      tSceneId,
      tCategories.map ((pCategorie) => pCategorie.nom)
    );
    const tItems = [];
    for (const tNom of tCategoriesOrdonnees)
      {
        tItems.push ({type : "categorie", nom : tNom});
        if (tNom === CALQUES_CATEGORIE_BULLES)
          {
            if (!categorie_scene_ouverte (CALQUES_CATEGORIE_BULLES, tSceneId)) continue;
            for (const tBulle of tBulles)
              tItems.push ({type : "bulle", carte_id : tBulle.carte_id, categorie : CALQUES_CATEGORIE_BULLES});
            continue;
          }

        const tItemsCategorie = tItemsParCategorie.get (tNom) || [];
        for (const tItem of tItemsCategorie)
          tItems.push (tItem);
      }
    return {
      categories : tCategoriesOrdonnees.map ((pNom) => ({nom : pNom})),
      items : tItems,
    };
  }

function applique_surbrillance_svg (pDoc, pIds)
  {
    const tListe = mode_lecture_scene_actif ()
      ? []
      : (Array.isArray (pIds) ? pIds : (pIds ? [pIds] : []));
    const tStyle = style_interface_scene_previsualise ();
    const tCouleursParId = new Map ();
    const tSelectionCategorie = categorie_selection_scene_active ();
    const tSelectionCalque = id_selection_scene_active ();
    const tArcEnCielActif = !!(tSelectionCategorie
      && !tSelectionCalque
      && tStyle.rainbow_category);
    if (tArcEnCielActif)
      {
        palette_arc_en_ciel_style_interface (tListe).forEach ((pColor, pId) =>
          {
            tCouleursParId.set (pId, pColor);
          });
      }
    dessine_cadres_pour_ids (pDoc, tListe, {
      classe   : "surbrillance_calque_hub",
      couleur  : tStyle.selection_color,
      couleursParId : tCouleursParId,
      epaisseur : tStyle.stroke_width,
      pointille: pointille_style_interface_scene (tStyle.stroke_style),
    });
  }

function dessine_cadres_pour_cartes (pDoc, pCarteIds, pOpts)
  {
    if (!pDoc || !pDoc.documentElement) return;
    const tAnciens = pDoc.querySelectorAll (`.${pOpts.classe}`);
    tAnciens.forEach ((pNoeud) =>
      {
        if (pNoeud.parentNode) pNoeud.parentNode.removeChild (pNoeud);
      });
    if (!Array.isArray (pCarteIds) || !pCarteIds.length) return;

    const tNs = "http://www.w3.org/2000/svg";
    const tRoot = pDoc.documentElement;
    const tBullesLayer = tRoot.querySelector (`.${CARTE_DECOR_BULLES_LAYER_CLASS}`);
    const tTransformCartes = transform_calque_cartes_doc (pDoc);
    pCarteIds.forEach ((pCarteId) =>
      {
        const tCarte = carte_source_pour_bulle_scene (pCarteId);
        const tVue = normalise_vue_carte_client (tCarte && tCarte.vue);
        if (!tVue) return;

        const tRect = pDoc.createElementNS (tNs, "rect");
        const tCouleur = pOpts.couleursParId && typeof pOpts.couleursParId.get === "function"
          ? (pOpts.couleursParId.get (pCarteId) || pOpts.couleur)
          : pOpts.couleur;
        tRect.setAttribute ("class", pOpts.classe);
        tRect.setAttribute ("data-carte-id", pCarteId);
        tRect.setAttribute ("x", tVue.x);
        tRect.setAttribute ("y", tVue.y);
        tRect.setAttribute ("width", tVue.w);
        tRect.setAttribute ("height", tVue.h);
        tRect.setAttribute ("fill", "none");
        tRect.setAttribute ("stroke", tCouleur);
        applique_transform_calque_cartes_noeud (tRect, tTransformCartes);
        tRect.setAttribute ("stroke-width", Number (pOpts.epaisseur) || 2);
        if (pOpts.pointille)
          tRect.setAttribute ("stroke-dasharray", pOpts.pointille);
        tRect.setAttribute ("vector-effect", "non-scaling-stroke");
        tRect.style.pointerEvents = "none";

        if (tBullesLayer && tBullesLayer.parentNode === tRoot)
          tRoot.insertBefore (tRect, tBullesLayer);
        else
          tRoot.appendChild (tRect);
      });
  }

function dessine_surbrillance_categorie_bulles_svg (pDoc, pCarteIds)
  {
    const tListe = mode_lecture_scene_actif ()
      ? []
      : (Array.isArray (pCarteIds) ? pCarteIds : []);
    const tStyle = style_interface_scene_previsualise ();
    const tCouleursParId = new Map ();
    const tArcEnCielActif = !!(gCategorieSelectionnee.get () === CALQUES_CATEGORIE_BULLES
      && !gCalqueSelectionne.get ()
      && !gBulleSelectionnee.get ()
      && tStyle.rainbow_category);
    if (tArcEnCielActif)
      {
        palette_arc_en_ciel_style_interface (tListe).forEach ((pColor, pCarteId) =>
          {
            tCouleursParId.set (pCarteId, pColor);
          });
      }

    dessine_cadres_pour_cartes (pDoc, tListe,
      {
        classe : CATEGORIE_BULLES_GROUP_CLASS,
        couleur : tStyle.selection_color,
        couleursParId : tCouleursParId,
        epaisseur : tStyle.stroke_width,
        pointille : pointille_style_interface_scene (tStyle.stroke_style),
      });
  }

function applique_hover_svg (pDoc, pId)
  {
    pId = normalise_hover_svg_interactif (pId);
    const tCarteBulleId = carte_id_depuis_hover_info_bulle (pId);
    const tSelectionBulle = bulle_selection_scene_active ();
    if (tCarteBulleId && tCarteBulleId === tSelectionBulle)
      {
        efface_cadres_hover_svg_standard (pDoc);
        dessine_hover_bulle_carte (pDoc, null);
        return;
      }
    if (tCarteBulleId && bulles_scene_visibles ()
     && !categorie_scene_verrouillee (CALQUES_CATEGORIE_BULLES))
      {
        efface_cadres_hover_svg_standard (pDoc);
        dessine_hover_bulle_carte (pDoc, tCarteBulleId);
        return;
      }

    dessine_hover_bulle_carte (pDoc, null);

    // Pas de cadre de hover sur un élément déjà mis en évidence (évite
    // la superposition).
    const tSelCalque = id_selection_scene_active ();
    const tSelCat    = categorie_selection_scene_active ();
    const tCatHover  = pId ? trouve_categorie_pour_id (pId) : null;
    if (pId && pId === tSelCalque) pId = null;
    if (pId && tCatHover && !categorie_scene_interactive (tCatHover)) pId = null;
    if (pId && tSelCat)
      {
        // Vérifie que le hover ne porte pas sur un id de la catégorie
        // sélectionnée (qui a déjà son cadre rouge).
        const tCadres = pDoc.querySelectorAll (
          `.surbrillance_calque_hub[data-cible="${pId}"]`);
        if (tCadres && tCadres.length) pId = null;
      }
    const tStyle = style_interface_scene_previsualise ();
    dessine_cadres_pour_ids (pDoc, pId ? [pId] : [], {
      classe   : "surbrillance_hover_hub",
      couleur  : tStyle.hover_color,
      epaisseur : tStyle.stroke_width,
      pointille: pointille_style_interface_scene (tStyle.stroke_style),
    });
  }

function efface_hover_svg (pTpl)
  {
    pTpl._svgHoverId = null;
    if (pTpl._svgDoc)
      {
        try { pTpl._svgDoc.documentElement.style.cursor = ""; } catch (e) {}
        applique_hover_svg (pTpl._svgDoc, null);
      }
    maj_etiquette_hover (pTpl, null);
  }

// Étiquette de hover suivant la souris : affiche le data-name complet.
function maj_etiquette_hover (pTpl, pId)
  {
    if (!pId)
      {
        if (pTpl._etiquetteHover)
          {
            pTpl._etiquetteHover.style.display = "none";
          }
        return;
      }
    const tDataName = pTpl._dataNamesParId && pTpl._dataNamesParId.get (pId);
    if (!tDataName)
      {
        if (pTpl._etiquetteHover)
          pTpl._etiquetteHover.style.display = "none";
        return;
      }
    if (!pTpl._etiquetteHover)
      {
        const tDiv = document.createElement ("div");
        tDiv.className = "etiquette_hover_svg";
        tDiv.style.cssText =
            "position:fixed;z-index:99998;pointer-events:none;"
          + "background:#1185c8;color:white;padding:3px 7px;"
          + "border-radius:4px;font-size:0.78rem;font-family:sans-serif;"
          + "box-shadow:0 2px 6px rgba(0,0,0,0.2);white-space:nowrap;"
          + "max-width:60vw;overflow:hidden;text-overflow:ellipsis;";
        document.body.appendChild (tDiv);
        pTpl._etiquetteHover = tDiv;
      }
    pTpl._etiquetteHover.textContent = tDataName;
    pTpl._etiquetteHover.style.display = "block";
  }

function positionne_etiquette_hover (pTpl, pClientX, pClientY)
  {
    const tEt = pTpl._etiquetteHover;
    if (!tEt || tEt.style.display === "none") return;
    const tX = pClientX + 14;
    const tY = pClientY + 16;
    // Empêche que l'étiquette dépasse à droite/en bas.
    const tLargeur = tEt.offsetWidth  || 0;
    const tHauteur = tEt.offsetHeight || 0;
    const tMaxX = window.innerWidth  - tLargeur - 8;
    const tMaxY = window.innerHeight - tHauteur - 8;
    tEt.style.left = Math.max (4, Math.min (tX, tMaxX)) + "px";
    tEt.style.top  = Math.max (4, Math.min (tY, tMaxY)) + "px";
  }

// Repositionne tous les cadres de mise en évidence après un changement
// de viewBox (la matrice dépend du viewBox courant).
function repositionne_surbrillances_svg (pDoc)
  {
    const tCadres = pDoc.querySelectorAll (
      ".surbrillance_calque_hub, .surbrillance_hover_hub, .surbrillance_hover_bulle_hub, .surbrillance_selection_bulle_hub");
    tCadres.forEach ((tRect) =>
      {
        const tIdRef = tRect.getAttribute ("data-cible");
        if (!tIdRef) return;
        const tElt = pDoc.getElementById (tIdRef);
        if (!tElt) return;
        maj_geometrie_cadre (tRect, tElt, pDoc.documentElement);
      });
  }

function maj_geometrie_cadre (pRect, pElt, pRoot)
  {
    let tBBox;
    try { tBBox = pElt.getBBox (); } catch (e) { return; }
    if (!tBBox) return;
    pRect.setAttribute ("x",      tBBox.x);
    pRect.setAttribute ("y",      tBBox.y);
    pRect.setAttribute ("width",  tBBox.width);
    pRect.setAttribute ("height", tBBox.height);
    try
      {
        const tEcranElt  = pElt.getScreenCTM  && pElt.getScreenCTM  ();
        const tEcranRoot = pRoot.getScreenCTM && pRoot.getScreenCTM ();
        if (tEcranElt && tEcranRoot)
          {
            const tM = tEcranRoot.inverse ().multiply (tEcranElt);
            pRect.setAttribute ("transform",
              `matrix(${tM.a} ${tM.b} ${tM.c} ${tM.d} ${tM.e} ${tM.f})`);
          }
      }
    catch (e) {}
  }

function dessine_cadres_pour_ids (pDoc, pIds, pOpts)
  {
    if (!pDoc || !pDoc.documentElement) return;
    const tAnciens = pDoc.querySelectorAll (`.${pOpts.classe}`);
    tAnciens.forEach ((n) => n.parentNode && n.parentNode.removeChild (n));
    if (!pIds || pIds.length === 0) return;

    const tNs   = "http://www.w3.org/2000/svg";
    const tRoot = pDoc.documentElement;
    const tBullesLayer = tRoot.querySelector (`.${CARTE_DECOR_BULLES_LAYER_CLASS}`);
    for (const tId of pIds)
      {
        const tElt = pDoc.getElementById (tId);
        if (!tElt || typeof tElt.getBBox !== "function") continue;
        let tBBox;
        try { tBBox = tElt.getBBox (); } catch (e) { continue; }
        if (!tBBox || (!tBBox.width && !tBBox.height)) continue;

        const tRect = pDoc.createElementNS (tNs, "rect");
        tRect.setAttribute ("fill",   "none");
        const tCouleur = pOpts.couleursParId && typeof pOpts.couleursParId.get === "function"
          ? (pOpts.couleursParId.get (tId) || pOpts.couleur)
          : pOpts.couleur;
        tRect.setAttribute ("stroke", tCouleur);
        tRect.setAttribute ("stroke-width", Number (pOpts.epaisseur) || 2);
        if (pOpts.pointille)
          tRect.setAttribute ("stroke-dasharray", pOpts.pointille);
        tRect.setAttribute ("vector-effect", "non-scaling-stroke");
        tRect.setAttribute ("class", pOpts.classe);
        tRect.setAttribute ("data-cible", tId);
        tRect.style.pointerEvents = "none";
        maj_geometrie_cadre (tRect, tElt, tRoot);
        if (tBullesLayer && tBullesLayer.parentNode === tRoot)
          tRoot.insertBefore (tRect, tBullesLayer);
        else
          tRoot.appendChild (tRect);
      }
  }

function dessine_cadre_pour_id (pDoc, pId, pOpts)
  { dessine_cadres_pour_ids (pDoc, pId ? [pId] : [], pOpts); }

function efface_cadres_hover_svg_standard (pDoc)
  {
    if (!pDoc || typeof pDoc.querySelectorAll !== "function") return;
    const tAnciens = pDoc.querySelectorAll (".surbrillance_hover_hub");
    tAnciens.forEach ((pNoeud) =>
      {
        if (pNoeud.parentNode) pNoeud.parentNode.removeChild (pNoeud);
      });
  }

function assure_cadre_bulle_svg (pDoc, pClasse, pOpt = {})
  {
    const tRoot = pDoc && pDoc.documentElement;
    if (!tRoot) return null;

    let tGroup = tRoot.querySelector (`.${pClasse}`);
    if (!tGroup)
      {
        tGroup = pDoc.createElementNS ("http://www.w3.org/2000/svg", "g");
        tGroup.setAttribute ("class", pClasse);
        tGroup.style.pointerEvents = "none";
        tGroup.style.opacity = "0";
        tGroup.style.transition = pOpt.transition || "";

        const tRect = pDoc.createElementNS ("http://www.w3.org/2000/svg", "rect");
        tRect.setAttribute ("class", `${pClasse}__frame`);
        tRect.setAttribute ("vector-effect", "non-scaling-stroke");

        const tLabelBg = pDoc.createElementNS ("http://www.w3.org/2000/svg", "rect");
        tLabelBg.setAttribute ("class", `${pClasse}__label_bg`);
        tLabelBg.setAttribute ("rx", "4");
        tLabelBg.setAttribute ("ry", "4");

        const tLabel = pDoc.createElementNS ("http://www.w3.org/2000/svg", "text");
        tLabel.setAttribute ("class", `${pClasse}__label`);
        tLabel.setAttribute ("text-anchor", "middle");
        tLabel.setAttribute ("dominant-baseline", "middle");
        tLabel.setAttribute ("font-size", "13");
        tLabel.setAttribute ("font-family", "sans-serif");
        tLabel.setAttribute ("font-weight", "600");

        tGroup.appendChild (tRect);
        tGroup.appendChild (tLabelBg);
        tGroup.appendChild (tLabel);
        tRoot.appendChild (tGroup);
      }

    if (tGroup.parentNode !== tRoot)
      tRoot.appendChild (tGroup);

    return {
      groupe : tGroup,
      cadre : tGroup.querySelector (`.${pClasse}__frame`),
      labelFond : tGroup.querySelector (`.${pClasse}__label_bg`),
      labelTexte : tGroup.querySelector (`.${pClasse}__label`),
    };
  }

function synchronise_poignees_selection_bulle_svg (pDoc, pGroupe, pCarteId, pVue, pStyle = {})
  {
    if (!pDoc || !pGroupe) return;
    const tExistantes = Array.from (pGroupe.querySelectorAll (`.${SELECTION_BULLE_HANDLE_CLASS}`));
    if (!pCarteId || !pVue)
      {
        tExistantes.forEach ((pHandle) => { pHandle.style.display = "none"; });
        return;
      }

    const tPoignees = [
      {coin : "nw", type : "circle", x : pVue.x, y : pVue.y, cursor : "nwse-resize", rayon : pStyle.rayon ?? 5.5},
      {coin : "ne", type : "circle", x : pVue.x + pVue.w, y : pVue.y, cursor : "nesw-resize", rayon : pStyle.rayon ?? 5.5},
      {coin : "sw", type : "circle", x : pVue.x, y : pVue.y + pVue.h, cursor : "nesw-resize", rayon : pStyle.rayon ?? 5.5},
      {coin : "se", type : "circle", x : pVue.x + pVue.w, y : pVue.y + pVue.h, cursor : "nwse-resize", rayon : pStyle.rayon ?? 5.5},
      {coin : "n", type : "rect", x : pVue.x + 10, y : pVue.y - 5, w : Math.max (12, pVue.w - 20), h : 10, cursor : "ns-resize"},
      {coin : "s", type : "rect", x : pVue.x + 10, y : pVue.y + pVue.h - 5, w : Math.max (12, pVue.w - 20), h : 10, cursor : "ns-resize"},
      {coin : "w", type : "rect", x : pVue.x - 5, y : pVue.y + 10, w : 10, h : Math.max (12, pVue.h - 20), cursor : "ew-resize"},
      {coin : "e", type : "rect", x : pVue.x + pVue.w - 5, y : pVue.y + 10, w : 10, h : Math.max (12, pVue.h - 20), cursor : "ew-resize"},
    ];
    const tExistantesParCoin = new Map (
      tExistantes.map ((pHandle) => [pHandle.getAttribute ("data-handle"), pHandle])
    );

    tPoignees.forEach ((pHandleInfo) =>
      {
        let tHandle = tExistantesParCoin.get (pHandleInfo.coin) || null;
        if (!tHandle)
          {
            tHandle = pDoc.createElementNS (
              "http://www.w3.org/2000/svg",
              pHandleInfo.type === "rect" ? "rect" : "circle"
            );
            tHandle.setAttribute ("class", SELECTION_BULLE_HANDLE_CLASS);
            tHandle.setAttribute ("vector-effect", "non-scaling-stroke");
            tHandle.style.pointerEvents = "all";
            pGroupe.appendChild (tHandle);
          }
        else if (tHandle.tagName.toLowerCase () !== (pHandleInfo.type === "rect" ? "rect" : "circle"))
          {
            pGroupe.removeChild (tHandle);
            tHandle = pDoc.createElementNS (
              "http://www.w3.org/2000/svg",
              pHandleInfo.type === "rect" ? "rect" : "circle"
            );
            tHandle.setAttribute ("class", SELECTION_BULLE_HANDLE_CLASS);
            tHandle.setAttribute ("vector-effect", "non-scaling-stroke");
            tHandle.style.pointerEvents = "all";
            pGroupe.appendChild (tHandle);
          }
        tHandle.setAttribute ("data-handle", pHandleInfo.coin);
        tHandle.setAttribute ("data-bulle-carte-id", pCarteId);
        if (pHandleInfo.type === "rect")
          {
            tHandle.setAttribute ("x", pHandleInfo.x);
            tHandle.setAttribute ("y", pHandleInfo.y);
            tHandle.setAttribute ("width", pHandleInfo.w);
            tHandle.setAttribute ("height", pHandleInfo.h);
            tHandle.setAttribute ("fill", pStyle.edgeFill || "#ffffff");
            tHandle.setAttribute ("fill-opacity", String (pStyle.edgeFillOpacity ?? 0.001));
            tHandle.setAttribute ("stroke", "none");
          }
        else
          {
            tHandle.setAttribute ("cx", pHandleInfo.x);
            tHandle.setAttribute ("cy", pHandleInfo.y);
            tHandle.setAttribute ("r", String (pHandleInfo.rayon));
            tHandle.setAttribute ("fill", pStyle.fill || "white");
            tHandle.setAttribute ("stroke", pStyle.stroke || "#ff5722");
            tHandle.setAttribute ("stroke-width", String (pStyle.strokeWidth ?? 2));
          }
        tHandle.style.cursor = pHandleInfo.cursor;
        tHandle.style.display = "";
      });
  }

function dessine_cadre_bulle_carte (pDoc, pCarteId, pOpt = {})
  {
    if (!pDoc || !pDoc.documentElement) return;
    const tClasse = pOpt.classe || HOVER_BULLE_GROUP_CLASS;
    const tHover = assure_cadre_bulle_svg (pDoc, tClasse, {transition : pOpt.transition});
    if (!tHover || !tHover.groupe || !tHover.cadre || !tHover.labelFond || !tHover.labelTexte)
      return;
    if (!pCarteId)
      {
        tHover.groupe.style.opacity = "0";
        tHover.groupe.style.pointerEvents = "none";
        tHover.groupe.removeAttribute ("data-bulle-carte-id");
        tHover.cadre.style.pointerEvents = "none";
        tHover.cadre.style.cursor = "";
        tHover.cadre.removeAttribute ("data-bulle-carte-id");
        tHover.cadre.removeAttribute ("data-overlay-role");
        tHover.labelFond.style.pointerEvents = "none";
        tHover.labelFond.style.cursor = "";
        tHover.labelFond.removeAttribute ("data-bulle-carte-id");
        tHover.labelFond.removeAttribute ("data-overlay-role");
        tHover.labelTexte.style.pointerEvents = "none";
        tHover.labelTexte.style.cursor = "";
        tHover.labelTexte.removeAttribute ("data-bulle-carte-id");
        tHover.labelTexte.removeAttribute ("data-overlay-role");
        return tHover;
      }

    const tCarte = carte_source_pour_bulle_scene (pCarteId);
    const tVue = normalise_vue_carte_client (tCarte && tCarte.vue);
    if (!tVue)
      {
        tHover.groupe.style.opacity = "0";
        tHover.groupe.style.pointerEvents = "none";
        tHover.groupe.removeAttribute ("data-bulle-carte-id");
        tHover.cadre.style.pointerEvents = "none";
        tHover.cadre.style.cursor = "";
        tHover.cadre.removeAttribute ("data-bulle-carte-id");
        tHover.cadre.removeAttribute ("data-overlay-role");
        tHover.labelFond.style.pointerEvents = "none";
        tHover.labelFond.style.cursor = "";
        tHover.labelFond.removeAttribute ("data-bulle-carte-id");
        tHover.labelFond.removeAttribute ("data-overlay-role");
        tHover.labelTexte.style.pointerEvents = "none";
        tHover.labelTexte.style.cursor = "";
        tHover.labelTexte.removeAttribute ("data-bulle-carte-id");
        tHover.labelTexte.removeAttribute ("data-overlay-role");
        return tHover;
      }

    const tNom = libelle_carte_scene (pCarteId) || "Carte";
    const tCentreX = tVue.x + (tVue.w / 2);
    const tLabelY = tVue.y + 7;
    applique_transform_calque_cartes_noeud (tHover.groupe, transform_calque_cartes_doc (pDoc));

    tHover.cadre.setAttribute ("x", tVue.x);
    tHover.cadre.setAttribute ("y", tVue.y);
    tHover.cadre.setAttribute ("width", tVue.w);
    tHover.cadre.setAttribute ("height", tVue.h);
    tHover.cadre.setAttribute ("fill", pOpt.fill || "grey");
    tHover.cadre.setAttribute ("fill-opacity", String (pOpt.fillOpacity ?? 0.1));
    tHover.cadre.setAttribute ("stroke", pOpt.stroke || "grey");
    tHover.cadre.setAttribute ("stroke-opacity", String (pOpt.strokeOpacity ?? 0.9));
    tHover.cadre.setAttribute ("stroke-width", String (pOpt.strokeWidth ?? 1.5));
    tHover.groupe.style.pointerEvents = pOpt.interactive ? "all" : "none";
    tHover.cadre.style.pointerEvents = pOpt.interactive ? "all" : "none";
    tHover.cadre.style.cursor = pOpt.interactive ? "grab" : "";

    tHover.labelTexte.textContent = tNom;
    tHover.labelTexte.setAttribute ("x", tCentreX);
    tHover.labelTexte.setAttribute ("fill", "white");
    tHover.labelTexte.setAttribute ("y", tLabelY + 10);

    let tBBox;
    try { tBBox = tHover.labelTexte.getBBox (); }
    catch (e) { tBBox = {width : Math.max (42, tNom.length * 7), height : 13}; }

    const tFondLargeur = Math.max (54, tBBox.width + 18);
    const tFondHauteur = Math.max (22, tBBox.height + 10);
    const tFondX = tCentreX - (tFondLargeur / 2);
    const tFondY = tVue.y + 6;

    tHover.labelFond.setAttribute ("x", tFondX);
    tHover.labelFond.setAttribute ("y", tFondY);
    tHover.labelFond.setAttribute ("width", tFondLargeur);
    tHover.labelFond.setAttribute ("height", tFondHauteur);
    tHover.labelFond.setAttribute ("fill", pOpt.labelBgFill || pOpt.stroke || "grey");
    tHover.labelFond.setAttribute ("fill-opacity", String (pOpt.labelBgOpacity ?? 1));
    tHover.labelTexte.setAttribute ("x", tCentreX);
    tHover.labelTexte.setAttribute ("y", tFondY + (tFondHauteur / 2));
    tHover.labelTexte.setAttribute ("fill", pOpt.labelFill || "white");
    tHover.labelFond.style.pointerEvents = pOpt.interactive ? "all" : "none";
    tHover.labelFond.style.cursor = pOpt.interactive ? "grab" : "";
    tHover.labelTexte.style.pointerEvents = pOpt.interactive ? "all" : "none";
    tHover.labelTexte.style.cursor = pOpt.interactive ? "grab" : "";

    tHover.groupe.setAttribute ("data-bulle-carte-id", pCarteId);
    tHover.cadre.setAttribute ("data-bulle-carte-id", pCarteId);
    tHover.cadre.setAttribute ("data-overlay-role", pOpt.interactive ? "move" : "frame");
    tHover.labelFond.setAttribute ("data-bulle-carte-id", pCarteId);
    tHover.labelFond.setAttribute ("data-overlay-role", pOpt.interactive ? "move" : "label-bg");
    tHover.labelTexte.setAttribute ("data-bulle-carte-id", pCarteId);
    tHover.labelTexte.setAttribute ("data-overlay-role", pOpt.interactive ? "move" : "label");
    tHover.groupe.style.opacity = "1";
    return tHover;
  }

function dessine_hover_bulle_carte (pDoc, pCarteId)
  {
    dessine_cadre_bulle_carte (pDoc, pCarteId,
      {
        classe : HOVER_BULLE_GROUP_CLASS,
        transition : "opacity 160ms ease",
      });
  }

function dessine_selection_bulle_carte (pDoc, pCarteId)
  {
    const tStyle = style_interface_scene_previsualise ();
    const tSelectionInteractive = carte_editor_est_actif ()
      || (!!gBulleSelectionnee.get ()
          && categorie_scene_interactive (CALQUES_CATEGORIE_BULLES));
    const tSelection = dessine_cadre_bulle_carte (pDoc, pCarteId,
      {
        classe : SELECTION_BULLE_GROUP_CLASS,
        fill : "grey",
        fillOpacity : 0.1,
        stroke : tStyle.selection_color,
        strokeOpacity : 0.98,
        strokeWidth : Math.max (1.8, Number (tStyle.stroke_width) || 2),
        labelBgFill : tStyle.selection_color,
        labelBgOpacity : 1,
        labelFill : "white",
        interactive : tSelectionInteractive,
      });
    const tCarte = pCarteId ? carte_source_pour_bulle_scene (pCarteId) : null;
    const tVue = normalise_vue_carte_client (tCarte && tCarte.vue);
    synchronise_poignees_selection_bulle_svg (
      pDoc,
      tSelection && tSelection.groupe,
      pCarteId,
      tVue,
      {
        stroke : tStyle.selection_color,
        strokeWidth : Math.max (1.8, Number (tStyle.stroke_width) || 2),
      }
    );
  }

// ---------------------------------------------------------------------------
// Application des valeurs des inputs x / y / zoom au viewBox courant.
// ---------------------------------------------------------------------------

function applique_panzoom_depuis_inputs (pTpl)
  {
    const tVB = pTpl._svgVB;
    const tApp = pTpl._svgApplique;
    const tNat = pTpl._svgVBNatif;
    if (!tVB || !tApp || !tNat) return;

    const tIx = pTpl.find (".js_pan_x");
    const tIy = pTpl.find (".js_pan_y");
    const tIz = pTpl.find (".js_zoom");
    const tX = tIx ? parseFloat (tIx.value) : tVB.x;
    const tY = tIy ? parseFloat (tIy.value) : tVB.y;
    let   tZ = tIz ? parseFloat (tIz.value) : (tNat.w / tVB.w);

    if (!isFinite (tZ) || tZ <= 0) tZ = 1;
    // Borne le zoom comme dans le wheel handler (1/50x .. 50x).
    tZ = Math.max (1 / 50, Math.min (50, tZ));

    const tNouvW = tNat.w / tZ;
    const tNouvH = tNat.h / tZ;
    annule_animation_viewbox_tpl (pTpl);
    tVB.x = isFinite (tX) ? tX : tVB.x;
    tVB.y = isFinite (tY) ? tY : tVB.y;
    tVB.w = tNouvW;
    tVB.h = tNouvH;
    arme_notice_cadrage_carte ();
    tApp ();
  }

// ---------------------------------------------------------------------------
// « Adopter ces coordonnées » : sauvegarde la vue courante comme vue
// par défaut de la scène, et régénère la vignette à partir de la zone
// actuellement visible du SVG.
// ---------------------------------------------------------------------------

function adopter_vue_courante (pTpl, pBouton)
  {
    const tDoc  = pTpl._svgDoc;
    const tVB   = vue_camera_courante_scene (pTpl);
    const tScId = Session.get ("scene_id");
    if (!tDoc || !tVB || !tScId)
      {
        gVueMessage.set ({texte : "SVG non chargé.", type : "erreur"});
        return;
      }
    const tScene = Scenes.findOne (tScId);
    if (!tScene)
      { gVueMessage.set ({texte : "Scène introuvable.", type : "erreur"}); return; }

    if (pBouton) pBouton.disabled = true;
    gVueMessage.set ({texte : "Génération de la vignette…", type : "info"});

    // Clone la racine SVG et nettoie les overlays internes (cadres de
    // surbrillance / hover) avant de sérialiser pour rasterisation.
    const tClone = tDoc.documentElement.cloneNode (true);
    const tParasites = tClone.querySelectorAll (
      ".surbrillance_calque_hub, .surbrillance_hover_hub, .surbrillance_hover_bulle_hub, .surbrillance_selection_bulle_hub");
    tParasites.forEach ((n) => n.parentNode && n.parentNode.removeChild (n));

    const tVueDef = adapte_vue_au_ratio_cible (
      {x : tVB.x, y : tVB.y, w : tVB.w, h : tVB.h},
      ratio_rapport_lh_scene_projet (rapport_lh_scene_projet_courant ())
    ) || {x : tVB.x, y : tVB.y, w : tVB.w, h : tVB.h};
    const tVueCapture = vue_camera_svg_depuis_vue_brute_scene (pTpl, tVueDef) || tVueDef;
    const tDimensionsScene = dimensions_scene_vignette_pour_rapport (rapport_lh_scene_projet_courant ());
    // On force explicitement le viewport du clone à partir de la vue
    // sauvegardée, plutôt que de dépendre du viewBox courant déjà présent
    // dans le DOM. Cela rend la vignette plus prévisible si le SVG source
    // porte ses propres dimensions intrinsèques.
    tClone.setAttribute ("viewBox", `${tVueCapture.x} ${tVueCapture.y} ${tVueCapture.w} ${tVueCapture.h}`);
    tClone.setAttribute ("width", String (tDimensionsScene.largeur));
    tClone.setAttribute ("height", String (tDimensionsScene.hauteur));
    tClone.setAttribute ("preserveAspectRatio", "xMidYMid meet");
    const tTexte = new XMLSerializer ().serializeToString (tClone);

    rasterise_svg_en_jpeg (
      tTexte,
      tDimensionsScene.largeur,
      tDimensionsScene.hauteur
    )
      .then ((tBlob) =>
        {
          const tBaseNom = (tScene.slug || "scene") + "-vue.jpg";
          return televerse_hubfile (tBlob, tBaseNom, "image/jpeg",
            {
              user_id : Meteor.userId (),
              ownerId : Meteor.userId (),
              kind    : "scene_vignette",
            });
        })
      .then ((tFile) =>
        {
          return new Promise ((resolve, reject) =>
            {
              Meteor.call ("maj_scene_vue_defaut",
                {
                  scene_id    : tScId,
                  vue_defaut  : tVueDef,
                  vignette_id : tFile && tFile._id,
                },
                (err) =>
                  {
                    if (err) { reject (err); return; }
                    resolve ();
                  });
            });
        })
      .then (() =>
        {
          pTpl._svgVBInitBrut = {...tVueDef};
          pTpl._svgVBInit = {...tVueCapture};
          programme_maj_cadre_scene_dimensions_tpl (pTpl);
          if (pBouton) pBouton.disabled = false;
          gVueMessage.set ({texte : "Vue par défaut enregistrée.", type : "succes"});
        })
      .catch ((err) =>
        {
          if (pBouton) pBouton.disabled = false;
          gVueMessage.set
            ({texte : "Erreur : " + ((err && (err.reason || err.message)) || err),
              type  : "erreur"});
        });
  }

// ---------------------------------------------------------------------------
// Téléversement du SVG (+ génération vignette JPEG) puis appel de la
// méthode serveur d'affectation à la scène.
// ---------------------------------------------------------------------------
function televerse_svg_pour_scene (pFichier, pSceneId)
  {
    const tScene = pSceneId ? Scenes.findOne (pSceneId, {fields : {svg_id : 1, objets_config : 1}}) : null;
    if (tScene && tScene.svg_id)
      {
        const tConfirme = confirm (
          "Remplacer le fichier SVG actuellement importé pour cette scène ?\n\nCe changement ne pourra pas être annulé."
        );
        if (!tConfirme) return;
      }

    gUploadSvgEnCours.set (true);
    gUploadSvgProgression.set (0);
    gUploadSvgPhase.set ("svg");
    const tRapportScene = rapport_lh_scene_projet_courant ();

    prepare_svg_pour_scene (
      pFichier,
      {scene_id : pSceneId},
      (pct, phase) =>
        {
          gUploadSvgPhase.set (phase);
          gUploadSvgProgression.set (pct);
        },
      {
        rapport_lh_scene : tRapportScene,
        objets_config : tScene && tScene.objets_config,
      })
      .then ((tRes) =>
        {
          Meteor.call ("affecte_svg_a_scene",
            {
              scene_id    : pSceneId,
              svg_id      : tRes.svg_id,
              svg_text    : tRes.svg_text,
              vignette_id : tRes.vignette_id,
              objets_config : tRes.objets_config,
            },
            (err2) =>
              {
                gUploadSvgEnCours.set (false);
                gUploadSvgProgression.set (0);
                gUploadSvgPhase.set ("");
                if (err2)
                  {
                    alert ("Affectation échouée : " + (err2.reason || err2.message));
                    try { HubFiles.remove ({_id : tRes.svg_id}); } catch (e) {}
                    try { HubFiles.remove ({_id : tRes.vignette_id}); } catch (e) {}
                  }
              });
        })
      .catch ((err) =>
        {
          gUploadSvgEnCours.set (false);
          gUploadSvgProgression.set (0);
          gUploadSvgPhase.set ("");
          alert ("Téléversement échoué : " + ((err && (err.reason || err.message)) || err));
        });
  }


// ===========================================================================
// Enregistreur audio (modal) : capture micro brute (48 kHz mono, sans
// traitements navigateur), onde progressive en temps réel + VU-mètre
// vertical permanent, puis édition (sélection + suppression / gain /
// désélection) via wavesurfer avant téléversement vers HubFiles et
// affectation au calque courant. Boîte draggable et redimensionnable,
// position persistée en localStorage.
// ===========================================================================

if (edition_hub_active ())
{
const REC_NORMALIZE_AUTO_STORAGE_KEY = "cogivoice_hub_recorder_normalize_auto";

function rec_lit_normalize_auto ()
  {
    try
      {
        const tVal = localStorage.getItem (REC_NORMALIZE_AUTO_STORAGE_KEY);
        if (tVal === null) return true;
        return tVal === "1" || tVal === "true";
      }
    catch (e) { return true; }
  }

const gRecord = new ReactiveDict ();
gRecord.set ("open",          false);
gRecord.set ("recording",     false);
gRecord.set ("has_buffer",    false);
gRecord.set ("elapsed",       0);
gRecord.set ("pulse",         false);
gRecord.set ("dur",           0);
gRecord.set ("cur",           0);
gRecord.set ("sel_start",     null);
gRecord.set ("sel_end",       null);
gRecord.set ("gain_db",       0);
gRecord.set ("playing",       false);
gRecord.set ("saving",        false);
gRecord.set ("edit_count",    0);
gRecord.set ("undo_count",    0);
gRecord.set ("stream_ready",  false);
gRecord.set ("warming",       false);
gRecord.set ("format_info",   "");
gRecord.set ("unsupported",   false);
gRecord.set ("unsupported_msg", "");
gRecord.set ("normalize_auto", rec_lit_normalize_auto ());

const gRec =
  {
    devices       : [],
    device_id     : "",
    stream        : null,
    media_recorder: null,
    chunks        : [],
    audio_ctx     : null,           // AudioContext partagé (capture + analyse)
    analyser      : null,
    monitor_src   : null,           // MediaStreamAudioSourceNode
    raf_id        : 0,
    timer_id      : 0,
    warmup_id     : 0,
    stream_started_at : 0,
    started_at    : 0,
    pulse_id      : 0,

    // Onde progressive : tableau de paires {min, max} accumulées
    // pendant l'enregistrement. Le rendu canvas redimensionne pour
    // tenir dans la largeur visible (downsample à la volée).
    live_peaks    : [],
    live_canvas   : null,
    last_peak_t   : 0,

    // VU-mètre : niveau lissé + crête (en dBFS) avec hold.
    vu_level_db   : -100,
    vu_peak_db    : -100,
    vu_peak_at    : 0,
    vu_bar_el     : null,
    vu_peak_el    : null,

    // Édition post-capture
    buffer        : null,
    blob_url      : null,
    ws            : null,
    regions       : null,
    region        : null,
    mediaSyncCleanup: null,
    history       : [],
    audio_ctx_play: null,
    last_modified : false,

    // Géométrie persistée de la boîte
    box           : null,           // {x,y,w,h}
    drag          : null,           // {ox, oy, sx, sy} pendant drag
    resize        : null,           // {sw, sh, sx, sy} pendant resize
    interaction_overlay : null,
    resize_obs    : null,
  };

// ---- Utilitaires --------------------------------------------------------

const REC_STORAGE_KEY = "cogivoice_hub_recorder_box";
const REC_OPEN_STORAGE_KEY = "cogivoice_hub_recorder_open";
const REC_DEVICE_STORAGE_KEY = "cogivoice_hub_recorder_device_id";
const REC_WAVEFORM_VERTICAL_SCALE = 3;
const REC_LIVE_BAR_WIDTH = 3;
const REC_LIVE_BAR_GAP = 2;
const REC_LIVE_MAX_BARS = 3000;
const REC_MIC_WARMUP_MS = 3200;
const REC_BOX_MARGIN = 8;
const REC_BOX_MIN_W = 480;
const REC_BOX_MIN_H = 306;

function rec_set_error (pMsg)
  {
    gRecord.set ("error", pMsg || null);
  }

function rec_ecrit_normalize_auto (pVal)
  {
    const tVal = !!pVal;
    try { localStorage.setItem (REC_NORMALIZE_AUTO_STORAGE_KEY, tVal ? "1" : "0"); }
    catch (e) {}
    gRecord.set ("normalize_auto", tVal);
  }

function rec_meteor_call (pNom, ...pArgs)
  {
    return new Promise ((resolve, reject) =>
      {
        Meteor.call (pNom, ...pArgs,
          (pErr, pRes) =>
            {
              if (pErr) reject (pErr);
              else resolve (pRes);
            });
      });
  }

function rec_lit_device_id ()
  {
    try
      {
        const tVal = localStorage.getItem (REC_DEVICE_STORAGE_KEY);
        return (typeof tVal === "string" && tVal) ? tVal : "";
      }
    catch (e) { return ""; }
  }

function rec_ecrit_device_id (pVal)
  {
    const tVal = (typeof pVal === "string") ? pVal : "";
    try
      {
        if (tVal) localStorage.setItem (REC_DEVICE_STORAGE_KEY, tVal);
        else localStorage.removeItem (REC_DEVICE_STORAGE_KEY);
      }
    catch (e) {}
    gRec.device_id = tVal;
  }

function rec_format_seconds (pS)
  {
    const tS = Math.max (0, Number (pS) || 0);
    const tMin = Math.floor (tS / 60);
    const tSec = Math.floor (tS - tMin * 60);
    const tMs  = Math.floor ((tS - Math.floor (tS)) * 100);
    return String (tMin).padStart (2, "0") + ":"
         + String (tSec).padStart (2, "0") + "."
         + String (tMs).padStart (2, "0");
  }

function rec_get_audio_ctx ()
  {
    if (gRec.audio_ctx && gRec.audio_ctx.state !== "closed") return gRec.audio_ctx;
    const Ctor = window.AudioContext || window.webkitAudioContext;
    if (!Ctor) return null;
    // Tente 48 kHz (Chrome/Firefox respectent ; Safari peut ignorer).
    try { gRec.audio_ctx = new Ctor ({sampleRate : 48000}); }
    catch (e) { gRec.audio_ctx = new Ctor (); }
    return gRec.audio_ctx;
  }

// Encode un AudioBuffer en WAV PCM 16 bits.
function rec_encode_wav (pAudioBuffer)
  {
    const tNumCh = pAudioBuffer.numberOfChannels;
    const tSr    = pAudioBuffer.sampleRate;
    const tFr    = pAudioBuffer.length;
    const tBytesPerSample = 2;
    const tBlockAlign = tNumCh * tBytesPerSample;
    const tByteRate   = tSr * tBlockAlign;
    const tDataSize   = tFr * tBlockAlign;
    const tBuf  = new ArrayBuffer (44 + tDataSize);
    const tView = new DataView (tBuf);
    let tOff = 0;
    function ws (s) { for (let i = 0; i < s.length; i++) tView.setUint8 (tOff++, s.charCodeAt (i)); }
    function w32 (v) { tView.setUint32 (tOff, v, true); tOff += 4; }
    function w16 (v) { tView.setUint16 (tOff, v, true); tOff += 2; }
    ws ("RIFF"); w32 (36 + tDataSize); ws ("WAVE");
    ws ("fmt "); w32 (16); w16 (1); w16 (tNumCh); w32 (tSr); w32 (tByteRate); w16 (tBlockAlign); w16 (16);
    ws ("data"); w32 (tDataSize);
    const tCh = [];
    for (let c = 0; c < tNumCh; c++) tCh.push (pAudioBuffer.getChannelData (c));
    for (let i = 0; i < tFr; i++)
      {
        for (let c = 0; c < tNumCh; c++)
          {
            let s = Math.max (-1, Math.min (1, tCh[c][i]));
            tView.setInt16 (tOff, s < 0 ? s * 0x8000 : s * 0x7FFF, true);
            tOff += 2;
          }
      }
    return new Blob ([tBuf], {type : "audio/wav"});
  }

function rec_visual_sample (pAudioBuffer, pIndex, pGain)
  {
    if (!pAudioBuffer) return 0;
    const tIdx = Math.max (0, Math.min (pAudioBuffer.length - 1, pIndex));
    let tBest = 0;
    for (let c = 0; c < pAudioBuffer.numberOfChannels; c++)
      {
        const v = (pAudioBuffer.getChannelData (c)[tIdx] || 0) * pGain;
        if (Math.abs (v) > Math.abs (tBest)) tBest = v;
      }
    return tBest;
  }

function rec_waveform_split_channels ()
  {
    const tNb = Math.max (1, gRec.buffer ? gRec.buffer.numberOfChannels : 1);
    return ws_split_channels_overlay (tNb);
  }

function rec_apply_playback_gain_preview (pTime)
  {
    if (!gRec.ws) return;
    const tGdb = Number (gRecord.get ("gain_db") || 0);
    if (!gRec.region || Math.abs (tGdb) < 0.01)
      { try { gRec.ws.setVolume (1); } catch (e) {} return; }

    const tT = (typeof pTime === "number")
      ? pTime
      : (typeof gRec.ws.getCurrentTime === "function" ? gRec.ws.getCurrentTime () : 0);
    const tIn = (tT >= gRec.region.start && tT <= gRec.region.end);
    try { gRec.ws.setVolume (audio_volume_preview (tIn ? db_to_lin (tGdb) : 1)); }
    catch (e) {}
  }

// ---- Géométrie de la boîte (drag + resize + persistance) ----------------

function rec_lit_box ()
  {
    try
      {
        const tRaw = localStorage.getItem (REC_STORAGE_KEY);
        if (!tRaw) return null;
        const tObj = JSON.parse (tRaw);
        if (typeof tObj !== "object" || !tObj) return null;
        const {x, y, w, h} = tObj;
        if ([x, y, w, h].some ((v) => typeof v !== "number" || !isFinite (v)))
          return null;
        return {x, y, w, h};
      }
    catch (e) { return null; }
  }

function rec_ecrit_box (pBox)
  {
    try { localStorage.setItem (REC_STORAGE_KEY, JSON.stringify (pBox)); }
    catch (e) {}
  }

function rec_box_par_defaut ()
  {
    const tBounds = rec_box_bounds (); 
    const tMaxW = Math.max (REC_BOX_MIN_W, tBounds.maxX - tBounds.minX);
    const tMaxH = Math.max (REC_BOX_MIN_H, tBounds.maxY - tBounds.minY);
    const tW = Math.min (1100, Math.floor (window.innerWidth * 0.92), tMaxW);
    const tH = Math.min (640, Math.floor ((window.innerHeight - tBounds.minY) * 0.88), tMaxH);
    return {
      x : Math.max (tBounds.minX, Math.floor ((window.innerWidth - tW) / 2)),
      y : Math.max (tBounds.minY, Math.floor ((window.innerHeight - tH + tBounds.minY) / 2)),
      w : tW,
      h : tH,
    };
  }

function rec_header_safe_top ()
  {
    const tHeader = document.querySelector (".app-header");
    if (!tHeader) return REC_BOX_MARGIN;
    const tRect = tHeader.getBoundingClientRect ();
    return Math.max (REC_BOX_MARGIN, Math.ceil (tRect.bottom) + REC_BOX_MARGIN);
  }

function rec_box_bounds ()
  {
    return {
      minX : REC_BOX_MARGIN,
      minY : rec_header_safe_top (),
      maxX : Math.max (REC_BOX_MARGIN, window.innerWidth - REC_BOX_MARGIN),
      maxY : Math.max (rec_header_safe_top (), window.innerHeight - REC_BOX_MARGIN),
    };
  }

const gRecorderCogibulle = cree_fenetre_cogibulle (
  {
    selector            : ".js_inspecteur_record_card",
    storage             : cree_stockage_local_fenetre_cogibulle (
      {
        boxKey  : REC_STORAGE_KEY,
        openKey : REC_OPEN_STORAGE_KEY,
      }),
    isOpen              : () => gRecord.get ("open"),
    setOpen             : (pOpen) => gRecord.set ("open", !!pOpen),
    minWidth            : REC_BOX_MIN_W,
    minHeight           : REC_BOX_MIN_H,
    preferredWidth      : 1100,
    preferredHeight     : 640,
    widthViewportRatio  : 0.92,
    heightViewportRatio : 0.88,
    margin              : REC_BOX_MARGIN,
    safeTop             : rec_header_safe_top,
    onEscape            : () =>
      {
        if (gRec.region)
          {
            rec_deselect ();
            return;
          }
        rec_close_dialog (true);
      },
  });

function rec_cree_overlay_interaction (pCursor)
  {
    rec_supprime_overlay_interaction ();
    const tOverlay = document.createElement ("div");
    tOverlay.style.cssText =
        "position:fixed;inset:0;z-index:100001;background:transparent;touch-action:none;"
      + "cursor:" + pCursor + ";";
    document.body.appendChild (tOverlay);
    document.body.style.userSelect = "none";
    document.body.style.cursor = pCursor;
    gRec.interaction_overlay = tOverlay;
  }

function rec_supprime_overlay_interaction ()
  {
    gRecorderCogibulle.removeInteractionOverlay ();
  }

// Vérifie qu'une box stockée tient dans la fenêtre actuelle (avec
// marge), sinon retombe sur les valeurs par défaut.
function rec_normalise_box (pBox)
  {
    return gRecorderCogibulle.normaliseBox (pBox);
  }

function rec_applique_box ()
  {
    gRecorderCogibulle.applyBox ();
  }

function rec_sauve_box ()
  {
    gRecorderCogibulle.saveBox ();
  }

function rec_drag_start (e)
  {
    gRecorderCogibulle.startDrag (e);
  }
function rec_drag_move (e)
  {
    if (!gRec.drag || !gRec.box) return;
    if (gRec.drag.pointerId !== undefined && e.pointerId !== gRec.drag.pointerId) return;
    const tBounds = rec_box_bounds ();
    let nx = e.clientX - gRec.drag.ox;
    let ny = e.clientY - gRec.drag.oy;
    nx = Math.max (tBounds.minX, Math.min (tBounds.maxX - gRec.box.w, nx));
    ny = Math.max (tBounds.minY, Math.min (tBounds.maxY - gRec.box.h, ny));
    gRec.box.x = nx; gRec.box.y = ny;
    rec_applique_box ();
  }
function rec_drag_end (e)
  {
    if (!gRec.drag) return;
    if (e && gRec.drag.pointerId !== undefined && e.pointerId !== gRec.drag.pointerId) return;
    gRec.drag = null;
    document.removeEventListener ("pointermove", rec_drag_move);
    document.removeEventListener ("pointerup", rec_drag_end);
    document.removeEventListener ("pointercancel", rec_drag_end);
    rec_supprime_overlay_interaction ();
    rec_sauve_box ();
  }

function rec_resize_start (e)
  {
    gRecorderCogibulle.startResize (e);
  }
function rec_resize_move (e)
  {
    if (!gRec.resize || !gRec.box) return;
    if (gRec.resize.pointerId !== undefined && e.pointerId !== gRec.resize.pointerId) return;
    const tBounds = rec_box_bounds ();
    let nw = gRec.resize.sw + (e.clientX - gRec.resize.sx);
    let nh = gRec.resize.sh + (e.clientY - gRec.resize.sy);
    nw = Math.max (REC_BOX_MIN_W, Math.min (tBounds.maxX - gRec.box.x, nw));
    nh = Math.max (REC_BOX_MIN_H, Math.min (tBounds.maxY - gRec.box.y, nh));
    gRec.box.w = nw; gRec.box.h = nh;
    rec_applique_box ();
  }
function rec_resize_end (e)
  {
    if (!gRec.resize) return;
    if (e && gRec.resize.pointerId !== undefined && e.pointerId !== gRec.resize.pointerId) return;
    gRec.resize = null;
    document.removeEventListener ("pointermove", rec_resize_move);
    document.removeEventListener ("pointerup", rec_resize_end);
    document.removeEventListener ("pointercancel", rec_resize_end);
    rec_supprime_overlay_interaction ();
    rec_sauve_box ();
  }

// ---- Boucle continue : VU-mètre + onde progressive ----------------------

function rec_db_to_y_pct (pDb)
  {
    // Échelle visuelle : 0 dBFS = bas du barème = 0%, -60 dBFS = bas = 100%
    // Pour la barre de niveau on veut hauteur = part remplie depuis le bas,
    // donc on renvoie le ratio (0..1) à utiliser pour height.
    const tDb = Math.max (-60, Math.min (0, pDb));
    return (tDb + 60) / 60;
  }

function rec_couleur_vu (pDb)
  {
    if (pDb >= -3)  return "#e74c3c";   // rouge
    if (pDb >= -12) return "#f39c12";   // orange
    return "#27ae60";                   // vert
  }

function rec_loop ()
  {
    gRec.raf_id = 0;
    if (!gRec.analyser) return;

    const tAna = gRec.analyser;
    const tBuf = new Float32Array (tAna.fftSize);
    if (typeof tAna.getFloatTimeDomainData === "function")
      tAna.getFloatTimeDomainData (tBuf);
    else
      {
        // Fallback (anciens Safari) : convertit depuis l'octet.
        const tU = new Uint8Array (tAna.fftSize);
        tAna.getByteTimeDomainData (tU);
        for (let i = 0; i < tU.length; i++) tBuf[i] = (tU[i] - 128) / 128;
      }

    // 1) Calcule peak (linéaire) et min/max signés sur la trame.
    let tMin = 0, tMax = 0, tPeak = 0;
    for (let i = 0; i < tBuf.length; i++)
      {
        const v = tBuf[i];
        if (v < tMin) tMin = v;
        if (v > tMax) tMax = v;
        const a = Math.abs (v);
        if (a > tPeak) tPeak = a;
      }

    // 2) VU-mètre : conversion dBFS, lissage léger, peak hold 800 ms.
    const tDb = tPeak > 1e-5 ? 20 * Math.log10 (tPeak) : -100;
    // Attaque rapide / release lente
    const tPrev = gRec.vu_level_db;
    if (tDb > tPrev) gRec.vu_level_db = tDb;
    else             gRec.vu_level_db = tPrev * 0.7 + tDb * 0.3;
    const tNow = performance.now ();
    if (tDb > gRec.vu_peak_db || tNow - gRec.vu_peak_at > 800)
      { gRec.vu_peak_db = tDb; gRec.vu_peak_at = tNow; }

    rec_render_vu ();

    // 3) Onde progressive : on accumule un point toutes les ~30 ms,
    // mais uniquement pendant l'enregistrement.
    if (gRecord.get ("recording"))
      {
        if (tNow - gRec.last_peak_t > 25)
          {
            gRec.live_peaks.push ({mn : tMin, mx : tMax});
            gRec.last_peak_t = tNow;
            if (gRec.live_peaks.length > REC_LIVE_MAX_BARS)
              gRec.live_peaks.splice (0, gRec.live_peaks.length - REC_LIVE_MAX_BARS);
          }
        rec_render_live_wave ();
      }

    gRec.raf_id = requestAnimationFrame (rec_loop);
  }

function rec_render_vu ()
  {
    const tBar  = gRec.vu_bar_el  || document.querySelector (".js_inspecteur_record_vu_bar");
    const tPeak = gRec.vu_peak_el || document.querySelector (".js_inspecteur_record_vu_peak");
    if (tBar)  gRec.vu_bar_el  = tBar;
    if (tPeak) gRec.vu_peak_el = tPeak;
    if (!tBar) return;
    const tRatio = rec_db_to_y_pct (gRec.vu_level_db);
    tBar.style.height = (tRatio * 100).toFixed (1) + "%";
    tBar.style.background = rec_couleur_vu (gRec.vu_level_db);
    if (tPeak)
      {
        const tP = rec_db_to_y_pct (gRec.vu_peak_db);
        tPeak.style.bottom = (tP * 100).toFixed (1) + "%";
        tPeak.style.background = rec_couleur_vu (gRec.vu_peak_db);
        tPeak.style.opacity = gRec.vu_peak_db > -100 ? "1" : "0";
      }
  }

function rec_render_live_wave ()
  {
    const tCanvas = gRec.live_canvas
      || document.querySelector (".js_inspecteur_record_canvas");
    if (!tCanvas) return;
    gRec.live_canvas = tCanvas;
    const tScroll = tCanvas.closest (".js_inspecteur_record_live_scroll")
      || document.querySelector (".js_inspecteur_record_live_scroll");
    const tDpr = window.devicePixelRatio || 1;
    const tViewportW = (tScroll && tScroll.clientWidth) || tCanvas.clientWidth || 800;
    const tCssH = (tScroll && tScroll.clientHeight) || tCanvas.clientHeight || 120;
    const tPts = gRec.live_peaks;
    const tStep = REC_LIVE_BAR_WIDTH + REC_LIVE_BAR_GAP;
    const tCssW = Math.max (tViewportW, tPts.length * tStep + REC_LIVE_BAR_WIDTH);
    tCanvas.style.width = tCssW + "px";
    tCanvas.style.height = "100%";
    const tW = Math.round (tCssW * tDpr);
    const tH = Math.round (tCssH * tDpr);
    if (tCanvas.width  !== tW) tCanvas.width  = tW;
    if (tCanvas.height !== tH) tCanvas.height = tH;
    const tCtx = tCanvas.getContext ("2d");
    if (!tCtx) return;
    const tDark = scene_theme_est_sombre ();
    tCtx.fillStyle = tDark ? "#1c1c1e" : "#f4f4f4";
    tCtx.fillRect (0, 0, tW, tH);
    // Ligne médiane
    tCtx.strokeStyle = tDark ? "rgba(255,255,255,0.10)" : "rgba(0,0,0,0.08)";
    tCtx.lineWidth = 1;
    tCtx.beginPath ();
    tCtx.moveTo (0, tH / 2);
    tCtx.lineTo (tW, tH / 2);
    tCtx.stroke ();

    if (!tPts.length) return;

    // Échelle fixe en temps réel : une normalisation recalculée pendant
    // la prise donne l'impression que le gain change brutalement. On
    // garde un zoom visuel constant, comme un oscilloscope.
    const tNorm = REC_WAVEFORM_VERTICAL_SCALE;

    tCtx.fillStyle = tDark ? "#0a84ff" : "#1185c8";
    const tBarW = Math.max (1, Math.round (REC_LIVE_BAR_WIDTH * tDpr));
    const tStepPx = Math.max (tBarW + 1, Math.round (tStep * tDpr));
    for (let i = 0; i < tPts.length; i++)
      {
        const p = tPts[i];
        const mn = p.mn;
        const mx = p.mx;
        const tMxN = Math.max (-1, Math.min (1, mx * tNorm));
        const tMnN = Math.max (-1, Math.min (1, mn * tNorm));
        const yMax = (1 - tMxN) * (tH / 2);
        const yMin = (1 - tMnN) * (tH / 2);
        const tHaut = Math.max (1, yMin - yMax);
        tCtx.fillRect (i * tStepPx, yMax, tBarW, tHaut);
      }

    if (tScroll) tScroll.scrollLeft = tScroll.scrollWidth;
  }

// ---- Énumération / monitoring entrée audio ------------------------------

async function rec_charge_devices (pAvecPermission)
  {
    try
      {
        if (!gRec.device_id) gRec.device_id = rec_lit_device_id ();
        if (pAvecPermission)
          {
            const tStream = await audio_get_user_media ({audio : true});
            tStream.getTracks ().forEach ((t) => t.stop ());
          }
        if (!(navigator.mediaDevices && navigator.mediaDevices.enumerateDevices))
          {
            gRec.devices = [];
            gRecord.set ("devices_loaded", Date.now ());
            return;
          }
        const tAll = await navigator.mediaDevices.enumerateDevices ();
        const tIns = tAll.filter ((d) => d.kind === "audioinput");
        gRec.devices = tIns.map ((d, i) =>
          ({deviceId : d.deviceId,
            label    : d.label || ("Entrée audio " + (i + 1))}));
        if (gRec.device_id
            && !gRec.devices.some ((d) => d.deviceId === gRec.device_id))
          gRec.device_id = "";
        if (!gRec.device_id && gRec.devices.length)
          rec_ecrit_device_id (gRec.devices[0].deviceId);
        else if (gRec.device_id)
          rec_ecrit_device_id (gRec.device_id);
        gRecord.set ("devices_loaded", Date.now ());
        rec_set_error (null);
      }
    catch (err)
      {
        rec_set_error ("Accès au micro refusé : " + (err.message || err.name));
      }
  }

// Contraintes audio « brutes » : aucun traitement navigateur, 48 kHz mono.
function rec_audio_constraints ()
  {
    const tBase = {
      echoCancellation   : false,
      noiseSuppression   : false,
      autoGainControl    : false,
      // Préfère 48 kHz / mono / 16 bits — certains navigateurs honorent.
      sampleRate         : 48000,
      channelCount       : 1,
      sampleSize         : 16,
      // Préfixes Google (Chrome ancien) : neutralise tout le DSP.
      googEchoCancellation       : false,
      googAutoGainControl        : false,
      googAutoGainControl2       : false,
      googNoiseSuppression       : false,
      googNoiseSuppression2      : false,
      googHighpassFilter         : false,
      googTypingNoiseDetection   : false,
      googAudioMirroring         : false,
    };
    if (gRec.device_id)
      tBase.deviceId = {exact : gRec.device_id};
    return {audio : tBase, video : false};
  }

// Démarre / redémarre le monitoring (analyser permanent qui alimente VU
// + onde live). Indépendant du MediaRecorder.
async function rec_start_monitor ()
  {
    rec_stop_monitor ();
    rec_set_error (null);
    if (!audio_has_capture_support ())
      {
        gRecord.set ("unsupported", true);
        gRecord.set ("unsupported_msg", rec_unsupported_message ());
        return;
      }
    try
      {
        gRec.stream = await audio_get_user_media (rec_audio_constraints ());
      }
    catch (err)
      {
        // Réessaie sans contraintes strictes en cas d'échec (Safari, etc.).
        try
          {
            gRec.stream = await audio_get_user_media (
              {audio : gRec.device_id
                ? {deviceId : {exact : gRec.device_id},
                   echoCancellation : false,
                   noiseSuppression : false,
                   autoGainControl  : false}
                : {echoCancellation : false,
                   noiseSuppression : false,
                   autoGainControl  : false}});
          }
        catch (err2)
          {
            rec_set_error ("Accès au micro refusé : " + (err2.message || err2.name));
            return;
          }
      }
    const tCtx = rec_get_audio_ctx ();
    if (!tCtx) { rec_set_error ("AudioContext indisponible."); return; }
    await audio_resume_context (tCtx);
    try
      {
        gRec.monitor_src = tCtx.createMediaStreamSource (gRec.stream);
        const tAna = tCtx.createAnalyser ();
        tAna.fftSize = 2048;
        tAna.smoothingTimeConstant = 0;
        gRec.monitor_src.connect (tAna);
        gRec.analyser = tAna;
      }
    catch (err)
      {
        rec_set_error ("Analyseur audio impossible : " + (err.message || err.name));
        return;
      }
    gRec.stream_started_at = Date.now ();
    gRecord.set ("warming", true);
    gRecord.set ("stream_ready", false);
    // Met à jour l'info de format (réelle).
    try
      {
        const tTrack = gRec.stream.getAudioTracks ()[0];
        const tSet   = (tTrack && tTrack.getSettings) ? tTrack.getSettings () : {};
        const tSr    = tSet.sampleRate || (gRec.audio_ctx && gRec.audio_ctx.sampleRate) || 0;
        const tCh    = tSet.channelCount || 1;
        gRecord.set ("format_info",
          (tSr ? Math.round (tSr / 1000) + " kHz · " : "")
          + (tCh === 1 ? "mono" : (tCh + " canaux"))
          + " · sans traitement");
      }
    catch (e) {}
    rec_raw_start ();
    if (gRec.warmup_id) clearTimeout (gRec.warmup_id);
    gRec.warmup_id = setTimeout (() =>
      {
        gRec.warmup_id = 0;
        if (!gRec.stream) return;
        gRecord.set ("warming", false);
        gRecord.set ("stream_ready", true);
      }, REC_MIC_WARMUP_MS);
    if (!gRec.raf_id) gRec.raf_id = requestAnimationFrame (rec_loop);
  }

function rec_stop_monitor ()
  {
    if (gRec.raf_id) { cancelAnimationFrame (gRec.raf_id); gRec.raf_id = 0; }
    if (gRec.warmup_id) { clearTimeout (gRec.warmup_id); gRec.warmup_id = 0; }
    rec_raw_stop ();
    if (gRec.monitor_src) { try { gRec.monitor_src.disconnect (); } catch (e) {} gRec.monitor_src = null; }
    gRec.analyser = null;
    if (gRec.stream)
      {
        try { gRec.stream.getTracks ().forEach ((t) => t.stop ()); } catch (e) {}
        gRec.stream = null;
      }
    gRec.vu_level_db = -100;
    gRec.vu_peak_db  = -100;
    gRecord.set ("stream_ready", false);
    gRecord.set ("warming", false);
    rec_render_vu ();
  }

// ---- Démarrage / arrêt de l'enregistrement ------------------------------

// Capture brute via ScriptProcessorNode : utilisée comme fallback quand
// MediaRecorder est indisponible (Safari ancien) ou par préférence pour
// éviter le passage par un codec perceptuel. Accumule des Float32Array
// par canal et reconstruit un AudioBuffer à l'arrêt.
function rec_raw_reset_chunks ()
  {
    const tCh = Math.max (1, gRec.raw_channels || 1);
    gRec.raw_chunks = [];
    for (let c = 0; c < tCh; c++) gRec.raw_chunks.push ([]);
  }

function rec_raw_start ()
  {
    const tCtx = gRec.audio_ctx;
    if (!tCtx || !gRec.monitor_src) return false;
    if (gRec.raw_processor)
      {
        rec_raw_reset_chunks ();
        return true;
      }
    const tCh = Math.max (1, gRec.monitor_src.channelCount || 1);
    let tProc;
    try { tProc = tCtx.createScriptProcessor (4096, tCh, tCh); }
    catch (e) { return false; }
    gRec.raw_channels = tCh;
    gRec.raw_sr       = tCtx.sampleRate;
    rec_raw_reset_chunks ();
    tProc.onaudioprocess = (e) =>
      {
        if (!gRecord.get ("recording")) return;
        for (let c = 0; c < tCh; c++)
          {
            const tIn = e.inputBuffer.getChannelData (c);
            // Copie obligatoire (le buffer est réutilisé par le navigateur).
            const tCp = new Float32Array (tIn.length);
            tCp.set (tIn);
            gRec.raw_chunks[c].push (tCp);
          }
      };
    // Branche : source -> processor -> gain muet -> destination
    // (un nœud terminal est requis pour activer onaudioprocess).
    const tMute = tCtx.createGain ();
    tMute.gain.value = 0;
    try
      {
        gRec.monitor_src.connect (tProc);
        tProc.connect (tMute);
        tMute.connect (tCtx.destination);
      }
    catch (e) { return false; }
    gRec.raw_processor = tProc;
    gRec.raw_mute      = tMute;
    return true;
  }

function rec_raw_stop ()
  {
    try { if (gRec.raw_processor) gRec.raw_processor.disconnect (); } catch (e) {}
    try { if (gRec.raw_mute)      gRec.raw_mute.disconnect (); } catch (e) {}
    if (gRec.raw_processor) gRec.raw_processor.onaudioprocess = null;
    gRec.raw_processor = null;
    gRec.raw_mute      = null;
  }

async function rec_raw_finalize ()
  {
    const tCtx = rec_get_audio_ctx ();
    if (!tCtx) { rec_set_error ("AudioContext indisponible."); return; }
    const tCh = gRec.raw_channels || 1;
    const tSr = gRec.raw_sr || tCtx.sampleRate;
    let tLen = 0;
    if (gRec.raw_chunks && gRec.raw_chunks[0])
      for (const seg of gRec.raw_chunks[0]) tLen += seg.length;
    if (!tLen) { rec_set_error ("Aucun échantillon capturé."); return; }
    const tNew = tCtx.createBuffer (tCh, tLen, tSr);
    for (let c = 0; c < tCh; c++)
      {
        const tDst = tNew.getChannelData (c);
        let tOff = 0;
        const tSegs = gRec.raw_chunks[c] || [];
        for (const seg of tSegs) { tDst.set (seg, tOff); tOff += seg.length; }
      }
    gRec.raw_chunks = null;
    rec_apply_new_buffer (tNew);
  }

async function rec_start ()
  {
    if (gRecord.get ("recording")) return;
    rec_set_error (null);
    if (!audio_has_capture_support ())
      {
        gRecord.set ("unsupported", true);
        gRecord.set ("unsupported_msg", rec_unsupported_message ());
        return;
      }

    if (!gRec.stream) await rec_start_monitor ();
    if (!gRec.stream) return;
  if (gRecord.get ("warming")) return;

    // Réinit live waveform (la nouvelle session remplace l'affichage,
    // l'AudioBuffer existant sera concaténé à la fin).
    gRec.live_peaks = [];
    gRec.live_canvas = null;
    gRec.use_raw    = false;

    if (rec_raw_start ())
      {
        rec_raw_reset_chunks ();
        gRec.use_raw = true;
      }
    else if (window.MediaRecorder)
      {
        gRec.chunks = [];
        let tMime = "";
        const tCandidats = ["audio/webm;codecs=opus", "audio/webm",
                            "audio/ogg;codecs=opus", "audio/mp4"];
        for (const m of tCandidats)
          { try { if (window.MediaRecorder.isTypeSupported (m)) { tMime = m; break; } } catch (e) {} }
        let tOk = false;
        try
          {
            const tOpts = tMime
              ? {mimeType : tMime, audioBitsPerSecond : 256000}
              : {audioBitsPerSecond : 256000};
            gRec.media_recorder = new MediaRecorder (gRec.stream, tOpts);
            tOk = true;
          }
        catch (err)
          {
            try { gRec.media_recorder = new MediaRecorder (gRec.stream); tOk = true; }
            catch (err2) { tOk = false; }
          }
        if (tOk)
          {
            gRec.media_recorder.ondataavailable = (e) =>
              { if (e.data && e.data.size) gRec.chunks.push (e.data); };
            gRec.media_recorder.onstop = () => rec_on_stop ();
            gRec.media_recorder.onerror = (e) =>
              {
                rec_set_error ("Erreur enregistrement : "
                  + ((e && e.error && e.error.message) || "inconnue"));
              };
            try { gRec.media_recorder.start (250); }
            catch (e3) { tOk = false; }
          }
        if (!tOk) gRec.use_raw = true;
      }
    else
      {
        gRec.use_raw = true;
      }

    if (gRec.use_raw && !gRec.raw_processor)
      {
        if (!rec_raw_start ())
          { rec_set_error ("Capture audio impossible (ScriptProcessorNode indisponible)."); return; }
      }

    rec_destroy_ws ();
    gRecord.set ("recording", true);
    Tracker.afterFlush (() => rec_render_live_wave ());
    gRec.started_at = Date.now ();
    gRecord.set ("elapsed", 0);
    if (gRec.timer_id) clearInterval (gRec.timer_id);
    gRec.timer_id = setInterval (() =>
      { gRecord.set ("elapsed", (Date.now () - gRec.started_at) / 1000); }, 100);
    if (gRec.pulse_id) clearInterval (gRec.pulse_id);
    gRec.pulse_id = setInterval (() =>
      { gRecord.set ("pulse", !gRecord.get ("pulse")); }, 500);
  }

function rec_stop ()
  {
    if (!gRecord.get ("recording")) return;
    if (gRec.use_raw)
      {
        if (gRec.timer_id) { clearInterval (gRec.timer_id); gRec.timer_id = 0; }
        if (gRec.pulse_id) { clearInterval (gRec.pulse_id); gRec.pulse_id = 0; }
        gRecord.set ("recording", false);
        rec_raw_finalize ();
        return;
      }
    if (gRec.media_recorder && gRec.media_recorder.state !== "inactive")
      { try { gRec.media_recorder.stop (); } catch (e) {} }
  }

async function rec_on_stop ()
  {
    if (gRec.timer_id) { clearInterval (gRec.timer_id); gRec.timer_id = 0; }
    if (gRec.pulse_id) { clearInterval (gRec.pulse_id); gRec.pulse_id = 0; }
    gRecord.set ("recording", false);

    const tBlob = new Blob (gRec.chunks,
      {type : (gRec.chunks[0] && gRec.chunks[0].type) || "audio/webm"});
    if (!tBlob.size)
      { rec_set_error ("Aucun échantillon capturé."); return; }

    const tCtx = rec_get_audio_ctx ();
    if (!tCtx) { rec_set_error ("AudioContext indisponible."); return; }
    let tNew;
    try
      {
        const tAb = await tBlob.arrayBuffer ();
        tNew = await tCtx.decodeAudioData (tAb.slice (0));
      }
    catch (err)
      {
        rec_set_error ("Décodage audio impossible : " + (err.message || err.name));
        return;
      }
    rec_apply_new_buffer (tNew);
  }

function rec_apply_new_buffer (pNew)
  {
    pNew = rec_force_mono (pNew);
    pNew = rec_compense_attaque_micro (pNew);
    if (gRec.buffer)
      {
        if (gRec.buffer.numberOfChannels !== 1)
          gRec.buffer = rec_force_mono (gRec.buffer);
        rec_pousse_undo (gRec.buffer);
        gRec.buffer = rec_concat_buffers (gRec.buffer, pNew);
        gRec.peak_abs_dirty = true;
      }
    else
      {
        gRec.buffer = pNew;
        gRec.peak_abs_dirty = true;
      }
    gRec.last_modified = true;
    gRecord.set ("has_buffer", true);
    gRecord.set ("dur", gRec.buffer.duration);
    gRecord.set ("sel_start", null);
    gRecord.set ("sel_end",   null);
    gRecord.set ("gain_db",   0);
    gRecord.set ("edit_count", gRecord.get ("edit_count") + 1);
    rec_load_in_wavesurfer ();
  }

function rec_force_mono (pBuf)
  {
    if (!pBuf || pBuf.numberOfChannels <= 1) return pBuf;
    const tCtx = rec_get_audio_ctx ();
    const tOut = tCtx.createBuffer (1, pBuf.length, pBuf.sampleRate);
    const tDst = tOut.getChannelData (0);
    const tCh  = pBuf.numberOfChannels;
    for (let c = 0; c < tCh; c++)
      {
        const tSrc = pBuf.getChannelData (c);
        for (let i = 0; i < pBuf.length; i++) tDst[i] += tSrc[i] / tCh;
      }
    return tOut;
  }

function rec_pousse_undo (pBuf)
  {
    gRec.history.push (pBuf);
    if (gRec.history.length > 20) gRec.history.shift ();
    gRecord.set ("undo_count", gRec.history.length);
  }

function rec_concat_buffers (pA, pB)
  {
    const tCtx = rec_get_audio_ctx ();
    const tCh  = Math.max (pA.numberOfChannels, pB.numberOfChannels);
    const tSr  = pA.sampleRate;
    const tLen = pA.length + pB.length;
    const tOut = tCtx.createBuffer (tCh, tLen, tSr);
    for (let c = 0; c < tCh; c++)
      {
        const tDst = tOut.getChannelData (c);
        const tA   = pA.getChannelData (Math.min (c, pA.numberOfChannels - 1));
        const tB   = pB.getChannelData (Math.min (c, pB.numberOfChannels - 1));
        tDst.set (tA, 0);
        tDst.set (tB, pA.length);
      }
    return tOut;
  }

function rec_rms_window (pBuf, pStart, pEnd)
  {
    const tCh = pBuf.numberOfChannels;
    let tSum = 0, tN = 0;
    for (let c = 0; c < tCh; c++)
      {
        const tData = pBuf.getChannelData (c);
        for (let i = pStart; i < pEnd; i++)
          { const v = tData[i]; tSum += v * v; tN++; }
      }
    return tN ? Math.sqrt (tSum / tN) : 0;
  }

function rec_compense_attaque_micro (pBuf)
  {
    if (!pBuf || pBuf.duration < 0.8) return pBuf;
    const tSr = pBuf.sampleRate;
    const tWin = Math.max (128, Math.round (tSr * 0.05));
    const tAttackEnd = Math.min (pBuf.length, Math.round (tSr * 3.4));
    const tRefStart  = Math.min (pBuf.length, Math.round (tSr * 3.2));
    const tRefEnd    = Math.min (pBuf.length, Math.round (tSr * Math.min (pBuf.duration, 6.0)));
    const tVals = [];
    for (let s = tRefStart; s + tWin <= tRefEnd; s += tWin)
      {
        const r = rec_rms_window (pBuf, s, s + tWin);
        if (r > 0.003) tVals.push (r);
      }
    if (tVals.length < 3) return pBuf;
    tVals.sort ((a, b) => a - b);
    const tRef = tVals[Math.floor (tVals.length * 0.65)];
    if (!tRef || tRef < 0.01) return pBuf;

    const tGains = [];
    let tNeedsFix = false;
    for (let s = 0; s < tAttackEnd; s += tWin)
      {
        const e = Math.min (pBuf.length, s + tWin);
        const r = rec_rms_window (pBuf, s, e);
        let g = 1;
        if (r > 0.003 && r < tRef * 0.85)
          {
            g = Math.min (6.0, tRef / Math.max (r, 1e-6));
            if (g > 1.12) tNeedsFix = true;
          }
        tGains.push (g);
      }
    if (!tNeedsFix) return pBuf;

    const tOut = rec_get_audio_ctx ().createBuffer (pBuf.numberOfChannels, pBuf.length, tSr);
    for (let c = 0; c < pBuf.numberOfChannels; c++)
      {
        const tSrc = pBuf.getChannelData (c);
        const tDst = tOut.getChannelData (c);
        for (let i = 0; i < pBuf.length; i++)
          {
            if (i >= tAttackEnd) { tDst[i] = tSrc[i]; continue; }
            const tPos = i / tWin;
            const tIdx = Math.floor (tPos);
            const tFrac = tPos - tIdx;
            const g0 = tGains[Math.min (tGains.length - 1, tIdx)] || 1;
            const g1 = tGains[Math.min (tGains.length - 1, tIdx + 1)] || g0;
            const g = g0 + (g1 - g0) * tFrac;
            tDst[i] = Math.max (-1, Math.min (1, tSrc[i] * g));
          }
      }
    return tOut;
  }

// ---- Wavesurfer (éditeur post-enregistrement) ---------------------------

function rec_destroy_ws ()
  {
    if (typeof gRec.mediaSyncCleanup === "function")
      { try { gRec.mediaSyncCleanup (); } catch (e) {} }
    gRec.mediaSyncCleanup = null;
    if (gRec.gain_overlay && gRec.gain_overlay.parentNode)
      { try { gRec.gain_overlay.parentNode.removeChild (gRec.gain_overlay); }
        catch (e) {} }
    if (gRec.ws) { try { gRec.ws.destroy (); } catch (e) {} }
    gRec.ws            = null;
    gRec.regions       = null;
    gRec.region        = null;
    gRec.ws_wrapper    = null;
    gRec.gain_overlay  = null;
    gRec.audio_ctx_play = null;
    gRecord.set ("playing", false);
    if (gRec.blob_url)
      { try { URL.revokeObjectURL (gRec.blob_url); } catch (e) {} gRec.blob_url = null; }
  }

function rec_hard_stop (pTime = null)
  {
    if (!gRec.ws) return;
    const tDur = Number (gRec.ws.getDuration () || gRecord.get ("dur") || 0);
    const tTarget = pTime === null || pTime === undefined
      ? Math.max (0, Math.min (tDur, Number (gRecord.get ("cur") || 0)))
      : Math.max (0, Math.min (tDur, Number (pTime) || 0));
    try { gRec.ws.pause (); } catch (e) {}
    try { gRec.ws.setVolume (1); } catch (e) {}
    try
      {
        const tMedia = gRec.ws.getMediaElement ();
        if (tMedia && typeof tMedia.pause === "function") tMedia.pause ();
      }
    catch (e) {}
    try { gRec.ws.setTime (tTarget); } catch (e) {}
    gRecord.set ("cur", +tTarget.toFixed (2));
    gRecord.set ("playing", false);
  }

function rec_ws_height ()
  {
    return "auto";
  }

function rec_resize_wavesurfer ()
  {
    if (!gRec.ws) return;
    try
      {
        gRec.ws.setOptions (
          {
            height    : rec_ws_height (),
            barHeight : REC_WAVEFORM_VERTICAL_SCALE,
          });
      }
    catch (e) {}
    rec_update_gain_preview ();
  }

function rec_load_in_wavesurfer ()
  {
    Tracker.afterFlush (async () =>
      {
        let tBlob = null;
        try
          {
            const tCont = document.querySelector (".js_inspecteur_record_ws");
            if (!tCont || !gRec.buffer) return;
            await charge_wavesurfer_edition ();
            rec_destroy_ws ();
            tBlob = rec_encode_wav (gRec.buffer);
            gRec.blob_url = URL.createObjectURL (tBlob);
            const tDark = scene_theme_est_sombre ();
            const tRegions = RegionsPlugin.create ();
            const tWsOptions =
              {
                container     : tCont,
                url           : gRec.blob_url,
                height        : rec_ws_height (),
                waveColor     : tDark ? "#0a84ff" : "#1185c8",
                progressColor : tDark ? "#5ac8fa" : "#0c6ea7",
                cursorColor   : tDark ? "#ff453a" : "#c0392b",
                cursorWidth   : 1,
                normalize     : false,
                barHeight     : REC_WAVEFORM_VERTICAL_SCALE,
                splitChannels : rec_waveform_split_channels (),
                minPxPerSec   : 100,
                fillParent    : true,
                autoScroll    : true,
                autoCenter    : true,
                plugins       : [tRegions],
              };
            if (!audio_prefere_backend_media_element ())
              tWsOptions.backend = "WebAudio";
            const tWs = WaveSurfer.create (tWsOptions);
            gRec.ws      = tWs;
            gRec.regions = tRegions;
            try
              {
                const tMedia = tWs.getMediaElement ();
                if (tMedia && typeof tMedia.getGainNode === "function")
                  gRec.audio_ctx_play = tMedia.getGainNode ().context;
              }
            catch (e) {}

            tRegions.enableDragSelection ({color: "rgba(255, 87, 34, 0.18)"});

            tRegions.on ("region-created", (r) =>
              {
                if (gRec.region && gRec.region !== r)
                  { try { gRec.region.remove (); } catch (e) {} }
                gRec.region = r;
                r.setOptions ({drag: true, resize: true, color: "rgba(255, 87, 34, 0.22)"});
                gRecord.set ("sel_start", +r.start.toFixed (3));
                gRecord.set ("sel_end",   +r.end.toFixed (3));
                gRecord.set ("gain_db", 0);
                rec_update_gain_preview ();
              });
            tRegions.on ("region-updated", (r) =>
              {
                if (r === gRec.region)
                  {
                    gRecord.set ("sel_start", +r.start.toFixed (3));
                    gRecord.set ("sel_end",   +r.end.toFixed (3));
                    rec_update_gain_preview ();
                  }
              });
            tRegions.on ("region-clicked", (r, e) =>
              {
                if (e && typeof e.stopPropagation === "function") e.stopPropagation ();
                gRec.region = r;
                r.play ();
                gRecord.set ("playing", true);
              });
            tRegions.on ("region-removed", (r) =>
              {
                if (r === gRec.region)
                  {
                    gRec.region = null;
                    gRecord.set ("sel_start", null);
                    gRecord.set ("sel_end",   null);
                    rec_update_gain_preview ();
                  }
              });

            // Désélection au clic dans la forme d'onde, hors région.
            tWs.on ("interaction", (pTime) =>
              {
                if (!gRec.region) return;
                const tT = Number (pTime) || 0;
                const tTol = 0.005;
                if (tT < gRec.region.start - tTol
                    || tT > gRec.region.end + tTol)
                  { rec_deselect (); }
              });

            tWs.on ("timeupdate", rec_apply_playback_gain_preview);
            tWs.on ("seeking",    rec_apply_playback_gain_preview);
            tWs.on ("play",       () => rec_apply_playback_gain_preview ());
            tWs.on ("pause", () => { try { tWs.setVolume (1); } catch (e) {} });

            tWs.once ("ready", () =>
              {
                gRecord.set ("dur", tWs.getDuration () || 0);
                try { gRec.ws_wrapper = tWs.getWrapper (); } catch (e) {}
                rec_update_gain_preview ();
              });
            tWs.on ("timeupdate", (t) =>
              {
                const tDur = Number (tWs.getDuration () || gRecord.get ("dur") || 0);
                const tCur = Number (t || 0);
                if (tDur > 0 && tCur >= tDur + 0.05)
                  {
                    rec_hard_stop (tDur);
                    return;
                  }
                gRecord.set ("cur", +Math.max (0, Math.min (tDur || tCur, tCur)).toFixed (2));
              });
            tWs.on ("play",   () => gRecord.set ("playing", true));
            tWs.on ("pause",  () => gRecord.set ("playing", false));
            tWs.on ("finish", () => rec_hard_stop (tWs.getDuration () || gRecord.get ("dur") || 0));
            tWs.on ("zoom",   () => rec_update_gain_preview ());
            tWs.on ("scroll", () => rec_update_gain_preview ());
            try
              {
                const tMedia = tWs.getMediaElement ();
                if (tMedia && typeof tMedia.addEventListener === "function")
                  {
                    const tOnEnded = () => rec_hard_stop (tWs.getDuration () || gRecord.get ("dur") || 0);
                    const tOnPause = () =>
                      {
                        try { tWs.setVolume (1); } catch (e) {}
                        gRecord.set ("playing", false);
                        const tDur = Number (tWs.getDuration () || gRecord.get ("dur") || 0);
                        const tCur = Number (typeof tWs.getCurrentTime === "function"
                          ? tWs.getCurrentTime () : gRecord.get ("cur") || 0);
                        gRecord.set ("cur", +Math.max (0, Math.min (tDur || tCur, tCur)).toFixed (2));
                      };
                    const tOnEmptied = () => rec_hard_stop (0);
                    tMedia.addEventListener ("ended", tOnEnded);
                    tMedia.addEventListener ("pause", tOnPause);
                    tMedia.addEventListener ("emptied", tOnEmptied);
                    gRec.mediaSyncCleanup = () =>
                      {
                        try { tMedia.removeEventListener ("ended", tOnEnded); } catch (e) {}
                        try { tMedia.removeEventListener ("pause", tOnPause); } catch (e) {}
                        try { tMedia.removeEventListener ("emptied", tOnEmptied); } catch (e) {}
                      };
                  }
              }
            catch (e) {}
          }
        catch (e)
          {
            console.warn ("[scene] initialisation WaveSurfer recorder impossible:", e);
            if (tBlob && gRec.blob_url)
              { try { URL.revokeObjectURL (gRec.blob_url); } catch (e2) {} gRec.blob_url = null; }
          }
      });
  }

// Prévisualisation visuelle du gain : un canvas overlay positionné
// EXACTEMENT au-dessus de la région sélectionnée redessine la portion
// sélectionnée du buffer audio mise à l'échelle par db_to_lin(gain).
// Reste de l'onde : intact (rendu wavesurfer original). Aucun reload,
// aucun setOptions destructif.
function rec_update_gain_preview ()
  {
    if (!gRec.ws || !gRec.buffer) return;
    const tWrapper = gRec.ws_wrapper;
    if (!tWrapper) return;
    let tOverlay = gRec.gain_overlay;
    const tDur   = gRec.ws.getDuration () || 0;
    const tGdb   = Number (gRecord.get ("gain_db") || 0);
    const tHasSel = (gRec.region && tDur > 0);
    if (!tHasSel || Math.abs (tGdb) < 0.05)
      { if (tOverlay) tOverlay.style.display = "none"; return; }

    const tWidthCss  = tWrapper.scrollWidth  || tWrapper.clientWidth || 0;
    const tHeightCss = tWrapper.clientHeight || 0;
    if (!tWidthCss || !tHeightCss) return;
    const tLeftPx = Math.floor ((gRec.region.start / tDur) * tWidthCss);
    const tEndPx  = Math.ceil  ((gRec.region.end   / tDur) * tWidthCss);
    const tWPx    = Math.max (1, tEndPx - tLeftPx);

    if (!tOverlay)
      {
        tOverlay = document.createElement ("canvas");
        tOverlay.className = "js_inspecteur_record_gain_overlay";
        tOverlay.style.position      = "absolute";
        tOverlay.style.top           = "0";
        tOverlay.style.pointerEvents = "none";
        tOverlay.style.zIndex        = "3";
        // Le wrapper de wavesurfer doit être positionné pour que le
        // canvas absolu se cale dessus (il l'est déjà en pratique).
        if (getComputedStyle (tWrapper).position === "static")
          tWrapper.style.position = "relative";
        tWrapper.appendChild (tOverlay);
        gRec.gain_overlay = tOverlay;
      }
    tOverlay.style.display = "block";
    tOverlay.style.left   = tLeftPx + "px";
    tOverlay.style.width  = tWPx    + "px";
    tOverlay.style.height = tHeightCss + "px";

    const tDpr = window.devicePixelRatio || 1;
    const tCw  = Math.max (1, Math.round (tWPx * tDpr));
    const tCh  = Math.max (1, Math.round (tHeightCss * tDpr));
    if (tOverlay.width  !== tCw) tOverlay.width  = tCw;
    if (tOverlay.height !== tCh) tOverlay.height = tCh;
    const tCtx = tOverlay.getContext ("2d");
    if (!tCtx) return;
    const tDark = scene_theme_est_sombre ();

    // Fond opaque pour masquer l'onde wavesurfer sous-jacente, teinté
    // de la couleur de région pour conserver le repère visuel.
    tCtx.clearRect (0, 0, tCw, tCh);
    tCtx.fillStyle = tDark ? "#1c1c1e" : "#f4f4f4";
    tCtx.fillRect (0, 0, tCw, tCh);
    tCtx.fillStyle = "rgba(255, 87, 34, 0.22)";
    tCtx.fillRect (0, 0, tCw, tCh);

    // Calcule des paires (min,max) sur le canal 0 du buffer pour la
    // tranche correspondant à la région, à raison d'une colonne par
    // pixel (avec DPR), puis dessine en bleu progressColor.
    const tBuf  = gRec.buffer;
    const tSr   = tBuf.sampleRate;
    const tS    = Math.max (0, Math.floor (gRec.region.start * tSr));
    const tE    = Math.min (tBuf.length, Math.ceil (gRec.region.end * tSr));
    const tSpan = Math.max (1, tE - tS);
    const tLin  = db_to_lin (tGdb);

    // Pic absolu du buffer pour aligner l'amplitude de l'overlay sur
    // celle dessinée par wavesurfer (qui utilise normalize:false →
    // pas de normalisation, on rapporte donc à 1 mais on conserve
    // ce calcul pour rester cohérent avec d'éventuelles évolutions).
    if (gRec.peak_abs === undefined || gRec.peak_abs_dirty)
      {
        let tPeak = 0;
        for (let c = 0; c < tBuf.numberOfChannels; c++)
          {
            const tD = tBuf.getChannelData (c);
            for (let i = 0; i < tD.length; i++)
              { const v = Math.abs (tD[i]); if (v > tPeak) tPeak = v; }
          }
        gRec.peak_abs = Math.max (1e-6, tPeak);
        gRec.peak_abs_dirty = false;
      }
    // normalize:false côté wavesurfer → on n'applique pas de
    // facteur de normalisation à l'overlay non plus.
    const tNorm = 1;

    tCtx.fillStyle = tDark ? "#5ac8fa" : "#0c6ea7";
    const tHalf = tCh / 2;
    for (let x = 0; x < tCw; x++)
      {
        const i0 = tS + Math.floor ((x       / tCw) * tSpan);
        const i1 = tS + Math.max (i0 - tS + 1,
                                  Math.floor (((x + 1) / tCw) * tSpan));
        let mn = 0, mx = 0;
        const tEnd = Math.min (i1, tBuf.length);
        for (let i = i0; i < tEnd; i++)
          {
            const v = rec_visual_sample (tBuf, i, tLin * tNorm);
            if (v < mn) mn = v;
            if (v > mx) mx = v;
          }
        // Clip dans [-1, 1] (saturation visible quand le boost dépasse).
        if (mn < -1) mn = -1; if (mx > 1) mx = 1;
        const yT = tHalf - mx * tHalf;
        const yB = tHalf - mn * tHalf;
        tCtx.fillRect (x, yT, 1, Math.max (1, yB - yT));
      }
  }

async function rec_play_pause ()
  {
    if (!gRec.ws) return;
    if (gRec.ws.isPlaying ()) { gRec.ws.pause (); return; }
    await audio_resume_context (gRec.audio_ctx_play);
    let tStart, tEnd;
    if (gRec.region)
      { tStart = gRec.region.start; tEnd = gRec.region.end; }
    const p = (tStart !== undefined)
      ? gRec.ws.play (tStart, tEnd)
      : gRec.ws.play ();
    rec_apply_playback_gain_preview (tStart);
    if (p && typeof p.catch === "function") p.catch (() => {});
  }

function rec_deselect ()
  {
    if (gRec.region)
      { try { gRec.region.remove (); } catch (e) {} }
    gRec.region = null;
    gRecord.set ("sel_start", null);
    gRecord.set ("sel_end",   null);
    gRecord.set ("gain_db",   0);
  }

function rec_supprime_selection ()
  {
    if (!gRec.buffer || !gRec.region) return;
    const tBuf = gRec.buffer;
    const tSr  = tBuf.sampleRate;
    const tCh  = tBuf.numberOfChannels;
    const tS   = Math.max (0, Math.floor (gRec.region.start * tSr));
    const tE   = Math.min (tBuf.length, Math.ceil (gRec.region.end * tSr));
    if (tE <= tS) return;
    const tNewLen = tBuf.length - (tE - tS);
    if (tNewLen < tSr * 0.05)
      { alert ("La suppression laisserait un fichier trop court."); return; }
    const tCtx = rec_get_audio_ctx ();
    const tOut = tCtx.createBuffer (tCh, tNewLen, tSr);
    for (let c = 0; c < tCh; c++)
      {
        const tSrc = tBuf.getChannelData (c);
        const tDst = tOut.getChannelData (c);
        tDst.set (tSrc.subarray (0, tS), 0);
        tDst.set (tSrc.subarray (tE), tS);
      }
    rec_pousse_undo (tBuf);
    gRec.buffer = tOut;
    gRec.peak_abs_dirty = true;
    gRec.last_modified = true;
    gRecord.set ("dur", tOut.duration);
    gRecord.set ("sel_start", null);
    gRecord.set ("sel_end",   null);
    gRecord.set ("gain_db", 0);
    gRecord.set ("edit_count", gRecord.get ("edit_count") + 1);
    rec_load_in_wavesurfer ();
  }

function rec_applique_gain (pDb)
  {
    if (!gRec.buffer || !gRec.region) return;
    const tBuf = gRec.buffer;
    const tSr  = tBuf.sampleRate;
    const tCh  = tBuf.numberOfChannels;
    const tS   = Math.max (0, Math.floor (gRec.region.start * tSr));
    const tE   = Math.min (tBuf.length, Math.ceil (gRec.region.end * tSr));
    if (tE <= tS) return;
    const tCtx = rec_get_audio_ctx ();
    const tOut = tCtx.createBuffer (tCh, tBuf.length, tSr);
    const tLin = Math.pow (10, Number (pDb) / 20);
    for (let c = 0; c < tCh; c++)
      {
        const tSrc = tBuf.getChannelData (c);
        const tDst = tOut.getChannelData (c);
        tDst.set (tSrc, 0);
        for (let i = tS; i < tE; i++)
          {
            const v = tDst[i] * tLin;
            tDst[i] = Math.max (-1, Math.min (1, v));
          }
      }
    rec_pousse_undo (tBuf);
    const tSStart = gRec.region.start;
    const tSEnd   = gRec.region.end;
    gRec.buffer = tOut;
    gRec.peak_abs_dirty = true;
    gRec.last_modified = true;
    gRecord.set ("gain_db", 0);
    gRecord.set ("edit_count", gRecord.get ("edit_count") + 1);
    rec_load_in_wavesurfer ();
    Tracker.afterFlush (() =>
      {
        setTimeout (() =>
          {
            if (!gRec.regions) return;
            try
              {
                const r = gRec.regions.addRegion (
                  {start: tSStart, end: tSEnd,
                   color: "rgba(255, 87, 34, 0.22)",
                   drag: true, resize: true});
                gRec.region = r;
                gRecord.set ("sel_start", +r.start.toFixed (3));
                gRecord.set ("sel_end",   +r.end.toFixed (3));
              }
            catch (e) {}
          }, 50);
      });
  }

function rec_undo ()
  {
    if (!gRec.history.length) return;
    const tPrev = gRec.history.pop ();
    gRec.buffer = tPrev;
    gRec.peak_abs_dirty = true;
    gRec.last_modified = true;
    gRecord.set ("undo_count", gRec.history.length);
    gRecord.set ("dur", tPrev.duration);
    gRecord.set ("sel_start", null);
    gRecord.set ("sel_end",   null);
    gRecord.set ("gain_db", 0);
    gRecord.set ("edit_count", gRecord.get ("edit_count") + 1);
    rec_load_in_wavesurfer ();
  }

function rec_clear ()
  {
    if (gRec.ws)
      { try { gRec.ws.pause (); } catch (e) {} }
    rec_destroy_ws ();
    gRec.buffer = null;
    gRec.history = [];
    gRec.live_peaks = [];
    gRec.last_modified = false;
    gRecord.set ("has_buffer", false);
    gRecord.set ("dur", 0);
    gRecord.set ("cur", 0);
    gRecord.set ("sel_start", null);
    gRecord.set ("sel_end",   null);
    gRecord.set ("gain_db", 0);
    gRecord.set ("undo_count", 0);
    gRecord.set ("edit_count", gRecord.get ("edit_count") + 1);
    if (gRecord.get ("open") && !gRecord.get ("recording") && !gRecord.get ("unsupported"))
      rec_start_monitor ();
  }

function rec_open_dialog ()
  {
    const tCible = gRec.target || cible_audio_editeur_courante ();
    if (!tCible || (tCible.type === "calque" && !tCible.objetId)
        || (tCible.type === "projet" && (!tCible.projet || !tCible.cle))
        || (tCible.type === "scene" && (!tCible.scene || !tCible.cle)))
      { alert ("Sélectionnez d'abord une cible audio."); return false; }
    gRec.target = tCible;
    return rec_open_dialog_opts ({});
  }

function rec_open_dialog_opts (pOptions = {})
  {
    rec_set_error (null);
    ws_force_pause ();
    const tUnsupMsg = rec_unsupported_message ();
    const tHasGum = audio_has_capture_support ();
    const tHasCtx = !!(window.AudioContext || window.webkitAudioContext);
    const tUnsup  = !!tUnsupMsg;
    if (tUnsup)
      console.warn ("[recorder] support manquant :",
        {hasGetUserMedia: tHasGum, hasAudioContext: tHasCtx,
         hasMediaRecorder: !!window.MediaRecorder,
         secureContext: window.isSecureContext,
         reason: tUnsupMsg});
    gRecord.set ("unsupported", tUnsup);
    gRecord.set ("unsupported_msg", tUnsupMsg);
    gRecorderCogibulle.open (pOptions);
    Tracker.afterFlush (() =>
      {
        rec_applique_box ();
        // ResizeObserver pour ajuster la hauteur du wavesurfer / canvas.
        const tCard = document.querySelector (".js_inspecteur_record_card");
        if (tCard && window.ResizeObserver)
          {
            if (gRec.resize_obs) gRec.resize_obs.disconnect ();
            gRec.resize_obs = new ResizeObserver (() =>
              {
                rec_resize_wavesurfer ();
                rec_render_live_wave ();
              });
            gRec.resize_obs.observe (tCard);
          }
      });
    rec_charge_devices (true).then (() =>
      {
        if (!gRecord.get ("unsupported")) rec_start_monitor ();
      });
    return true;
  }

function rec_cleanup_dialog_runtime ()
  {
    if (gRecord.get ("recording")) rec_stop ();
    if (gRec.ws)
      { try { gRec.ws.pause (); } catch (e) {} }
    if (gRec.timer_id) { clearInterval (gRec.timer_id); gRec.timer_id = 0; }
    if (gRec.pulse_id) { clearInterval (gRec.pulse_id); gRec.pulse_id = 0; }
    rec_raw_stop ();
    gRec.raw_chunks = null;
    rec_stop_monitor ();
    rec_destroy_ws ();
    if (gRec.resize_obs) { try { gRec.resize_obs.disconnect (); } catch (e) {} gRec.resize_obs = null; }
    rec_supprime_overlay_interaction ();
    gRec.buffer = null;
    gRec.history = [];
    gRec.live_peaks = [];
    gRec.last_modified = false;
    gRec.chunks = [];
    gRec.live_canvas = null;
    gRec.vu_bar_el  = null;
    gRec.vu_peak_el = null;
    gRec.target = null;
    gRecord.set ("saving",      false);
    gRecord.set ("recording",   false);
    gRecord.set ("has_buffer",  false);
    gRecord.set ("elapsed",     0);
    gRecord.set ("dur",         0);
    gRecord.set ("cur",         0);
    gRecord.set ("sel_start",   null);
    gRecord.set ("sel_end",     null);
    gRecord.set ("gain_db",     0);
    gRecord.set ("playing",     false);
    gRecord.set ("undo_count",  0);
    gRecord.set ("error",       null);
    gRecord.set ("unsupported_msg", "");
  }

function rec_close_dialog (pConfirm)
  {
    if (pConfirm && gRec.last_modified
        && !confirm ("Annuler l'enregistrement en cours ? Les modifications seront perdues."))
      return false;
    rec_cleanup_dialog_runtime ();
    gRecorderCogibulle.close ();
    return true;
  }

async function rec_save ()
  {
    if (!gRec.buffer) return;
    const tCible = gRec.target || cible_audio_editeur_courante ();
    if (!tCible || (tCible.type === "calque" && (!tCible.objetId || !Session.get ("scene_id")))
        || (tCible.type === "projet" && (!tCible.projet || !tCible.cle))
        || (tCible.type === "scene" && (!tCible.scene || !tCible.cle)))
      { alert ("Aucune cible audio sélectionnée."); return; }

    gRecord.set ("saving", true);
    try
      {
        const tWav = rec_encode_wav (gRec.buffer);
        const tNom = "enregistrement_" + new Date ()
          .toISOString ().replace (/[:.]/g, "-") + ".wav";
        const tFichier = new File ([tWav], tNom, {type : "audio/wav"});

        const tUpload = HubFiles.insert (
          {
            file      : tFichier,
            meta      : tCible.type === "projet"
              ? {ownerId : Meteor.userId (), user_id : Meteor.userId (), kind : "projet-audio",
                 projet_id : tCible.projet._id, cle : tCible.cle,
                 source : "project-audio-recorder"}
              : (tCible.type === "scene"
                ? {ownerId : Meteor.userId (), user_id : Meteor.userId (), kind : "scene-audio",
                   projet_id : tCible.scene.projet_id, scene_id : tCible.scene._id, cle : tCible.cle,
                   source : "scene-audio-recorder"}
                : {ownerId : Meteor.userId (), kind : "calque-audio",
                   scene_id : Session.get ("scene_id"), objet_id : tCible.objetId,
                   source : "recorder"}),
            chunkSize : "dynamic",
          }, false);
        const tUploadFile = await new Promise ((resolve, reject) =>
          {
            tUpload.on ("end", (err, fileObj) =>
              {
                if (err) return reject (err);
                resolve (fileObj);
              });
            tUpload.start ();
          });

        let tFileId = tUploadFile && tUploadFile._id;
        if (!tFileId)
          throw new Meteor.Error ("upload-failed", "Téléversement audio incomplet");

        if (gRecord.get ("normalize_auto") && tCible.type === "calque")
          {
            const tNormalise = await rec_meteor_call (
              "hub.files.normalizeRecorderAudio",
              {
                fileId   : tFileId,
                scene_id : tCible.type === "calque" ? Session.get ("scene_id") : "",
                objet_id : tCible.type === "calque" ? tCible.objetId : "",
              }
            );
            if (tNormalise && tNormalise.fileId) tFileId = tNormalise.fileId;
          }

        if (tCible.type === "projet")
          await rec_meteor_call (
            "maj_audio_projet",
            {
              projet_id : tCible.projet._id,
              cle : tCible.cle,
              audio_file_id : tFileId,
            }
          );
        else if (tCible.type === "scene")
          await rec_meteor_call (
            "maj_audio_scene",
            {scene_id : tCible.scene._id, cle : tCible.cle, audio_file_id : tFileId}
          );
        else
          await rec_meteor_call (
            "maj_objet_scene",
            {scene_id : Session.get ("scene_id"), objet_id : tCible.objetId, audio_file_id : tFileId}
          );
        rec_close_dialog (false);
      }
    catch (err)
      {
        alert ("Téléversement échoué : " + (err.reason || err.message || err));
      }
    finally
      {
        gRecord.set ("saving", false);
      }
  }

// ---- Helpers Blaze ------------------------------------------------------

if (edition_hub_active ())
  Template.scene.helpers
    ({
    inspecteur_record_dialog_open ()    { return gRecord.get ("open"); },
    inspecteur_record_unsupported  ()    { return gRecord.get ("unsupported"); },
    inspecteur_record_unsupported_message ()
      {
        return gRecord.get ("unsupported_msg")
          || "Votre navigateur ne supporte pas l'enregistrement audio.";
      },
    inspecteur_record_error        ()    { return gRecord.get ("error"); },
    inspecteur_record_recording    ()    { return gRecord.get ("recording"); },
    inspecteur_record_recording_attr ()
      { return gRecord.get ("recording") ? "disabled" : null; },
    inspecteur_record_has_buffer   ()    { return gRecord.get ("has_buffer"); },
    inspecteur_record_saving       ()    { return gRecord.get ("saving"); },
    inspecteur_record_pulse_visible ()   { return gRecord.get ("pulse"); },
    inspecteur_record_live_active  ()    { return gRecord.get ("recording"); },
    inspecteur_record_warming      ()    { return gRecord.get ("warming"); },
    inspecteur_record_stream_ready ()    { return gRecord.get ("stream_ready"); },
    inspecteur_record_format_info  ()    { return gRecord.get ("format_info"); },
    inspecteur_record_elapsed_label ()
      { return rec_format_seconds (gRecord.get ("elapsed")); },
    inspecteur_record_selection_label ()
      {
        const s = gRecord.get ("sel_start");
        const e = gRecord.get ("sel_end");
        if (s === null || e === null || s === undefined || e === undefined)
          return "—";
        return rec_format_seconds (s) + " → " + rec_format_seconds (e)
             + "  (" + (e - s).toFixed (2) + " s)";
      },
    inspecteur_record_devices ()
      {
        gRecord.get ("devices_loaded");
        return gRec.devices.map ((d) =>
          ({deviceId    : d.deviceId,
            label       : d.label,
            selectedAttr: d.deviceId === gRec.device_id ? "selected" : null}));
      },
    inspecteur_record_device_disabled_attr ()
      { return gRecord.get ("recording") ? "disabled" : null; },
    inspecteur_record_start_disabled_attr ()
      {
        if (gRecord.get ("unsupported")) return "disabled";
        if (gRecord.get ("error")) return "disabled";
        if (gRecord.get ("warming")) return "disabled";
        if (!gRecord.get ("stream_ready")) return "disabled";
        return null;
      },
    inspecteur_record_delete_disabled_attr ()
      {
        const s = gRecord.get ("sel_start"), e = gRecord.get ("sel_end");
        if (s === null || e === null) return "disabled";
        if (e - s <= 0) return "disabled";
        return null;
      },
    inspecteur_record_deselect_disabled_attr ()
      {
        const s = gRecord.get ("sel_start"), e = gRecord.get ("sel_end");
        return (s === null || e === null) ? "disabled" : null;
      },
    inspecteur_record_undo_disabled_attr ()
      { return gRecord.get ("undo_count") > 0 ? null : "disabled"; },
    inspecteur_record_gain_disabled_attr ()
      {
        const s = gRecord.get ("sel_start"), e = gRecord.get ("sel_end");
        if (s === null || e === null) return "disabled";
        if (e - s <= 0) return "disabled";
        return null;
      },
    inspecteur_record_save_disabled_attr ()
      {
        if (gRecord.get ("saving")) return "disabled";
        if (!gRecord.get ("has_buffer")) return "disabled";
        if (gRecord.get ("recording")) return "disabled";
        const tCible = gRec.target || cible_audio_editeur_courante ();
        if (!tCible) return "disabled";
        if (tCible.type === "calque" && !tCible.objetId) return "disabled";
        if (tCible.type === "projet" && (!tCible.projet || !tCible.cle)) return "disabled";
        if (tCible.type === "scene" && (!tCible.scene || !tCible.cle)) return "disabled";
        return null;
      },
    inspecteur_record_normalize_auto_checked ()
      { return gRecord.get ("normalize_auto") ? "checked" : null; },
    });

Template.scene.onRendered (function ()
  {
    if (!edition_hub_active ()) return;
    const tpl = this;
    tpl.autorun (() =>
      {
        const tDur  = gRecord.get ("dur");
        const tCur  = gRecord.get ("cur");
        const tPlay = gRecord.get ("playing");
        const tGdb  = gRecord.get ("gain_db");
        Tracker.afterFlush (() =>
          {
            const tCV = document.querySelector (".js_inspecteur_record_cur");
            const tDV = document.querySelector (".js_inspecteur_record_dur");
            const tPL = document.querySelector (".js_inspecteur_record_play_label");
            const tGV = document.querySelector (".js_inspecteur_record_gain_val");
            const tGI = document.querySelector (".js_inspecteur_record_gain");
            if (tCV) tCV.textContent = (Number (tCur || 0)).toFixed (2);
            if (tDV) tDV.textContent = (Number (tDur || 0)).toFixed (2);
            if (tPL) tPL.textContent = tPlay ? "❚❚" : "▶";
            if (tGV) tGV.textContent = (Number (tGdb || 0)).toFixed (1) + " dB";
            if (tGI && document.activeElement !== tGI)
              tGI.value = String (tGdb || 0);

            // Aperçu visuel + audible du gain : ne touche que la portion
            // sélectionnée. Le visuel se fait via un canvas overlay
            // positionné au pixel près sur la région ; l'audible est
            // appliqué dynamiquement pendant la lecture.
            rec_update_gain_preview ();
          });
      });
    // Dès qu'on (ré)affiche la dialog avec une box mémorisée, applique-la.
    tpl.autorun (() =>
      {
        if (!gRecord.get ("open")) return;
        gRecord.get ("edit_count");
        Tracker.afterFlush (() => { rec_applique_box (); });
      });
    tpl._recorderViewportResize = () =>
      {
        gRecorderCogibulle.syncToViewport ();
      };
    window.addEventListener ("resize", tpl._recorderViewportResize);
  });

Template.scene.onDestroyed (function ()
  {
    if (!edition_hub_active ()) return;
    if (this._recorderViewportResize)
      window.removeEventListener ("resize", this._recorderViewportResize);
  });

if (edition_hub_active ())
  Template.scene.events
    ({
    "click .js_inspecteur_record_open" (e)
      {
        const tBlocScene = e.currentTarget && e.currentTarget.closest
          ? e.currentTarget.closest (".js_inspecteur_scene_audio_bloc")
          : null;
        const tCleScene = tBlocScene && tBlocScene.getAttribute ("data-key");
        if (tCleScene) selectionne_audio_scene_actif (tCleScene);
        const tBloc = !tCleScene && e.currentTarget && e.currentTarget.closest
          ? e.currentTarget.closest (".js_inspecteur_projet_audio_bloc")
          : null;
        const tCle = tBloc && tBloc.getAttribute ("data-key");
        if (tCle) selectionne_audio_projet_actif (tCle);
        if (!tCleScene && !tCle) gInspecteurSceneAudioActif.set (null);
        gRec.target = tCleScene ? cible_audio_scene_pour_cle (tCleScene) : cible_audio_editeur_courante ();
        rec_open_dialog ();
      },
    "click .js_inspecteur_record_close, click .js_inspecteur_record_cancel" ()
      { rec_close_dialog (true); },
    // Pas de fermeture par clic extérieur : la boîte est draggable et
    // pourrait être fermée par accident lors d'un drag relâché hors carte.
    "change .js_inspecteur_record_device" (e)
      {
        rec_ecrit_device_id (e.currentTarget.value);
        if (gRecord.get ("open") && !gRecord.get ("recording"))
          rec_start_monitor ();
      },
    "change .js_inspecteur_record_normalize_auto" (e)
      { rec_ecrit_normalize_auto (!!e.currentTarget.checked); },
    "click .js_inspecteur_record_refresh_devices" ()
      { rec_charge_devices (true); },
    "click .js_inspecteur_record_start" ()
      { rec_start (); },
    "click .js_inspecteur_record_stop" ()
      { rec_stop (); },
    "click .js_inspecteur_record_clear" ()
      {
        if (!confirm ("Effacer l'enregistrement courant ?")) return;
        rec_clear ();
      },
    "pointerdown .js_inspecteur_record_play" (e)
      {
        e.preventDefault ();
        e.stopPropagation ();
        if (e.stopImmediatePropagation) e.stopImmediatePropagation ();
        rec_play_pause ();
      },
    "click .js_inspecteur_record_play" (e)
      {
        e.preventDefault ();
        e.stopPropagation ();
        if (e.stopImmediatePropagation) e.stopImmediatePropagation ();
      },
    "click .js_inspecteur_record_delete_sel" ()
      { rec_supprime_selection (); },
    "click .js_inspecteur_record_deselect" ()
      { rec_deselect (); },
    "click .js_inspecteur_record_undo" ()
      { rec_undo (); },
    "input .js_inspecteur_record_gain" (e)
      {
        gRecord.set ("gain_db", Number (e.currentTarget.value));
        rec_update_gain_preview ();
        rec_apply_playback_gain_preview ();
      },
    "click .js_inspecteur_record_apply_gain" ()
      {
        const tGdb = Number (gRecord.get ("gain_db") || 0);
        if (Math.abs (tGdb) < 0.01) return;
        rec_applique_gain (tGdb);
      },
    "input .js_inspecteur_record_zoom" (e)
      {
        if (!gRec.ws) return;
        const tV = Number (e.currentTarget.value);
        try { gRec.ws.zoom (Math.max (10, 100 + tV)); } catch (e2) {}
      },
    "mousedown .js_inspecteur_record_ws_conteneur" (e)
      {
        try { e.currentTarget.focus ({preventScroll: true}); } catch (e2) {}
      },
    "keydown .js_inspecteur_record_ws_conteneur" (e)
      {
        if (e.key === " " || e.code === "Space")
          { e.preventDefault (); rec_play_pause (); }
        else if (e.key === "Backspace" || e.key === "Delete")
          {
            if (gRec.region) { e.preventDefault (); rec_supprime_selection (); }
          }
        else if (e.key === "Escape")
          {
            if (gRec.region) { e.preventDefault (); rec_deselect (); }
          }
        else if (e.key === "Home")
          {
            e.preventDefault ();
            if (gRec.ws)
              { try { gRec.ws.pause (); } catch (e2) {}
                try { gRec.ws.setTime (gRec.region ? gRec.region.start : 0); }
                catch (e2) {} }
          }
      },
    "click .js_inspecteur_record_save" ()
      { rec_save (); },

    "pointerdown .js_inspecteur_record_card" ()
      {
        gRecorderCogibulle.notePointerDown ();
      },

    // ---- Drag & resize de la boîte ----
    "pointerdown .js_inspecteur_record_drag" (e)
      {
        // Ne lance pas le drag depuis un bouton ou un input.
        if (e.target && e.target.closest ("button, input, select, a"))
          return;
        rec_drag_start (e.originalEvent || e);
      },
    "pointerdown .js_inspecteur_record_resize" (e)
      { rec_resize_start (e.originalEvent || e); },
    });
}
